import { findMemberShelfTier } from "../../src/domain/memberShelfTier.ts";
import { normalizeLeadingDot } from "../../src/domain/leadingDot.ts";
import {
  INVENTORY_LIST_COLUMNS,
  createInventoryMetricCells,
  createInventoryRow,
  createInventoryHead,
  createInventoryTable,
  buildBottleGroupHeadingRow,
} from "./inventoryTablePresentation.ts";
import { mountSettingsPresentation } from "./settingsPresentation.ts";
import { collapseRowActions } from "./rowMenu.ts";
import {
  getVendorTermsSetupContext,
  enforceVendorTermsSetup,
  type VendorTermsSetupContextDto,
  type EnforceVendorTermsInput,
  type EnforceVendorTermsResultDto,
  getLocationVendorOfferTerms,
  saveLocationVendorOfferTerms,
  type EffectiveLocationVendorOfferTermsDto,
  type LocationVendorOfferTermsContextDto,
  type SaveLocationVendorOfferTermsInput,
  type CreateVendorItemOfferInput,
} from "./api.ts";
import { getLocalSellingShelves, currentProductSelling, type LocationSellingContextDto, type LocalShelfSellingDto, type SaveLocationSettingsInput } from "./api.ts";
import type { CatalogLocalCreateSelection } from "./catalogNetworkModel.ts";
import {createCatalogLocalItem} from "./api.ts";
import { mountCatalogNetwork } from "./catalogNetwork.ts";
import { productCatalogRequest } from "./api.ts";
import { hoverHelp, hideHoverHelp, setHoverHelp, wireHelpInHover } from "./hoverHelp.ts";
import { setDisabledReason } from "./disabledReason.ts";
import type { IngredientChoiceInput, IngredientChoiceGroupInput, UnresolvedIngredientChoiceDto } from "./api.ts";
import { readLocalDiscountPolicy } from "./api.ts";
import { figure as autoParFigure } from "./autoParModel.ts";
import { initTenantPermitPanel } from "./locationPermitPanels.ts";
import { createReportAiBilling, type ReportAiBillingView } from "./reportAiBilling.ts";
import { createReportAiPersonSettings } from "./reportAiPersonSettings.ts";
import { createReportAiHelp, type ReportAiHelpView } from "./reportAiHelp.ts";
import { getRecipeDraftCost, getUserReportAi, setUserReportAi } from "./api.ts";
import { customerBillingOverview, customerBillingPortal, previewCalculatedCatalogPrice, reportAiRequest } from "./api.ts";
import { postItemMath, type ItemMathDto, type ItemMathRequest } from "./api.ts";
import { createBillingStatementsCard } from "./billingStatements.ts";
import { downloadWithToken } from "./consoleDownload.ts";
// ALPHA-11 manager console: the Settings screen's full content,
// expanded from ALPHA-10 item 3's single seasonal-toggle list into three
// sections (Drinks, Bottles, Location settings). This module owns every
// element inside #admin-screen's three panels and their add/edit forms;
// main.ts owns only the screen-level show/hide chrome (showAdminScreen,
// the tenant label, the back-to-tabs button) and hands off to
// openAdminScreen below when the "Settings" button is tapped.
//
// Reached only from Open Tabs' explicit "Settings" button, never
// from the ring screen, never mid-ticket. This surface MAY show prices
// (unlike the bartender availability toggle it grew out of, and unlike
// the ring screen's own no-price seasonal note): a manager configuring a
// bottle's sell price or a drink's build charge needs to see the number.
// Every tax/tip rate on the Location settings panel carries the
// provisional label rule (docs/decision-proposals/provisional-pricing-
// config.md) since DEC-004/DEC-005/DEC-006 stay open.
//
// Touch-only throughout (CLAUDE.md, ui-ux-designer role): every field is a
// picker, a chip, or a numeric input with the right inputmode; nothing
// here depends on a keyboard shortcut, hover, or a title-attribute
// tooltip.

import { itemKindEntrySaleMechanisms, itemKindsStepDone } from "./itemKindMechanismModel.ts";
import {
  discountApprovalLabel,
  fillDiscountApprovalSelect,
} from "./discountApprovalPicks.ts";
import { loadLocationSettingsPanel, resetLocationSettingsPanel } from "./locationSettingsPanel.ts";

import {
  buildVendorOfferRequest,
  isImplausibleBottleCost,
  readVendorOfferCost,
} from "./vendorOfferRequestModel.ts";
import {
  closeCountSession,
  CONTAINER_TYPES,
  createCountSection,
  createLiquorProduct,
  recordOpeningBalanceMovement,
  createMenuDrink,
  createUser,
  deactivateTerminal,
  deactivateUser,
  getCountSessionReview,
  getCountSessionWalk,
  getCountHistorySummaries,
  getCountSessionVariances,
  getMenuDrink,
  getLiquorProduct,
  getLocationAddresses,
  searchSharedCatalog as searchSharedCatalogRequest,
  confirmSharedCatalogProduct as confirmSharedCatalogProductRequest,
  lookupProductWithAi as lookupProductWithAiRequest,
  type SharedCatalogProductDto,
  putLocationAddress,
  getSettings,
  getUserPermissions,
  getRolePermissions,
  listTenantRoles,
  createTenantRole,
  ITEM_KINDS,
  isCurrentItemKind,
  listCountItems,
  listCountSections,
  listCountSessions,
  listLiquorProducts,
  listMenuDrinks,
  getLiquorProductOnHand,
  getLiquorProductStockSettings,
  getPlanningLocations,
  listTenantLocations,
  setUserLocations,
  type PlanningLocationDto,
  getPreferredOffersByLocation,
  putPreferredOffersByLocation,
  getVendorLocationAccounts,
  putVendorLocationAccounts,
  type PreferredOfferLocationDto,
  getLiquorProductShelfTiers,
  getLiquorProductItemCategories,
  getLiquorProductPourOptions,
  getPourPriceOverrides,
  putPourPriceOverride,
  type PourPriceOverrideDto,
  type PourPriceOverridesDto,
  listWasteReasons,
  recordWasteMovement,
  recordReturnToStockMovement,
  recordVendorReturnMovement,
  listInventoryMovements,
  listStockHistory,
  reverseInventoryMovement,
  type InventoryMovementDto,
  type StockHistoryDto,
  type WasteReasonDto,
  listTerminals,
  listTabletDevices,
  listVoidReasons,
  createDiscount,
  createDiscountReason,
  createActionReason,
  createPriceChangeReason,
  listDiscounts,
  listNetworkRecipeOffers,
  listRecipePublicationSources,
  shareSavedRecipe,
  withdrawOwnedRecipePublication,
  listOwnRecipePublications,
  cloneNetworkRecipeOffer,
  listRecipeShares,
  answerNetworkRecipeOffer,
  mapNetworkRecipeOffer,
  listNetworkOffers,
  adoptNetworkOffer,
  declineNetworkOffer,
  listDiscountReasons,
  listActionReasons,
  listPriceChangeReasons,
  updateDiscount,
  updateDiscountReason,
  updateActionReason,
  updatePriceChangeReason,
  type DiscountDto,
  type DiscountReasonDto,
  type ActionReasonDto,
  type ActionReasonKind,
  type PriceChangeReasonDto,
  type DiscountScheduleDto,
  type DiscountScopeClauseDto,
  type DiscountSpecialScopeEntryDto,
  listUsersForAdmin,
  PRICING_MODELS,
  ROUNDING_MODES,
  reassignCountSession,
  recordCount,
  registerTerminal,
  createVoidReason,
  updateVoidReason,
  type VoidReasonDto,
  listRefundReasons,
  createRefundReason,
  updateRefundReason,
  type RefundReasonDto,
  reactivateUser,
  renameTerminal,
  resetPin,
  setCountSessionMode,
  setUserPermissionOverride,
  setUserRoles,
  startCountSession,
  unlockUser,
  updateCountSection,
  updateLiquorProduct,
  confirmLiquorSize,
  updateMenuDrink,
  bulkEditMenuDrinks,
  updateSettings,
  confirmTaxRate,
  saveLocationSettings,
  getPurchaseApprovalThreshold,
  updatePurchaseApprovalThreshold,
  updateUserProfile,
  type CountSectionDto,
  type CountSessionReviewRowDto,
  type CountHistorySummaryDto,
  type CountSessionDto,
  type CountSessionVarianceDto,
  type CountWalkItemDto,
  type CreateMenuDrinkComponentInput,
  type InventoryItemFieldsInput,
  type ItemKind,
  type ProductSaleMechanism,
  type LiquorProductDto,
  type BulkSetVendorResultDto,
  type UpdateLiquorProductInput,
  type AdminUserDto,
  type MenuDrinkComponentDto,
  type RecipeMethodDto,
  type MenuDrinkListItemDto,
  type PermissionGrant,
  type PricingModel,
  type RoundingMode,
  type ShelfTierSettingDto,
  type TerminalDto,
  type UserDto,
  type UserPermissionRowDto,
  type RolePermissionRowDto,
  type TenantRoleDto,
  getVocabularies,
  addVocabularyValue,
  setVocabularyValueActive,
  type MenuDrinkModifierDetailDto,
  updateItemKindSaleMechanism,
  updateItemKindSaleMechanisms,
  setItemKindPourVolumes,
  getItemKindTargetMarkups,
  setItemKindTargetMarkup,
  listReconciliationCandidates,
  resolveReconciliationCandidate,
  type ReconciliationCandidateDto,
  getMyTimeSyncFreshness,
  type MyTimeSyncFreshnessDto,
  createVendor,
  createVendorItemOffer,
  vendorOfferWrites,
  bulkEditItems,
  bulkSetItemVendor,
  deactivateVendor,
  deactivateVendorItemOffer,
  listVendorItemOffers,
  listVendors,
  updateVendor,
  updateVendorItemOffer,
  type VendorDto,
  type VendorOrderMethod,
  type VendorOrderFileLayout,
  type VendorPriceFileLayout,
  type UpdateVendorItemOfferInput,
  type VendorItemOfferDto,
  type LocationAddressDto,
  type LocationWithAddressDto,
  type ReportId,
} from "./api.ts";
import {
  draftFromDiscount,
  renderSpecialEditor,
  zoneIsUnset,
  type SpecialEditorDraft,
} from "./discountSpecialEditor.ts";
import {
  CATEGORY_DISPLAY_ORDER,
  categoryLabel,
  containerTypeLabel,
  formatRecipeQuantity,
  itemKindLabel,
  LIQUOR_SLOT_CATEGORIES,
  pricingModelLabel,
} from "./catalogDisplay.ts";
import { humanizeComponentReference } from "./adminRecipeNames.ts";
import {
  blankDraft,
  chooseItem,
  draftsFromComponents,
  markLiquorPour,
  matchItems,
  needsProduct,
  recipeChanged,
  recipeToBody,
  recipeUnitChoices,
  unmarkLiquorPour,
  type RecipeDraft,
  type RecipeItem,
} from "./recipeEditor.ts";
import {
  buildCountWalkProductStatus,
  countHeldByOtherMessage,
  formatCountQuantityForDisplay,
  formatCountVarianceForDisplay,
  plainCountMessage,
  roundOuncesForDisplay,
} from "./countWalk.ts";
import {
  closeCountTable,
  countTableSessionId,
  openCountTable,
  readCountViewPreference,
  writeCountViewPreference,
  type CountTableContext,
} from "./countTable.ts";
import type { CountClosedSummary } from "./countTableModel.ts";
// INV8 Section 2.6 (DEC-176): the item editor's own read-only Par-level
// unit hint, sharing Section 2.1's count-unit derivation with the Low
// list (ui/src/main.ts's buildPurchasingCartProposalRow), reproduced
// client-side here since this hint has to update live as a person edits
// the form, before any save round-trips through the server.
import { describeParUnitHint, resolveClientCountUnit } from "./purchasingDisplay.ts";
// INVLOC1 Slice 2 (DEC-326): the item editor's table of locations and the Stock list's location header, as pure functions.
import {
  locationSwitchVisible,
  buildStockSettingsPayload,
  editorLayout,
  emptyLocationNote,
  emptyLocationNoteCopy,
  hasNoPar,
  matchesNoParFilter,
  NO_LOCATION_COPY,
  noParNoteCopy,
  parCellText,
  parUnitWord,
  showNoParNote,
  singleLocationCopy,
  tableCopy,
  type LocationSettingsEntry,
  type StockSettingsPayload,
  type TypedLocationValues,
} from "./stockSettingsModel.ts";
// INVLOC1 Slice 5: the preferred vendor and the account number, per location (shown only when there is more than one location).
import { locationChoicesVisible, locationCountShowsChoices } from "./vendorLocationChoicesModel.ts";
// BULKVENDOR1: Set vendor on many items at the Items list's location (docs/build-briefs/BULKVENDOR1-bulk-set-vendor.md).
import { closeBulkRecipeSheet, openBulkRecipeSheet } from "./recipeBulkSheet.ts";
import type { BulkRecipeField } from "./recipeBulkModel.ts";
import { createRecipeHead, createRequiresLiquorCell, recipeTableColumns, renderRecipeStrip } from "./recipeTable.ts";
import { BULK_EDIT_COPY, BULK_EDIT_FIXED_OPTIONS, BULK_EDIT_SPECS, selectAllLabel, selectedLabel, type BulkEditKind } from "./bulkEditModel.ts";
import { closeBulkEditSheet, openBulkEditSheet } from "./bulkEditSheet.ts";
import {
  buildVendorLocationAccounts,
  offerPickerLabel,
  renderPreferredByLocation,
} from "./vendorLocationChoices.ts";
import { resolveActorDisplayName } from "./actorDisplay.ts";
import { countHistoryLine, historyUnitFor } from "./countHistoryModel.ts";
import { extraChargeInput, shelfTierKeyLabel, shelfTierPositionLabel } from "./menuEntryModel.ts";
import {
  basisPointsToPercentString,
  centsToAmountString,
  formatCents,
  parseDollarsToCentsExact,
  parseExactDecimalToSmallestUnit,
  parsePercentToBasisPoints,
} from "./money.ts";
import {
  abvBasisPointsToStrengthDisplayString,
  parseStrengthDisplayStringToAbvBasisPoints,
  rescaleStrengthDisplayString,
  strengthFieldLabel,
  strengthFieldShowsPercentSuffix,
} from "./strength.ts";
import {
  FIND_DEBOUNCE_MS,
  FIND_MIN_CHARS,
  groupByProducer,
  newProductLabel,
  previewNotice,
  previewRows,
  resultHint,
  chooseLocationCategory,
} from "./sharedCatalogFind.ts";
import {
  canRecordStockFlag,
  canViewStock86History,
  ensureStock86History,
  loadMenuStock86,
  loadStock86Since,
  offerClearStock86,
  requestApproval,
  requireSession,
  sessionOrUndefined as pageSessionOrUndefined,
  setMenuStockFlag,
  setStockFlag,
  showAdminScreen,
  showToast,
  stock86HistoryIsCurrent,
  stock86HistoryLinesFor,
  stock86MenuEntries,
  stock86SinceFor,
  type ToastVariant,
} from "./main.ts";
// STK1 (DEC-269): who marked an item out, and since when, for the editor.
import {
  editorOutSinceText,
  menuEditorOutSinceText,
  settingsMenuRowAction,
  settingsMenuRowStatus,
  settingsMenuRows,
  stock86DrinkKey,
  type SettingsMenuRow,
} from "./stock86.ts";
import {
  boundRangeText,
  hasPermission,
  loadTerminalConfig,
  maxDollarsBoundText,
  maxLengthBoundText,
  percentBoundRangeText,
  pinLengthRangeText,
  type Session,
} from "./session.ts";
import {
  MAX_ABANDONED_TAB_GRATUITY_BASIS_POINTS,
  MAX_HOLD_AMOUNT_CENTS,
  MAX_HOLD_INCREMENT_THRESHOLD_BASIS_POINTS,
  MAX_SESSION_INACTIVITY_MINUTES,
  MAX_TAX_RATE_BASIS_POINTS,
  MAX_TIP_PRESET_BASIS_POINTS,
  MAX_TIP_CHOICES_ON_SAVE,
  MAX_VOCABULARY_LABEL_LENGTH,
  MIN_HOLD_INCREMENT_THRESHOLD_BASIS_POINTS,
  MIN_SESSION_INACTIVITY_MINUTES,
  MIN_TIP_PRESET_COUNT,
  PIN_LENGTH_MAX,
  PIN_LENGTH_MIN,
  PIN_LOCKOUT_ATTEMPTS_MAX,
  PIN_LOCKOUT_ATTEMPTS_MIN,
  PIN_LOCKOUT_MINUTES_MAX,
  PIN_LOCKOUT_MINUTES_MIN,
  PROFILE_FIELD_MAX_LENGTH,
} from "../../src/domain/settingsBounds.ts";
// UX16-F2 (docs/build-briefs/UX16-F2-negative-on-hand-shown-not-hidden.md;
// DEC-178): the same exact-decimal parser inventoryStockState.ts already
// trusts for its own zero-check, reused here rather than a second,
// independently-written numeric parse that could disagree at the edges.
import { isNegativeQuantity } from "../../src/domain/inventorySizeUnits.ts";
// UX43: the pure comparator and its token parser live in their own
// DOM-free module (sizeTokens.ts's own header explains why) rather than
// inline here, so a plain node:test file can import bySizeTokenAmount
// without a DOM shim.
import { bySizeTokenAmount, parseSizeToken } from "./sizeTokens.ts";
// INV26 Slice 1 (docs/build-briefs/INV26-backbar-inventory.md; DEC-206
// item 2): the Settings inventory list's sort, same DOM-free-module
// reason as sizeTokens.ts above (inventoryListSort.ts's own header).
import {
  INVENTORY_LIST_STATUS_LABEL,
  inventoryListStatus,
  inventoryListStatusRank,
  nextInventoryListSort,
  packageAmountMl,
  sortInventoryListRows,
  type InventoryListSortKeys,
  type InventoryListSortState,
} from "./inventoryListSort.ts";
// UX88 (DEC-339): the one shared A to Z name sort for every list of named things.
import { activeVocabularyOptions, compareNames, sortByName, sortOptions } from "./nameSort.ts";
import { countSectionPickerOptions, roleOptionsFrom } from "./namedListsModel.ts";
// INV26 Slice 3 (docs/build-briefs/INV26-backbar-inventory.md; DEC-206
// item 4, DEC-207): the calculator's own math, same DOM-free-module
// reason as inventoryListSort.ts and sizeTokens.ts above.
import { bottlesPerOrderUnitOf } from "../../src/domain/orderUnitBottles.ts";
import { computePriceCalculator, type PriceCalculatorResult } from "./priceCalculator.ts";
import { capitalizeServerMessage } from "./serverMessageDisplay.ts";
import { discountSaveRefusal, type DiscountSaveAttempt } from "./discountSaveRefusal.ts";
import { readerDetailLine, readerStatusChip } from "./terminalReaderDisplay.ts";
import {
  buildMovementRows,
  classifyMovement,
  confirmSentence,
  indexReversals,
  isAlreadyReversedRefusal,
  movementTypeLabel,
  negativeOnHandWarning,
  ouncesPerBottle,
  plainRefusal,
  projectedOnHand,
  quantityWords,
  type MovementRowView,
  buildHistoryRows,
} from "./movementReversal.ts";
import { bottleFigure } from "./usageWasteModel.ts";
import { resetGoogleSheets } from "./googleSheets.ts";
import {
  leaveReports,
  openReports,
  refreshReportsGating,
  reportsRouteState,
  resetReports,
} from "./reports.ts";
import {
  dismissChangeHistorySheet,
  openChangeHistory,
  refreshChangeHistorySession,
  resetChangeHistory,
} from "./changeHistory.ts";
// AU18: the Audit log's Activity tab draws and owns its own list (a module of its own).
import { openAuditLog, refreshAuditLogSession, resetAuditLog } from "./auditLog.ts";
import { openSupportSessions, resetSupportSessions } from "./supportSessions.ts";
// INVXFER1-S2: the stock transfer screens draw and own their own state (a module of their own).
import { openTransfers, refreshTransfersSession, resetTransfers } from "./transfers.ts";
import type { AuditLogFilters } from "./auditLogModel.ts";
import type { AuditLogLinkDto } from "./api.ts";
import { endFranchisorSupportSession } from "./api.ts";
// R5, R6, R7 (DEC-313): the role dialogs, the staged-save review, the add-role preview and the
// Access readout draw and own their own dialogs (a module of its own); this file only wires them in.
import {
  confirmAddRole,
  dismissRolesManagementSheets,
  initRolesManagement,
  openRoleOptions,
  pickException,
  renderAccessReadout,
  resetRolesManagement,
  reviewRoleSave,
  switchBackOn,
} from "./rolesManagement.ts";
import { unsavedText } from "./rolesManagementModel.ts";
import {
  noMatchText,
  permissionCountText,
  PERMISSION_DESCRIPTIONS,
  sectionDef,
  sectionKeyForPermission,
  searchPermissionSections,
  splitForHighlight,
} from "./permissionSections.ts";
// INV31 (docs/build-briefs/INV31-set-starting-stock.md): the Set starting stock
// screen draws and owns its own state (a module of its own, so none of it adds
// a binding here); this file only wires it in.
import {
  applyStartingStockEntryGating,
  configureStartingStock,
  openStartingStockScreen,
  noStartingStockIds,
  onNoStartingStockChanged,
  readStartingStockStatus,
  refreshStartingStockEntry,
  resetStartingStock,
  setStockListEmpty,
} from "./startingStock.ts";
import {
  buildRequest as buildStartingStockRequest,
  classifyItem as classifyStartingStockItem,
  classifySaveResponse as classifyStartingStockResponse,
  parseStartStockText,
  startingStockStepState,
  startStockKindFor,
} from "./startingStockModel.ts";
import { describeSetting } from "./settingsDescription.ts";
import { createNetworkOffers } from "./networkDiscountOffers.ts";
import { createNetworkRecipeOffers } from "./networkRecipeOffers.ts";
import { createRecipeSharing } from "./recipeSharing.ts";
import { discountKindLabel } from "./discountKind.ts";
import { copyRowView, isNetworkCopy } from "./networkDiscountOffersModel.ts";
import { mountTaxRateSection } from "./taxRateSettings.ts";
import { mountTabletsSection } from "./tablets.ts";
import { mountLocationsSection } from "./locations.ts";
import {
  businessDayConfirmView,
  CHANGE_TO_SAVE_TEXT,
  DEFAULT_NOT_CONFIRMED_TEXT,
  DEFAULT_TIMEZONE,
  TIMEZONE_PLACEHOLDER_TEXT,
} from "./businessDayConfirm.ts";
import {
  ADMIN_SECTION_ORDER,
  ADMIN_TAB_PERMISSION_IDS,
  ADMIN_TAB_PERMISSIONS,
  SETTINGS_ENTRY_PERMISSIONS,
  type AdminSection,
} from "./settingsGate.ts";
import { mountDiscountAddSheet } from "./discountAddSheet.ts";
import { createSetupDisclosure } from "./setupDisclosure.ts";
// UX108: the Settings group, the operations view, the report and the open count are part of the screen's address.
import { requestRoutePublish } from "./routeHistory.ts";
import {
  asRecord,
  asString,
  clearDraft,
  loadDraft,
  saveDraft,
  type DraftScope,
} from "./screenDrafts.ts";
import {
  clearItemDraft,
  emptyItemDraftFields,
  ITEM_DRAFT_STEPS,
  itemDraftFieldsEqual,
  loadItemDraft,
  saveItemDraft,
  type ItemDraft,
  type ItemDraftScope,
  type ItemDraftStep,
} from "./itemDraft.ts";
import type { ReportRange, Route } from "./screenRoute.ts";
import { mountPayrollSection } from "./payrollGusto.ts";
import { mountCardPaymentsSection } from "./cardPayments.ts";
import { mountBankAccountsSection } from "./bankAccounts.ts";
import { clearSubscriptionDebitReturnMarker, type SubscriptionDebitArrival } from "./bankAccountsModel.ts";
import { clearRoyaltyReturnMarker, type RoyaltyArrival } from "./royaltyAuthorizationModel.ts";
import { mountIdVerifySetting } from "./idVerifySetting.ts";
import { mountReceiptSetting } from "./receiptSetting.ts";
import { mountCashSetting } from "./cashSetting.ts";
import { mountAccountingSection } from "./accounting.ts";
import type { AccountingArrival } from "./accountingModel.ts";
import { mountAutoPar } from "./autoPar.ts";
import { everyLabel, weekdayName } from "./autoParModel.ts";
import { mountReaderTipsSection } from "./readerTips.ts";
import { mountSystemChecksCard } from "./systemChecks.ts";
import type { LocationSystemChecksDto } from "./api.ts";
import { mountPrintersSection } from "./printers.ts";
import { clearStripeReturnMarker } from "./setupWizardModel.ts";
import { CARD_READER_REGISTERED_EVENT } from "./terminalSignInModel.ts";
import { type CardPaymentsArrival } from "./cardPaymentsModel.ts";
import {
  buildPriceListCardEntry,
  buildPriceListSummary,
  configureVendorPriceList,
  resetVendorPriceList,
} from "./vendorPriceList.ts";
import { openInventoryImport, resetInventoryImport } from "./inventoryImport.ts";
import { openRecipeImport, resetRecipeImport, RECIPE_IMPORT_PERMISSION } from "./recipeImport.ts";
import { buildZoneGroups } from "./timeZoneOptions.ts";
import { bindAutofillSelect } from "./autofill.ts";
import { CLOSING_HOUR_CHOICES } from "./ownerWizardModel.ts";
import { cutoffText } from "./reportFormat.ts";

mountSettingsPresentation(document);

function mustFind<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (element === null) {
    throw new Error(`expected element for selector "${selector}"`);
  }
  return element;
}

// ---------------------------------------------------------------------
// Element lookup
// ---------------------------------------------------------------------

// Settings rebuild (docs/backlog.md SET1/SET2/SET3) and UX80 (DEC-298): the
// groups, their order and the permissions that open each one live in
// ui/src/settingsGate.ts (pure data, unit tested). Settings is configuration
// only: Reports, counting, Stock levels, Match clients, ordering and closeout
// are operational screens in the account menu (openOperationsView below).
//
// Independent-review Finding B, 2026-08-26: each group gates itself on its own
// permissions, never on a role, so a standalone Inventory Manager sees only the
// groups their permissions open. A group's view-only ids (People, Vendors) can
// show the group to someone already in Settings but never put a Settings row in
// the menu on their own (see settingsGate.ts's SETTINGS_ENTRY_PERMISSIONS).
export { ADMIN_TAB_PERMISSIONS, SETTINGS_ENTRY_PERMISSIONS };

// ACCTEXPORT1 and PAYEXPORT1: whether at least one outside connection (QuickBooks, Gusto) is on for this deployment and this person,
// as the two cards' own reads answered. The Connections rail item is hidden until one of them says yes. A const holder, reset on sign-out.
const connectionsState = { available: false };

function sectionVisible(activeSession: Session, section: AdminSection): boolean {
  if (section === "connections" && !connectionsState.available) return false;
  return ADMIN_TAB_PERMISSION_IDS[section].some((id) => hasPermission(activeSession, id));
}

// The two content sections the Inventory items group shows (UX80 narrowed the
// second from the whole Counts panel to the count areas): the item catalog, and
// the count areas, each on its own permission. Counting itself and Stock levels
// are menu screens now.
const INVENTORY_SUBSECTION_PERMISSION_IDS = {
  bottles: ["admin.liquor_product.manage"],
  countAreas: ["inv.count_section.manage"],
} as const;

function inventorySubsectionVisible(
  activeSession: Session,
  subsection: keyof typeof INVENTORY_SUBSECTION_PERMISSION_IDS,
): boolean {
  return INVENTORY_SUBSECTION_PERMISSION_IDS[subsection].some((id) =>
    hasPermission(activeSession, id),
  );
}

// T14 (docs/backlog.md), re-gated by T15 (DEC-041 item 3, 2026-09-02).
// The Operations group's own outer gate is admin.tenant_settings.view;
// this subsection reads its OWN dedicated pair, guest.reconciliation.
// view/.resolve (migration 0060), not the borrowed admin.manager_
// exception.view/.resolve pair T14 originally shipped on, so it is
// hidden for anyone who cannot see it even if they can see the rest of
// Operations -- the same "own subsection, own gate" split
// inventorySubsectionVisible establishes above. Every UI gate here reads
// hasPermission, never a role name (CLAUDE.md's permission-driven rule).
function reconciliationQueueVisible(activeSession: Session): boolean {
  return hasPermission(activeSession, "guest.reconciliation.view");
}

// UX18-F1: the business-day cutoff and session-inactivity blocks (and
// their save button) are tenant settings and keep the tab's former gate
// as their own, now that the tab itself is reachable more widely.
function operationsTenantSettingsVisible(activeSession: Session): boolean {
  return hasPermission(activeSession, "admin.tenant_settings.view");
}

// SET16-F1: People's own tenant-configuration block (the PIN policy) is
// the one UX18-F1's per-block-gate pattern was never applied to, even
// though the People tab's own outer union has always been admin.user
// .view alone (:269), not admin.tenant_settings.view. Without this, a
// session that reaches People through admin.user.view alone still saw
// whichever session last painted these inputs: showSection's own call-
// site gate (below) simply never calls loadSettings() for a session
// that lacks admin.tenant_settings.view, and nothing else ever
// repaints them. Same shape as operationsTenantSettingsVisible above,
// checked on every entry to People rather than only at a session
// boundary, the same defense-in-depth operationsTenantSettingsVisible
// already gets.
function pinPolicyVisible(activeSession: Session): boolean {
  return hasPermission(activeSession, "admin.tenant_settings.view");
}

// UX86: the Session inactivity card moved from Location to People and access.
// It keeps the permission it always had.
function sessionInactivityVisible(activeSession: Session): boolean {
  return hasPermission(activeSession, "admin.tenant_settings.view");
}

function reconciliationResolveAllowed(activeSession: Session): boolean {
  return hasPermission(activeSession, "guest.reconciliation.resolve");
}

// VEN3: the vendor list's own gate, inv.vendor.view -- the SAME permission
// the server's GET /tenants/:id/vendors route actually requires
// (harness/src/api/server.ts). Purchasing's own outer tab gate is a wide
// union (see ADMIN_TAB_PERMISSION_IDS.purchasing above) that deliberately
// omits admin.tenant_settings.view, so a user can reach this tab through
// purchasing.order.view/.cart.manage/etc. without holding inv.vendor.view
// at all; this content block must not assume the tab's own reachability
// implies the vendor list is. Never admin.tenant_settings.view (that was
// the accidental gate this item exists to remove) and never a role name.
function purchasingVendorsVisible(activeSession: Session): boolean {
  return hasPermission(activeSession, "inv.vendor.view");
}

// VEN4: the vendor block's own WRITE gate, independent of
// purchasingVendorsVisible immediately above -- view decides whether the
// block appears at all, manage decides whether the add control and the
// per-vendor write affordances inside it do. Before this, an
// assistant_manager (inv.vendor.view: allowed, inv.vendor.manage: denied)
// could see the vendor list, correctly, and was ALSO offered an add
// button and editable fields that the server's own requirePermission
// (harness/src/api/server.ts:3478, :3516, :3521, :3559) would refuse the
// moment they were used. The server was never the gap; the client was
// offering an action it already had the information to know would be
// refused. Never admin.tenant_settings.view and never a role name
// (DEC-023: roles are configurable, so today's assistant_manager gap is
// not a permanent one).
function purchasingVendorsManageAllowed(activeSession: Session): boolean {
  return hasPermission(activeSession, "inv.vendor.manage");
}

// PO2, owner ruling 2026-09-29 (DEC-247). Mirrors purchasingVendorsVisible/
// purchasingVendorsManageAllowed exactly: VIEW decides whether the "Order
// approval" block appears at all (purchasing.order.view -- the same
// permission that already governs whether a user can see the orders and
// approvals this threshold affects), MANAGE decides whether the field and
// Save button are read-only. Never admin.tenant_settings.view/.manage
// (this is deliberately its own, narrower permission per the ruling) and
// never a role name.
function purchasingApprovalThresholdVisible(activeSession: Session): boolean {
  return hasPermission(activeSession, "purchasing.order.view");
}

function purchasingApprovalThresholdManageAllowed(activeSession: Session): boolean {
  return hasPermission(activeSession, "purchasing.approval_threshold.manage");
}

// MT1 (docs/backlog.md), P0.11's "a reconciliation process detects missed
// or failed incremental updates." Its own gate, admin.mytime_sync.view
// (migration 0063), independent of both admin.tenant_settings.view (the
// Operations panel's own outer gate) and guest.reconciliation.view
// (T14/T15's DIFFERENT client-identity queue immediately above): a
// manager could hold any one of these three without the other two.
function mytimeSyncFreshnessVisible(activeSession: Session): boolean {
  return hasPermission(activeSession, "admin.mytime_sync.view");
}

// Settings rebuild (docs/backlog.md SET1/SET2/SET3): a left rail of ten
// grouped areas replaces the old five flat tabs. Money/Tabs and payment/
// Discounts/Corrections/Operations are new group containers (Money and
// Tabs and payment/Operations split out of the old single #admin-
// settings-panel; Discounts and Corrections were always distinct
// content, just under that one panel before). Menu, Inventory (Bottles +
// Counts together now) and People and access reuse the SAME content
// elements the old Drinks/Bottles+Counts/Users tabs already had --
// #admin-drinks-panel, #admin-bottles-panel, #admin-counts-panel and
// #admin-users-panel keep their ids unchanged, only their outer nav
// changed, so nothing that reads or writes their contents below had to
// change.
//
// SET10 (DEC-130 ruling (d)) split the rail to eleven: Recipes is a new
// group reusing #admin-drinks-panel's own unchanged id and content (the
// Drinks list and its add/edit sheet), and what stays in Menu (the eight
// vocabulary editors) moved into a new wrapper, #admin-group-panel-menu,
// since #admin-drinks-panel itself now belongs to Recipes alone.
const railSearchInput = mustFind<HTMLInputElement>("#admin-settings-search");
const railSearchCountEl = mustFind<HTMLElement>("#admin-settings-search-count");
const railSearchResultsEl = mustFind<HTMLElement>("#admin-settings-search-results");
const settingsGroupsEl = mustFind<HTMLElement>("#admin-settings-groups");

// UX26: the "Set up this location" checklist card, sits above the rail.
const setupChecklistEl = mustFind<HTMLElement>("#admin-setup-checklist");
const setupChecklistProgressEl = mustFind<HTMLElement>("#admin-setup-checklist-progress");
const setupChecklistToggleEl = mustFind<HTMLButtonElement>("#admin-setup-checklist-toggle");
const setupChecklistListEl = mustFind<HTMLElement>("#admin-setup-checklist-list");
const setupChecklistTodoEl = mustFind<HTMLElement>("#admin-setup-checklist-todo");

const tabMoney = mustFind<HTMLButtonElement>("#admin-group-tab-money");
const tabTabsPayment = mustFind<HTMLButtonElement>("#admin-group-tab-tabspayment");
const tabDiscounts = mustFind<HTMLButtonElement>("#admin-group-tab-discounts");
const tabCorrections = mustFind<HTMLButtonElement>("#admin-group-tab-corrections");
const tabRecipes = mustFind<HTMLButtonElement>("#admin-group-tab-recipes");
const tabMenu = mustFind<HTMLButtonElement>("#admin-group-tab-menu");
const tabInventory = mustFind<HTMLButtonElement>("#admin-group-tab-inventory");
const tabPurchasing = mustFind<HTMLButtonElement>("#admin-group-tab-purchasing");
const tabPeople = mustFind<HTMLButtonElement>("#admin-group-tab-people");
const settingsShellEl = mustFind<HTMLElement>('[data-testid="admin-settings-shell"]');
const tabOperations = mustFind<HTMLButtonElement>("#admin-group-tab-operations");
const tabTablets = mustFind<HTMLButtonElement>("#admin-group-tab-tablets");
const tabAuditLog = mustFind<HTMLButtonElement>("#admin-group-tab-auditlog");
const tabNetwork = mustFind<HTMLButtonElement>("#admin-group-tab-network");
const tabConnections = mustFind<HTMLButtonElement>("#admin-group-tab-connections");

const panelMoney = mustFind<HTMLElement>("#admin-group-panel-money");
const panelTabsPayment = mustFind<HTMLElement>("#admin-group-panel-tabspayment");
const panelDiscounts = mustFind<HTMLElement>("#admin-group-panel-discounts");
const panelCorrections = mustFind<HTMLElement>("#admin-group-panel-corrections");
// SET10: panelDrinks is now reached only through the Recipes tab; the eight
// vocabulary editors that used to share this element with it now live in
// their own wrapper, panelMenu, reached through the unchanged Menu tab.
const panelDrinks = mustFind<HTMLElement>("#admin-drinks-panel");
const panelMenu = mustFind<HTMLElement>("#admin-group-panel-menu");
const panelInventory = mustFind<HTMLElement>("#admin-group-panel-inventory");
const panelBottles = mustFind<HTMLElement>("#admin-bottles-panel");
let catalogPlanningLocationId: string | undefined;
let catalogPlanningSequence = 0;
const catalogScopeHost = document.createElement("div");
catalogScopeHost.className = "catalog-network";
const productCatalogHost = document.createElement("section");
productCatalogHost.className = "admin-section";
panelInventory.prepend(catalogScopeHost, productCatalogHost);
const productCatalogSection = mountCatalogNetwork(productCatalogHost, {
  prepareLocalItem:(selection,isCurrent,completed)=>openCatalogLocalItemSetup(selection,isCurrent,completed),
  network: false,
  identity: () => {
    const live = sessionOrUndefined();
    const siteId = catalogPlanningLocationId;
    return live && currentSection === "inventory" && siteId
      ? JSON.stringify([live.token, live.permissions, siteId])
      : null;
  },
  canView: () => {
    const live = sessionOrUndefined();
    return live !== undefined && hasPermission(live, "admin.liquor_product.view");
  },
  canManage: () => {
    const live = sessionOrUndefined();
    return live !== undefined && hasPermission(live, "admin.liquor_product.manage");
  },
  request: async (method, path, body) => {
    const live = sessionOrUndefined(),
      siteId = catalogPlanningLocationId;
    if (!live || !siteId) return { status: 403, body: null };
    return productCatalogRequest(live.apiBaseUrl, live.tenantId, siteId, method, path, body);
  },
});

async function refreshLocalProductCatalog(): Promise<void> {
  const live = sessionOrUndefined(),
    sequence = ++catalogPlanningSequence;
  productCatalogSection.reset();
  catalogScopeHost.replaceChildren();
  if (!live || !hasPermission(live, "admin.liquor_product.view") || currentSection !== "inventory")
    return;
  const result = await getPlanningLocations(live.apiBaseUrl, live.tenantId);
  if (
    sequence !== catalogPlanningSequence ||
    sessionOrUndefined()?.token !== live.token ||
    currentSection !== "inventory"
  )
    return;
  if (result.status !== 200) {
    catalogScopeHost.textContent = "Catalog locations unavailable. Reopen Inventory to retry.";
    return;
  }
  const listed = result.body.locations,
    deviceId = loadTerminalConfig()?.locationId;
  if (!listed.some((site) => site.id === catalogPlanningLocationId))
    catalogPlanningLocationId =
      listed.find((site) => site.id === deviceId)?.id ??
      (listed.length === 1 ? listed[0]?.id : undefined);
  const label = document.createElement("label"),
    picker = document.createElement("select");
  label.textContent = "Catalog location";
  // HIGFIX4A (F-37): the one field style, like every other select on a manager page.
  picker.className = "field-input";
  picker.append(new Option("Choose location", ""));
  for (const site of listed) picker.append(new Option(site.name, site.id));
  picker.value = catalogPlanningLocationId ?? "";
  label.append(picker);
  catalogScopeHost.append(label);
  picker.onchange = async () => {
    const picked = picker.value;
    const navigationGeneration = loadGeneration;
    if (!(await productCatalogSection.confirmLeave()) || !loadIsCurrent(navigationGeneration)) {
      picker.value = catalogPlanningLocationId ?? "";
      return;
    }
    productCatalogSection.reset();
    catalogPlanningLocationId = listed.find((site) => site.id === picked)?.id;
    void productCatalogSection.load();
  };
  void productCatalogSection.load();
}
const panelCountAreas = mustFind<HTMLElement>("#admin-count-areas-panel");
// UX80: the operational views that share this screen's sheets and loaders.
const panelCounts = mustFind<HTMLElement>("#admin-counts-panel");
const panelStockLevels = mustFind<HTMLElement>("#admin-stock-levels-panel");
// CORE7-F1: the inline 86 panel for a drink or an ingredient, and the drink editor's own 86 block.
const menu86Panel = mustFind<HTMLElement>("#admin-menu86-panel");
const menu86CloseButton = mustFind<HTMLButtonElement>("#admin-menu86-close");
const menu86TitleEl = mustFind<HTMLElement>("#admin-menu86-title");
const menu86InButton = mustFind<HTMLButtonElement>("#admin-menu86-in");
const menu86OutButton = mustFind<HTMLButtonElement>("#admin-menu86-out");
const menu86SinceEl = mustFind<HTMLElement>("#admin-menu86-since");
const menu86HistoryHeadingEl = mustFind<HTMLElement>("#admin-menu86-history-heading");
const menu86HistoryEl = mustFind<HTMLElement>("#admin-menu86-history");
const drinkStockBlock = mustFind<HTMLElement>("#admin-drink-stock-block");
const drinkStockInButton = mustFind<HTMLButtonElement>("#admin-drink-stock-in");
const drinkStockOutButton = mustFind<HTMLButtonElement>("#admin-drink-stock-out");
const drinkStockSinceEl = mustFind<HTMLElement>("#admin-drink-stock-since");
const stockLevelsSearchInput = mustFind<HTMLInputElement>("#admin-stock-search");
const stockLevelsFilterEl = mustFind<HTMLElement>("#admin-stock-filter");
const stockLevelsListEl = mustFind<HTMLElement>("#admin-stock-list");
const stockDetailPanel = mustFind<HTMLElement>("#admin-stock-detail-panel");
const stockDetailTitleEl = mustFind<HTMLElement>("#admin-stock-detail-title");
const stockDetailCloseButton = mustFind<HTMLButtonElement>("#admin-stock-detail-close");
const panelMatchClients = mustFind<HTMLElement>("#admin-match-clients-panel");
const panelPurchasing = mustFind<HTMLElement>("#admin-group-panel-purchasing");
const panelUsers = mustFind<HTMLElement>("#admin-users-panel");
const panelReports = mustFind<HTMLElement>("#admin-group-panel-reports");
const panelPriceListPicker = mustFind<HTMLElement>("#admin-price-list-picker-panel");
const panelChangeHistory = mustFind<HTMLElement>("#admin-change-history-panel");
const panelTransfers = mustFind<HTMLElement>("#admin-transfers-panel");
const auditActivityHost = mustFind<HTMLElement>("#audit-log-activity-host");
const auditSettingsHost = mustFind<HTMLElement>("#audit-log-settings-host");
const auditTabActivity = mustFind<HTMLButtonElement>("#audit-log-tab-activity");
const auditTabSettings = mustFind<HTMLButtonElement>("#audit-log-tab-settings");
const auditTabSupport = mustFind<HTMLButtonElement>("#audit-log-tab-support");
const auditSupportHost = mustFind<HTMLElement>("#audit-log-support-host");
const panelOperations = mustFind<HTMLElement>("#admin-group-panel-operations");
const operationsTenantSettingsEl = mustFind<HTMLElement>("#admin-operations-tenant-settings");
const panelTablets = mustFind<HTMLElement>("#admin-group-panel-tablets");
// UX126: ui/src/tablets.ts clears its root on every draw, so it owns this host and the Card readers card beside it is never erased.
const tabletsHostEl = mustFind<HTMLElement>("#admin-tablets-host");
const cardReadersCardEl = mustFind<HTMLElement>("#admin-card-readers-card");
const panelNetwork = mustFind<HTMLElement>("#admin-group-panel-network");
const panelConnections = mustFind<HTMLElement>("#admin-group-panel-connections");
// BILLSTMT1: the Billing page's balances, the BANK1 Bank accounts slot and the statement history sit above AI spending.
// Offered only with billing.ai.manage, the permission that already gates this Settings group's content.
const billingStatementsCard = createBillingStatementsCard(panelNetwork, {
  load: async () => {
    const captured = currentSession;
    if (captured === undefined || !hasPermission(captured, "billing.ai.manage")) return { status: 403, body: null };
    const response = await customerBillingOverview(captured.apiBaseUrl, captured.tenantId);
    return captured === currentSession ? response : { status: 401, body: null };
  },
  openPaymentMethod: async () => {
    const captured = currentSession;
    if (captured === undefined) return null;
    const reply = await customerBillingPortal(captured.apiBaseUrl, captured.tenantId);
    return reply.status === 200 && typeof reply.body?.url === "string" ? reply.body.url : null;
  },
  download: async (id, fileName) => {
    const captured = currentSession;
    if (captured === undefined) return "unauthorized";
    return downloadWithToken(
      `${captured.apiBaseUrl}/tenants/${encodeURIComponent(captured.tenantId)}/billing/statements/${encodeURIComponent(id)}/pdf`,
      captured.token,
      fileName,
    );
  },
  sessionLost: () => undefined,
});
// AIBILL1 (DEC-596): the one AI help section on Billing, above the AI usage detail it summarizes.
const aiHelpSection = createReportAiHelp(panelNetwork, {
  readHelp: async () => {
    const captured = currentSession;
    if (captured === undefined || !hasPermission(captured, "billing.ai.manage")) return { status: 403, body: null };
    const response = await reportAiRequest<ReportAiHelpView & { message?: string }>(
      captured.apiBaseUrl,
      captured.tenantId,
      "GET",
      "/billing/help",
    );
    return captured === currentSession ? response : { status: 401, body: null };
  },
  readBilling: async () => {
    const captured = currentSession;
    if (captured === undefined || !hasPermission(captured, "billing.ai.manage")) return { status: 403, body: null };
    const response = await reportAiRequest<ReportAiBillingView & { message?: string }>(
      captured.apiBaseUrl,
      captured.tenantId,
      "GET",
      "/billing",
    );
    return captured === currentSession ? response : { status: 401, body: null };
  },
  canSwitch: () => currentSession !== undefined && hasPermission(currentSession, "admin.location_settings.manage"),
  setEnabled: async (enabled) => {
    const captured = currentSession;
    if (captured === undefined || !hasPermission(captured, "admin.location_settings.manage")) return { status: 403, body: null };
    const response = await reportAiRequest<{ enabled?: boolean; message?: string }>(
      captured.apiBaseUrl,
      captured.tenantId,
      "POST",
      "/enabled",
      { enabled },
    );
    return captured === currentSession ? response : { status: 401, body: null };
  },
  saveCap: async (input) => {
    const captured = currentSession;
    if (captured === undefined) return { status: 401, body: null };
    const response = await reportAiRequest<{ message?: string }>(
      captured.apiBaseUrl,
      captured.tenantId,
      "POST",
      "/billing/caps",
      input,
    );
    return captured === currentSession ? response : { status: 401, body: null };
  },
  changed: () => void aiBillingPanel.load(),
});
const aiBillingPanel = createReportAiBilling(panelNetwork, {
  read: async (month) => {
    const captured = currentSession;
    if (captured === undefined || !hasPermission(captured, "billing.ai.manage"))
      return { status: 403, body: null };
    const response = await reportAiRequest<ReportAiBillingView & { message?: string }>(
      captured.apiBaseUrl,
      captured.tenantId,
      "GET",
      `/billing${month === undefined ? "" : `?month=${encodeURIComponent(month)}`}`,
    );
    return captured === currentSession ? response : { status: 401, body: null };
  },
  saveCap: async (input) => {
    const captured = currentSession;
    if (captured === undefined) return { status: 401, body: null };
    const response = await reportAiRequest<{ message?: string }>(
      captured.apiBaseUrl,
      captured.tenantId,
      "POST",
      "/billing/caps",
      input,
    );
    return captured === currentSession ? response : { status: 401, body: null };
  },
  export: async (month) => {
    const captured = currentSession;
    if (captured === undefined) return;
    const outcome = await downloadWithToken(
      `${captured.apiBaseUrl}/tenants/${encodeURIComponent(captured.tenantId)}/reports/custom/ai/billing/export?format=csv&month=${encodeURIComponent(month)}`,
      captured.token,
      `ai-usage-${month}.csv`,
    );
    if (outcome !== "ok") throw new Error("AI usage export unavailable");
  },
});

const drinkListEl = mustFind<HTMLElement>("#admin-drink-list");
const addDrinkButton = mustFind<HTMLButtonElement>("#admin-add-drink");
const importRecipesButton = mustFind<HTMLButtonElement>("#admin-import-recipes");

// Amendment 2026-08-22 (tenth): bulk drink operations. Search/filter
// mirrors the Bottles list's own element shape exactly.
const drinkSearchInput = mustFind<HTMLInputElement>("#admin-drink-search");
const drinkFilterCategoryEl = mustFind<HTMLElement>("#admin-drink-filter-category");
const drinkBulkBarEl = mustFind<HTMLElement>("#admin-drink-bulk-bar");
const drinkFilterRequiresEl = mustFind<HTMLElement>("#admin-drink-filter-requires");

const bottleSearchInput = mustFind<HTMLInputElement>("#admin-bottle-search");
const bottleListEl = mustFind<HTMLElement>("#admin-bottle-list");
const addBottleButton = mustFind<HTMLButtonElement>("#admin-add-bottle");
const importInventoryButton = mustFind<HTMLButtonElement>("#admin-import-inventory");
// Owner alpha-review feedback (Amendment 2026-08-21, fourth): filter
// chips above the bottle list, by shelf tier and by liquor category.
const bottleFilterShelfTierEl = mustFind<HTMLElement>("#admin-bottle-filter-shelf-tier");
const bottleFilterCategoryEl = mustFind<HTMLElement>("#admin-bottle-filter-category");
const bottleFilterKindEl = mustFind<HTMLElement>("#admin-bottle-filter-kind");
const bulkEditStripEl = mustFind<HTMLElement>("#admin-bulk-edit-strip");

// TAX2-S1-F1 (docs/build-briefs/TAX2-S1-F1-tax-rate-settings.md; DEC-307): the Sales tax section.
// It replaced the one-field "Tax rate" editor that saved with this form. Every callback below
// reads live state when called, so nothing here is captured at module load.
const taxRateSection = mountTaxRateSection(mustFind<HTMLElement>("#admin-tax-rate-section"), {
  apiBaseUrl: () => session().apiBaseUrl,
  tenantId: () => session().tenantId,
  canManage: () => canChangeTaxRate(),
  // UX22/DEC-137: the bound is stated beside the field from the constant the validator uses.
  rangeText: () => percentBoundRangeText(0, MAX_TAX_RATE_BASIS_POINTS),
  combinedUnits: () => settingsTaxRateBasisPoints,
  zone: () => ({
    timeZone: businessDayStoredTimezone,
    unconfirmedUtc:
      businessDayConfirmedAt === null && businessDayStoredTimezone === DEFAULT_TIMEZONE,
  }),
  openLocationSettings: () => {
    void (async () => {
      if (!(await showSection("operations"))) return;
      document
        .querySelector<HTMLElement>("#admin-settings-business-day-timezone")
        ?.scrollIntoView({ block: "center", behavior: "smooth" });
    })();
  },
  onRateInEffect: (units) => {
    settingsTaxRateBasisPoints = units;
    // ONB1: recording a rate confirms it on the server; re-read the answer while the card still says not confirmed.
    if (taxRateConfirmKnown && taxRateConfirmedAt === null) {
      void refreshTaxRateConfirmation();
    }
  },
  isCurrent: () => sessionOrUndefined() !== undefined,
  newKey: () => crypto.randomUUID(),
  confirmation: () =>
    taxRateConfirmKnown && taxRateConfirmedAt !== null
      ? { at: taxRateConfirmedAt, byName: taxRateConfirmedByName }
      : null,
  // LOCSET1 (B11): only a session that may change the rate is offered the confirmation, and only while it is unconfirmed.
  confirmAction: () =>
    taxRateConfirmKnown &&
    taxRateConfirmedAt === null &&
    currentSession !== undefined &&
    hasPermission(currentSession, "admin.tax_rate.manage")
      ? { busy: taxRateConfirmBusy, onConfirm: () => void confirmSalesTaxRate() }
      : null,
});
/** CARDNUDGE1: reads the two confirmations cardPaymentsMissingStep depends on, so the Open Tabs card can name what the
 * location still has to fill in without Settings having been opened first. Nothing is read for a session that may not
 * see settings; the step then stays unknown and the card says only that the account is being set up. */
export async function readCardPaymentsPrerequisites(activeSession: Session): Promise<void> {
  if (!hasPermission(activeSession, "admin.tenant_settings.view")) return;
  const result = await getSettings(activeSession.apiBaseUrl, activeSession.tenantId);
  if (sessionOrUndefined()?.token !== activeSession.token || result.status !== 200) return;
  businessDayConfirmedAt = result.body.settings.businessDayConfirmedAt ?? null;
  businessDayKnown = true;
  applyTaxRateConfirmation(result.body.settings);
}

/** CARDSETUP1: what this location must finish before card payments can start, with where to do it, or null when nothing
 * on this screen's side is missing. Shared by the Card payments section and the Open Tabs card (CARDNUDGE1). */
export function cardPaymentsMissingStep(): { line: string; linkLabel: string; open: () => void } | null {
  if (businessDayKnown && businessDayConfirmedAt === null) {
    return {
      line: "Card payments need the time zone and cutoff confirmed first.",
      linkLabel: "Confirm time zone and cutoff",
      open: () => {
        void (async () => {
          if (!(await showSection("operations"))) return;
          document
            .querySelector<HTMLElement>("#admin-settings-business-day-timezone")
            ?.scrollIntoView({ block: "center", behavior: "smooth" });
        })();
      },
    };
  }
  if (taxRateConfirmKnown && taxRateConfirmedAt === null) {
    return {
      line: "Card payments need the sales tax rate confirmed first.",
      linkLabel: "Confirm sales tax rate",
      open: () => {
        void (async () => {
          if (!(await showSection("money"))) return;
          document
            .querySelector<HTMLElement>("#admin-tax-rate-section")
            ?.scrollIntoView({ block: "center", behavior: "smooth" });
        })();
      },
    };
  }
  return null;
}
// PAY1 Slice 3 (docs/build-briefs/PAY1-S3-standup-onboarding.md): Settings > Tabs and payment > Card payments, drawn by
// ui/src/cardPayments.ts. Every callback reads live state; the section hides itself when the feature is off.
const cardPaymentsSection = mountCardPaymentsSection(
  mustFind<HTMLElement>("#admin-card-payments-section"),
  {
    apiBaseUrl: () => session().apiBaseUrl,
    tenantId: () => session().tenantId,
    canView: () => hasPermission(session(), "admin.terminal.view"),
    canOnboard: () => hasPermission(session(), "admin.payments.onboard"),
    isCurrent: () => sessionOrUndefined() !== undefined,
    // CARDSETUP1: card payments cannot start until the time zone and cutoff, then the sales tax rate, are confirmed.
    missingStep: cardPaymentsMissingStep,
    beforeOpen: () => {
      // STRIPETAB1: Stripe opens in its own tab. ONB4 slice 3: Settings is what sends the browser this time, so the
      // return belongs to Settings, not the wizard; cleared before the tab opens so the new tab does not inherit it.
      clearStripeReturnMarker(window.sessionStorage);
    },
  },
);
// PAYEXPORT1 (docs/build-briefs/PAYEXPORT1-gusto-tips-connection.md): Settings > Connections > Payroll (Gusto), drawn by ui/src/payrollGusto.ts.
// Every callback reads live state; the section hides itself when the feature is off or the person holds neither payroll permission.
const payrollSection = mountPayrollSection(mustFind<HTMLElement>("#admin-payroll-section"), {
  apiBaseUrl: () => session().apiBaseUrl,
  tenantId: () => session().tenantId,
  isCurrent: () => sessionOrUndefined() !== undefined,
  navigate: (url) => {
    // Only the address the server minted for Connect: Gusto's own page (https) or, in the simulated environment, this app.
    let target: URL;
    try {
      target = new URL(url);
    } catch {
      return;
    }
    if (target.protocol !== "https:" && !(target.protocol === "http:" && target.hostname === window.location.hostname)) return;
    window.location.assign(target.toString());
  },
});
// BANK1 (DEC-509): Settings > Network and billing > Bank accounts, in the Billing page's slot (BILLSTMT1), drawn by ui/src/bankAccounts.ts. The Royalties row is PAY2-S6's
// authorization (it used to be a section of its own). Every callback reads live state; the card hides itself unless the server
// offers a row.
// The statements card hides itself when it has nothing to show (a franchisee on its franchisor's bill, billing not set up), and it
// would take the slot with it, so the slot sits right after the card in the same Billing panel instead of inside it.
billingStatementsCard.root.after(billingStatementsCard.bankSlot);
const billingAdminOnlyNote = document.createElement("p");
billingAdminOnlyNote.className = "rl-muted";
billingAdminOnlyNote.dataset["testid"] = "billing-admin-only-note";
billingAdminOnlyNote.textContent =
  "Billing, invoices and card account setup are handled by your Admin. Ask them if you need an invoice or a change to the plan.";
billingAdminOnlyNote.hidden = true;
panelNetwork.prepend(billingAdminOnlyNote);
const bankAccountsSection = mountBankAccountsSection(
  mustFind<HTMLElement>("#billing-bank-accounts-slot"),
  {
    apiBaseUrl: () => session().apiBaseUrl,
    tenantId: () => session().tenantId,
    canView: () => hasPermission(session(), "admin.terminal.view"),
    canOnboard: () => hasPermission(session(), "admin.payments.onboard"),
    isCurrent: () => sessionOrUndefined() !== undefined,
    navigate: (_which, url) => {
      // Settings is what sends the browser this time, so the return belongs to Settings, not the wizard.
      clearStripeReturnMarker(window.sessionStorage);
      clearRoyaltyReturnMarker(window.sessionStorage);
      clearSubscriptionDebitReturnMarker(window.sessionStorage);
      window.location.assign(url);
    },
    openExternal: (url) => {
      window.open(url, "_blank", "noopener,noreferrer");
    },
  },
);
// TERM8-F1 (DEC-335): Settings > Tabs and payment > Card reader tips, drawn by ui/src/readerTips.ts. Every callback
// reads live state.
const readerTipsSection = mountReaderTipsSection(
  mustFind<HTMLElement>("#admin-reader-tips-section"),
  {
    apiBaseUrl: () => session().apiBaseUrl,
    tenantId: () => session().tenantId,
    canView: () => hasPermission(session(), "admin.tenant_settings.view"),
    canManage: () => hasPermission(session(), "admin.location_settings.manage"),
    deviceLocation: () => {
      const config = loadTerminalConfig();
      return config?.locationId !== undefined && config.locationName !== undefined
        ? { id: config.locationId, name: config.locationName }
        : undefined;
    },
    isCurrent: () => sessionOrUndefined() !== undefined,
    toast: (message, variant) => adminToast(message, variant),
    // TABSPAY1: the tip choices are edited under Tip presets in Money and pricing; the link shows only to a session that can open it.
    openTipChoices: () =>
      tabMoney.hidden
        ? undefined
        : () => {
            tabMoney.click();
            document.getElementById("admin-tip-presets-heading")?.scrollIntoView({ block: "start" });
          },
  },
);
// DEPCHECKUI1 (DEC-612): the System checks card at the top of Settings, drawn by ui/src/systemChecks.ts. The red strip under the
// header lives in main.ts and hears of every answer through onSystemChecksResult. A const array, so there is no new
// module-level state for invalidate() to reset; the card resets itself in invalidate().
const systemChecksListeners: ((view: LocationSystemChecksDto) => void)[] = [];
const systemChecksCard = mountSystemChecksCard(
  mustFind<HTMLElement>("#admin-system-checks"),
  {
    apiBaseUrl: () => session().apiBaseUrl,
    tenantId: () => session().tenantId,
    canView: () => {
      const active = sessionOrUndefined();
      return active !== undefined && hasPermission(active, "admin.tenant_settings.view");
    },
    deviceLocationId: () => loadTerminalConfig()?.locationId,
    isCurrent: () => sessionOrUndefined() !== undefined,
  },
  { onResult: (view) => systemChecksListeners.forEach((listener) => listener(view)) },
);
export function onSystemChecksResult(listener: (view: LocationSystemChecksDto) => void): void {
  systemChecksListeners.push(listener);
}
/** The red strip's link: opens Settings and brings the System checks card into view. */
export async function openSystemChecksInSettings(activeSession: Session): Promise<void> {
  await openAdminScreen(activeSession);
  await systemChecksCard.refresh();
  systemChecksCard.show();
}
// RCPT1-S7, UX127: Settings > Devices > Receipt printers, drawn by ui/src/printers.ts. Every callback reads live state.
const printersSection = mountPrintersSection(mustFind<HTMLElement>("#admin-printers-section"), {
  apiBaseUrl: () => session().apiBaseUrl,
  tenantId: () => session().tenantId,
  canManage: () => hasPermission(session(), "printer.manage"),
  deviceLocation: () => {
    const config = loadTerminalConfig();
    return config?.locationId !== undefined && config.locationName !== undefined
      ? { id: config.locationId, name: config.locationName }
      : undefined;
  },
  isCurrent: () => sessionOrUndefined() !== undefined,
  newKey: () => crypto.randomUUID(),
  lockScroll: () => lockBodyScroll(),
  unlockScroll: () => unlockBodyScroll(),
});
// ID1 (PRD P0.10): Settings > Location > Service, the location's ID Verified switch, drawn by ui/src/idVerifySetting.ts.
const idVerifySection = mountIdVerifySetting(mustFind<HTMLElement>("#admin-id-verify-section"), {
  apiBaseUrl: () => session().apiBaseUrl,
  tenantId: () => session().tenantId,
  canView: () => hasPermission(session(), "admin.tenant_settings.view"),
  canManage: () => hasPermission(session(), "admin.location_settings.manage"),
  deviceLocation: () => {
    const config = loadTerminalConfig();
    return config?.locationId !== undefined && config.locationName !== undefined
      ? { id: config.locationId, name: config.locationName }
      : undefined;
  },
  isCurrent: () => sessionOrUndefined() !== undefined,
});
// RCPT2 (DEC-403): Settings > Location > Receipts, the location's receipt mode, drawn by ui/src/receiptSetting.ts.
const receiptSettingSection = mountReceiptSetting(mustFind<HTMLElement>("#admin-receipt-section"), {
  apiBaseUrl: () => session().apiBaseUrl,
  tenantId: () => session().tenantId,
  canView: () => hasPermission(session(), "admin.tenant_settings.view"),
  canManage: () => hasPermission(session(), "admin.location_settings.manage"),
  deviceLocation: () => {
    const config = loadTerminalConfig();
    return config?.locationId !== undefined && config.locationName !== undefined
      ? { id: config.locationId, name: config.locationName }
      : undefined;
  },
  isCurrent: () => sessionOrUndefined() !== undefined,
});
// CASH1-S2-U1: Settings > Location > Cash, the location's Cash switch, drawn by ui/src/cashSetting.ts.
const cashSettingSection = mountCashSetting(mustFind<HTMLElement>("#admin-cash-section"), {
  apiBaseUrl: () => session().apiBaseUrl,
  tenantId: () => session().tenantId,
  canView: () => hasPermission(session(), "admin.tenant_settings.view"),
  canManage: () => hasPermission(session(), "admin.location_settings.manage"),
  deviceLocation: () => {
    const config = loadTerminalConfig();
    return config?.locationId !== undefined && config.locationName !== undefined
      ? { id: config.locationId, name: config.locationName }
      : undefined;
  },
  isCurrent: () => sessionOrUndefined() !== undefined,
});
// ACCTEXPORT1: Settings > Tabs and payment > Accounting (QuickBooks), drawn by ui/src/accounting.ts. Hidden while the feature is off
// (the server answers 404) and for a session that may not view settings.
const accountingSection = mountAccountingSection(mustFind<HTMLElement>("#admin-accounting-section"), {
  apiBaseUrl: () => session().apiBaseUrl,
  tenantId: () => session().tenantId,
  canView: () => hasPermission(session(), "admin.tenant_settings.view"),
  canManage: () => hasPermission(session(), "admin.location_settings.manage"),
  deviceLocation: () => {
    const config = loadTerminalConfig();
    return config?.locationId !== undefined && config.locationName !== undefined
      ? { id: config.locationId, name: config.locationName }
      : undefined;
  },
  isCurrent: () => sessionOrUndefined() !== undefined,
  navigate: (url) => window.location.assign(url),
});
// AUTOPAR1: Settings > Inventory items, Automatic par levels (the switch, each vendor's order pace, the Calculation steppers)
// and the Par levels tab, drawn by ui/src/autoPar.ts. Every callback reads live state.
// LOCSET1 (B13, DEC-532): Import, the automatic par settings and Recalculate sit behind one Setup menu beside Add item.
const inventoryHeaderActions = mustFind<HTMLElement>("#admin-bottle-header-actions");
const inventorySetup = createSetupDisclosure("admin-inventory-setup", [mustFind<HTMLButtonElement>("#admin-import-inventory")]);
inventoryHeaderActions.prepend(inventorySetup);
const autoParSection = mountAutoPar(
  {
    group: panelInventory,
    tabs: mustFind<HTMLElement>("#admin-autopar-tabs"),
    settings: mustFind<HTMLElement>("#admin-autopar-settings"),
    levels: mustFind<HTMLElement>("#admin-autopar-levels"),
    sheetHost: mustFind<HTMLElement>("#admin-screen"),
    inventoryActions: inventoryHeaderActions,
    inventorySetupMenu: inventorySetup.querySelector<HTMLElement>(".setup-disclosure-menu") ?? inventoryHeaderActions,
    vendorFilter: mustFind<HTMLElement>("#admin-bottle-filter-vendor"),
  },
  {
    apiBaseUrl: () => session().apiBaseUrl,
    tenantId: () => session().tenantId,
    canView: () => hasPermission(session(), "inv.stock_state.view"),
    canManage: () => hasPermission(session(), "admin.liquor_product.manage"),
    canManageVendors: () => hasPermission(session(), "inv.vendor.manage"),
    deviceLocation: () => {
      const config = loadTerminalConfig();
      return config?.locationId !== undefined && config.locationName !== undefined
        ? { id: config.locationId, name: config.locationName }
        : undefined;
    },
    isCurrent: () => sessionOrUndefined() !== undefined,
    unifiedCatalog: {
      available: () => {
        const activeSession = sessionOrUndefined();
        return activeSession !== undefined && inventorySubsectionVisible(activeSession, "bottles") && hasPermission(activeSession, "admin.liquor_product.view");
      },
      changed: () => {
        if (sessionOrUndefined() === undefined) return;
        renderBottleList();
        // INVVENDOR1: the overview was reset (its read was refused) while a list read for its location was in flight. That read is
        // discarded as no longer current, so the list would stay on "Loading bottles" with nothing left to repaint it. Read it again for
        // the location the list now follows.
        // MAINRED11: not while invalidate() resets this section. The generation has already advanced but the session is not
        // cleared yet, so a read started here would carry the dead token and its failure toast would replace the signed-out one.
        if (adminTeardownRunning) return;
        if (!bottleListReady || bottleCatalogLoadedLocationId !== autoParSection.inventoryLocationId()) void loadBottles();
      },
      locationChanged: () => {
        if (sessionOrUndefined() === undefined) return;
        renderBottleList();
        void loadBottles();
      },
    },
  },
);
// SEC2-S3 (DEC-321): Settings > Devices (UX84, DEC-336), drawn by ui/src/tablets.ts. Every callback reads live state.
const tabletsSection = mountTabletsSection(tabletsHostEl, {
  apiBaseUrl: () => session().apiBaseUrl,
  tenantId: () => session().tenantId,
  canManage: () => hasPermission(session(), "admin.device.manage"),
  isCurrent: () => sessionOrUndefined() !== undefined,
  newKey: () => crypto.randomUUID(),
  lockScroll: () => lockBodyScroll(),
  unlockScroll: () => unlockBodyScroll(),
});
// LOC2, UX128: Settings > Location > Locations, drawn by ui/src/locations.ts. Every callback reads live state.
const locationsSection = mountLocationsSection(mustFind<HTMLElement>("#admin-locations-section"), {
  apiBaseUrl: () => session().apiBaseUrl,
  tenantId: () => session().tenantId,
  canAddLocations: () => hasPermission(session(), "admin.location.manage"),
  canListLocations: () => hasPermission(session(), "admin.user.view"),
  isCurrent: () => sessionOrUndefined() !== undefined,
  newKey: () => crypto.randomUUID(),
  onLocationsChanged: () => void refreshAddressLocations(),
});
const settingsTipPresetsEl = mustFind<HTMLElement>("#admin-settings-tip-presets");
const settingsTipPresetInput = mustFind<HTMLInputElement>("#admin-settings-tip-preset-input");
const settingsTipPresetAddButton = mustFind<HTMLButtonElement>("#admin-settings-tip-preset-add");
const settingsRoundingModeEl = mustFind<HTMLElement>("#admin-settings-rounding-mode");
// Inventory track Slice 3, Amendment 2026-08-22 (thirteenth).
const settingsPricingModelEl = mustFind<HTMLElement>("#admin-settings-pricing-model");
const settingsStandardPourOuncesEl = mustFind<HTMLInputElement>("#admin-settings-standard-pour-ounces");
const settingsShelfTiersEl = mustFind<HTMLElement>("#admin-settings-shelf-tiers");
// UX22/DEC-137 slice 5: appends the bound this paragraph's static text
// (index.html) did not previously state. Covers both the rename field on
// each existing tier row and the Add row's own create field, since both
// are rendered as children of settingsShelfTiersEl by renderShelfTierRows
// below and the bound is identical for both. Full-mirror database CHECK
// (tenant_vocabulary_values_label_check, migration 0044 -- the same
// constraint every other vocabulary-label field already shares), covered
// by test/settings-bounds-db-check-agreement.test.ts.
// INV27 (docs/build-briefs/INV27-item-editor-redesign.md; DEC-227 item 3):
// the "Default target markup %, by item kind" card's own row list, lives
// in Settings > Inventory's own group panel (index.html, coordinator
// correction 2026-09-29), populated by renderTargetMarkupCard below.
const targetMarkupsListEl = mustFind<HTMLElement>("#admin-target-markups-list");
const settingsShelfTiersHelpEl = mustFind<HTMLElement>("#admin-settings-shelf-tiers-help");
settingsShelfTiersHelpEl.textContent =
  "Names and prices for each tier, used to price a pour when pricing is Shelf. " +
  `A tier name can be up to ${maxLengthBoundText(MAX_VOCABULARY_LABEL_LENGTH)}.`;
// Settings rebuild SET1: one Save per group that carries tenant_settings
// fields (Money and pricing, Tabs and payment, Operations), rather than
// one shared button under a single monolithic panel. All three still
// call the SAME saveSettingsForm below, which still reads and submits
// every field regardless of which button was clicked -- no change to
// what gets validated or saved, only to where the trigger lives.
const settingsLoadRetryButton = mustFind<HTMLButtonElement>("#admin-settings-load-retry");
const settingsSaveButtonMoney = mustFind<HTMLButtonElement>("#admin-settings-save-money");
const settingsSaveStatusMoneyEl = mustFind<HTMLElement>("#admin-settings-save-status-money");
const settingsSaveButtonTabsPayment = mustFind<HTMLButtonElement>(
  "#admin-settings-save-tabspayment",
);
const settingsSaveStatusTabsPaymentEl = mustFind<HTMLElement>(
  "#admin-settings-save-status-tabspayment",
);
const settingsSaveButtonOperations = mustFind<HTMLButtonElement>("#admin-settings-save-operations");
const settingsSaveStatusOperationsEl = mustFind<HTMLElement>(
  "#admin-settings-save-status-operations",
);

// Operations group (SET1), the three "no screen" fields hard requirement
// 2 names: business day cutoff/timezone govern every closeout, and
// session inactivity is the third. All three already existed in
// tenant_settings with a working PATCH .../settings route; this is the
// first editor for any of them. UX86: session inactivity's card now lives
// in People and access, not here.
const settingsBusinessDayCutoffInput = mustFind<HTMLSelectElement>(
  "#admin-settings-business-day-cutoff",
);
const settingsBusinessDayTimezoneInput = mustFind<HTMLSelectElement>(
  "#admin-settings-business-day-timezone",
);
const settingsBusinessDayHelpEl = mustFind<HTMLElement>("#admin-settings-business-day-help");
const settingsBusinessDayTimezoneSearchInput = mustFind<HTMLInputElement>(
  "#admin-settings-business-day-timezone-search",
);

// CL6-F1-F3 (docs/build-briefs/CL6-F1-F3-timezone-required.md; DEC-281,
// DEC-286). The Business day card: the warning, the rail tag, the status and
// field tags, and the Confirm / Save changes actions. What each one shows is
// decided by ui/src/businessDayConfirm.ts; the server is what refuses live
// money, this only tells the person why and how to fix it.
const businessDayWarningEl = mustFind<HTMLElement>("#admin-business-day-warning");
const businessDayWarningNextEl = mustFind<HTMLElement>("#admin-business-day-warning-next");
const businessDayWarningLeadEl = mustFind<HTMLElement>("#admin-business-day-warning-lead");
const businessDayRailTagEl = mustFind<HTMLElement>("#admin-group-tab-operations-tag");
const businessDayStatusTagEl = mustFind<HTMLElement>("#admin-business-day-status");
const businessDayTimezoneTagEl = mustFind<HTMLElement>("#admin-business-day-timezone-tag");
const businessDayCutoffTagEl = mustFind<HTMLElement>("#admin-business-day-cutoff-tag");
const businessDayConfirmButton = mustFind<HTMLButtonElement>("#admin-business-day-confirm");
const businessDayConfirmReasonEl = mustFind<HTMLElement>("#admin-business-day-confirm-reason");
const businessDayConfirmLineEl = mustFind<HTMLElement>("#admin-business-day-confirm-line");
const businessDayConfirmedNoteEl = mustFind<HTMLElement>("#admin-business-day-confirmed-note");
const businessDayReadOnlyEl = mustFind<HTMLElement>("#admin-business-day-read-only");
// ONB1: the sales tax rate confirmation card (Money and pricing). Card payments stay off until an admin confirms the
// stored rate or records a new one, like the time zone above.
const adminSessionInactivityCardEl = mustFind<HTMLElement>("#admin-session-inactivity-card");
const sessionInactivitySaveButton = mustFind<HTMLButtonElement>("#admin-session-inactivity-save");
const sessionInactivitySaveStatusEl = mustFind<HTMLElement>(
  "#admin-session-inactivity-save-status",
);

// T14 (docs/backlog.md): the MyTime ambiguous-reconciliation queue, its
// own subsection inside the Operations panel, gated independently on
// guest.reconciliation.view/.resolve (T15, DEC-041 item 3; see
// reconciliationQueueVisible).
const reconciliationSectionEl = mustFind<HTMLElement>("#admin-reconciliation-section");
const reconciliationEmptyEl = mustFind<HTMLElement>("#admin-reconciliation-empty");
const reconciliationListEl = mustFind<HTMLElement>("#admin-reconciliation-list");

// MT1: the MyTime client-sync freshness check's own elements.
const mytimeSyncFreshnessSectionEl = mustFind<HTMLElement>("#admin-mytime-sync-freshness-section");
const mytimeSyncFreshnessBodyEl = mustFind<HTMLElement>("#admin-mytime-sync-freshness-body");

/* Owner ruling 2026-08-30: "we need to turn the timezone into a picklist.
 * You can not expect someone to get the formating perfect."
 *
 * THE LIST COMES FROM THE BROWSER'S OWN IANA DATA, not from a constant in
 * this file, and that is the load-bearing choice. The server validates by
 * asking Intl whether the zone is recognised
 * (src/domain/tenantSettings.ts's validateBusinessDayTimezone), so a
 * hand-maintained list here could offer a value the server then rejects,
 * or omit one it would have accepted. Reading the same source on both
 * sides means the picker cannot drift from what is acceptable.
 *
 * US ZONES FIRST, because this is a US franchise and a picker that opens
 * on Africa/Abidjan is a picker somebody scrolls past four hundred
 * entries in. The rest follow in one group, so a franchisee elsewhere is
 * not shut out; nothing is removed, only ordered.
 *
 * THE STORED VALUE IS SHOWN even when it is not in the generated list,
 * which is the retire-never-lose rule this codebase applies to every
 * other vocabulary. A location configured before this picker existed, or
 * on a browser with narrower ICU data, must still see what it is set to
 * rather than silently reading as the first alphabetical zone and being
 * saved as that on the next unrelated edit. */
function populateTimezoneSelect(
  currentValue: string,
  showPlaceholder = false,
  filter = "",
  picked = "",
): void {
  const supported =
    typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : [];
  const zones = supported.length > 0 ? [...supported] : [currentValue].filter((z) => z.length > 0);
  // CL6-F1-F3: UTC must always be pickable. A browser's own list may leave it
  // out, and once the default stops being preselected it is a real answer a
  // location can choose on purpose.
  if (!zones.includes(DEFAULT_TIMEZONE)) {
    zones.push(DEFAULT_TIMEZONE);
  }
  // UX133: options are built by the shared builder, "UTC-05:00 Eastern Time (New York)", United States first, then the
  // regions, by offset. The stored value is always shown (retire never lose), and the option value is the IANA id.
  const groups = buildZoneGroups(zones, { current: currentValue });
  // ONB1: type to filter. A zone matches when every typed word is inside its label or its identifier ("new york",
  // "eastern" and "-05" all find America/New_York). The picked zone is never filtered away: a select whose chosen
  // option disappears silently changes its value.
  const words = timezoneFilterWords(filter);
  const matches = (option: { value: string; label: string }): boolean => {
    const haystack = `${option.label} ${option.value}`.toLowerCase().replace(/[_/]/g, " ");
    return words.every((word) => haystack.includes(word));
  };
  settingsBusinessDayTimezoneInput.innerHTML = "";
  for (const group of groups) {
    const shown = group.options.filter(
      (option) => words.length === 0 || matches(option) || option.value === picked,
    );
    if (shown.length === 0) continue;
    const node = document.createElement("optgroup");
    node.label = group.label;
    for (const zone of shown) {
      const option = document.createElement("option");
      option.value = zone.value;
      option.textContent = zone.label;
      node.append(option);
    }
    settingsBusinessDayTimezoneInput.append(node);
  }

  // CL6-F1-F3 (design/principles.md rule 4: a required choice has no
  // preselected default). While the location is unconfirmed and still on the
  // default zone the picker opens on "Pick your time zone", not on UTC.
  if (showPlaceholder) {
    const placeholder = document.createElement("option");
    placeholder.value = "";
    placeholder.textContent = TIMEZONE_PLACEHOLDER_TEXT;
    placeholder.disabled = true;
    settingsBusinessDayTimezoneInput.prepend(placeholder);
  }
}

// CL6-F1-F3. What the server last said about the confirmation, kept so the
// card can be redrawn when a field changes without another request. Cleared
// by invalidate() so the next employee on a shared tablet never inherits it.
let businessDayConfirmedAt: string | null = null;
/** CARDSETUP1: true once a settings answer has set the confirmation above (null before that means unknown). */
let businessDayKnown = false;
let businessDayConfirmedByName: string | null = null;
let businessDayStoredTimezone: string = DEFAULT_TIMEZONE;
for (const choice of CLOSING_HOUR_CHOICES) {
  settingsBusinessDayCutoffInput.append(new Option(choice.label, choice.value));
}
/** Sets the cutoff list; a saved time that is not a whole hour from the list is added so it still shows. */
function setCutoffListValue(value: string): void {
  if (value !== "" && !Array.from(settingsBusinessDayCutoffInput.options).some((o) => o.value === value)) {
    settingsBusinessDayCutoffInput.append(new Option(cutoffText(value), value));
  }
  settingsBusinessDayCutoffInput.value = value;
}

/** The server's own column default (db/schema.ts, business_day_cutoff_time_local). */
const DEFAULT_CUTOFF_TIME = "03:00";
let businessDayStoredCutoff: string = DEFAULT_CUTOFF_TIME;

// ONB1. What the server last said about the sales tax confirmation, kept so the card can be redrawn without another
// request. Cleared by invalidate() so the next employee on a shared tablet never inherits it.
let taxRateConfirmedAt: string | null = null;
let taxRateConfirmedByName: string | null = null;
let taxRateConfirmKnown = false;

// LOCSET1 (B11): the confirmation is drawn inside the one Sales tax card by ui/src/taxRateSettings.ts, through the
// confirmAction dependency below. True while the Confirm command is in flight.
let taxRateConfirmBusy = false;

/** Redraws the Sales tax card, which carries the confirmation status and button. */
function renderTaxRateConfirm(): void {
  taxRateSection.renderConfirmation?.();
}

/** Writes the server's sales tax confirmation answer into the card. The one place that does. */
function applyTaxRateConfirmation(settings: {
  taxRateConfirmedAt?: string | null;
  taxRateConfirmedByName?: string | null;
}): void {
  if (settings.taxRateConfirmedAt === undefined) return;
  taxRateConfirmedAt = settings.taxRateConfirmedAt;
  taxRateConfirmedByName = settings.taxRateConfirmedByName ?? null;
  taxRateConfirmKnown = true;
  renderTaxRateConfirm();
}

/** The Confirm tap. */
async function confirmSalesTaxRate(): Promise<void> {
  const s = session();
  taxRateConfirmBusy = true;
  renderTaxRateConfirm();
  const gen = loadGeneration;
  try {
    const result = await confirmTaxRate(s.apiBaseUrl, s.tenantId);
    if (!loadIsCurrent(gen)) return;
    if (result.status !== 200 && result.status !== 201) {
      adminToast(result.body.message ?? "Could not confirm the sales tax rate.", "danger");
      return;
    }
    applyTaxRateConfirmation({
      taxRateConfirmedAt: result.body.taxRateConfirmedAt,
      taxRateConfirmedByName: result.body.taxRateConfirmedByName,
    });
    adminToast("Sales tax rate confirmed.", "success");
    // The setup checklist has a row for this; it reads done now.
    void loadSetupChecklist(s);
  } catch (error) {
    adminToast(
      error instanceof Error ? error.message : "Could not confirm the sales tax rate.",
      "danger",
    );
  } finally {
    taxRateConfirmBusy = false;
    renderTaxRateConfirm();
  }
}

/** Re-reads the settings answer for the confirmation alone (a rate was just recorded). */
async function refreshTaxRateConfirmation(): Promise<void> {
  const active = sessionOrUndefined();
  if (active === undefined) return;
  const gen = loadGeneration;
  const result = await getSettings(active.apiBaseUrl, active.tenantId);
  if (!loadIsCurrent(gen) || result.status !== 200) return;
  const wasConfirmed = taxRateConfirmedAt !== null;
  applyTaxRateConfirmation(result.body.settings);
  if (!wasConfirmed && taxRateConfirmedAt !== null) void loadSetupChecklist(active);
}

function canManageBusinessDay(): boolean {
  const active = currentSession;
  return active !== undefined && hasPermission(active, "admin.location_settings.manage");
}

/** Redraws the Business day card, the warning and the rail tag from the
 * confirmation state and the two fields' current values. */
function renderBusinessDayConfirm(): void {
  const view = businessDayConfirmView({
    confirmedAt: businessDayConfirmedAt,
    confirmedByName: businessDayConfirmedByName,
    storedTimezone: businessDayStoredTimezone,
    pickedTimezone: settingsBusinessDayTimezoneInput.value,
    cutoff: settingsBusinessDayCutoffInput.value,
    canManage: canManageBusinessDay(),
  });
  businessDayWarningEl.hidden = !view.showWarning;
  businessDayRailTagEl.hidden = !view.showRailTag;
  if (view.showWarning) {
    const onDefaults =
      businessDayStoredTimezone === DEFAULT_TIMEZONE &&
      settingsBusinessDayCutoffInput.value === DEFAULT_CUTOFF_TIME;
    businessDayWarningLeadEl.textContent = onDefaults
      ? "This location is still on the defaults, UTC and 3:00 AM. Every closeout, tip report and sales day is dated from these two settings, so a wrong one puts money on the wrong day."
      : "This location has not confirmed its time zone and cutoff. Every closeout, tip report and sales day is dated from these two settings, so a wrong one puts money on the wrong day.";
  }
  businessDayStatusTagEl.textContent = view.cardTag.text;
  businessDayStatusTagEl.classList.toggle("bd-tag-ok", view.cardTag.tone === "ok");
  businessDayTimezoneTagEl.textContent = DEFAULT_NOT_CONFIRMED_TEXT;
  businessDayTimezoneTagEl.hidden = !view.showTimezoneTag;
  businessDayCutoffTagEl.textContent = DEFAULT_NOT_CONFIRMED_TEXT;
  businessDayCutoffTagEl.hidden = !view.showCutoffTag;
  settingsBusinessDayTimezoneInput.classList.toggle(
    "bd-needs-pick",
    !view.confirmed && settingsBusinessDayTimezoneInput.value === "",
  );
  businessDayConfirmButton.hidden = !view.confirmButtonVisible;
  businessDayConfirmButton.disabled = !view.confirmButtonEnabled;
  const nothingToSave =
    settingsBusinessDayTimezoneInput.value === businessDayStoredTimezone &&
    settingsBusinessDayCutoffInput.value === businessDayStoredCutoff;
  // The same plain "why" line serves both blocked buttons: Confirm before a zone is
  // picked, and Save changes while nothing differs from what the server holds.
  const blockedReason =
    view.confirmDisabledReason.length > 0
      ? view.confirmDisabledReason
      : view.saveButtonVisible && nothingToSave
        ? CHANGE_TO_SAVE_TEXT
        : "";
  businessDayConfirmReasonEl.hidden = blockedReason.length === 0;
  businessDayConfirmReasonEl.textContent = blockedReason;
  businessDayConfirmLineEl.hidden = view.confirmLine.length === 0;
  businessDayConfirmLineEl.textContent = view.confirmLine;
  settingsSaveButtonOperations.hidden = !view.saveButtonVisible;
  // "Save changes" is live only when a field differs from what the server holds
  // (design/mockups/cl6-f1-f3-timezone-required.html shows it blocked at rest).
  settingsSaveButtonOperations.disabled = nothingToSave;
  businessDayWarningNextEl.textContent = view.warningNextLine;
  settingsBusinessDayTimezoneInput.disabled = view.fieldsDisabled;
  settingsBusinessDayTimezoneSearchInput.disabled = view.fieldsDisabled;
  settingsBusinessDayCutoffInput.disabled = view.fieldsDisabled;
  businessDayConfirmedNoteEl.hidden = view.confirmedNote.length === 0;
  businessDayConfirmedNoteEl.textContent = view.confirmedNote;
  businessDayReadOnlyEl.hidden = view.readOnlyLine.length === 0;
  businessDayReadOnlyEl.textContent = view.readOnlyLine;
}

/** Writes the server's business day answer into the card and the two fields.
 * The one place that does, so a load, a Confirm, a Save changes and the big
 * form's own Save can never disagree about whether the picker shows the
 * placeholder. */
/** The words typed into the time zone filter, lower case, empty words dropped. */
function timezoneFilterWords(filter: string): string[] {
  return filter
    .toLowerCase()
    .split(/\s+/)
    .filter((word) => word.length > 0);
}

settingsBusinessDayTimezoneSearchInput.addEventListener("input", () => {
  const picked = settingsBusinessDayTimezoneInput.value;
  const showPlaceholder =
    picked === "" &&
    businessDayConfirmedAt === null &&
    businessDayStoredTimezone === DEFAULT_TIMEZONE;
  populateTimezoneSelect(
    businessDayStoredTimezone,
    showPlaceholder,
    settingsBusinessDayTimezoneSearchInput.value,
    picked,
  );
  settingsBusinessDayTimezoneInput.value = picked;
  renderBusinessDayConfirm();
});

function applyBusinessDayFromSettings(settings: {
  businessDayTimezone: string;
  businessDayCutoffTimeLocal: string;
  businessDayConfirmedAt: string | null;
  businessDayConfirmedByName: string | null;
  taxRateConfirmedAt?: string | null;
  taxRateConfirmedByName?: string | null;
}): void {
  // ONB1: the sales tax confirmation rides on the same settings answer.
  applyTaxRateConfirmation(settings);
  businessDayConfirmedAt = settings.businessDayConfirmedAt ?? null;
  businessDayKnown = true;
  businessDayConfirmedByName = settings.businessDayConfirmedByName ?? null;
  businessDayStoredTimezone = settings.businessDayTimezone;
  businessDayStoredCutoff = settings.businessDayCutoffTimeLocal;
  const showPlaceholder =
    businessDayConfirmedAt === null && settings.businessDayTimezone === DEFAULT_TIMEZONE;
  settingsBusinessDayTimezoneSearchInput.value = "";
  populateTimezoneSelect(settings.businessDayTimezone, showPlaceholder);
  settingsBusinessDayTimezoneInput.value = showPlaceholder ? "" : settings.businessDayTimezone;
  setCutoffListValue(settings.businessDayCutoffTimeLocal);
  discountBusinessDayTimezone = settings.businessDayTimezone;
  settingsBusinessDayHelpEl.textContent = "";
  renderBusinessDayConfirm();
}
const settingsSessionInactivityInput = mustFind<HTMLInputElement>(
  "#admin-settings-session-inactivity",
);
// UX22/DEC-137 slice 3: this field's own description states its real
// bound, above the field, before anyone decides what to type. The
// ceiling here has no database CHECK backstop at all (settingsBounds.ts's
// own header), so this constant is the ceiling's only complete home; the
// floor's own database CHECK (migration 0019, >= 1) is deliberately not
// re-tested live here, per the same "no ceiling to agree against" reason
// tax rate and hold amount are excluded.
const settingsSessionInactivityHelpEl = mustFind<HTMLElement>(
  "#admin-settings-session-inactivity-help",
);
settingsSessionInactivityHelpEl.textContent =
  `Minutes of inactivity before a signed-in session is signed out automatically. Must be ` +
  `${boundRangeText(MIN_SESSION_INACTIVITY_MINUTES, MAX_SESSION_INACTIVITY_MINUTES, "minutes")}.`;
settingsSessionInactivityInput.min = String(MIN_SESSION_INACTIVITY_MINUTES);
settingsSessionInactivityInput.max = String(MAX_SESSION_INACTIVITY_MINUTES);

// PAY-LOC1 (docs/backlog.md) review finding: this location's own address,
// needed only so a real Stripe Terminal Location can be created. Its own
// independent Save, the same "not routed through the big form" shape
// savePinPolicy below already establishes.
const settingsAddressLine1Input = mustFind<HTMLInputElement>("#admin-settings-address-line1");
const settingsAddressLine2Input = mustFind<HTMLInputElement>("#admin-settings-address-line2");
const settingsAddressCityInput = mustFind<HTMLInputElement>("#admin-settings-address-city");
const settingsAddressStateInput = mustFind<HTMLSelectElement>("#admin-settings-address-state");
const settingsAddressPostalCodeInput = mustFind<HTMLInputElement>(
  "#admin-settings-address-postal-code",
);
const settingsAddressCountryInput = mustFind<HTMLSelectElement>("#admin-settings-address-country");
// AUTOFILL1: a password manager may write a full state name or lowercase code; both resolve to the option code.
bindAutofillSelect(settingsAddressStateInput);
bindAutofillSelect(settingsAddressCountryInput);
const settingsSaveButtonAddress = mustFind<HTMLButtonElement>("#admin-settings-save-address");
const settingsSaveStatusAddressEl = mustFind<HTMLElement>("#admin-settings-save-status-address");
// LOC3 (DEC-413, provisional): the Address card edits ONE location's address. The location select shows only when the customer
// has more than one location; the signed-in location is selected first.
const settingsAddressLocationRow = mustFind<HTMLElement>("#admin-settings-address-location-row");
const settingsAddressLocationSelect = mustFind<HTMLSelectElement>(
  "#admin-settings-address-location",
);
let addressLocations: LocationWithAddressDto[] = [];
const tenantPermitPanel = initTenantPermitPanel(mustFind<HTMLElement>("#admin-location-permits"),sessionOrUndefined);

// Settings redesign gap (migration 0043): PIN policy, per tenant.
// SET16-F1: the block itself, so it can carry UX18-F1's own per-block
// gate (see pinPolicyVisible below) the way admin-operations-tenant-
// settings already does; the fields below never had one.
const adminPinPolicyEl = mustFind<HTMLElement>("#admin-pin-policy");
const settingsPinMinLengthInput = mustFind<HTMLInputElement>("#admin-settings-pin-min-length");
const settingsPinMaxLengthInput = mustFind<HTMLInputElement>("#admin-settings-pin-max-length");
const settingsPinLockoutAttemptsInput = mustFind<HTMLInputElement>(
  "#admin-settings-pin-lockout-attempts",
);
const settingsPinLockoutMinutesInput = mustFind<HTMLInputElement>(
  "#admin-settings-pin-lockout-minutes",
);
const settingsSaveButtonPinPolicy = mustFind<HTMLButtonElement>("#admin-settings-save-pin-policy");

// UX22/DEC-137: this field's own description states its real bound, above
// the field, before anyone decides what to type. The number in each
// sentence below comes from settingsBounds.ts through boundRangeText, the
// same imported identifiers the HTML min/max attributes and savePinPolicy's
// own pre-check are set from a few lines down -- never a hand-typed digit a
// future bound change could leave stale. See settingsBounds.ts's own header
// for the one home this mechanism cannot reach (the database CHECK in
// migration 0043) and test/settings-bounds-db-check-agreement.test.ts for
// how that remaining gap is covered instead.
const settingsPinMinLengthHelpEl = mustFind<HTMLElement>("#admin-settings-pin-min-length-help");
const settingsPinMaxLengthHelpEl = mustFind<HTMLElement>("#admin-settings-pin-max-length-help");
const settingsPinLockoutAttemptsHelpEl = mustFind<HTMLElement>(
  "#admin-settings-pin-lockout-attempts-help",
);
const settingsPinLockoutMinutesHelpEl = mustFind<HTMLElement>(
  "#admin-settings-pin-lockout-minutes-help",
);
settingsPinMinLengthHelpEl.textContent =
  `Must be ${boundRangeText(PIN_LENGTH_MIN, PIN_LENGTH_MAX, "digits")}, and no longer than the ` +
  "longest PIN allowed below.";
settingsPinMaxLengthHelpEl.textContent =
  `Must be ${boundRangeText(PIN_LENGTH_MIN, PIN_LENGTH_MAX, "digits")}, and no shorter than the ` +
  "shortest PIN allowed above. A PIN generated for someone is always minted at this length.";
settingsPinLockoutAttemptsHelpEl.textContent = `Must be ${boundRangeText(PIN_LOCKOUT_ATTEMPTS_MIN, PIN_LOCKOUT_ATTEMPTS_MAX, "attempts")}.`;
settingsPinLockoutMinutesHelpEl.textContent =
  `Must be ${boundRangeText(PIN_LOCKOUT_MINUTES_MIN, PIN_LOCKOUT_MINUTES_MAX, "minutes")} (24 ` +
  "hours). A manager can always unlock someone immediately, whatever this is set to.";

// The same shared constants drive the browser's own spinner constraint, so
// the markup never carries a second, independently-hand-typed "4"/"12"/
// "3"/"20"/"1"/"1440" for a control this file already owns the bound of.
settingsPinMinLengthInput.min = String(PIN_LENGTH_MIN);
settingsPinMinLengthInput.max = String(PIN_LENGTH_MAX);
settingsPinMaxLengthInput.min = String(PIN_LENGTH_MIN);
settingsPinMaxLengthInput.max = String(PIN_LENGTH_MAX);
settingsPinLockoutAttemptsInput.min = String(PIN_LOCKOUT_ATTEMPTS_MIN);
settingsPinLockoutAttemptsInput.max = String(PIN_LOCKOUT_ATTEMPTS_MAX);
settingsPinLockoutMinutesInput.min = String(PIN_LOCKOUT_MINUTES_MIN);
settingsPinLockoutMinutesInput.max = String(PIN_LOCKOUT_MINUTES_MAX);

// Settings redesign gap (migration 0044): the tenant vocabularies. One
// descriptor per list, so the editor below is written once rather than
// once per list, which is why adding beer_pour_size (SET8) needed one
// VOCAB_EDITORS entry and no new editor code.
//
// This comment used to end "five as of WIN1's wine_pour_size (migration
// 0046)". That count was already wrong at seven before SET8 and would be
// wrong at eight after it. The number is not restated here: VOCAB_EDITORS
// below IS the list, and a comment that counts what the code already
// holds is a second copy that drifts, which this repository has been
// bitten by often enough to have a rule about it.
interface VocabEditor {
  vocabulary: string;
  listEl: HTMLElement;
  /** VOC1 (DEC-044). A numeric list has an amount button that opens the
   * shared pad and a unit picker; every other list has one text field
   * whose contents become both the label and, derived, the identifier. */
  amountButton: HTMLButtonElement | null;
  unitPicker: HTMLElement | null;
  displayInput: HTMLInputElement | null;
  addButton: HTMLButtonElement;
  statusEl: HTMLElement;
}

const VOCAB_EDITORS: readonly VocabEditor[] = [
  // ITEM1 Slice 2 (DEC-145 criterion 1): "adding a kind is a settings
  // change, never a schema change." item_kind gets the identical generic
  // editor every other non-numeric vocabulary here already gets, no new
  // editor code, matching this array's own header comment.
  ["item-kind", "item_kind"],
  ["container-type", "container_type"],
  // ITEM2 Slice 5 (docs/build-briefs/ITEM2-a-kind-sale-mechanism-is-picked-
  // in-settings.md Section 4's "Slice 5" paragraph (a); DEC-166). One flat,
  // LOCATION-WIDE list of pour volumes replaces the pour-size, wine-pour-
  // size, beer-pour-size and cider-pour-size editors that used to sit here
  // (each retired from this array below: the legacy vocabularies keep
  // their own rows as DATA, read-only, through item_kind_pour_volumes'
  // own migration-0120 backfill, until deploy 3 (ITEM2-F3) deletes them;
  // adding to them through this screen would add a row nothing reads any
  // more, since every read is assignment-only from this deploy). Which
  // volumes each kind actually pours is a separate per-kind assignment
  // (appendItemKindPourVolumesPicker below), not a property of this flat
  // list itself. Liquor's own legacy "Pour sizes" editor is retired here
  // too, on the coordinator's own instruction: under DEC-166 liquor's
  // shot sizes are assigned volumes exactly like every other kind's, and
  // adding a value through the legacy editor would create precisely the
  // orphan row ITEM2-F3's own deploy-3 cleanup has to guard against (a
  // pour_size row with no item_kind_pour_volumes assignment behind it,
  // since only the "Pour volumes" screen's own add route seeds one).
  ["pour-volume", "pour_volume"],
  // B4: the bottle Size field's own two lists (see index.html's comment
  // on these two admin-subsection blocks).
  ["liquor-bottle-size", "liquor_bottle_size"],
  ["serving-size", "serving_size"],
  ["drink-display-category", "drink_display_category"],
].map(([slug, vocabulary]) => ({
  vocabulary: vocabulary as string,
  listEl: mustFind<HTMLElement>(`#admin-vocab-${slug}-list`),
  amountButton: document.querySelector<HTMLButtonElement>(`#admin-vocab-${slug}-amount`),
  unitPicker: document.querySelector<HTMLElement>(`#admin-vocab-${slug}-unit-picker`),
  displayInput: document.querySelector<HTMLInputElement>(`#admin-vocab-${slug}-display`),
  addButton: mustFind<HTMLButtonElement>(`#admin-vocab-${slug}-add`),
  statusEl: mustFind<HTMLElement>(`#admin-vocab-${slug}-status`),
}));

// ITEM1 Slice 3 (DEC-147, DEC-153; VOC3's own gap closed): item_category
// does not fit VOCAB_EDITORS' shape above -- it is one vocabulary scoped
// by a kind picker, not one flat list -- so it gets its own small, bespoke
// editor rather than a ninth VOCAB_EDITORS entry, the same "not one more
// generic-editor row" precedent shelf_tier already set (see this file's
// own comment on shelf_tier near centsToAmountString).
const itemCategoryKindEl = mustFind<HTMLElement>("#admin-item-category-kind");
const itemCategoryListEl = mustFind<HTMLElement>("#admin-item-category-list");
const itemCategoryDisplayInput = mustFind<HTMLInputElement>("#admin-item-category-display");
const itemCategoryAddButton = mustFind<HTMLButtonElement>("#admin-item-category-add");
const itemCategoryStatusEl = mustFind<HTMLElement>("#admin-item-category-status");

// VOC1-F4 (docs/build-briefs/VOC1-F4-drink-modifier-catalog.md, Section
// 2.1). Also its own bespoke editor rather than a ninth VOCAB_EDITORS
// entry, for the identical reason shelf_tier and item_category already
// are: VOCAB_EDITORS' own generic renderVocabulary supports add and
// activate/deactivate only, never a rename or reprice of an existing row,
// and this catalog needs both (its own money fact, mirroring shelf_tier's
// row shape exactly per Section 1.6). Unlike shelf_tier this commits each
// edit IMMEDIATELY through the plain generic vocabulary routes
// (updateVocabularyValue/addVocabularyValue/setVocabularyValueActive),
// never batched into "Save location settings": drink_modifier is an
// ordinary tenant_vocabulary_values row, not a tenant_settings field, and
// shelf_tier's own batched-save integration (saveLocationSettingsWithShelfTiers)
// is a special case that route makes for shelf_tier alone.

// ITEM1 Slice 3: itemKindScope is optional on this client-side shape (most
// vocabularies never carry one) because a bare `Record<string, {...}[]>`
// with a required field would force every OTHER vocabulary's fixture and
// call site to also supply `itemKindScope: null` for no reason; only
// item_category rows ever populate it.
// ITEM2 Slice 2: saleMechanism is optional here for the identical reason
// itemKindScope already is above -- only item_kind rows ever populate it,
// and a bare Record with a required field would force every other
// vocabulary's fixture and call site to also supply
// `saleMechanism: null` for no reason.
// ITEM2 Slice 3 (QA-lead Ruling B). hasSoldLines IS now sent by GET
// .../vocabularies (src/domain/vocabularies.ts's listVocabulary/
// listAllVocabularies, migration 0116), so a fetched row already knows
// whether it is locked before any write is attempted. saleMechanismLocked
// stays CLIENT-SIDE STATE ONLY, set the moment a write attempt itself
// reports the lock (the route's own sold-lines refusal), so a row that
// gains its first sold line AFTER this screen's own last load still locks
// without a re-fetch, the same "the response already carries its new
// state" pattern immediately below for the happy path. Both are read
// together (see appendItemKindMechanismPicker's own `locked` below), so a
// row locks whichever way it was learned.
// ITEM2 Slice 4 deploy 2 / Slice 5 (DEC-166): assignedPourVolumeValues is
// optional here for the identical reason itemKindScope/saleMechanism
// already are above -- only item_kind rows ever populate it (liquor
// included), and a bare Record with a required field would force every
// other vocabulary's fixture and call site to also supply an empty array
// for no reason.
let adminVocabularies: Record<
  string,
  {
    value: string;
    label: string;
    active: boolean;
    itemKindScope?: string | null;
    saleMechanism?: string | null;
    saleMechanisms?: ProductSaleMechanism[];
    policyEnforcementActive?: boolean;
    saleMechanismLocked?: boolean;
    hasSoldLines?: boolean;
    assignedPourVolumeValues?: string[];
    /** P9-F2 (DEC-367): version of assignedPourVolumeValues as loaded. */
    pourVolumesVersion?: string;
    /** VOC1-F4 (Section 2.1; DEC-173). Cents, zero or positive, and ONLY
     * on the 'drink_modifier' vocabulary; optional for the identical
     * reason itemKindScope/saleMechanism already are above -- only
     * drink_modifier rows ever populate it. */
    priceAdjustmentCents?: number | null;
    /** KEGMATH1. Ounces in one keg, and ONLY on the 'keg_size' vocabulary; the
     * server already sends it on every row. */
    volumeOz?: string | null;
  }[]
> = {};
/** KEGMATH1: keg_size value -> volume_oz, read from the server's vocabulary rows (never a constant). */
function kegSizeVolumeOzByValue(): Map<string, string> {
  const byValue = new Map<string, string>();
  for (const entry of adminVocabularies["keg_size"] ?? []) {
    if (entry.volumeOz !== undefined && entry.volumeOz !== null) {
      byValue.set(entry.value, entry.volumeOz);
    }
  }
  return byValue;
}
/** ITEM2 Slice 2: one row out of adminVocabularies above, named so a
 * function reading one entry does not have to restate the inline shape. */
type AdminVocabularyEntry = (typeof adminVocabularies)[string][number];
// INV27 (docs/build-briefs/INV27-item-editor-redesign.md; DEC-227 item 3).
// Settings > Inventory's default target markup % per item kind, keyed by
// item_kind value; a kind with no configured default is simply absent from
// this map (never a fabricated 0), mirroring listItemKindTargetMarkups'
// own null-vs-absent-row contract server-side.
let itemKindTargetMarkupDefaults: Record<string, number> = {};
// INV27: whether the manager has typed their OWN value into the
// calculator's Target markup % input this sheet-open, for either column.
// Once true, the Settings default is never re-applied over it (DEC-224's
// own "a real user pick is never silently overwritten by a default" rule,
// applied here to markup instead of Container type).
let bottleCalcOzTargetMarkupTouched = false;
let bottleCalcBottleTargetMarkupTouched = false;
// ITEM1 Slice 3. Which kind's own category list the item-category editor
// is currently showing; null until the first render picks a default
// (the tenant's own first active, non-liquor item kind). Cleared by
// invalidate() below so a signed-out session does not leave a stale
// selection painted for the next employee on a shared tablet.
let itemCategoryEditorScope: string | null = null;
const settingsSaveStatusPinPolicyEl = mustFind<HTMLElement>(
  "#admin-settings-save-status-pin-policy",
);

// Adversarial gate correction (Finding 4), payments-track Slice 4
// (DEC-002): "Card holds and abandoned tabs" settings fields.
const settingsHoldAmountInput = mustFind<HTMLInputElement>("#admin-settings-hold-amount");
const settingsHoldIncrementThresholdInput = mustFind<HTMLInputElement>(
  "#admin-settings-hold-increment-threshold",
);
const settingsAbandonedTabGratuityInput = mustFind<HTMLInputElement>(
  "#admin-settings-abandoned-tab-gratuity",
);
const settingsAbandonedTabGratuityDisclosureInput = mustFind<HTMLInputElement>(
  "#admin-settings-abandoned-tab-gratuity-disclosure",
);
// DUALPRICE1: the dual pricing switch and its percent (two decimals, 0.00 to 4.00).
const settingsDualPricingInput = mustFind<HTMLInputElement>("#admin-settings-dual-pricing");
const settingsDualPricingPercentInput = mustFind<HTMLInputElement>(
  "#admin-settings-dual-pricing-percent",
);
// CARDCLOSE1 (DEC-555): whether the end-of-day batch captures and closes card tickets.
const settingsCloseCardTicketsInput = mustFind<HTMLInputElement>(
  "#admin-settings-close-card-tickets",
);
// TIPFULL1: whether the card reader suggests tips on the price before discounts.
const settingsTipOnPreDiscountInput = mustFind<HTMLInputElement>(
  "#admin-settings-tip-on-pre-discount",
);

// UX22/DEC-137 slice 2: each field's own bound belongs in its own
// description, above the field. Text set below from scripts/
// settingsBounds.ts's own constants through percentBoundRangeText/
// maxDollarsBoundText, never hand-typed, so a description cannot state a
// number the validator does not actually enforce. See settingsBounds.ts's
// own header for which of these five fields carry a live database CHECK
// this mechanism cannot reach (tax rate and hold amount are FLOOR-ONLY in
// the database) and test/settings-bounds-db-check-agreement.test.ts for
// the two that do (hold increment threshold, abandoned tab gratuity).
const settingsTipPresetsHelpEl = mustFind<HTMLElement>("#admin-settings-tip-presets-help");
const settingsHoldAmountHelpEl = mustFind<HTMLElement>("#admin-settings-hold-amount-help");
const settingsHoldIncrementThresholdHelpEl = mustFind<HTMLElement>(
  "#admin-settings-hold-increment-threshold-help",
);
const settingsAbandonedTabGratuityHelpEl = mustFind<HTMLElement>(
  "#admin-settings-abandoned-tab-gratuity-help",
);
settingsTipPresetsHelpEl.textContent =
  `Whole percents shown to guests on the tablet Pay screen and the card reader. Each preset must be ` +
  `${percentBoundRangeText(0, MAX_TIP_PRESET_BASIS_POINTS)}; the list may hold ` +
  `${boundRangeText(MIN_TIP_PRESET_COUNT, MAX_TIP_CHOICES_ON_SAVE, "presets")}.`;
settingsHoldAmountHelpEl.textContent = `Must be more than $0.00, up to ${maxDollarsBoundText(MAX_HOLD_AMOUNT_CENTS)}.`;
settingsHoldIncrementThresholdHelpEl.textContent =
  `Must be ${percentBoundRangeText(MIN_HOLD_INCREMENT_THRESHOLD_BASIS_POINTS, MAX_HOLD_INCREMENT_THRESHOLD_BASIS_POINTS)} ` +
  "of the hold amount.";
settingsAbandonedTabGratuityHelpEl.textContent =
  `Must be ${percentBoundRangeText(0, MAX_ABANDONED_TAB_GRATUITY_BASIS_POINTS)}. The charge never ` +
  "applies unless signage is confirmed posted below.";
// HIGFIX4A (F-24): the helper paragraphs above (and the section intros in index.html) are hover help on their label or title.
wireHelpInHover();

// Payments-track Slice 3: Settings > Payments > Terminals.
const settingsTerminalsEl = mustFind<HTMLElement>("#admin-settings-terminals");
const settingsTerminalNameInput = mustFind<HTMLInputElement>("#admin-settings-terminal-name-input");
const settingsTerminalRegisterButton = mustFind<HTMLButtonElement>(
  "#admin-settings-terminal-register",
);
const settingsTerminalStatusEl = mustFind<HTMLElement>("#admin-settings-terminal-status");
const settingsTerminalCodeInput = mustFind<HTMLInputElement>("#admin-settings-terminal-code-input");

// INV4: Settings > Purchasing > Vendors.
// VEN3: the whole block's own visibility container, gated independently
// of the tab (see purchasingVendorsVisible below).
const purchasingVendorsSectionEl = mustFind<HTMLElement>("#admin-purchasing-vendors-section");
const settingsVendorsEl = mustFind<HTMLElement>("#admin-settings-vendors");
const settingsVendorNameInput = mustFind<HTMLInputElement>("#admin-settings-vendor-name-input");
const settingsVendorAddButton = mustFind<HTMLButtonElement>("#admin-settings-vendor-add");
const settingsVendorStatusEl = mustFind<HTMLElement>("#admin-settings-vendor-status");

// PO2, owner ruling 2026-09-29 (DEC-247). Settings > Purchasing > Order
// approval. Own visibility container, same "own gate, independent of the
// tab" shape purchasingVendorsSectionEl immediately above already
// establishes (see purchasingApprovalThresholdVisible below).
const purchasingApprovalThresholdSectionEl = mustFind<HTMLElement>(
  "#admin-purchasing-approval-threshold-section",
);
const purchasingApprovalThresholdInput = mustFind<HTMLInputElement>(
  "#admin-purchasing-approval-threshold-input",
);
const purchasingApprovalThresholdSaveButton = mustFind<HTMLButtonElement>(
  "#admin-purchasing-approval-threshold-save",
);
const purchasingApprovalThresholdStatusEl = mustFind<HTMLElement>(
  "#admin-purchasing-approval-threshold-status",
);

// VEN7-F1 (docs/build-briefs/VEN7-F1-vendor-status-persistence.md;
// design/mockups/ven7-f1-vendor-save-error-persistence.html, option a,
// owner-approved 2026-09-29). settingsVendorStatusEl is ONE shared line
// under the whole Vendors list; every vendor-field commit, delivery-day
// toggle and minimum-order save reads and writes it. The old shape
// cleared it at the START of every save, including one already queued
// behind a failed one, so a manager could tap Wednesday half a second
// after Tuesday failed and watch Tuesday's own failure vanish unread.
// The fix: no call site clears this line pre-emptively anymore. It is
// written ONLY from a settled outcome, tracked by this pair so a later
// success knows whether it is safe to overwrite what is showing.
let vendorStatusIsError = false;
let vendorStatusErrorKey: string | undefined;

/** Identifies WHICH vendor+field a status message is about, so a later
 * success for the SAME thing that just failed can still clear it (a
 * retry that fixes the exact problem), while a success for something
 * else entirely leaves the still-unread failure exactly as it was. */
function vendorStatusKey(vendorId: string, field: string): string {
  return `${vendorId}:${field}`;
}

/** Writes a failure to the shared line and marks it sticky: nothing but
 * an explicit Dismiss, or a later success for this SAME key, clears it. */
function writeVendorStatusError(message: string, key: string): void {
  vendorStatusIsError = true;
  vendorStatusErrorKey = key;
  renderVendorStatusLine(message);
}

/** Called after a settled SUCCESS. Only overwrites (clears) the shared
 * line when nothing else is currently showing a failure, or when the
 * failure showing is for this exact same key -- never when a different
 * vendor or field's still-unread failure is on screen. */
function resolveVendorStatusSuccess(key: string): void {
  if (!vendorStatusIsError || vendorStatusErrorKey === key) {
    clearVendorStatus();
  }
}

/** The Dismiss control and the only other path that ever clears the
 * line: unconditional, regardless of whose failure is showing. */
function clearVendorStatus(): void {
  vendorStatusIsError = false;
  vendorStatusErrorKey = undefined;
  settingsVendorStatusEl.classList.remove("admin-vendor-status--error");
  settingsVendorStatusEl.classList.remove("admin-vendor-status--ok");
  settingsVendorStatusEl.innerHTML = "";
}

function renderVendorStatusLine(message: string): void {
  settingsVendorStatusEl.innerHTML = "";
  settingsVendorStatusEl.classList.remove("admin-vendor-status--ok");
  settingsVendorStatusEl.classList.add("admin-vendor-status--error");
  const text = document.createElement("span");
  text.textContent = message;
  const dismiss = document.createElement("button");
  dismiss.type = "button";
  dismiss.className = "admin-vendor-status-dismiss";
  dismiss.dataset["testid"] = "admin-settings-vendor-status-dismiss";
  dismiss.setAttribute("aria-label", "Dismiss");
  dismiss.textContent = "✕";
  dismiss.addEventListener("click", () => clearVendorStatus());
  settingsVendorStatusEl.append(text, dismiss);
}
// INV4: the Inventory item editor's own "Vendor offers" panel.
const bottleVendorOffersEl = mustFind<HTMLElement>("#admin-bottle-vendor-offers");
const bottleVendorOfferListEl = mustFind<HTMLElement>("#admin-bottle-vendor-offer-list");
// INVLOC1 Slice 5: the item's preferred vendor at each location, a table shown only when the person plans for more than one location.
const bottlePreferredByLocationEl = mustFind<HTMLElement>("#admin-bottle-preferred-by-location");
const bottleVendorOfferVendorSelect = mustFind<HTMLSelectElement>(
  "#admin-bottle-vendor-offer-vendor",
);
const bottleVendorOfferSkuInput = mustFind<HTMLInputElement>("#admin-bottle-vendor-offer-sku");
// VEN6: a fixed, five-option vocabulary (Bottle, Case, Keg, Can, Unit;
// see ORDER_UNIT_OPTIONS below), never free text -- an order unit is a
// physical-world constant, not a tenant preference, the same class as
// DELIVERY_DAY_OPTIONS elsewhere in this file.
const bottleVendorOfferOrderUnitInput = mustFind<HTMLSelectElement>(
  "#admin-bottle-vendor-offer-order-unit",
);
const bottleVendorOfferPackSizeInput = mustFind<HTMLInputElement>(
  "#admin-bottle-vendor-offer-pack-size",
);
const bottleVendorOfferCaseRow = mustFind<HTMLElement>("#admin-bottle-vendor-offer-case-row");
const bottleVendorOfferPackNounEl = mustFind<HTMLElement>("#admin-bottle-vendor-offer-pack-noun");
const bottleVendorOfferSizeNoteEl = mustFind<HTMLElement>("#admin-bottle-vendor-offer-size-note");
const bottleVendorOfferCostLabelEl = mustFind<HTMLElement>("#admin-bottle-vendor-offer-cost-label");
const bottleVendorOfferCostLineEl = mustFind<HTMLElement>("#admin-bottle-vendor-offer-cost-line");
const bottleVendorOfferCostInput = mustFind<HTMLInputElement>("#admin-bottle-vendor-offer-cost");
const bottleVendorOfferAddButton = mustFind<HTMLButtonElement>("#admin-bottle-vendor-offer-add");
const bottleVendorOfferStatusEl = mustFind<HTMLElement>("#admin-bottle-vendor-offer-status");

// M0: Settings > Void reasons.
const settingsVoidReasonsEl = mustFind<HTMLElement>("#admin-settings-void-reasons");
const settingsVoidReasonInput = mustFind<HTMLInputElement>("#admin-settings-void-reason-input");
const settingsVoidReasonAddButton = mustFind<HTMLButtonElement>("#admin-settings-void-reason-add");
const settingsVoidReasonStatusEl = mustFind<HTMLElement>("#admin-settings-void-reason-status");
const settingsDiscountsEl = mustFind<HTMLElement>("#admin-settings-discounts");
// TEN18: the "From <network name>" offers above the list. The control is built once; its calls read the current
// session each time, like every other write on this screen.
const networkOffers = createNetworkOffers({
  section: mustFind<HTMLElement>("#admin-network-offers"),
  title: mustFind<HTMLElement>("#admin-network-offers-title"),
  list: mustFind<HTMLElement>("#admin-network-offers-list"),
  status: mustFind<HTMLElement>("#admin-network-offers-status"),
  fetchOffers: () => {
    const s = session();
    return listNetworkOffers(s.apiBaseUrl, s.tenantId);
  },
  answer: (publicationId, kind) => {
    const s = session();
    return kind === "adopt"
      ? adoptNetworkOffer(s.apiBaseUrl, s.tenantId, publicationId)
      : declineNetworkOffer(s.apiBaseUrl, s.tenantId, publicationId);
  },
  onAdopted: () => void loadDiscounts(),
  onAnswered: () => window.dispatchEvent(new Event("network-offers-answered")),
  toast: (message, variant) => adminToast(message, variant),
  isCurrent: () => sessionOrUndefined() !== undefined,
});
// REC7-S4: the received versions and local recipes share one sorted list.
let unifiedRecipeOfferRows: HTMLElement[] = [];
const recipeOfferHost = document.createElement("section");
const recipeOfferTitle = document.createElement("h2");
const recipeOfferList = document.createElement("div");
const networkRecipeOffers = createNetworkRecipeOffers({
  source: "all",
  section: recipeOfferHost,
  title: recipeOfferTitle,
  list: recipeOfferList,
  status: mustFind<HTMLElement>("#admin-recipe-offers-status"),
  fetchOffers: () => {
    const s = session();
    return listNetworkRecipeOffers(s.apiBaseUrl, s.tenantId);
  },
  answer: (publicationId, kind) => {
    const s = session();
    return answerNetworkRecipeOffer(s.apiBaseUrl, s.tenantId, publicationId, kind);
  },
  fetchItems: () => {
    const s = session();
    return listLiquorProducts(s.apiBaseUrl, s.tenantId, { activeOnly: true });
  },
  map: (publicationId, body) => {
    const s = session();
    return mapNetworkRecipeOffer(s.apiBaseUrl, s.tenantId, publicationId, body);
  },
  clone: (publicationId, body) => {
    const s = session();
    return cloneNetworkRecipeOffer(s.apiBaseUrl, s.tenantId, publicationId, body);
  },
  onRender: (rows) => {
    unifiedRecipeOfferRows = rows;
    renderDrinkList();
  },
  onMapped: () => void loadDrinks(),
  onAnswered: () => window.dispatchEvent(new Event("network-offers-answered")),
  toast: (message, variant) => adminToast(message, variant),
  isCurrent: () => sessionOrUndefined() !== undefined,
  scope: () => {
    const current = sessionOrUndefined();
    return current === undefined ? null : JSON.stringify([current.token, current.tenantId, current.apiBaseUrl, current.userId, loadTerminalConfig()?.locationId, loadTerminalConfig()?.deviceId]);
  },
});
const recipeSharing = createRecipeSharing({
  panel: mustFind<HTMLElement>("#admin-recipe-sharing-panel"),
  status: mustFind<HTMLElement>("#admin-recipe-sharing-status"),
  formStatus: mustFind<HTMLElement>("#admin-drink-form-error"),
  fetch: async () => {
    const s = session();
    const [own, share, sources] = await Promise.all([
      listOwnRecipePublications(s.apiBaseUrl, s.tenantId),
      listRecipeShares(s.apiBaseUrl, s.tenantId),
      listRecipePublicationSources(s.apiBaseUrl, s.tenantId),
    ]);
    return { own, share, sources };
  },
  share: (id, body) => {
    const s = session();
    return shareSavedRecipe(s.apiBaseUrl, s.tenantId, id, body);
  },
  withdraw: (id) => {
    const s = session();
    return withdrawOwnedRecipePublication(s.apiBaseUrl, s.tenantId, id);
  },
  confirmWithdraw: (name) =>
    showConfirmSheet({
      title: `Withdraw ${name}?`,
      message: "",
      confirmLabel: "Withdraw shared version",
    }),
  onChange: () => renderDrinkList(),
  onShared: () => {
    void networkRecipeOffers.refresh();
    void loadDrinks();
  },
  toast: (message) => adminToast(message, "success"),
  isCurrent: () => sessionOrUndefined() !== undefined,
});
let recipeFormSharingSelected = false;
let recipeSharingSavePending = false;
let drinkFormGeneration = 0;
function selectRecipeFormTab(sharing: boolean): void {
  recipeFormSharingSelected = sharing;
  mustFind<HTMLElement>("#admin-recipe-main-panel").hidden = sharing;
  mustFind<HTMLElement>("#admin-recipe-sharing-panel").hidden = !sharing;
  mustFind<HTMLElement>("#admin-recipe-tab-recipe").setAttribute("aria-selected", String(!sharing));
  mustFind<HTMLElement>("#admin-recipe-tab-sharing").setAttribute("aria-selected", String(sharing));
  drinkFormSave.textContent = sharing ? "Share recipe" : "Save drink";
}
async function saveRecipeSharing(): Promise<void> {
  if (recipeSharingSavePending || editingDrinkId === undefined) return;
  recipeSharingSavePending = true;
  const formGen = drinkFormGeneration;
  const loadGen = loadGeneration;
  const sourceId = editingDrinkId;
  const current = () =>
    formGen === drinkFormGeneration &&
    loadIsCurrent(loadGen) &&
    editingDrinkId === sourceId &&
    !drinkFormScrim.hidden;
  try {
    if (hasPermission(session(), "admin.menu_drink.manage") && !(await saveDrinkForm(true))) return;
    if (!current()) return;
    drinkFormSave.disabled = true;
    if (await recipeSharing.save()) {
      if (current()) closeDrinkForm();
    }
  } finally {
    if (loadIsCurrent(loadGen)) recipeSharingSavePending = false;
    if (current()) drinkFormSave.disabled = false;
  }
}
// DSC22 (DEC-097): sort and filter chip-group containers, filled by
// renderDiscountFilterChips via the same renderBottleFilterChipGroup the
// Bottles and Drinks panels already use.
const discountSortEl = mustFind<HTMLElement>("#admin-discount-sort");
const discountFilterTypeEl = mustFind<HTMLElement>("#admin-discount-filter-type");
const discountFilterStateEl = mustFind<HTMLElement>("#admin-discount-filter-state");
// UX92: the popout is built here, before the lookups below that point into it.
mountDiscountAddSheet(mustFind<HTMLElement>("#admin-screen"));
const settingsDiscountNameInput = mustFind<HTMLInputElement>("#admin-settings-discount-name-input");
const settingsDiscountTypeInput = mustFind<HTMLSelectElement>(
  "#admin-settings-discount-type-input",
);
const settingsDiscountValueInput = mustFind<HTMLInputElement>(
  "#admin-settings-discount-value-input",
);
// UX100: the Add Discount form carries labels and values only. An out-of-range value is refused inline by the
// server, so no description sits above the field.
const settingsDiscountTicketsInput = mustFind<HTMLInputElement>(
  "#admin-settings-discount-tickets-input",
);
const settingsDiscountItemsInput = mustFind<HTMLInputElement>(
  "#admin-settings-discount-items-input",
);
const settingsDiscountApprovalInput = mustFind<HTMLInputElement>(
  "#admin-settings-discount-approval-input",
);
const settingsDiscountReasonInput = mustFind<HTMLInputElement>(
  "#admin-settings-discount-reason-input",
);
const settingsDiscountAddButton = mustFind<HTMLButtonElement>("#admin-settings-discount-add");
// UX92: the Add Discount popout. The inputs above live inside it; the button at the top of the
// list opens it, and settingsDiscountAddButton is its primary action.
const discountAddOpenButton = mustFind<HTMLButtonElement>("#admin-settings-discount-open");
const discountAddScrim = mustFind<HTMLElement>("#admin-discount-add-scrim");
const discountAddErrorEl = mustFind<HTMLElement>("#admin-discount-add-error");
const discountAddCloseButton = mustFind<HTMLButtonElement>("#admin-discount-add-close");
const discountAddCancelButton = mustFind<HTMLButtonElement>("#admin-discount-add-cancel");
const discountAddTitleEl = mustFind<HTMLElement>("#admin-discount-add-title");
// LOCSET1 (B9): the extra controls the same sheet shows while it edits a discount.
const discountEditRulesEl = mustFind<HTMLElement>("#admin-discount-edit-rules");
const discountEditAutoApplyInput = mustFind<HTMLInputElement>("#admin-settings-discount-auto-apply-input");
const discountEditClosesTicketInput = mustFind<HTMLInputElement>("#admin-settings-discount-closes-ticket-input");
const discountEditClosesTicketHint = mustFind<HTMLElement>("#admin-discount-closes-ticket-hint");
const discountEditMemberDailyCompEl = mustFind<HTMLElement>("#admin-discount-member-daily-comp");
const discountEditScopeEl = mustFind<HTMLElement>("#admin-discount-edit-scope");
const discountEditScheduleEl = mustFind<HTMLElement>("#admin-discount-edit-schedule");
const settingsDiscountStatusEl = mustFind<HTMLElement>("#admin-settings-discount-status");
const settingsDiscountReasonsEl = mustFind<HTMLElement>("#admin-settings-discount-reasons");
const settingsDiscountReasonNameInput = mustFind<HTMLInputElement>(
  "#admin-settings-discount-reason-name-input",
);
const settingsDiscountReasonAddButton = mustFind<HTMLButtonElement>(
  "#admin-settings-discount-reason-add",
);
const settingsDiscountReasonStatusEl = mustFind<HTMLElement>(
  "#admin-settings-discount-reason-status",
);

// M5 (DEC-282): Settings > Price change reasons and the closeout flag
// threshold, a structural twin of the discount-reasons block above.
const settingsPriceChangeReasonsEl = mustFind<HTMLElement>("#admin-settings-price-change-reasons");
const settingsPriceChangeReasonNameInput = mustFind<HTMLInputElement>(
  "#admin-settings-price-change-reason-name-input",
);
const settingsPriceChangeReasonAddButton = mustFind<HTMLButtonElement>(
  "#admin-settings-price-change-reason-add",
);
const settingsPriceChangeReasonStatusEl = mustFind<HTMLElement>(
  "#admin-settings-price-change-reason-status",
);
const settingsPriceChangeFlagInput = mustFind<HTMLInputElement>(
  "#admin-settings-price-change-flag-input",
);
const settingsPriceChangeFlagSaveButton = mustFind<HTMLButtonElement>(
  "#admin-settings-price-change-flag-save",
);
const settingsPriceChangeFlagStatusEl = mustFind<HTMLElement>(
  "#admin-settings-price-change-flag-status",
);

// PD2: the discount approval thresholds, two picks under one Manager approval card with one Save (UIFIX5).
const settingsDiscountApprovalPercentSelect = mustFind<HTMLSelectElement>(
  "#admin-settings-discount-approval-percent",
);
const settingsDiscountApprovalSaveButton = mustFind<HTMLButtonElement>(
  "#admin-settings-discount-approval-save",
);
const settingsDiscountApprovalAmountSelect = mustFind<HTMLSelectElement>(
  "#admin-settings-discount-approval-amount",
);
const settingsDiscountApprovalStatusEl = mustFind<HTMLElement>(
  "#admin-settings-discount-approval-status",
);

// AU14-F4, AU14-F14, AU14-F15 / DEC-276, DEC-316: Settings > the shared action
// reasons lists (reopen, Sold past an 86, reversal, discounts taken off), the same shape as the price change
// reasons block above, one editor per kind. The reopen editor keeps its element
// and test ids (admin-settings-reopen-reasons and friends); the others follow
// the same pattern from their slug.
interface ActionReasonEditor {
  readonly kind: ActionReasonKind;
  /** Builds every element id and test id: admin-settings-<slug>-reasons, admin-<slug>-reason-row. */
  readonly slug: string;
  readonly listEl: HTMLElement;
  readonly nameInput: HTMLInputElement;
  readonly addButton: HTMLButtonElement;
  readonly statusEl: HTMLElement;
  /** Plain words, in the editor's own voice. */
  readonly words: {
    readonly loadFailed: string;
    readonly empty: string;
    readonly needsName: string;
    readonly turnOffMessage: (name: string) => string;
    readonly backOnList: (label: string) => string;
    readonly offList: (label: string) => string;
    readonly renamed: (label: string) => string;
  };
}

function buildActionReasonEditor(
  kind: ActionReasonKind,
  slug: string,
  words: ActionReasonEditor["words"],
): ActionReasonEditor {
  return {
    kind,
    slug,
    listEl: mustFind<HTMLElement>(`#admin-settings-${slug}-reasons`),
    nameInput: mustFind<HTMLInputElement>(`#admin-settings-${slug}-reason-name-input`),
    addButton: mustFind<HTMLButtonElement>(`#admin-settings-${slug}-reason-add`),
    statusEl: mustFind<HTMLElement>(`#admin-settings-${slug}-reason-status`),
    words,
  };
}

const actionReasonEditors: readonly ActionReasonEditor[] = [
  buildActionReasonEditor("reopen", "reopen", {
    loadFailed: "Could not load reopen reasons.",
    empty: "No reopen reasons yet.",
    needsName: "A reopen reason needs a name.",
    turnOffMessage: (name) =>
      `Turning ${name} off takes it off the picker when someone reopens a ticket and leaves past reopens readable.`,
    backOnList: (label) => `${label} is back on the reopen list.`,
    offList: (label) => `${label} is off the reopen list. Past reopens still show it.`,
    renamed: (label) => `Renamed to ${label}. Past reopens keep the old wording.`,
  }),
  buildActionReasonEditor("stock_override", "stock-override", {
    loadFailed: "Could not load the Sold past an 86 reasons.",
    empty: "No Sold past an 86 reasons yet.",
    needsName: "A Sold past an 86 reason needs a name.",
    turnOffMessage: (name) =>
      `Turning ${name} off takes it off the picker when someone sells an 86'd item and leaves past 86 sales readable.`,
    backOnList: (label) => `${label} is back on the Sold past an 86 list.`,
    offList: (label) => `${label} is off the Sold past an 86 list. Past sales still show it.`,
    renamed: (label) => `Renamed to ${label}. Past 86 sales keep the old wording.`,
  }),
  buildActionReasonEditor("movement_reversal", "movement-reversal", {
    loadFailed: "Could not load reversal reasons.",
    empty: "No reversal reasons yet.",
    needsName: "A reversal reason needs a name.",
    turnOffMessage: (name) =>
      `Turning ${name} off takes it off the picker when someone reverses a stock movement and leaves past reversals readable.`,
    backOnList: (label) => `${label} is back on the reversal list.`,
    offList: (label) => `${label} is off the reversal list. Past reversals still show it.`,
    renamed: (label) => `Renamed to ${label}. Past reversals keep the old wording.`,
  }),
  buildActionReasonEditor("discount_removal", "discount-removal", {
    loadFailed: "Could not load the discounts taken off reasons.",
    empty: "No discounts taken off reasons yet.",
    needsName: "A discounts taken off reason needs a name.",
    turnOffMessage: (name) =>
      `Turning ${name} off takes it off the picker when someone takes a discount off and leaves past removals readable.`,
    backOnList: (label) => `${label} is back on the discounts taken off list.`,
    offList: (label) =>
      `${label} is off the discounts taken off list. Past removals still show it.`,
    renamed: (label) => `Renamed to ${label}. Past removals keep the old wording.`,
  }),
  buildActionReasonEditor("settlement_difference", "settlement-difference", {
    loadFailed: "Could not load the settlement difference reasons.",
    empty: "No settlement difference reasons yet.",
    needsName: "A settlement difference reason needs a name.",
    turnOffMessage: (name) =>
      `Turning ${name} off takes it off the picker when a manager finalizes a day with a settlement difference and leaves past closeouts readable.`,
    backOnList: (label) => `${label} is back on the settlement differences list.`,
    offList: (label) =>
      `${label} is off the settlement differences list. Past closeouts still show it.`,
    renamed: (label) => `Renamed to ${label}. Past closeouts keep the old wording.`,
  }),
  buildActionReasonEditor("drawer_variance", "drawer-variance", {
    loadFailed: "Could not load the drawer variance reasons.",
    empty: "No drawer variance reasons yet.",
    needsName: "A drawer variance reason needs a name.",
    turnOffMessage: (name) =>
      `Turning ${name} off takes it off the picker when someone closes a drawer with a difference and leaves past closes readable.`,
    backOnList: (label) => `${label} is back on the drawer variance list.`,
    offList: (label) => `${label} is off the drawer variance list. Past closes still show it.`,
    renamed: (label) => `Renamed to ${label}. Past closes keep the old wording.`,
  }),
  buildActionReasonEditor("drawer_paid_in", "drawer-paid-in", {
    loadFailed: "Could not load the paid in reasons.",
    empty: "No paid in reasons yet.",
    needsName: "A paid in reason needs a name.",
    turnOffMessage: (name) =>
      `Turning ${name} off takes it off the picker when someone records cash paid in and leaves past entries readable.`,
    backOnList: (label) => `${label} is back on the paid in list.`,
    offList: (label) => `${label} is off the paid in list. Past entries still show it.`,
    renamed: (label) => `Renamed to ${label}. Past entries keep the old wording.`,
  }),
  buildActionReasonEditor("drawer_paid_out", "drawer-paid-out", {
    loadFailed: "Could not load the paid out reasons.",
    empty: "No paid out reasons yet.",
    needsName: "A paid out reason needs a name.",
    turnOffMessage: (name) =>
      `Turning ${name} off takes it off the picker when someone records cash paid out and leaves past entries readable.`,
    backOnList: (label) => `${label} is back on the paid out list.`,
    offList: (label) => `${label} is off the paid out list. Past entries still show it.`,
    renamed: (label) => `Renamed to ${label}. Past entries keep the old wording.`,
  }),
  buildActionReasonEditor("drawer_drop", "drawer-drop", {
    loadFailed: "Could not load the drop reasons.",
    empty: "No drop reasons yet.",
    needsName: "A drop reason needs a name.",
    turnOffMessage: (name) =>
      `Turning ${name} off takes it off the picker when someone drops cash to the safe and leaves past drops readable.`,
    backOnList: (label) => `${label} is back on the drop list.`,
    offList: (label) => `${label} is off the drop list. Past drops still show it.`,
    renamed: (label) => `Renamed to ${label}. Past drops keep the old wording.`,
  }),
];

// REF1 (DEC-011): Settings > Refund reasons, a THIRD configurable reason
// list, structural twin of the discount-reasons block directly above.
const settingsRefundReasonsEl = mustFind<HTMLElement>("#admin-settings-refund-reasons");
const settingsRefundReasonInput = mustFind<HTMLInputElement>("#admin-settings-refund-reason-input");
const settingsRefundReasonAddButton = mustFind<HTMLButtonElement>(
  "#admin-settings-refund-reason-add",
);
const settingsRefundReasonStatusEl = mustFind<HTMLElement>("#admin-settings-refund-reason-status");
let settingsVoidReasons: VoidReasonDto[] = [];
let voidReasonReadSequence = 0;
let voidReasonMutationRevision = 0;
let voidReasonPendingWrites = 0;
let voidReasonListLoaded = false;
let settingsDiscounts: DiscountDto[] = [];
let localDiscountsAllowed: boolean | undefined;
let discountPolicyReadSequence = 0;
// DSC22 (DEC-097): the Discounts panel's own sort and filter, applied
// client-side over settingsDiscounts rather than as a server query (this
// is a location's whole configured discount catalog, not a paginated
// per-product inventory). "" is each group's own default chip: "Shop
// order" for sort (settingsDiscounts's own loaded sort_order, name
// sequence, unmodified), "All" for both filters. Reset in invalidate().
// UX88 (DEC-339): the Discounts list opens A to Z; "Shop order" stays a chip.
let discountSortDirection: "" | "name-asc" | "name-desc" = "name-asc";
let discountFilterMarkdownType: "" | "percentage" | "amount" = "";
let discountFilterActive: "" | "true" | "false" = "";
let settingsDiscountReasons: DiscountReasonDto[] = [];
let discountReasonReadSequence = 0;
let discountReasonMutationRevision = 0;
let discountReasonPendingWrites = 0;
let discountReasonListLoaded = false;
let settingsPriceChangeReasons: PriceChangeReasonDto[] = [];
// Each editor's loaded list, by kind. A const map cleared by invalidate().
const settingsActionReasons = new Map<ActionReasonKind, ActionReasonDto[]>();
let settingsRefundReasons: RefundReasonDto[] = [];
let refundReasonReadSequence = 0;
let refundReasonMutationRevision = 0;
let refundReasonPendingWrites = 0;
let refundReasonListLoaded = false;

// Amendment 2026-08-22 (ninth): the touch-first rename sheet.
const terminalRenameScrim = mustFind<HTMLElement>("#admin-terminal-rename-scrim");
const terminalRenameTitleEl = mustFind<HTMLElement>("#admin-terminal-rename-title");
const terminalRenameInput = mustFind<HTMLInputElement>("#admin-terminal-rename-input");
const terminalRenameErrorEl = mustFind<HTMLElement>("#admin-terminal-rename-error");
const terminalRenameCloseButton = mustFind<HTMLButtonElement>("#admin-terminal-rename-close");
const terminalRenameCancelButton = mustFind<HTMLButtonElement>("#admin-terminal-rename-cancel");
const terminalRenameSaveButton = mustFind<HTMLButtonElement>("#admin-terminal-rename-save");

// Inventory track (track/inventory) Slice 2: the Counts panel and its two
// sheets (the count-session walk/review flow, and the read-only history
// detail). See this file's own header for the touch-only rule this
// screen follows just like every other admin surface.
const countsSectionsListEl = mustFind<HTMLElement>("#admin-counts-sections-list");
const countsSectionNameInput = mustFind<HTMLInputElement>("#admin-counts-section-name-input");
const countsSectionAddButton = mustFind<HTMLButtonElement>("#admin-counts-section-add");
const countsStartButton = mustFind<HTMLButtonElement>("#admin-counts-start-button");
const countsStartEmptyEl = mustFind<HTMLElement>("#admin-counts-start-empty");
// CORE8: the open count belongs to someone else. Who holds it, and Take over.
const countsHeldNoteEl = mustFind<HTMLElement>("#admin-counts-held-note");
const countsTakeOverButton = mustFind<HTMLButtonElement>("#admin-counts-takeover-button");
const countSheetTakeOverButton = mustFind<HTMLButtonElement>("#admin-count-takeover-button");
const countsHistoryListEl = mustFind<HTMLElement>("#admin-counts-history-list");
// INV2 (docs/backlog.md, PRD P0.7).
const countsStartModeLabelEl = mustFind<HTMLElement>("#admin-counts-start-mode-label");
const countsStartModePickerEl = mustFind<HTMLElement>("#admin-counts-start-mode-picker");
// HIGFIX4A (F-22): what each mode means and what a count covers is hover help on the mode choice, not a line under the button.
hoverHelp(
  countsStartModePickerEl,
  "Blind count hides what the system expects until you review.",
  "A count lists every active item at this location.",
);

// INVCOUNT2-S2: the table-based count screen, the default way to count. Built by ui/src/countTable.ts.
const countTableMountEl = mustFind<HTMLElement>("#admin-count-table-screen");
const countTableViewButton = mustFind<HTMLButtonElement>("#admin-count-table-view-button");

const countSessionScrim = mustFind<HTMLElement>("#admin-count-session-scrim");
const countSessionCloseButton = mustFind<HTMLButtonElement>("#admin-count-session-close");
const countProgressEl = mustFind<HTMLElement>("#admin-count-progress");
const countProgressFillEl = mustFind<HTMLElement>("#admin-count-progress-fill");
const countErrorEl = mustFind<HTMLElement>("#admin-count-error");
const countWalkViewEl = mustFind<HTMLElement>("#admin-count-walk-view");
const countWalkEmptyEl = mustFind<HTMLElement>("#admin-count-walk-empty");
const countWalkContentEl = mustFind<HTMLElement>("#admin-count-walk-content");
const countSectionPickerEl = mustFind<HTMLElement>("#admin-count-section-picker");
const countProductNameEl = mustFind<HTMLElement>("#admin-count-product-name");
const countProductStatusEl = mustFind<HTMLElement>("#admin-count-product-status");
const countQuantityInput = mustFind<HTMLInputElement>("#admin-count-quantity-input");
const countUnitPickerEl = mustFind<HTMLElement>("#admin-count-unit-picker");
const countReviewViewEl = mustFind<HTMLElement>("#admin-count-review-view");
const countReviewEmptyEl = mustFind<HTMLElement>("#admin-count-review-empty");
const countReviewListEl = mustFind<HTMLElement>("#admin-count-review-list");
const countReviewHonestyNoteEl = mustFind<HTMLElement>("#admin-count-review-honesty-note");
const countReviewUncountedNoteEl = mustFind<HTMLElement>("#admin-count-review-uncounted-note");
const countWalkFooterEl = mustFind<HTMLElement>("#admin-count-walk-footer");
const countReviewFooterEl = mustFind<HTMLElement>("#admin-count-review-footer");
const countPrevButton = mustFind<HTMLButtonElement>("#admin-count-prev");
const countSkipButton = mustFind<HTMLButtonElement>("#admin-count-skip");
const countSaveNextButton = mustFind<HTMLButtonElement>("#admin-count-save-next");
const countGoReviewButton = mustFind<HTMLButtonElement>("#admin-count-go-review");
const countBackToWalkButton = mustFind<HTMLButtonElement>("#admin-count-back-to-walk");
const countCloseButton = mustFind<HTMLButtonElement>("#admin-count-close-button");

const countCloseConfirmScrim = mustFind<HTMLElement>("#admin-count-close-confirm-scrim");
const countCloseConfirmMessageEl = mustFind<HTMLElement>("#admin-count-close-confirm-message");
const countCloseConfirmListEl = mustFind<HTMLElement>("#admin-count-close-confirm-list");
const countCloseConfirmCancelButton = mustFind<HTMLButtonElement>(
  "#admin-count-close-confirm-cancel",
);
const countCloseConfirmConfirmButton = mustFind<HTMLButtonElement>(
  "#admin-count-close-confirm-confirm",
);

const countHistoryDetailScrim = mustFind<HTMLElement>("#admin-count-history-detail-scrim");
const countHistoryDetailTitleEl = mustFind<HTMLElement>("#admin-count-history-detail-title");
const countHistoryDetailCloseButton = mustFind<HTMLButtonElement>(
  "#admin-count-history-detail-close",
);
const countHistoryDetailEmptyEl = mustFind<HTMLElement>("#admin-count-history-detail-empty");
const countHistoryDetailListEl = mustFind<HTMLElement>("#admin-count-history-detail-list");

// INV2 (docs/backlog.md, PRD P0.7): assigned counter, reassignment, mode
// change, and the tap-a-tenth partial-bottle control.
const countAssignedCounterEl = mustFind<HTMLElement>("#admin-count-assigned-counter");
const countReassignButton = mustFind<HTMLButtonElement>("#admin-count-reassign-button");
const countBlindToggleButton = mustFind<HTMLButtonElement>("#admin-count-blind-toggle");
const countReassignPanelEl = mustFind<HTMLElement>("#admin-count-reassign-panel");
const countReassignSelect = mustFind<HTMLSelectElement>("#admin-count-reassign-select");
const countReassignErrorEl = mustFind<HTMLElement>("#admin-count-reassign-error");
const countReassignConfirmButton = mustFind<HTMLButtonElement>("#admin-count-reassign-confirm");
const countReassignCancelButton = mustFind<HTMLButtonElement>("#admin-count-reassign-cancel");

// Auth track (track/auth) Slice 1: Settings > Users. Manager-only, per
// Amendment 2026-08-21 (sixth); every mutating route below is enforced
// again server-side regardless of this panel's own visibility.
const userAddButton = mustFind<HTMLButtonElement>("#admin-user-add");
const userListEl = mustFind<HTMLElement>("#admin-user-list");

// The one-time temporary-PIN reveal dialog (createUser, resetPin), shared
// with nothing else: index.html's own comment on why it is a top-level
// scrim outside #admin-screen rather than nested under this panel.
// U1: the three-tab Edit User modal.
const userEditScrim = mustFind<HTMLElement>("#admin-user-edit-scrim");
const userEditTitleEl = mustFind<HTMLElement>("#admin-user-edit-title");
const userEditCloseButton = mustFind<HTMLButtonElement>("#admin-user-edit-close");
const userEditTabInfo = mustFind<HTMLButtonElement>("#admin-user-edit-tab-info");
const userEditTabLocations = mustFind<HTMLButtonElement>("#admin-user-edit-tab-locations");
const userEditTabPermissions = mustFind<HTMLButtonElement>("#admin-user-edit-tab-permissions");
const userEditPanelInfo = mustFind<HTMLElement>("#admin-user-edit-panel-info");
const userEditPanelLocations = mustFind<HTMLElement>("#admin-user-edit-panel-locations");
const userEditPanelPermissions = mustFind<HTMLElement>("#admin-user-edit-panel-permissions");
const userEditDisplayNameInput = mustFind<HTMLInputElement>("#admin-user-edit-display-name");
const userEditFirstNameInput = mustFind<HTMLInputElement>("#admin-user-edit-first-name");
const userEditLastNameInput = mustFind<HTMLInputElement>("#admin-user-edit-last-name");
const userEditEmailInput = mustFind<HTMLInputElement>("#admin-user-edit-email");
const userEditPhoneInput = mustFind<HTMLInputElement>("#admin-user-edit-phone");
// UX22/DEC-137 slice 4: each of the five profile fields' own description
// states its real bound (PROFILE_FIELD_MAX_LENGTH), above the field, on
// both this sheet and the Add User sheet below -- ten call sites total
// (MIN_CALL_SITES in scripts/check-settings-bound-description.ts), since
// the bound is identical on both forms and neither re-renders the other.
// No database CHECK exists for any of these four columns
// (settingsBounds.ts's own header), so this constant is each field's only
// complete-bound home.
const userEditDisplayNameHelpEl = mustFind<HTMLElement>("#admin-user-edit-display-name-help");
const userEditFirstNameHelpEl = mustFind<HTMLElement>("#admin-user-edit-first-name-help");
const userEditLastNameHelpEl = mustFind<HTMLElement>("#admin-user-edit-last-name-help");
const userEditEmailHelpEl = mustFind<HTMLElement>("#admin-user-edit-email-help");
const userEditPhoneHelpEl = mustFind<HTMLElement>("#admin-user-edit-phone-help");
userEditDisplayNameHelpEl.textContent =
  `The name on this person's sign-in tile. Everyone sees it, so keep it the name the team ` +
  `uses. Up to ${maxLengthBoundText(PROFILE_FIELD_MAX_LENGTH)}.`;
userEditFirstNameHelpEl.textContent = `Up to ${maxLengthBoundText(PROFILE_FIELD_MAX_LENGTH)}.`;
userEditLastNameHelpEl.textContent = `Up to ${maxLengthBoundText(PROFILE_FIELD_MAX_LENGTH)}.`;
userEditEmailHelpEl.textContent = `Up to ${maxLengthBoundText(PROFILE_FIELD_MAX_LENGTH)}.`;
userEditPhoneHelpEl.textContent = `Up to ${maxLengthBoundText(PROFILE_FIELD_MAX_LENGTH)}.`;
const userEditRolesEl = mustFind<HTMLElement>("#admin-user-edit-roles");
const userEditLocationsEl = mustFind<HTMLElement>("#admin-user-edit-locations");
// R7: the Access tab's readout (tiles, what was changed just for this person, every area).
const userEditAccessReadoutEl = mustFind<HTMLElement>("#admin-access-readout");
const userEditSheetEl = mustFind<HTMLElement>("#admin-user-edit-sheet");

// U7 (DEC-103, DEC-107): the Add user sheet. A new, purpose-built sheet
// nested inside the SAME #admin-user-edit-scrim backdrop as the Edit User
// sheet above (see that element's own comment in index.html for why: the
// chrome-ratchet gate caps the count of .scrim/.confirm-scrim elements
// with no slack, so this reuses the existing backdrop rather than adding
// one). Its own functions never call openEditUser/handleUpdateUser.
const addUserSheetEl = mustFind<HTMLElement>("#admin-add-user-sheet");
const addUserCloseButton = mustFind<HTMLButtonElement>("#admin-add-user-close");
const addUserPanelProfile = mustFind<HTMLElement>("#admin-add-user-panel-profile");
const addUserPanelPin = mustFind<HTMLElement>("#admin-add-user-panel-pin");
const addUserDisplayNameInput = mustFind<HTMLInputElement>("#admin-add-user-display-name");
const addUserFirstNameInput = mustFind<HTMLInputElement>("#admin-add-user-first-name");
const addUserLastNameInput = mustFind<HTMLInputElement>("#admin-add-user-last-name");
const addUserEmailInput = mustFind<HTMLInputElement>("#admin-add-user-email");
const addUserPhoneInput = mustFind<HTMLInputElement>("#admin-add-user-phone");
// UX64 (DEC-209): the five per-field "Up to 200 characters." paragraphs
// this sheet used to carry (the identical block still on the Edit User
// sheet above) are collapsed into this ONE shared line, freeing the
// vertical room DEC-209 asks back so the details step fits at 1280x800
// with no internal scroll. This is the ONE call site the Add User sheet
// now has for PROFILE_FIELD_MAX_LENGTH (the Edit User sheet's five above
// are unchanged); MIN_CALL_SITES.PROFILE_FIELD_MAX_LENGTH in
// scripts/check-settings-bound-description.ts is 6 (5 + 1), not 10.
const addUserSharedBoundHelpEl = mustFind<HTMLElement>("#admin-add-user-shared-bound-help");
addUserSharedBoundHelpEl.textContent =
  `Display name, first name, last name, email, and phone can each be up to ` +
  `${maxLengthBoundText(PROFILE_FIELD_MAX_LENGTH)}.`;
const addUserRolePickerEl = mustFind<HTMLElement>("#admin-add-user-role-picker");
const addUserProfileErrorEl = mustFind<HTMLElement>("#admin-add-user-profile-error");
const addUserCancelButton = mustFind<HTMLButtonElement>("#admin-add-user-cancel");
const addUserNextButton = mustFind<HTMLButtonElement>("#admin-add-user-next");
const addUserPinIntroEl = mustFind<HTMLElement>("#admin-add-user-pin-intro");
const addUserPinGenerateButton = mustFind<HTMLButtonElement>("#admin-add-user-pin-generate");
const addUserPinChooseButton = mustFind<HTMLButtonElement>("#admin-add-user-pin-choose");
const addUserPinChooseFields = mustFind<HTMLElement>("#admin-add-user-pin-choose-fields");
const addUserPinValueLabel = mustFind<HTMLElement>("#admin-add-user-pin-value-label");
const addUserPinValueInput = mustFind<HTMLInputElement>("#admin-add-user-pin-value");
const addUserPinConfirmInput = mustFind<HTMLInputElement>("#admin-add-user-pin-confirm");
const addUserPinErrorEl = mustFind<HTMLElement>("#admin-add-user-pin-error");
const addUserPinSaveButton = mustFind<HTMLButtonElement>("#admin-add-user-pin-save");
const addUserPinBackButton = mustFind<HTMLButtonElement>("#admin-add-user-pin-back");

// RT1: the role-template editor.
const rolesListEl = mustFind<HTMLElement>("#admin-roles-list");
const roleEditScrim = mustFind<HTMLElement>("#admin-role-edit-scrim");
const roleEditTitleEl = mustFind<HTMLElement>("#admin-role-edit-title");
const roleEditCloseButton = mustFind<HTMLButtonElement>("#admin-role-edit-close");
const roleEditHoldersEl = mustFind<HTMLElement>("#admin-role-edit-holders");
const roleEditPermissionGridEl = mustFind<HTMLElement>("#admin-role-edit-permission-grid");
const roleEditPermissionSearchEl = mustFind<HTMLInputElement>("#admin-role-permission-search");
const roleEditPermissionCountEl = mustFind<HTMLElement>("#admin-role-permission-count");
const roleEditSheetEl = mustFind<HTMLElement>("#admin-role-edit-sheet");
// R6 (DEC-313 ruling 2): role edits are staged; this location says how many are waiting and offers
// Discard and Review and save.
const roleEditSaveBarEl = mustFind<HTMLElement>("#admin-role-edit-savebar");
const roleEditSaveBarTextEl = mustFind<HTMLElement>("#admin-role-edit-savebar-text");
const roleEditDiscardButton = mustFind<HTMLButtonElement>("#admin-role-edit-discard");
const roleEditReviewButton = mustFind<HTMLButtonElement>("#admin-role-edit-review");

// RT2 / UX64 (DEC-209): the Add role popup, nested inside the SAME
// #admin-role-edit-scrim backdrop as the role editor above (see that
// element's own comment in index.html for why). #admin-role-add is now
// the button at the top of the section that OPENS this popup, not a
// submit control that reads an inline input's value.
const roleAddOpenButton = mustFind<HTMLButtonElement>("#admin-role-add");
const roleAddSheetEl = mustFind<HTMLElement>("#admin-role-add-sheet");
const roleAddCloseButton = mustFind<HTMLButtonElement>("#admin-role-add-close");
const roleAddCancelButton = mustFind<HTMLButtonElement>("#admin-role-add-cancel");
const roleAddConfirmButton = mustFind<HTMLButtonElement>("#admin-role-add-confirm");
const roleLabelInput = mustFind<HTMLInputElement>("#admin-role-label-input");
const roleAddStatusEl = mustFind<HTMLElement>("#admin-role-add-status");
const userEditActiveInput = mustFind<HTMLInputElement>("#admin-user-edit-active");
const userEditActiveNoteEl = mustFind<HTMLElement>("#admin-user-edit-active-note");
const userEditCancelButton = mustFind<HTMLButtonElement>("#admin-user-edit-cancel");
const userEditSaveButton = mustFind<HTMLButtonElement>("#admin-user-edit-save");
const userEditAccessNoteEl = mustFind<HTMLElement>("#admin-user-edit-access-note");
const userEditErrorEl = mustFind<HTMLElement>("#admin-user-edit-error");

const pinRevealScrim = mustFind<HTMLElement>("#pin-reveal-scrim");
const pinRevealTitleEl = mustFind<HTMLElement>("#pin-reveal-title");
const pinRevealValueEl = mustFind<HTMLElement>("#pin-reveal-value");
const pinRevealDismissButton = mustFind<HTMLButtonElement>("#pin-reveal-dismiss");

// T9: the generate-or-choose sheet Reset PIN now opens.
const resetPinScrim = mustFind<HTMLElement>("#admin-reset-pin-scrim");
const resetPinTitleEl = mustFind<HTMLElement>("#admin-reset-pin-title");
const resetPinGenerateButton = mustFind<HTMLButtonElement>("#admin-reset-pin-generate");
const resetPinChooseButton = mustFind<HTMLButtonElement>("#admin-reset-pin-choose");
const resetPinChooseFields = mustFind<HTMLElement>("#admin-reset-pin-choose-fields");
const resetPinValueLabel = mustFind<HTMLElement>("#admin-reset-pin-value-label");
const resetPinValueInput = mustFind<HTMLInputElement>("#admin-reset-pin-value");
const resetPinConfirmInput = mustFind<HTMLInputElement>("#admin-reset-pin-confirm");
const resetPinErrorEl = mustFind<HTMLElement>("#admin-reset-pin-error");
const resetPinSaveButton = mustFind<HTMLButtonElement>("#admin-reset-pin-save");
const resetPinCancelButton = mustFind<HTMLButtonElement>("#admin-reset-pin-cancel");

/** The user whose PIN the open reset sheet is about. Undefined whenever
 * the sheet is closed. */
let resetPinTarget: UserDto | undefined;

const drinkFormScrim = mustFind<HTMLElement>("#admin-drink-form-scrim");
const drinkFormTitle = mustFind<HTMLElement>("#admin-drink-form-title");
const drinkFormClose = mustFind<HTMLButtonElement>("#admin-drink-form-close");
const drinkNameInput = mustFind<HTMLInputElement>("#admin-drink-name");
const drinkCategorySelect = mustFind<HTMLSelectElement>("#admin-drink-category");
// UX33: cross-link to the Drink menu categories list under Menu > General.
const drinkCategoryLineEl = mustFind<HTMLElement>("#admin-drink-category-line");
const drinkBuildChargeInput = mustFind<HTMLInputElement>("#admin-drink-build-charge");
const drinkRequiresLiquorInput = mustFind<HTMLInputElement>("#admin-drink-requires-liquor");
const drinkNeedsRecipeEl = mustFind<HTMLElement>("#admin-drink-needs-recipe");
const drinkLiquorSlotsSection = mustFind<HTMLElement>("#admin-drink-liquor-slots-section");
// UX33: cross-link to the Liquor categories list under Menu > Liquor.
const drinkLiquorCategoriesLineEl = mustFind<HTMLElement>("#admin-drink-liquor-categories-line");
const drinkLiquorSlotsEl = mustFind<HTMLElement>("#admin-drink-liquor-slots");
// VOC1-F4 (Section 2.2). Edit-only (see drinkFormModifiers' own comment);
// resetDrinkForm/openAddDrinkForm hide this section entirely.
const drinkModifiersSection = mustFind<HTMLElement>("#admin-drink-modifiers-section");
// UX33: cross-link to the Drink modifiers list under Menu > General.
const drinkModifiersLineEl = mustFind<HTMLElement>("#admin-drink-modifiers-line");
const drinkModifiersEl = mustFind<HTMLElement>("#admin-drink-modifiers");
const drinkComponentsEl = mustFind<HTMLElement>("#admin-drink-components");
const drinkAddComponentButton = mustFind<HTMLButtonElement>("#admin-drink-add-component");
const drinkActiveRow = mustFind<HTMLElement>("#admin-drink-active-row");
const drinkActiveInput = mustFind<HTMLInputElement>("#admin-drink-active");
const drinkFormError = mustFind<HTMLElement>("#admin-drink-form-error");
const drinkNetworkSourceEl = mustFind<HTMLElement>("#admin-drink-network-source");
const drinkMethodEl = mustFind<HTMLElement>("#admin-drink-method");
const drinkFormSave = mustFind<HTMLButtonElement>("#admin-drink-form-save");

const bottleFormScrim = mustFind<HTMLElement>("#admin-bottle-form-scrim");
const bottleFormTitle = mustFind<HTMLElement>("#admin-bottle-form-title");
const bottleFormClose = mustFind<HTMLButtonElement>("#admin-bottle-form-close");
// INV26 Slice 2 (DEC-206 item 3): the three Backbar-style tabs. See
// switchBottleFormTab below.
type BottleFormTab = "drink" | "inventory" | "calculator";
const BOTTLE_FORM_TABS: readonly BottleFormTab[] = ["drink", "inventory", "calculator"];
const bottleTabButtons: Record<BottleFormTab, HTMLButtonElement> = {
  drink: mustFind<HTMLButtonElement>("#admin-bottle-tab-drink"),
  inventory: mustFind<HTMLButtonElement>("#admin-bottle-tab-inventory"),
  calculator: mustFind<HTMLButtonElement>("#admin-bottle-tab-calculator"),
};
const bottleTabPanels: Record<BottleFormTab, HTMLElement> = {
  drink: mustFind<HTMLElement>("#admin-bottle-panel-drink"),
  inventory: mustFind<HTMLElement>("#admin-bottle-panel-inventory"),
  calculator: mustFind<HTMLElement>("#admin-bottle-panel-calculator"),
};
// INV27 (docs/build-briefs/INV27-item-editor-redesign.md; DEC-227 item 2),
// superseding INV26 Slice 3's pour-cost-% framing. The Pricing tab's own
// elements. See renderBottleCalculator and saveBottleCalculatorPrice.
const bottleCalcUnavailableEl = mustFind<HTMLElement>("#admin-bottle-calc-unavailable");
const bottleCostTargetSectionEl = mustFind<HTMLElement>("#admin-bottle-cost-target-section");
const bottleCalcGridEl = mustFind<HTMLElement>("#admin-bottle-calc-grid");
const bottleCalcOzSavesBadgeEl = mustFind<HTMLElement>("#admin-bottle-calc-oz-saves-badge");
const bottleCalcBottleSavesBadgeEl = mustFind<HTMLElement>("#admin-bottle-calc-bottle-saves-badge");
// INV27 coordinator review, 2026-09-29: the column that does not save
// (e.g. per bottle for a per-ounce item) dims (styles.css's
// .calc-cell.dimmed) and shows a plain-language reason instead of
// reading like a real, live price. See renderBottleCalculator's own
// dimming block.
const bottleCalcOzNotUsedNoteEl = mustFind<HTMLElement>("#admin-bottle-calc-oz-not-used-note");
const bottleCalcBottleNotUsedNoteEl = mustFind<HTMLElement>(
  "#admin-bottle-calc-bottle-not-used-note",
);
const bottleCalcOzDimmableCells = [
  mustFind<HTMLElement>("#admin-bottle-calc-oz-cost-cell"),
  mustFind<HTMLElement>("#admin-bottle-calc-oz-markup-today-cell"),
  mustFind<HTMLElement>("#admin-bottle-calc-oz-target-markup-cell"),
  mustFind<HTMLElement>("#admin-bottle-calc-oz-new-price-cell"),
  mustFind<HTMLElement>("#admin-bottle-calc-oz-profit-cell"),
];
const bottleCalcBottleDimmableCells = [
  mustFind<HTMLElement>("#admin-bottle-calc-bottle-cost-cell"),
  mustFind<HTMLElement>("#admin-bottle-calc-bottle-markup-today-cell"),
  mustFind<HTMLElement>("#admin-bottle-calc-bottle-target-markup-cell"),
  mustFind<HTMLElement>("#admin-bottle-calc-bottle-new-price-cell"),
  mustFind<HTMLElement>("#admin-bottle-calc-bottle-profit-cell"),
];
const bottleCalcOzCostEl = mustFind<HTMLElement>("#admin-bottle-calc-oz-cost");
const bottleCalcBottleCostEl = mustFind<HTMLElement>("#admin-bottle-calc-bottle-cost");
const bottleCalcOzMarkupTodayEl = mustFind<HTMLElement>("#admin-bottle-calc-oz-markup-today");
const bottleCalcBottleMarkupTodayEl = mustFind<HTMLElement>(
  "#admin-bottle-calc-bottle-markup-today",
);
const bottleCalcOzTargetMarkupInput = mustFind<HTMLInputElement>(
  "#admin-bottle-calc-oz-target-markup",
);
const bottleCalcBottleTargetMarkupInput = mustFind<HTMLInputElement>(
  "#admin-bottle-calc-bottle-target-markup",
);
const bottleCalcOzNewPriceEl = mustFind<HTMLElement>("#admin-bottle-calc-oz-new-price");
const bottleCalcBottleNewPriceEl = mustFind<HTMLElement>("#admin-bottle-calc-bottle-new-price");
const bottleCalcOzProfitEl = mustFind<HTMLElement>("#admin-bottle-calc-oz-profit");
const bottleCalcBottleProfitEl = mustFind<HTMLElement>("#admin-bottle-calc-bottle-profit");
const bottleCalcHelpEl = mustFind<HTMLElement>("#admin-bottle-calc-help");
const bottleCalcSaveButton = mustFind<HTMLButtonElement>("#admin-bottle-calc-save");
const bottleCalcSaveReasonEl = mustFind<HTMLElement>("#admin-bottle-calc-save-reason");
// INV27 coordinator review, 2026-09-29: the INV26 Slice 2 read-only
// Costing-group display (SKU/Unit cost/Units-per-case/Case cost/Vendor,
// plus its own "Manage vendor offers" jump) removed entirely. It
// duplicated the Vendor offers list immediately below it on this same
// Stock & vendor tab (the preferred Vendor Option's own row summary already
// shows vendor name, SKU, and cost), and its own "Manage vendor offers"
// link existed only to jump to a panel that, since INV27 moved Vendor
// offers onto this same tab, is now always already visible right below
// it -- a jump to nowhere. No edit path is lost: every one of these
// fields was read-only/disabled here (readonly inputs, a disabled
// select), never a typed value; the underlying facts (vendor_sku,
// cost_cents, units_per_case) are set only when a vendor offer is
// created (the add-offer-card form below), which is unchanged.
// INV26 Slice 2 (DEC-207 item 3). A plain Save-submitted checkbox, not the
// shared activation switch; see index.html's own comment on this control.
const bottleHideOnMenuInput = mustFind<HTMLInputElement>("#admin-bottle-hide-on-menu");
// STK1 (DEC-269): the editor's instant "Manual availability" control.
const bottleStockBlock = mustFind<HTMLElement>("#admin-bottle-stock-block");
const bottleStockInButton = mustFind<HTMLButtonElement>("#admin-bottle-stock-in");
const bottleStockOutButton = mustFind<HTMLButtonElement>("#admin-bottle-stock-out");
const bottleStockSinceEl = mustFind<HTMLElement>("#admin-bottle-stock-since");
const bottleStockSavedEl = mustFind<HTMLElement>("#admin-bottle-stock-saved");
/** INVUX1: set once an 86 change has been written from this editor, so Cancel can say it does not undo it. */
let bottleStockChangedThisOpen = false;
const bottleItemKindEl = mustFind<HTMLElement>("#admin-bottle-item-kind");
const bottleNameInput = mustFind<HTMLInputElement>("#admin-bottle-name");
const bottleCategoryLiquorGroup = mustFind<HTMLElement>("#admin-bottle-category-liquor-group");
const bottleCategorySelect = mustFind<HTMLSelectElement>("#admin-bottle-category");
const bottleCategoryOtherGroup = mustFind<HTMLElement>("#admin-bottle-category-other-group");
// CT3 (DEC-129, DEC-141): replaces the free-text #admin-bottle-category-
// text input this element used to be. One SELECT, shared by beer,
// wine and packaged, repopulated from that kind's own category vocabulary
// (CATEGORY_VOCABULARY_BY_ITEM_KIND) whenever the item-kind chip changes;
// see populateBottleCategoryOtherSelect.
const bottleCategoryOtherSelect = mustFind<HTMLSelectElement>("#admin-bottle-category-other");
/** CATALOGAI2: true once the person has chosen a category on the form themselves; a change made by the form's own code does not count. */
let bottleCategoryPicked = false;
bottleCategorySelect.addEventListener("change", () => { bottleCategoryPicked = true; });
bottleCategoryOtherSelect.addEventListener("change", () => { bottleCategoryPicked = true; });

const bottleLiquorPriceGroup = mustFind<HTMLElement>("#admin-bottle-liquor-price-group");
const bottleCurrentRateEl = mustFind<HTMLElement>("#admin-bottle-current-rate");
let bottleCurrentPerOuncePriceCents: number | null = null;
const bottlePourOptionsEl = mustFind<HTMLElement>("#admin-bottle-pour-options");
const bottlePackagedPriceGroup = mustFind<HTMLElement>("#admin-bottle-packaged-price-group");
const bottleUnitPriceInput = mustFind<HTMLInputElement>("#admin-bottle-unit-price");
const bottleSaleMechanismWrap = mustFind<HTMLElement>("#admin-bottle-sale-mechanism");
const bottleSaleMechanismStatus = mustFind<HTMLElement>("#admin-bottle-sale-mechanism-status");
const bottleSaleMechanismGroup = mustFind<HTMLElement>("#admin-bottle-sale-mechanism-group");
// INV27-F2 (DEC-253): this wrapper, and #admin-bottle-liquor-price-group
// above, are the two blocks applyBottlePriceLabelsForPricingModel reorders
// inside their shared parent (bottlePricingCurrentCardEl) by pricing model.
const bottleShelfTierGroupEl = mustFind<HTMLElement>("#admin-bottle-shelf-tier-group");
const bottleShelfTierLabelEl = mustFind<HTMLElement>("#admin-bottle-shelf-tier-label");
const bottleShelfTierHelpEl = mustFind<HTMLElement>("#admin-bottle-shelf-tier-help");
const bottleShelfTierSelect = mustFind<HTMLSelectElement>("#admin-bottle-shelf-tier");
const bottleCalcSubtitleEl = mustFind<HTMLElement>("#admin-bottle-calc-subtitle");
// INV19 Slice 1 (docs/build-briefs/INV19-pour-size-price-override.md;
// ADR-010 revised text; DEC-139).
const bottlePourPriceOverridesSection = mustFind<HTMLElement>(
  "#admin-bottle-pour-price-overrides-section",
);
const bottlePourPriceOverridesEl = mustFind<HTMLElement>("#admin-bottle-pour-price-overrides");
const bottleStopOrderingInput = mustFind<HTMLInputElement>("#admin-bottle-stop-ordering");
bottleStopOrderingInput.addEventListener("change", () => {
  bottleParLevelInput.disabled = bottleStopOrderingInput.checked;
  if (bottleStopOrderingInput.checked) bottleParLevelInput.value = "";
});
const bottleParLevelInput = mustFind<HTMLInputElement>("#admin-bottle-par-level");
// INV8 Section 2.6 (DEC-176): the read-only unit hint next to Par level.
const bottleParLevelHintEl = mustFind<HTMLElement>("#admin-bottle-par-level-hint");
// INVLOC1 Slice 2 (DEC-326): par and bin are per location. One location shows the two fields above with "at <location>" labels; more
// than one shows a table of locations; none shows a sentence.
const bottleStockHeadingLabelEl = mustFind<HTMLElement>("#admin-bottle-stock-heading-label");
const bottleStockIntroEl = mustFind<HTMLElement>("#admin-bottle-stock-intro");
const bottleStockStatusEl = mustFind<HTMLElement>("#admin-bottle-stock-status");
const bottleStopOrderingHelpEl = mustFind<HTMLElement>("#admin-bottle-stop-ordering-help");
const bottleStockSingleEl = mustFind<HTMLElement>("#admin-bottle-stock-single");
const bottleParLevelLabelEl = mustFind<HTMLElement>("#admin-bottle-par-level-label");
const bottleStockTableEl = mustFind<HTMLElement>("#admin-bottle-stock-table");
const bottleParLevelHintTableEl = mustFind<HTMLElement>("#admin-bottle-par-level-hint-table");
const bottleStockNoteEl = mustFind<HTMLElement>("#admin-bottle-stock-note");
const bottleStockNoteLeadEl = mustFind<HTMLElement>("#admin-bottle-stock-note-lead");
const bottleStockNoteBodyEl = mustFind<HTMLElement>("#admin-bottle-stock-note-body");
const bottleStockCopyButton = mustFind<HTMLButtonElement>("#admin-bottle-stock-copy");
const bottleStockCopyLaterEl = mustFind<HTMLElement>("#admin-bottle-stock-copy-later");
const stockLocationHeaderEl = mustFind<HTMLElement>("#admin-stock-location-header");
const stockLocationSwitchEl = mustFind<HTMLElement>("#admin-stock-location-switch");
const stockNoParNoteEl = mustFind<HTMLElement>("#admin-stock-no-par-note");
const stockNoParNoteTitleEl = mustFind<HTMLElement>("#admin-stock-no-par-note-title");
const stockNoParNoteBodyEl = mustFind<HTMLElement>("#admin-stock-no-par-note-body");
// B1 (docs/backlog.md, owner ruling 2026-08-27). See index.html's own
// comment on this input for why free text is correct here. DEC-047
// retired B1's sibling supplier SKU field.
const bottleBarcodeInput = mustFind<HTMLInputElement>("#admin-bottle-barcode");
// B6-F1: case barcode and case size, an existing item only, for a session holding admin.liquor_product.manage.
const bottleCaseRow = mustFind<HTMLElement>("#admin-bottle-case-row");
const bottleCaseBarcodeInput = mustFind<HTMLInputElement>("#admin-bottle-case-barcode");
const bottleCaseSizeInput = mustFind<HTMLInputElement>("#admin-bottle-case-size");
const MAX_BOTTLE_CASE_SIZE = 100000;
// B4: the Size picker. INV15: its help text now names what "Size" means
// for the currently selected item kind, and the raw value/unit inputs
// below (still authoritative for save) are no longer reachable through
// any select option; see populateBottleSizeSelect's own header.
const bottleSizeSelect = mustFind<HTMLSelectElement>("#admin-bottle-size");
const bottleSizeHelpEl = mustFind<HTMLElement>("#admin-bottle-size-help");
const bottleSizeValueInput = mustFind<HTMLInputElement>("#admin-bottle-size-value");
const bottleSizeUnitInput = mustFind<HTMLInputElement>("#admin-bottle-size-unit");
const bottleContainerTypeSelect = mustFind<HTMLSelectElement>("#admin-bottle-container-type");
// STARTSTOCK1 (DEC-601): "On hand now" on a new item's Stock & vendor step.
const bottleStartStockRow = mustFind<HTMLElement>("#admin-bottle-start-stock-row");
const bottleStartStockInput = mustFind<HTMLInputElement>("#admin-bottle-start-stock");
const bottleStartStockUnitEl = mustFind<HTMLElement>("#admin-bottle-start-stock-unit");
const bottleSizeConfirmRow = mustFind<HTMLElement>("#admin-bottle-size-confirm-row");
const bottleSizeConfirmButton = mustFind<HTMLButtonElement>("#admin-bottle-size-confirm");
// INV8 Section 2.7 (DEC-176): the keg-size picker, shown only for a keg
// container (refreshBottleDefaultKegSizeVisibility below).
const bottleDefaultKegSizeRow = mustFind<HTMLElement>("#admin-bottle-default-keg-size-row");
const bottleDefaultKegSizeSelect = mustFind<HTMLSelectElement>("#admin-bottle-default-keg-size");
// TF3: the read-only on-hand figure beside Par level.
const bottleOnHandRow = mustFind<HTMLElement>("#admin-bottle-onhand-row");
const bottleOnHandValueEl = mustFind<HTMLElement>("#admin-bottle-onhand-value");
// UX16-F2 (docs/build-briefs/UX16-F2-negative-on-hand-shown-not-hidden.md;
// DEC-178): a plain warning shown beside a negative on-hand figure, never
// hidden or refused.
const bottleOnHandWarningEl = mustFind<HTMLElement>("#admin-bottle-onhand-warning");
// INV1: waste/return-to-stock/vendor-return recording, reached from the
// same on-hand row.
const recordMovementButton = mustFind<HTMLButtonElement>("#admin-record-movement-button");
const inventoryMovementScrim = mustFind<HTMLElement>("#admin-inventory-movement-scrim");
// DOCSBUG4 (DEC-274): the Recent movements list and the two reverse dialogs.
const bottleMovementsSection = mustFind<HTMLElement>("#admin-bottle-movements");
const bottleMovementsStatusEl = mustFind<HTMLElement>("#admin-bottle-movements-status");
const bottleMovementsCountEl = mustFind<HTMLElement>("#admin-bottle-movements-count");
const bottleMovementsEmptyEl = mustFind<HTMLElement>("#admin-bottle-movements-empty");
const bottleMovementsListEl = mustFind<HTMLElement>("#admin-bottle-movements-list");
const bottleMovementsOlderButton = mustFind<HTMLButtonElement>("#admin-bottle-movements-older");
const bottleMovementsRetryButton = mustFind<HTMLButtonElement>("#admin-bottle-movements-retry");
const inventoryMovementErrorEl = mustFind<HTMLElement>("#admin-inventory-movement-error");
const inventoryMovementTypePickerEl = mustFind<HTMLElement>(
  "#admin-inventory-movement-type-picker",
);
const inventoryMovementQuantityInput = mustFind<HTMLInputElement>(
  "#admin-inventory-movement-quantity-input",
);
const inventoryMovementQuantityLabelEl = mustFind<HTMLElement>(
  "#admin-inventory-movement-quantity-label",
);
const inventoryMovementUnitHelpEl = mustFind<HTMLElement>("#admin-inventory-movement-unit-help");
const inventoryMovementUnitPickerEl = mustFind<HTMLElement>(
  "#admin-inventory-movement-unit-picker",
);
const inventoryMovementReasonRow = mustFind<HTMLElement>("#admin-inventory-movement-reason-row");
const inventoryMovementReasonPickerEl = mustFind<HTMLElement>(
  "#admin-inventory-movement-reason-picker",
);
const inventoryMovementVendorRow = mustFind<HTMLElement>("#admin-inventory-movement-vendor-row");
const inventoryMovementVendorPickerEl = mustFind<HTMLElement>(
  "#admin-inventory-movement-vendor-picker",
);
const inventoryMovementVendorEmptyEl = mustFind<HTMLElement>(
  "#admin-inventory-movement-vendor-empty",
);
const inventoryMovementNoteRow = mustFind<HTMLElement>("#admin-inventory-movement-note-row");
const inventoryMovementNoteInput = mustFind<HTMLInputElement>(
  "#admin-inventory-movement-note-input",
);
const inventoryMovementCloseButton = mustFind<HTMLButtonElement>("#admin-inventory-movement-close");
const inventoryMovementCancelButton = mustFind<HTMLButtonElement>(
  "#admin-inventory-movement-cancel",
);
const inventoryMovementSaveButton = mustFind<HTMLButtonElement>("#admin-inventory-movement-save");
const bottleAbvInput = mustFind<HTMLInputElement>("#admin-bottle-abv");
// B3: the Strength field's label and "%" suffix follow the item kind
// currently selected on this form (applyBottleStrengthLabelForItemKind).
const bottleAbvLabelEl = mustFind<HTMLElement>("#admin-bottle-abv-label");
const bottleAbvSuffixEl = mustFind<HTMLElement>("#admin-bottle-abv-suffix");
const bottleProducerInput = mustFind<HTMLInputElement>("#admin-bottle-producer");
// CATALOG1: Add item's "Find a product" (see the block of that name near renderAddItemNcCode).
const findSection = mustFind<HTMLElement>("#admin-bottle-find-section");
const findInput = mustFind<HTMLInputElement>("#admin-bottle-find-input");
const findResultsEl = mustFind<HTMLElement>("#admin-bottle-find-results");
const findPreviewEl = mustFind<HTMLElement>("#admin-bottle-find-preview");
const findPreviewTitle = mustFind<HTMLElement>("#admin-bottle-find-preview-title");
const findPreviewNotice = mustFind<HTMLElement>("#admin-bottle-find-preview-notice");
const findPreviewRowsEl = mustFind<HTMLElement>("#admin-bottle-find-preview-rows");
const findPreviewSources = mustFind<HTMLElement>("#admin-bottle-find-preview-sources");
const findBackButton = mustFind<HTMLButtonElement>("#admin-bottle-find-back");
const findUseButton = mustFind<HTMLButtonElement>("#admin-bottle-find-use");
const bottleCountryRegionInput = mustFind<HTMLInputElement>("#admin-bottle-country-region");
const bottleDescriptionInput = mustFind<HTMLTextAreaElement>("#admin-bottle-description");
const bottleActiveRow = mustFind<HTMLElement>("#admin-bottle-active-row");
const bottleActiveInput = mustFind<HTMLInputElement>("#admin-bottle-active");
// VOC1-F4-F2 (Section 2.3). Edit-only (see bottleFormModifiers' own
// comment); resetBottleForm/openAddBottleForm hide this section entirely.
const bottleModifiersSection = mustFind<HTMLElement>("#admin-bottle-modifiers-section");
const bottleModifiersEl = mustFind<HTMLElement>("#admin-bottle-modifiers");
const bottleFormError = mustFind<HTMLElement>("#admin-bottle-form-error");
const bottleFormSave = mustFind<HTMLButtonElement>("#admin-bottle-form-save");
const bottleFormNext = mustFind<HTMLButtonElement>("#admin-bottle-form-next");
const bottleFormCancel = mustFind<HTMLButtonElement>("#admin-bottle-form-cancel");
const itemTrackStockInput = mustFind<HTMLInputElement>("#admin-item-track-stock");
// MODRM1: inventory owns stock; recipe amounts replace item-level add-on properties.
// CHOICEGRP1: choice groups only matter when the item is rung up on its own. "Sold on its own" is the rule the floor uses
// to offer an item (active at the location and not hidden on the menu), read from the editor's own two toggles so the
// section follows them. An item that is not sold alone keeps the section, read-only, only when it already has saved choices.
const bottleModifiersNote = mustFind<HTMLElement>("#admin-bottle-modifiers-note");
function refreshItemAddonFields(): void {
  const soldAlone = bottleActiveInput.checked && !bottleHideOnMenuInput.checked;
  const hasSaved =
    ingredientDrafts(bottleFormModifiers).length > 0 || attachmentGroups(bottleFormModifiers).length > 0;
  bottleModifiersSection.hidden =
    editingBottleId === undefined || !bottleModifiersLoaded || (!soldAlone && !hasSaved);
  bottleModifiersNote.hidden = bottleModifiersSection.hidden || soldAlone;
  if (!bottleModifiersSection.hidden) {
    renderModifierAttachmentChips(bottleFormModifiers, bottleModifiersEl);
    if (!soldAlone) {
      for (const control of bottleModifiersEl.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLButtonElement>(
        "input, select, button",
      )) {
        control.disabled = true;
      }
    }
  }
}
bottleActiveInput.addEventListener("change", refreshItemAddonFields);
bottleHideOnMenuInput.addEventListener("change", refreshItemAddonFields);

// Review 9 correction (both gates, Medium): the shared destructive-
// deactivation confirm sheet.
const confirmScrim = mustFind<HTMLElement>("#admin-confirm-scrim");
const confirmTitleEl = mustFind<HTMLElement>("#admin-confirm-title");
const confirmMessageEl = mustFind<HTMLElement>("#admin-confirm-message");
const confirmCancelButton = mustFind<HTMLButtonElement>("#admin-confirm-cancel");
const confirmConfirmButton = mustFind<HTMLButtonElement>("#admin-confirm-confirm");
// DOCSBUG4: the optional richer body of the shared confirm sheet.
const confirmExtraEl = mustFind<HTMLElement>("#admin-confirm-extra");

/** Current non-Liquor pour choices use each kind's assigned active
 * pour_volume values. Liquor uses the independent Standard pour setting.
 * Stored product selections remain available to historical readers. */
// UX26: an optional `vocabularies` snapshot, defaulting to the shared
// module state, lets loadSetupChecklist pass its own freshly-fetched
// LOCAL copy instead (Section 2.3's own coupling finding: writing into
// the shared adminVocabularies from a background prefetch is not what
// makes this unsafe, but this default keeps every existing call site's
// behavior byte-for-byte unchanged while the checklist reads real data
// without touching module state at all).
function pourOptionsForItemKind(
  itemKind: ItemKind,
  vocabularies: typeof adminVocabularies = adminVocabularies,
): string[] {
  if (itemKind === "liquor") return [];
  // VER57-F4: a session that cannot read the vocabularies reads the sizes
  // from the narrow pour-options route instead (already active-only and
  // ascending, server side).
  if (vocabularies === adminVocabularies && (vocabularies["item_kind"] ?? []).length === 0) {
    return pourOptionsByItemKindFallback[itemKind] ?? [];
  }
  const entry = (vocabularies["item_kind"] ?? []).find((candidate) => candidate.value === itemKind);
  const assigned = new Set(entry?.assignedPourVolumeValues ?? []);
  if (assigned.size === 0) {
    return [];
  }
  const activeVolumes = (vocabularies["pour_volume"] ?? []).filter((volume) => volume.active);
  return activeVolumes.filter((volume) => assigned.has(volume.value)).map((volume) => volume.value);
}

/** Ascending by numeric amount, never by the vocabulary's own stored
 * insertion order (sort_order). Every pour_volume value is a plain
 * decimal-ounces string (migration 0120's own unit CHECK requires
 * unit = 'oz' for this vocabulary), so Number(value) is always a safe,
 * meaningful comparison; this must never be applied to a non-numeric
 * vocabulary (liquor_bottle_size's "750ml" tokens, for one, are not a
 * bare number and are correctly left in their own hand-ordered seed
 * sequence, smallest to largest by design, not by this comparator). */
function byPourVolumeAmount<T extends { value: string }>(entries: readonly T[]): T[] {
  return [...entries].sort((a, b) => Number(a.value) - Number(b.value));
}

/** Display form for a token's unit: "l" reads as "L" (liter), everything
 * else is shown as stored. */
const SIZE_UNIT_DISPLAY: Readonly<Record<string, string>> = { ml: "ml", l: "L", oz: "oz" };

/** Only used for the short pre-loadVocabularies() window, exactly like
 * STANDARD_LIQUOR_BOTTLE_SIZES/STANDARD_SERVING_SIZES below: a token's own
 * label once the tenant's real list has loaded always comes from the
 * vocabulary row itself (an admin may relabel it), never computed here. */
function fallbackSizeTokenLabel(token: string): string {
  const parsed = parseSizeToken(token);
  if (parsed === undefined) {
    return token;
  }
  return `${parsed.value} ${SIZE_UNIT_DISPLAY[parsed.unit] ?? parsed.unit}`;
}

const STANDARD_LIQUOR_BOTTLE_SIZES = [
  "50ml",
  "200ml",
  "375ml",
  "500ml",
  "700ml",
  "750ml",
  "1l",
  "1.75l",
];
const STANDARD_SERVING_SIZES = ["8oz", "10oz", "12oz", "16oz", "22oz", "24oz", "32oz", "40oz"];

function tenantLiquorBottleSizes(): { value: string; label: string }[] {
  const values = (adminVocabularies["liquor_bottle_size"] ?? []).filter((entry) => entry.active);
  if (values.length > 0) {
    // UX43 Site 1: sorted by real magnitude, not the vocabulary's own
    // stored insertion order, since a size added later gets MAX(sort_
    // order) + 1 and would otherwise land at the end regardless of amount.
    return bySizeTokenAmount(values.map((entry) => ({ value: entry.value, label: entry.label })));
  }
  return STANDARD_LIQUOR_BOTTLE_SIZES.map((value) => ({
    value,
    label: fallbackSizeTokenLabel(value),
  }));
}

function tenantServingSizes(): { value: string; label: string }[] {
  const values = (adminVocabularies["serving_size"] ?? []).filter((entry) => entry.active);
  if (values.length > 0) {
    return bySizeTokenAmount(values.map((entry) => ({ value: entry.value, label: entry.label })));
  }
  return STANDARD_SERVING_SIZES.map((value) => ({ value, label: fallbackSizeTokenLabel(value) }));
}

/** The size options this item kind offers, filtered so a keg is never
 * shown "750 ml" and a liquor bottle is never shown "16 oz can". Wine
 * offers the liquor bottle-size list (WIN2, DEC-040): wine ships in
 * bottles and its ruled 750 ml default must be a selectable option; the
 * list-source choice is a named project judgment in the WIN2 brief, not
 * itself part of the ruling.
 *
 * ITEM1 Slice 4: cider offers the SERVING-size list (the beer/packaged
 * one), not the liquor bottle-size list, a named project judgment in
 * this slice's own brief and not an owner ruling: an ordinary cider is
 * sold in a 12 oz can or bottle, the same oz-based sizes a beer or a
 * packaged item already offers here, not the ml-based spirit-bottle
 * volumes wine and liquor use. This governs only which SIZE list the
 * form offers; cider's own POUR (how it is served by the glass) still
 * follows wine's shape, per pourOptionsForItemKind above. */
function sizeOptionsForItemKind(itemKind: ItemKind): { value: string; label: string }[] {
  if (itemKind === "liquor" || itemKind === "wine") {
    return tenantLiquorBottleSizes();
  }
  if (itemKind === "beer" || itemKind === "packaged" || itemKind === "cider") {
    return tenantServingSizes();
  }
  return [];
}

// INV15 (owner report 2026-09-10, "I dont even know size of what?"):
// Size means two different things depending on item kind, the container
// for liquor/wine and the serving for beer/packaged (same split
// sizeOptionsForItemKind's own comment above already documents), so a
// single sentence cannot cover both honestly. This describes the field
// AS IT WORKS TODAY; it does not resolve the underlying split, which
// stays INV14's own remaining scope (see the INV15 build brief).
const SIZE_HELP_TEXT_BY_ITEM_KIND: Readonly<Partial<Record<ItemKind, string>>> = {
  liquor: "The bottle size, e.g. 750 ml.",
  wine: "The bottle size, e.g. 750 ml.",
  beer: "The size this item is served or sold as, e.g. a 16 oz pour or a 12 oz can.",
  cider: "The size this item is served or sold as, e.g. a 16 oz pour or a 12 oz can.",
};

/** Called from resetBottleForm, openEditBottleForm, and the item-kind
 * chip's own change handler, mirroring applyBottleStrengthLabelForItemKind's
 * existing three call sites exactly. */
function applyBottleSizeHelpTextForItemKind(): void {
  // ITEM1 Slice 2: ItemKind widened from a fixed literal union to
  // `string`, so this Record can no longer be indexed as exhaustive (the
  // brief's own named false-green risk, Section 7); Cider gained its own
  // entry in Slice 4, and any kind still absent (a tenant-added kind
  // beyond the five this product wires) falls back to the flat/per-ounce
  // description below rather than leaving the help text silently blank.
  bottleSizeHelpEl.textContent =
    SIZE_HELP_TEXT_BY_ITEM_KIND[bottleFormItemKind] ?? "The size this item is served or sold as.";
}

// B4 owner ruling 2026-08-27: liquor defaults to 750 ml ("it is the
// shop's most common size"); drafts, bottles and cans all default to
// 12 oz. Wine defaults to 750 ml too, but under its own later ruling
// (WIN2, DEC-040 2026-09-02); B4 had deliberately left it undefaulted.
// A default here is a SIZE TOKEN from the lists above, applied only on
// a brand-new bottle form (see refreshBottleSizeForItemKind's own
// header on why edit mode never applies one) and only to a field the
// user has not already chosen for themselves this session
// (bottleFormSizeUserSet / bottleFormContainerTypeUserSet below).
const SIZE_DEFAULT_TOKEN_BY_ITEM_KIND: Partial<Record<ItemKind, string>> = {
  liquor: "750ml",
  wine: "750ml",
  beer: "12oz",
  // ITEM1 Slice 4: cider follows beer/packaged's own default, not wine's,
  // matching sizeOptionsForItemKind's own serving-size choice above.
  cider: "12oz",
};

// Container type defaults: liquor to bottle, beer to bottle (INV39: it was
// "other", which left on-hand unavailable), packaged to can (B4's own text: "all three already in CONTAINER_TYPES").
// Wine carries no default for the same reason its size does not.
const CONTAINER_TYPE_DEFAULT_BY_ITEM_KIND: Partial<Record<ItemKind, string>> = {
  liquor: "bottle",
  beer: "bottle",
};

// UX22 slice 2: the seven money-and-pricing bound constants this section
// used to declare privately (MAX_TAX_RATE_BASIS_POINTS,
// MAX_TIP_PRESET_BASIS_POINTS, MAX_TIP_PRESET_COUNT, MAX_HOLD_AMOUNT_CENTS,
// MIN/MAX_HOLD_INCREMENT_THRESHOLD_BASIS_POINTS,
// MAX_ABANDONED_TAB_GRATUITY_BASIS_POINTS) are now imported from
// scripts/settingsBounds.ts (this file's own top-of-file import block),
// the same shared constants src/domain/tenantSettings.ts's own validators
// enforce and this screen's field descriptions state -- a client-side
// pre-check that used to be a SECOND, independently hand-typed copy of
// each ceiling is now the same import, so this early rejection and the
// server's real guard rail cannot silently disagree. The server is still
// the real enforcement point; a value this screen lets through unrejected
// but the server still refuses shows the server's own message instead,
// exactly like every other field on this form.

// ---------------------------------------------------------------------
// Local state
// ---------------------------------------------------------------------

let currentSession: Session | undefined;
// SET22: tracking the active panel never chooses the ordinary Settings landing.
let currentSection: AdminSection = ADMIN_SECTION_ORDER[0]!;
let settingsJumpVisit = 0;
const settingsJumpVisits = new WeakMap<HTMLElement, number>();
let drinksLoaded = false;
// SET10: independent of drinksLoaded, on purpose. loadVocabularies() is now
// reachable from BOTH the Recipes branch (the Add/Edit Drink sheet's own
// Display category and Allowed liquor categories pickers read
// adminVocabularies) and the Menu branch (the eight vocabulary editors
// themselves) of showSection below, exactly the same "whichever of these
// is opened FIRST triggers it" shape settingsLoaded already carries across
// seven groups. Reusing drinksLoaded here would leave Menu's own vocabulary
// editors permanently empty for any session that opens Recipes first.
let vocabulariesLoaded = false;
// VER57-F2: set when the current vocabularies load failed (non-200 or
// network failure), so the Add/Edit item pour area can say so and offer
// Try again. Reset when
// a load starts, when it succeeds, and at sign-out. It changes nothing for
// the Menu and Recipes screens, which still only read vocabulariesLoaded.
let vocabulariesLoadFailed = false;
// VER57-F4 (DEC-273): a role that may manage liquor items but is denied
// admin.tenant_settings.view cannot read the vocabularies, so the Add/Edit
// item pour sizes come from GET .../liquor-products/pour-options instead.
// `pourOptionsByItemKindFallback` holds that read (ounce strings per item
// kind, nothing else). It is never set for a session that can read the
// vocabularies, and vocabulariesLoaded is deliberately NOT set from this
// path (Menu and Recipes still read it and still say "You do not have
// permission"). It resets at sign-out.
let pourOptionsByItemKindFallback: Record<string, string[]> = {};
// ADDITEMNEXT1 (DEC-592): the same idea for the Category lists on Add item. A session denied tenant settings reads the
// active categories per item kind from the narrow item-categories route. Never set for a session that can read the
// vocabularies; reset at sign-out.
let itemCategoriesByScopeFallback: Record<string, { value: string; label: string }[]> = {};
let bottlesLoaded = false;
/** INVVENDOR1: the vendor option write count the loaded Items list already reflects; a closed item sheet re-reads the list when it is behind. */
let bottleListVendorOfferWrites = 0;
let settingsLoaded = false;

// UX26: the "Set up this location" checklist card's own state. Per-
// viewer, per-session working state, never a stored preference
// (Section 2.6's own reasoning: a collapse choice made by one employee
// must not follow the next employee on a shared tablet, the same shape
// bottleFilterShelfTier/drinkFilterCategory already carry below), so a
// plain module-level let reset by invalidate() rather than localStorage.
let setupChecklistStepStates: Partial<Record<SetupChecklistStepId, SetupChecklistStepState>> = {};
// ONB2 S5: a short phrase a step shows under its title (the hold amount), written with the states.
let setupChecklistStepDetails: Partial<Record<SetupChecklistStepId, string>> = {};
let setupChecklistCollapsed = true;

let adminMenuDrinks: MenuDrinkListItemDto[] = [];
let adminLiquorProducts: LiquorProductDto[] = [];
let bottleListRequestSequence = 0;
let bottleListReady = false;
const f3SellingUi: {
  openSequence: number;
  inventoryContext?: LocationSellingContextDto;
  shelvesContext?: LocationSellingContextDto;
  shelves: Map<string, LocalShelfSellingDto>;
  editor?: { siteId: string | undefined; context: LocationSellingContextDto; requestId: string; generation: number; session: Session; version: number; body?: UpdateLiquorProductInput; bodyStamp?: string; uncertain: boolean; createBody?: Parameters<typeof createLiquorProduct>[2]; createStamp?: string };
  settings?: { siteId: string | undefined; context: LocationSellingContextDto; shelves: Map<string, LocalShelfSellingDto>; requestIds: Map<string, string> };
  reconciledSettings?: { session: Session; siteId: string | undefined; body: SaveLocationSettingsInput; stamp: string }[];
  pendingSettings?: { session: Session; siteId: string | undefined; body: SaveLocationSettingsInput; stamp: string };
} = { openSequence: 0, shelves: new Map() };
let bottleExplicitLocationId: string | undefined;
function inventorySellingSiteId(): string | undefined {
  return autoParSection.inventoryLocationId() ?? loadTerminalConfig()?.locationId;
}
function clearF3SellingUi(): void {
  f3SellingUi.openSequence += 1;
  delete f3SellingUi.inventoryContext;
  delete f3SellingUi.shelvesContext;
  delete f3SellingUi.editor;
  delete f3SellingUi.settings;
  delete f3SellingUi.pendingSettings;
  delete f3SellingUi.reconciledSettings;
  f3SellingUi.shelves.clear();
}
async function loadLocalSellingShelves(
  siteId: string | undefined,
  gen: number,
  active: Session,
): Promise<LocationSellingContextDto | undefined> {
  if (!hasPermission(active, "admin.liquor_product.view")) return undefined;
  const result = await getLocalSellingShelves(active.apiBaseUrl, active.tenantId, siteId);
  if (!loadIsCurrent(gen) || !adminReadSessionIsCurrent(active)) return undefined;
  if (result.status !== 200 || result.body.sellingContext === undefined) return undefined;
  if (siteId !== undefined && result.body.sellingContext.locationId !== siteId) return undefined;
  f3SellingUi.shelvesContext = result.body.sellingContext;
  f3SellingUi.shelves = new Map(
    result.body.shelves.flatMap((tier) =>
      tier.selling === undefined ? [] : [[tier.value, tier.selling] as const],
    ),
  );
  return result.body.sellingContext;
}
function bottleSellingCurrent(): boolean {
  const editor = f3SellingUi.editor;
  return (
    editor !== undefined &&
    !bottleFormScrim.hidden &&
    loadIsCurrent(editor.generation) &&
    sessionOrUndefined() === editor.session &&
    editor.siteId === (catalogItemSetup?.locationId ?? bottleExplicitLocationId ?? inventorySellingSiteId())
  );
}
function bottleSelling(bottle: LiquorProductDto) {
  return currentProductSelling(bottle, inventorySellingSiteId());
}

let bottleCatalogLoadedLocationId: string | undefined;
let bottleSearchDebounceTimer: ReturnType<typeof setTimeout> | undefined;
// INV26 Slice 1 (DEC-206 item 2): the Settings inventory list's own sort
// state. One active sort at all times (never "unsorted"), defaulting to
// Item ascending, the same default the mockup's own Artboard 1 shows.
// Persists across re-renders (a search or filter change re-sorts by the
// same column the admin already picked) but resets to the default on
// tab teardown, same lifetime as adminLiquorProducts itself.
let bottleListSort: InventoryListSortState = { column: "item", direction: "asc" };
// UX80 (DEC-298): the Stock levels screen's own list, search, filter and sort.
// It loads its own copy of the item list (adminLiquorProducts belongs to the
// Settings catalog and its server side filters) and filters client side.
let stockLevelItems: LiquorProductDto[] = [];
let stockLevelsSort: InventoryListSortState = { column: "item", direction: "asc" };
let stockLevelsFilter: "" | "low" | "86" | "noPar" = "";
/** CORE7-F1: whether this location's drinks and ingredients were read, for the rows after the items. "failed" shows N/A. */
let menu86LoadState: "idle" | "loaded" | "failed" = "idle";
/** The drink or ingredient the inline 86 panel is open on. */
let menu86Open: SettingsMenuRow | undefined;
// INVLOC1 Slice 2 (DEC-326): the locations the person may plan for (this tablet's first), and the one the Stock list is
// showing (undefined means this tablet's own). With one location the Stock list names it and shows no switch.
let planningLocations: PlanningLocationDto[] = [];
let stockLevelsLocationId: string | undefined;
// The item editor's per-location par and bin: what was loaded for the open item, whether it is loaded, and the inputs of the
// table layout. A token guards a slow answer arriving after the form was closed or reopened for another item.
let bottleStockLoaded: LocationSettingsEntry[] = [];
let bottleStockLoadState: "idle" | "loading" | "ready" | "failed" = "idle";
let bottleStockLoadToken = 0;
let bottleStockTableInputs = new Map<
  string,
  { par: HTMLInputElement; stopOrdering: HTMLInputElement }
>();
let bottleStockParHeaderEl: HTMLElement | undefined;

// Amendment 2026-08-22 (tenth): the Drinks list's own search/filter
// state, the identical shape bottleFilterShelfTier/bottleFilterCategory
// below already establish (server-side, combines with the search box as
// AND). "" means "All".
let drinkSearchDebounceTimer: ReturnType<typeof setTimeout> | undefined;
let drinkFilterCategory = "";

// RECIPETABLE1: the Requires liquor filter chip ("any" | "on" | "off"), applied in the browser to the rows already loaded.
let drinkFilterRequires: "any" | "on" | "off" = "any";

// RECIPETABLE1: the checkbox column is always on for a person who may edit drinks. selectedDrinkIds is pruned to the rows listed
// every time the list is drawn, so a filter change can never leave a drink selected that the person cannot see.
let selectedDrinkIds: Set<string> = new Set();

// undefined editingDrinkId means Add mode; a real id means editing that
// drink. Existing components render read-only in edit mode (see
// renderDrinkComponents' own comment: there is no server route to change
// a published drink's recipe components, a deliberate scope limit tied to
// P0.5's still-unbuilt recipe-versioning requirement, not an oversight).
let editingDrinkId: string | undefined;
// SET7: kept alongside editingDrinkId, mirroring editingBottle (CT1), so
// populateDrinkCategorySelect can merge this drink's own stored category
// back into the offered options when it is not among the tenant's active
// drink_display_category values (retired, or never registered at all;
// see that function's own comment for why the second case is reachable
// here). Set in openEditDrinkForm before resetDrinkForm() runs, and
// undefined in Add mode, so a brand-new drink form is never affected.
let editingDrink: MenuDrinkListItemDto | undefined;
let drinkFormComponents: RecipeDraft[] = [];
// CORE14: the location's own active items the ingredient pickers search (fetched each time the sheet opens), who publishes
// the drink being edited (null for the location's own), and the recipe as loaded so Save sends it only when it changed.
let drinkRecipeItems: RecipeItem[] = [];
let drinkRecipeItemsAvailable = true;
// ADDDRINK1: true from the moment the Add drink sheet opens until the location's items have arrived, so an ingredient chooser
// opened in that window says it is loading and fills in when the items land, never "No ingredients found".
let drinkRecipeItemsLoading = false;
let refreshRecipePickerResults: (() => void) | undefined;
let drinkNetworkSource: { networkName: string; required: boolean; withdrawn: boolean } | null =
  null;
let drinkFormLiquorSlots: Set<string> = new Set();
// VOC1-F4 (docs/build-briefs/VOC1-F4-drink-modifier-catalog.md, Section
// 2.2). Which catalog modifiers this drink offers (map key = value) and
// whether each is required (map value). Attachment only, never an amount:
// the price a chip shows here is always the catalog's own, read-only.
// Edit-only, matching Section 2.2's own "creation attaches no modifiers"
// (createMenuDrink accepts no modifiers field at all); resetDrinkForm
// clears this to empty and hides the section for Add.
let drinkFormModifiers: Map<string, boolean> = new Map();
let existingDrinkComponents: MenuDrinkComponentDto[] = [];
let drinkStoredComponents: MenuDrinkComponentDto[] = [];

let editingBottleId: string | undefined;
// Review 9 correction (both gates, Medium): kept alongside editingBottleId
// so saveBottleForm can compute the deactivation-confirm warning (needs
// the bottle's own category and name) without an extra round trip.
let editingBottle: LiquorProductDto | undefined;
let catalogItemSetup:
  | {
      selection: CatalogLocalCreateSelection;
      locationId: string;
      isCurrent: () => boolean;
      completed: () => void;
      uncertain: boolean;
    }
  | undefined;
const catalogFixedControls = new Map<
  HTMLInputElement | HTMLSelectElement | HTMLButtonElement,
  boolean
>();
function clearCatalogItemSetup(): void {
  for (const [control, disabled] of catalogFixedControls)
    control.disabled = disabled;
  catalogFixedControls.clear();
  catalogItemSetup = undefined;
}

let bottleFormPourOptions: Set<string> = new Set();
let bottleFormSaleMechanism: ProductSaleMechanism | null = null;
let bottleFormSaleMechanismTouched = false;
// Inventory track Slice 3, Amendment 2026-08-22 (twelfth). Drives which
// price fields the form shows/requires; see applyBottleItemKindVisibility.
let bottleFormItemKind: ItemKind = "liquor";
// B4's own bug-risk rule 2: "changing item kind must not stomp a size or
// container type the user already chose on purpose." True the instant the
// user manually picks a Size option or a Container type option in THIS
// open Add-bottle form; false again on resetBottleForm. Only meaningful in
// Add mode (editingBottleId === undefined): edit mode never auto-applies a
// default at all, per rule 1, so these flags are not consulted there.
let bottleFormSizeUserSet = false;
let bottleFormContainerTypeUserSet = false;
// VOC1-F4-F2 (Section 2.3). The Inventory item editor's own counterpart to
// drinkFormModifiers above: which catalog modifiers this liquor product
// offers (map key = value) and whether each is required (map value).
// Attachment only, never an amount. Edit-only, matching Section 2.2's own
// "creation attaches no modifiers" (createLiquorProduct accepts no
// modifiers field at all); resetBottleForm clears this to empty and hides
// the section for Add.
let bottleFormModifiers: Map<string, boolean> = new Map();
let bottleModifiersLoaded = false;

// Owner alpha-review feedback (Amendment 2026-08-21, fourth): the
// Bottles list's own filter state. "" means "All" for both; "none" is
// bottleFilterShelfTier's own sentinel for "No shelf" (unassigned),
// mirroring src/domain/catalog.ts's ListLiquorProductsParams.shelfTier.
// Both combine with each other and with the search box as AND, server-
// side (loadBottles passes all three to listLiquorProducts at once)
// rather than filtering an already-fetched list client-side, so a
// location with hundreds of bottles stays responsive: see loadBottles's
// own comment for the full reasoning.
let bottleFilterShelfTier = "";
// BULKVENDOR1: the Items list's Type filter ("" is All). BULKEDIT1: the ticked rows. The selection only ever holds rows that are listed now.
let bottleFilterKind = "";
let bulkEditSelected = new Set<string>();
let bulkEditShownIds: string[] = [];
let bottleFilterCategory = "";
// STK1 (DEC-269): the "86'd only" chip beside the search box. Applied to the
// loaded list, since flagged86 already rides on every row.
let settingsTaxRateBasisPoints = 0;
let settingsTipPresets: number[] = [];
let settingsShelfTiers: ShelfTierSettingDto[] = [];
// Payments-track Slice 4 (DEC-002), Finding 4. holdAmountCents mirrors
// TenantSettingsRow's own null-until-a-manager-sets-it convention (see
// src/domain/tenantSettings.ts): null means "not set yet", never a
// silent zero.
let settingsHoldAmountCents: number | null = null;
let settingsHoldIncrementThresholdBasisPoints = 8000;
let settingsAbandonedTabGratuityBasisPoints = 0;
let settingsAbandonedTabGratuityDisclosureConfirmed = false;
let settingsCloseCardTicketsAtDayEnd = true;
let settingsDualPricingEnabled = false;
let settingsDualPricingBasisPoints = 270;
let settingsTipOnPreDiscountPrice = false;
// Inventory track Slice 3, Amendment 2026-08-22 (thirteenth): one of
// PRICING_MODELS ("per_ounce"/"shelf"), governing neat/rocks pour and
// draft-beer pricing location-wide. "per_ounce" until the first settings
// load actually resolves it, matching tenantSettings.ts's own bootstrap
// default so the radio group never renders with nothing selected.
let settingsPricingModel: PricingModel = "per_ounce";
// VOC1-F9: "half-up-cent" until the first settings load resolves it,
// matching the database column's own DEFAULT 'half-up-cent' (migration
// 0052) and ROUNDING_MODES' only legal value today, so the picker never
// renders with nothing selected during that window.
let settingsRoundingMode: RoundingMode = "half-up-cent";
// Remains empty and disabled until the saved tenant baseline loads.
let settingsStandardPourOunces = "";
// Payments-track Slice 3: Settings > Payments > Terminals. Loaded and
// rendered independently of the tax/tip/shelf-tier fields above (its own
// GET, its own list), since register/deactivate act immediately over
// their own API calls rather than through "Save location settings".
let settingsTerminals: TerminalDto[] = [];
// Amendment 2026-08-22 (ninth): which terminal the rename sheet is
// currently open for, undefined when the sheet is closed.
let terminalBeingRenamedId: string | undefined;

// Inventory track (track/inventory) Slice 2: Counts panel state.
let countsLoaded = false;
let adminCountSections: CountSectionDto[] = [];
let adminCountHistory: CountSessionDto[] = [];
/** INVPOLISH1: the Past counts summaries by session id; empty when the read failed, and the rows then show the time alone. */
let adminCountHistorySummaries = new Map<string, CountHistorySummaryDto>();
/** INVPOLISH1: the open history detail's container type by product id, so a keg reads in kegs. */
let adminCountHistoryContainerTypes: Record<string, string | null> = {};

// Auth track (track/auth) Slice 1: Users panel state. adminUsers holds
// BOTH active and inactive users (activeOnly: false), unlike the tile
// picker's own read, since a manager needs to see who is deactivated or
// locked too.
let usersLoaded = false;
// SEC3 (DEC-100): AdminUserDto, not UserDto -- every read and write this
// screen makes is behind session() (an authenticated caller), so `role`
// is always present. See api.ts's AdminUserDto comment.
let adminUsers: AdminUserDto[] = [];
/* SET5. Collapsed by default: a disabled account is the one you are not
 * looking for. Session state only, deliberately not persisted, so the
 * list opens the same way every time rather than in whatever state the
 * last person left it. */
let inactiveUsersCollapsed = true;
// RBAC track (DEC-023): "lounger" replaces "bartender" outright as the
// default new-hire role (src/domain/users.ts's AUTH_ROLES header).
let addUserRole = "lounger";
// UX64 (DEC-211, corrected): Display name pre-fills from First name and
// keeps following it keystroke for keystroke while the person has not
// yet typed into Display name directly. True from the sheet's own
// opening state (empty follows empty); typing into Display name detaches
// it; clearing Display name back to empty resumes following. Client-side
// only -- the server stores exactly the Display name value submitted
// (submitAddUser reads addUserDisplayNameInput.value verbatim), never
// re-derived from First name.
let addUserDisplayNameFollowsFirstName = true;

// T14 (docs/backlog.md): the MyTime ambiguous-reconciliation queue,
// inside the Operations group. Its own load flag, independent of
// settingsLoaded, since it is gated by a DIFFERENT, finer-grained
// permission (guest.reconciliation.view, T15/DEC-041 item 3) than the
// rest of this group (admin.tenant_settings.view) -- the same "own
// subsection, own gate, own load flag" shape countsLoaded/usersLoaded
// already establish above.
// UX80: no latch any more: Match clients reloads on every entry, so what is on
// screen is never older than the manager's last visit.

// VEN3: the vendor list's own load flag, independent of settingsLoaded,
// since it is gated by a DIFFERENT permission (inv.vendor.view) than the
// admin.tenant_settings.view union member that used to be the only thing
// triggering loadVendors() (via loadSettings()). The same "own
// subsection, own gate, own load flag" shape as reconciliationLoaded
// immediately above. loadSettings() still calls loadVendors() too,
// unconditionally, for a session that reaches this tab through
// admin.tenant_settings.view: no regression for that already-working path.
let vendorsLoaded = false;
let vendorReadSequence = 0;
let vendorCreateRevision = 0;
let vendorCreatePending = false;
let vendorCreateRecoveryNeeded = false;
// PO2, owner ruling 2026-09-29 (DEC-247). Same "set only on success"
// latch shape vendorsLoaded uses immediately above.
let purchasingApprovalThresholdLoaded = false;
let adminReconciliationCandidates: ReconciliationCandidateDto[] = [];
// One in-flight resolve at a time, keyed by candidate id, so a second tap
// on the same row while its own request is still in flight is a no-op
// rather than a duplicate resolve attempt (the server itself also
// refuses a second resolution of the same candidate; this is the
// touch-target-level belt to that belt-and-suspenders pattern this
// codebase uses elsewhere for a single in-flight action, e.g.
// terminalBeingRenamedId above).
let reconciliationRowBusyId: string | undefined;

// MT1: own gate (mytimeSyncFreshnessVisible); reloaded on every entry to Match
// clients (UX80), so it needs no latch either.

// The fixed, five-role vocabulary (src/domain/permissions.ts's ROLES),
// hardcoded here the same way the pre-RBAC bartender/manager pair was:
// this codebase's own house style keeps a small, stable, server-owned
// enum's labels inline at its one UI call site rather than a network
// round trip for five words that essentially never change. GET
// .../tenants/:tenantId/roles exists server-side for a future caller that
// needs the list without hardcoding it.
const ROLE_OPTIONS: { value: string; label: string }[] = [
  { value: "admin", label: "Admin" },
  { value: "manager", label: "Manager" },
  { value: "assistant_manager", label: "Assistant Lounge Manager" },
  { value: "lounger", label: "Lounger" },
  { value: "inventory_manager", label: "Inventory Manager" },
  { value: "report_creator", label: "Report Creator" },
];

/** R5 (DEC-313 ruling 5): a role is shown by the tenant's CURRENT name for it, switched-off roles
 * included, so a rename shows everywhere. The shipped five are the fallback before the roles load. */
function roleLabel(value: string): string {
  return (
    adminTenantRoles.find((role) => role.role === value)?.label ??
    ROLE_OPTIONS.find((option) => option.value === value)?.label ??
    value
  );
}

/** The roles a person can be given: the tenant's switched-on roles, shipped and custom together, A to Z
 * by name (UX88, DEC-339). Falls back to the shipped five until the tenant's roles have loaded. */
function roleOptions(): { value: string; label: string }[] {
  return roleOptionsFrom(adminTenantRoles, ROLE_OPTIONS);
}

/** One item in the count-specific projection. The server returns only
 * identity, allowed count units, and (for visible sessions) the expected
 * quantity object. */
type CountWalkProduct = CountWalkItemDto;

/** One saved (or locally edited but not yet saved) observation for the
 * CURRENT open session, keyed by liquorProductId. Unit/quantity mirror
 * the last successful recordCount call; sectionId is whatever section
 * chip was selected at save time. */
interface CountWalkEntry {
  countedQuantity: string;
  unit: string;
  countSectionId: string | undefined;
  /** INV37. Set when the entry was counted in bottles but stored in ounces
   * (DEC-252): the stored ounces, shown beside the bottles the counter
   * typed. countedQuantity/unit then hold the typed bottles. */
  storedOunces?: string;
}

/** INV37. A saved or resumed count line as a walk entry. A line the ledger
 * stored in ounces after the counter typed bottles is shown, and reopened,
 * in the bottles the counter typed. */
function countWalkEntryFromLine(line: {
  counted_quantity: string;
  unit: string;
  entered_quantity?: string | null;
  entered_unit?: string | null;
  count_section_id: string | null;
}): CountWalkEntry {
  if (line.entered_unit === "bottle" && line.entered_quantity != null) {
    // The record route returns NUMERIC text ("1.5000"); the resume route
    // returns it trimmed. Trim here so both read "1.5".
    const typed = line.entered_quantity.includes(".")
      ? line.entered_quantity.replace(/0+$/, "").replace(/\.$/, "")
      : line.entered_quantity;
    return {
      countedQuantity: typed,
      unit: "bottle",
      countSectionId: line.count_section_id ?? undefined,
      storedOunces: line.counted_quantity,
    };
  }
  return {
    countedQuantity: line.counted_quantity,
    unit: line.unit,
    countSectionId: line.count_section_id ?? undefined,
  };
}

/** Local state for the currently open count-session sheet. undefined
 * whenever the sheet is not open (Slice 2's own module-local session
 * state; nothing here is persisted beyond what recordCount/closeCount-
 * Session already save server-side, so losing this object by reloading
 * the page loses only in-progress UI position, never a saved count). */
let countWalkState:
  | {
      sessionId: string;
      products: CountWalkProduct[];
      index: number;
      /** Sticky across products, on purpose: the counter physically walks
       * one shelf/section at a time, so the section chip should not reset
       * on every product the way the unit picker does. See renderCount-
       * SectionPicker's own comment. */
      selectedSectionId: string | undefined;
      entries: Map<string, CountWalkEntry>;
      view: "walk" | "review";
      // INV2 (docs/backlog.md, PRD P0.7).
      assignedCounterEmployeeId: string;
      /** PO6-S1-F3 (docs/backlog.md). Rendered through
       * resolveActorDisplayName, never this raw id, by
       * renderCountGovernanceRow. */
      assignedCounterDisplayName: string | null;
      /** Presentation mode. While a reveal projection is loading or has
       * failed, this remains true so expected quantities stay hidden. */
      blindMode: boolean;
      modeTransition?: "reveal" | "hide";
      modeRefreshRequired?: boolean;
      reviewRows?: CountSessionReviewRowDto[];
      reviewReadAt?: Date;
    }
  | undefined;

let countOperationSequence = 0;
let countBusyOriginalDisabled:
  Map<HTMLButtonElement | HTMLInputElement | HTMLSelectElement, boolean> | undefined;

function setCountSheetBusy(busy: boolean): void {
  const controls = Array.from(
    countSessionScrim.querySelectorAll<HTMLButtonElement | HTMLInputElement | HTMLSelectElement>(
      "button, input, select",
    ),
  ).filter((control) => control !== countSessionCloseButton);

  if (busy) {
    countBusyOriginalDisabled ??= new Map();
    for (const control of controls) {
      if (!countBusyOriginalDisabled.has(control)) {
        countBusyOriginalDisabled.set(control, control.disabled);
      }
      control.disabled = true;
    }
    countSessionScrim.setAttribute("aria-busy", "true");
    return;
  }

  for (const control of controls) {
    control.disabled = countBusyOriginalDisabled?.get(control) ?? false;
  }
  countBusyOriginalDisabled = undefined;
  countSessionScrim.removeAttribute("aria-busy");
}

function setCountSheetModeRetryOnly(): void {
  const controls = countSessionScrim.querySelectorAll<
    HTMLButtonElement | HTMLInputElement | HTMLSelectElement
  >("button, input, select");
  for (const control of controls) {
    if (control !== countSessionCloseButton) {
      control.disabled = control !== countBlindToggleButton;
    }
  }
  countSessionScrim.removeAttribute("aria-busy");
}

function countAsyncIsCurrent(
  gen: number,
  operation: number,
  sessionId: string,
  sessionSnapshot: Session,
): boolean {
  // Bug fix (docs writer finding, 2026-09-29; DOCSBUG3): this used to
  // compare `sessionOrUndefined() === sessionSnapshot` by OBJECT
  // IDENTITY. main.ts's refreshPermissionSnapshotOnNavigation (RT3,
  // DEC-038) replaces admin.ts's currentSession with a brand-new object
  // (`{...current, ...}`) for the SAME logged-in user on every
  // background permission-snapshot refresh, entirely unrelated to
  // counting -- so a refresh landing while a count fetch is in flight
  // used to fail this check even though nothing about the session
  // (which login, which tenant, which employee) actually changed.
  // Comparing the stable bearer token instead treats "the same login,
  // refreshed" as current, which is what it is, while still discarding
  // a response requested by a session that has genuinely changed (a
  // sign-out or a different login) between request and response.
  const current = sessionOrUndefined();
  return (
    loadIsCurrent(gen) &&
    current !== undefined &&
    current.token === sessionSnapshot.token &&
    countOperationSequence === operation &&
    countWalkState?.sessionId === sessionId
  );
}

/** Gate finding B fix. Keyed by count session id, updated the instant a
 * count actually saves (handleCountSaveNext), and read back by
 * openCountSessionSheet whenever a session that already has at least one
 * observation is resumed. This is the one place this screen can honestly
 * learn "which section was the counter just standing at": the
 * count-session detail route's own observations (GET
 * .../count-sessions/:id) are DISTINCT ON liquor_product_id, so their
 * array order is by product id, never by recency, and nothing in that
 * response carries a per-observation timestamp fine enough to derive
 * true recency from it alone (see src/domain/counts.ts's
 * listLatestObservations). This map survives exactly the pause the
 * gate's own scenario exercises (closing the counting sheet clears
 * countWalkState via pauseCountSession, never this map); a genuine full
 * page reload loses it, the same accepted limit every other purely
 * in-memory piece of this screen's state already has (see
 * countWalkState's own comment above), and resuming then falls back to
 * "No section" honestly rather than guessing from the unordered
 * response. Cleared for a session once that session actually closes
 * (handleConfirmCloseCountSession), so this cannot grow without bound
 * over a long-lived tab. */
const lastRecordedCountSectionIdBySession = new Map<string, string | undefined>();

// Review 9 correction (QA gate, Low): lock body scroll while an admin
// sheet is open. A plain counter, not a boolean, because two sheets can
// be open at once (the bottle form plus the confirm sheet stacked over
// it); scroll only unlocks once the LAST open sheet closes.
let openSheetCount = 0;

function lockBodyScroll(): void {
  openSheetCount += 1;
  document.body.style.overflow = "hidden";
}

function unlockBodyScroll(): void {
  openSheetCount = Math.max(0, openSheetCount - 1);
  if (openSheetCount === 0) {
    document.body.style.overflow = "";
  }
}

function session(): Session {
  return currentSession ?? requireSession();
}

/** SET16-F1: session()'s non-throwing counterpart. An await inside a
 * handler (a confirm sheet an admin may leave open for a while, a
 * settings fetch) can span a real session teardown; a call site that
 * resumes after one of those and already knows how to bail out quietly
 * uses this and returns early on undefined, instead of letting
 * session()'s throw reach nothing that could show it. Mirrors
 * session()'s own currentSession-then-page-session fallback, without
 * the throw. */
function sessionOrUndefined(): Session | undefined {
  return currentSession ?? pageSessionOrUndefined();
}

/** Permission refresh replaces the Session object even when the same authorized login remains current. */
function adminReadSessionIsCurrent(snapshot: Session): boolean {
  const active = sessionOrUndefined();
  if (active === undefined || active.token !== snapshot.token || active.userId !== snapshot.userId ||
    active.tenantId !== snapshot.tenantId || active.apiBaseUrl !== snapshot.apiBaseUrl ||
    active.mustChangePin !== snapshot.mustChangePin) return false;
  const permissions = Object.keys(snapshot.permissions);
  return permissions.length === Object.keys(active.permissions).length &&
    permissions.every(permission => active.permissions[permission] === snapshot.permissions[permission]);
}

/** SET16-F4 (docs/build-briefs/SET16-F4-discard-invalidated-admin-loads.md):
 * a loader captures `const gen = loadGeneration` before its first await
 * and calls `loadIsCurrent(gen)` after every await, discarding the
 * response (writing no latch, no module binding, no DOM) when it is
 * false. `invalidate()` INCREMENTS this, never resets it to 0: a reset
 * would make a stale in-flight response captured at 0 look current again
 * to whichever session comes next, the exact failure mode
 * countsLoadSequence's own comment already documents for its own,
 * narrower counter. sessionOrUndefined() alone cannot answer this
 * question once a second employee has signed in, because it is defined
 * again by then; this counter distinguishes the DEAD session's own
 * in-flight response from the LIVE one only because invalidate() moved
 * it. Scope is the `load*` family plus openEditDrinkForm and
 * loadBottleOnHand (the two form openers that fetch before rendering),
 * not every async function in this file: SET17 already measured and
 * rejected a sweep of every await as a 170-finding shape. */
let loadGeneration = 0;

/** True only while invalidate() resets the sections; see the unifiedCatalog changed hook. */
let adminTeardownRunning = false;

function loadIsCurrent(gen: number): boolean {
  return gen === loadGeneration;
}

function adminToast(message: string, variant: ToastVariant): void {
  showToast(message, variant);
}

// ---------------------------------------------------------------------
// Review 9 correction (both gates, Medium): shared destructive-
// deactivation confirm sheet. One in-flight confirmation at a time (the
// only caller pattern this screen has: a single toggle or form-save
// action awaiting a single answer); a second call while one is already
// open would be a bug in the caller, not something this needs to queue.
// ---------------------------------------------------------------------

let pendingConfirmResolve: ((confirmed: boolean) => void) | undefined;

function showConfirmSheet(options: {
  title: string;
  message: string;
  confirmLabel: string;
  /** DOCSBUG4: defaults to "Cancel". */
  cancelLabel?: string;
  /** DOCSBUG4: a notice with one button, nothing to cancel. */
  hideCancel?: boolean;
  /** DOCSBUG4: the confirm button is the accent primary, not danger red,
   * for an action that deletes nothing. Default is the danger look the
   * deactivation callers rely on. */
  tone?: "accent";
  /** DOCSBUG4: a richer body shown under the message. */
  extra?: HTMLElement;
  /** DOCSBUG4: the message is a refusal, shown in a warning box. */
  refusal?: boolean;
  /** AU14-F14: the confirm button starts blocked, until the body (a required
   * reason picker) calls setConfirmSheetBlocked(false). */
  confirmBlocked?: boolean;
}): Promise<boolean> {
  confirmTitleEl.textContent = options.title;
  confirmConfirmButton.disabled = options.confirmBlocked === true;
  confirmMessageEl.textContent = options.message;
  confirmConfirmButton.textContent = options.confirmLabel;
  confirmCancelButton.textContent = options.cancelLabel ?? "Cancel";
  confirmCancelButton.hidden = options.hideCancel === true;
  confirmConfirmButton.classList.toggle("btn-danger", options.tone !== "accent");
  confirmConfirmButton.classList.toggle("btn-primary", options.tone === "accent");
  confirmMessageEl.classList.toggle("admin-confirm-message--lead", options.extra !== undefined);
  confirmMessageEl.classList.toggle("admin-confirm-message--refusal", options.refusal === true);
  confirmExtraEl.replaceChildren(...(options.extra === undefined ? [] : [options.extra]));
  confirmExtraEl.hidden = options.extra === undefined;
  confirmScrim.hidden = false;
  lockBodyScroll();
  return new Promise<boolean>((resolve) => {
    pendingConfirmResolve = resolve;
  });
}

/** AU14-F14: wake or block the open confirm sheet's confirm button. */
function setConfirmSheetBlocked(blocked: boolean): void {
  confirmConfirmButton.disabled = blocked;
}

function resolveConfirmSheet(confirmed: boolean): void {
  if (confirmScrim.hidden) {
    return;
  }
  confirmScrim.hidden = true;
  unlockBodyScroll();
  const resolve = pendingConfirmResolve;
  pendingConfirmResolve = undefined;
  resolve?.(confirmed);
}

// ---------------------------------------------------------------------
// UX11: one shared activation control. Every screen that lets an Admin
// turn something on or off (a drink, a vocabulary value, and in time the
// rest of this file's own several copies of the same markup) builds the
// switch through this one function, so there is exactly one place that
// decides what the control looks like and how it behaves, rather than a
// dozen near-identical inline blocks that can drift apart. This also
// keeps the one behavior that matters non-negotiable in one place:
// turning something OFF is a destructive action and always confirms
// first; turning it back ON never does.
// ---------------------------------------------------------------------

interface ActivationSwitchWriteResult {
  readonly ok: boolean;
  /** The server's own resulting active value. Present on success; read
   * back rather than assumed, since a request can be coalesced or
   * refused in ways the caller does not predict. */
  readonly active?: boolean;
  /** Present on failure: a plain-language reason to show the Admin. */
  readonly message?: string;
}

interface CurrentActivationControl {
  readonly input: HTMLInputElement;
  readonly status: HTMLElement;
  readonly active: boolean;
}

interface ActivationSwitchConfig {
  readonly active: boolean;
  /** A refreshable list resolves its current record and controls by stable id. */
  readonly resolveCurrent?: () => CurrentActivationControl | undefined;
  /** Used in the confirm dialog, the switch's own aria-label, and the
   * default error toast. */
  readonly entityLabel: string;
  readonly toggleTestId: string;
  readonly statusTestId: string;
  /** UX37 (DEC-184): the id of the text that says what this switch is for and
   * what Off does. check:settings-described refuses a call that omits it,
   * unless the function is listed there as not a settings screen. */
  readonly describedById?: string;
  readonly activeText: string;
  readonly inactiveText: string;
  /** Confirm-sheet copy for the destructive active -> inactive move.
   * Never called for the inactive -> active direction. */
  readonly confirmDeactivate?: (entityLabel: string) => {
    title: string;
    message: string;
    confirmLabel: string;
  };
  /** TF4-F2: optional confirm for the inactive -> active move, for a switch
   * where turning ON takes something from another row (the ABC2GO mark).
   * Returns undefined when nothing needs asking. Absent for every other
   * switch, which never confirms turning on. */
  readonly confirmActivate?: () => Parameters<typeof showConfirmSheet>[0] | undefined;
  /** Performs the write and reports the server's own resulting state.
   * This is the ONLY place in this file that may call the underlying
   * activation write for the entity kind the caller is rendering
   * (enforced by harness/scripts/check-activation-control.ts). */
  readonly write: (nextActive: boolean) => Promise<ActivationSwitchWriteResult>;
  /** Runs after a successful write, with the server's own resulting
   * active value. */
  readonly onChange?: (active: boolean) => void;
  /** Runs after a failed write, with a plain-language message. Defaults
   * to the shared toast; a caller with its own inline status line (the
   * vocabulary editors, matching every other list on this screen) passes
   * its own. */
  readonly onError?: (message: string) => void;
  /** CORE13. Draws the switch disabled, for a session whose permission set does not allow the write
   * behind it, so the refusal is not discovered by tapping. Decided by the caller from a permission,
   * never from a role name. */
  readonly disabled?: boolean;
}

function renderActivationSwitch(config: ActivationSwitchConfig): HTMLLabelElement {
  const toggleLabel = document.createElement("label");
  toggleLabel.className = "admin-toggle-label";
  // Tapping the switch itself never triggers a row's own "open the edit
  // form" click handler, the same convention every switch on this screen
  // already follows.
  toggleLabel.addEventListener("click", (event) => event.stopPropagation());

  const toggleInput = document.createElement("input");
  toggleInput.type = "checkbox";
  toggleInput.className = "admin-toggle-input";
  toggleInput.dataset["testid"] = config.toggleTestId;
  toggleInput.checked = config.active;
  toggleInput.disabled = config.disabled === true;
  toggleInput.setAttribute("aria-label", `${config.entityLabel} active`);
  describeSetting(toggleInput, config.describedById);

  const toggleTrack = document.createElement("span");
  toggleTrack.className = "admin-toggle-track";

  const toggleStatus = document.createElement("span");
  toggleStatus.className = "admin-toggle-status";
  toggleStatus.dataset["testid"] = config.statusTestId;
  toggleStatus.textContent = config.active ? config.activeText : config.inactiveText;

  toggleInput.addEventListener(
    "change",
    () => void applyActivationSwitchChange(config, toggleInput, toggleStatus),
  );

  toggleLabel.append(toggleInput, toggleTrack, toggleStatus);
  return toggleLabel;
}

async function applyActivationSwitchChange(
  config: ActivationSwitchConfig,
  input: HTMLInputElement,
  statusEl: HTMLElement,
): Promise<void> {
  let nextActive = input.checked;
  const openingGeneration = loadGeneration;
  const openingSession = sessionOrUndefined();
  const openingSite = inventorySellingSiteId();
  const originalInput = input;
  const openingControl = config.resolveCurrent?.();
  const currentControl = (): CurrentActivationControl | undefined => {
    if (config.resolveCurrent === undefined) return undefined;
    if (openingSession === undefined || !loadIsCurrent(openingGeneration) ||
      !adminReadSessionIsCurrent(openingSession) || openingSite !== inventorySellingSiteId())
      return undefined;
    return config.resolveCurrent();
  };
  const restoreCurrentFocus = (): void => {
    if (originalInput.isConnected ||
      (document.activeElement !== document.body && document.activeElement !== originalInput)) return;
    currentControl()?.input.focus({ preventScroll: true });
  };
  if (config.resolveCurrent !== undefined && openingControl === undefined) return;

  // The checkbox has ALREADY flipped to its new (unconfirmed) state by
  // the time a native "change" event fires. Deactivating is destructive,
  // so this reverts the switch back to its original position immediately
  // and only actually deactivates once the Admin confirms; a cancelled
  // confirmation leaves it there, never flipped. Reactivating needs no
  // confirmation.
  if (!nextActive && config.confirmDeactivate !== undefined) {
    input.checked = true;
    const confirmed = await showConfirmSheet(config.confirmDeactivate(config.entityLabel));
    if (!confirmed) {
      restoreCurrentFocus();
      return;
    }
    input.checked = false;
    nextActive = false;
  }
  const activateConfirm = nextActive ? config.confirmActivate?.() : undefined;
  if (activateConfirm !== undefined) {
    input.checked = false;
    const confirmed = await showConfirmSheet(activateConfirm);
    if (!confirmed) {
      return;
    }
    input.checked = true;
  }

  if (config.resolveCurrent !== undefined) {
    const current = currentControl();
    if (current === undefined || current.active !== openingControl?.active) {
      if (current !== undefined) {
        current.input.checked = current.active;
        current.status.textContent = current.active ? config.activeText : config.inactiveText;
      }
      restoreCurrentFocus();
      return;
    }
    input = current.input;
    statusEl = current.status;
    input.checked = nextActive;
    restoreCurrentFocus();
  }

  // SET16-F1: the deactivate path's own confirm sheet above can sit
  // open long enough for the acting employee's session to actually die.
  // Every config.write implementation on this screen re-resolves its
  // own session with session() rather than reusing one captured before
  // the confirm, so one guard at this shared chokepoint protects all of
  // them instead of a copy inside each.
  if (sessionOrUndefined() === undefined) {
    return;
  }
  const gen = loadGeneration;
  const writeSession = sessionOrUndefined();
  input.disabled = true;
  const result = await config.write(nextActive);
  if (!loadIsCurrent(gen) || sessionOrUndefined()?.token !== writeSession?.token) return;
  if (config.resolveCurrent !== undefined) {
    const current = currentControl();
    if (current === undefined) return;
    input = current.input;
    statusEl = current.status;
  }
  input.disabled = false;

  if (!result.ok || result.active === undefined) {
    input.checked = !nextActive;
    const message = result.message ?? "Could not change that.";
    if (config.onError !== undefined) {
      config.onError(message);
    } else {
      adminToast(
        `Could not change ${config.entityLabel}. ${capitalizeServerMessage(message)}`,
        "danger",
      );
    }
    return;
  }

  input.checked = result.active;
  statusEl.textContent = result.active ? config.activeText : config.inactiveText;
  config.onChange?.(result.active);
}

/** Review 9 correction (both gates, Medium): when a bottle being
 * deactivated is the LAST remaining active bottle in its category, and
 * some active menu drink's liquorSlots requires that category, the
 * confirm sheet names up to three of the affected drinks. Fetched fresh
 * from the server rather than reusing adminLiquorProducts/adminMenuDrinks
 * (which may reflect a narrowed search filter or an unloaded panel, not
 * the tenant's full active set) so this check is correct regardless of
 * what the manager happens to have searched for or which tab they opened
 * first. Returns undefined when there is nothing to warn about (another
 * active bottle covers the category, or no active drink needs it), never
 * throws: a failed advisory lookup falls back to the plain confirm with
 * no warning line rather than blocking deactivation, since the server
 * itself stays permissive here (see the confirm sheet's own message and
 * docs/review-record.md's Review 9 disposition). */
async function computeBottleDeactivationWarning(
  bottle: LiquorProductDto,
): Promise<string | undefined> {
  const s = session();
  const siblingsResult = await listLiquorProducts(s.apiBaseUrl, s.tenantId, {
    category: bottle.category,
    activeOnly: true,
  });
  if (siblingsResult.status !== 200) {
    return undefined;
  }
  const otherActiveSiblings = siblingsResult.body.liquorProducts.filter(
    (product) => product.id !== bottle.id,
  );
  if (otherActiveSiblings.length > 0) {
    return undefined;
  }
  const drinksResult = await listMenuDrinks(s.apiBaseUrl, s.tenantId, { activeOnly: true });
  if (drinksResult.status !== 200) {
    return undefined;
  }
  const affected = drinksResult.body.menuDrinks.filter((drink) =>
    drink.liquor_slots.includes(bottle.category),
  );
  if (affected.length === 0) {
    return undefined;
  }
  const shown = affected.slice(0, 3).map((drink) => drink.name);
  const remaining = affected.length - shown.length;
  const drinkList = remaining > 0 ? `${shown.join(", ")}, and ${remaining} more` : shown.join(", ");
  return (
    `${bottle.name} is the last active bottle in ${categoryLabel(bottle.category)}. ` +
    `Guests can still substitute, but the standard list for these drinks goes empty: ${drinkList}.`
  );
}

// SETIA1 reuses each bound editor once, moving its DOM node without changing saved data.
const inventorySetupState = { tab: "items" as "items" | "setup", kind: "" };
const setupEditorNodes = new Map<string, HTMLElement>();

// Phase E keeps current read authority separate from immutable sent commands.
interface VendorSetupIntent {
  actor: Pick<Session, "apiBaseUrl" | "tenantId" | "userId">;
  body: Readonly<EnforceVendorTermsInput>;
  reviewedSites: ReadonlyArray<Readonly<VendorTermsSetupContextDto["locations"][number]>>;
  generation: number;
  busy: boolean;
  message: string;
}
const vendorSetupOriginals = new Map<string, VendorSetupIntent>();
const vendorSetupUi: {
  generation: number;
  sequence: number;
  host?: HTMLElement;
  read: "unverified" | "loading" | "available" | "unavailable" | "denied";
  scope?: string;
  readSession?: Session;
  context?: VendorTermsSetupContextDto;
  message: string;
  confirming: boolean;
  reviewing: boolean;
  detailsOpen: boolean;
  detailsState: "idle" | "loading" | "ready" | "unavailable";
  details?: Array<{
    item: string;
    vendor: string;
    sku: string | null;
    cost: number | null;
    unit: string | null;
  }>;
} = {
  generation: 0,
  sequence: 0,
  read: "unverified",
  message: "",
  confirming: false,
  reviewing: false,
  detailsOpen: false,
  detailsState: "idle",
};
function vendorSetupScope(active = sessionOrUndefined()): string | undefined {
  return active === undefined
    ? undefined
    : JSON.stringify([active.apiBaseUrl, active.tenantId, active.userId]);
}
function vendorSetupCurrent(active: Session, generation: number): boolean {
  return (
    generation === vendorSetupUi.generation &&
    sessionOrUndefined() === active &&
    currentSection === "inventory" &&
    currentOperationsView === undefined &&
    inventorySetupState.tab === "setup" &&
    hasPermission(active, "inv.vendor_offer.manage")
  );
}
interface VendorSetupConfirmation {
  extra: HTMLElement;
  resolve: typeof pendingConfirmResolve;
  active: Session;
  generation: number;
  escape: (event: KeyboardEvent) => void;
}
let vendorSetupConfirmation: VendorSetupConfirmation | undefined;
function restoreVendorSetupFocus(active: Session, generation: number): void {
  if (!vendorSetupCurrent(active, generation) || !confirmScrim.hidden) return;
  const trigger = vendorSetupUi.host?.querySelector<HTMLButtonElement>(
    '[data-vendor-setup-action="begin"]',
  );
  const target =
    trigger?.isConnected === true && !trigger.disabled
      ? trigger
      : vendorSetupUi.host?.querySelector<HTMLElement>("#admin-vendor-options-setup-heading");
  if (target?.isConnected === true) target.focus();
}
function disposeVendorSetupConfirmation(owned: VendorSetupConfirmation, cancel: boolean): void {
  document.removeEventListener("keydown", owned.escape);
  // Shared callers replace the extra node. Identity keeps an old continuation
  // from clearing or dismissing a newer, unrelated shared confirmation.
  if (confirmExtraEl.contains(owned.extra)) {
    if (cancel && pendingConfirmResolve === owned.resolve) resolveConfirmSheet(false);
    owned.extra.remove();
    confirmExtraEl.hidden = true;
    confirmMessageEl.textContent = "";
  }
  if (vendorSetupConfirmation === owned) {
    vendorSetupConfirmation = undefined;
    vendorSetupUi.confirming = false;
  }
}

function clearVendorSetupView(): void {
  vendorSetupUi.generation += 1;
  vendorSetupUi.sequence += 1;
  vendorSetupUi.read = "unverified";
  delete vendorSetupUi.scope;
  delete vendorSetupUi.readSession;
  delete vendorSetupUi.context;
  delete vendorSetupUi.details;
  vendorSetupUi.detailsState = "idle";
  vendorSetupUi.detailsOpen = false;
  vendorSetupUi.message = "";
  vendorSetupUi.reviewing = false;
  if (vendorSetupConfirmation !== undefined)
    disposeVendorSetupConfirmation(vendorSetupConfirmation, true);
  vendorSetupUi.host?.replaceChildren();
}
function validVendorSetupContext(value: unknown): value is VendorTermsSetupContextDto {
  if (value === null || typeof value !== "object") return false;
  const data = value as VendorTermsSetupContextDto;
  return (
    (data.state === "preparation" || data.state === "enforced") &&
    Array.isArray(data.locations) &&
    data.locations.every(
      (site, index, sites) =>
        site !== null &&
        typeof site === "object" &&
        typeof site.id === "string" &&
        /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/.test(site.id) &&
        typeof site.name === "string" &&
        typeof site.active === "boolean" &&
        (index === 0 || sites[index - 1]!.id < site.id),
    )
  );
}
function vendorSetupBusiness(active: Session): string {
  const config = loadTerminalConfig();
  return config?.tenantId === active.tenantId &&
    config.apiBaseUrl.replace(/\/$/, "") === active.apiBaseUrl.replace(/\/$/, "") &&
    config.businessName?.trim()
    ? config.businessName.trim()
    : "this business";
}
function vendorSetupParagraph(text: string): HTMLParagraphElement {
  const paragraph = document.createElement("p");
  paragraph.className = "admin-field-help";
  paragraph.textContent = text;
  return paragraph;
}
function vendorSetupButton(
  label: string,
  action: () => void,
  disabled = false,
  primary = false,
): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = primary ? "btn-primary" : "btn-secondary";
  button.textContent = label;
  button.disabled = disabled;
  button.addEventListener("click", action);
  return button;
}
function renderVendorSetupCard(): void {
  const host = vendorSetupUi.host;
  if (host === undefined) return;
  const active = sessionOrUndefined();
  const allowed = active !== undefined && hasPermission(active, "inv.vendor_offer.manage");
  host.hidden = !allowed;
  host.replaceChildren();
  if (!allowed || active === undefined) return;
  const key = vendorSetupScope(active)!;
  const original = vendorSetupOriginals.get(key);
  const context =
    vendorSetupUi.read === "available" && vendorSetupUi.scope === key
      ? vendorSetupUi.context
      : undefined;
  const headingRow = document.createElement("div");
  headingRow.className = "setia1-section-heading";
  const heading = document.createElement("h2");
  heading.className = "section-title";
  heading.id = "admin-vendor-options-setup-heading";
  heading.tabIndex = -1;
  heading.textContent = "Vendor Options";
  const mode = document.createElement("span");
  mode.className = "inv-locations-tag";
  mode.textContent =
    original !== undefined
      ? "Change unconfirmed"
      : context?.state === "enforced"
        ? "Per-location details"
        : context === undefined
          ? "State unavailable"
          : "Shared details";
  hoverHelp(
    heading,
    "Controls whether Vendor SKUs and purchase costs are shared or kept separately for each location.",
    "Used in Vendor Options and purchasing. Existing orders keep their recorded details.",
    "Two locations can buy the same item from a vendor at different costs.",
  );
  headingRow.append(heading, mode);
  host.append(headingRow, vendorSetupParagraph(`All locations in ${vendorSetupBusiness(active)}`));
  if (context !== undefined)
    host.append(
      vendorSetupParagraph(
        `${context.locations.length} ${context.locations.length === 1 ? "location" : "locations"}, including inactive locations.`,
      ),
    );
  host.append(
    vendorSetupParagraph(
      original !== undefined
        ? original.busy
          ? "Saving this change for all reviewed locations…"
          : "Your original change has not been confirmed. Retry the original request to confirm its result."
        : context?.state === "enforced"
          ? "Vendor SKUs and purchase costs are managed separately for each location."
          : vendorSetupUi.read === "loading"
            ? "Loading vendor details…"
            : context === undefined
              ? "Vendor details could not be loaded. Current access to every location is required."
              : "Vendor SKUs and purchase costs are shared across your locations.",
    ),
  );
  if (original !== undefined && context !== undefined) {
    host.append(
      vendorSetupParagraph(
        `Current state: ${context.state === "enforced" ? "per-location details" : "shared details"}. This read does not confirm the original request.`,
      ),
    );
    host.append(
      vendorSetupParagraph(
        `Original reviewed locations: ${original.reviewedSites.map((site) => `${site.name}${site.active ? "" : " (inactive)"}`).join(", ")}.`,
      ),
    );
  }
  const status = vendorSetupParagraph(
    vendorSetupUi.message ||
      (original !== undefined
        ? "Original request retained in this running page. Keep this page open until its result is confirmed."
        : context?.locations.length === 0
          ? "Add a location before reviewing this change."
          : ""),
  );
  status.setAttribute("role", "status");
  host.append(status);
  if (context?.state === "preparation" && original === undefined)
    renderVendorSetupDetails(host, active);
  const actions = document.createElement("div");
  actions.className = "admin-vendor-ordering-actions";
  if (original !== undefined) {
    actions.append(
      vendorSetupButton(
        "Retry original request",
        () => void sendVendorSetupOriginal(original),
        original.busy,
        true,
      ),
      vendorSetupButton(
        "Refresh current state",
        () => void loadVendorSetupContext(),
        original.busy || vendorSetupUi.read === "loading",
      ),
    );
  } else if (context?.state !== "enforced") {
    const begin = vendorSetupButton(
      "Use per-location details",
      () => void reviewVendorSetup(),
      context === undefined || context.locations.length === 0 || vendorSetupUi.reviewing,
      true,
    );
    begin.dataset["vendorSetupAction"] = "begin";
    actions.append(begin);
    if (context === undefined && vendorSetupUi.read !== "loading")
      actions.append(vendorSetupButton("Reload", () => void loadVendorSetupContext()));
  }
  host.append(
    actions,
    vendorSetupParagraph(
      "A submitted request is retained only while this page remains open. Reloading, closing the tab or restarting loses its original request identity. A later current-state read cannot recover that identity or confirm its outcome.",
    ),
  );
}
async function loadVendorSetupContext(): Promise<void> {
  const active = sessionOrUndefined();
  if (active === undefined || !hasPermission(active, "inv.vendor_offer.manage")) {
    clearVendorSetupView();
    renderVendorSetupCard();
    return;
  }
  const gen = vendorSetupUi.generation;
  const seq = ++vendorSetupUi.sequence;
  vendorSetupUi.read = "loading";
  vendorSetupUi.scope = vendorSetupScope(active)!;
  vendorSetupUi.readSession = active;
  delete vendorSetupUi.context;
  delete vendorSetupUi.details;
  vendorSetupUi.detailsState = "idle";
  vendorSetupUi.detailsOpen = false;
  renderVendorSetupCard();
  const result = await getVendorTermsSetupContext(active.apiBaseUrl, active.tenantId);
  if (!vendorSetupCurrent(active, gen) || seq !== vendorSetupUi.sequence) return;
  if (result.status !== 200 || !validVendorSetupContext(result.body)) {
    vendorSetupUi.read = result.status === 403 ? "denied" : "unavailable";
    vendorSetupUi.message =
      "Vendor details are unavailable. Access to every location is required; any original request is retained.";
  } else {
    vendorSetupUi.read = "available";
    vendorSetupUi.context = result.body;
    vendorSetupUi.message = "";
  }
  renderVendorSetupCard();
}
async function reviewVendorSetup(): Promise<void> {
  const active = sessionOrUndefined();
  const shown = vendorSetupUi.context;
  const key = vendorSetupScope(active);
  if (
    active === undefined ||
    shown === undefined ||
    key === undefined ||
    vendorSetupOriginals.has(key) ||
    shown.state !== "preparation" ||
    shown.locations.length === 0 ||
    vendorSetupUi.read !== "available" ||
    vendorSetupUi.reviewing ||
    vendorSetupUi.confirming
  )
    return;
  const gen = vendorSetupUi.generation;
  vendorSetupUi.reviewing = true;
  renderVendorSetupCard();
  const response = await getVendorTermsSetupContext(active.apiBaseUrl, active.tenantId);
  if (!vendorSetupCurrent(active, gen)) return;
  vendorSetupUi.reviewing = false;
  if (response.status !== 200 || !validVendorSetupContext(response.body)) {
    vendorSetupUi.read = response.status === 403 ? "denied" : "unavailable";
    delete vendorSetupUi.context;
    delete vendorSetupUi.details;
    vendorSetupUi.message = "Vendor details could not be reviewed. Reload before continuing.";
    renderVendorSetupCard();
    return;
  }
  vendorSetupUi.context = response.body;
  if (
    response.body.state !== "preparation" ||
    JSON.stringify(response.body.locations.map((site) => site.id)) !==
      JSON.stringify(shown.locations.map((site) => site.id))
  ) {
    vendorSetupUi.message =
      "The current location list or state changed before sending. Review the current locations again.";
    renderVendorSetupCard();
    return;
  }
  const review = response.body;
  const extra = document.createElement("div");
  const list = document.createElement("ul");
  list.setAttribute("aria-label", "All affected locations");
  for (const site of review.locations) {
    const row = document.createElement("li");
    row.textContent = `${site.name}${site.active ? "" : " (inactive)"}`;
    list.append(row);
  }
  extra.append(
    list,
    vendorSetupParagraph(
      "Each location starts with the current shared codes and costs. Missing values stay missing; $0.00 stays $0.00.",
    ),
    vendorSetupParagraph("This cannot be switched back. Selling prices stay unchanged."),
  );
  hideHoverHelp();
  vendorSetupUi.confirming = true;
  const answer = showConfirmSheet({
    title: "Use per-location details?",
    message: `Each location will keep its own Vendor SKUs and purchase costs. This change applies to all locations in ${vendorSetupBusiness(active)}.`,
    confirmLabel: "Use per-location details",
    cancelLabel: "Cancel",
    tone: "accent",
    extra,
    confirmBlocked: review.locations.length === 0,
  });
  const owned: VendorSetupConfirmation = {
    extra,
    resolve: pendingConfirmResolve,
    active,
    generation: gen,
    escape: (event) => {
      if (
        event.key !== "Escape" ||
        event.defaultPrevented ||
        document.querySelector(".hover-tip") !== null ||
        vendorSetupConfirmation !== owned ||
        !vendorSetupCurrent(active, gen) ||
        !confirmExtraEl.contains(extra) ||
        pendingConfirmResolve !== owned.resolve
      )
        return;
      event.preventDefault();
      event.stopPropagation();
      resolveConfirmSheet(false);
    },
  };
  vendorSetupConfirmation = owned;
  document.addEventListener("keydown", owned.escape);
  confirmCancelButton.focus();
  const confirmed = await answer;
  disposeVendorSetupConfirmation(owned, false);
  if (
    !vendorSetupCurrent(active, gen) ||
    vendorSetupScope() !== key ||
    vendorSetupOriginals.has(key)
  )
    return;
  if (!confirmed) {
    renderVendorSetupCard();
    restoreVendorSetupFocus(active, gen);
    return;
  }
  const original: VendorSetupIntent = {
    actor: { apiBaseUrl: active.apiBaseUrl, tenantId: active.tenantId, userId: active.userId },
    body: Object.freeze({
      requestId: crypto.randomUUID(),
      reviewedLocationIds: Object.freeze(review.locations.map((site) => site.id)),
    }),
    reviewedSites: Object.freeze(review.locations.map((site) => Object.freeze({ ...site }))),
    generation: gen,
    busy: false,
    message: "",
  };
  vendorSetupOriginals.set(key, original);
  const submission = sendVendorSetupOriginal(original);
  restoreVendorSetupFocus(active, gen);
  await submission;
}
async function sendVendorSetupOriginal(original: VendorSetupIntent): Promise<void> {
  const active = sessionOrUndefined();
  const key = vendorSetupScope(active);
  if (
    active === undefined ||
    key === undefined ||
    vendorSetupOriginals.get(key) !== original ||
    original.actor.apiBaseUrl !== active.apiBaseUrl ||
    original.actor.tenantId !== active.tenantId ||
    original.actor.userId !== active.userId ||
    original.busy ||
    !hasPermission(active, "inv.vendor_offer.manage")
  )
    return;
  const gen = vendorSetupUi.generation;
  original.busy = true;
  original.message = "";
  renderVendorSetupCard();
  const response = await enforceVendorTermsSetup(active.apiBaseUrl, active.tenantId, original.body);
  if (vendorSetupOriginals.get(key) !== original) return;
  original.busy = false;
  const result: Partial<EnforceVendorTermsResultDto> | null = response.body;
  const applied =
    response.status === 200 &&
    result !== null &&
    result.state === "enforced" &&
    Number.isSafeInteger(result.version) &&
    result.version! > 0 &&
    result.requestId === original.body.requestId &&
    typeof result.replayed === "boolean";
  if (applied) vendorSetupOriginals.delete(key);
  else
    original.message =
      "The original change could not be confirmed. Retry the original request when access is available. A changed location list cannot replace this submitted review.";
  if (!vendorSetupCurrent(active, gen)) return;
  if (applied) {
    // The captured result may settle while another session/view owns a newer
    // draft. Only this still-current Setup view can clear disposable reads.
    redactBottleVendorTerms(false);
    bottlePreferredToken += 1;
    resetInventoryImport();
    resetVendorPriceList();
    if (!bottleFormScrim.hidden && editingBottleId !== undefined)
      void loadBottleVendorOffers(editingBottleId);
    await loadVendorSetupContext();
    if (vendorSetupCurrent(active, gen)) {
      vendorSetupUi.message = "Per-location details are in use. The original request succeeded.";
      renderVendorSetupCard();
    }
  } else {
    vendorSetupUi.message = original.message;
    if (response.status === 403) {
      vendorSetupUi.read = "denied";
      delete vendorSetupUi.context;
      delete vendorSetupUi.details;
    }
    renderVendorSetupCard();
  }
}

function refreshVendorSetupAccess(): void {
  const active = sessionOrUndefined();
  if (active === undefined || !hasPermission(active, "inv.vendor_offer.manage")) {
    clearVendorSetupView();
    renderVendorSetupCard();
    return;
  }
  if (currentSection === "inventory" && currentOperationsView === undefined && inventorySetupState.tab === "setup") {
    if (vendorSetupUi.readSession !== active || vendorSetupUi.scope !== vendorSetupScope(active)) {
      clearVendorSetupView();
      void loadVendorSetupContext();
    } else renderVendorSetupCard();
  }
}
function renderVendorSetupDetails(host: HTMLElement, active: Session): void {
  if (vendorSetupUi.detailsState === "unavailable") {
    host.append(vendorSetupParagraph("Current shared details could not be loaded."));
    return;
  }
  if (
    !["inv.vendor_offer.view", "inv.vendor.view", "admin.liquor_product.view"].every((permission) =>
      hasPermission(active, permission),
    )
  ) {
    host.append(
      vendorSetupParagraph("Current shared details are unavailable with your current access."),
    );
    return;
  }
  const details = document.createElement("details");
  details.open = vendorSetupUi.detailsOpen;
  const summary = document.createElement("summary");
  summary.textContent = "Current shared details";
  details.append(summary);
  if (vendorSetupUi.detailsState === "ready" && vendorSetupUi.details !== undefined) {
    if (vendorSetupUi.details.length === 0)
      details.append(vendorSetupParagraph("No Vendor Options are configured yet."));
    else {
      const table = document.createElement("table");
      table.className = "admin-table";
      const head = document.createElement("tr");
      for (const text of ["Item / vendor", "Vendor SKU", "Purchase cost"]) {
        const cell = document.createElement("th");
        cell.textContent = text;
        head.append(cell);
      }
      table.append(head);
      for (const entry of vendorSetupUi.details) {
        const row = document.createElement("tr");
        for (const text of [
          `${entry.item} / ${entry.vendor}`,
          entry.sku ?? "Not set",
          entry.cost === null
            ? "Not set"
            : `${formatCents(entry.cost)}${entry.unit === null ? "" : ` per ${orderUnitDisplayLabel(entry.unit)}`}`,
        ]) {
          const cell = document.createElement("td");
          cell.textContent = text;
          row.append(cell);
        }
        table.append(row);
      }
      details.append(table);
    }
  } else
    details.append(
      vendorSetupParagraph(
        vendorSetupUi.detailsState === "loading"
          ? "Loading shared details…"
          : "Open to view the currently shared codes and costs.",
      ),
    );
  details.addEventListener("toggle", () => {
    vendorSetupUi.detailsOpen = details.open;
    if (details.open && vendorSetupUi.detailsState === "idle") void loadVendorSetupDetails(active);
  });
  host.append(details);
}
async function loadVendorSetupDetails(active: Session): Promise<void> {
  const gen = vendorSetupUi.generation;
  const seq = vendorSetupUi.sequence;
  if (vendorSetupUi.context?.state !== "preparation" || vendorSetupUi.read !== "available") return;
  vendorSetupUi.detailsState = "loading";
  renderVendorSetupCard();
  const [offers, vendors, products] = await Promise.all([
    listVendorItemOffers(active.apiBaseUrl, active.tenantId, { activeOnly: false }),
    listVendors(active.apiBaseUrl, active.tenantId, { activeOnly: false }),
    listLiquorProducts(active.apiBaseUrl, active.tenantId, { activeOnly: false }),
  ]);
  if (!vendorSetupCurrent(active, gen) || seq !== vendorSetupUi.sequence) return;
  const currentMode = await getVendorTermsSetupContext(active.apiBaseUrl, active.tenantId);
  if (!vendorSetupCurrent(active, gen) || seq !== vendorSetupUi.sequence) return;
  vendorSetupUi.detailsState = "unavailable";
  if (currentMode.status === 200 && validVendorSetupContext(currentMode.body)) {
    vendorSetupUi.context = currentMode.body;
    if (
      currentMode.body.state === "preparation" &&
      offers.status === 200 &&
      vendors.status === 200 &&
      products.status === 200 &&
      Array.isArray(offers.body.vendorItemOffers) &&
      Array.isArray(vendors.body.vendors) &&
      Array.isArray(products.body.liquorProducts)
    ) {
      const names = new Map(
        products.body.liquorProducts.map((product) => [product.id, product.name]),
      );
      const suppliers = new Map(vendors.body.vendors.map((vendor) => [vendor.id, vendor.name]));
      if (
        offers.body.vendorItemOffers.every(
          (offer) => names.has(offer.liquor_product_id) && suppliers.has(offer.vendor_id),
        )
      ) {
        vendorSetupUi.details = offers.body.vendorItemOffers.map((offer) => ({
          item: names.get(offer.liquor_product_id)!,
          vendor: suppliers.get(offer.vendor_id)!,
          sku: offer.vendor_sku,
          cost: offer.cost_cents,
          unit: offer.order_unit,
        }));
        vendorSetupUi.detailsState = "ready";
      }
    }
  } else {
    vendorSetupUi.read = currentMode.status === 403 ? "denied" : "unavailable";
    delete vendorSetupUi.context;
  }
  renderVendorSetupCard();
}

function initializeInventorySetup(): void {
  const help: Record<string, readonly [string, string, string]> = {
    "item-kind": [
      "Sets inventory types and their selling methods.",
      "Used when adding inventory. Off kinds remain here so they can be turned on.",
      "Beer can be sold by volume or as packaged items.",
    ],
    "item-category": [
      "Groups inventory within the selected kind.",
      "Used in inventory filters, counts and reports.",
      "Bourbon under Liquor.",
    ],
    "container-type": [
      "Sets the shared list of package types.",
      "Used by inventory editors across kinds. Changes apply across kinds.",
      "Can, bottle or keg.",
    ],
    "liquor-bottle-size": [
      "Sets liquor package volumes.",
      "Used in the Size picker for liquor inventory.",
      "750 ml bottle.",
    ],
    "pour-volume": [
      "Sets the shared list of serving volumes in ounces.",
      "Assign volumes to the selected kind below. Shared list edits apply across per-ounce kinds.",
      "A 5 oz wine serving.",
    ],
    "serving-size": [
      "Sets the shared draft and packaged size list.",
      "Used by applicable inventory editors across kinds.",
      "A 16 oz can.",
    ],
    "drink-display-category": [
      "Groups drinks on the menu.",
      "Used in recipe editors and the drink list.",
      "Cocktails or Non-alcoholic.",
    ],
  };
  for (const [key, content] of Object.entries(help)) {
    const node = mustFind<HTMLElement>(`[data-testid="admin-vocab-${key}"]`);
    setupEditorNodes.set(key, node);
    const heading = node.querySelector<HTMLElement>(".section-title");
    if (heading !== null) {
      const row = document.createElement("div");
      row.className = "setia1-section-heading";
      heading.before(row);
      hoverHelp(heading, ...content);
      row.append(heading);
    }
    for (const paragraph of node.querySelectorAll<HTMLElement>("p.admin-field-help[id]")) {
      if (!paragraph.id.endsWith("-status")) paragraph.hidden = true;
    }
  }
  const standardHeading = mustFind<HTMLElement>("#admin-standard-pour-heading");
  hoverHelp(standardHeading, "Sets the volume of a straight liquor pour.", "Cocktail recipes keep their own volumes.", "A 1.5 oz standard gives 3 oz for Double.");
  panelDrinks.append(setupEditorNodes.get("drink-display-category")!);
  panelInventory.append(panelMenu);
  panelMenu.setAttribute("aria-labelledby", "setia1-setup-tab");
  const jump = document.createElement("nav");
  jump.id = "setia1-kind-jump";
  jump.className = "setia1-kind-jump";
  jump.setAttribute("aria-label", "Item kind setup");
  const title = document.createElement("h2");
  title.id = "setia1-kind-title";
  const grid = document.createElement("div");
  grid.id = "setia1-kind-editors";
  grid.className = "setia1-kind-editors";
  const parked = document.createElement("div");
  parked.id = "setia1-parked-editors";
  parked.hidden = true;
  for (const [key, node] of setupEditorNodes)
    if (key !== "item-kind" && key !== "drink-display-category") parked.append(node);
  vendorSetupUi.host = document.createElement("section");
  vendorSetupUi.host.id = "admin-vendor-options-setup";
  vendorSetupUi.host.className = "admin-section";
  vendorSetupUi.host.setAttribute("aria-labelledby", "admin-vendor-options-setup-heading");
  panelMenu.replaceChildren(vendorSetupUi.host, setupEditorNodes.get("item-kind")!, jump, title, grid, parked);
  // INVSETUP1: the count areas and the default target markup cards are configuration, so they sit under Setup, below the
  // kind editors. The same elements move (ids, test ids and loaders unchanged); the Items tab keeps only the item list.
  panelMenu.append(panelCountAreas, mustFind<HTMLElement>('[data-testid="admin-target-markups"]'));
  // INVSETUP1: the count areas and the default target markup cards are configuration, so they sit under Setup, below the
  // kind editors. The same elements move (ids, test ids and loaders unchanged); the Items tab keeps only the item list.
  panelMenu.append(panelCountAreas, mustFind<HTMLElement>('[data-testid="admin-target-markups"]'));
  itemCategoryKindEl.hidden = true;
  for (const tab of ["items", "setup"] as const) {
    const button = mustFind<HTMLButtonElement>(`#setia1-${tab}-tab`);
    button.addEventListener("click", () => selectInventorySetupTab(tab));
    button.addEventListener("keydown", (event) => {
      if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
        event.preventDefault();
        const next = tab === "items" ? "setup" : "items";
        selectInventorySetupTab(next);
        mustFind<HTMLButtonElement>(`#setia1-${next}-tab`).focus();
      }
    });
  }
  selectInventorySetupTab("items");
}

function selectInventorySetupTab(tab: "items" | "setup"): void {
  inventorySetupState.tab = tab;
  hideHoverHelp();
  mustFind<HTMLElement>("#setia1-items").hidden = tab !== "items";
  panelMenu.hidden = tab !== "setup";
  for (const candidate of ["items", "setup"] as const) {
    const button = mustFind<HTMLButtonElement>(`#setia1-${candidate}-tab`);
    button.setAttribute("aria-selected", String(candidate === tab));
    button.tabIndex = candidate === tab ? 0 : -1;
  }
  if (tab === "setup") {
    renderInventorySetupKind();
    void loadVendorSetupContext();
  } else clearVendorSetupView();
  if (currentSection === "inventory") ensureSettingsSectionJump(panelInventory);
}

function renderInventorySetupKind(): void {
  hideHoverHelp();
  const jump = document.querySelector<HTMLElement>("#setia1-kind-jump");
  if (jump === null) return;
  const kinds = sortByName((adminVocabularies["item_kind"] ?? []).filter((entry) => entry.active && isCurrentItemKind(entry.value)), (entry) => entry.label);
  if (!kinds.some((entry) => entry.value === inventorySetupState.kind))
    inventorySetupState.kind = kinds[0]?.value ?? "";
  jump.replaceChildren();
  for (const kind of kinds) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "btn-secondary btn-compact";
    button.textContent = kind.label;
    button.setAttribute("aria-pressed", String(kind.value === inventorySetupState.kind));
    button.addEventListener("click", () => {
      inventorySetupState.kind = kind.value;
      hideHoverHelp();
      renderInventorySetupKind();
    });
    jump.append(button);
  }
  const grid = mustFind<HTMLElement>("#setia1-kind-editors");
  const parked = mustFind<HTMLElement>("#setia1-parked-editors");
  for (const [key, node] of setupEditorNodes)
    if (key !== "item-kind" && key !== "drink-display-category") parked.append(node);
  grid.replaceChildren();
  const kind = kinds.find((entry) => entry.value === inventorySetupState.kind);
  mustFind<HTMLElement>("#setia1-kind-title").textContent = kind?.label ?? "No active item kinds";
  if (kind === undefined) return;
  itemCategoryEditorScope = kind.value;
  renderItemCategoryList();
  grid.append(setupEditorNodes.get("item-category")!);
  const perOunce =
    itemKindEntrySellsPerOunce(kind) || (kind.saleMechanisms ?? []).includes("per_ounce");
  const packaged =
    (kind.saleMechanisms ?? []).includes("whole_unit") || kind.saleMechanism === "whole_unit";
  if (kind.value !== "ingredient" && (kind.value === "liquor" || perOunce || packaged)) {
    grid.append(
      setupEditorNodes.get("container-type")!,
      setupEditorNodes.get(kind.value === "liquor" ? "liquor-bottle-size" : "serving-size")!,
    );
  }
  if (perOunce && kind.value !== "liquor") {
    grid.append(setupEditorNodes.get("pour-volume")!);
    const assignment = document.createElement("section");
    assignment.className = "admin-subsection";
    const heading = document.createElement("h2");
    heading.className = "section-title";
    heading.textContent = `${kind.label} pour volumes`;
    const headingRow = document.createElement("div");
    headingRow.className = "setia1-section-heading";
    hoverHelp(heading, "Selects the configured serving volumes for this kind.", "Used by the inventory item serving controls. The shared volume list above stays unchanged.", "Wine can offer 5 oz and 6 oz servings.");
    headingRow.append(heading);
    assignment.append(headingRow);
    appendItemKindPourVolumesPicker(assignment, kind);
    grid.append(assignment);
  }
  if (currentSection === "inventory") ensureSettingsSectionJump(panelInventory);
}

function revealInventorySetupTarget(selector: string | undefined): void {
  if (selector === undefined) return;
  const target = document.querySelector<HTMLElement>(selector);
  if (target === null || !panelMenu.contains(target)) return;
  const kinds = sortByName((adminVocabularies["item_kind"] ?? []).filter((entry) => entry.active && isCurrentItemKind(entry.value)), (entry) => entry.label);
  const chosen = kinds.find((entry) => entry.value === itemCategoryEditorScope);
  if (selector.includes("liquor-bottle-size"))
    inventorySetupState.kind = kinds.find((entry) => entry.value === "liquor")?.value ?? "";
  else if (selector.includes("serving-size"))
    inventorySetupState.kind =
      kinds.find(
        (entry) =>
          entry.value !== "liquor" &&
          ((entry.saleMechanisms ?? []).includes("whole_unit") ||
            itemKindEntrySellsPerOunce(entry)),
      )?.value ?? inventorySetupState.kind;
  else if (selector.includes("pour-volume"))
    inventorySetupState.kind =
      kinds.find((entry) => entry.value !== "liquor" && itemKindEntrySellsPerOunce(entry))?.value ?? "";
  else if (selector.includes("item-category") && chosen !== undefined)
    inventorySetupState.kind = chosen.value;
  selectInventorySetupTab("setup");
}

// ---------------------------------------------------------------------
// Section switching
// ---------------------------------------------------------------------

const ADMIN_SECTION_TABS: Record<AdminSection, HTMLButtonElement> = {
  money: tabMoney,
  tabsPayment: tabTabsPayment,
  discounts: tabDiscounts,
  corrections: tabCorrections,
  recipes: tabRecipes,
  menu: tabMenu,
  inventory: tabInventory,
  purchasing: tabPurchasing,
  people: tabPeople,
  operations: tabOperations,
  tablets: tabTablets,
  auditLog: tabAuditLog,
  network: tabNetwork,
  connections: tabConnections,
};

const ADMIN_SECTION_PANELS: Record<AdminSection, HTMLElement> = {
  money: panelMoney,
  tabsPayment: panelTabsPayment,
  discounts: panelDiscounts,
  corrections: panelCorrections,
  recipes: panelDrinks,
  menu: panelMenu,
  inventory: panelInventory,
  purchasing: panelPurchasing,
  people: panelUsers,
  operations: panelOperations,
  tablets: panelTablets,
  auditLog: panelChangeHistory,
  network: panelNetwork,
  connections: panelConnections,
};

// Independent-review Finding B, 2026-08-26: every tab's own <button> is
// hidden (not merely left unselected) unless this session's resolved
// permissions grant it, so a standalone Inventory Manager sees exactly
// Inventory, not ten tabs with nine of them dead ends. Called fresh every
// time the screen opens rather than once at module load: the SAME static
// DOM elements are reused across logins/logouts within one browser tab
// (this module's own top-level mustFind calls run once, ever), so a
// different session opening this screen later must not still show the
// PREVIOUS session's own tab set.
//
// Settings rebuild addition: within the reachable Inventory group, the
// Bottles and Counts CONTENT sections each still independently hide per
// their own pre-existing permission, exactly as when they were separate
// top-level tabs -- see inventorySubsectionVisible's own comment.
function applyTabVisibility(activeSession: Session): void {
  const railGroups = mustFind<HTMLElement>("#admin-settings-rail-groups");
  tabMenu.hidden = true;
  for (const section of ADMIN_SECTION_ORDER) railGroups.append(ADMIN_SECTION_TABS[section]);
  for (const section of ADMIN_SECTION_ORDER) {
    ADMIN_SECTION_TABS[section].hidden = !sectionVisible(activeSession, section);
  }
  panelBottles.hidden = !inventorySubsectionVisible(activeSession, "bottles");
  panelCountAreas.hidden = !inventorySubsectionVisible(activeSession, "countAreas");
}

/** SET22: ordinary entry always starts with the first permitted group in actual sidebar order. */
function initialSectionFor(activeSession: Session): AdminSection {
  return ADMIN_SECTION_ORDER.find((section) => sectionVisible(activeSession, section)) ?? ADMIN_SECTION_ORDER[0]!;
}

/** UX108: whether a Settings group is one this session may open (a route naming one it may not is not restored). */
export function settingsSectionAllowed(activeSession: Session, section: AdminSection): boolean {
  return sectionVisible(activeSession, section === "menu" ? "inventory" : section);
}

export async function openAdminScreen(
  activeSession: Session,
  options: { section?: AdminSection } = {},
): Promise<void> {
  currentSession = activeSession;
  applyTabVisibility(activeSession);
  clearSettingsSearch();
  // ACCTEXPORT1 and PAYEXPORT1: the Connections rail item exists only when a connection is on, which the two cards' reads say. An
  // address that names it waits for them; any other entry reads them in the background and the item appears when they answer.
  if (options.section === "connections") await refreshConnections(activeSession);
  else void refreshConnections(activeSession);
  // UX108: a group named by the address opens when this session may see it; otherwise the usual landing applies.
  const wanted =
    options.section !== undefined && sectionVisible(activeSession, options.section)
      ? options.section
      : initialSectionFor(activeSession);
  if (!(await showSection(wanted))) return;
  // UX26 Section 2.3: called from here, never from showSection's own
  // text -- see loadSetupChecklist's own comment for why.
  void loadSetupChecklist(activeSession);
  // SYSLOC1 (Kerry, 2026-10-31): System checks live only in the software owner console; a location cannot fix them.
}

/** CL6-F1-F3 (DEC-286): "Open Settings" on the floor's refusal sheet. Opens the
 * Settings screen straight on the Location tab, where the time zone and cutoff
 * are confirmed. main.ts has already shown the Settings screen. Only offered to
 * someone holding admin.location_settings.manage, but this still falls back to
 * the normal entry section when the Location tab is not visible to the session,
 * rather than landing on a tab it may not see. */
export async function openLocationSettings(activeSession: Session): Promise<void> {
  currentSession = activeSession;
  applyTabVisibility(activeSession);
  clearSettingsSearch();
  const section: AdminSection = "operations";
  if (!(await showSection(
    sectionVisible(activeSession, section) ? section : initialSectionFor(activeSession),
  ))) return;
  void loadSetupChecklist(activeSession);
}

/** PAY1 Slice 3: the browser came back from Stripe's hosted page (`?card_payments=return`) or was sent to a link
 * that ran out (`?card_payments=refresh`). main.ts has already shown the Settings screen. Opens Tabs and payment
 * and lets the Card payments section read the account (return) or say the link expired (refresh). A session that
 * cannot see Tabs and payment falls back to its normal entry section and shows nothing. */
export async function openCardPaymentsSettings(
  activeSession: Session,
  arrival: CardPaymentsArrival,
): Promise<void> {
  currentSession = activeSession;
  applyTabVisibility(activeSession);
  clearSettingsSearch();
  // CARDSETUP1 (B2): everyone returns to Tabs and payment, where "Back from Stripe" shows. Billing's Bank accounts card
  // offers a second Connect right after Stripe, so it is not the landing page.
  const section: AdminSection = "tabsPayment";
  const visible = sectionVisible(activeSession, section);
  if (!(await showSection(visible ? section : initialSectionFor(activeSession)))) return;
  if (visible) {
    await cardPaymentsSection.handleArrival(arrival);
  }
  void loadSetupChecklist(activeSession);
}

/** Reads both Connections cards and shows the rail item only when at least one of them is on for this deployment and person. */
async function refreshConnections(activeSession: Session): Promise<void> {
  await Promise.all([accountingSection.refresh(), payrollSection.refresh()]);
  if (sessionOrUndefined()?.token !== activeSession.token) return;
  connectionsState.available = accountingSection.isOffered() || payrollSection.isOffered();
  tabConnections.hidden = !sectionVisible(activeSession, "connections");
}

/** Opens Settings on the Connections page after reading which cards are on. False when neither is (a person who cannot see
 * it falls back to the normal entry section and shows nothing). */
async function openConnectionsPage(activeSession: Session): Promise<boolean> {
  currentSession = activeSession;
  applyTabVisibility(activeSession);
  clearSettingsSearch();
  await refreshConnections(activeSession);
  const visible = sectionVisible(activeSession, "connections");
  if (!(await showSection(visible ? "connections" : initialSectionFor(activeSession)))) return false;
  void loadSetupChecklist(activeSession);
  return visible;
}

/** ACCTEXPORT1: the browser came back from QuickBooks (`?accounting=connected|denied|failed|invalid`). main.ts has already shown the
 * Settings screen. Opens the Connections page and lets the QuickBooks card say what happened. */
export async function openAccountingSettings(activeSession: Session, arrival: AccountingArrival): Promise<void> {
  if (await openConnectionsPage(activeSession)) await accountingSection.handleArrival(arrival);
}

/** PAYEXPORT1: the browser came back from Gusto (`?gusto=connected` or `?gusto=error&reason=...`). main.ts has already shown
 * the Settings screen. Opens the Connections page, where the Payroll (Gusto) card reads the connection and says how it went. */
export async function openPayrollSettings(
  activeSession: Session,
  outcome: "connected" | "error",
  reason: string | null,
): Promise<void> {
  if (await openConnectionsPage(activeSession)) await payrollSection.handleArrival(outcome, reason);
}

/** The Billing page (Network and billing) carries the Bank accounts card for a person who manages billing. */
function billingPageVisible(activeSession: Session): boolean {
  return sectionVisible(activeSession, "network") && hasPermission(activeSession, "billing.ai.manage");
}

/** PAY2-S6: the browser came back from Stripe's hosted bank debit page (`?royalty_authorization=return|cancel`).
 * main.ts has already shown the Settings screen. Opens the Billing page and lets the Bank accounts card read the
 * authorization (return) or just show it (cancel). */
export async function openRoyaltyAuthorizationSettings(
  activeSession: Session,
  arrival: RoyaltyArrival,
): Promise<void> {
  await openBankAccountsSettings(activeSession, "royalties", arrival);
}

/** BANK1: the same for the Snip & Sip subscription's hosted bank debit page (`?subscription_debit=return|cancel`). */
export async function openSubscriptionDebitSettings(
  activeSession: Session,
  arrival: SubscriptionDebitArrival,
): Promise<void> {
  await openBankAccountsSettings(activeSession, "subscription", arrival);
}

async function openBankAccountsSettings(
  activeSession: Session,
  source: "royalties" | "subscription",
  arrival: "return" | "cancel",
): Promise<void> {
  currentSession = activeSession;
  applyTabVisibility(activeSession);
  clearSettingsSearch();
  const section: AdminSection = "network";
  const visible = billingPageVisible(activeSession);
  if (!(await showSection(visible ? section : initialSectionFor(activeSession)))) return;
  if (visible) {
    await bankAccountsSection.handleArrival(source, arrival);
  }
  void loadSetupChecklist(activeSession);
}

/** VEN4-F1 (docs/build-briefs/VEN4-F1-refresh-reapplies-in-section-
 * gates.md): the client-side write gates a permission change must
 * re-apply to whatever is ALREADY on screen, not only to which tabs are
 * offered. Before this, every one of these lived only inside
 * showSection's own body, so refreshTabVisibility (below) -- which
 * deliberately does not call showSection when the current section stays
 * visible, precisely so a permission refresh cannot flicker the screen or
 * discard an in-progress search -- never re-ran them. A downgrade
 * removing inv.vendor.manage while leaving inv.vendor.view kept
 * Purchasing visible (sectionVisible stayed true), so showSection was
 * never called, so the per-row write affordances stayed enabled until the
 * admin happened to leave the section and come back; the same hole
 * existed one screen over for admin.tenant_settings.view and the PIN-
 * policy block (SET16-F1's own leak, reached through a permission change
 * instead of a sign-out).
 *
 * Called from BOTH showSection (an entry into the section) and
 * refreshTabVisibility (a permission snapshot refreshing while already
 * on it), so there is exactly one place that decides this rather than two
 * copies that can drift apart again.
 *
 * What stays OUT of here, deliberately: anything that FETCHES, the latch
 * checks that decide whether to fetch, and clearSettingsSearch(). Those
 * three are what makes refreshTabVisibility narrower than showSection on
 * purpose (its own doc comment below), and folding them in here would
 * turn every permission-snapshot refresh into a request storm and an
 * in-progress search wipe. renderVendorList() below stays guarded on
 * vendorsLoaded for the same reason: a refresh arriving before any vendor
 * has ever loaded must be a no-op, not a render of an empty list. */
function applyInSectionGating(section: AdminSection): void {
  if (section === "inventory") refreshVendorSetupAccess();
  // SET16-F1: People's own PIN-policy block, gated the same way and for
  // the same reason as operationsTenantSettingsEl/purchasingVendors-
  // SectionEl in showSection -- on EVERY entry to People, and now on
  // every refresh while already there, never only when a fetch happens
  // to run, since a session that lacks admin.tenant_settings.view may
  // never trigger loadSettings() at all and this is the only thing
  // standing between it and whatever the previous session left painted.
  if (section === "people") {
    const maySeePinPolicy = pinPolicyVisible(session());
    adminPinPolicyEl.hidden = !maySeePinPolicy;
    if (!maySeePinPolicy) {
      // SET16-F1 follow-up, from independent review: hiding the block is
      // not the same claim as clearing it. `hidden` removes it from the
      // visual screen, which is what a next employee with a finger sees,
      // but the previous employee's values stay in `input.value` and come
      // back with one devtools inspection. CLAUDE.md's own touch-only
      // carve-out puts this screen on a laptop with a keyboard and
      // pointer (DEC-060), so devtools here is an ordinary browser
      // feature rather than an exotic attack, unlike on the floor tablet.
      // Emptied rather than left, so there is nothing to come back to.
      settingsPinMinLengthInput.value = "";
      settingsPinMaxLengthInput.value = "";
      settingsPinLockoutAttemptsInput.value = "";
      settingsPinLockoutMinutesInput.value = "";
    }
    // UX86: the session inactivity card sits in People and access now, under
    // the same gate as the PIN policy (admin.tenant_settings.view, the gate
    // it had in Location), and is emptied for the same reason when closed.
    const maySeeSessionInactivity = sessionInactivityVisible(session());
    adminSessionInactivityCardEl.hidden = !maySeeSessionInactivity;
    if (!maySeeSessionInactivity) {
      settingsSessionInactivityInput.value = "";
      sessionInactivitySaveStatusEl.textContent = "";
    }
  }
  // VEN3: own load, own gate (purchasingVendorsVisible), independent of
  // settingsLoaded -- the tab's own outer gate is a wide union that
  // deliberately does not include admin.tenant_settings.view (PO6-R1,
  // DEC-056), so a user reaching this tab through, say,
  // purchasing.order.view alone must not have the vendor block's own
  // fetch or visibility decided by whether loadSettings() happened to
  // succeed for them. Hide/show the block on EVERY entry to Purchasing
  // (cheap, no fetch, and must never depend on vendorsLoaded, exactly
  // like operationsTenantSettingsEl's own unconditional line in
  // showSection), and now on every refresh while already there too.
  if (section === "purchasing") {
    purchasingVendorsSectionEl.hidden = !purchasingVendorsVisible(session());
    // VEN4: the add-vendor control's own gate, applied on every entry to
    // Purchasing and now on every refresh while already there -- the
    // same "own gate, every entry, no fetch" shape
    // purchasingVendorsSectionEl.hidden immediately above already
    // follows -- inv.vendor.manage can change mid-visit (RT3, DEC-038)
    // independent of inv.vendor.view, and independent of whether any
    // vendor has ever loaded (an add control offered on an empty list is
    // still a write affordance).
    applyVendorAddControlGating();
    // PO2, owner ruling 2026-09-29 (DEC-247). Same "own gate, every
    // entry, no fetch for the gating itself" shape immediately above;
    // the fetch is separately latched by purchasingApprovalThresholdLoaded.
    purchasingApprovalThresholdSectionEl.hidden = !purchasingApprovalThresholdVisible(session());
    applyPurchasingApprovalThresholdGating();
    if (purchasingApprovalThresholdVisible(session()) && !purchasingApprovalThresholdLoaded) {
      void loadPurchaseApprovalThreshold();
    }
    if (vendorsLoaded && purchasingVendorsVisible(session())) {
      // VEN4: the per-vendor-row write affordances (the nine fields,
      // the delivery-day chips, and the active switch) are only decided
      // when a row is actually built, not continuously, so a manage
      // downgrade mid-visit (RT3) needs an explicit re-render to reach
      // rows already on screen -- exactly VEN4-F1's own defect when this
      // re-render never ran on a refresh with no section change. Cheap:
      // reuses the already-loaded settingsVendors, no fetch. Guarded on
      // vendorsLoaded so a first visit (or a refresh before any vendor
      // has ever loaded) is untouched -- loadVendors()'s own
      // renderVendorList() call applies the correct gate the first time.
      renderVendorList();
    }
  }
}

/** RT3 (DEC-038): main.ts's own background permission-snapshot refresh
 * (fired on every screen navigation) calls this when the admin screen
 * happens to be the one currently on view, so a role-template change an
 * Admin just made elsewhere is reflected in this rail on the acting
 * user's next navigation, without waiting for a fresh visit to Settings.
 * Deliberately narrower than openAdminScreen: it only re-applies which
 * tabs/panels the refreshed session can see, and never resets the search
 * box or reloads an already-loaded panel's data, so it cannot flicker or
 * discard an in-progress search the way a full re-open would. If the
 * section the admin was just looking at is no longer visible to the
 * refreshed session, it follows openAdminScreen's own "land on the first
 * visible group" rule rather than leaving a hidden panel selected with no
 * tab pointing at it. VEN4-F1: if the section stays visible, the
 * in-section write gates (applyInSectionGating) are re-applied to
 * whatever is already on screen, since showSection is deliberately NOT
 * called in that branch and nothing else would ever re-run them. */
export function refreshTabVisibility(activeSession: Session): void {
  currentSession = activeSession;
  refreshVendorSetupAccess();
  tenantPermitPanel.refreshAccess();
  applyTabVisibility(activeSession);
  // UX80: an operations view is on screen; Settings groups are not.
  if (currentOperationsView !== undefined) {
    refreshOperationsViewGating(activeSession);
    return;
  }
  if (!sectionVisible(activeSession, currentSection)) {
    // Forced access loss must clear Catalog drafts and invalidate in-flight replies without a discard prompt.
    productCatalogSection.reset();
    void showSection(initialSectionFor(activeSession));
    return;
  }
  applyInSectionGating(currentSection);
  if (railSearchInput.value.trim() !== "") renderSettingsSearch();
  // RPTLAYOUT1-F1-S2: the Audit log group re-applies what it offers (its tabs and filters) without reloading the list.
  if (currentSection === "auditLog") {
    refreshChangeHistorySession(activeSession);
    refreshAuditLogSession(activeSession);
  }
}

// ---------------------------------------------------------------------
// UX80 (docs/build-briefs/UX80-operations-menu.md; DEC-298): operations views
// ---------------------------------------------------------------------
//
// The screens that left Settings (Reports, Count stock and Past counts, Stock
// levels, Match clients, Update prices from a file, Set starting stock) share
// this screen's own sheets, loaders and generation guard, so none of that was
// rewritten or copied. What changed is how they are reached and what frames
// them: ui/src/accountMenu.ts lists the menu rows, main.ts opens a view here,
// and while a view is open the screen carries the admin-ops-mode class, which
// hides the Settings header, the setup checklist and the rail. The view names
// itself in #admin-ops-header and "Menu" reopens the account menu at the
// group it came from. Settings itself never shows any of these panels.

/** A permission snapshot refreshed while an operations view is open: re-apply
 * what to offer without reloading figures (the same shape refreshTabVisibility
 * has for a Settings group). A view the person no longer holds says so. */
function refreshOperationsViewGating(activeSession: Session): void {
  const view = currentOperationsView;
  if (view === undefined) return;
  if (!operationsViewAllowed(activeSession, view)) {
    opsSubtitleEl.textContent = "You do not have permission to use this screen.";
    opsSubtitleEl.hidden = false;
    for (const panel of Object.values(OPERATIONS_VIEW_PANELS)) {
      if (panel !== undefined) panel.hidden = true;
    }
    return;
  }
  if (view === "reports") {
    refreshReportsGating(activeSession);
  }
  if (view === "stockLevels") {
    applyStartingStockEntryGating(activeSession);
    renderStockLevelsList();
  }
  if (view === "transfers") {
    refreshTransfersSession(activeSession);
  }
  if (view === "matchClients") {
    reconciliationSectionEl.hidden = !reconciliationQueueVisible(activeSession);
    mytimeSyncFreshnessSectionEl.hidden = !mytimeSyncFreshnessVisible(activeSession);
  }
}

export type OperationsView =
  | "reports"
  | "counts"
  | "stockLevels"
  | "matchClients"
  | "priceList"
  | "startingStock"
  | "transfers";

type OperationsMenuGroup = "ordering" | "inventory" | "reports" | "shift";

interface OperationsViewDef {
  readonly title: string;
  readonly subtitle: string;
  /** HIGFIX4A (F-22): what a page paragraph under the title used to say, now hover help on the title (design/style-guide.md section 11). */
  readonly help?: string;
  readonly group: OperationsMenuGroup;
  /** The permissions that open it: ANY one. The menu row has the same ids. */
  readonly permissions: readonly string[];
}

const REPORT_VIEW_PERMISSIONS: readonly string[] = [
  "purchasing.order.view",
  "inv.transfer.view",
  "report.sales_by_day.view",
  "report.sales_by_item.view",
  "report.tips_by_employee.view",
  "report.stock_value.view",
  "report.usage_waste.view",
  "report.cost_margin.view",
  "report.abc_spirits.view",
  "report.count_variance.view",
  "report.receipts_spend.view",
  "report.sales_tax.view",
  "report.menu_profitability.view",
  "report.custom.view",
];

const OPERATIONS_VIEWS: Record<OperationsView, OperationsViewDef> = {
  reports: {
    title: "Reports",
    subtitle:
      "How the location is doing, by day, by drink and by employee, and what stock is worth.",
    group: "reports",
    permissions: REPORT_VIEW_PERMISSIONS,
  },
  counts: {
    title: "Count stock",
    subtitle: "",
    help: "Count what is on the shelf. Closing a count posts an adjustment only for what changed; everything not counted is left alone.",
    group: "inventory",
    permissions: ["inv.count_session.run"],
  },
  stockLevels: {
    title: "Stock levels",
    // INV38 (owner, 2026-10-09): no subtitle under the title. Empty means the line is not drawn.
    subtitle: "",
    help: "Tap an item to see its on hand, record waste or a return, and reverse a recent movement. Item names, sizes and prices are set in Settings, Inventory items.",
    group: "inventory",
    permissions: ["inv.onhand.view"],
  },
  matchClients: {
    title: "Match clients",
    subtitle: "Guests that could be two different people.",
    group: "shift",
    permissions: ["guest.reconciliation.view"],
  },
  priceList: {
    title: "Update prices from a file",
    subtitle: "A vendor sent new prices.",
    group: "ordering",
    permissions: ["inv.vendor_price_list.import"],
  },
  startingStock: {
    title: "Set starting stock",
    subtitle: "Enter what is on the shelf now, once, before the first sale.",
    group: "inventory",
    permissions: ["inv.movement.record"],
  },
  // INVXFER1-S2: the screens draw their own header (back, eyebrow, title), so the shell's own is hidden by CSS.
  transfers: {
    title: "Transfers",
    subtitle: "",
    group: "inventory",
    permissions: ["inv.transfer.view"],
  },
};

const OPERATIONS_VIEW_PANELS: Record<OperationsView, HTMLElement | undefined> = {
  reports: panelReports,
  counts: panelCounts,
  stockLevels: panelStockLevels,
  matchClients: panelMatchClients,
  priceList: panelPriceListPicker,
  startingStock: undefined,
  transfers: panelTransfers,
};

const adminScreenEl = mustFind<HTMLElement>("#admin-screen");
const settingsHeaderEl = mustFind<HTMLElement>("#admin-settings-header");
const opsHeaderEl = mustFind<HTMLElement>("#admin-ops-header");
const opsTitleEl = mustFind<HTMLElement>("#admin-ops-title");
const opsSubtitleEl = mustFind<HTMLElement>("#admin-ops-subtitle");
const priceListPickerStatusEl = mustFind<HTMLElement>("#admin-price-list-picker-status");
const priceListPickerListEl = mustFind<HTMLElement>("#admin-price-list-picker-list");

/** UIFIX5: the next Count stock open lands on the closed counts only (the Past counts menu row). */
let countsLandingPast = false;
export function setCountsLandingPast(past: boolean): void {
  countsLandingPast = past;
}

/** Which operations view is on screen, or undefined while Settings is. */
let currentOperationsView: OperationsView | undefined;

export function operationsViewAllowed(activeSession: Session, view: OperationsView): boolean {
  return OPERATIONS_VIEWS[view].permissions.some((id) => hasPermission(activeSession, id));
}

/** The menu group the open view belongs to, for the "Menu" back row. */
export function operationsViewGroup(): OperationsMenuGroup | undefined {
  return currentOperationsView === undefined
    ? undefined
    : OPERATIONS_VIEWS[currentOperationsView].group;
}

export function currentOperationsViewName(): OperationsView | undefined {
  return currentOperationsView;
}

/** UX108: where the admin screen is, as an address: the operations view (with its report and range, or its open
 * count) when one is on screen, otherwise the Settings group. */
export function currentAdminRoute(): Route {
  const view = currentOperationsView;
  if (view === undefined) {
    return { kind: "settings", section: currentSection };
  }
  if (view === "reports") {
    const shown = reportsRouteState();
    return shown.report === undefined
      ? { kind: "view", view }
      : shown.range === undefined
        ? { kind: "view", view, report: shown.report }
        : { kind: "view", view, report: shown.report, range: shown.range };
  }
  if (view === "counts" && countWalkState !== undefined) {
    return { kind: "view", view, countSessionId: countWalkState.sessionId };
  }
  // INVCOUNT2-S2: the table is part of the screen's address just like the walk.
  const tableSessionId = countTableSessionId();
  if (view === "counts" && tableSessionId !== undefined) {
    return { kind: "view", view, countSessionId: tableSessionId };
  }
  return { kind: "view", view };
}

function enterOperationsMode(view: OperationsView): void {
  if (view !== "counts" || currentOperationsView !== "counts") resetCountStartSelection();
  clearVendorSetupView();
  hideHoverHelp();
  closeStockDetail({ reload: false });
  // CRB1: another view than Reports puts the person's own theme back (the builder is drawn in Snip & Sip Light).
  if (view !== "reports") leaveReports();
  currentOperationsView = view;
  adminScreenEl.classList.add("admin-ops-mode");
  adminScreenEl.dataset["opsView"] = view;
  settingsHeaderEl.hidden = true;
  opsHeaderEl.hidden = false;
  // UIFIX5: Past counts opens the closed counts as its own list, with no Start, Resume or Take over above it.
  const pastCountsList = view === "counts" && countsLandingPast;
  panelCounts.dataset["landing"] = pastCountsList ? "past" : "start";
  if (view === "counts") countsLandingPast = false;
  opsTitleEl.textContent = pastCountsList ? "Past counts" : OPERATIONS_VIEWS[view].title;
  opsSubtitleEl.textContent = pastCountsList
    ? "Closed counts, newest first."
    : OPERATIONS_VIEWS[view].subtitle;
  opsSubtitleEl.hidden = !pastCountsList && OPERATIONS_VIEWS[view].subtitle === "";
  setHoverHelp(opsTitleEl, pastCountsList ? "" : (OPERATIONS_VIEWS[view].help ?? ""));
  clearSettingsSearch();
  for (const section of ADMIN_SECTION_ORDER) {
    ADMIN_SECTION_PANELS[section].hidden = true;
  }
  for (const [name, panel] of Object.entries(OPERATIONS_VIEW_PANELS) as Array<
    [OperationsView, HTMLElement | undefined]
  >) {
    if (panel !== undefined) {
      panel.hidden = name !== view;
    }
  }
  // UX76: Reports uses the full page width, and so does the Change history.
  settingsShellEl.classList.toggle(
    "settings-shell-wide",
    view === "reports",
  );
}

/** Back to the Settings frame. Called by showSection and by invalidate. */
function exitOperationsMode(): void {
  resetCountStartSelection();
  closeStockDetail({ reload: false });
  leaveReports();
  currentOperationsView = undefined;
  adminScreenEl.classList.remove("admin-ops-mode");
  delete adminScreenEl.dataset["opsView"];
  settingsHeaderEl.hidden = false;
  opsHeaderEl.hidden = true;
  for (const panel of Object.values(OPERATIONS_VIEW_PANELS)) {
    if (panel !== undefined) {
      panel.hidden = true;
    }
  }
  settingsShellEl.classList.remove("settings-shell-wide");
}

export async function openOperationsView(
  activeSession: Session,
  view: OperationsView,
  options: {
    report?: ReportId;
    open?: string;
    range?: ReportRange;
    countSessionId?: string;
    stockFilter?: "low" | "86";
  } = {},
): Promise<void> {
  const navigationGeneration = loadGeneration;
  if(!(await productCatalogSection.confirmLeave()) || !loadIsCurrent(navigationGeneration) || !tenantPermitPanel.confirmLeave())return;
  productCatalogSection.reset();
  catalogPlanningSequence++;catalogPlanningLocationId=undefined;catalogScopeHost.replaceChildren();
  tenantPermitPanel.reset();
  currentSession = activeSession;
  enterOperationsMode(view);
  // UX108: the view is part of the screen's address (a report adds itself once it is chosen).
  requestRoutePublish();
  if (!operationsViewAllowed(activeSession, view)) {
    opsSubtitleEl.textContent = "You do not have permission to use this screen.";
    opsSubtitleEl.hidden = false;
    for (const panel of Object.values(OPERATIONS_VIEW_PANELS)) {
      if (panel !== undefined) panel.hidden = true;
    }
    return;
  }
  const gen = loadGeneration;
  switch (view) {
    case "reports":
      await openReports(activeSession, options.report, options.range, options.open);
      return;
    case "counts":
      // The start button is the one control whose meaning (Start or Resume)
      // depends on this read, so it waits for it rather than offering a Start
      // that an already open count would refuse.
      countsStartButton.disabled = true;
      setDisabledReason(countsStartButton, "Checking for a count already open.");
      try {
        await loadCounts();
      } finally {
        countsStartButton.disabled = false;
        setDisabledReason(countsStartButton, "");
      }
      // UX108: a count named by the address reopens only while it is still the one open count; a finished or
      // missing one lands on Count stock with nothing said.
      if (
        options.countSessionId !== undefined &&
        countsStartButton.dataset["countSessionId"] === options.countSessionId
      ) {
        await openCountSessionSheet(options.countSessionId, { restoreDraft: true });
      }
      return;
    case "stockLevels":
      // INVPOWALK1 (F9): the Low, Out and 86'd tiles open the list on what they counted.
      if (options.stockFilter !== undefined) stockLevelsFilter = options.stockFilter;
      renderStockFilterChips();
      applyStartingStockEntryGating(activeSession);
      void refreshStartingStockEntry(activeSession);
      await loadStockLevels();
      return;
    case "matchClients": {
      reconciliationSectionEl.hidden = !reconciliationQueueVisible(activeSession);
      mytimeSyncFreshnessSectionEl.hidden = !mytimeSyncFreshnessVisible(activeSession);
      if (reconciliationQueueVisible(activeSession)) {
        await loadReconciliationCandidates();
      }
      if (!loadIsCurrent(gen)) return;
      if (mytimeSyncFreshnessVisible(activeSession)) {
        await loadMyTimeSyncFreshness();
      }
      return;
    }
    case "priceList":
      await renderPriceListPicker(gen);
      return;
    case "startingStock":
      await openStartingStockScreen(activeSession);
      return;
    case "transfers":
      await openTransfers(panelTransfers, activeSession, {
        exit: () => mustFind<HTMLButtonElement>("#admin-ops-back").click(),
        toast: (message, tone) => adminToast(message, tone),
      });
      return;
  }
}

/** AU18: which screen a row's link opens must be one this session may open, or the link is left off. */
function auditLinkAllowed(s: Session, link: AuditLogLinkDto): boolean {
  switch (link.kind) {
    case "ticket":
      return true;
    case "void-history":
      return hasPermission(s, "report.void_history.view");
    case "closeout":
      return hasPermission(s, "report.closeout.view");
    case "count":
      return operationsViewAllowed(s, "counts");
  }
}

/** AU18: the Audit log's tabs. Only the shown tab holds a list; the others are forgotten. TEN11-S5 added
 * Support access, which can open Activity pre-filtered to one support person. */
async function openAuditLogTab(
  s: Session,
  tab: "activity" | "settings" | "support",
  initial?: Partial<AuditLogFilters>,
): Promise<void> {
  for (const [button, id] of [
    [auditTabActivity, "activity"],
    [auditTabSettings, "settings"],
    [auditTabSupport, "support"],
  ] as const) {
    button.classList.toggle("ch-on", tab === id);
    button.setAttribute("aria-selected", tab === id ? "true" : "false");
  }
  auditActivityHost.hidden = tab !== "activity";
  auditSettingsHost.hidden = tab !== "settings";
  auditSupportHost.hidden = tab !== "support";
  resetChangeHistory();
  resetAuditLog();
  resetSupportSessions(auditSupportHost);
  if (tab === "activity") {
    await openAuditLog(s, auditActivityHost, {
      canOpenLink: auditLinkAllowed,
      ...(initial === undefined ? {} : { initial }),
    });
  } else if (tab === "settings") {
    await openChangeHistory(s, auditSettingsHost);
  } else {
    await openSupportSessions(s, auditSupportHost, {
      openActivity: (filters) => void openAuditLogTab(session(), "activity", filters),
      endNow: async (sessionId) => {
        await endFranchisorSupportSession(s.apiBaseUrl, s.tenantId, sessionId);
        await openAuditLogTab(session(), "support");
      },
    });
  }
}

auditTabActivity.addEventListener("click", () => {
  void openAuditLogTab(session(), "activity");
});
auditTabSettings.addEventListener("click", () => {
  void openAuditLogTab(session(), "settings");
});
auditTabSupport.addEventListener("click", () => {
  void openAuditLogTab(session(), "support");
});

/** Update prices from a file, from the menu: the vendors a price list can be
 * loaded for, each with the same "Prices last updated" line and button the
 * vendor card in Settings carries. */
async function renderPriceListPicker(gen: number): Promise<void> {
  const s = session();
  priceListPickerListEl.innerHTML = "";
  priceListPickerStatusEl.textContent = "";
  if (!hasPermission(s, "inv.vendor.view")) {
    priceListPickerStatusEl.textContent =
      "You can load a price list, but your account cannot see the vendor list to pick from. Ask a manager to open this for you.";
    return;
  }
  priceListPickerStatusEl.textContent = "Loading vendors...";
  await loadVendors();
  if (!loadIsCurrent(gen)) return;
  priceListPickerStatusEl.textContent = "";
  const targets = settingsVendors.filter(
    (vendor) => vendor.active && vendor.price_file_layout === "nc_abc",
  );
  if (targets.length === 0) {
    priceListPickerStatusEl.textContent = "No vendor has a price file layout.";
    return;
  }
  for (const vendor of targets) {
    const entry = buildPriceListCardEntry(s, vendor);
    if (entry === null) continue;
    const row = document.createElement("li");
    row.className = "admin-vendor-row admin-price-list-picker-row";
    row.dataset["testid"] = "admin-price-list-picker-row";
    row.dataset["vendorId"] = vendor.id;
    const name = document.createElement("div");
    name.className = "admin-price-list-picker-name";
    name.textContent = vendor.name;
    row.append(name, entry);
    priceListPickerListEl.append(row);
  }
}

// ---------------------------------------------------------------------
// UX66 part C (docs/build-briefs/UX66-cart-quantities-and-vendor-
// readiness.md). Navigation main.ts's Purchasing Cart screen uses for its
// own "Back to Purchasing" control and its blocked-vendor fix links, so
// the cart never has to duplicate this screen's own section/tab
// mechanics. Each falls back to this session's own first visible section
// (the same fallback openAdminScreen already applies) rather than
// breaking for a session that reached the cart through some permission
// that does not also carry Purchasing or Inventory visibility.
// ---------------------------------------------------------------------

async function openAdminScreenOnSection(
  activeSession: Session,
  section: "purchasing" | "inventory",
): Promise<void> {
  currentSession = activeSession;
  applyTabVisibility(activeSession);
  clearSettingsSearch();
  if (!(await showSection(
    sectionVisible(activeSession, section) ? section : initialSectionFor(activeSession),
  ))) return;
  void loadSetupChecklist(activeSession);
  // SYSLOC1 (Kerry, 2026-10-31): System checks live only in the software owner console; a location cannot fix them.
}

/** The cart screen's own "Back to Purchasing" control (main.ts's
 * registerScreen secondaryAction for purchasing-cart-screen), replacing
 * the topbar Home button's former fall-through to Open Tabs, a floor
 * screen this admin-only flow never came from. */
export async function openPurchasingSettings(activeSession: Session): Promise<void> {
  await openAdminScreenOnSection(activeSession, "purchasing");
}

/** A blocked cart vendor's "Fix vendor setup" link (PURCHBLOCK1): opens straight to
 * that vendor's own row in Settings > Purchasing, scrolled into view and
 * focused on its Contact name field, rather than leaving a manager to
 * hunt the vendor list for the one row the cart already named. With a
 * return context the row shows a "Return to cart" strip; the vendor's own
 * Save contact and Vendor active controls are unchanged, and returning
 * saves nothing. */
export async function openVendorContactEditor(
  activeSession: Session,
  vendorId: string,
  returnContext?: VendorOptionReturn & { vendorName: string },
  focus: "contact" | "active" = "contact",
): Promise<{ opened: boolean; message?: string }> {
  clearVendorOptionReturn();
  await openAdminScreenOnSection(activeSession, "purchasing");
  if (returnContext && !returnContext.isCurrent()) return { opened: false };
  const row = settingsVendorsEl.querySelector<HTMLElement>(`[data-vendor-id="${vendorId}"]`);
  if (row === null) {
    return { opened: false, message: "Could not open this vendor in Settings. Try again." };
  }
  // UX120: the row is closed until opened; open it before scrolling to and focusing the field.
  setExpandedVendor(vendorId);
  // An inactive vendor's cause is its Vendor active switch, at the head of the row, so the row starts at the top.
  row.scrollIntoView({ block: focus === "active" ? "start" : "center" });
  (focus === "active"
    ? row.querySelector<HTMLInputElement>(".admin-vendor-active-row .admin-toggle-input")
    : row.querySelector<HTMLInputElement>('[data-testid="admin-vendor-contact-name"]'))?.focus();
  if (returnContext) {
    vendorCartReturn = returnContext;
    const strip = mustFind<HTMLElement>("#admin-vendor-cart-return");
    mustFind<HTMLElement>("#admin-vendor-cart-return-text").textContent =
      `Fixing ${returnContext.vendorName} for your cart. Saving here does not change your cart lines or quantities.`;
    strip.hidden = false;
  }
  return { opened: true };
}

let vendorCartReturn: (VendorOptionReturn & { vendorName: string }) | undefined;
mustFind<HTMLButtonElement>("#admin-vendor-cart-return-button").addEventListener("click", () => {
  const back = vendorCartReturn;
  clearVendorOptionReturn();
  if (back?.isCurrent()) back.onClose();
});

/** A blocked cart line's "Edit this offer" fix link. A vendor offer has
 * no editor of its own outside the item it belongs to (INV4's own
 * design), so this opens that item's Edit sheet on its Stock & vendor
 * tab (INV27, DEC-227 item 1: moved here from Item), scrolled to its
 * Vendor Options panel. */
type VendorOptionReturn = { isCurrent(): boolean; onClose(): void };
let vendorOptionReturn: VendorOptionReturn | undefined;

export function clearVendorOptionReturn(): void {
  vendorOptionReturn = undefined;
  vendorCartReturn = undefined;
  mustFind<HTMLElement>("#admin-vendor-cart-return").hidden = true;
  mustFind<HTMLButtonElement>("#admin-bottle-form-cancel").textContent = "Cancel";
}

export async function openVendorOfferEditor(
  activeSession: Session,
  liquorProductId: string,
  locationId: string,
  returnContext?: VendorOptionReturn,
): Promise<{ opened: boolean; message?: string }> {
  clearVendorOptionReturn();
  await openAdminScreenOnSection(activeSession, "inventory");
  if (returnContext && !returnContext.isCurrent()) return { opened: false };
  const reply = await getLiquorProduct(
    activeSession.apiBaseUrl,
    activeSession.tenantId,
    liquorProductId,
    locationId,
  );
  const current = sessionOrUndefined();
  if (
    current === undefined ||
    current.token !== activeSession.token ||
    current.userId !== activeSession.userId ||
    (returnContext && !returnContext.isCurrent())
  ) return { opened: false };
  if (reply.status !== 200)
    return { opened: false, message: reply.body.message ?? "Could not open Vendor Options. Try again." };
  if (reply.body.sellingContext?.locationId !== locationId)
    return { opened: false, message: "Vendor Options are unavailable at this location." };
  await openEditBottleForm(reply.body.liquorProduct, locationId, returnContext?.isCurrent);
  if (
    (returnContext && !returnContext.isCurrent()) ||
    bottleFormScrim.hidden || editingBottleId !== liquorProductId ||
    f3SellingUi.editor?.siteId !== locationId
  ) return { opened: false, message: "Could not open Vendor Options. Try again." };
  switchBottleFormTab("inventory");
  bottleVendorOffersEl.scrollIntoView({ block: "nearest" });
  if (returnContext) {
    vendorOptionReturn = returnContext;
    mustFind<HTMLButtonElement>("#admin-bottle-form-cancel").textContent = "Return to cart";
  }
  return { opened: true };
}

function setTabState(tab: HTMLButtonElement, selected: boolean): void {
  tab.setAttribute("aria-selected", selected ? "true" : "false");
}

// ---------------------------------------------------------------------
// SET14: leaving a settings tab discards an unsaved edit with no
// warning, and returning re-displays the typed text as though it saved.
// docs/build-briefs/SET14-unsaved-settings-edit-discarded-silently.md is
// the full design. Two independent unsaved-change domains, matching the
// real SAVE boundary rather than the visual tab boundary: the combined
// Money / Tabs & payment / Operations form (one saveSettingsForm call
// submits all of it, regardless of which of its three buttons fired) and
// PIN policy in People and access (its own, separately-saved form). A
// snapshot of each domain's own live values is captured immediately
// after that domain's own successful load or successful save; an
// attempt to leave the domain for a DIFFERENT one compares the current
// live values against that snapshot, and if any differ, warns before
// discarding.
// ---------------------------------------------------------------------

interface SettingsFormSnapshot {
  readonly tipPresets: readonly number[];
  readonly shelfTiers: readonly { readonly name: string; readonly price: string }[];
  readonly holdAmount: string;
  readonly holdIncrementThreshold: string;
  readonly abandonedTabGratuity: string;
  readonly abandonedTabGratuityDisclosure: boolean;
  readonly closeCardTicketsAtDayEnd: boolean;
  readonly dualPricingEnabled: boolean;
  readonly dualPricingPercent: string;
  readonly tipOnPreDiscountPrice: boolean;
  readonly roundingMode: RoundingMode;
  readonly pricingModel: PricingModel;
  readonly standardPourOunces: string;
  readonly businessDayCutoff: string;
  readonly businessDayTimezone: string;
}

interface PinPolicySnapshot {
  readonly minLength: string;
  readonly maxLength: string;
  readonly lockoutAttempts: string;
  readonly lockoutMinutes: string;
  /** UX86: the session inactivity card moved from Location to People and
   * access, so it is saved by its own button and tracked in this domain. */
  readonly sessionInactivity: string;
}

/** undefined until this domain's own first successful load; a domain
 * that has never successfully loaded cannot be dirty (nothing to compare
 * against), which matches SET15's own two-state shape: no third "failed"
 * state to track here either. */
let settingsFormSnapshot: SettingsFormSnapshot | undefined;
let pinPolicySnapshot: PinPolicySnapshot | undefined;

/** Reads shelf tier name/price straight from their live DOM inputs,
 * never from the settingsShelfTiers module array: the price input only
 * writes back into that array on blur or on Save
 * (saveSettingsForm's own comment explains why), so a value typed and
 * not yet blurred would be invisible to a dirty check that read the
 * array instead. Name updates the array on every keystroke already, but
 * is read the same way here for one consistent source per row. */
function currentShelfTierDomValues(): { name: string; price: string }[] {
  return [
    ...settingsShelfTiersEl.querySelectorAll<HTMLElement>(
      '[data-testid="admin-settings-shelf-tier-row"]',
    ),
  ].map((row) => ({
    name:
      row.querySelector<HTMLInputElement>('[data-testid="admin-settings-shelf-tier-name"]')
        ?.value ?? "",
    price:
      row.querySelector<HTMLInputElement>('[data-testid="admin-settings-shelf-tier-price"]')
        ?.value ?? "",
  }));
}

function captureSettingsFormSnapshot(): SettingsFormSnapshot {
  return {
    tipPresets: [...settingsTipPresets],
    shelfTiers: currentShelfTierDomValues(),
    holdAmount: settingsHoldAmountInput.value,
    holdIncrementThreshold: settingsHoldIncrementThresholdInput.value,
    abandonedTabGratuity: settingsAbandonedTabGratuityInput.value,
    abandonedTabGratuityDisclosure: settingsAbandonedTabGratuityDisclosureInput.checked,
    closeCardTicketsAtDayEnd: settingsCloseCardTicketsInput.checked,
    dualPricingEnabled: settingsDualPricingInput.checked,
    dualPricingPercent: settingsDualPricingPercentInput.value,
    tipOnPreDiscountPrice: settingsTipOnPreDiscountInput.checked,
    roundingMode: settingsRoundingMode,
    pricingModel: settingsPricingModel,
    standardPourOunces: settingsStandardPourOunces,
    businessDayCutoff: settingsBusinessDayCutoffInput.value,
    businessDayTimezone: settingsBusinessDayTimezoneInput.value,
  };
}

// B7-S3, narrowed by the P1(a) fix below (Codex review, PR #59 against
// 2dcb2df). Add and retire/reactivate (renderAddShelfTierRow's own
// button, renderShelfTierRows' own activation switch) commit immediately
// and independently of the Money group's Save button (build brief Section
// 5), so leaving the Money group afterward must not raise a "Discard
// unsaved changes?" confirm for a change that already committed
// (showSection's own SET14 discard-changes guard).
//
// The ORIGINAL fix here refreshed the snapshot's WHOLE shelfTiers slice
// from the live DOM on every add/retire/reactivate. That reintroduced the
// exact bug it was written to close, one level down: renderShelfTierRows()
// redraws EVERY row on any single tier's commit, so re-snapshotting every
// row from the DOM silently adopted whatever any OTHER tier's still-
// unsaved rename or reprice happened to say as the new "saved" baseline
// the moment a completely unrelated tier committed its own add or retire.
// A tier price is money, so that was silent loss of a money edit, not a
// display glitch.
//
// The fix is to refresh ONLY the one tier this specific action actually
// committed, identified by `committedTierKey` (settingsShelfTiers is the
// order-of-truth both this function and captureSettingsFormSnapshot's
// index-aligned discardSettingsFormChanges rely on):
//   - Add: this tier did not exist in the previous snapshot at all -- its
//     index falls past the snapshot's current length -- so there is no
//     unsaved edit on it to protect. Append it at its REAL committed
//     values, read from settingsShelfTiers itself (handleAddShelfTier
//     already wrote the server's own name/priceCents into that array
//     before calling this), never from the DOM: the add row's own inputs
//     are a different row entirely and carry nothing relevant here.
//   - Retire/reactivate: the server commit for THIS action only ever
//     changes the active flag, which this snapshot type does not even
//     track (SettingsFormSnapshot.shelfTiers is `{ name, price }` only,
//     by design -- active is not a Save-button field). So this tier's
//     existing snapshot entry is left exactly as it was: adopting the
//     DOM's current name/price for this same row would silently clear an
//     unsaved rename or reprice sitting on the very row whose toggle just
//     fired. This is a deliberate no-op branch, not a missing case.
// A no-op entirely when settingsFormSnapshot is still undefined (Money has
// never successfully loaded, so there is nothing to refresh) or when
// committedTierKey cannot be found in settingsShelfTiers.
function refreshShelfTierSnapshot(committedTierKey: string): void {
  if (settingsFormSnapshot === undefined) {
    return;
  }
  const index = settingsShelfTiers.findIndex((tier) => tier.tier === committedTierKey);
  if (index === -1 || index < settingsFormSnapshot.shelfTiers.length) {
    // Unknown key, or an existing tier (retire/reactivate): nothing to
    // change here, see the retire/reactivate branch above.
    return;
  }
  // A newly committed tier from a successful Add: append its own real
  // committed values, never the DOM's.
  const committed = settingsShelfTiers[index];
  if (committed === undefined) {
    return;
  }
  settingsFormSnapshot = {
    ...settingsFormSnapshot,
    shelfTiers: [
      ...settingsFormSnapshot.shelfTiers,
      { name: committed.name, price: centsToAmountString(committed.priceCents) },
    ],
  };
}

function capturePinPolicySnapshot(): PinPolicySnapshot {
  return {
    minLength: settingsPinMinLengthInput.value,
    maxLength: settingsPinMaxLengthInput.value,
    lockoutAttempts: settingsPinLockoutAttemptsInput.value,
    lockoutMinutes: settingsPinLockoutMinutesInput.value,
    sessionInactivity: settingsSessionInactivityInput.value,
  };
}

function isSettingsFormDirty(): boolean {
  if (settingsFormSnapshot === undefined) {
    return false;
  }
  return JSON.stringify(captureSettingsFormSnapshot()) !== JSON.stringify(settingsFormSnapshot);
}

function isPinPolicyDirty(): boolean {
  if (pinPolicySnapshot === undefined) {
    return false;
  }
  return JSON.stringify(capturePinPolicySnapshot()) !== JSON.stringify(pinPolicySnapshot);
}

/** Resets the combined form's fields to the values already captured
 * after the last successful load or save. No network round trip, so
 * this cannot itself fail, time out, or race with SET15's own
 * retry-on-failure fix (see the brief's "Discard restores from the
 * in-memory snapshot, never a network re-fetch" section). */
function discardSettingsFormChanges(): void {
  const snapshot = settingsFormSnapshot;
  if (snapshot === undefined) {
    return;
  }
  applySettingsFormSnapshot(snapshot);
  // UX108: a discarded edit is not kept as a draft either.
  const scope = adminDraftScope();
  if (scope !== undefined) clearDraft(scope, SETTINGS_FORM_DRAFT);
}

/** Puts a snapshot's values on the form: the saved baseline when an edit is discarded (UX108: or a draft read back
 * after a refresh, which then shows as an unsaved edit again because it differs from the baseline). */
function applySettingsFormSnapshot(snapshot: SettingsFormSnapshot): void {
  settingsTipPresets = [...snapshot.tipPresets];
  renderTipPresetChips();
  snapshot.shelfTiers.forEach((tierSnapshot, index) => {
    const tier = settingsShelfTiers[index];
    if (tier === undefined) {
      return;
    }
    tier.name = tierSnapshot.name;
    try {
      tier.priceCents = parseDollarsToCentsExact(tierSnapshot.price, "Shelf tier price");
    } catch {
      // The snapshot was captured from a state that had already rendered
      // successfully, so this should never throw; if it somehow does,
      // leave priceCents as it was rather than write a bad value.
    }
  });
  renderShelfTierRows();
  settingsHoldAmountInput.value = snapshot.holdAmount;
  settingsHoldIncrementThresholdInput.value = snapshot.holdIncrementThreshold;
  settingsAbandonedTabGratuityInput.value = snapshot.abandonedTabGratuity;
  settingsAbandonedTabGratuityDisclosureInput.checked = snapshot.abandonedTabGratuityDisclosure;
  settingsCloseCardTicketsInput.checked = snapshot.closeCardTicketsAtDayEnd;
  settingsDualPricingInput.checked = snapshot.dualPricingEnabled;
  settingsDualPricingPercentInput.value = snapshot.dualPricingPercent;
  settingsTipOnPreDiscountInput.checked = snapshot.tipOnPreDiscountPrice;
  settingsRoundingMode = snapshot.roundingMode;
  renderRoundingModeChips();
  settingsPricingModel = snapshot.pricingModel;
  renderPricingModelChips();
  settingsStandardPourOunces = snapshot.standardPourOunces;
  renderStandardPourOuncesInput();
  setCutoffListValue(snapshot.businessDayCutoff);
  settingsBusinessDayTimezoneInput.value = snapshot.businessDayTimezone;
  settingsBusinessDayHelpEl.textContent = "";
  renderBusinessDayConfirm();
  clearSettingsSaveStatuses();
}

function discardPinPolicyChanges(): void {
  const snapshot = pinPolicySnapshot;
  if (snapshot === undefined) {
    return;
  }
  applyPinPolicySnapshot(snapshot);
  const scope = adminDraftScope();
  if (scope !== undefined) clearDraft(scope, PEOPLE_FORM_DRAFT);
}

function applyPinPolicySnapshot(snapshot: PinPolicySnapshot): void {
  settingsPinMinLengthInput.value = snapshot.minLength;
  settingsPinMaxLengthInput.value = snapshot.maxLength;
  settingsPinLockoutAttemptsInput.value = snapshot.lockoutAttempts;
  settingsPinLockoutMinutesInput.value = snapshot.lockoutMinutes;
  settingsSessionInactivityInput.value = snapshot.sessionInactivity;
  settingsSaveStatusPinPolicyEl.textContent = "";
  sessionInactivitySaveStatusEl.textContent = "";
}

// ---------------------------------------------------------------------
// UX108 (docs/build-briefs/UX108-refresh-keeps-the-screen.md, section 2.3). An edit that has not been saved survives
// a browser refresh as a draft in sessionStorage: the same two snapshots the Discard prompt already keeps, written
// when they differ from the saved baseline and read back after the next successful load. The numbers here are
// configuration values (a tip percent, a hold amount, a PIN length bound), never a PIN; a draft that fails any
// check below is dropped, not partly applied. Cleared on save, on Discard, and on sign-out.
// ---------------------------------------------------------------------

const SETTINGS_FORM_DRAFT = "settingsForm";
const PEOPLE_FORM_DRAFT = "peopleForm";
let settingsFormDraftPending = true;
let peopleFormDraftPending = true;
let settingsDraftSaveTimer: ReturnType<typeof setTimeout> | undefined;

function adminDraftScope(): DraftScope | undefined {
  const current = sessionOrUndefined();
  return current === undefined ? undefined : { tenantId: current.tenantId, userId: current.userId };
}

function shortText(value: unknown, max: number): string | undefined {
  const text = asString(value);
  return text !== undefined && text.length <= max ? text : undefined;
}

function parseSettingsFormDraft(raw: unknown): SettingsFormSnapshot | undefined {
  const record = asRecord(raw);
  if (record === undefined) return undefined;
  const tips = record["tipPresets"];
  const tiers = record["shelfTiers"];
  if (
    !Array.isArray(tips) ||
    tips.length > 12 ||
    !tips.every((n) => typeof n === "number" && Number.isInteger(n) && n >= 0 && n <= 100_000)
  ) {
    return undefined;
  }
  // The tiers are matched to the loaded ones by position, so a different count is a different form.
  if (!Array.isArray(tiers) || tiers.length !== settingsShelfTiers.length) return undefined;
  const shelfTiers: { name: string; price: string }[] = [];
  for (const tier of tiers) {
    const tierRecord = asRecord(tier);
    const name = shortText(tierRecord?.["label"], 80);
    const price = shortText(tierRecord?.["price"], 20);
    if (name === undefined || price === undefined) return undefined;
    shelfTiers.push({ name, price });
  }
  const holdAmount = shortText(record["holdAmount"], 20);
  const holdIncrementThreshold = shortText(record["holdIncrementThreshold"], 20);
  const abandonedTabGratuity = shortText(record["abandonedTabGratuity"], 20);
  const standardPourOunces = shortText(record["standardPourOunces"], 10);
  const businessDayCutoff = shortText(record["businessDayCutoff"], 10);
  const businessDayTimezone = shortText(record["businessDayTimezone"], 80);
  const disclosure = record["abandonedTabGratuityDisclosure"];
  // A draft saved before this field existed carries none; the default is on.
  const closeCardTickets = record["closeCardTicketsAtDayEnd"] ?? true;
  // A draft saved before dual pricing existed carries neither field: off, and the starting percent.
  const dualPricingEnabled = record["dualPricingEnabled"] ?? false;
  const dualPricingPercent = record["dualPricingPercent"] ?? "2.70";
  // A draft saved before this field existed carries none; the default is off.
  const tipOnPreDiscount = record["tipOnPreDiscountPrice"] ?? false;
  const roundingMode = ROUNDING_MODES.find((mode) => mode === record["roundingMode"]);
  const pricingModel = PRICING_MODELS.find((model) => model === record["pricingModel"]);
  if (
    holdAmount === undefined ||
    holdIncrementThreshold === undefined ||
    abandonedTabGratuity === undefined ||
    standardPourOunces === undefined ||
    businessDayCutoff === undefined ||
    businessDayTimezone === undefined ||
    typeof disclosure !== "boolean" ||
    typeof closeCardTickets !== "boolean" ||
    typeof dualPricingEnabled !== "boolean" ||
    typeof dualPricingPercent !== "string" ||
    typeof tipOnPreDiscount !== "boolean" ||
    roundingMode === undefined ||
    pricingModel === undefined
  ) {
    return undefined;
  }
  return {
    tipPresets: tips as number[],
    shelfTiers,
    holdAmount,
    holdIncrementThreshold,
    abandonedTabGratuity,
    abandonedTabGratuityDisclosure: disclosure,
    closeCardTicketsAtDayEnd: closeCardTickets,
    dualPricingEnabled,
    dualPricingPercent,
    tipOnPreDiscountPrice: tipOnPreDiscount,
    roundingMode,
    pricingModel,
    standardPourOunces,
    businessDayCutoff,
    businessDayTimezone,
  };
}

function parsePinPolicyDraft(raw: unknown): PinPolicySnapshot | undefined {
  const record = asRecord(raw);
  if (record === undefined) return undefined;
  const minLength = shortText(record["minLength"], 6);
  const maxLength = shortText(record["maxLength"], 6);
  const lockoutAttempts = shortText(record["lockoutAttempts"], 6);
  const lockoutMinutes = shortText(record["lockoutMinutes"], 6);
  const sessionInactivity = shortText(record["sessionInactivity"], 6);
  if (
    minLength === undefined ||
    maxLength === undefined ||
    lockoutAttempts === undefined ||
    lockoutMinutes === undefined ||
    sessionInactivity === undefined
  ) {
    return undefined;
  }
  return { minLength, maxLength, lockoutAttempts, lockoutMinutes, sessionInactivity };
}

/** Writes (or clears) the two drafts from the live forms. Debounced from every edit on the admin screen. */
function saveSettingsDrafts(): void {
  const scope = adminDraftScope();
  if (scope === undefined) return;
  if (settingsFormSnapshot !== undefined) {
    if (isSettingsFormDirty()) {
      const snapshot = captureSettingsFormSnapshot();
      // The draft calls a shelf tier's name its label: a field named "name" is never kept (screenDrafts.ts).
      saveDraft(scope, SETTINGS_FORM_DRAFT, {
        ...snapshot,
        shelfTiers: snapshot.shelfTiers.map((tier) => ({ label: tier.name, price: tier.price })),
      });
    } else {
      clearDraft(scope, SETTINGS_FORM_DRAFT);
    }
  }
  if (pinPolicySnapshot !== undefined) {
    if (isPinPolicyDirty()) {
      saveDraft(scope, PEOPLE_FORM_DRAFT, capturePinPolicySnapshot());
    } else {
      clearDraft(scope, PEOPLE_FORM_DRAFT);
    }
  }
}

function scheduleSettingsDraftSave(): void {
  if (settingsDraftSaveTimer !== undefined) clearTimeout(settingsDraftSaveTimer);
  settingsDraftSaveTimer = setTimeout(() => {
    settingsDraftSaveTimer = undefined;
    saveSettingsDrafts();
  }, 150);
}

/** A save has just committed the form: its draft is finished. */
function noteSettingsFormCommitted(): void {
  const scope = adminDraftScope();
  if (scope !== undefined) clearDraft(scope, SETTINGS_FORM_DRAFT);
}

function notePinPolicyCommitted(): void {
  const scope = adminDraftScope();
  if (scope !== undefined) clearDraft(scope, PEOPLE_FORM_DRAFT);
}

/** After the first successful settings load of a page: puts back what an earlier page was in the middle of typing. */
function restoreSettingsDrafts(): void {
  const scope = adminDraftScope();
  if (scope === undefined) return;
  if (settingsFormDraftPending) {
    settingsFormDraftPending = false;
    const draft = parseSettingsFormDraft(loadDraft(scope, SETTINGS_FORM_DRAFT));
    if (draft === undefined) {
      clearDraft(scope, SETTINGS_FORM_DRAFT);
    } else {
      applySettingsFormSnapshot(draft);
    }
  }
  if (peopleFormDraftPending) {
    peopleFormDraftPending = false;
    const draft = parsePinPolicyDraft(loadDraft(scope, PEOPLE_FORM_DRAFT));
    if (draft === undefined) {
      clearDraft(scope, PEOPLE_FORM_DRAFT);
    } else {
      applyPinPolicySnapshot(draft);
    }
  }
}

// A refresh can come in the debounce window after the last keystroke, so a pending write is flushed as the page goes.
window.addEventListener("pagehide", () => {
  if (settingsDraftSaveTimer === undefined) return;
  clearTimeout(settingsDraftSaveTimer);
  settingsDraftSaveTimer = undefined;
  saveSettingsDrafts();
});
adminScreenEl.addEventListener("input", scheduleSettingsDraftSave);
adminScreenEl.addEventListener("change", scheduleSettingsDraftSave);
adminScreenEl.addEventListener("click", scheduleSettingsDraftSave);

type SettingsDirtyDomain = "settingsForm" | "pinPolicy";

/** The two save boundaries found by reading saveSettingsForm and
 * savePinPolicy, not the visual tab layout: Discounts, Corrections,
 * Recipes, Purchasing, Menu, Inventory, Users and Network carry none of
 * either domain's fields, so leaving them is never a "leaving a dirty
 * domain" event even though some of them share the same settingsLoaded
 * fetch. SET10: Recipes falls through the same as Menu did before it
 * split off the Drinks add/edit sheet, which already has its own
 * save/cancel and was never part of this shared dirty tracking. */
function settingsDirtyDomainFor(section: AdminSection): SettingsDirtyDomain | undefined {
  if (section === "money" || section === "tabsPayment" || section === "operations") {
    return "settingsForm";
  }
  if (section === "people") {
    return "pinPolicy";
  }
  return undefined;
}

function isSettingsDirtyDomainDirty(domain: SettingsDirtyDomain): boolean {
  return domain === "settingsForm" ? isSettingsFormDirty() : isPinPolicyDirty();
}

function discardSettingsDirtyDomain(domain: SettingsDirtyDomain): void {
  if (domain === "settingsForm") {
    discardSettingsFormChanges();
  } else {
    discardPinPolicyChanges();
  }
}

async function showSection(section: AdminSection): Promise<boolean> {
  const navigationGeneration = loadGeneration;
  if (!(await productCatalogSection.confirmLeave()) || !loadIsCurrent(navigationGeneration)) return false;
  const legacySetup = section === "menu";
  if (legacySetup) section = "inventory";
  hideHoverHelp();
  if(currentSection==="operations"&&section!==currentSection&&!tenantPermitPanel.confirmLeave())return false;
  clearVendorOptionReturn();
  // Defensive, mirrors every server route's own real enforcement: every
  // reachable path to this function (the tab click handlers below, and
  // openAdminScreen's own initialSectionFor) already checks
  // sectionVisible, so this is a fail-safe against a future call site
  // that does not, never the actual authorization boundary -- the
  // underlying data routes each panel calls (loadDrinks, loadBottles,
  // etc.) are independently permission-gated server-side regardless.
  if (currentSession !== undefined && !sectionVisible(currentSession, section)) {
    return false;
  }
  // UX120: leaving Purchasing closes the open vendor.
  if (section !== "purchasing") {
    expandedVendorId = undefined;
  }
  // SET14: warn before an unsaved edit in a dirty domain (the combined
  // Money/Tabs & payment/Operations form, or PIN policy) is discarded by
  // switching to a section OUTSIDE that same domain. Switching between
  // sections that share one save boundary (e.g. Operations to Money)
  // never warns, since either domain's own Save button would commit the
  // same edit; see settingsDirtyDomainFor's own comment for the boundary.
  if (section !== currentSection) {
    const leavingDomain = settingsDirtyDomainFor(currentSection);
    if (
      leavingDomain !== undefined &&
      settingsDirtyDomainFor(section) !== leavingDomain &&
      isSettingsDirtyDomainDirty(leavingDomain)
    ) {
      const discard = await showConfirmSheet({
        title: "Discard unsaved changes?",
        message:
          "You have changes on this screen that have not been saved. Leaving now will discard them.",
        confirmLabel: "Discard changes",
      });
      if (!discard) {
        // Cancel: stay on the current tab with the edit untouched.
        return false;
      }
      discardSettingsDirtyDomain(leavingDomain);
    }
  }
  // SET16-F1: the confirm sheet just above can sit open for as long as
  // the acting employee takes to answer it, long enough for their own
  // session to actually die in the meantime (an idle timeout, a forced
  // sign-out from another device). Every permission check and loader
  // below resolves the session again through session(), which now
  // throws once it is really gone rather than quietly reusing a stale
  // one; bailing out here turns that into an ordinary early return
  // instead of an unhandled rejection reaching nothing that could show
  // it.
  if (sessionOrUndefined() === undefined) {
    return false;
  }
  productCatalogSection.reset();
  catalogPlanningSequence++;catalogPlanningLocationId=undefined;catalogScopeHost.replaceChildren();
  if (section !== "inventory") clearVendorSetupView();
  // UIFIX2 (SF-12): a different Settings group opens at its top, never at the scroll position of the last one.
  if (currentSection !== section) {
    settingsJumpVisit += 1;
    resetSettingsScroll();
  }
  currentSection = section;
  if (section === "inventory") selectInventorySetupTab(legacySetup ? "setup" : "items");
  if(section==="operations"&&hasPermission(session(),"admin.location_permits.view")){
    mustFind<HTMLElement>("#admin-location-permits").hidden=false;
    void tenantPermitPanel.load(session(),settingsAddressLocationSelect.value||undefined);
  }else tenantPermitPanel.reset();
  // ACCTEXPORT1 and PAYEXPORT1: the QuickBooks and Gusto cards live on the Connections page and read on every entry to it.
  if (section === "connections") void refreshConnections(session());
  // MGRROLE1 (DEC-599): billing stays with the Admin. A person who can open this page without that permission is
  // told so and who can help, instead of meeting a blank panel.
  billingAdminOnlyNote.hidden = !(section === "network" && !hasPermission(session(), "billing.ai.manage"));
  if (section === "network" && hasPermission(session(), "billing.ai.manage")) {
    void billingStatementsCard.load();
    void aiHelpSection.load();
    void aiBillingPanel.load();
    void bankAccountsSection.refresh();
  } else {
    billingStatementsCard.reset();
    aiHelpSection.reset();
    aiBillingPanel.reset();
    bankAccountsSection.reset();
  }
  // UX80: a Settings group is on screen, so no operations view is.
  exitOperationsMode();
  // UX108: the group is part of the screen's address.
  requestRoutePublish();
  for (const candidate of ADMIN_SECTION_ORDER) {
    setTabState(ADMIN_SECTION_TABS[candidate], candidate === section);
    ADMIN_SECTION_PANELS[candidate].hidden = candidate !== section;
  }
  // RPTLAYOUT1-F1-S2: the Audit log group reads its Activity tab on every entry. UIFIX2 (SF-12): the rail stays a
  // rail here, as on every other Settings group, so the page no longer flips to the top chip row on this one tab.
  settingsShellEl.classList.remove("settings-shell-wide");
  if (section === "auditLog" && hasPermission(session(), "admin.change_history.view")) {
    await openAuditLogTab(session(), "activity");
  }
  if(section==="inventory")void refreshLocalProductCatalog();
  // UX92: the Add Discount button depends on the permission alone, not on any load, so it is
  // settled the moment the group is shown.
  if (section === "discounts") syncDiscountAddOpenButton();

  if (section === "recipes" && !drinksLoaded) {
    // SET15: drinksLoaded is set inside loadDrinks itself, only once its
    // own fetch actually succeeds -- not here, before the await -- so a
    // failed load leaves the latch false and the next visit to Recipes
    // retries rather than skipping the fetch forever.
    renderDrinkFilterChips();
    // TEN19: what the franchise network offers this location, drawn above the list. Independent of the list read; a search
    // or filter change re-reads the list only.
    if (hasPermission(session(), "admin.menu_drink.manage")) {
      void networkRecipeOffers.refresh();
    }
    await loadDrinks();
  }
  if (section === "recipes") importRecipesButton.hidden = !hasPermission(session(), RECIPE_IMPORT_PERMISSION);
  if (section === "recipes" && hasPermission(session(), "admin.recipe.publish"))
    void recipeSharing.refresh();
  // SET10: loadVocabularies() is shared across Recipes (the Add/Edit Drink
  // sheet's own pickers) and Menu (the eight vocabulary editors
  // themselves), the same "whichever is opened FIRST triggers it" shape
  // loadSettings() already uses across seven groups below. A genuinely
  // independent latch, not drinksLoaded -- see vocabulariesLoaded's own
  // declaration for why reusing drinksLoaded here would be wrong. Called
  // directly from showSection's own text, never through a wrapper, so
  // check:loader-permission-coupling's text scan keeps seeing it as the
  // self-permission loader it already is.
  //
  // INV27 (DEC-227 item 3, coordinator correction 2026-09-29): "inventory"
  // added to this list when the "Default target markup %, by item kind"
  // card moved from Menu to Settings > Inventory (mockup Artboard D). The
  // card reads the identical adminVocabularies["item_kind"] list and
  // itemKindTargetMarkupDefaults store this loader already populates, so a
  // manager who opens Inventory without ever visiting Menu or Recipes
  // first still sees real data rather than an empty card.
  if (
    (section === "recipes" || section === "menu" || section === "inventory") &&
    !vocabulariesLoaded
  ) {
    await loadVocabularies();
  }
  if (section === "inventory") {
    // AUTOPAR1: the Automatic par levels cards and the Par levels tab read their own route and draw nothing for a
    // session that may not view stock levels (ui/src/autoPar.ts).
    void autoParSection.refresh();
  }
  if (section === "inventory" && !bottlesLoaded && hasPermission(session(), "admin.liquor_product.manage")) {
    // SET15: bottlesLoaded is set inside loadBottles itself; see the
    // drinksLoaded comment above for why.
    // Owner alpha-review feedback: the shelf-tier filter chips need the
    // tenant's configured tier NAMES (never the raw tier_1..tier_4 keys),
    // which live in Money and pricing, not in a liquor product itself.
    // Fetched once here (idempotent no-op if already loaded, e.g. the
    // manager already visited Money and pricing this session) so the
    // chips show real names on the very first Inventory-group open, not
    // a placeholder that then jumps once settings load separately.
    // showSection itself is not one of the gen-guarded loaders (SET16-F4's
    // own approved scope), so a local `gen` is captured here specifically
    // to span this one await -- ensureShelfTierSettingsLoaded's own fetch
    // can span a real teardown (DEC-083 independent review), and resuming
    // straight into loadBottles() afterward would call session() with no
    // session left, an unhandled rejection rather than an ordinary bail.
    const gen = loadGeneration;
    await ensureShelfTierSettingsLoaded(gen);
    if (!loadIsCurrent(gen)) return false;
    renderBottleFilterChips();
    await loadBottles();
  } else if (
    (section === "money" ||
      section === "tabsPayment" ||
      section === "discounts" ||
      section === "corrections" ||
      section === "operations" ||
      // INV4: Purchasing now carries real content (vendors), so it needs
      // the same loadSettings() (and, inside it, loadVendors()) every
      // other group already gets. Before this, clicking Purchasing FIRST
      // (never having visited another settingsLoaded-gated group first)
      // left the vendor list permanently empty, since nothing ever called
      // loadVendors() -- found live by e2e/inv4-vendor-management.spec.ts's
      // own reload-and-reread test, not by reading.
      section === "purchasing" ||
      // Migration 0043: People and access now carries real tenant
      // settings of its own (the PIN policy), so it needs the same
      // loadSettings() the other groups do. It is in the condition but
      // NOT the else-if chain below, because this group also needs
      // loadUsers() and an else-if would run only one of them.
      section === "people") &&
    !settingsLoaded &&
    // Regression found in review of PR #54 (VEN3 + SET15 combined). GET
    // /tenants/:tenantId/settings requires admin.tenant_settings.view.
    // money/tabsPayment/discounts/corrections can only ever be reached by
    // a session that already holds admin.tenant_settings.view (their own
    // ADMIN_TAB_PERMISSION_IDS entry is exactly that one permission), so
    // for them this check is always true and nothing changes. operations
    // and people are each reachable through a WIDER union that does not
    // require admin.tenant_settings.view (report.closeout.view etc. for
    // operations; admin.user.view alone for people) -- purchasing already
    // documented this same gap above for inv.vendor.view et al. An actor
    // who can reach one of those tabs without holding
    // admin.tenant_settings.view is not having a transient failure: the
    // server will refuse this exact request every single time. Before
    // SET15, that cost one wasted call and one toast per screen visit;
    // after SET15 stopped setting the latch ahead of the await (correctly,
    // for genuine transient failures), the same denied actor got the
    // request AND the toast repeated on every single visit, forever, since
    // the latch can never become true and the outer !settingsLoaded check
    // never blocks the retry. Checking the permission before issuing the
    // request, the same way purchasingVendorsVisible already gates the
    // vendor block below, removes the retry loop for an authorization
    // outcome that a retry can never change, while leaving SET15's own
    // retry-on-genuine-failure behavior (a timeout, a 5xx) untouched for
    // every session that does hold the permission.
    hasPermission(session(), "admin.tenant_settings.view")
  ) {
    // All seven groups above still share the ONE loadSettings() call the
    // old monolithic "settings" tab made: it already loads tenant_settings,
    // terminals, vendors, void reasons, discounts, discount reasons and
    // refund reasons together, and whichever of these groups is opened
    // FIRST (among sessions that hold admin.tenant_settings.view) is the
    // one that happens to trigger it, exactly like the old single tab did
    // for whoever clicked it. No change to what loads or when relative to
    // each other; only which click can be first changed.
    //
    // SET15: settingsLoaded is set inside loadSettings itself, only once
    // its own fetch actually succeeds, so a failed load (a real transient
    // one, from a session that does hold the permission above) leaves the
    // latch false and the next visit to any of these groups retries.
    await loadSettings();
  }
  // SETMAINFIX1: the loaders above are real network round trips the session can die during (a sign-out while
  // Inventory is still loading). Every block below calls session() again, which throws with no session, so the
  // same bailout the later SET16-F1 guard makes applies before the first of them, not only before the vendor block.
  if (sessionOrUndefined() === undefined) {
    return false;
  }
  if (section === "tablets" && hasPermission(session(), "admin.device.manage")) {
    // SEC2-S3: the section draws and reads its own lists (devices, waiting codes) on every entry, so a
    // code that ended or a tablet revoked elsewhere is never shown stale. Its route's permission is the same
    // one this group requires.
    await tabletsSection.refresh();
  }
  if (section === "tablets") {
    // UX126: Card readers are listed in Devices. The card is drawn for a session that may view readers (the
    // permission GET .../terminals requires), and the list is read on every entry.
    const mayViewReaders = hasPermission(session(), "admin.terminal.view");
    cardReadersCardEl.hidden = !mayViewReaders;
    if (mayViewReaders) {
      await loadTerminals();
    }
    // UX127: Receipt printers are listed in Devices too; nothing is drawn or requested without printer.manage.
    await printersSection.refresh();
  }
  if (section === "people" && !usersLoaded) {
    // SET15: usersLoaded is set inside loadUsers itself; see above.
    await loadUsers();
    // SET17-F3: called directly here, in showSection's own text, rather than
    // from inside loadUsers' own body, so check:loader-permission-coupling can
    // see loadTenantRoles as its own screen-entry loader and verify
    // admin.user.view (its own route's permission) against this section's
    // permission union, the same fix SET10 made for loadVocabularies.
    await loadTenantRoles();
  }
  // inventory can need BOTH the bottles load above AND the counts load
  // below on its first open (an else-if chain only ever runs one
  // branch), so counts gets its own unconditional check rather than
  // living in the chain.
  if (
    section === "inventory" &&
    !countsLoaded &&
    hasPermission(session(), "inv.count_section.manage")
  ) {
    // SET15: countsLoaded is set inside loadCountAreas itself; see above.
    // UX80: Settings only needs the count AREAS now; the full counting
    // loader (open session, past counts) belongs to the Counts view.
    await loadCountAreas();
  }
  // SET16-F1: loadSettings/loadUsers/loadCounts above are each a real
  // network round trip the session can die during; the vendor and
  // operations blocks below call session() again, so the same bailout
  // as the discard-confirm guard above applies here too.
  if (sessionOrUndefined() === undefined) {
    return false;
  }
  // VEN4-F1: the People PIN-policy visibility gate and the Purchasing
  // block-visibility/add-control/row-re-render gates used to live only
  // here, inline, which is why a permission-snapshot refresh with no
  // section change (refreshTabVisibility) could never reach them.
  // Extracted so there is exactly one place that decides this, called
  // from both showSection (here, an entry into the section) and
  // refreshTabVisibility (already on it).
  applyInSectionGating(section);
  if (section === "purchasing" && !vendorsLoaded && purchasingVendorsVisible(session())) {
    // SET15: vendorsLoaded is set inside loadVendors itself, only once
    // its own fetch actually succeeds; see the drinksLoaded comment
    // above. (This latch was found, mid-batch, to carry the identical
    // pre-fix defect the other seven had: VEN3 added it with the flip
    // still happening here, before the await.)
    await loadVendors();
  }
  // UX80: the Location group (formerly Operations) keeps only the business day,
  // session and address settings; the client matching queue and the MyTime
  // sync line moved to Match clients (openOperationsView).
  if (section === "operations") {
    operationsTenantSettingsEl.hidden = !operationsTenantSettingsVisible(session());
    // UX128: Locations are set up here, not in Devices. The card draws for a session that may add or list locations and reads
    // the list on every entry.
    await locationsSection.refresh();
  }
  // UX12: last, so every hidden-attribute change this function makes for
  // Inventory's bottles/counts split and Operations' reconciliation/
  // MyTime sections has already landed before the jump control reads
  // which subsections are actually visible.
  ensureSettingsSectionJump(ADMIN_SECTION_PANELS[section]);
  return true;
}

// ---------------------------------------------------------------------
// UX12 (DEC-052 ruling (2)): settings section jump
// ---------------------------------------------------------------------
//
// Generic over every settings group rather than special-cased to Menu: a
// group's panel earns a jump control the moment its own VISIBLE
// subsection headings exceed SETTINGS_SECTION_JUMP_THRESHOLD, built from
// those same headings. A group that grows past the threshold later
// (Inventory, Purchasing) gains the control with no second
// implementation; nothing here names a group.
// HIGFIX4A (F-25): five sections fit one scroll, so a group of five or fewer has no jump row under the rail.
const SETTINGS_SECTION_JUMP_THRESHOLD = 5;
const SETTINGS_SECTION_JUMP_HEADING_SELECTOR = ".section-title, .admin-subsection-title";

function slugifyForSettingsSectionJump(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** The heading's own label, stripped of a provisional-decision flag chip
 * nested inside it (Money's "Sales tax"/"Tip presets" headings each carry
 * one): the jump button names the subsection, not the flag beside it. */
function settingsSectionJumpLabelFor(heading: HTMLElement): string {
  const clone = heading.cloneNode(true) as HTMLElement;
  // UIFIX2 (SF-12): a chip names its section and never carries help text. HELPHOVER1: a heading's help is its hover
  // tooltip, which is not part of the heading's text, so only the chip and buttons are stripped.
  for (const extra of Array.from(
    clone.querySelectorAll(".provisional-flag, button"),
  )) {
    extra.remove();
  }
  return (clone.textContent ?? "").trim();
}

/** UIFIX2 (SF-12): the page scrolls inside #app-shell, not the window, so both are reset when the group changes. */
function resetSettingsScroll(): void {
  document.querySelector<HTMLElement>("#app-shell")?.scrollTo({ top: 0, behavior: "instant" });
  window.scrollTo(0, 0);
}

function scrollSettingsHeadingIntoView(heading: HTMLElement): void {
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  heading.scrollIntoView({ behavior: reducedMotion ? "auto" : "smooth", block: "start" });
}

/** UX36 independent-verification fix (Control 5): native Chromium
 * focus-scroll does not reliably bring a MID-LIST jump chip inside
 * `.admin-section-jump-list`'s own visible window when it receives
 * keyboard focus by forward Tab -- measured directly, one focus move
 * late, so the chip the keyboard is actually on can sit invisible off
 * the right edge. This closes the "Off-screen chip by keyboard" open
 * item this brief left unconfirmed rather than resolved. Reads live
 * `getBoundingClientRect()` for both the list and the chip, not
 * `offsetLeft`/`offsetWidth` against the list's own box: `.admin-
 * section-jump` (the list's own parent) is `position: sticky`, which
 * makes IT the buttons' own `offsetParent`, not the list itself, so an
 * `offsetLeft` read would be relative to the wrong box entirely. A
 * bounding-rect comparison sidesteps that ambiguity outright and already
 * reflects the list's own `padding-inline` (the chip's real rendered
 * position already accounts for it, since padding is part of layout, not
 * something to add back in separately). Sets `scrollLeft` DIRECTLY
 * (never `scrollIntoView`, never `behavior: "smooth"`): an animated
 * scroll here would be a SECOND concurrent smooth-scroll animation
 * alongside a jump's own smooth page scroll, which is exactly the
 * frozen-scroll regression measured and documented on
 * `updateActiveSettingsSectionJumpButton`'s own comment above. A direct
 * assignment has no animation to collide with. No-ops when the chip
 * already sits fully inside the list's own visible bounds, so a chip
 * already on screen (the common case, including the one a click just
 * brought into view) is never redundantly nudged. */
function scrollSettingsJumpChipIntoView(list: HTMLElement, chip: HTMLElement): void {
  const listRect = list.getBoundingClientRect();
  const chipRect = chip.getBoundingClientRect();
  const alreadyVisible = chipRect.left >= listRect.left && chipRect.right <= listRect.right;
  if (alreadyVisible) return;
  // Aligns the chip's own left edge to the list's own scroll-padding-
  // adjusted start, the SAME position `.admin-section-jump-button`'s own
  // `scroll-snap-align: start` (harness/ui/styles.css) would resolve to.
  // Measured directly: a "scroll just enough to reveal the trailing
  // edge" computation (the minimal amount, not a real snap point) gets
  // silently reverted to the PREVIOUS scrollLeft by this list's own
  // `scroll-snap-type: x proximity` the moment layout next settles,
  // because that computed value is not itself a valid snap point and
  // proximity snapping pulls an unaligned position back to the nearest
  // one -- reproduced live: assigning the minimal reveal amount read
  // back as the value that had been written only while `scroll-snap-
  // type` was forced to `none`, and reverted the instant it was restored.
  // Aligning to the chip's own real snap point instead means there is
  // nothing left to snap back to, and it holds. `scrollIntoView` with
  // `behavior: "instant"` was tried first and rejected: it did not move
  // this list at all for this exact chip, measured directly, so it is
  // not a reliable substitute here either.
  const scrollPaddingLeft = parseFloat(getComputedStyle(list).scrollPaddingLeft) || 0;
  const chipContentLeft = chipRect.left - listRect.left + list.scrollLeft;
  list.scrollLeft = chipContentLeft - scrollPaddingLeft;
}

/** UX36 (DEC-186): marks `active`'s own button aria-current="true" and
 * clears every sibling. Called only from a jump button's own click
 * handler below, with the heading that button names, so no computation
 * of "which heading counts as current" is needed here: the click itself
 * is the unambiguous answer.
 *
 * DEVIATION FROM THIS ITEM'S OWN ORIGINAL DESIGN, recorded here rather
 * than silently, because it narrows what shipped: DEC-186's own approved
 * Option A build called for active-chip tracking that reacts to ANY
 * scroll (via an IntersectionObserver, "cheaper, and it already
 * recalculates automatically on resize"), scrolling the active chip
 * itself into view as part of that reaction. Measured directly during
 * this item's build session, each confirmed by toggling ONE change at a
 * time with everything else held constant: reacting to scroll AT ALL --
 * by an IntersectionObserver watching even one heading, or by a plain
 * `element.addEventListener("scroll", ...)` on the real scrolling
 * ancestor, or simply by calling a SECOND `scrollIntoView({ behavior:
 * "smooth" })` (on the button, to bring the active chip into view
 * horizontally) synchronously alongside the heading's own jump scroll --
 * each independently freezes `scrollIntoView({ behavior: "smooth" })`
 * completely under the floor tablet's own touch/mobile-emulated viewport
 * (FLOOR_DEVICE): the heading never moves at all. None of these
 * reproduce under a plain desktop Chrome context (ADMIN_WORKSTATION),
 * and all of them broke `harness/e2e/admin.spec.ts`'s own UX12 test,
 * which exercises this exact panel under FLOOR_DEVICE regardless of
 * Settings being a laptop/PC surface (DEC-060; that file's own header
 * comment explains why). What shipped instead: the active chip is set
 * on CLICK only, never from scroll, and this function never itself calls
 * `scrollIntoView` -- the clicked button is necessarily already visible
 * in the horizontal strip (a person cannot click what they cannot see or
 * reach), so no further scroll is needed for that case, and no organic,
 * non-click scroll updates the active chip at all. This still satisfies
 * every one of DEC-186's own three quoted requirements (one row, scrolls
 * sideways, never covers the heading) and this item's own Test Plan
 * step 8, which asserts the active state only after a click; it does not
 * satisfy the fuller "tracks... as you scroll" behavior the mockup's own
 * Option A demo shows, which is the scope this deviation gives up. */
function updateActiveSettingsSectionJumpButton(
  buttonsByHeadingId: ReadonlyMap<string, HTMLButtonElement>,
  active: HTMLElement,
): void {
  for (const [headingId, button] of buttonsByHeadingId) {
    button.setAttribute("aria-current", String(headingId === active.id));
  }
}

/** UX38 (DEC-359, PROVISIONAL): closes a panel's open "All N sections"
 * list, if any. Keyed by the nav so ensureSettingsSectionJump can close a
 * list that belongs to the nav it is about to rebuild, which also removes
 * the document-level listeners that list added. */
const settingsSectionListClosers = new WeakMap<HTMLElement, () => void>();

/** The group's own tab label, for the list's header ("Sections in Menu").
 * Read from the tab that owns this panel, never a second hand-typed name. */
function settingsGroupLabelFor(panel: HTMLElement): string | null {
  for (const section of ADMIN_SECTION_ORDER) {
    if (ADMIN_SECTION_PANELS[section] === panel) {
      return (ADMIN_SECTION_TABS[section].textContent ?? "").trim() || null;
    }
  }
  return null;
}

/** The section the reader is at, read once when the list opens: the last
 * heading that has reached the top of the page (within the heading's own
 * scroll-margin-top, which is where a jump lands it), or the first.
 * Deliberately not a scroll listener (see
 * updateActiveSettingsSectionJumpButton). */
function settingsHeadingInView(headings: readonly HTMLElement[]): HTMLElement {
  let current = headings[0]!;
  for (const heading of headings) {
    const margin = parseFloat(getComputedStyle(heading).scrollMarginTop) || 0;
    // UX103: within 64px of where a jump lands it counts as reached; the content above a jumped-to heading can still
    // settle after the scroll starts and leave it a few dozen pixels short.
    if (heading.getBoundingClientRect().top - margin <= 64) current = heading;
  }
  // UX103: a short last section cannot reach the top of the page (the Menu lists are shorter now), so at the bottom of
  // the scroller (within 64px: a smooth scroll can still be creeping and the page has bottom padding) the last heading that is on screen is the current one.
  const last = headings[headings.length - 1]!;
  let scroller: HTMLElement | null = last.parentElement;
  while (scroller !== null && scroller.scrollHeight <= scroller.clientHeight + 1) {
    scroller = scroller.parentElement;
  }
  if (
    scroller !== null &&
    scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 64 &&
    last.getBoundingClientRect().top < window.innerHeight
  ) {
    current = last;
  }
  return current;
}

/** Builds, rebuilds, or removes the panel's own jump control from its
 * current subsection headings. A heading not hidden by an ancestor
 * (Inventory's own bottles/counts split, Operations' reconciliation and
 * MyTime sections each independently hide by permission) counts; one
 * that is does not, so the list never offers a target with nothing to
 * scroll to. A heading gains a stable id the first time it is seen here,
 * namespaced by its own panel's id so two groups can carry an
 * identically worded subsection without their ids colliding. */
function ensureSettingsSectionJump(panel: HTMLElement): void {
  const existing = panel.querySelector<HTMLElement>(":scope > .admin-section-jump");
  // Late reads must not erase navigation already used during this section visit.
  const sameVisit = existing !== null && settingsJumpVisits.get(existing) === settingsJumpVisit &&
    ADMIN_SECTION_PANELS[currentSection] === panel && panel.closest("[hidden]") === null;
  const selectedTestId = sameVisit ? existing.querySelector<HTMLElement>(
    '.admin-section-jump-button[aria-current="true"]',
  )?.dataset["testid"] : undefined;
  const focused = document.activeElement;
  const focusedTestId = sameVisit && focused instanceof HTMLElement && existing.contains(focused) &&
    focused.classList.contains("admin-section-jump-button") ? focused.dataset["testid"] : undefined;
  const previousScroll = sameVisit ? existing.querySelector<HTMLElement>(".admin-section-jump-list")?.scrollLeft : undefined;
  const headings = Array.from(
    panel.querySelectorAll<HTMLElement>(SETTINGS_SECTION_JUMP_HEADING_SELECTOR),
  ).filter((heading) => heading.closest("[hidden]") === null);

  if (headings.length <= SETTINGS_SECTION_JUMP_THRESHOLD) {
    existing?.remove();
    return;
  }

  const nav = existing ?? document.createElement("nav");
  settingsJumpVisits.set(nav, settingsJumpVisit);
  nav.className = "admin-section-jump";
  nav.setAttribute("aria-label", "Jump to a section of this settings group");
  nav.innerHTML = "";

  const label = document.createElement("span");
  label.className = "admin-section-jump-label";
  label.textContent = "Jump to:";
  nav.append(label);

  const list = document.createElement("div");
  list.className = "admin-section-jump-list";
  const buttonsByHeadingId = new Map<string, HTMLButtonElement>();
  for (const heading of headings) {
    if (heading.id === "") {
      const slug = slugifyForSettingsSectionJump(settingsSectionJumpLabelFor(heading));
      heading.id = `${panel.id}-section-${slug}`;
    }
    const button = document.createElement("button");
    button.type = "button";
    button.className = "admin-section-jump-button";
    button.dataset["testid"] = `admin-section-jump-${heading.id}`;
    button.textContent = settingsSectionJumpLabelFor(heading);
    button.setAttribute("aria-current", String(button.dataset["testid"] === selectedTestId));
    list.append(button);
    buttonsByHeadingId.set(heading.id, button);
  }
  nav.append(list);

  // AU14 designer polish: the fade at the strip's edge is a faint cue, and a chip
  // can sit wholly past it. A visible "more" arrow shows while chips remain to the
  // right of the visible strip, and one tap scrolls them in. The strip stays one
  // row (UX36).
  const more = document.createElement("button");
  more.type = "button";
  more.className = "admin-section-jump-more";
  more.dataset["testid"] = `admin-section-jump-more-${panel.id}`;
  more.setAttribute("aria-label", "Show more sections");
  more.textContent = "\u203a";
  more.hidden = true;
  const refreshMore = (): void => {
    more.hidden = list.scrollLeft + list.clientWidth >= list.scrollWidth - 2;
  };
  more.addEventListener("click", () => {
    list.scrollBy({ left: Math.max(120, Math.round(list.clientWidth * 0.6)), behavior: "smooth" });
  });
  list.addEventListener("scroll", refreshMore, { passive: true });
  if (typeof ResizeObserver !== "undefined") {
    new ResizeObserver(refreshMore).observe(list);
  }
  nav.append(more);

  // UX38 (DEC-279 option A, DEC-359 PROVISIONAL): a pinned "All N sections"
  // button that opens a tap list of every section, so no section is out of
  // reach however many the strip hides. The strip stays sticky while the list
  // is open (the list lives inside the same sticky nav), and the list closes
  // on a row tap, any tap outside it, the button again, or Escape.
  settingsSectionListClosers.get(nav)?.();
  const allButton = document.createElement("button");
  allButton.type = "button";
  allButton.className = "admin-section-jump-all";
  allButton.dataset["testid"] = `admin-section-jump-all-${panel.id}`;
  allButton.setAttribute("aria-expanded", "false");
  const allLabel = document.createElement("span");
  allLabel.textContent = `All ${headings.length} sections`;
  const allCaret = document.createElement("span");
  allCaret.className = "admin-section-jump-caret";
  allCaret.setAttribute("aria-hidden", "true");
  allButton.append(allLabel, allCaret);

  const listPop = document.createElement("div");
  listPop.className = "admin-section-jump-pop";
  listPop.id = `${panel.id}-section-jump-pop`;
  listPop.dataset["testid"] = `admin-section-jump-pop-${panel.id}`;
  listPop.hidden = true;
  allButton.setAttribute("aria-controls", listPop.id);
  const popHead = document.createElement("div");
  popHead.className = "admin-section-jump-pop-head";
  const groupLabel = settingsGroupLabelFor(panel);
  popHead.textContent = groupLabel === null ? "Sections" : `Sections in ${groupLabel}`;
  listPop.append(popHead);
  const rowsByHeadingId = new Map<string, HTMLButtonElement>();
  for (const heading of headings) {
    const row = document.createElement("button");
    row.type = "button";
    row.className = "admin-section-jump-row";
    row.dataset["testid"] = `admin-section-jump-row-${heading.id}`;
    const rowLabel = document.createElement("span");
    rowLabel.textContent = settingsSectionJumpLabelFor(heading);
    const here = document.createElement("span");
    here.className = "admin-section-jump-here";
    here.textContent = "You are here";
    here.hidden = true;
    row.append(rowLabel, here);
    listPop.append(row);
    rowsByHeadingId.set(heading.id, row);
  }
  nav.append(allButton, listPop);

  const onOutsidePointer = (event: Event): void => {
    const target = event.target;
    if (target instanceof Node && (listPop.contains(target) || allButton.contains(target))) return;
    closeList(false);
  };
  const onKey = (event: KeyboardEvent): void => {
    if (event.key === "Escape") closeList(true);
  };
  const closeList = (returnFocus: boolean): void => {
    if (listPop.hidden) return;
    listPop.hidden = true;
    allButton.setAttribute("aria-expanded", "false");
    document.removeEventListener("pointerdown", onOutsidePointer, true);
    document.removeEventListener("keydown", onKey, true);
    if (returnFocus) allButton.focus({ preventScroll: true });
  };
  const openList = (): void => {
    const current = settingsHeadingInView(headings);
    for (const [headingId, row] of rowsByHeadingId) {
      const here = row.querySelector<HTMLElement>(".admin-section-jump-here");
      const isCurrent = headingId === current.id;
      row.setAttribute("aria-current", String(isCurrent));
      if (here !== null) here.hidden = !isCurrent;
    }
    listPop.hidden = false;
    allButton.setAttribute("aria-expanded", "true");
    document.addEventListener("pointerdown", onOutsidePointer, true);
    document.addEventListener("keydown", onKey, true);
    // The strip may sit below the page header and setup checklist rather than
    // at the top of the screen, so the list can open past the bottom edge.
    // Scroll whichever ancestor scrolls (the app shell does, not the window)
    // just enough, instantly and before any jump scroll starts, that the whole
    // list is on screen.
    listPop.scrollIntoView({ block: "nearest", behavior: "auto" });
  };
  settingsSectionListClosers.set(nav, () => closeList(false));
  allButton.addEventListener("click", () => {
    if (listPop.hidden) openList();
    else closeList(true);
  });

  if (existing === null) {
    panel.insertBefore(nav, panel.firstChild);
  }

  let restoringFocus = false;
  for (const heading of headings) {
    const button = buttonsByHeadingId.get(heading.id);
    if (button === undefined) continue;
    button.addEventListener("click", () => {
      scrollSettingsHeadingIntoView(heading);
      updateActiveSettingsSectionJumpButton(buttonsByHeadingId, heading);
    });
    rowsByHeadingId.get(heading.id)?.addEventListener("click", () => {
      scrollSettingsHeadingIntoView(heading);
      updateActiveSettingsSectionJumpButton(buttonsByHeadingId, heading);
      closeList(true);
    });
    // UX36 independent-verification fix: every chip, not only the one a
    // click brought into view, keeps itself inside the strip's own
    // visible window the moment it receives real keyboard focus. See
    // scrollSettingsJumpChipIntoView's own comment for why this reads
    // live geometry and sets scrollLeft directly rather than reusing
    // scrollIntoView.
    // UX38: the "more" arrow takes about 65px of the strip while chips remain to
    // the right, and it is only re-evaluated on a scroll event or resize, a frame
    // late. A key press right after the strip scrolled (Tab straight after the
    // first chip was focused) measured the chip against the stale, wider strip,
    // judged it visible, and left it clipped once the arrow came back. So the
    // arrow is settled synchronously before measuring, and again after the scroll.
    button.addEventListener("focus", () => {
      if (restoringFocus) return;
      refreshMore();
      scrollSettingsJumpChipIntoView(list, button);
      refreshMore();
    });
  }
  if (previousScroll !== undefined) list.scrollLeft = previousScroll;
  refreshMore();
  const replacementFocus = Array.from(buttonsByHeadingId.values()).find(
    (button) => focusedTestId !== undefined && button.dataset["testid"] === focusedTestId,
  );
  if (replacementFocus !== undefined) {
    restoringFocus = true;
    replacementFocus.focus({ preventScroll: true });
    restoringFocus = false;
  }
}

/** SET16-F2 (docs/build-briefs/SET16-F2-teardown-dismisses-open-sheets.md):
 * dismisses every admin sheet still open at teardown through THAT SHEET'S
 * OWN real close function, exactly as if the employee had tapped its own
 * close control, so unlockBodyScroll() runs once per sheet and
 * openSheetCount decrements 1:1 the same way it does on an ordinary
 * dismissal. Never assigns openSheetCount directly: doing so would
 * desync it from the DOM again, the exact bug openSheetCount's own
 * former check:admin-invalidate-coverage allowlist entry existed to
 * name.
 *
 * Scoped to the 12 admin-*-scrim elements that sit inside #admin-screen's
 * own DOM subtree (independently verified while reviewing SET16-F1).
 * #admin-screen is already hidden on both paths that reach invalidate()
 * (leaving the admin screen for tabs, or the user picker shown after a
 * real sign-out), so nothing about THIS employee's own session ever sees
 * a sheet left open. What breaks is the NEXT employee's open: a child
 * element's own `hidden` attribute is independent of its parent's, so a
 * sheet left `hidden = false` here stays exactly that way, and reappears,
 * with whatever it was showing, the instant #admin-screen's own `hidden`
 * is cleared again.
 *
 * The confirm sheet is handled inline rather than through
 * resolveConfirmSheet(): pendingConfirmResolve must never be RESOLVED by
 * a teardown, only dropped, which invalidate() already does elsewhere in
 * its own body. An awaiting showConfirmSheet() caller must simply never
 * settle, not settle as though someone tapped Cancel or Confirm; calling
 * resolveConfirmSheet() here would do exactly that. */
function dismissOpenAdminSheets(): void {
  clearVendorOptionReturn();
  if (!confirmScrim.hidden) {
    confirmScrim.hidden = true;
    unlockBodyScroll();
  }
  if (!drinkFormScrim.hidden) closeDrinkForm();
  if (!bottleFormScrim.hidden) closeBottleForm();
  closeBulkEditSheet();
  if (!terminalRenameScrim.hidden) closeTerminalRenameSheet();
  if (!discountAddScrim.hidden) closeDiscountAddSheet();
  if (!vocabPadScrim.hidden) closeVocabPad();
  if (!inventoryMovementScrim.hidden) closeInventoryMovementSheet();
  closeBulkRecipeSheet();
  if (!countSessionScrim.hidden) pauseCountSession();
  // INVCOUNT2-S2: the table is a full-screen view, not a scrim; leaving the screen closes it, every row already saved.
  closeCountTable();
  if (!countCloseConfirmScrim.hidden) closeCountCloseConfirm();
  if (!countHistoryDetailScrim.hidden) closeCountHistoryDetail();
  // R5, R6, R7: the role dialogs sit inside the two shared backdrops below. Closing them FIRST
  // releases the scroll lock they hold once, before the sheets underneath are closed.
  dismissRolesManagementSheets();
  // The shared backdrop hosts two mutually exclusive sheets (U7); which
  // one is open decides which product close function is the right one,
  // the same test openAddUserSheet/openEditUser themselves use.
  if (!userEditScrim.hidden) {
    if (!addUserSheetEl.hidden) {
      closeAddUserSheet();
    } else {
      closeEditUser();
    }
  }
  // The shared backdrop hosts two mutually exclusive sheets (UX64), the
  // same dispatch userEditScrim's own two-sheet case above uses.
  if (!roleEditScrim.hidden) {
    if (!roleAddSheetEl.hidden) {
      closeAddRoleSheet();
    } else {
      closeRoleEditor();
    }
  }
  if (!pinRevealScrim.hidden) closePinReveal();
  if (!resetPinScrim.hidden) closeResetPinSheet();
  // AU17: the Change history detail sheet lives in its own module, so its close is called here.
  dismissChangeHistorySheet();
  // SEC2-S3: the Tablets sheets live in their own module. One of them holds a setup code on screen.
  tabletsSection.dismissSheets();
  // RCPT1-S7: the printers sheet can hold a pairing code or password on screen.
  printersSection.dismissSheets();
}

/** Forces every section to reload next time it is shown. Called when the
 * admin screen is left (handleAdminBackToTabsClick in main.ts already
 * refreshes the bartender menu on that same path); the next open should
 * never show data a save elsewhere may have changed. */
export function confirmLeavePermitSettings():boolean {return tenantPermitPanel.confirmLeave();}
export function confirmLeaveCatalog():Promise<boolean> {return productCatalogSection.confirmLeave();}

export function invalidate(): void {
  // INVPOLISH1: one employee's count history units never outlive a sign-out or location switch.
  adminCountHistoryContainerTypes = {};
  // ADDITEMDRAFT1: a sign-out writes what is pending and keeps the stored draft; it is offered back at the next sign-in.
  endItemDraftSession();
  // CATALOG1: nothing typed or found in Find a product outlives a sign-out or location switch.
  resetFindProduct();
  // ADDITEM2: the Add item draft never outlives a sign-out or location switch. The math sequence moves forward so a reply
  // still on the wire is ignored, and a pending math timer is cancelled.
  if (addItemMathTimer !== undefined) window.clearTimeout(addItemMathTimer);
  addItemMathTimer = undefined;
  addItemMath = undefined;
  addItemMathKey = "";
  addItemMathSequence = addItemMathSequence + 1;
  addItemTypedPrice = undefined;
  addItemKegWasteInitial = undefined;
  addItemOrderUnitTouched = false;
  addItemUnitPriceTouched = false;
  addItemOrderAs = undefined;
  addItemLocationStates = undefined;
  addItemLocationStatesLoading = false;
  addItemStep3RenderedAsNew = false;
  clearVendorOptionReturn();
  clearVendorSetupView();
  clearF3SellingUi();
  hideHoverHelp();
  inventorySetupState.kind = "";
  vendorOfferCostConfirmedKey = undefined;
  vendorAddFormOpen = false;
  roleEditReadOnly = false;
  recipeCostGeneration = recipeCostGeneration + 1;
  if (recipeCostTimer !== undefined) clearTimeout(recipeCostTimer);
  recipeCostTimer = undefined;
  closeRecipePicker(true);
  recipeInventoryReturn = undefined;
  tenantPermitPanel.reset();
  resetLocationSettingsPanel();
  setupChecklistStepDetails = {};
  systemChecksCard.reset();
  menu86HistoryReading = false;
  countsLandingPast = false;
  // SET16-F4: advance the load generation FIRST, so any loader response
  // that lands after this point (even one already awaiting when this
  // runs) reads loadIsCurrent() as false and discards itself rather than
  // writing a latch, a module binding or the DOM. Written as
  // `loadGeneration = loadGeneration + 1` rather than `+=` so
  // check:admin-invalidate-coverage's own narrow `name = ...` scan
  // recognizes this as the assignment it is; `+=` would read as
  // uncleared to that scan even though it runs every time. NEVER reset to
  // 0: a reset would make a stale in-flight response captured at 0 look
  // current again to whichever session comes next, the exact failure
  // mode countsLoadSequence's own comment documents for its own,
  // narrower counter.
  loadGeneration = loadGeneration + 1;
  bottleCurrentPerOuncePriceCents = null;
  bottleCalculatorSaveSequence = bottleCalculatorSaveSequence + 1;
  // RPT1: the Reports group keeps its own state (the chosen report and
  // range, and every figure drawn); wipe it with the rest.
  resetReports();
  // EXP1-S6: drop the Google access token (held in browser memory only) and the answer about the Google client id.
  resetGoogleSheets();
  // INV31: close the Set starting stock screen and drop everything it holds.
  resetStartingStock();
  // PO10: close the vendor price list screen and drop its preview.
  resetVendorPriceList();
  // INVIMP1: close the Import inventory screen and drop the file and its preview.
  resetInventoryImport();
  // RECIPEAI1: close the Import recipes screen and drop the file and its preview.
  resetRecipeImport();
  // AU17: drop the Change history list and close its detail sheet.
  resetChangeHistory();
  // AU18: drop the Audit log's Activity list.
  resetAuditLog();
  // INVXFER1-S2: drop the transfer screens and anything they hold.
  resetTransfers();
  // TEN11-S5: and the Support access list.
  resetSupportSessions(auditSupportHost);
  // R5, R6, R7: close the role dialogs and drop them.
  resetRolesManagement();
  // SET16-F2: dismiss whatever sheet the previous employee left open
  // FIRST, before anything else below clears the session or state those
  // close paths themselves read (pauseCountSession's own refresh call
  // still needs a valid session()).
  dismissOpenAdminSheets();
  purgeCountSensitiveDisplay();
  drinksLoaded = false;
  vocabulariesLoaded = false;
  vocabulariesLoadFailed = false;
  pourOptionsByItemKindFallback = {};
  itemCategoriesByScopeFallback = {};
  bottlesLoaded = false;
  settingsLoaded = false;
  setSettingsLoadLocked(true);
  countsLoaded = false;
  usersLoaded = false;
  vendorsLoaded = false;
  vendorReadSequence += 1;
  vendorCreateRevision += 1;
  vendorCreatePending = false;
  vendorCreateRecoveryNeeded = false;
  vendorOrderingDrafts.clear();
  // UX120: the next employee sees every vendor collapsed.
  expandedVendorId = undefined;
  clearVendorOptionReturn();
  // PO2, owner ruling 2026-09-29 (DEC-247). Same reasoning as
  // vendorsLoaded immediately above: a shared tablet's next employee must
  // not see a stale threshold value or status line left by the last one.
  purchasingApprovalThresholdLoaded = false;
  purchasingApprovalThresholdInput.value = "";
  purchasingApprovalThresholdStatusEl.textContent = "";
  // VEN7-F1: a sticky failure on the shared vendor status line is this
  // employee's own unread state, not something the next person on a
  // shared tablet should inherit.
  vendorStatusIsError = false;
  vendorStatusErrorKey = undefined;
  // UX26: a collapse choice or a step-state snapshot computed from THIS
  // employee's own permissions must never carry over to the next
  // employee on a shared tablet.
  setupChecklistStepStates = {};
  setupChecklistCollapsed = true;
  // Forces the next Bottles-panel open to re-fetch the tenant's shelf-tier
  // names (ensureShelfTierSettingsLoaded below) rather than reusing a
  // possibly-stale copy from before a Location-settings rename.
  settingsShelfTiers = [];
  bottleFilterShelfTier = "";
  bottleFilterCategory = "";
  bottleFilterKind = "";
  bulkEditSelected = new Set<string>();
  bulkEditShownIds = [];
  // INV26 Slice 1 (DEC-206 item 2): reset to the default sort (Item
  // ascending) on teardown, the same check:admin-invalidate-coverage
  // discipline every other module-level list-view `let` here follows, so
  // a new session does not silently inherit a previous session's sort
  // choice.
  bottleListSort = { column: "item", direction: "asc" };
  // UX80: Stock levels' own list, filter, search and sort, and the operations
  // view frame, go with the rest when the screen is left or the session ends.
  stockLevelItems = [];
  stockLevelsSort = { column: "item", direction: "asc" };
  stockLevelsFilter = "";
  // INVUX1: the item editor's "86 changed" note belongs to the person who was signed in.
  bottleStockChangedThisOpen = false;
  menu86LoadState = "idle";
  menu86Open = undefined;
  menu86Panel.hidden = true;
  panelStockLevels.classList.remove("admin-menu86-open");
  // INVLOC1 Slice 2: the locations and the shown location belong to the person who was signed in.
  planningLocations = [];
  stockLevelsLocationId = undefined;
  bottleStockLoaded = [];
  bottleStockLoadState = "idle";
  bottleStockLoadToken += 1;
  bottleStockTableInputs = new Map();
  bottleStockParHeaderEl = undefined;
  stockLocationHeaderEl.hidden = true;
  stockLocationSwitchEl.innerHTML = "";
  stockNoParNoteEl.hidden = true;
  bottleStockTableEl.innerHTML = "";
  stockLevelsSearchInput.value = "";
  stockLevelsListEl.innerHTML = "";
  priceListPickerListEl.innerHTML = "";
  priceListPickerStatusEl.textContent = "";
  exitOperationsMode();
  drinkFilterCategory = "";
  drinkFilterRequires = "any";
  selectedDrinkIds = new Set();
  // DSC22 (DEC-097): a sort/filter choice is working state for the person
  // currently signed in, not a stored preference; it resets exactly like
  // its sibling Bottles/Drinks filters above.
  discountSortDirection = "name-asc";
  discountFilterMarkdownType = "";
  discountFilterActive = "";
  // SET16: the DATA each latch above guards is cleared here too, beside
  // the latch rather than in a second teardown that has to be kept in
  // step with this one. Clearing a latch alone only guarantees a refetch
  // BEFORE the data is next shown; it leaves the previous visitor's
  // records sitting in module memory until that fetch resolves, which on
  // a shared tablet means the previous EMPLOYEE's records. Every binding
  // below is guarded by a latch reset above, so none of them can be read
  // without a fetch having happened first, which is why emptying them is
  // not a behavior change on the leave-the-screen path.
  adminUsers = [];
  adminTenantRoles = [];
  adminReconciliationCandidates = [];
  adminMenuDrinks = [];
  adminLiquorProducts = [];
  bottleListRequestSequence += 1;
  bottleListReady = false;
  bottleCatalogLoadedLocationId = undefined;
  adminCountSections = [];
  adminCountHistory = [];
  adminCountHistorySummaries = new Map();
  settingsVendors = [];
  settingsTerminals = [];
  settingsVoidReasons = [];
  voidReasonReadSequence += 1;
  voidReasonMutationRevision += 1;
  voidReasonPendingWrites = 0;
  voidReasonListLoaded = false;
  settingsDiscounts = [];
  localDiscountsAllowed = undefined;
  discountPolicyReadSequence = discountPolicyReadSequence + 1;
  aiHelpSection.reset();
  aiBillingPanel.reset();
  billingStatementsCard.reset();
  networkOffers.reset();
  networkRecipeOffers.reset();
  unifiedRecipeOfferRows = [];
  recipeFormSharingSelected = false;
  recipeSharingSavePending = false;
  recipeSharing.reset();
  discountListMutationRevision = discountListMutationRevision + 1;
  discountListRefresh = undefined;
  discountEditorDrafts.clear();
  discountEditorOpen.clear();
  discountAddOpenButton.hidden = true;
  discountBusinessDayTimezone = "UTC";
  discountLocationTimezone = null;
  // CL6-F1-F3: the confirmation state, and the warning and tags it draws.
  businessDayConfirmedAt = null;
  businessDayKnown = false;
  businessDayConfirmedByName = null;
  businessDayStoredTimezone = DEFAULT_TIMEZONE;
  businessDayStoredCutoff = DEFAULT_CUTOFF_TIME;
  businessDayWarningEl.hidden = true;
  businessDayRailTagEl.hidden = true;
  // ONB1: the sales tax confirmation, and the card it draws.
  taxRateConfirmBusy = false;
  taxRateConfirmedAt = null;
  taxRateConfirmedByName = null;
  taxRateConfirmKnown = false;
  settingsBusinessDayTimezoneSearchInput.value = "";
  sessionInactivitySaveStatusEl.textContent = "";
  settingsDiscountReasons = [];
  discountReasonReadSequence += 1;
  discountReasonMutationRevision += 1;
  discountReasonPendingWrites = 0;
  discountReasonListLoaded = false;
  settingsPriceChangeReasons = [];
  settingsPriceChangeFlagInput.value = "";
  settingsPriceChangeFlagStatusEl.textContent = "";
  settingsPriceChangeReasonStatusEl.textContent = "";
  settingsDiscountApprovalPercentSelect.replaceChildren();
  settingsDiscountApprovalAmountSelect.replaceChildren();
  settingsDiscountApprovalStatusEl.textContent = "";
  settingsActionReasons.clear();
  for (const editor of actionReasonEditors) {
    editor.statusEl.textContent = "";
    editor.listEl.innerHTML = "";
    editor.nameInput.value = "";
  }
  settingsRefundReasons = [];
  refundReasonReadSequence += 1;
  refundReasonMutationRevision += 1;
  refundReasonPendingWrites = 0;
  refundReasonListLoaded = false;
  userAiSettings.reset();
  editingUser = undefined;
  // TEN12: the locations the person editor listed belong to the signed-in tenant; drop them with the session.
  userEditLocations = undefined;
  userEditLocationsFailed = false;
  userEditTickedLocations = new Set();
  userEditOriginalLocations = [];
  editingUserPermissions = [];
  editingUserPermissionsLoaded = false;
  editingRole = undefined;
  editingRolePermissions = [];

  // SET16-F1: the 44 bindings SET16's own brief committed to clearing
  // and did not. check:admin-invalidate-coverage
  // (harness/scripts/check-admin-invalidate-coverage.ts) refuses a new
  // module-level `let` in this file that lands here without either an
  // assignment below or an allowlist entry naming why one is not owed,
  // so this cannot rot silently the way it did once already.

  // Tenant-fetched reference data. Unlike the lists above, nothing
  // latches these behind a loaded flag that forces a re-fetch before a
  // screen reads them again, so a stale copy is visible the instant a
  // panel that reads it renders, with no request in between for the
  // next employee's permissions to fail against.
  adminVocabularies = {};
  // INV27: Settings default target markup %, and the per-sheet-open
  // touched flags that stop it from overwriting a manager's own typed
  // value; cleared on teardown like every other tenant-fetched/session
  // scoped state above.
  itemKindTargetMarkupDefaults = {};
  bottleCalcOzTargetMarkupTouched = false;
  bottleCalcBottleTargetMarkupTouched = false;
  // Coordinator finding, re-review 2026-09-29: clearing the DATA above is
  // not enough on its own for this one card. Unlike a vocabulary list
  // (whose next open always re-fetches through loadVocabularies, which
  // repaints from scratch on both its success AND its denial branch --
  // the fix just above), Settings > Inventory can already be ON SCREEN
  // when invalidate() runs (a sign-out from an already-open admin screen,
  // the SET16-F1 handoff shape), and the card's rows are a rendered DOM
  // snapshot, not a live binding to itemKindTargetMarkupDefaults. Clearing
  // the DOM here directly, the same way dismissOpenAdminSheets/
  // purgeCountSensitiveDisplay above handle other on-screen state a data
  // reset alone cannot retroactively erase, closes the gap the denial-
  // branch fix above closes for the NEXT load but not for whatever is
  // already painted right now.
  targetMarkupsListEl.innerHTML = "";
  // ITEM1 Slice 3. The item-category editor's own selected kind is
  // working state for whoever is signed in, the same reasoning
  // bottleFilterCategory/drinkFilterCategory above already carry.
  itemCategoryEditorScope = null;
  inventoryMovementWasteReasons = [];
  inventoryMovementVendors = [];
  bottleVendorOffers = [];
  bottleTermsContext = undefined;
  bottleEffectiveTerms.clear();
  bottleVendorReadState = "unverified";
  bottleVendorReadScope = undefined;
  bottlePreferredTermsByLocation.clear();
  clearBottleVendorAddFields();
  bottleTermsLoadSequence += 1;
  bottleExplicitLocationId = undefined;
  bottlePreferredByLocation = [];
  bottlePreferredDefaultOfferId = null;
  bottlePreferredToken += 1;
  bottlePreferredByLocationEl.innerHTML = "";
  bottlePreferredByLocationEl.hidden = true;
  vendorPlanningLocationCount = 0;

  // Tenant configuration scalars (Money and pricing, Tabs and payment).
  // SET16's own brief committed to clearing these and the code did not;
  // this is that fix. Each resets to its declaration's own initial
  // value, not one this function invents.
  settingsTaxRateBasisPoints = 0;
  taxRateSection.reset();
  // PAY1 Slice 3: this location's card payments state goes with the session.
  cardPaymentsSection.reset();
  payrollSection.reset();
  bankAccountsSection.reset();
  readerTipsSection.reset();
  productCatalogSection.reset();
  catalogPlanningSequence = catalogPlanningSequence + 1;
  catalogPlanningLocationId = undefined;
  catalogScopeHost.replaceChildren();
  idVerifySection.reset();
  receiptSettingSection.reset();
  printersSection.reset();
  cashSettingSection.reset();
  accountingSection.reset();
  payrollSection.reset();
  connectionsState.available = false;
  tabConnections.hidden = true;
  adminTeardownRunning = true;
  try {
    autoParSection.reset();
  } finally {
    adminTeardownRunning = false;
  }
  // SEC2-S3: devices, waiting codes and any code on screen go with the session.
  tabletsSection.reset();
  locationsSection.reset();
  addressLocations = [];
  settingsAddressLocationSelect.replaceChildren();
  settingsAddressLocationRow.hidden = true;
  settingsTipPresets = [];
  settingsHoldAmountCents = null;
  settingsHoldIncrementThresholdBasisPoints = 8000;
  settingsAbandonedTabGratuityBasisPoints = 0;
  settingsAbandonedTabGratuityDisclosureConfirmed = false;
  settingsCloseCardTicketsAtDayEnd = true;
  settingsDualPricingEnabled = false;
  settingsDualPricingBasisPoints = 270;
  settingsTipOnPreDiscountPrice = false;
  settingsRoundingMode = "half-up-cent";
  settingsPricingModel = "per_ounce";
  settingsStandardPourOunces = "";

  // Unsaved-edit snapshots the dirty check compares the live form
  // against. A stale snapshot from the previous employee would make the
  // next employee's first, untouched load of this form register as
  // "dirty" against a comparison value they never set, and
  // pinPolicySnapshot itself carries the previous employee's own PIN
  // min/max length and lockout settings until overwritten.
  settingsFormSnapshot = undefined;
  pinPolicySnapshot = undefined;
  // UX108: the next sign-in restores its own drafts on its first load, and nothing of this one is written late.
  settingsFormDraftPending = true;
  peopleFormDraftPending = true;
  if (settingsDraftSaveTimer !== undefined) {
    clearTimeout(settingsDraftSaveTimer);
  }
  settingsDraftSaveTimer = undefined;

  // A specific user's, drink's or bottle's in-progress edit. None of
  // this -- an id, a name, a component list, a search term -- belongs
  // to the next employee's fresh session.
  resetPinTarget = undefined;
  editingDrinkId = undefined;
  editingDrink = undefined;
  drinkFormComponents = [];
  drinkFormLiquorSlots = new Set();
  // VOC1-F4 (Section 2.2): the next employee's fresh session starts with
  // no in-progress modifier-attachment edit either.
  drinkFormModifiers = new Map();
  existingDrinkComponents = [];
  drinkStoredComponents = [];
  drinkRecipeItems = [];
  drinkRecipeItemsAvailable = true;
  drinkRecipeItemsLoading = false;
  drinkNetworkSource = null;
  editingBottleId = undefined;
  editingBottle = undefined;
  clearCatalogItemSetup();
  bottleFormPourOptions = new Set();
  bottlePourPriceOverrides = undefined;
  // VOC1-F4-F2 (Section 2.3): the next employee's fresh session starts
  // with no in-progress modifier-attachment edit either, mirroring
  // drinkFormModifiers' own identical reset immediately above.
  bottleFormModifiers = new Map();
  bottleModifiersLoaded = false;
  bottleFormItemKind = "liquor";
  // SET20: the two ITEM2-F7 sale-mechanism bindings for this same sheet,
  // reset beside their siblings above and below so a half-picked or
  // never-saved sale mechanism does not survive sign-out on a shared
  // tablet. check:admin-invalidate-coverage flagged both as uncleared.
  bottleFormSaleMechanism = null;
  bottleFormSaleMechanismTouched = false;
  bottleFormSizeUserSet = false;
  bottleFormContainerTypeUserSet = false;
  terminalBeingRenamedId = undefined;
  inactiveUsersCollapsed = true;
  addUserRole = "lounger";
  addUserDisplayNameFollowsFirstName = true;
  reconciliationRowBusyId = undefined;
  inventoryMovementFormType = "waste";
  inventoryMovementSelectedReasonId = undefined;
  inventoryMovementSelectedVendorId = undefined;
  vocabPadEditor = null;
  vocabPadEntry = "";
  editingUserActive = true;

  // A counter physically walking shelves with a device in hand carries
  // that counter's own product list, index and entries in countWalk-
  // State; leaving it set would hand the next employee a mid-count in
  // progress.
  countWalkState = undefined;
  // INVCOUNT2-S2: the same for the table, which carries the previous employee's entries on screen.
  closeCountTable();

  // The section rail's own remembered tab. Left set, initialSectionFor
  // (below) would land the next employee on the previous one's tab
  // whenever that section also happens to be visible to the new
  // session; reset to the declaration's own initial value so a fresh
  // session always starts from openAdminScreen's real
  // first-visible-section logic instead.
  settingsJumpVisit += 1;
  currentSection = ADMIN_SECTION_ORDER[0]!;

  // Live setTimeout handles. Assigning over one without clearing it
  // first would leave the old callback armed to fire later and read
  // whatever is then in the search box; the same defensive shape as
  // handleBottleSearchInput/handleDrinkSearchInput's own re-arm.
  if (bottleSearchDebounceTimer !== undefined) {
    clearTimeout(bottleSearchDebounceTimer);
    bottleSearchDebounceTimer = undefined;
  }
  if (drinkSearchDebounceTimer !== undefined) {
    clearTimeout(drinkSearchDebounceTimer);
    drinkSearchDebounceTimer = undefined;
  }

  // A confirm sheet's own pending answer. The sheet it belongs to sits
  // inside #admin-screen, which is hidden on both paths that reach this
  // function (leaving the admin screen for tabs, or the user picker
  // shown after a real sign-out), so nothing lets a new session tap it;
  // this only drops the dangling resolver so it cannot later fire
  // against a target the next employee never chose.
  pendingConfirmResolve = undefined;

  // DOCSBUG4: the open item's Recent movements, the on-hand figure the
  // reverse confirm reads, and the in-flight guard are the last employee's
  // own; the next one on a shared tablet starts from nothing.
  bottleOnHandSnapshot = undefined;
  bottleMovements = [];
  bottleStockHistory = [];
  bottleMovementsShowAll = false;
  bottleMovementActorNames = new Map();
  bottleMovementActorsLoaded = false;
  bottleFreshReversalIds = new Set();
  reverseInFlight = false;

  // QA finding, post-verification review: pourVolumeToggleQueue and
  // pourVolumeTogglesPending (declared with handleItemKindPourVolumeToggle
  // above) are `const` Maps, not `let` bindings, so check:admin-
  // invalidate-coverage's own narrow `let`-only scan neither requires nor
  // sees this clearing -- the same documented exemption vocabAmounts/
  // vocabUnits already carry, and the same shape that gate's own header
  // comment states explicitly ("any module-level `const`... is not what
  // 'invalidate() forgot to reset' describes"). Cleared here anyway,
  // MANUALLY, because unlike vocabAmounts/vocabUnits (which only ever
  // hold this employee's own uncommitted pad state, already meaningless
  // once the pad itself is gone), a queued or in-flight toggle keeps a
  // checkbox genuinely DISABLED through pourVolumeTogglesPending's own
  // membership check in appendItemKindPourVolumesPicker's paint() -- if a
  // toggle is queued when the employee signs out, its own repaint()
  // (which the finally block in handleItemKindPourVolumeToggle above now
  // guards behind currentSession being present, so it never fires against
  // a session that no longer exists) would otherwise never run to clear
  // that entry, leaving the checkbox permanently disabled for the NEXT
  // employee on this shared tablet, on a row that employee did not touch.
  // A leftover entry here cannot leak this employee's own selections
  // either way: the next session's own paint() reads straight from
  // adminVocabularies' own freshly fetched assignedPourVolumeValues for
  // its checked state, never from these two maps.
  pourVolumeToggleQueue.clear();
  pourVolumeTogglesPending.clear();

  // The permission snapshot the admin screen resolves its own gates
  // against. openAdminScreen overwrites it on the next open, so the
  // window is short, but "short window in which a permission check
  // answers for the previous employee" is not a property worth keeping.
  currentSession = undefined;
  discountFieldErrorSeq = 0;
}

// ---------------------------------------------------------------------
// SET2: settings search. Owner requirement, stated twice: "the search
// function will update the result set with every keystroke," matching
// "tier" to Shelf tiers in Money and pricing and "gratuity" to the
// abandoned tab gratuity in Tabs and payment without the operator
// knowing which group either lives in -- the whole point of search
// here. Covers every setting that actually has a home on this screen
// (built fields plus the six SET1 hard-requirement-2 editors); it does
// NOT list the still-unbuilt items the published settings tree also
// names (PID length, Purchasing, Network and billing, etc.), since a
// result for those would claim a control exists where none does.
// ---------------------------------------------------------------------

interface SettingsSearchEntry {
  group: AdminSection;
  label: string;
  description: string;
  /** Scrolled into view and briefly flashed when this result is picked.
   * Omitted for a few entries with no single field to point at (a
   * per-row toggle across many discounts, for instance); picking those
   * still switches to the right group. */
  anchorSelector?: string;
}

const GROUP_LABELS: Record<AdminSection, string> = {
  money: "Money and pricing",
  tabsPayment: "Tabs and payment",
  discounts: "Discounts",
  corrections: "Reasons",
  recipes: "Recipes",
  menu: "Inventory Setup",
  inventory: "Inventory items",
  purchasing: "Vendors",
  people: "Team",
  operations: "Location",
  tablets: "Devices",
  auditLog: "Audit log",
  network: "Billing",
  connections: "Connections",
};

const SETTINGS_SEARCH_INDEX: readonly SettingsSearchEntry[] = [
  { group: "inventory", label: "Vendor Options setup", description: "Review all locations and deliberately use separate Vendor SKUs and purchase costs for each location.", anchorSelector: "#admin-vendor-options-setup" },
  {
    group: "money",
    label: "Tax rate",
    description: "Sales tax applied to the ticket.",
    anchorSelector: "#admin-tax-rate-section",
  },
  {
    group: "money",
    label: "Tip presets",
    description: "The tip percentage choices a guest sees at Pay.",
    anchorSelector: "#admin-settings-tip-presets",
  },
  {
    group: "money",
    label: "Suggest tips on the price before discounts",
    description: "Whether the card reader suggests tips on the full price of the drinks.",
    anchorSelector: "#admin-settings-tip-on-pre-discount",
  },
  {
    group: "money",
    label: "Rounding",
    description: "How a line's price rounds to the cent. One mode is supported today.",
    anchorSelector: "#admin-settings-rounding-mode",
  },
  {
    group: "money",
    label: "Pricing model",
    description: "Whether a pour is priced per ounce or by shelf tier.",
    anchorSelector: "#admin-settings-pricing-model",
  },
  {
    group: "money",
    label: "Shelf tiers",
    description:
      "Tier names and prices. Edited here; read in the bottle form, Inventory filter chips, and Discounts scope.",
    anchorSelector: "#admin-settings-shelf-tiers",
  },
  {
    group: "tabsPayment",
    label: "Hold amount",
    description: "What a guest's card is held for when they keep a tab open.",
    anchorSelector: "#admin-settings-hold-amount",
  },
  {
    group: "tabsPayment",
    label: "Hold increment threshold",
    description: "The percentage of the hold at which it is raised.",
    anchorSelector: "#admin-settings-hold-increment-threshold",
  },
  {
    group: "tabsPayment",
    label: "Abandoned tab gratuity",
    description: "An automatic tip charged to a tab nobody closed.",
    anchorSelector: "#admin-settings-abandoned-tab-gratuity",
  },
  {
    group: "tabsPayment",
    label: "Signage posted for auto-gratuity",
    description: "Gates the abandoned tab gratuity; it cannot go above zero without this.",
    anchorSelector: "#admin-settings-abandoned-tab-gratuity-disclosure",
  },
  {
    group: "tablets",
    label: "Card readers",
    description: "Card readers this location can charge to.",
    anchorSelector: "#admin-settings-terminals",
  },
  {
    group: "tablets",
    label: "Receipt printers",
    description: "Receipt printers this location prints to.",
    anchorSelector: "#admin-printers-section",
  },
  {
    group: "discounts",
    label: "Discounts",
    description: "The discounts a bartender can put on a ticket or a single drink.",
    anchorSelector: "#admin-settings-discounts",
  },
  {
    group: "discounts",
    label: "Discount scope",
    description: "Which shelf tiers, categories, or kinds of line a discount can touch.",
    anchorSelector: "#admin-settings-discounts",
  },
  {
    group: "discounts",
    label: "Auto-apply on membership",
    description: "Applies to a member's ticket automatically, with no bartender tap.",
    anchorSelector: "#admin-settings-discounts",
  },
  {
    group: "discounts",
    label: "Member daily comp",
    description: "Marks the one bundled daily-drink discount. Read only.",
    anchorSelector: "#admin-settings-discounts",
  },
  {
    group: "discounts",
    label: "Closes the ticket",
    description: "True only for Walkout: stops a comp from silently destroying tips. Read only.",
    anchorSelector: "#admin-settings-discounts",
  },
  {
    group: "corrections",
    label: "Void reasons",
    description: "Why a drink came off a ticket.",
    anchorSelector: "#admin-settings-void-reasons",
  },
  {
    group: "corrections",
    label: "Voiding a prepared drink",
    // B1, owner ruling 2026-08-29: a Lounger may void with a manager's
    // PIN. Two sentences max, matching the dashboard-description rule:
    // what it does, what to do about it, no provenance and no method.
    description:
      "A Lounger can void a prepared drink with a manager's PIN. A reason from the list is always required.",
    anchorSelector: "#admin-settings-void-reasons",
  },
  {
    group: "corrections",
    label: "Discount reasons",
    description: "Why the price dropped. A separate list from void reasons on purpose.",
    anchorSelector: "#admin-settings-discount-reasons",
  },
  {
    group: "corrections",
    label: "Refund reasons",
    // REF1, DEC-011 (2026-08-29): a third configurable reason list, why
    // money went back. Two sentences max, matching the dashboard-
    // description rule.
    description:
      "Why money was refunded. A separate list from void and discount reasons on purpose.",
    anchorSelector: "#admin-settings-refund-reasons",
  },
  {
    // SET10 (DEC-130 ruling (d)): Drinks moved off the Menu tab onto its
    // own Recipes tab. anchorSelector is unchanged: #admin-drink-list's
    // own id did not move.
    group: "recipes",
    label: "Drinks",
    description: "Turn drinks on or off for the season, or add and edit one.",
    anchorSelector: "#admin-drink-list",
  },
  // SET10-F1 (docs/build-briefs/SET10-F1-settings-search-finds-every-
  // vocabulary-editor.md): one entry per vocabulary editor left in Menu
  // once SET10 moved Drinks onto Recipes. SETTINGS_SEARCH_INDEX carried
  // zero group: "menu" entries before this, so a manager typing "Pour
  // sizes" or "Liquor category" got zero results for controls that were
  // already live and in daily use. Eleven editors today, not the eight
  // the brief named: ITEM1 Slice 2 (item-kind), Slice 3 (item-category)
  // and Slice 4 (cider-pour-size) all landed after the brief was
  // committed; the first two are named by the brief's own "What happens
  // when item-kind lands" section. Every
  // label is the editor's own current heading text verbatim
  // (harness/ui/index.html), so a search result never claims a name the
  // screen does not show. item-category's own anchorSelector reads
  // #admin-item-category-list, not #admin-vocab-item-category-list: its
  // markup gives the list itself a plain id, unlike every other editor
  // here, confirmed by reading index.html rather than assumed from this
  // array's own naming convention.
  {
    group: "inventory",
    label: "Item kinds",
    description:
      "What an inventory item is: Liquor, Beer, Wine, Cider, or a kind the list does not cover yet. Turning one off takes it off new items and leaves existing ones readable.",
    anchorSelector: "#admin-vocab-item-kind-list",
  },
  {
    group: "inventory",
    label: "Item categories",
    description: "",
    anchorSelector: "#admin-item-category-list",
  },
  {
    group: "inventory",
    label: "Container types",
    description:
      "How a bottle arrives: bottle, can, keg, or a container the list does not cover yet.",
    anchorSelector: "#admin-vocab-container-type-list",
  },
  // ITEM2 Slice 5 (DEC-166; coordinator instruction): replaces the Pour
  // sizes/Wine pour sizes/Beer pour sizes/Cider pour sizes entries that
  // used to sit here, liquor's own legacy entry included. One
  // location-wide list now covers every per-ounce kind, liquor included;
  // which volumes a kind pours is assigned on that kind's own row in the
  // Item kinds editor above, not a separate searchable screen of its own.
  {
    group: "inventory",
    label: "Pour volumes",
    description:
      "Serving volumes for Wine, Beer, Cider and other per-ounce kinds. Straight liquor uses Standard pour.",
    anchorSelector: "#admin-vocab-pour-volume-list",
  },
  {
    group: "inventory",
    label: "Liquor bottle sizes",
    description:
      "The bottle sizes offered on the Inventory item editor's Size field for a liquor item, such as 375ml or 750ml.",
    anchorSelector: "#admin-vocab-liquor-bottle-size-list",
  },
  {
    group: "inventory",
    label: "Draft and whole-unit serving sizes",
    description:
      "The sizes offered on the Inventory item editor's Size field for a draft beer or a packaged bottled or canned item, such as 16oz.",
    anchorSelector: "#admin-vocab-serving-size-list",
  },
  {
    group: "recipes",
    label: "Drink menu categories",
    description:
      "How drinks are grouped on the floor menu. Presentation only; does not change what a drink is made of.",
    anchorSelector: "#admin-vocab-drink-display-category-list",
  },
  // VOC1-F4 (docs/build-briefs/VOC1-F4-drink-modifier-catalog.md, Section
  // 2.1).
  {
    group: "inventory",
    label: "Ingredients",
    description: "Inventory items",
    anchorSelector: "#admin-bottle-list",
  },
  {
    group: "inventory",
    label: "Items you stock",
    description:
      "Liquor, wine, beer and cider: pricing, shelf tier, par level, reorder threshold.",
    anchorSelector: "#admin-bottle-list",
  },
  {
    group: "inventory",
    label: "Count areas",
    description: "Ordering for a count session; never a separate stock pool.",
    anchorSelector: "#admin-counts-sections-list",
  },
  {
    group: "people",
    label: "People",
    description: "Add people to the team (users, staff), assign roles and locations, set permission overrides.",
    anchorSelector: "#admin-user-list",
  },
  {
    group: "people",
    label: "Reset PIN and unlock account",
    description: "Per-employee action; managers reset and never view a PIN.",
    anchorSelector: "#admin-user-list",
  },
  {
    group: "operations",
    label: "Business day cutoff",
    description: "The local time the business day rolls over. Governs every closeout.",
    anchorSelector: "#admin-settings-business-day-cutoff",
  },
  {
    group: "operations",
    label: "Locations",
    description: "Add a location to this business.",
    anchorSelector: "#admin-locations-section",
  },
  {
    group: "operations",
    label: "Business day time zone",
    description: "What the business day cutoff time is measured against.",
    anchorSelector: "#admin-settings-business-day-timezone",
  },
  {
    group: "tablets",
    label: "Add a device",
    description: "Make a setup code that ties a device to one location.",
    anchorSelector: '[data-testid="tablets-add-button"]',
  },
  {
    group: "tablets",
    label: "Revoke a device",
    description: "Disconnect a device and sign out anyone using it.",
  },
  {
    group: "people",
    label: "Shortest PIN allowed",
    // UX22/DEC-137: this used to say "Four is the floor and is not
    // configurable below that," which states the floor but never the
    // ceiling -- the same incomplete claim this item exists to fix, in a
    // different UI element from the field's own description above it
    // (slice 1). Both numbers now come from the same imported
    // settingsBounds.ts constants the validator and the field's own
    // description enforce and state.
    description: `The fewest digits a staff PIN may have. Must be ${boundRangeText(PIN_LENGTH_MIN, PIN_LENGTH_MAX, "digits")}, and no longer than the longest PIN allowed.`,
    anchorSelector: "#admin-settings-pin-min-length",
  },
  {
    group: "people",
    label: "Longest PIN allowed",
    description: `The most digits a staff PIN may have. Must be ${boundRangeText(PIN_LENGTH_MIN, PIN_LENGTH_MAX, "digits")}, and no shorter than the shortest PIN allowed. A generated PIN is minted at this length.`,
    anchorSelector: "#admin-settings-pin-max-length",
  },
  {
    group: "people",
    label: "Wrong PINs before the account locks",
    description: `How many consecutive incorrect PIN attempts lock someone out of sign in. Must be ${boundRangeText(PIN_LOCKOUT_ATTEMPTS_MIN, PIN_LOCKOUT_ATTEMPTS_MAX, "attempts")}.`,
    anchorSelector: "#admin-settings-pin-lockout-attempts",
  },
  {
    group: "people",
    label: "How long the PIN lock lasts",
    description: `Minutes an account stays locked after too many wrong PINs. Must be ${boundRangeText(PIN_LOCKOUT_MINUTES_MIN, PIN_LOCKOUT_MINUTES_MAX, "minutes")}. A manager can always unlock immediately.`,
    anchorSelector: "#admin-settings-pin-lockout-minutes",
  },
  {
    group: "people",
    label: "Session inactivity timeout",
    description: `Minutes of inactivity before a signed-in session is signed out. Must be ${boundRangeText(MIN_SESSION_INACTIVITY_MINUTES, MAX_SESSION_INACTIVITY_MINUTES, "minutes")}.`,
    anchorSelector: "#admin-settings-session-inactivity",
  },
];

/** Reverts to normal group browsing: clears the box, hides the results
 * overlay, and un-does the "hide every group panel" CSS class the
 * overlay used while it was showing. Called whenever a rail button is
 * clicked and when the admin screen (re)opens, so a stale search never
 * survives a navigation away from it. */
function clearSettingsSearch(): void {
  railSearchInput.value = "";
  railSearchResultsEl.hidden = true;
  railSearchResultsEl.innerHTML = "";
  railSearchCountEl.textContent = "";
  settingsGroupsEl.classList.remove("settings-search-active");
}

/** SET2's hard requirement: recomputed on every keystroke (the "input"
 * listener below fires for typing, backspace, and paste alike; there is
 * no debounce and no submit step). Matches a setting's label OR its
 * description, so a manager typing "tip" finds the abandoned tab
 * gratuity by its description even though the word "tip" is not in its
 * label. */
function renderSettingsSearch(): void {
  const rawQuery = railSearchInput.value.trim();
  const query = rawQuery.toLowerCase();
  if (query.length === 0) {
    railSearchResultsEl.hidden = true;
    railSearchResultsEl.innerHTML = "";
    railSearchCountEl.textContent = "";
    settingsGroupsEl.classList.remove("settings-search-active");
    return;
  }
  const matches = SETTINGS_SEARCH_INDEX.filter(
    (entry) =>
      (entry.anchorSelector !== "#admin-vendor-options-setup" || (sessionOrUndefined() !== undefined && hasPermission(sessionOrUndefined()!, "inv.vendor_offer.manage"))) &&
      (entry.label.toLowerCase().includes(query) || entry.description.toLowerCase().includes(query)),
  );
  settingsGroupsEl.classList.add("settings-search-active");
  railSearchResultsEl.hidden = false;
  railSearchResultsEl.innerHTML = "";
  railSearchCountEl.textContent =
    matches.length === 0
      ? "0 results"
      : `${matches.length} result${matches.length === 1 ? "" : "s"}`;
  if (matches.length === 0) {
    const empty = document.createElement("p");
    empty.className = "settings-search-empty";
    empty.dataset["testid"] = "admin-settings-search-empty";
    empty.textContent = `No settings match "${rawQuery}".`;
    railSearchResultsEl.append(empty);
    return;
  }
  for (const entry of matches) {
    const result = document.createElement("button");
    result.type = "button";
    result.className = "settings-search-result";
    result.dataset["testid"] = "admin-settings-search-result";
    const groupTag = document.createElement("span");
    groupTag.className = "settings-search-result-group";
    groupTag.textContent = GROUP_LABELS[entry.group];
    const label = document.createElement("span");
    label.className = "settings-search-result-label";
    label.textContent = entry.label;
    const description = document.createElement("p");
    description.className = "settings-search-result-description";
    description.textContent = entry.description;
    result.append(groupTag, label, description);
    result.addEventListener("click", () => void goToSettingsSearchResult(entry));
    railSearchResultsEl.append(result);
  }
}

/** Switches to the result's group and, when it names one, scrolls the
 * actual field into view and flashes it briefly so the operator's eye
 * finds it against the rest of the panel. */
async function goToSettingsSearchResult(entry: SettingsSearchEntry): Promise<void> {
  clearSettingsSearch();
  if (!(await showSection(entry.group))) return;
  revealInventorySetupTarget(entry.anchorSelector);
  if (entry.anchorSelector === undefined) {
    return;
  }
  const target = document.querySelector<HTMLElement>(entry.anchorSelector);
  if (target === null) {
    return;
  }
  target.scrollIntoView({ block: "center", behavior: "smooth" });
  // UX103: content above the target (the setup checklist, lists still drawing) can change height while the smooth
  // scroll runs, which leaves the target below the fold (seen as a flaky e2e/ux34 run once the Menu lists were
  // shorter). Once the scroll has settled, center it again if it is out of view.
  setTimeout(() => {
    const rect = target.getBoundingClientRect();
    if (rect.bottom < 0 || rect.top > window.innerHeight) {
      target.scrollIntoView({ block: "center" });
    }
  }, 1000);
  target.classList.add("settings-search-target-flash");
  setTimeout(() => target.classList.remove("settings-search-target-flash"), 1500);
}

/** UX33: a shared, unconditional cross-tab dependency line. Renders one
 * link that jumps, through the existing settings-search mechanism, to the
 * vocabulary a picker on this screen depends on. Always shown: every
 * vocabulary this is wired to is seeded for every tenant and its last
 * active value can never be retired, so the "nothing to depend on" state
 * this line would otherwise need to hide for is unreachable (UX33 brief,
 * Section 1.3). Called once per site at module init against a static,
 * purpose-built container, never re-rendered per showSection call. */
interface CrossTabDependencyLineOptions {
  container: HTMLElement;
  linkId: string;
  linkText: string;
  anchorSelector: string;
  itemCategoryScope?: string;
}

function renderCrossTabDependencyLine(options: CrossTabDependencyLineOptions): void {
  const paragraph = document.createElement("p");
  paragraph.className = "admin-field-help";
  const link = document.createElement("button");
  link.type = "button";
  // UX105: a 44px tap row (the sentence link was 22px high), worded as the place it goes.
  link.className = "admin-inline-link admin-inline-link--tap";
  link.id = options.linkId;
  link.dataset["testid"] = options.linkId;
  link.textContent = options.linkText;
  link.addEventListener("click", () => {
    const entry = SETTINGS_SEARCH_INDEX.find(
      (candidate) => candidate.anchorSelector === options.anchorSelector,
    );
    if (entry !== undefined) {
      if (options.itemCategoryScope !== undefined) {
        itemCategoryEditorScope = options.itemCategoryScope;
      }
      void goToSettingsSearchResult(entry);
    }
  });
  paragraph.replaceChildren(link);
  options.container.replaceChildren(paragraph);
}

renderCrossTabDependencyLine({
  container: drinkLiquorCategoriesLineEl,
  linkId: "admin-drink-liquor-categories-link",
  linkText: "Manage item categories",
  anchorSelector: "#admin-item-category-list",
  itemCategoryScope: "liquor",
});

renderCrossTabDependencyLine({
  container: drinkCategoryLineEl,
  linkId: "admin-drink-category-link",
  linkText: "Manage drink categories",
  anchorSelector: "#admin-vocab-drink-display-category-list",
});

// VOC1-F4 (docs/build-briefs/VOC1-F4-drink-modifier-catalog.md, Section
// 2.2).
renderCrossTabDependencyLine({
  container: drinkModifiersLineEl,
  linkId: "admin-drink-modifiers-link",
  linkText: "Inventory items",
  anchorSelector: "#admin-bottle-list",
});

// ---------------------------------------------------------------------
// UX26 (docs/build-briefs/UX26-set-up-this-location-checklist.md): the
// "Set up this location" checklist card, additive above the rail.
// ---------------------------------------------------------------------

export type SetupChecklistStepId =
  | "itemKinds"
  | "pourVolumes"
  | "liquorCategories"
  | "bottles"
  | "startingStock"
  | "recipes"
  | "drinkCategories"
  | "pricing"
  | "memberPrice"
  | "taxRate"
  | "businessDay"
  | "holdAmount"
  | "terminals"
  | "devices"
  | "users"
  | "vendors";

export type SetupChecklistStepState = "done" | "pending" | "unknown";
/** UX118: what a row paints. "loading" is a step the load has not answered yet; it is never a stored state. */
export type SetupChecklistRowState = SetupChecklistStepState | "loading";

interface SetupChecklistStepDef {
  id: SetupChecklistStepId;
  title: string;
  group: AdminSection;
  anchorSelector: string;
  /** ONB2 S3: a step whose destination is the whole tab, with no field of its own in the search index. */
  opensSection?: boolean;
}

// Section 2.5: each step's own destination is looked up from
// SETTINGS_SEARCH_INDEX by (group, anchorSelector) pair, never a freshly
// hand-typed duplicate of the same literal. Both values here must match
// an existing SETTINGS_SEARCH_INDEX entry exactly (confirmed this
// session, Section 1.2's own table).
const SETUP_CHECKLIST_STEPS: readonly SetupChecklistStepDef[] = [
  {
    id: "itemKinds",
    title: "Item kinds and categories",
    group: "inventory",
    anchorSelector: "#admin-vocab-item-kind-list",
  },
  {
    id: "pourVolumes",
    title: "Standard pour",
    group: "money",
    anchorSelector: "#admin-settings-standard-pour-ounces",
  },
  {
    id: "liquorCategories",
    title: "Item categories and bottle sizes",
    group: "inventory",
    anchorSelector: "#admin-item-category-list",
  },
  {
    id: "bottles",
    title: "Items you stock",
    group: "inventory",
    anchorSelector: "#admin-bottle-list",
  },
  // INV31 (DEC-288): optional and never blocking; "This location starts empty"
  // on the screen marks it done.
  {
    id: "startingStock",
    title: "Starting stock",
    group: "inventory",
    anchorSelector: "#admin-starting-stock-open",
  },
  {
    id: "recipes",
    title: "Recipes",
    group: "recipes",
    anchorSelector: "#admin-drink-list",
  },
  {
    id: "drinkCategories",
    title: "Drink menu categories",
    group: "recipes",
    anchorSelector: "#admin-vocab-drink-display-category-list",
  },
  {
    id: "pricing",
    title: "Pricing: tax, tips, rounding, pricing model, Standard Pour, shelf tiers",
    group: "money",
    anchorSelector: "#admin-tax-rate-section",
  },
  // MEMBERPRICE1 (DEC-595): required. A new location starts with the Member Shelf at $0.00 (DEC-589), which gives members
  // no free drink, so the step stays To do until an active Member tier has a price above $0.00.
  {
    id: "memberPrice",
    title: "Set the Member Shelf price",
    group: "money",
    anchorSelector: "#admin-settings-shelf-tiers",
  },
  // ONB2 S5 (QA 2026-10-11 item 15): the card hold amount. A new location starts at $25.00 (database default, migration 0020);
  // the step shows the amount so a manager sees it, and reads To do for a location that has none.
  {
    id: "holdAmount",
    title: "Card hold amount",
    group: "tabsPayment",
    anchorSelector: "#admin-settings-hold-amount",
  },
  // ONB1: live card payments also wait for the sales tax rate to be confirmed or recorded.
  {
    id: "taxRate",
    title: "Sales tax rate, confirmed",
    group: "money",
    anchorSelector: "#admin-tax-rate-section",
  },
  // CL6-F1-F3 (DEC-286): live card payments stay off until the time zone and
  // cutoff are confirmed, so this is a step of its own. Its destination is the
  // Location tab's time zone field.
  {
    id: "businessDay",
    title: "Time zone and cutoff, confirmed",
    group: "operations",
    anchorSelector: "#admin-settings-business-day-timezone",
  },
  {
    id: "terminals",
    title: "Card readers",
    group: "tablets",
    anchorSelector: "#admin-settings-terminals",
  },
  // ONB2 S3 (QA 2026-10-11 item 12): tablets are named and connected from Devices.
  {
    id: "devices",
    title: "Devices",
    group: "tablets",
    anchorSelector: '[data-testid="tablets-add-button"]',
  },
  {
    id: "users",
    title: "Staff, roles and PINs",
    group: "people",
    anchorSelector: "#admin-user-list",
  },
  // ONB2 S3: the Vendors tab has no field in the settings search index, so this step opens the tab.
  {
    id: "vendors",
    title: "Vendors",
    group: "purchasing",
    anchorSelector: "#admin-purchasing-vendors-section",
    opensSection: true,
  },
];

/** Section 2.3: called from openAdminScreen, never from showSection's own
 * text (see that function's own comment for why: check:loader-
 * permission-coupling's rule (f) would otherwise refuse a wrapper whose
 * own body resolves no permission because it only delegates to other
 * load-prefixed or ensure-prefixed functions, which is exactly this
 * function's shape). Each of the nine reads independently checks its own
 * permission BEFORE calling the underlying route, so a session lacking
 * that permission never fires that one fetch and that step's state is
 * recorded "unknown" rather than a false "pending".
 *
 * Fetches through the SAME six routes the existing loaders use, but calls
 * the api.ts routes DIRECTLY rather than the loadVocabularies/loadBottles/
 * loadDrinks/loadSettings/loadTerminals/loadUsers wrappers themselves, and
 * writes the results into LOCAL variables here rather than the shared
 * adminVocabularies/adminLiquorProducts/adminMenuDrinks/settingsTipPresets/
 * settingsTerminals/adminUsers module state. This departs from Section
 * 2.3's own original design (reuse the wrappers so a tab visited later
 * never refetches), found wrong by testing rather than by reading: the
 * six wrappers are not simply idempotent data fetches, several are
 * dual-purpose -- showSection's own "inventory" branch bundles
 * renderBottleFilterChips() with loadBottles() behind the SAME
 * `!bottlesLoaded` gate, "recipes" bundles renderDrinkFilterChips() with
 * loadDrinks() behind `!drinksLoaded`, and "people" bundles
 * loadTenantRoles() with loadUsers() behind `!usersLoaded`. Calling the
 * wrapper from here flips that shared latch true in the background
 * before the tab is ever really opened, so the tab's own later, real
 * visit sees its "first open" gate already false and SKIPS the paired
 * render/fetch entirely -- reproduced live: e2e/admin.spec.ts's "the
 * discount scope picker labels shelves..." test failed consistently with
 * the wrapper-reuse design (`.admin-chip-option` never rendered inside
 * `#admin-bottle-filter-shelf-tier` at all after visiting Inventory) and
 * passed cleanly once this function stopped calling the wrappers, proven
 * by reverting ui/src/admin.ts and ui/index.html to `git show HEAD:...`
 * and confirming the SAME test also fails there for a DIFFERENT, older,
 * unrelated reason (see Section 4's own negative-control record for the
 * pre-existing scrollIntoView finding); the wrapper-reuse-specific
 * failure is what stopped once this function fetched independently.
 * Fetching independently costs an extra round trip when a tab is visited
 * later in the same session; it never risks silently skipping another
 * tab's own one-time setup. */
async function loadSetupChecklist(activeSession: Session): Promise<void> {
  // SET16-F4's own guard, applied here too and more thoroughly than a
  // single-fetch loader needs: captured before this function's own first
  // await, and re-checked after EVERY one of the six fetches below, not
  // only once at the end. A single end-of-function check is not enough
  // here specifically: `noteResponse` (ui/src/api.ts) reacts to ANY 401
  // from ANY caller by raising session-lost and running invalidate()
  // SYNCHRONOUSLY, before that response's own await ever resumes -- so a
  // 401 on this function's OWN third or fourth fetch (the session dying
  // mid-chain, not from a teardown that happened elsewhere) advances
  // loadGeneration itself, and the checks below stop the remaining
  // fetches from firing against a session that is already gone, rather
  // than blindly continuing through all six. Found live: e2e/set16-f4-
  // invalidated-load-is-discarded.spec.ts's own retry test hung
  // (Playwright's own click on a tab that had already been torn down by
  // one of THIS function's later, unguarded fetches, not by the test's
  // own deliberate retry) until every await below got its own check.
  // Every intermediate result (vocabularies, tipPresets, liquorProducts,
  // menuDrinks, terminals, users) is a plain local variable, never shared
  // module state, so nothing observable happens until the FINAL write at
  // the bottom of this function either way; these earlier checks exist to
  // stop firing MORE requests, not to protect a write.
  const gen = loadGeneration;
  const nextStates: Partial<Record<SetupChecklistStepId, SetupChecklistStepState>> = {};
  const { apiBaseUrl, tenantId } = activeSession;

  const tenantSettingsPermitted = hasPermission(activeSession, "admin.tenant_settings.view");
  let vocabularies: typeof adminVocabularies = {};
  let tipPresets: number[] = [];
  // CL6-F1-F3: undefined until the settings read answers, so an unreadable
  // answer leaves the step "unknown", never a false pending or done.
  let businessDayConfirmed: boolean | undefined;
  // ONB2 S5: undefined until the settings read answers; null is a location with no hold amount at all.
  let holdAmountCents: number | null | undefined;
  // ONB1: undefined until the settings read answers, like the time zone above.
  let taxRateConfirmed: boolean | undefined;
  let checklistStandardPour: string | undefined;
  let checklistPricingModel: string | undefined;
  let checklistShelfTiers: ShelfTierSettingDto[] = [];
  if (tenantSettingsPermitted) {
    const vocabResult = await getVocabularies(apiBaseUrl, tenantId);
    if (!loadIsCurrent(gen)) return;
    if (vocabResult.status === 200) {
      vocabularies = vocabResult.body.vocabularies;
    }
    const settingsResult = await getSettings(apiBaseUrl, tenantId);
    if (!loadIsCurrent(gen)) return;
    if (settingsResult.status === 200) {
      tipPresets = [...settingsResult.body.settings.tipPresetsBasisPoints];
      businessDayConfirmed = settingsResult.body.settings.businessDayConfirmedAt !== null;
      holdAmountCents = settingsResult.body.settings.holdAmountCents;
      taxRateConfirmed = settingsResult.body.settings.taxRateConfirmedAt !== null;
      checklistStandardPour = settingsResult.body.settings.standardPourOunces;
      checklistPricingModel = settingsResult.body.settings.pricingModel;
      checklistShelfTiers = settingsResult.body.settings.shelfTiers;
    }
  }

  const liquorProductPermitted = hasPermission(activeSession, "admin.liquor_product.view");
  let liquorProducts: LiquorProductDto[] = [];
  if (liquorProductPermitted) {
    const result = await listLiquorProducts(apiBaseUrl, tenantId, { activeOnly: false });
    if (!loadIsCurrent(gen)) return;
    if (result.status === 200) {
      liquorProducts = result.body.liquorProducts;
    }
  }

  const menuDrinkPermitted = hasPermission(activeSession, "admin.menu_drink.view");
  let menuDrinks: MenuDrinkListItemDto[] = [];
  if (menuDrinkPermitted) {
    const result = await listMenuDrinks(apiBaseUrl, tenantId, { activeOnly: false });
    if (!loadIsCurrent(gen)) return;
    if (result.status === 200) {
      menuDrinks = result.body.menuDrinks;
    }
  }

  // Section 1.4's own named finding: the ROUTE's own permission
  // (admin.terminal.view), never the tabsPayment tab's coincidental
  // admin.tenant_settings.view gate that loadSettings' own nested
  // loadTerminals() call today relies on.
  const terminalPermitted = hasPermission(activeSession, "admin.terminal.view");
  let terminals: TerminalDto[] = [];
  if (terminalPermitted) {
    const result = await listTerminals(apiBaseUrl, tenantId);
    if (!loadIsCurrent(gen)) return;
    if (result.status === 200) {
      terminals = result.body.terminals;
    }
  }

  // ONB2 S3: tablets (Devices) and vendors, each under its own route's permission. An unreadable answer leaves the
  // step "unknown", never a false pending or done.
  const devicePermitted = hasPermission(activeSession, "admin.device.manage");
  let activeDeviceCount: number | undefined;
  if (devicePermitted) {
    const result = await listTabletDevices(apiBaseUrl, tenantId);
    if (!loadIsCurrent(gen)) return;
    if (result.status === 200) {
      activeDeviceCount = result.body.devices.filter((device) => device.state === "active").length;
    }
  }
  const vendorPermitted = hasPermission(activeSession, "inv.vendor.view");
  let activeVendorCount: number | undefined;
  if (vendorPermitted) {
    const result = await listVendors(apiBaseUrl, tenantId, { activeOnly: true });
    if (!loadIsCurrent(gen)) return;
    if (result.status === 200) {
      activeVendorCount = result.body.vendors.length;
    }
  }

  const userPermitted = hasPermission(activeSession, "admin.user.view");
  let users: AdminUserDto[] = [];
  if (userPermitted) {
    const result = await listUsersForAdmin(apiBaseUrl, tenantId, { activeOnly: false });
    if (!loadIsCurrent(gen)) return;
    if (result.status === 200) {
      users = result.body.users;
    }
  }

  // INV31: starting stock. Its own permission (inv.movement.record, the same
  // one the write is gated on) and its own read; an unreadable answer leaves the
  // step "unknown", never a false pending or done.
  const startingStockPermitted = hasPermission(activeSession, "inv.movement.record");
  let startingStockStatus: Awaited<ReturnType<typeof readStartingStockStatus>>;
  if (startingStockPermitted) {
    startingStockStatus = await readStartingStockStatus(activeSession);
    if (!loadIsCurrent(gen)) return;
  }

  // Step 1: item kinds and categories.
  if (tenantSettingsPermitted) {
    const itemKindRows = vocabularies["item_kind"] ?? [];
    // ONB4-F2: the same resolution Settings uses (policy set, legacy value, fixed kinds), so the step
    // cannot disagree with the cards. A tenant-added kind with no method picked stays pending
    // (review finding, 2026-09-22).
    nextStates.itemKinds = itemKindsStepDone(itemKindRows) ? "done" : "pending";
  } else {
    nextStates.itemKinds = "unknown";
  }

  // Keep the existing checklist identity; it now reads the independent standard.
  const standardHundredths = checklistStandardPour === undefined ? null : parseOuncesToHundredthsClient(checklistStandardPour);
  nextStates.pourVolumes = checklistStandardPour === undefined ? "unknown"
    : standardHundredths !== null && standardHundredths > 0n && standardHundredths <= 1000n ? "done" : "pending";

  // Step 3: liquor categories and bottle sizes.
  if (tenantSettingsPermitted) {
    const hasActiveLiquorCategory = (vocabularies["item_category"] ?? [])
      .filter((entry) => entry.itemKindScope === "liquor")
      .some((row) => row.active);
    const hasActiveBottleSize = (vocabularies["liquor_bottle_size"] ?? []).some(
      (row) => row.active,
    );
    nextStates.liquorCategories =
      hasActiveLiquorCategory && hasActiveBottleSize ? "done" : "pending";
  } else {
    nextStates.liquorCategories = "unknown";
  }

  // Step 4: bottles and packaged items. Existence alone is not enough
  // (Section 2.2's own false-green: a deactivated product is not
  // sellable).
  const readyItem = liquorProducts.some(
    (item) =>
      item.active &&
      !item.addon_only &&
      !item.hide_on_menu &&
      (item.saleMechanism === "whole_unit"
        ? item.unit_price_cents !== null
        : checklistPricingModel === "shelf"
          ? checklistShelfTiers.some((tier) => tier.tier === item.shelf_tier)
          : checklistPricingModel === "per_ounce" && item.per_ounce_price_cents !== null),
  );
  nextStates.bottles = !liquorProductPermitted
    ? "unknown"
    : readyItem
      ? "done"
      : checklistPricingModel === undefined && liquorProducts.some((item) => item.active)
        ? "unknown"
        : "pending";

  // Step 5: starting stock (INV31).
  nextStates.startingStock = startingStockStepState({
    permitted: startingStockPermitted,
    startsEmpty: startingStockStatus?.startsEmpty ?? false,
    initialStockCompleted: startingStockStatus?.initialStockCompleted ?? false,
    products: startingStockStatus?.products,
  });

  // Step 6: recipes.
  nextStates.recipes = menuDrinkPermitted
    ? menuDrinks.some((drink) => drink.active)
      ? "done"
      : "pending"
    : "unknown";

  // Step 7: drink menu categories.
  nextStates.drinkCategories = tenantSettingsPermitted
    ? (vocabularies["drink_display_category"] ?? []).some((row) => row.active)
      ? "done"
      : "pending"
    : "unknown";

  // Step 8: pricing, narrowed to tip presets (Section 2.2's own reasoning
  // for why this is the one genuinely-unset sub-field of the compound
  // step).
  nextStates.pricing = tenantSettingsPermitted
    ? tipPresets.length > 0
      ? "done"
      : "pending"
    : "unknown";

  // MEMBERPRICE1 (DEC-595): done when the active Member tier (the same lookup the free drink uses) is priced above $0.00.
  const nextDetails: Partial<Record<SetupChecklistStepId, string>> = {};
  if (!tenantSettingsPermitted || checklistPricingModel === undefined) {
    nextStates.memberPrice = "unknown";
  } else {
    const memberTier = findMemberShelfTier(
      checklistShelfTiers.map((tier) => ({ label: tier.name, active: tier.active, priceCents: tier.priceCents })),
    );
    if (memberTier !== undefined && memberTier.priceCents > 0) {
      nextStates.memberPrice = "done";
    } else {
      nextStates.memberPrice = "pending";
      nextDetails.memberPrice =
        memberTier === undefined
          ? "Add a Member Shelf tier and price it. Members get no free drink until then."
          : "Members get no free drink until it is set.";
    }
  }

  // ONB1: sales tax rate confirmed. A session that cannot read the settings leaves it unknown.
  nextStates.taxRate =
    tenantSettingsPermitted && taxRateConfirmed !== undefined
      ? taxRateConfirmed
        ? "done"
        : "pending"
      : "unknown";

  // Step 9: time zone and cutoff confirmed (CL6-F1-F3, DEC-286).
  nextStates.businessDay =
    tenantSettingsPermitted && businessDayConfirmed !== undefined
      ? businessDayConfirmed
        ? "done"
        : "pending"
      : "unknown";

  // Step 10: terminals. listTerminals returns inactive terminals too, so
  // the active filter here is load-bearing.
  nextStates.terminals = terminalPermitted
    ? terminals.some((terminal) => terminal.active)
      ? "done"
      : "pending"
    : "unknown";

  // ONB2 S5: the card hold amount. Set (any positive amount) is done and the amount is shown; none is pending.
  if (holdAmountCents === undefined) {
    nextStates.holdAmount = "unknown";
  } else if (holdAmountCents === null) {
    nextStates.holdAmount = "pending";
  } else {
    nextStates.holdAmount = "done";
    nextDetails.holdAmount = formatCents(holdAmountCents);
  }

  // ONB2 S3: tablets. This device counts, so a location that got this far normally reads done; revoking every device reads
  // pending.
  nextStates.devices =
    activeDeviceCount === undefined ? "unknown" : activeDeviceCount > 0 ? "done" : "pending";

  // ONB2 S3: vendors.
  nextStates.vendors =
    activeVendorCount === undefined ? "unknown" : activeVendorCount > 0 ? "done" : "pending";

  // Step 11: roles and users. More than the one active bootstrap admin
  // every tenant starts with; a deactivated second user does not count.
  nextStates.users = userPermitted
    ? users.filter((user) => user.active).length > 1
      ? "done"
      : "pending"
    : "unknown";

  // Discard rather than write: this session's own teardown (a sign-out,
  // or leaving the admin screen) ran while one of the six fetches above
  // was still in flight. The response already reached this function --
  // it is a genuine, correct answer to a question no one is asking
  // anymore -- so it is dropped here rather than repainting a checklist
  // for a session that no longer owns the screen.
  if (!loadIsCurrent(gen)) {
    return;
  }

  setupChecklistStepStates = nextStates;
  setupChecklistStepDetails = nextDetails;

  renderSetupChecklist();
}

/** Section 2.5: looks a step's own destination up from
 * SETTINGS_SEARCH_INDEX by (group, anchorSelector), never a freshly
 * hand-typed duplicate of the same literal (the same reasoning UX24's
 * own cross-link applies). Step 9's own anchorSelector is shared by a
 * second entry ("Reset PIN and unlock account"); matching group too and
 * taking Array.prototype.find's first match resolves to the "Users"
 * entry, which appears first in SETTINGS_SEARCH_INDEX's own array order
 * (confirmed this session). */
function goToSetupChecklistStep(group: AdminSection, anchorSelector: string): void {
  const entry = SETTINGS_SEARCH_INDEX.find(
    (candidate) => candidate.group === group && candidate.anchorSelector === anchorSelector,
  );
  if (entry !== undefined) {
    void goToSettingsSearchResult(entry);
  }
}

/** ONB2 S3: how far "Set up this location" has got, for the first-sign-in banner on Open Tabs. Reads the checklist fresh
 * (the same reads the Settings card uses, each under its own permission). Undefined when the person may not see Settings
 * location setup, or when the session ended while the reads were in flight. A step the person cannot read counts as not
 * done, so the banner never says more is finished than is known. */
export async function readSetupChecklistProgress(
  activeSession: Session,
): Promise<{ done: number; total: number } | undefined> {
  if (!hasPermission(activeSession, "admin.tenant_settings.view")) {
    return undefined;
  }
  const gen = loadGeneration;
  await loadSetupChecklist(activeSession);
  // A load that was discarded half way leaves the earlier states in place; those are not an answer.
  if (sessionOrUndefined()?.token !== activeSession.token || !loadIsCurrent(gen)) {
    return undefined;
  }
  return {
    done: SETUP_CHECKLIST_STEPS.filter((step) => setupChecklistStepStates[step.id] === "done")
      .length,
    total: SETUP_CHECKLIST_STEPS.length,
  };
}

/** ONB4 slice 1: the 16 checklist reads for the setup wizard, fresh, with each row's detail line. The wizard has no
 * state of its own: its steps are computed from this. Undefined when the person may not see Settings location setup,
 * or when the session ended while the reads were in flight. */
export async function readSetupChecklistSnapshot(activeSession: Session): Promise<
  | {
      states: Partial<Record<SetupChecklistStepId, SetupChecklistStepState>>;
      details: Partial<Record<SetupChecklistStepId, string>>;
    }
  | undefined
> {
  if (!hasPermission(activeSession, "admin.tenant_settings.view")) {
    return undefined;
  }
  const gen = loadGeneration;
  await loadSetupChecklist(activeSession);
  // A load that was discarded half way leaves the earlier states in place; those are not an answer.
  if (sessionOrUndefined()?.token !== activeSession.token || !loadIsCurrent(gen)) {
    return undefined;
  }
  return { states: { ...setupChecklistStepStates }, details: { ...setupChecklistStepDetails } };
}

/** ONB4 slice 1: a wizard link into Settings. main.ts has already shown the Settings screen. Opens the same
 * destination the checklist row for this step opens. */
export async function openSetupStepInSettings(
  activeSession: Session,
  stepId: SetupChecklistStepId,
): Promise<void> {
  await openAdminScreen(activeSession);
  const step = SETUP_CHECKLIST_STEPS.find((candidate) => candidate.id === stepId);
  if (step === undefined) {
    return;
  }
  if (step.id === "startingStock") {
    await openStartingStockFromChecklist();
    return;
  }
  if (step.opensSection === true) {
    clearSettingsSearch();
    if (!(await showSection(step.group))) return;
    return;
  }
  goToSetupChecklistStep(step.group, step.anchorSelector);
}

/** What tapping a checklist step does. Shared by the step's own row link and the "still to do" links in the folded header. */
function openSetupChecklistStepLink(step: SetupChecklistStepDef, state: SetupChecklistRowState): void {
  if (step.id === "startingStock") {
    // INV31, UX80: the screen lives in the Inventory menu now; this link
    // is how first time setup reaches it. Back returns to Settings.
    if (state !== "unknown" && state !== "loading") {
      void openStartingStockFromChecklist();
    }
    return;
  }
  if (step.opensSection === true) {
    clearSettingsSearch();
    void showSection(step.group);
    return;
  }
  goToSetupChecklistStep(step.group, step.anchorSelector);
}

/** LOCSET1 (B12): the short name each unfinished step carries in the folded header. */
const SETUP_CHECKLIST_SHORT_TITLES: Readonly<Record<SetupChecklistStepId, string>> = {
  itemKinds: "Item kinds",
  pourVolumes: "Standard pour",
  liquorCategories: "Categories and sizes",
  bottles: "Items you stock",
  startingStock: "Starting stock",
  recipes: "Recipes",
  drinkCategories: "Drink categories",
  pricing: "Pricing",
  memberPrice: "Member Shelf price",
  taxRate: "Sales tax",
  businessDay: "Time zone and cutoff",
  holdAmount: "Card hold amount",
  terminals: "Card readers",
  devices: "Devices",
  users: "Staff and PINs",
  vendors: "Vendors",
};
/** Steps that keep card payments off come first. */
const SETUP_CHECKLIST_GO_LIVE_FIRST: readonly SetupChecklistStepId[] = ["businessDay", "taxRate", "memberPrice"];
const SETUP_CHECKLIST_TODO_SHOWN = 3;

/** LOCSET1 (B12): names the unfinished steps with a link each until they are done. Only a step the server answered "pending"
 * is named; unknown (no permission) and loading steps are never called unfinished. */
function renderSetupChecklistTodo(): void {
  setupChecklistTodoEl.replaceChildren();
  const pending = SETUP_CHECKLIST_STEPS.filter((step) => setupChecklistStepStates[step.id] === "pending").sort(
    (a, b) =>
      Number(SETUP_CHECKLIST_GO_LIVE_FIRST.includes(b.id)) - Number(SETUP_CHECKLIST_GO_LIVE_FIRST.includes(a.id)),
  );
  setupChecklistTodoEl.hidden = pending.length === 0 || !setupChecklistListEl.hidden;
  if (setupChecklistTodoEl.hidden) return;
  const label = document.createElement("span");
  label.className = "admin-setup-checklist-todo-label";
  label.textContent = "To do:";
  setupChecklistTodoEl.append(label);
  for (const step of pending.slice(0, SETUP_CHECKLIST_TODO_SHOWN)) {
    const link = document.createElement("button");
    link.type = "button";
    link.className = "admin-setup-checklist-todo-link";
    link.dataset["testid"] = "admin-setup-checklist-todo-link";
    link.dataset["todoStep"] = step.id;
    link.textContent = SETUP_CHECKLIST_SHORT_TITLES[step.id];
    link.addEventListener("click", () => openSetupChecklistStepLink(step, "pending"));
    setupChecklistTodoEl.append(link);
  }
  const more = pending.length - SETUP_CHECKLIST_TODO_SHOWN;
  if (more > 0) {
    const all = document.createElement("button");
    all.type = "button";
    all.className = "admin-setup-checklist-todo-link admin-setup-checklist-todo-more";
    all.dataset["testid"] = "admin-setup-checklist-todo-more";
    all.textContent = `and ${more} more`;
    all.addEventListener("click", () => {
      setupChecklistCollapsed = false;
      renderSetupChecklist();
    });
    setupChecklistTodoEl.append(all);
  }
}

function renderSetupChecklist(): void {
  const doneCount = SETUP_CHECKLIST_STEPS.filter(
    (step) => setupChecklistStepStates[step.id] === "done",
  ).length;
  setupChecklistProgressEl.textContent = `${doneCount} of ${SETUP_CHECKLIST_STEPS.length} done`;
  // UX91: once every step reads done the whole card is hidden (no header, no
  // gap). It is shown while any step is pending or unknown, so a step a later
  // software update adds brings the card back. The progress text above is
  // still written while hidden: specs use it as the "painted" signal.
  setupChecklistEl.hidden = doneCount === SETUP_CHECKLIST_STEPS.length;
  setupChecklistToggleEl.textContent = setupChecklistCollapsed ? "Show" : "Hide";
  setupChecklistListEl.hidden = setupChecklistCollapsed;
  renderSetupChecklistTodo();

  setupChecklistListEl.innerHTML = "";
  for (const step of SETUP_CHECKLIST_STEPS) {
    const state: SetupChecklistRowState = setupChecklistStepStates[step.id] ?? "loading";
    const row = document.createElement("li");
    row.className = "admin-setup-checklist-step";
    row.dataset["testid"] = "admin-setup-checklist-step";
    row.dataset["step"] = step.id;
    row.dataset["state"] = state;

    const body = document.createElement("div");
    body.className = "admin-setup-checklist-step-body";
    const title = document.createElement("div");
    title.className = "admin-setup-checklist-step-title";
    title.textContent = step.title;
    body.append(title);
    const detailText = setupChecklistStepDetails[step.id];
    if (detailText !== undefined && state !== "unknown" && state !== "loading") {
      const detail = document.createElement("div");
      detail.className = "admin-setup-checklist-step-detail";
      detail.dataset["testid"] = "admin-setup-checklist-step-detail";
      detail.textContent = detailText;
      body.append(detail);
    }

    const link = document.createElement("button");
    link.type = "button";
    link.className = "admin-setup-checklist-step-link";
    link.dataset["testid"] = "admin-setup-checklist-step-link";
    link.textContent =
      state === "loading"
        ? "Loading"
        : state === "unknown"
          ? "You do not have permission to view this"
          : step.id === "startingStock"
            ? "Set starting stock"
            : GROUP_LABELS[step.group];
    // UX118: no answer yet, so nothing to open and no permission wording.
    link.disabled = state === "loading";
    // Section 2.5: a step whose own permission read came back "unknown"
    // still renders its link. Navigating to the tab is governed by
    // showSection's own sectionVisible guard, already enforced
    // independently.
    link.addEventListener("click", () => openSetupChecklistStepLink(step, state));

    row.append(body, link);
    setupChecklistListEl.append(row);
  }
}

async function openStartingStockFromChecklist(): Promise<void> {
  clearSettingsSearch();
  // UX80: opened from Settings, so Close returns to Settings, Inventory items
  // (currentOperationsView is undefined here; see the hook below).
  if (!(await showSection("inventory"))) return;
  const active = sessionOrUndefined();
  if (active !== undefined) {
    await openStartingStockScreen(active);
  }
}

// INV31: how the Set starting stock screen reaches back into Settings. It is
// drawn and owned by ui/src/startingStock.ts; these are its only exits.
configureStartingStock({
  getSession: () => sessionOrUndefined(),
  onClose: () => {
    const active = sessionOrUndefined();
    // UX80: opened from the Inventory menu, back to Stock levels; opened from
    // the Settings setup checklist, back to Settings.
    if (
      (currentOperationsView === "startingStock" || currentOperationsView === "stockLevels") &&
      active !== undefined
    ) {
      void openOperationsView(active, "stockLevels");
      return;
    }
    void showSection("inventory");
  },
  onChanged: () => {
    const active = sessionOrUndefined();
    if (active !== undefined) {
      void loadSetupChecklist(active);
      void refreshStartingStockEntry(active);
    }
  },
  goToCounts: () => {
    // UX80: counting is the Inventory menu's Count stock screen now.
    const active = sessionOrUndefined();
    if (active !== undefined) {
      void openOperationsView(active, "counts");
    }
  },
  setBottleSize: (itemId) => {
    void openItemStockAndVendor(itemId);
  },
  toast: (message, tone) => adminToast(message, tone),
});

// PO10 (DEC-294): how the "Update prices from a file" screen reaches back into
// Settings. It is drawn and owned by ui/src/vendorPriceList.ts; these are its only
// exits. Closing returns to Purchasing, which re-draws the vendor list and so
// the card's "Prices last updated" line.
configureVendorPriceList({
  getPlanningLocationId: inventorySellingSiteId,
  getSession: () => sessionOrUndefined(),
  onClose: () => {
    const active = sessionOrUndefined();
    // UX80: opened from Ordering in the menu, back to the vendor list there;
    // opened from a vendor card in Settings, back to Settings, Vendors.
    if (currentOperationsView === "priceList" && active !== undefined) {
      void openOperationsView(active, "priceList");
      return;
    }
    void showSection("purchasing");
  },
  openedFromMenu: () => currentOperationsView === "priceList",
  confirm: (options) => showConfirmSheet(options),
  toast: (message, tone) => adminToast(message, tone),
});

/** The item's own Stock & vendor tab, where its bottle size is set. */
async function openItemStockAndVendor(liquorProductId: string): Promise<void> {
  const active = sessionOrUndefined();
  if (active === undefined) {
    return;
  }
  await openAdminScreenOnSection(active, "inventory");
  const bottle = adminLiquorProducts.find((product) => product.id === liquorProductId);
  if (bottle === undefined) {
    return;
  }
  await openEditBottleForm(bottle);
  switchBottleFormTab("inventory");
}

setupChecklistToggleEl.addEventListener("click", () => {
  setupChecklistCollapsed = !setupChecklistCollapsed;
  renderSetupChecklist();
});

// ---------------------------------------------------------------------
// Drinks: list, seasonal toggle, add/edit form
// ---------------------------------------------------------------------

// Amendment 2026-08-22 (tenth): the Drinks list gains the Bottles list's
// own search/filter ergonomics, server-side (combines with the category
// chip as AND), the same reasoning loadBottles' own comment gives:
// bulk selection at scale requires filtering first, and a real location
// can hold far more drinks than is comfortable to filter client-side.
async function loadDrinks(): Promise<void> {
  const gen = loadGeneration;
  const s = session();
  // SET17: resolve the permission this loader's own route requires,
  // rather than inheriting whatever the section that reached it happens
  // to demand. The Recipes tab's own union requires admin.menu_drink.MANAGE
  // (SET10: Drinks moved off the old Menu tab onto its own Recipes tab,
  // same permission), a different permission from the one this route
  // requires.
  // No seeded role holds manage without view today, so this was LATENT
  // rather than live, and it is exactly the coincidence SET17 exists to
  // stop relying on: DEC-023 makes roles configurable and a per-user
  // override is enough to break the alignment with no code change.
  if (!hasPermission(s, "admin.menu_drink.view")) {
    // SET16-F1: this guard used to return before writing ANY placeholder,
    // so a session denied this permission left whatever the PREVIOUS
    // employee's session had painted here -- their own menu rows, not
    // even a loading state over them. invalidate() clearing
    // adminMenuDrinks only prevents a later re-render from showing stale
    // rows; it does nothing about rows already sitting in the DOM from
    // before this guard was ever reached.
    drinkListEl.innerHTML = '<p class="tabs-empty">You do not have permission to view this.</p>';
    return;
  }
  drinkListEl.innerHTML = '<p class="tabs-loading">Loading menu drinks…</p>';
  const searchTerm = drinkSearchInput.value.trim();
  const result = await listMenuDrinks(s.apiBaseUrl, s.tenantId, {
    activeOnly: false,
    q: searchTerm.length > 0 ? searchTerm : undefined,
    category: drinkFilterCategory.length > 0 ? drinkFilterCategory : undefined,
  });
  // SET16-F4: this session's teardown ran while the fetch above was in
  // flight; discard the response rather than write it over whatever
  // invalidate() (or the session now on screen) already put here.
  if (!loadIsCurrent(gen)) return;
  if (result.status !== 200) {
    drinkListEl.innerHTML = `<p class="tabs-empty">Could not load the menu (${result.body.message ?? result.body.error ?? result.status}).</p>`;
    adminToast(
      `Could not load the menu. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`,
      "danger",
    );
    return;
  }
  // SET15: set only on success, following the already-correct
  // editingUserPermissionsLoaded shape, so a failed load leaves the
  // latch false and the next tab visit retries instead of skipping the
  // fetch for the rest of the visit.
  drinksLoaded = true;
  // RECUX2-F2: a franchisor's drinks come first, grouped by the franchisor's Menu category in the franchisor's order, A to Z
  // inside a category; the location's own drinks follow exactly as before (A to Z).
  adminMenuDrinks = [...result.body.menuDrinks].sort((a, b) => {
    const left = a.recipe_origin?.franchisorCategory ?? null;
    const right = b.recipe_origin?.franchisorCategory ?? null;
    if (left !== null && right === null) return -1;
    if (left === null && right !== null) return 1;
    if (left !== null && right !== null && left.position !== right.position) return left.position - right.position;
    return compareNames(a.name, b.name);
  });
  // A filter/search change can drop a previously-visible drink out of
  // the list; its selection (if any) is dropped with it, so "select all
  // visible" and the bulk bar's own count never silently refer to a
  // drink no longer on screen.
  renderDrinkList();
}

// ---------------------------------------------------------------------
// RECIPETABLE1 (docs/build-briefs/RECIPETABLE1-recipes-table.md): the Recipes drinks list is a table, like the inventory items list.
//
// Columns: checkbox | Drink | Display category | Requires liquor | Active | Edit and Sharing. The checkbox column is always on for a
// person who may edit drinks; ticking any row shows the sticky purple bar (recipeTable.ts renderRecipeStrip) with one button per bulk
// field. Each button opens a pick-one sheet (recipeBulkSheet.ts) that sends the SAME write the drink editor uses
// (POST .../menu-drinks/bulk-edit, which calls updateMenuDrink drink by drink). The selection is pruned to the rows listed now every
// time the list is drawn, so a filter change can never leave a drink selected that the person cannot see. "Select all shown" is the
// rows listed, never the tenant's whole menu.
// ---------------------------------------------------------------------

/** The tenant's label for a drink display category value (a retired one keeps its label), else the built-in label. */
function drinkCategoryDisplayLabel(value: string): string {
  const entry = (adminVocabularies["drink_display_category"] ?? []).find((candidate) => candidate.value === value);
  return entry?.label ?? categoryLabel(value);
}

/** The tenant's label for a liquor category value, else the built-in label. */
function liquorCategoryDisplayLabel(value: string): string {
  const entry = (adminVocabularies["item_category"] ?? []).find(
    (candidate) => candidate.itemKindScope === "liquor" && candidate.value === value,
  );
  return entry?.label ?? categoryLabel(value);
}

/** The loaded drinks the Requires liquor chip leaves showing. Search and category are already applied by the server. */
function visibleDrinks(): MenuDrinkListItemDto[] {
  if (drinkFilterRequires === "any") return adminMenuDrinks;
  const wanted = drinkFilterRequires === "on";
  return adminMenuDrinks.filter((drink) => drink.requires_liquor_selection === wanted);
}

function drinkTableSelectable(): boolean {
  const activeSession = sessionOrUndefined();
  return activeSession !== undefined && hasPermission(activeSession, "admin.menu_drink.manage");
}

// Mirrors bottleListEmptyMessage's own shape: an empty result under an
// active filter or search says plainly what was searched for.
function drinkListEmptyMessage(): string {
  const searchTerm = drinkSearchInput.value.trim();
  const hasCategory = drinkFilterCategory.length > 0;
  const hasSearch = searchTerm.length > 0;
  const hasRequires = drinkFilterRequires !== "any";
  if (!hasCategory && !hasSearch && !hasRequires) {
    return "No menu drinks for this location yet.";
  }
  const subject = hasCategory ? `${categoryLabel(drinkFilterCategory)} drinks` : "drinks";
  const searchPhrase = hasSearch ? ` matching "${searchTerm}"` : "";
  const requiresPhrase = hasRequires ? ` with Requires liquor ${drinkFilterRequires}` : "";
  return `No ${subject}${requiresPhrase}${searchPhrase} yet.`;
}

function renderDrinkList(): void {
  drinkListEl.innerHTML = "";
  if (sessionOrUndefined() === undefined) {
    drinkBulkBarEl.hidden = true;
    return;
  }
  const selectable = drinkTableSelectable();
  const shown = visibleDrinks();
  // A filter or search change can drop a previously-visible drink out of the list; its selection goes with it.
  const shownIds = new Set(shown.map((drink) => drink.id));
  for (const id of [...selectedDrinkIds]) {
    if (!shownIds.has(id) || !selectable) selectedDrinkIds.delete(id);
  }

  // What the franchise network offers this location, and the older sharing tiles, carry accept, decline and map controls a table
  // row cannot hold: they stay cards, above the table, only when no Requires liquor filter is on (an offer has no such setting).
  const term = drinkSearchInput.value.trim().toLocaleLowerCase();
  const offerNodes: HTMLElement[] = [];
  if (drinkFilterRequires === "any") {
    for (const node of [...unifiedRecipeOfferRows, ...recipeSharing.legacyRows()]) {
      const name = node.dataset["recipeName"] ?? "";
      const category = node.dataset["recipeCategory"] ?? "";
      if (
        name.toLocaleLowerCase().includes(term) &&
        (drinkFilterCategory === "" || category.toLocaleLowerCase() === drinkFilterCategory.toLocaleLowerCase())
      ) {
        offerNodes.push(node);
      }
    }
    offerNodes.sort((a, b) => compareNames(a.dataset["recipeName"] ?? "", b.dataset["recipeName"] ?? ""));
  }
  if (offerNodes.length > 0) {
    const offers = document.createElement("div");
    offers.className = "admin-recipe-offers";
    offers.dataset["testid"] = "admin-recipe-offers";
    offers.append(...offerNodes);
    drinkListEl.append(offers);
  }

  if (shown.length === 0) {
    if (offerNodes.length === 0) {
      const empty = document.createElement("p");
      empty.className = "tabs-empty";
      empty.dataset["testid"] = "admin-drink-empty";
      empty.textContent = drinkListEmptyMessage();
      drinkListEl.append(empty);
    }
    syncDrinkSelectionUi();
    return;
  }

  const table = document.createElement("table");
  table.className = "inv-table recipe-table";
  table.dataset["testid"] = "admin-drink-table";
  table.append(
    createRecipeHead({
      selectable,
      selectState: "none",
      onToggleAll: (checked) => {
        for (const drink of shown) {
          if (checked) selectedDrinkIds.add(drink.id);
          else selectedDrinkIds.delete(drink.id);
        }
        syncDrinkSelectionUi();
      },
    }),
  );
  const columnCount = recipeTableColumns(selectable).length;
  const tbody = document.createElement("tbody");
  let shownFranchisorCategory: string | undefined;
  for (const drink of shown) {
    // RECUX2-F2: a franchisor's drinks come grouped by the franchisor's Menu category, in the franchisor's order.
    const franchisorCategory = drink.recipe_origin?.franchisorCategory ?? null;
    if (franchisorCategory !== null && franchisorCategory.name !== shownFranchisorCategory) {
      shownFranchisorCategory = franchisorCategory.name;
      const headingRow = document.createElement("tr");
      headingRow.className = "admin-bottle-group-heading-row";
      const headingCell = document.createElement("td");
      headingCell.colSpan = columnCount;
      const groupTitle = document.createElement("h3");
      groupTitle.className = "admin-drink-group-title";
      groupTitle.dataset["testid"] = "admin-drink-group-title";
      groupTitle.textContent = franchisorCategory.name;
      headingCell.append(groupTitle);
      headingRow.append(headingCell);
      tbody.append(headingRow);
    }
    tbody.append(buildDrinkRow(drink, selectable));
  }
  table.append(tbody);
  drinkListEl.append(table);
  syncDrinkSelectionUi();
}

function buildDrinkRow(drink: MenuDrinkListItemDto, selectable: boolean): HTMLElement {
  const row = document.createElement("tr");
  row.className = "inv-row recipe-row";
  row.dataset["testid"] = "admin-drink-row";
  row.dataset["menuDrinkId"] = drink.id;
  row.dataset["recipeName"] = drink.name;
  row.tabIndex = 0;
  const openEditor = (): void => void openEditDrinkForm(drink);

  if (selectable) {
    const selectCell = document.createElement("td");
    selectCell.className = "recipe-col-select";
    const box = document.createElement("input");
    box.type = "checkbox";
    box.className = "recipe-select-box";
    box.dataset["testid"] = "admin-drink-select-checkbox";
    box.setAttribute("aria-label", `Select ${drink.name}`);
    box.checked = selectedDrinkIds.has(drink.id);
    // The row opens the drink editor on click and on Enter or Space; the box must not.
    box.addEventListener("click", (event) => event.stopPropagation());
    box.addEventListener("keydown", (event) => event.stopPropagation());
    box.addEventListener("change", () => {
      if (box.checked) selectedDrinkIds.add(drink.id);
      else selectedDrinkIds.delete(drink.id);
      syncDrinkSelectionUi();
    });
    selectCell.append(box);
    row.append(selectCell);
  }

  const info = document.createElement("td");
  info.className = "recipe-col-drink";
  const name = document.createElement("div");
  name.className = "admin-drink-name";
  name.textContent = drink.name;
  info.append(name);
  const origin = document.createElement("div");
  origin.className = "admin-recipe-origin";
  origin.dataset["testid"] = "admin-recipe-origin";
  origin.textContent =
    drink.recipe_origin?.kind === "corporate"
      ? "From corporate"
      : drink.recipe_origin?.kind === "franchisee"
        ? `From ${drink.recipe_origin.name}`
        : "";
  // UIFIX5: the location's own recipes carry no origin line; only a recipe from corporate or another franchisee does.
  if (origin.textContent !== "") info.append(origin);
  if (drink.recipe_origin !== undefined && drink.recipe_origin !== null) {
    const mapped = document.createElement("span");
    mapped.className = "admin-recipe-sharing-indicator";
    mapped.textContent = drink.recipe_origin.required && !drink.recipe_origin.withdrawn ? "Required" : "Mapped";
    info.append(mapped);
  }
  for (const label of recipeSharing.indicators(drink.id)) {
    const flag = document.createElement("span");
    flag.className = "admin-recipe-sharing-indicator";
    flag.textContent = label;
    info.append(flag);
  }
  // LOCFLOOR1 (sweep B17): a drink with no ingredients and no liquor choice cannot be rung, so say so on its row.
  if (drink.has_recipe === false) {
    const needs = document.createElement("span");
    needs.className = "admin-recipe-sharing-indicator admin-drink-needs-recipe-chip";
    needs.dataset["testid"] = "admin-drink-needs-recipe-chip";
    needs.textContent = "! Needs a recipe";
    info.append(needs);
  }
  const publishedLocations = drink.network_locations ?? [];
  if (publishedLocations.length > 0) {
    const locations = document.createElement("div");
    locations.className = "admin-drink-category";
    locations.dataset["testid"] = "admin-drink-locations";
    locations.textContent = publishedLocations.join(", ");
    info.append(locations);
  }
  row.append(info);

  const category = document.createElement("td");
  category.className = "recipe-col-category";
  category.dataset["testid"] = "admin-drink-row-category";
  category.textContent = drinkCategoryDisplayLabel(drink.category);
  row.append(category);

  row.append(
    createRequiresLiquorCell(
      drink.requires_liquor_selection,
      (drink.liquor_slots ?? []).map((value) => liquorCategoryDisplayLabel(value)),
    ),
  );

  // Plain language toggle, staff-facing 8th-grade wording: "Active"/"Inactive" text plus a physical switch, never a bare unlabeled
  // checkbox. Tapping the toggle itself never opens the edit form (stopPropagation inside the shared helper). UX11: built by the
  // one shared activation-switch helper every activation control on this screen uses.
  const activeCell = document.createElement("td");
  activeCell.className = "recipe-col-active";
  activeCell.append(
    renderActivationSwitch({
      active: drink.active,
      entityLabel: drink.name,
      toggleTestId: "admin-active-toggle",
      disabled: drink.recipe_origin?.required === true && !drink.recipe_origin.withdrawn,
      describedById: "admin-drink-list-help",
      statusTestId: "admin-active-status",
      activeText: "Active",
      inactiveText: "Inactive",
      confirmDeactivate: (drinkName) => ({
        title: `Make ${drinkName} inactive?`,
        message: `Guests won't see ${drinkName} on the menu until it's made active again. Nothing already on a ticket is affected.`,
        confirmLabel: "Make inactive",
      }),
      write: async (nextActive) => {
        const s = session();
        const result = await updateMenuDrink(s.apiBaseUrl, s.tenantId, drink.id, { active: nextActive });
        if (result.status !== 200) {
          return {
            ok: false,
            message: result.body.message ?? result.body.error ?? `Could not update this drink (${result.status}).`,
          };
        }
        drink.active = result.body.menuDrink.active;
        return { ok: true, active: result.body.menuDrink.active };
      },
      onChange: (active) => {
        adminToast(active ? `${drink.name} is now active.` : `${drink.name} is now inactive.`, "success");
      },
    }),
  );
  row.append(activeCell);

  const actions = document.createElement("td");
  actions.className = "recipe-col-actions";
  const buttons = document.createElement("div");
  buttons.className = "recipe-row-actions";
  const edit = document.createElement("button");
  edit.type = "button";
  edit.className = "btn-secondary btn-compact";
  edit.dataset["testid"] = "admin-drink-edit";
  edit.textContent = "Edit";
  edit.setAttribute("aria-label", `Edit ${drink.name}`);
  edit.addEventListener("click", (event) => {
    event.stopPropagation();
    openEditor();
  });
  buttons.append(edit);
  if (drink.network_publication_id == null && hasPermission(session(), "admin.recipe.publish")) {
    const share = document.createElement("button");
    share.type = "button";
    share.className = "btn-secondary btn-compact admin-recipe-sharing-open";
    share.dataset["testid"] = "admin-recipe-sharing-open";
    share.textContent = "Sharing";
    share.addEventListener("click", (event) => {
      event.stopPropagation();
      const gen = loadGeneration;
      void openEditDrinkForm(drink).then(() => {
        if (loadIsCurrent(gen) && editingDrinkId === drink.id && !drinkFormScrim.hidden) selectRecipeFormTab(true);
      });
    });
    buttons.append(share);
  }
  actions.append(buttons);
  row.append(actions);

  // Tapping the row (anywhere that is not a control) opens the drink editor, as the card did.
  row.addEventListener("click", openEditor);
  row.addEventListener("keydown", (event) => {
    if (event.target === row && (event.key === "Enter" || event.key === " ")) {
      event.preventDefault();
      openEditor();
    }
  });
  return row;
}

/** Brings the checkboxes, the header checkbox, the row tint and the purple bar in line with selectedDrinkIds, without redrawing rows. */
function syncDrinkSelectionUi(): void {
  const shown = visibleDrinks();
  const shownSelected = shown.filter((drink) => selectedDrinkIds.has(drink.id)).length;
  for (const row of Array.from(drinkListEl.querySelectorAll<HTMLElement>("tr.recipe-row"))) {
    const picked = selectedDrinkIds.has(row.dataset["menuDrinkId"] ?? "");
    row.classList.toggle("recipe-row--selected", picked);
    const box = row.querySelector<HTMLInputElement>("input.recipe-select-box");
    if (box !== null) box.checked = picked;
  }
  const headBox = drinkListEl.querySelector<HTMLInputElement>("thead input.recipe-select-box");
  if (headBox !== null) {
    headBox.checked = shown.length > 0 && shownSelected === shown.length;
    headBox.indeterminate = shownSelected > 0 && shownSelected < shown.length;
  }
  renderRecipeStrip(drinkBulkBarEl, {
    count: selectedDrinkIds.size,
    shownCount: shown.length,
    onField: (field) => openRecipeBulk(field),
    onSelectAll: () => {
      selectedDrinkIds = new Set(shown.map((drink) => drink.id));
      syncDrinkSelectionUi();
    },
    onClear: () => {
      selectedDrinkIds = new Set();
      syncDrinkSelectionUi();
    },
  });
}

/** Opens the pick-one sheet for one bulk field over the ticked drinks. The sheet sends the drink editor's own write, drink by drink. */
function openRecipeBulk(field: BulkRecipeField): void {
  const activeSession = sessionOrUndefined();
  if (activeSession === undefined || !drinkTableSelectable() || selectedDrinkIds.size === 0) return;
  const drinks = adminMenuDrinks
    .filter((drink) => selectedDrinkIds.has(drink.id))
    .map((drink) => ({ id: drink.id, name: drink.name }));
  const gen = loadGeneration;
  openBulkRecipeSheet({
    field,
    drinks,
    options:
      field === "category" ? tenantDrinkCategories() : field === "requiresLiquor" ? tenantLiquorCategories() : [],
    applyBatch: async (request, drinkIds) => {
      if (!loadIsCurrent(gen)) return { ok: false, message: "The session changed." };
      const result = await bulkEditMenuDrinks(activeSession.apiBaseUrl, activeSession.tenantId, {
        ...request,
        menuDrinkIds: drinkIds,
      });
      if (result.status !== 200 || result.body.changed === undefined) {
        return { ok: false, message: capitalizeServerMessage(result.body.message ?? "Check your connection.") };
      }
      return { ok: true, result: result.body };
    },
    onClosed: ({ touched, failedIds }) => {
      if (!loadIsCurrent(gen)) return;
      if (touched) {
        selectedDrinkIds = new Set(failedIds);
        void loadDrinks();
      } else {
        syncDrinkSelectionUi();
      }
    },
  });
}

function renderDrinkFilterChips(): void {
  renderBottleFilterChipGroup(
    drinkFilterCategoryEl,
    "admin-drink-filter-category",
    "admin-drink-filter-category-option",
    [{ value: "", label: "All" }, ...tenantDrinkCategories()],
    drinkFilterCategory,
    (value) => {
      drinkFilterCategory = value;
      void loadDrinks();
    },
  );
  renderBottleFilterChipGroup(
    drinkFilterRequiresEl,
    "admin-drink-filter-requires",
    "admin-drink-filter-requires-option",
    [
      { value: "any", label: "Any" },
      { value: "on", label: "On" },
      { value: "off", label: "Off" },
    ],
    drinkFilterRequires,
    (value) => {
      drinkFilterRequires = value === "on" || value === "off" ? value : "any";
      renderDrinkList();
    },
  );
}

function handleDrinkSearchInput(): void {
  if (drinkSearchDebounceTimer !== undefined) {
    clearTimeout(drinkSearchDebounceTimer);
  }
  drinkSearchDebounceTimer = setTimeout(() => {
    void loadDrinks();
  }, 250);
}

/* SET7: the drink display categories these admin pickers OFFER come from
 * the tenant's own drink_display_category vocabulary.
 *
 * NARROWER THAN IT LOOKS, and worth stating so nobody "finishes" it
 * somewhere it is not broken. The FLOOR is already correct:
 * catalogDisplay.ts's orderCategories appends any unrecognized category
 * rather than dropping it, and categoryLabel falls back to the raw
 * value, so a tenant-added category already appears on the menu grid. It
 * is these two ADMIN pickers that iterate CATEGORY_DISPLAY_ORDER
 * directly, so a manager could add "seltzer" in Settings and then not be
 * offered it on the Drinks form that is supposed to use it.
 *
 * Order: UX87 sorts these pickers alphabetically by the shown label (the
 * tenant's sort_order still drives the floor menu's tabs), with
 * CATEGORY_DISPLAY_ORDER kept as the fallback for the window before
 * loadVocabularies() returns. Labels come from the
 * vocabulary too, so a tenant that renames "other" to "House Specials"
 * sees that here rather than the seeded label.
 *
 * Active only, unlike the pour-size helper: these lists are for CHOOSING
 * a category on a new or edited drink, and a retired category must not
 * be offered. A drink already carrying a retired category keeps it,
 * because the server's update path does not re-validate a value it read
 * back, which is SET6's rule. */
function tenantDrinkCategories(): { value: string; label: string }[] {
  const offered = activeVocabularyOptions(
    adminVocabularies["drink_display_category"] ?? [],
    CATEGORY_DISPLAY_ORDER.map((category) => ({
      value: category,
      label: categoryLabel(category),
    })),
  );
  // UX87 (owner, 2026-10-09: "the list here should be alphabetized for
  // easier reading"): these pickers list categories A to Z by the label
  // shown, case-insensitively, whatever the vocabulary's sort_order says.
  // The stored order is untouched: the floor menu's category tabs still
  // follow it (catalogDisplay.ts), and Menu > General still edits it. A
  // new array is sorted, never the vocabulary rows themselves.
  return offered;
}

/* SET7: rebuilds the Display category <select> from the tenant's own
 * drink_display_category vocabulary (active values only). A drink
 * currently open for editing whose stored category is not among those
 * active values (retired since the drink was saved, or never registered
 * in this tenant's vocabulary at all, which IS reachable here: unlike
 * container_type, this column carries no database CHECK or foreign key
 * and no server-side enum validation, so a stored category can diverge
 * from the vocabulary table freely, not merely by later retirement) is
 * merged back into the offered options by value, keeping its own
 * vocabulary label when one still exists (even retired) or the raw
 * stored string when it does not, mirroring
 * populateBottleContainerTypeSelect's own merge rule (CT1). editingDrink
 * is already set by the time this runs in edit mode (openEditDrinkForm
 * assigns it before calling resetDrinkForm, this function's only caller),
 * and is undefined in Add mode, so a brand-new drink form is never
 * affected. */
function populateDrinkCategorySelect(): void {
  drinkCategorySelect.innerHTML = "";
  const offered = tenantDrinkCategories();
  const currentValue = editingDrink?.category ?? undefined;
  if (
    currentValue !== undefined &&
    currentValue.length > 0 &&
    !offered.some((entry) => entry.value === currentValue)
  ) {
    const storedEntry = (adminVocabularies["drink_display_category"] ?? []).find(
      (entry) => entry.value === currentValue,
    );
    offered.push({ value: currentValue, label: storedEntry?.label ?? currentValue });
    // UX87: a merged-in stored value takes its alphabetical place too.
    offered.splice(0, offered.length, ...sortOptions(offered));
  }
  for (const category of offered) {
    const option = document.createElement("option");
    option.value = category.value;
    option.textContent = category.label;
    drinkCategorySelect.append(option);
  }
}

/* SET7 (extended): the LIQUOR CATEGORIES these three manager pickers
 * offer come from the tenant's own Liquor-scoped item categories.
 *
 * WHY THIS EXISTS AT ALL, given SET6 claimed liquor_category was already
 * wired end to end. It was wired in the DOMAIN and in the DATABASE: the
 * API accepts a tenant-added category and a tenant-scoped foreign key
 * enforces it. The MANAGER CONSOLE was never wired. catalogDisplay.ts's
 * own comment says so plainly ("the manager console's own copy of
 * src/domain/catalog.ts's LIQUOR_SLOT_CATEGORIES, for the Bottles
 * add/edit form's category picker and the Drinks add/edit form's
 * allowed-liquor-categories chip picker") and nobody read it as the gap
 * it was.
 *
 * Reported by the owner 2026-08-30, from the live screen: "In inventory
 * I dont see a way to add a new category... I need to add Mezcal and
 * Whiskey." Adding a category over the API worked the whole time; there
 * was no way to CHOOSE it on the screen where bottles are added.
 *
 * LIQUOR_SLOT_CATEGORIES survives as the fallback for the window before
 * loadVocabularies() returns, exactly as the pour-size helper does.
 * Active only, because these pickers are for choosing a category on a
 * new or edited row; callers merge in a value the row already carries so
 * retiring a category never strips it from existing data. */
function tenantLiquorCategories(): { value: string; label: string }[] {
  // UX88 (DEC-339): A to Z by the shown label, like the drink display
  // categories (UX87). Callers that merge a stored value in re-sort.
  if ((adminVocabularies["item_category"] ?? []).length === 0 && itemCategoriesByScopeFallback["liquor"] !== undefined) {
    return sortOptions([...itemCategoriesByScopeFallback["liquor"]]);
  }
  return activeVocabularyOptions(
    (adminVocabularies["item_category"] ?? []).filter((entry) => entry.itemKindScope === "liquor"),
    LIQUOR_SLOT_CATEGORIES.map((category) => ({
      value: category,
      label: categoryLabel(category),
    })),
  );
}

function renderDrinkLiquorSlotChips(): void {
  drinkLiquorSlotsEl.innerHTML = "";
  const offered = tenantLiquorCategories();
  for (const selected of drinkFormLiquorSlots) {
    if (!offered.some((entry) => entry.value === selected)) {
      offered.push({ value: selected, label: categoryLabel(selected) });
    }
  }
  for (const { value: category, label: categoryText } of sortOptions(offered)) {
    const optionLabel = document.createElement("label");
    optionLabel.className = "admin-chip-option";
    const input = document.createElement("input");
    input.type = "checkbox";
    input.className = "admin-chip-option-input";
    input.dataset["testid"] = "admin-drink-liquor-slot-option";
    input.dataset["category"] = category;
    input.checked = drinkFormLiquorSlots.has(category);
    input.addEventListener("change", () => {
      if (input.checked) {
        drinkFormLiquorSlots.add(category);
      } else {
        drinkFormLiquorSlots.delete(category);
      }
      updateNeedsRecipeMark();
    });
    const text = document.createElement("span");
    text.className = "admin-chip-option-label";
    // The vocabulary's own label, so a tenant that renames a category
    // sees the rename here rather than the seeded display name.
    text.textContent = categoryText;
    optionLabel.append(input, text);
    drinkLiquorSlotsEl.append(optionLabel);
  }
}

// VOC1-F4 (docs/build-briefs/VOC1-F4-drink-modifier-catalog.md, Section
// 2.2). Active catalog modifiers only (the fixed-vocabulary-picker rule
// applied here exactly as tenantLiquorCategories already applies it), plus
// any value the attachment being edited already offers even if since
// retired (the identical merge-in renderDrinkLiquorSlotChips' own
// offered.push loop above already does), so an edit never silently drops
// an existing, now-retired attachment from view. VOC1-F4-F2 (Section 2.3):
// generalized to take its OWN attachment state (a Map<value, required>)
// as a parameter rather than reading the module-level drinkFormModifiers
// directly, so the cocktail form and the Inventory item form can both call
// this against their own separate state map.
interface AddonGroupDraft { id?: string;label:string;required:boolean;optionValues:string[] }
interface IngredientChoiceDraft {
  key:string;persisted:boolean;inventoryItemId:string;label:string;quantity:string;unit:string;price:string;enabled:boolean;
}
const addonGroupsByAttachments = new WeakMap<Map<string, boolean>, AddonGroupDraft[]>();
const ingredientDraftsByAttachments = new WeakMap<Map<string,boolean>,IngredientChoiceDraft[]>();
function loadAddonGroups(attachments:Map<string,boolean>,details:readonly MenuDrinkModifierDetailDto[],unresolved:readonly UnresolvedIngredientChoiceDto[]=[]):void {
  const groups=new Map<string,AddonGroupDraft>();
  ingredientDraftsByAttachments.set(attachments,details.map(d=>({key:d.value,persisted:true,
    inventoryItemId:d.inventoryItemId ?? "",label:d.label,quantity:d.quantityPerUse ?? "",unit:d.unit ?? "",
    price:centsToAmountString(d.priceAdjustmentCents),enabled:d.enabled ?? d.active})));
  for(const d of details) if(d.groupId!=null){const g=groups.get(d.groupId)??{id:d.groupId,label:d.groupLabel??"",required:d.groupRequired===true,optionValues:[]};g.optionValues.push(d.value);groups.set(d.groupId,g)}
  for(const d of unresolved){
    attachments.set(d.key,d.required);
    ingredientDrafts(attachments).push({key:d.key,persisted:false,inventoryItemId:d.inventoryItemId??"",label:d.label,
      quantity:d.quantityPerUse??"",unit:d.unit??"",price:d.priceAdjustmentCents===null?"":centsToAmountString(d.priceAdjustmentCents),enabled:d.enabled});
    if(d.groupId!==null){const g=groups.get(d.groupId)??{id:d.groupId,label:d.groupLabel??"",required:d.groupRequired,optionValues:[]};g.optionValues.push(d.key);groups.set(d.groupId,g)}
  }
  addonGroupsByAttachments.set(attachments,[...groups.values()]);
}
function attachmentGroups(attachments:Map<string,boolean>):AddonGroupDraft[]{
  const groups=addonGroupsByAttachments.get(attachments)??[];addonGroupsByAttachments.set(attachments,groups);return groups;
}
function ingredientDrafts(attachments:Map<string,boolean>):IngredientChoiceDraft[]{
  const drafts=ingredientDraftsByAttachments.get(attachments)??[];ingredientDraftsByAttachments.set(attachments,drafts);return drafts;
}
function ingredientChoiceBody(attachments:Map<string,boolean>):{
  ingredientChoices:IngredientChoiceInput[];
  ingredientChoiceGroups:IngredientChoiceGroupInput[];
}{
  const groups=attachmentGroups(attachments);
  return {ingredientChoiceGroups:groups.map(g=>({id:g.id,label:g.label,required:g.required})),
    ingredientChoices:ingredientDrafts(attachments).map(d=>({id:d.persisted?d.key:undefined,
      inventoryItemId:d.inventoryItemId,quantityPerUse:normalizeLeadingDot(d.quantity),unit:d.unit,
      priceAdjustmentCents:parseDollarsToCentsExact(d.price,"Ingredient price adjustment"),enabled:d.enabled,
      groupId:groups.find(g=>g.optionValues.includes(d.key))?.id ?? null,
      required:groups.some(g=>g.optionValues.includes(d.key))?false:attachments.get(d.key)===true}))};
}
function renderModifierAttachmentChips(attachmentState:Map<string,boolean>,container:HTMLElement):void {
  container.replaceChildren();
  const drafts=ingredientDrafts(attachmentState),groups=attachmentGroups(attachmentState);
  const locked=container===drinkModifiersEl&&drinkNetworkSource!==null;
  const items=new Map([...adminLiquorProducts.filter(p=>bottleSelling(p)?.active === true).map(p=>({id:p.id,name:p.name})),...drinkRecipeItems].map(item=>[item.id,item]));
  const inputField=(label:string,value:string,onInput:(value:string)=>void):HTMLLabelElement=>{
    const field=document.createElement("label");field.className="modrm1-choice-field";field.append(document.createTextNode(label));
    const input=document.createElement("input");input.className="field-input";input.value=value;input.inputMode="decimal";input.disabled=locked;
    input.addEventListener("input",()=>onInput(input.value));field.append(input);return field;
  };
  const renderChoice=(draft:IngredientChoiceDraft,target:HTMLElement):void=>{
    const row=document.createElement("div");row.className="modrm1-choice-row";
    const itemField=document.createElement("label");itemField.className="modrm1-choice-field";itemField.append(document.createTextNode("Inventory ingredient"));
    const select=document.createElement("select");select.className="field-input";select.disabled=locked;
    const empty=document.createElement("option");empty.value="";empty.textContent="Choose ingredient";select.append(empty);
    const offered=new Map(items);if(draft.inventoryItemId&&!offered.has(draft.inventoryItemId))offered.set(draft.inventoryItemId,{id:draft.inventoryItemId,name:draft.label});
    for(const item of sortByName([...offered.values()],i=>i.name)){const option=document.createElement("option");option.value=item.id;option.textContent=item.name;select.append(option)}
    select.value=draft.inventoryItemId;select.addEventListener("change",()=>{draft.inventoryItemId=select.value;draft.label=offered.get(select.value)?.name??"";draft.enabled=true});itemField.append(select);
    const unitField=document.createElement("label");unitField.className="modrm1-choice-field";unitField.append(document.createTextNode("Unit"));
    const unit=document.createElement("select");unit.className="field-input";unit.disabled=locked;
    for(const [value,label]of [["","Choose unit"],["oz","oz"],["unit","each"]]){const option=document.createElement("option");option.value=value!;option.textContent=label!;unit.append(option)}
    unit.value=draft.unit;unit.addEventListener("change",()=>{draft.unit=unit.value});unitField.append(unit);
    const remove=document.createElement("button");remove.type="button";remove.className="btn btn-secondary";remove.textContent="Remove";remove.disabled=locked;
    remove.addEventListener("click",()=>{drafts.splice(drafts.indexOf(draft),1);attachmentState.delete(draft.key);for(const g of groups)g.optionValues=g.optionValues.filter(v=>v!==draft.key);renderModifierAttachmentChips(attachmentState,container)});
    row.append(itemField,inputField("Amount",draft.quantity,v=>{draft.quantity=v}),unitField,inputField("Price adjustment",draft.price,v=>{draft.price=v}),remove);target.append(row);
  };
  const addChoice=(target:HTMLElement,group?:AddonGroupDraft):void=>{
    const add=document.createElement("button");add.type="button";add.className="btn btn-secondary";add.textContent="Add ingredient choice";add.disabled=locked||items.size===0;
    add.addEventListener("click",()=>{const key=crypto.randomUUID();drafts.push({key,persisted:false,inventoryItemId:"",label:"",quantity:"",unit:"",price:"0.00",enabled:true});attachmentState.set(key,false);group?.optionValues.push(key);renderModifierAttachmentChips(attachmentState,container)});target.append(add);
  };
  const ungrouped=drafts.filter(d=>!groups.some(g=>g.optionValues.includes(d.key)));
  if(ungrouped.length){const section=document.createElement("section");section.className="modrm1-choice-group";for(const d of ungrouped){renderChoice(d,section);const required=document.createElement("label");const checkbox=document.createElement("input");checkbox.type="checkbox";checkbox.checked=attachmentState.get(d.key)===true;checkbox.disabled=locked;checkbox.addEventListener("change",()=>attachmentState.set(d.key,checkbox.checked));required.append(checkbox,document.createTextNode("Required"));section.append(required)}container.append(section)}
  for(const group of groups){
    const section=document.createElement("section");section.className="modrm1-choice-group";
    const header=document.createElement("div");header.className="modrm1-choice-header";
    const name=document.createElement("input");name.className="field-input";name.value=group.label;name.placeholder="Group name";name.setAttribute("aria-label","Ingredient choice group name");name.disabled=locked;name.addEventListener("input",()=>{group.label=name.value});
    const required=document.createElement("label"),checkbox=document.createElement("input");checkbox.type="checkbox";checkbox.checked=group.required;checkbox.disabled=locked;checkbox.addEventListener("change",()=>{group.required=checkbox.checked});required.append(checkbox,document.createTextNode("Required"));
    const remove=document.createElement("button");remove.type="button";remove.className="btn btn-secondary";remove.textContent="Remove group";remove.disabled=locked;remove.addEventListener("click",()=>{for(const key of group.optionValues){const i=drafts.findIndex(d=>d.key===key);if(i>=0)drafts.splice(i,1);attachmentState.delete(key)}groups.splice(groups.indexOf(group),1);renderModifierAttachmentChips(attachmentState,container)});
    header.append(name,required,remove);section.append(header);for(const draft of drafts.filter(d=>group.optionValues.includes(d.key)))renderChoice(draft,section);addChoice(section,group);container.append(section);
  }
  const addGroup=document.createElement("button");addGroup.type="button";addGroup.className="btn btn-secondary";addGroup.textContent="Add choice group";addGroup.disabled=locked||items.size===0;
  addGroup.addEventListener("click",()=>{groups.push({id:crypto.randomUUID(),label:"",required:false,optionValues:[]});renderModifierAttachmentChips(attachmentState,container)});container.append(addGroup);
  if(items.size===0&&!locked){const status=document.createElement("p");status.className="admin-field-help";status.textContent="Inventory ingredients unavailable.";container.append(status)}
}

// RECUX1: Cancel restores the held item, including a partially typed search.
let recipePicker: HTMLDialogElement | undefined;
let cancelRecipePicker: (() => void) | undefined;
let recipeCostGeneration = 0;
let recipeCostTimer: ReturnType<typeof setTimeout> | undefined;
let recipeInventoryReturn: (() => void) | undefined;

function closeRecipePicker(restore = false): void {
  if (restore) cancelRecipePicker?.();
  cancelRecipePicker = undefined;
  recipePicker?.close();
  recipePicker?.remove();
  recipePicker = undefined;
  refreshRecipePickerResults = undefined;
}

function openRecipePicker(draft: RecipeDraft, added = false): void {
  closeRecipePicker(true);
  const held = { ...draft };
  const dialog = document.createElement("dialog");
  dialog.className = "admin-recipe-picker";
  dialog.setAttribute("aria-label", added ? "Add ingredient" : "Change ingredient");
  const heading = document.createElement("h3");
  heading.textContent = added ? "Add ingredient" : "Change ingredient";
  const content = document.createElement("div");
  const cancel = document.createElement("button");
  cancel.type = "button";
  cancel.className = "btn-secondary";
  cancel.textContent = "Cancel";
  const leave = (): void => {
    if (dialog.dataset["pending"] === "true") return;
    closeRecipePicker(true);
    renderDrinkComponents();
  };
  cancel.addEventListener("click", leave);
  dialog.addEventListener("cancel", event => { event.preventDefault(); leave(); });
  dialog.append(heading, content, cancel);
  document.body.append(dialog);
  recipePicker = dialog;
  cancelRecipePicker = () => {
    if (added) drinkFormComponents = drinkFormComponents.filter(component => component !== draft);
    else Object.assign(draft, held);
  };
  renderRecipeItemPicker(draft, content);
  dialog.showModal();
  content.querySelector<HTMLInputElement>("input[type=search]")?.focus();
}

function recipeCostInputs(): Array<{ quantity: string; unit: string; liquorProductId: string | null; isLiquorSlot: boolean }> {
  return drinkFormComponents.map(component => ({
    quantity: component.storedQuantity !== null && component.quantity.trim() === formatRecipeQuantity(component.storedQuantity)
      ? component.storedQuantity : normalizeLeadingDot(component.quantity),
    unit: component.unit, liquorProductId: component.liquorProductId, isLiquorSlot: component.isLiquorSlot,
  }));
}

/** LOCFLOOR1 (sweep B17): true while the drink being edited has no ingredient and no liquor choice, the state the server
 * and the floor read as "no recipe": nothing to put on a line. Mirrors listMenuDrinkIdsWithRecipe (components or slots). */
function drinkNeedsRecipe(): boolean {
  if (drinkNetworkSource !== null) return existingDrinkComponents.length === 0;
  const anyIngredient = drinkFormComponents.some(
    (component) => component.liquorProductId !== null || component.isLiquorSlot,
  );
  const guestChoosesLiquor = drinkRequiresLiquorInput.checked && drinkFormLiquorSlots.size > 0;
  return !anyIngredient && !guestChoosesLiquor;
}

function updateNeedsRecipeMark(): void {
  drinkNeedsRecipeEl.hidden = !drinkNeedsRecipe();
}

function refreshRecipeCost(): void {
  updateNeedsRecipeMark();
  const generation = ++recipeCostGeneration;
  if (recipeCostTimer !== undefined) clearTimeout(recipeCostTimer);
  const summary = mustFind<HTMLElement>("#admin-recipe-cost-summary");
  summary.replaceChildren();
  const activeSession = sessionOrUndefined();
  const allowed = activeSession !== undefined && hasPermission(activeSession, "inv.cost.view") && hasPermission(activeSession, "admin.menu_drink.manage");
  summary.hidden = !allowed;
  const lineCosts = [...drinkComponentsEl.querySelectorAll<HTMLElement>(".admin-recipe-line-cost")];
  lineCosts.forEach(node => { node.hidden = !allowed; node.textContent = "Cost unavailable"; });
  if (!allowed || activeSession === undefined || drinkFormScrim.hidden) return;
  const title = document.createElement("strong");
  title.textContent = "Drink cost: Incomplete";
  const subtotal = document.createElement("span");
  subtotal.textContent = "Known ingredient subtotal unavailable";
  summary.append(title, subtotal);
  if (drinkNetworkSource !== null) return;
  const components = recipeCostInputs();
  if (!components.length || components.some(component => !/^\d+(?:\.\d{1,4})?$/.test(component.quantity) || Number(component.quantity) <= 0)) return;
  const formGeneration = drinkFormGeneration;
  const loadGen = loadGeneration;
  const requestedLocation = stockLevelsLocationId;
  const stamp = JSON.stringify(components);
  const current = (): boolean => recipeCostGeneration === generation && drinkFormGeneration === formGeneration &&
    loadIsCurrent(loadGen) && sessionOrUndefined() === activeSession && !drinkFormScrim.hidden &&
    stockLevelsLocationId === requestedLocation && stamp === JSON.stringify(recipeCostInputs());
  recipeCostTimer = setTimeout(() => {
    void (async () => {
      try {
        const reply = await getRecipeDraftCost(activeSession.apiBaseUrl, activeSession.tenantId, components, requestedLocation);
        if (!current()) return;
        if (reply.status !== 200) { title.textContent = "Drink cost unavailable"; return; }
        const preview = reply.body;
        title.textContent = preview.complete && preview.totalCostCents !== null
          ? `Drink cost: ${formatCents(preview.totalCostCents)}` : "Drink cost: Incomplete";
        const untrackedNote = (preview.untrackedCount ?? 0) > 0 ? " · Untracked ingredients not counted" : "";
        subtotal.textContent = `${preview.locationName} · Known ingredient subtotal: ${preview.knownSubtotalCents === null ? "-" : formatCents(preview.knownSubtotalCents)}${untrackedNote}`;
        preview.components.forEach((component, index) => {
          const node = lineCosts[index];
          if (node !== undefined) node.textContent = component.reason === "guest_choice" ? "Varies with liquor"
            : component.reason === "not_tracked" ? "Not tracked"
            : component.costCents === null ? "Cost missing"
              : component.source === "vendor_price" ? `${formatCents(component.costCents)} (vendor price)` : formatCents(component.costCents);
        });
      } catch { if (current()) title.textContent = "Drink cost unavailable"; }
    })();
  }, 200);
}

/** CORE14: the one-line "Needs a product" mark and the item picker (a search box with a short result list). */
function renderRecipeItemPicker(draft: RecipeDraft, row: HTMLElement): void {
  const picker = document.createElement("div");
  picker.className = "admin-component-item";

  const search = document.createElement("input");
  search.className = "field-input admin-component-name-input";
  search.type = "search";
  search.autocomplete = "off";
  search.placeholder = drinkRecipeItemsAvailable ? "Search ingredients" : "Ingredients unavailable";
  search.disabled = !drinkRecipeItemsAvailable;
  search.setAttribute("aria-label", "Ingredient item");
  search.dataset["testid"] = "admin-drink-component-item-search";
  // A chosen item shows its name; typing again starts a new search and drops the choice until one is picked.
  search.value = draft.liquorProductId !== null ? draft.componentReference : "";

  const results = document.createElement("div");
  results.className = "admin-component-item-results";
  results.setAttribute("role", "listbox");
  results.dataset["testid"] = "admin-drink-component-item-results";
  results.hidden = true;
  const count = document.createElement("p");
  count.className = "admin-field-help";
  count.setAttribute("aria-live", "polite");
  count.textContent = drinkRecipeItemsAvailable ? "" : "Ingredients unavailable";
  let createChoice: HTMLButtonElement | undefined;

  const showResults = (): void => {
    results.innerHTML = "";
    if (!drinkRecipeItemsAvailable) {
      count.textContent = "Ingredients unavailable";
      results.hidden = true;
      return;
    }
    const matches = matchItems(drinkRecipeItems, search.value);
    count.textContent = drinkRecipeItemsLoading ? "Loading ingredients" : "";
    if (createChoice !== undefined) createChoice.hidden = matches.length !== 0 || search.value.trim().length === 0;
    for (const item of matches) {
      const option = document.createElement("button");
      option.type = "button";
      option.className = "admin-component-item-option";
      option.setAttribute("role", "option");
      option.dataset["testid"] = "admin-drink-component-item-option";
      option.textContent = item.name;
      option.addEventListener("click", () => {
        chooseItem(draft, item);
        closeRecipePicker();
        renderDrinkComponents();
      });
      results.append(option);
    }
    if (matches.length === 0 && !drinkRecipeItemsLoading) {
      const none = document.createElement("p");
      none.className = "admin-field-help";
      none.dataset["testid"] = "admin-drink-component-item-none";
      none.textContent = "No ingredients found";
      results.append(none);
    }
    results.hidden = false;
  };
  search.addEventListener("input", () => {
    if (draft.liquorProductId !== null) {
      draft.liquorProductId = null;
      draft.componentReference = "";
    }
    showResults();
  });
  search.addEventListener("focus", () => {
    if (draft.liquorProductId === null) showResults();
  });
  // ADDDRINK1: the items arrived after this chooser opened, so redraw whatever it was showing.
  refreshRecipePickerResults = () => {
    if (draft.liquorProductId === null && (!results.hidden || search.value.trim().length > 0)) showResults();
    else count.textContent = "";
  };

  picker.append(search, count);
  const activeSession = sessionOrUndefined();
  if (activeSession !== undefined && hasPermission(activeSession, "admin.liquor_product.manage")) {
    const createButton = document.createElement("button");
    createButton.type = "button";
    createButton.className = "btn-secondary btn-compact";
    createButton.textContent = "Create inventory ingredient";
    createButton.hidden = true;
    createChoice = createButton;
    createButton.disabled = !drinkRecipeItemsAvailable;
    const createPanel = document.createElement("div");
    createPanel.hidden = true;
    const nameLabel = document.createElement("label");
    nameLabel.className = "field-label";
    nameLabel.textContent = "Name";
    const nameInput = document.createElement("input");
    nameInput.className = "field-input";
    nameInput.type = "text";
    nameLabel.append(nameInput);
    const confirm = document.createElement("button");
    confirm.type = "button";
    confirm.className = "btn-secondary btn-compact";
    confirm.textContent = "Create ingredient";
    const cancel = document.createElement("button");
    cancel.type = "button";
    cancel.className = "btn-secondary btn-compact";
    cancel.textContent = "Cancel";
    const error = document.createElement("p");
    error.className = "admin-field-help";
    error.setAttribute("role", "alert");
    const facts = document.createElement("p");
    facts.className = "admin-field-help";
    facts.textContent = "Ingredient · Recipe only · Stock tracking off · Selling price $0.00. Size, vendor and cost not set.";
    createPanel.append(nameLabel, facts, confirm, cancel, error);
    picker.append(createButton, createPanel);
    let pending = false;
    createButton.addEventListener("click", () => {
      createPanel.hidden = false;
      createButton.hidden = true;
      nameInput.value = search.value.trim();
      nameInput.focus();
    });
    cancel.addEventListener("click", () => {
      if (pending) return;
      createPanel.hidden = true;
      createButton.hidden = false;
    });
    const ingredientSellingRequestId = crypto.randomUUID();
    const ingredientSiteId = inventorySellingSiteId();
    let originalIngredientBody: Parameters<typeof createLiquorProduct>[2] | undefined;
    let ingredientUncertain = false;
    confirm.addEventListener("click", () => {
      if (pending) return;
      const name = nameInput.value.trim();
      if (name.length === 0) {
        error.textContent = "Enter an ingredient name.";
        return;
      }
      const categoryOptions = (adminVocabularies["item_category"] ?? []).filter(
        (entry) => entry.active && entry.itemKindScope === INGREDIENT_KIND,
      );
      const category =
        categoryOptions.find((entry) => entry.value === "pantry") ?? categoryOptions[0];
      const kind = itemKindEntry(INGREDIENT_KIND);
      if (
        kind?.active !== true ||
        category === undefined ||
        !itemKindSaleMechanisms(INGREDIENT_KIND).includes("whole_unit")
      ) {
        error.textContent = "Ingredient setup unavailable.";
        return;
      }
      const gen = loadGeneration;
      const formGen = drinkFormGeneration;
      const isCurrent = (): boolean =>
        loadIsCurrent(gen) &&
        sessionOrUndefined() === activeSession &&
        drinkFormGeneration === formGen &&
        !drinkFormScrim.hidden &&
        row.isConnected &&
        drinkNetworkSource === null &&
        drinkFormComponents.includes(draft) && inventorySellingSiteId() === ingredientSiteId;
      if (!isCurrent()) return;
      pending = true;
      if (recipePicker !== undefined) recipePicker.dataset["pending"] = "true";
      confirm.disabled = true;
      cancel.disabled = true;
      nameInput.disabled = true;
      error.textContent = "";
      void (async () => {
        try {
          const submittedBody: Parameters<typeof createLiquorProduct>[2] = {
              ...(ingredientSiteId === undefined ? {} : { locationId: ingredientSiteId, sellingRequestId: ingredientSellingRequestId, sellingVersion: 0 }),
              name,
              category: category.value,
              itemKind: INGREDIENT_KIND,
              ...(itemKindPolicyIsActive(INGREDIENT_KIND)
                ? { saleMechanism: "whole_unit" as const }
                : {}),
              unitPriceCents: 0,
              trackStock: false,
              hideOnMenu: true,
            };
          if (ingredientUncertain && JSON.stringify(originalIngredientBody) !== JSON.stringify(submittedBody)) {
            error.textContent = "Previous creation unconfirmed. Restore the original ingredient details to retry.";
            return;
          }
          if (!ingredientUncertain) originalIngredientBody = structuredClone(submittedBody);
          const response = await createLiquorProduct(activeSession.apiBaseUrl, activeSession.tenantId, originalIngredientBody!);
          if (response.networkError || response.status >= 500) ingredientUncertain = true;
          if (!isCurrent()) return;
          if (response.status !== 201) {
            error.textContent = response.body.message ?? "Could not create this ingredient.";
            return;
          }
          const item = response.body.liquorProduct;
          drinkRecipeItems = [
            ...drinkRecipeItems.filter((entry) => entry.id !== item.id),
            { id: item.id, name: item.name },
          ];
          drinkRecipeItemsAvailable = true;
          bottlesLoaded = false;
          chooseItem(draft, item);
          closeRecipePicker();
          renderDrinkComponents();
        } catch {
          ingredientUncertain = true;
          if (isCurrent()) error.textContent = "Could not create this ingredient. Retry the original details.";
        } finally {
          if (isCurrent()) {
            pending = false;
            if (recipePicker !== undefined) recipePicker.dataset["pending"] = "false";
            confirm.disabled = false;
            cancel.disabled = false;
            nameInput.disabled = false;
          }
        }
      })();
    });
  }
  if (needsProduct(draft) && draft.componentReference.length > 0) {
    const legacy = document.createElement("div");
    legacy.className = "admin-component-needs-product";
    legacy.dataset["testid"] = "admin-drink-component-needs-product";
    const legacyName = document.createElement("span");
    legacyName.textContent = humanizeComponentReference(draft.componentReference);
    const badge = document.createElement("span");
    badge.className = "admin-component-needs-product-badge";
    badge.textContent = "Needs a product";
    legacy.append(legacyName, badge);
    picker.append(legacy);
  }
  picker.append(results);
  if (draft.liquorProductId === null) showResults();
  row.append(picker);
}

function renderDrinkComponents(): void {
  drinkComponentsEl.innerHTML = "";
  // CORE14: a drink created from a franchisor's published recipe keeps the network's ingredients, so they stay read
  // only here and the server refuses an edit. The location's own drink gets editable rows below.
  if (editingDrinkId !== undefined && drinkNetworkSource !== null) {
    if (existingDrinkComponents.length === 0) {
      const empty = document.createElement("p");
      empty.className = "admin-field-help";
      empty.textContent = "This drink has no ingredients yet. Ingredients appear here once the network adds them.";
      drinkComponentsEl.append(empty);
    }
    for (const component of existingDrinkComponents) {
      const row = document.createElement("div");
      row.className = "admin-component-row";
      row.dataset["testid"] = "admin-drink-existing-component-row";
      const label = document.createElement("span");
      // UX41 (docs/build-briefs/UX41-recipe-quantities-two-decimals.md,
      // Section 1.3, DEC-193/DEC-197): display-only; component.quantity
      // itself is never reassigned.
      // UX105: a name, not a slug ("lime-juice" reads "Lime juice"), and the liquor pour marker only where the
      // reference does not already say it.
      const componentName = humanizeComponentReference(component.component_reference);
      const markPour =
        component.is_liquor_slot && !/liquor pour/i.test(component.component_reference);
      label.textContent = `${componentName}: ${formatRecipeQuantity(component.quantity)} ${component.unit}${markPour ? " (liquor pour)" : ""}`;
      row.append(label);
      drinkComponentsEl.append(row);
    }
    drinkAddComponentButton.hidden = true;
    refreshRecipeCost();
    return;
  }

  drinkAddComponentButton.hidden = false;
  for (let index = 0; index < drinkFormComponents.length; index++) {
    const component = drinkFormComponents[index];
    if (component === undefined) {
      continue;
    }
    const row = document.createElement("div");
    row.className = "admin-component-row";
    row.dataset["testid"] = "admin-drink-component-row";

    if (component.isLiquorSlot) {
      const pourLabel = document.createElement("span");
      pourLabel.className = "admin-component-name-input admin-component-pour-name";
      pourLabel.dataset["testid"] = "admin-drink-component-pour-name";
      pourLabel.textContent = `${humanizeComponentReference(component.componentReference)} · Guest chooses when ordering`;
      row.append(pourLabel);
    } else {
      const item = document.createElement("div");
      item.className = "admin-component-item";
      const name = document.createElement("strong");
      name.textContent = component.componentReference || "Choose ingredient";
      const change = document.createElement("button");
      change.type = "button";
      change.className = "btn-secondary btn-compact";
      change.textContent = "Change";
      change.addEventListener("click", () => openRecipePicker(component));
      item.append(name, change);
      const itemSession = sessionOrUndefined();
      if (component.liquorProductId !== null && itemSession !== undefined && hasPermission(itemSession, "admin.liquor_product.manage")) {
        const inventory = document.createElement("button");
        inventory.type = "button";
        inventory.className = "btn-secondary btn-compact";
        inventory.textContent = "Inventory item";
        inventory.addEventListener("click", () => {
          const formGen = drinkFormGeneration;
          const itemId = component.liquorProductId;
          if (itemId === null) return;
          const active = (): boolean => sessionOrUndefined() === itemSession && drinkFormGeneration === formGen &&
            !drinkFormScrim.hidden && row.isConnected && component.liquorProductId === itemId;
          inventory.disabled = true;
          void (async () => {
            try {
              const reply = await getLiquorProduct(itemSession.apiBaseUrl, itemSession.tenantId, itemId);
              if (!active()) return;
              if (reply.status !== 200) { adminToast("Could not open this item.", "danger"); return; }
              recipeInventoryReturn = () => { if (active()) refreshRecipeCost(); };
              openEditBottleForm(reply.body.liquorProduct);
            } catch { if (active()) adminToast("Could not open this item.", "danger"); }
            finally { if (active()) inventory.disabled = false; }
          })();
        });
        item.append(inventory);
      }
      row.append(item);
    }

    const quantityInput = document.createElement("input");
    quantityInput.className = "field-input admin-component-quantity-input";
    quantityInput.type = "number";
    quantityInput.inputMode = "decimal";
    quantityInput.step = "0.01";
    quantityInput.min = "0";
    quantityInput.placeholder = "Qty";
    quantityInput.setAttribute("aria-label", "Amount");
    quantityInput.value = component.quantity;
    quantityInput.dataset["testid"] = "admin-drink-component-quantity";
    quantityInput.addEventListener("input", () => {
      component.quantity = quantityInput.value;
      refreshRecipeCost();
    });

    // CORE14-F1: the unit is picked from the fixed list, never typed. A unit an old recipe holds that is not on the
    // list shows as one extra option on its own row so the row can be saved unchanged.
    const unitInput = document.createElement("select");
    unitInput.className = "field-input admin-component-unit-input";
    unitInput.setAttribute("aria-label", "Unit");
    for (const unitValue of recipeUnitChoices(component.unit)) {
      const option = document.createElement("option");
      option.value = unitValue;
      option.textContent = unitValue;
      unitInput.append(option);
    }
    unitInput.value = component.unit;
    unitInput.dataset["testid"] = "admin-drink-component-unit";
    unitInput.addEventListener("change", () => {
      component.unit = unitInput.value;
      refreshRecipeCost();
    });

    row.append(quantityInput, unitInput);
    const cost = document.createElement("span");
    cost.className = "admin-recipe-line-cost";
    row.append(cost);

    // Radio-group semantics via a single shared `name`: selecting one
    // component's liquor-pour marker natively unchecks every other one,
    // no custom toggle logic required. Only shown when the drink requires
    // a liquor selection (renderDrinkComponents is re-run on that
    // checkbox's own change handler below).
    if (drinkRequiresLiquorInput.checked) {
      const liquorSlotLabel = document.createElement("label");
      liquorSlotLabel.className = "admin-component-liquor-slot-label";
      const liquorSlotInput = document.createElement("input");
      liquorSlotInput.type = "radio";
      liquorSlotInput.name = "admin-drink-component-liquor-slot";
      liquorSlotInput.checked = component.isLiquorSlot;
      liquorSlotInput.dataset["testid"] = "admin-drink-component-liquor-slot";
      liquorSlotInput.addEventListener("change", () => {
        for (const other of drinkFormComponents) {
          if (other !== component && other.isLiquorSlot) unmarkLiquorPour(other);
        }
        markLiquorPour(component);
        renderDrinkComponents();
      });
      liquorSlotLabel.append(liquorSlotInput, document.createTextNode("Liquor pour"));
      row.append(liquorSlotLabel);
    }

    const removeButton = document.createElement("button");
    removeButton.type = "button";
    removeButton.className = "admin-component-remove";
    removeButton.dataset["testid"] = "admin-drink-component-remove";
    removeButton.setAttribute("aria-label", "Remove ingredient");
    removeButton.textContent = "✕";
    removeButton.addEventListener("click", () => {
      drinkFormComponents.splice(index, 1);
      renderDrinkComponents();
    });
    row.append(removeButton);

    drinkComponentsEl.append(row);
  }
  refreshRecipeCost();
}

/** DEC-607: says how the build charge enters the price, in the location's pricing model. */
function updateBuildChargeHint(): void {
  const hint = mustFind<HTMLElement>("#admin-drink-build-charge-hint");
  const cents = Math.round(Number(drinkBuildChargeInput.value) * 100);
  const charge = Number.isFinite(cents) && cents > 0 ? formatCents(cents) : "$0.00";
  hint.hidden = !drinkRequiresLiquorInput.checked;
  hint.textContent =
    settingsPricingModel === "shelf"
      ? `Shelf price + ${charge} build charge`
      : `${charge} build charge + the pour at the per-ounce price`;
}

function updateDrinkRequiresLiquorVisibility(): void {
  updateBuildChargeHint();
  const requires = drinkRequiresLiquorInput.checked;
  drinkLiquorSlotsSection.hidden = !requires;
  if (!requires) {
    // CORE14: a drink that takes no liquor selection has no liquor pour; the row goes back to needing an item.
    for (const component of drinkFormComponents) {
      if (component.isLiquorSlot) unmarkLiquorPour(component);
    }
  }
  renderDrinkComponents();
}

function resetDrinkForm(): void {
  recipeInventoryReturn = undefined;
  closeRecipePicker(true);
  recipeCostGeneration += 1;
  if (recipeCostTimer !== undefined) clearTimeout(recipeCostTimer);
  drinkFormGeneration += 1;
  recipeSharing.close();
  selectRecipeFormTab(false);
  mustFind<HTMLElement>("#admin-recipe-form-tabs").hidden = true;
  drinkStockBlock.hidden = true;
  drinkFormError.textContent = "";
  drinkNameInput.value = "";
  drinkNameInput.disabled = false;
  drinkCategorySelect.disabled = false;
  populateDrinkCategorySelect();
  drinkBuildChargeInput.value = "";
  // LIQSEL1 (owner ruling 2026-10-29): a new drink asks for a liquor selection unless the person turns it off.
  drinkRequiresLiquorInput.checked = true;
  drinkFormLiquorSlots = new Set();
  drinkFormComponents = [];
  existingDrinkComponents = [];
  drinkStoredComponents = [];
  drinkNetworkSource = null;
  drinkFormSave.disabled = false;
  drinkNetworkSourceEl.hidden = true;
  drinkNetworkSourceEl.textContent = "";
  renderDrinkMethod(null);
  renderDrinkLiquorSlotChips();
  updateDrinkRequiresLiquorVisibility();
  // VOC1-F4 (Section 2.2): edit-only, see drinkFormModifiers' own comment.
  // Every reset (Add, and the start of every Edit before its own fetch
  // populates it) clears this and hides the section; openEditDrinkForm
  // shows it once the drink's real attachments are loaded.
  drinkFormModifiers = new Map();
  drinkModifiersSection.hidden = false;
  renderModifierAttachmentChips(drinkFormModifiers, drinkModifiersEl);
}

/** CORE14: reads the location's own active items for the ingredient pickers. A location without permission to see items gets a
 * disabled search ("N/A") rather than a request that is refused. A late reply from a closed session writes nothing. */
async function loadRecipeItems(gen: number): Promise<void> {
  const s = session();
  if (!hasPermission(s, "admin.liquor_product.view")) {
    drinkRecipeItems = [];
    drinkRecipeItemsAvailable = false;
    return;
  }
  const recipeSiteId = inventorySellingSiteId();
  const result = await listLiquorProducts(s.apiBaseUrl, s.tenantId, { activeOnly: true, ...(recipeSiteId === undefined ? {} : { locationId: recipeSiteId }) });
  if (!loadIsCurrent(gen) || sessionOrUndefined() !== s || inventorySellingSiteId() !== recipeSiteId) return;
  if (result.status !== 200) {
    drinkRecipeItems = [];
    drinkRecipeItemsAvailable = false;
    adminToast("Could not load your items.", "danger");
    return;
  }
  drinkRecipeItems = result.body.liquorProducts
    .filter((product) => currentProductSelling(product, recipeSiteId)?.active === true)
    .map((product) => ({ id: product.id, name: product.name }));
  drinkRecipeItemsAvailable = true;
}

async function openAddDrinkForm(): Promise<void> {
  const gen = loadGeneration;
  const priorFormGen = drinkFormGeneration;
  if (!vocabulariesLoaded) await loadVocabularies();
  if (!loadIsCurrent(gen) || drinkFormGeneration !== priorFormGen) return;
  editingDrinkId = undefined;
  editingDrink = undefined;
  resetDrinkForm();
  const formGen = drinkFormGeneration;
  drinkFormLiquorSlots = new Set(
    (adminVocabularies["item_category"] ?? [])
      .filter((entry) => entry.active && entry.itemKindScope === "liquor")
      .map((entry) => entry.value),
  );
  renderDrinkLiquorSlotChips();
  drinkFormTitle.textContent = "Add drink";
  drinkActiveRow.hidden = true;
  drinkFormScrim.hidden = false;
  lockBodyScroll();
  drinkNameInput.focus();
  drinkRecipeItemsLoading = true;
  try {
    await loadRecipeItems(gen);
  } finally {
    drinkRecipeItemsLoading = false;
  }
  if (!loadIsCurrent(gen) || drinkFormGeneration !== formGen || drinkFormScrim.hidden) return;
  refreshRecipePickerResults?.();
  renderDrinkComponents();
  renderModifierAttachmentChips(drinkFormModifiers,drinkModifiersEl);
}

async function openEditDrinkForm(drink: MenuDrinkListItemDto): Promise<void> {
  const gen = loadGeneration;
  editingDrinkId = drink.id;
  editingDrink = drink;
  resetDrinkForm();
  const formGen = drinkFormGeneration;
  drinkFormTitle.textContent = `Edit ${drink.name}`;
  drinkNameInput.value = drink.name;
  drinkCategorySelect.value = drink.category;
  drinkBuildChargeInput.value = (drink.build_charge_cents / 100).toFixed(2);
  drinkRequiresLiquorInput.checked = drink.requires_liquor_selection;
  updateBuildChargeHint();
  drinkFormLiquorSlots = new Set(drink.liquor_slots);
  renderDrinkLiquorSlotChips();
  drinkActiveRow.hidden = false;
  drinkActiveInput.checked = drink.active;
  drinkFormScrim.hidden = false;
  lockBodyScroll();
  // CORE7-F1: the 86 control for this drink, once the location's drinks have been read.
  if (canRecordStockFlag()) {
    void loadMenuStock86().then(() => {
      if (loadIsCurrent(gen) && editingDrinkId === drink.id) refreshDrinkStockControl();
    });
  }

  const s = session();
  const [detail] = await Promise.all([
    getMenuDrinkDetailForEdit(s, drink.id),
    loadRecipeItems(gen),
  ]);
  // SET16-F4: this session's teardown ran while the fetch above was in
  // flight; discard rather than write the just-closed form's own state
  // back into module memory or the DOM.
  if (!loadIsCurrent(gen)) return;
  if (formGen !== drinkFormGeneration || editingDrinkId !== drink.id || drinkFormScrim.hidden)
    return;
  if (!detail.loaded) {
    drinkFormSave.disabled=true;
    drinkFormError.textContent="Could not load this recipe. Close it and try again.";
    return;
  }
  existingDrinkComponents = detail.components;
  drinkStoredComponents = detail.components;
  drinkNetworkSource = detail.networkSource;
  renderDrinkMethod(detail.recipeMethod);
  if (detail.networkSource === null) {
    drinkFormComponents = draftsFromComponents(detail.components);
  } else {
    // CORE14 (TEN19): a published recipe is the network's. Say who publishes it; a Required one cannot be changed at all.
    drinkNameInput.disabled = true;
    drinkCategorySelect.disabled = true;
    drinkNetworkSourceEl.hidden = false;
    drinkNetworkSourceEl.textContent = `Published by ${detail.networkSource.networkName}`;
    if (detail.networkSource.required && !detail.networkSource.withdrawn) {
      drinkFormSave.disabled = true;
    }
  }
  // VOC1-F4 (Section 2.2): this drink's own current attachments, shown
  // only now that they are actually loaded (resetDrinkForm above already
  // hid the section and cleared drinkFormModifiers at the start of this
  // same open).
  drinkFormModifiers = new Map(
    detail.modifiers.map((modifier) => [modifier.value, modifier.required]),
  );
  loadAddonGroups(drinkFormModifiers, detail.modifiers,detail.unresolved);
  drinkModifiersSection.hidden = false;
  renderModifierAttachmentChips(drinkFormModifiers, drinkModifiersEl);
  updateDrinkRequiresLiquorVisibility();
  if (detail.networkSource === null && hasPermission(s, "admin.recipe.publish")) {
    mustFind<HTMLElement>("#admin-recipe-form-tabs").hidden = false;
    mustFind<HTMLElement>("#admin-recipe-tab-recipe").hidden = !hasPermission(
      s,
      "admin.menu_drink.manage",
    );
    recipeSharing.open(drink.id);
  }
}

async function getMenuDrinkDetailForEdit(
  s: Session,
  id: string,
): Promise<{
  loaded:boolean;
  unresolved:UnresolvedIngredientChoiceDto[];
  components: MenuDrinkComponentDto[];
  modifiers: MenuDrinkModifierDetailDto[];
  networkSource: { networkName: string; required: boolean; withdrawn: boolean } | null;
  recipeMethod: RecipeMethodDto | null;
}> {
  const result = await getMenuDrink(s.apiBaseUrl, s.tenantId, id);
  if (result.status !== 200) {
    adminToast("Could not load this drink's components.", "danger");
    return { loaded:false,unresolved:[],components: [], modifiers: [], networkSource: null, recipeMethod: null };
  }
  return {
    loaded:true,
    unresolved:result.body.unresolvedIngredientChoices??[],
    components: result.body.components,
    modifiers: result.body.modifiers,
    networkSource: result.body.networkSource ?? null,
    recipeMethod: result.body.recipeMethod ?? null,
  };
}

/** RECUX2-F1: shows the franchisor's glass, garnish and numbered steps on a location's saved drink. Read only: no input exists in it. */
function renderDrinkMethod(method: RecipeMethodDto | null): void {
  drinkMethodEl.replaceChildren();
  drinkMethodEl.hidden = method === null;
  if (method === null) return;
  const heading = document.createElement("h4");
  heading.className = "admin-recipe-method-title";
  heading.textContent = "How to make it";
  drinkMethodEl.append(heading);
  const facts: Array<[string, string | null, string]> = [
    ["Glass", method.glass, "admin-drink-method-glass"],
    ["Garnish", method.garnish, "admin-drink-method-garnish"],
  ];
  for (const [label, value, testId] of facts) {
    if (value === null) continue;
    const row = document.createElement("p");
    row.className = "admin-recipe-method-fact";
    row.dataset["testid"] = testId;
    const name = document.createElement("strong");
    name.textContent = `${label}: `;
    row.append(name, document.createTextNode(value));
    drinkMethodEl.append(row);
  }
  if (method.steps.length > 0) {
    const list = document.createElement("ol");
    list.className = "admin-recipe-method-steps";
    list.dataset["testid"] = "admin-drink-method-steps";
    for (const step of method.steps) {
      const item = document.createElement("li");
      item.textContent = step;
      list.append(item);
    }
    drinkMethodEl.append(list);
  }
  const note = document.createElement("p");
  note.className = "admin-field-help";
  note.textContent = "Set by the franchisor. Locations cannot change it.";
  drinkMethodEl.append(note);
}

function closeDrinkForm(): void {
  recipeInventoryReturn = undefined;
  closeRecipePicker(true);
  recipeCostGeneration += 1;
  if (recipeCostTimer !== undefined) clearTimeout(recipeCostTimer);
  drinkFormGeneration += 1;
  recipeSharing.close();
  if (drinkFormScrim.hidden) {
    return;
  }
  drinkFormScrim.hidden = true;
  unlockBodyScroll();
}

async function saveDrinkForm(keepOpen = false): Promise<boolean> {
  drinkFormError.textContent = "";
  const s = session();
  const formGen = drinkFormGeneration;
  const loadGen = loadGeneration;
  const current = () =>
    formGen === drinkFormGeneration && loadIsCurrent(loadGen) && !drinkFormScrim.hidden;
  const name = drinkNameInput.value.trim();
  if (name.length === 0) {
    drinkFormError.textContent = "Drink name is required.";
    return false;
  }
  const category = drinkCategorySelect.value;
  const requiresLiquorSelection = drinkRequiresLiquorInput.checked;
  let buildChargeCents: number;
  try {
    // UI-batch gate fix (exactness rule extension): this used to call the
    // now-deleted parseDollarsToCents, which silently Math.round()ed a
    // third decimal digit away (live-proven: 2.005 saved as 201 cents
    // with no message). parseDollarsToCentsExact is the same rejecting
    // parser the shelf-tier price field already uses, so a build charge
    // that cannot convert losslessly to integer cents is rejected by
    // name, never silently rounded.
    // ONB2 S4: a blank Build charge means no build charge ($0.00), not an error.
    buildChargeCents = parseDollarsToCentsExact(
      extraChargeInput(drinkBuildChargeInput.value),
      "Build charge",
    );
  } catch (error) {
    drinkFormError.textContent = error instanceof Error ? error.message : "Invalid build charge.";
    return false;
  }
  const liquorSlots = [...drinkFormLiquorSlots];
  if (requiresLiquorSelection && liquorSlots.length === 0) {
    drinkFormError.textContent = "Pick at least one allowed liquor category.";
    return false;
  }

  drinkFormSave.disabled = true;
  try {
    if (editingDrinkId === undefined) {
      // CORE14: every ingredient is one of the location's own items (or the liquor pour); the rules live in recipeEditor.ts.
      const recipe = recipeToBody(drinkFormComponents, { requiresLiquorSelection });
      if (!recipe.ok && drinkFormComponents.length > 0) {
        drinkFormError.textContent = recipe.message;
        return false;
      }
      const components: CreateMenuDrinkComponentInput[] = recipe.ok ? recipe.components : [];
      const result = await createMenuDrink(s.apiBaseUrl, s.tenantId, {
        name,
        category,
        buildChargeCents,
        requiresLiquorSelection,
        liquorSlots: requiresLiquorSelection ? liquorSlots : undefined,
        components: components.length > 0 ? components : undefined,
        ...ingredientChoiceBody(drinkFormModifiers),
      });
      if (!current()) return false;
      if (result.status !== 201) {
        drinkFormError.textContent =
          result.body.message ?? result.body.error ?? "Could not save this drink.";
        return false;
      }
      adminToast(`${result.body.menuDrink.name} added.`, "success");
    } else {
      let editRecipe: CreateMenuDrinkComponentInput[] | undefined;
      if (
        drinkNetworkSource === null &&
        recipeChanged(drinkStoredComponents, drinkFormComponents)
      ) {
        const recipe = recipeToBody(drinkFormComponents, { requiresLiquorSelection });
        if (!recipe.ok) {
          drinkFormError.textContent = recipe.message;
          return false;
        }
        editRecipe = recipe.components;
      }
      const result = await updateMenuDrink(s.apiBaseUrl, s.tenantId, editingDrinkId, {
        name,
        category,
        buildChargeCents,
        requiresLiquorSelection,
        liquorSlots: requiresLiquorSelection ? liquorSlots : [],
        active: drinkActiveInput.checked,
        // VOC1-F4 (Section 2.2): REPLACES the drink's whole attachment set,
        // mirroring liquorSlots' own replace-not-merge contract immediately
        // above.
        ...(drinkNetworkSource === null
          ? {
              ...ingredientChoiceBody(drinkFormModifiers),
            }
          : {}),
        // CORE14: the recipe goes only when it changed, and never for a published recipe (the server refuses it).
        ...(editRecipe === undefined ? {} : { components: editRecipe }),
      });
      if (!current()) return false;
      if (result.status !== 200) {
        drinkFormError.textContent =
          result.body.message ?? result.body.error ?? "Could not save this drink.";
        return false;
      }
      adminToast(`${result.body.menuDrink.name} updated.`, "success");
    }
    if (!keepOpen) closeDrinkForm();
    await loadDrinks();
    return !keepOpen || current();
  } finally {
    if (current()) drinkFormSave.disabled = false;
  }
}

// ---------------------------------------------------------------------
// Bottles: list with search, add/edit form, deactivate
// ---------------------------------------------------------------------

// Owner alpha-review feedback (Amendment 2026-08-21, fourth): resolves the
// tenant's shelf-tier settings for display, falling back to the plain
// tier key ("tier_1"...) at $0.00 when settings have not loaded yet (or
// failed to load). Shared by the bottle add/edit form's tier picker
// (populateShelfTierSelect) and the Bottles list's filter chips and
// per-row chip (shelfTierDisplayName below), so all three always agree on
// what a tier is called.
function resolveShelfTiers(): ShelfTierSettingDto[] {
  return settingsShelfTiers.length > 0
    ? settingsShelfTiers
    : (["tier_1", "tier_2", "tier_3", "tier_4"] as const).map((tier) => ({
        tier,
        name: shelfTierKeyLabel(tier),
        priceCents: 0,
        active: true,
      }));
}

function shelfTierDisplayName(tier: string): string {
  return (
    resolveShelfTiers().find((candidate) => candidate.tier === tier)?.name ??
    shelfTierKeyLabel(tier)
  );
}

/** Fetches the tenant's shelf-tier names once (idempotent: a no-op once
 * settingsShelfTiers is populated, whether that happened here or because
 * the manager already visited Location settings this session). A failed
 * read leaves settingsShelfTiers empty and every caller above falls back
 * to the plain tier key rather than blocking the Bottles panel on it.
 *
 * SET16-F4 follow-up (DEC-083 independent review): takes the CALLER's own
 * captured `gen` as a parameter rather than reading loadGeneration
 * internally, because reading it internally would always compare equal
 * to itself and prove nothing -- every caller (loadBottles, loadDiscounts)
 * already captures `gen` before its own first await, and this is a write
 * that can land after THIS session's own teardown just like any other
 * loader's: invalidate() clears settingsShelfTiers at exactly this race
 * for this reason, and a response landing after that clear must not
 * repopulate it from a session that no longer owns the screen. */
async function ensureShelfTierSettingsLoaded(gen: number): Promise<void> {
  if (settingsShelfTiers.length > 0) {
    return;
  }
  const s = session();
  // SET18: GET /tenants/:tenantId/settings requires
  // admin.tenant_settings.view, and this loader used to ask without
  // checking. A denial leaves settingsShelfTiers empty, which is this
  // function's ONLY latch, so the next visit asked again, and the next.
  // Same idiom as the shared settings fetch's own gate in showSection
  // rather than a second one.
  //
  // The silent non-200 return below is deliberately unchanged: a 403 is
  // now never requested, while a genuine transient failure still leaves
  // the array empty so a later visit retries, which is SET15's rule and
  // the right behavior for a transient fault.
  if (hasPermission(s, "admin.tenant_settings.view")) {
    const result = await getSettings(s.apiBaseUrl, s.tenantId);
    // SET16-F4 follow-up: this session's teardown ran while the fetch
    // above was in flight; discard rather than write over what
    // invalidate() already cleared.
    if (!loadIsCurrent(gen)) return;
    if (result.status === 200) {
      settingsShelfTiers = result.body.settings.shelfTiers.map((tier) => ({ ...tier }));
      // INV27-F2 (DEC-253): a manager who opens Inventory directly, never
      // having visited Money/Purchasing/People first, would otherwise
      // never trigger loadSettings() at all (showSection's own else-if
      // chain does not include "inventory"), leaving settingsPricingModel
      // stuck at its "per_ounce" module default even on a tenant actually
      // running shelf pricing -- the exact wrong-label bug this item
      // fixes, reintroduced through a different loader if this were
      // skipped. This is the ONLY tenant-settings field this loader reads
      // beyond shelf tiers, on purpose: it exists to unblock the item
      // editor's own pricing-model-dependent labels, not to duplicate
      // loadSettings()'s wider job.
      settingsPricingModel = (result.body.settings.pricingModel as PricingModel) ?? "per_ounce";
      applyBottlePriceLabelsForPricingModel();
      renderBottleCalculator();
    }
    return;
  }
  // P1(b) fix (Codex review, PR #59 against 2dcb2df). Falling straight
  // through here used to leave settingsShelfTiers empty for EVERY actor
  // denied admin.tenant_settings.view, so resolveShelfTiers() fell back to
  // the synthetic tier_1..tier_4-at-$0.00 list above -- approximately
  // right while tiers were a fixed four, wrong once B7-S3 let a tenant
  // add, rename or retire one: a retired tier still offered itself in
  // this actor's OWN bottle-form picker (the server then refuses the
  // assignment) and a newly added tier could never be assigned at all.
  // inventory_manager and assistant_manager both hold admin.liquor_
  // product.view even though they are denied admin.tenant_settings.view
  // (src/domain/permissions.ts), so read the tenant's real list through
  // the dedicated route gated on that permission instead of widening
  // admin.tenant_settings.view to them, which would hand this role the
  // whole settings surface to fix a tier picker.
  if (hasPermission(s, "admin.liquor_product.view")) {
    const result = await getLiquorProductShelfTiers(s.apiBaseUrl, s.tenantId);
    if (!loadIsCurrent(gen)) return;
    if (result.status === 200) {
      settingsShelfTiers = result.body.shelfTiers.map((tier) => ({ ...tier }));
    }
  }
}

// Owner alpha-review feedback: single-select filter-chip rows, one for
// shelf tier and one for liquor category, rendered once when the Bottles
// panel first opens (and again after invalidate() clears them, e.g. a
// Location-settings tier rename). Native radio inputs (shared `name` per
// group) rather than a hand-rolled single-select: the browser already
// enforces "at most one checked per group" and keeps the checked chip's
// visual state in sync with no re-render required after the first tap.
/** HIGFIX4A (F-36): the Filters control names how many of its filters are in use, read from the controls themselves
 * (the Vendor filter is drawn by autoPar.ts, so its state is not held here). */
const bottleFiltersDisclosure = mustFind<HTMLElement>("#admin-bottle-filters-disclosure");
const bottleFiltersCountEl = mustFind<HTMLElement>("#admin-bottle-filters-count");
function refreshBottleFiltersCount(): void {
  let inUse = 0;
  for (const group of bottleFiltersDisclosure.querySelectorAll<HTMLElement>(".admin-filter-group")) {
    const checked = group.querySelector<HTMLInputElement>('input[type="radio"]:checked');
    const select = group.querySelector<HTMLSelectElement>("select");
    if ((checked !== null && (checked.dataset["value"] ?? "") !== "") || (select !== null && select.value !== "")) inUse += 1;
  }
  bottleFiltersCountEl.hidden = inUse === 0;
  bottleFiltersCountEl.textContent = String(inUse);
}
bottleFiltersDisclosure.addEventListener("change", refreshBottleFiltersCount);
// Watch the groups only: the count itself lives in the summary, and watching it would call this again on every write.
new MutationObserver(refreshBottleFiltersCount).observe(
  mustFind<HTMLElement>('[data-testid="admin-bottle-filters"]'),
  { childList: true, subtree: true },
);

function renderBottleFilterChips(): void {
  renderBottleFilterChipGroup(
    bottleFilterShelfTierEl,
    "admin-bottle-filter-shelf-tier",
    "admin-bottle-filter-shelf-tier-option",
    [
      { value: "", label: "All" },
      // B7-S3 (build brief Section 2.4): every tier, active and retired,
      // unlike the bottle-form picker (populateShelfTierSelect) which
      // offers only active tiers for a NEW assignment. A retired tier
      // still prices whatever is already assigned to it (DEC-128 Ruling
      // 1), so an admin must still be able to find those bottles by tier
      // here in order to ever reassign them off it.
      ...resolveShelfTiers().map((tier) => ({
        value: tier.tier,
        label: tier.active ? tier.name : `${tier.name} (off)`,
      })),
      { value: "none", label: "No shelf" },
    ],
    bottleFilterShelfTier,
    (value) => {
      bottleFilterShelfTier = value;
      void loadBottles();
    },
  );
  // BULKVENDOR1: Type (the item kind), so "all the liquor" is one tap. Same server filter the Inventory list has always accepted.
  renderBottleFilterChipGroup(
    bottleFilterKindEl,
    "admin-bottle-filter-kind",
    "admin-bottle-filter-kind-option",
    [
      { value: "", label: "All" },
      ...sortByName(
        (adminVocabularies["item_kind"] ?? []).filter((entry) => entry.active && isCurrentItemKind(entry.value)),
        (entry) => entry.label,
      ).map((entry) => ({ value: entry.value, label: entry.label })),
    ],
    bottleFilterKind,
    (value) => {
      bottleFilterKind = value;
      void loadBottles();
    },
  );
  renderBottleFilterChipGroup(
    bottleFilterCategoryEl,
    "admin-bottle-filter-category",
    "admin-bottle-filter-category-option",
    [{ value: "", label: "All" }, ...tenantLiquorCategories()],
    bottleFilterCategory,
    (value) => {
      bottleFilterCategory = value;
      void loadBottles();
    },
  );
}

function renderBottleFilterChipGroup(
  container: HTMLElement,
  radioGroupName: string,
  testId: string,
  options: { value: string; label: string }[],
  selectedValue: string,
  onSelect: (value: string) => void,
  describedById?: string,
): void {
  container.innerHTML = "";
  for (const option of options) {
    const optionLabel = document.createElement("label");
    optionLabel.className = "admin-chip-option";
    const input = document.createElement("input");
    describeSetting(input, describedById);
    input.type = "radio";
    input.name = radioGroupName;
    input.className = "admin-chip-option-input";
    input.dataset["testid"] = testId;
    input.dataset["value"] = option.value;
    input.checked = option.value === selectedValue;
    input.addEventListener("change", () => {
      if (input.checked) {
        onSelect(option.value);
      }
    });
    const text = document.createElement("span");
    text.className = "admin-chip-option-label";
    text.textContent = option.label;
    optionLabel.append(input, text);
    container.append(optionLabel);
  }
}

// Owner alpha-review feedback: server-side filtering (this passes q,
// category, and shelfTier straight to GET .../liquor-products, mirroring
// the existing q/category pattern src/domain/catalog.ts's
// listLiquorProducts already supported before this item) rather than
// fetching every bottle once and filtering client-side. A real location
// can hold hundreds of bottles, per the owner's own stated reason for
// asking for these filters at all; client-side filtering would still pay
// the cost of transferring and holding the unbounded full list on every
// keystroke/tap, while the server can push the WHERE clause down to
// Postgres and return only the matching rows. The three filters combine
// as AND, both with each other and with the search box.
async function loadBottles(): Promise<void> {
  const gen = loadGeneration;
  const requestedCatalogLocationId = autoParSection.inventoryLocationId();
  const vendorOfferWritesAtRequest = vendorOfferWrites();
  const s = session();
  const requestSequence = ++bottleListRequestSequence;
  const searchTerm = bottleSearchInput.value.trim();
  const category = bottleFilterCategory;
  const shelfTier = bottleFilterShelfTier;
  const itemKind = bottleFilterKind;
  const isCurrent = (): boolean =>
    loadIsCurrent(gen) &&
    requestSequence === bottleListRequestSequence &&
    requestedCatalogLocationId === autoParSection.inventoryLocationId() &&
    searchTerm === bottleSearchInput.value.trim() &&
    category === bottleFilterCategory && shelfTier === bottleFilterShelfTier && itemKind === bottleFilterKind;
  // Other inventory reads may ask to repaint while this request is pending.
  // Keep them from painting the previous query's stored rows or hiding its error.
  bottleListReady = false;
  // SET17: resolve the permission this loader's own route requires,
  // rather than inheriting whatever the section that reached it happens
  // to demand. The Inventory tab's union requires admin.liquor_product.MANAGE, a
  // different permission from the one this route requires.
  // No seeded role holds manage without view today, so this was LATENT
  // rather than live, and it is exactly the coincidence SET17 exists to
  // stop relying on: DEC-023 makes roles configurable and a per-user
  // override is enough to break the alignment with no code change.
  if (!hasPermission(s, "admin.liquor_product.view")) {
    // SET16-F1: same gap as loadDrinks' own guard above -- returning
    // before any placeholder write leaves a denied session looking at
    // whatever the previous employee's session had painted here.
    bottleListEl.innerHTML = '<p class="tabs-empty">You do not have permission to view this.</p>';
    return;
  }
  bottleListEl.innerHTML = '<p class="tabs-loading">Loading bottles…</p>';
  await ensureShelfTierSettingsLoaded(gen);
  // SET16-F4: checked after THIS await too, not only the loader's own
  // final fetch below -- the brief's own "loadSettings awaits, writes,
  // then fires five more loads" example is this same shape one level
  // shallower, and a write between two awaits is exactly as discardable
  // as one after the last.
  if (!isCurrent()) return;
  const result = await listLiquorProducts(s.apiBaseUrl, s.tenantId, {
    activeOnly: false,
    ...(requestedCatalogLocationId === undefined ? {} : { locationId: requestedCatalogLocationId }),
    q: searchTerm.length > 0 ? searchTerm : undefined,
    category: category.length > 0 ? category : undefined,
    shelfTier: shelfTier.length > 0 ? shelfTier : undefined,
    itemKind: itemKind.length > 0 ? itemKind : undefined,
  });
  if (!isCurrent()) return;
  if (result.status !== 200) {
    bottleListEl.innerHTML = `<p class="tabs-empty">Could not load inventory items (${result.body.message ?? result.body.error ?? result.status}).</p>`;
    adminToast(
      `Could not load inventory items. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`,
      "danger",
    );
    return;
  }
  // SET15: set only on success; see loadDrinks's own comment above.
  bottlesLoaded = true;
  bottleListVendorOfferWrites = vendorOfferWritesAtRequest;
  f3SellingUi.inventoryContext = result.body.sellingContext;
  bottleCatalogLoadedLocationId = requestedCatalogLocationId;
  adminLiquorProducts = [...result.body.liquorProducts].sort((a, b) =>
    compareNames(a.name, b.name),
  );
  bottleListReady = true;
  renderBottleList();
}

// Owner alpha-review feedback, requirement 3: an empty result under an
// active filter or search says plainly what was searched for ("No
// bourbon bottles on the Top Shelf yet"), never a bare "No bottles
// found." with no explanation of why the list is empty. Only the
// qualifiers actually in effect are named, so an unfiltered empty tenant
// still gets the plain original message.
function bottleListEmptyMessage(): string {
  const searchTerm = bottleSearchInput.value.trim();
  const hasCategory = bottleFilterCategory.length > 0;
  const hasShelfTier = bottleFilterShelfTier.length > 0;
  const hasSearch = searchTerm.length > 0;
  if (!hasCategory && !hasShelfTier && !hasSearch) {
    return "No inventory items for this location yet.";
  }
  const baseSubject = hasCategory ? `${categoryLabel(bottleFilterCategory)} items` : "items";
  const subject = baseSubject;
  const shelfPhrase =
    bottleFilterShelfTier === "none"
      ? " with no shelf assigned"
      : hasShelfTier
        ? ` on the ${shelfTierDisplayName(bottleFilterShelfTier)}`
        : "";
  const searchPhrase = hasSearch ? ` matching "${searchTerm}"` : "";
  return `No ${subject}${shelfPhrase}${searchPhrase} yet.`;
}

// INV26 Slice 1 (docs/build-briefs/INV26-backbar-inventory.md; DEC-206
// item 2, DEC-207) REPLACES this section's old row-button rendering
// entirely, per the brief's own text: "The admin list UI ... renders five
// columns only: Item, Package, On hand ..., Par, Status ... No type or
// subtype column". That retires the old per-row category/price detail
// line, item-kind chip, and shelf-tier chip (bottleRowPriceText and the
// two chip blocks this replaces), matching the owner's DEC-206 ruling
// ("We dont use type and subtype but you get the idea from the
// screenshot") and design/mockups/inv26-inventory-backbar.html's own
// Artboard 1, which the owner approved (DEC-207: "Good mockup design.
// Approved."). The shelf-tier and category FILTER chips above the list
// (admin-bottle-filters) are unchanged; only the per-row display and the
// row's own five data cells are new.
//
// TF2's Active/Disabled grouping (below) is preserved rather than
// dropped: nothing in DEC-206/DEC-207 rescinds that owner ruling, and
// this brief's own "no type or subtype columns" is about the five data
// columns, not about whether a deactivated item still gets its own
// section. The old per-row "Active"/"Inactive" status pill is retired
// as redundant now that every row in a group already sits under a
// heading that says which group it is in, freeing the Status column
// for its new job (the quantityState/flagged86-derived OK/Low/Out/
// Negative chip DEC-206 item 2 actually asks for). Sorting is applied
// WITHIN each group (Active rows sorted among themselves, then Disabled
// rows sorted among themselves), never across the Active/Disabled
// boundary, so "sortable by any column, one active sort" and "disabled
// items sink to their own section" both hold at once.

/** The Package column's display text: "750 ml" (a sized container, size
 * only, no container word: the On hand column's own unit, e.g. "2.5
 * bottles", already carries it -- see design/mockups/inv26-list-fit.html's
 * own header comment), "1/2 barrel" (a keg, size label alone, whose size
 * lives in the default_keg_size vocabulary word rather than
 * size_value/size_unit), or an em dash when neither resolves. A container
 * with no size (a bare can/other with no size_value/size_unit) still
 * falls back to its lowercase container word, matching the mockup
 * (containerTypeLabel from catalogDisplay.ts is capitalized for the edit
 * form's own picker labels; this is a separate, list-only rendering, not
 * a reuse of that constant). */
const INVENTORY_LIST_CONTAINER_WORD: Readonly<Record<string, string>> = {
  bottle: "bottle",
  can: "can",
  keg: "keg",
  other: "container",
};

function inventoryPackageDisplayText(bottle: LiquorProductDto): string {
  if (bottle.container_type === "keg") {
    const kegLabel =
      bottle.default_keg_size !== null
        ? ((adminVocabularies["keg_size"] ?? []).find(
            (entry) => entry.value === bottle.default_keg_size,
          )?.label ?? bottle.default_keg_size)
        : null;
    return kegLabel !== null ? kegLabel : "Keg";
  }
  if (bottle.size_value !== null && bottle.size_unit !== null) {
    return formatSizeValueUnit(bottle.size_value, bottle.size_unit);
  }
  if (bottle.container_type !== null) {
    return INVENTORY_LIST_CONTAINER_WORD[bottle.container_type] ?? bottle.container_type;
  }
  return "—";
}

/** STK1 (DEC-269), moved by UX80 (DEC-298): the Stock column belongs to the Stock
 * levels screen only, and only for a session holding inv.stock_flag.record. The
 * Settings item catalog has no on hand, status or 86 columns. */
function stockLevelsHasStockColumn(): boolean {
  return canRecordStockFlag();
}

/** INVUX1 (finding 18): the selling availability, said as a state beside its action, in the item editor's own words. It is
 * read from the manual 86 flag alone and never from on hand, so "Out" in the Status column cannot read as "86'd" here. */
function buildAvailabilityState(flagged86: boolean, testid: string): HTMLElement {
  const state = document.createElement("span");
  state.className = flagged86 ? "inv-avail-state inv-avail-state--86" : "inv-avail-state";
  state.dataset["testid"] = testid;
  state.textContent = flagged86 ? "86'd" : "Not 86'd";
  return state;
}

/** The Stock cell: one 44px button, "Mark 86" or "Clear 86". It stops
 * the click (and Enter/Space) from reaching the row, whose own action opens
 * the item's stock sheet. Same write path as the floor, with the same Undo
 * toast. */
function buildInventoryStockCell(bottle: LiquorProductDto): HTMLElement {
  const cell = document.createElement("td");
  cell.className = "inv-col-stock";
  const button = document.createElement("button");
  button.type = "button";
  button.dataset["testid"] = "admin-stock-button";
  button.className = bottle.flagged86 ? "inv-stock-btn inv-stock-btn--back" : "inv-stock-btn";
  button.textContent = bottle.flagged86 ? "Clear 86" : "Mark 86";
  button.setAttribute("aria-label", `${button.textContent}: ${bottle.name}`);
  const swallow = (event: Event): void => event.stopPropagation();
  button.addEventListener("keydown", swallow);
  button.addEventListener("click", (event) => {
    event.stopPropagation();
    void setBottleStockFlag(bottle, !bottle.flagged86);
  });
  cell.append(buildAvailabilityState(bottle.flagged86, "admin-stock-availability"), button);
  return cell;
}

/** Sets or clears one bottle's 86 from Stock levels through the shared floor
 * write path. Keeps this screen's own copy (the list row and an open
 * editor's) in step, optimistically, and puts it back on a refusal. */
async function setBottleStockFlag(bottle: LiquorProductDto, flagged: boolean): Promise<boolean> {
  const sync = (value: boolean): void => {
    for (const list of [adminLiquorProducts, stockLevelItems]) {
      const own = list.find((candidate) => candidate.id === bottle.id);
      if (own !== undefined) {
        own.flagged86 = value;
      }
    }
    bottle.flagged86 = value;
    renderStockLevelsList();
    refreshBottleStockControl();
  };
  return setStockFlag({
    productId: bottle.id,
    name: bottle.name,
    flagged,
    onLocalChange: sync,
  });
}

/** Parses a nullable/undefined NUMERIC(12,4) string into a number, never
 * NaN: an unparseable value (should not occur -- Postgres always returns
 * a valid numeric string here) reads as null/unresolved rather than
 * fabricating 0, matching CLAUDE.md's "never fall back to a hardcoded
 * figure". */
function parseOptionalQuantity(value: string | null | undefined): number | null {
  if (value === null || value === undefined) {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** The Settings inventory list's own on-hand text: rounded to tenths with
 * the count unit's plural word, e.g. "2.5 bottles" -- reuses INV20's
 * existing roundQuantityToOneDecimalPlace/pluralizeCountUnitLabel (the
 * item-detail on-hand figure, formatOnHandFigure, ~11440 below) but
 * WITHOUT that function's own parenthetical size, since the Package
 * column already shows the size for this row. An em dash when the
 * server could not resolve a count unit (bottle.onHandInCountUnit null:
 * no size/keg configuration yet, or a genuinely mixed-unit ledger),
 * never "0" or another invented figure. */
function inventoryOnHandDisplayText(bottle: LiquorProductDto): string {
  if (bottle.onHandInCountUnit === null || bottle.onHandInCountUnit === undefined) {
    return "—";
  }
  const unit = bottle.countUnit ?? "oz";
  const rounded = roundQuantityToOneDecimalPlace(bottle.onHandInCountUnit);
  return `${rounded} ${pluralizeCountUnitLabel(unit, rounded)}`;
}

interface InventoryListRowKeys extends InventoryListSortKeys {
  bottle: LiquorProductDto;
}

/** MGRDAYMAJ1 (manager-day-12): whether Stock levels should say "No starting stock yet" for this item rather than Out. Only for
 * this tablet's own location, which is the one the status read describes. */
function hasNoStartingStock(bottle: LiquorProductDto): boolean {
  const siteLocation = shownStockLocation();
  if (siteLocation !== undefined && !siteLocation.isThisTablet) return false;
  return noStartingStockIds().has(bottle.id);
}

function inventorySortKeysFor(bottle: LiquorProductDto): InventoryListRowKeys {
  const onHand = parseOptionalQuantity(bottle.onHandInCountUnit);
  const status = inventoryListStatus(
    bottle.quantityState,
    onHand,
    bottle.flagged86,
    !hasNoPar(bottle),
    parseOptionalQuantity(bottle.par_level),
    hasNoStartingStock(bottle),
  );
  return {
    bottle,
    itemName: bottle.name,
    packageAmount: packageAmountMl(bottle.size_value, bottle.size_unit),
    onHand,
    par: parseOptionalQuantity(bottle.par_level),
    statusRank: inventoryListStatusRank(status),
  };
}

/** TF2: the heading above each of the two groups. Rendered only when both
 * groups have something in them, so a location with nothing disabled sees
 * exactly the flat list it saw before this change. A `<tr>` with one
 * colspanning cell, since the list is a `<table>`. */
/** The two renderings of the inventory list (UX80, DEC-298). "catalog" is the
 * Settings item list: Item, Package, Par. "stock" is the Stock levels list: Item,
 * Package, On hand, Par, Status and the 86 button. Their test ids differ
 * (admin-bottle-* and admin-stock-*) because both lists can be in the page at
 * once and a test id has to name one element. */
type InventoryListMode = "catalog" | "stock";

interface InventoryListTestIds {
  readonly row: string;
  readonly name: string;
  readonly package: string;
  readonly onHand: string;
  readonly par: string;
  readonly status: string;
  readonly sortPrefix: string;
  readonly table: string;
  readonly groupActive: string;
  readonly groupDisabled: string;
}

const INVENTORY_LIST_TEST_IDS: Record<InventoryListMode, InventoryListTestIds> = {
  catalog: {
    row: "admin-bottle-row",
    name: "admin-bottle-name-text",
    package: "admin-bottle-package",
    onHand: "admin-bottle-onhand",
    par: "admin-bottle-par",
    status: "admin-bottle-status",
    sortPrefix: "admin-bottle-sort-",
    table: "admin-bottle-list-table",
    groupActive: "admin-bottle-group-active",
    groupDisabled: "admin-bottle-group-disabled",
  },
  stock: {
    row: "admin-stock-row",
    name: "admin-stock-name-text",
    package: "admin-stock-package",
    onHand: "admin-stock-onhand",
    par: "admin-stock-par",
    status: "admin-stock-status",
    sortPrefix: "admin-stock-sort-",
    table: "admin-stock-list-table",
    groupActive: "admin-stock-group-active",
    groupDisabled: "admin-stock-group-disabled",
  },
};

/** The sortable columns, in display order, per mode. */
function inventoryListColumnCount(mode: InventoryListMode): number {
  const base = INVENTORY_LIST_COLUMNS[mode].length;
  // Catalog: Auto par, Used per week and Lock, then Vendor, Shelf tier and Status (BULKEDIT1), and the checkbox when drawn.
  return mode === "catalog" ? base + 6 + (bulkEditMayUseAny() ? 1 : 0) : stockLevelsHasStockColumn() ? base + 1 : base;
}

function inventoryListSortFor(mode: InventoryListMode): InventoryListSortState {
  if (mode === "catalog" && !catalogStockIsCurrent() && (bottleListSort.column === "onHand" || bottleListSort.column === "par")) return { column: "item", direction: "asc" };
  return mode === "catalog" ? bottleListSort : stockLevelsSort;
}

function catalogStockIsCurrent(): boolean {
  const activeSession = sessionOrUndefined();
  const selectedLocationId = autoParSection.inventoryLocationId();
  return activeSession !== undefined && selectedLocationId !== undefined && hasPermission(activeSession, "inv.stock_state.view") && bottleCatalogLoadedLocationId === selectedLocationId;
}

/** Tablet-portrait width and below (the settings shell's own breakpoint): the Par cell says "No par", not "No par set". */
const NARROW_ADMIN_QUERY = "(max-width: 900px)";
function stockListIsNarrow(): boolean {
  return window.matchMedia(NARROW_ADMIN_QUERY).matches;
}
window.matchMedia(NARROW_ADMIN_QUERY).addEventListener("change", () => {
  if (stockLevelItems.length > 0 && !panelStockLevels.hidden) renderStockLevelsList();
});

/** One row of either list. The row's own action opens the item editor from the
 * catalog and the item's stock sheet from Stock levels. */
function buildInventoryListRow(
  bottle: LiquorProductDto,
  mode: InventoryListMode,
): HTMLElement {
  const ids = INVENTORY_LIST_TEST_IDS[mode];
  const catalogStats =
    mode === "catalog" && catalogStockIsCurrent()
      ? autoParSection.inventoryRow(bottle.id)
      : undefined;
  const mayReadCatalogStock =
    mode === "catalog" &&
    catalogStockIsCurrent() &&
    bottleSelling(bottle)?.trackStock === true;
  const onHandNumber = parseOptionalQuantity(bottle.onHandInCountUnit);
  const extraCells: HTMLElement[] = [];
  if (mode === "catalog") {
    const metrics = mayReadCatalogStock
      ? autoParSection.inventoryCells(bottle.id)
      : undefined;
    const unavailable =
      bottleSelling(bottle)?.trackStock !== true
        ? "This item is not stock tracked."
        : !catalogStockIsCurrent()
          ? "Inventory data is unavailable for the current location or access."
          : "Automatic par data is unavailable.";
    extraCells.push(...createInventoryMetricCells(metrics, unavailable));
  }

  if (mode === "stock") {
    const statusCell = document.createElement("td");
    const status = inventoryListStatus(
      bottle.quantityState,
      onHandNumber,
      bottle.flagged86,
      !hasNoPar(bottle),
      parseOptionalQuantity(bottle.par_level),
      hasNoStartingStock(bottle),
    );
    const statusChip = document.createElement("span");
    statusChip.dataset["testid"] = ids.status;
    if (status === null) {
      statusChip.className = "inv-status-chip";
      statusChip.textContent = "—";
    } else {
      statusChip.className = `inv-status-chip inv-status-${status}`;
      statusChip.textContent = INVENTORY_LIST_STATUS_LABEL[status];
    }
    statusCell.append(statusChip);
    extraCells.push(statusCell);
    if (stockLevelsHasStockColumn()) {
      extraCells.push(buildInventoryStockCell(bottle));
    }
  }

  // BULKEDIT1: the Items list shows what a bulk action would change: Vendor, Shelf tier and Status, after the item.
  const selectCell = mode === "catalog" ? buildBulkEditSelectCell(bottle) : undefined;
  const midCells = mode === "catalog" ? buildCatalogMidCells(bottle) : undefined;
  const builtRow = createInventoryRow({
    ...(selectCell === undefined ? {} : { selectCell }),
    ...(midCells === undefined ? {} : { midCells }),
    mode,
    ids,
    productId: bottle.id,
    name: bottle.name,
    stopped: bottle.stop_ordering === true,
    flagged: bottle.flagged86 && mode === "stock",
    packageText: inventoryPackageDisplayText(bottle),
    sizeNeedsConfirmation: bottle.size_needs_confirmation === true,
    negative: onHandNumber !== null && onHandNumber < 0,
    onHand:
      mode === "stock"
        ? inventoryOnHandDisplayText(bottle)
        : !mayReadCatalogStock
          ? "–"
          : catalogStats === undefined
            ? bottle.onHandInCountUnit == null || bottle.countUnit == null
              ? "–"
              : inventoryOnHandDisplayText(bottle)
            : catalogStats.onHand === null || catalogStats.countUnit === null
              ? "–"
              : inventoryOnHandDisplayText({
                  ...bottle,
                  onHandInCountUnit: catalogStats.onHand,
                  countUnit: catalogStats.countUnit,
                }),
    par:
      mode === "stock"
        ? parCellText(bottle, stockListIsNarrow())
        : mayReadCatalogStock
          ? catalogStats === undefined
            ? bottle.par_level == null
              ? "–"
              : autoParFigure(bottle.par_level)
            : catalogStats.parNow === null
              ? "–"
              : autoParFigure(catalogStats.parNow)
          : "–",
    noPar: mode === "stock" && hasNoPar(bottle),
    extraCells,
    openRow: () => {
      if (mode === "catalog") openEditBottleForm(bottle);
      else openStockDetail(bottle);
    },
  });
  if (mode === "catalog" && bulkEditSelected.has(bottle.id)) builtRow.classList.add("inv-row--selected");
  return builtRow;
}

/** BULKEDIT1: the Vendor, Shelf tier and Status cells of one Items row. */
function buildCatalogMidCells(bottle: LiquorProductDto): HTMLElement[] {
  const textCell = (text: string, testid: string): HTMLElement => {
    const cell = document.createElement("td");
    cell.className = "inv-col-text";
    cell.dataset["testid"] = testid;
    cell.textContent = text;
    return cell;
  };
  const vendor = textCell(bottle.preferredVendorName === undefined ? "–" : (bottle.preferredVendorName ?? "Not set"), "admin-bottle-row-vendor");
  const tier = bottleSelling(bottle)?.shelfTier ?? bottle.shelf_tier;
  const shelf = textCell(tier === null || tier === undefined || tier === "" ? "Not set" : shelfTierDisplayName(tier), "admin-bottle-row-shelf-tier");
  const statusCell = document.createElement("td");
  statusCell.className = "inv-col-text";
  const chip = document.createElement("span");
  chip.dataset["testid"] = "admin-bottle-state";
  const active = bottleSelling(bottle)?.active === true;
  if (bottle.flagged86) {
    chip.className = "inv-status-chip inv-status-flagged86";
    chip.textContent = "86'd";
  } else if (active) {
    chip.className = "inv-status-chip inv-status-ok";
    chip.textContent = "Active";
  } else {
    chip.className = "inv-status-chip";
    chip.textContent = "Inactive";
  }
  statusCell.append(chip);
  return [vendor, shelf, statusCell];
}

/** BULKEDIT1: the Items list's sortable columns, narrowed to make room for Vendor, Shelf tier, Status and the checkbox. */
function catalogHeadColumns(): typeof INVENTORY_LIST_COLUMNS.catalog {
  const widths: Record<string, string> = { item: bulkEditMayUseAny() ? "21%" : "25%", onHand: "9%", par: "7%" };
  return INVENTORY_LIST_COLUMNS.catalog.map((entry) => ({ ...entry, width: widths[entry.column] ?? entry.width }));
}

/** Builds the sortable `<thead>`, matching the mockup's own aria-sort/caret
 * affordance: the active column's header carries aria-sort ("ascending" or
 * "descending") and a solid caret pointing the active direction; every other
 * header carries aria-sort="none" and a quiet double-caret, so the whole row
 * reads as sortable before anything is tapped. A real <button> per header,
 * clicking it advances the sort via nextInventoryListSort and re-renders. */
function buildInventoryListHead(mode: InventoryListMode): HTMLElement {
  return createInventoryHead({
    mode,
    ids: INVENTORY_LIST_TEST_IDS[mode],
    sort: inventoryListSortFor(mode),
    withStock: mode === "stock" && stockLevelsHasStockColumn(),
    stockUnavailable: !catalogStockIsCurrent(),
    columns: mode === "catalog" ? catalogHeadColumns() : INVENTORY_LIST_COLUMNS[mode],
    ...(mode === "catalog"
      ? {
          midHeads: [
            { label: "Vendor", width: "14%" },
            { label: "Shelf tier", width: "10%" },
            { label: "Status", width: "10%" },
          ],
          metricWidths: { auto: "11%", used: "8%", lock: "6%" },
          ...(bulkEditSelectAllModel() === undefined ? {} : { selectAll: { width: "4%", ...bulkEditSelectAllModel()! } }),
        }
      : {}),
    onSort: (column) => {
      if (mode === "catalog") {
        bottleListSort = nextInventoryListSort(bottleListSort, column);
        renderBottleList();
      } else {
        stockLevelsSort = nextInventoryListSort(stockLevelsSort, column);
        renderStockLevelsList();
      }
    },
  });
}

/** Sorts `bottles` by the mode's own sort state, using the pure comparator in
 * ui/src/inventoryListSort.ts. */
function sortedInventoryListRows(
  bottles: readonly LiquorProductDto[],
  mode: InventoryListMode,
): LiquorProductDto[] {
  const sort = inventoryListSortFor(mode);
  const keyed = bottles.map((bottle) => {
    const stats = mode === "catalog" && catalogStockIsCurrent() ? autoParSection.inventoryRow(bottle.id) : undefined;
    return inventorySortKeysFor(stats === undefined ? bottle : { ...bottle, par_level: stats.parNow, onHandInCountUnit: stats.onHand, countUnit: stats.countUnit });
  });
  return sortInventoryListRows(keyed, sort.column, sort.direction).map((row) => row.bottle);
}

/** Draws one list into `container`: active items first, then everything
 * disabled under its own heading at the bottom (TF2, owner testing feedback
 * 2026-08-28), the current sort applied within each group. `emptyMessage` is
 * the plain sentence for an empty result. Grouping is not a filter: a filtered
 * view is grouped the same way. */
function renderInventoryTable(
  container: HTMLElement,
  bottles: readonly LiquorProductDto[],
  mode: InventoryListMode,
  emptyMessage: string,
): void {
  const ids = INVENTORY_LIST_TEST_IDS[mode];
  container.innerHTML = "";
  if (bottles.length === 0) {
    const empty = document.createElement("p");
    empty.className = "tabs-empty";
    empty.dataset["testid"] = mode === "catalog" ? "admin-bottle-empty" : "admin-stock-empty";
    empty.textContent = emptyMessage;
    container.append(empty);
    return;
  }
  const activeBottles = sortedInventoryListRows(
    bottles.filter((bottle) => bottleSelling(bottle)?.active === true),
    mode,
  );
  const disabledBottles = sortedInventoryListRows(
    bottles.filter((bottle) => bottleSelling(bottle)?.active !== true),
    mode,
  );
  const columnCount = inventoryListColumnCount(mode);

  const table=createInventoryTable(mode,ids.table,buildInventoryListHead(mode));
  const tbody = document.createElement("tbody");
  if (activeBottles.length > 0 && disabledBottles.length > 0) {
    tbody.append(
      buildBottleGroupHeadingRow("Active", activeBottles.length, ids.groupActive, columnCount),
    );
  }
  for (const bottle of activeBottles) {
    tbody.append(buildInventoryListRow(bottle, mode));
  }
  if (disabledBottles.length > 0) {
    tbody.append(
      buildBottleGroupHeadingRow(
        "Disabled",
        disabledBottles.length,
        ids.groupDisabled,
        columnCount,
      ),
    );
    for (const bottle of disabledBottles) {
      tbody.append(buildInventoryListRow(bottle, mode));
    }
  }
  table.append(tbody);
  if (mode === "catalog") hideEmptyInventoryColumns(table);
  container.append(table);
}

/** LOCSET1 (B13, style guide section 5): Auto par and Used per week are not drawn while every row of the list has nothing
 * to show in them (automatic par off, or no history yet). Nothing is removed: the cells stay in the table and return the
 * moment any row has a figure. */
function hideEmptyInventoryColumns(table: HTMLTableElement): void {
  for (const [cellClass, flag] of [
    ["inv-col-auto", "invHideAuto"],
    ["inv-col-used", "invHideUsed"],
  ] as const) {
    const cells = Array.from(table.querySelectorAll<HTMLElement>(`td.${cellClass}`));
    const empty = cells.length > 0 && cells.every((cell) => {
      const text = (cell.textContent ?? "").trim();
      return text === "" || text === "–" || text === "-";
    });
    if (empty) table.dataset[flag] = "true";
  }
}

/** The Settings item catalog (UX80: Item, Package, Par). */
function renderBottleList(): void {
  if (!bottleListReady) return;
  const activeSession = sessionOrUndefined();
  if (activeSession === undefined || !hasPermission(activeSession, "admin.liquor_product.view")) return;
  const rows = adminLiquorProducts.filter((bottle) => autoParSection.inventoryMatchesVendor(bottle.id));
  renderInventoryTable(bottleListEl, rows, "catalog", rows.length === 0 && autoParSection.inventoryPending() ? "Loading inventory…" : rows.length === 0 && adminLiquorProducts.length > 0 ? "No matching items." : bottleListEmptyMessage());
  decorateBulkEditSelection(rows);
}

// ---------------------------------------------------------------------
// BULKEDIT1 (docs/build-briefs/BULKEDIT1-inventory-bulk-edit.md; BULKVENDOR1 before it): the checkbox column, the action strip and the
// four bulk actions of the Items list.
//
// The checkbox column is always drawn for a person allowed at least one action. Ticking items shows the sticky strip: Vendor, Shelf
// tier, Active / Inactive and 86 / Back in stock, each only for a person holding the permission the single-item edit of that field
// needs. The selection is pruned to the rows listed NOW every time the list is drawn, so a filter change can never leave an item
// selected that the person cannot see. "Select all shown" is the rows listed, active and disabled, never the whole catalog. Each
// button opens the sheet (bulkEditSheet.ts), which sends the SAME audited domain writes the item editor uses.
// ---------------------------------------------------------------------
const BULK_EDIT_KINDS: readonly BulkEditKind[] = ["vendor", "shelfTier", "active", "stock86"];

/** Whether the person may run this action: the permission its single-item edit needs. 86 is a floor act at this device's own
 * location (Mark 86), so it is offered only while the list shows that location. */
function bulkEditAllowed(kind: BulkEditKind): boolean {
  const activeSession = sessionOrUndefined();
  if (activeSession === undefined) return false;
  if (kind === "vendor") {
    return hasPermission(activeSession, "inv.vendor_offer.manage") && hasPermission(activeSession, "inv.vendor.view");
  }
  if (kind === "stock86") {
    const device = loadTerminalConfig()?.locationId;
    return canRecordStockFlag() && device !== undefined && device === autoParSection.inventoryLocationId();
  }
  return hasPermission(activeSession, "admin.liquor_product.manage");
}

function bulkEditMayUseAny(): boolean {
  return BULK_EDIT_KINDS.some((kind) => bulkEditAllowed(kind));
}

/** Prunes the selection to the rows listed now. Called every time the list is drawn. */
function pruneBulkEditSelection(rows: readonly LiquorProductDto[]): void {
  if (!bulkEditMayUseAny()) {
    bulkEditSelected = new Set<string>();
  }
  bulkEditShownIds = rows.map((bottle) => bottle.id);
  const shown = new Set(bulkEditShownIds);
  bulkEditSelected = new Set([...bulkEditSelected].filter((id) => shown.has(id)));
}

function bulkEditHeaderState(): { checked: boolean; indeterminate: boolean; disabled: boolean } {
  const count = bulkEditSelected.size;
  const shown = bulkEditShownIds.length;
  return { checked: shown > 0 && count === shown, indeterminate: count > 0 && count < shown, disabled: shown === 0 };
}

function setBulkEditSelection(ids: Iterable<string>): void {
  bulkEditSelected = new Set(ids);
  syncBulkEditBoxes();
  renderBulkEditStrip();
}

function decorateBulkEditSelection(rows: readonly LiquorProductDto[]): void {
  pruneBulkEditSelection(rows);
  renderBulkEditStrip();
}

/** The select-all-shown box of the table head, for the person allowed at least one action. */
function bulkEditSelectAllModel() {
  if (!bulkEditMayUseAny()) return undefined;
  return {
    ...bulkEditHeaderState(),
    onToggle: (checked: boolean) => setBulkEditSelection(checked ? bulkEditShownIds : []),
  };
}

/** The checkbox cell of one Items row. */
function buildBulkEditSelectCell(bottle: LiquorProductDto): HTMLElement | undefined {
  if (!bulkEditMayUseAny()) return undefined;
  const cell = document.createElement("td");
  cell.className = "inv-col-select";
  const box = document.createElement("input");
  box.type = "checkbox";
  box.className = "inv-select-box";
  box.dataset["testid"] = "admin-bulk-edit-check";
  box.setAttribute("aria-label", `Select ${bottle.name}`);
  box.checked = bulkEditSelected.has(bottle.id);
  box.addEventListener("change", () => {
    if (box.checked) bulkEditSelected.add(bottle.id);
    else bulkEditSelected.delete(bottle.id);
    box.closest("tr")?.classList.toggle("inv-row--selected", box.checked);
    syncBulkEditHeaderBox();
    renderBulkEditStrip();
  });
  cell.append(box);
  return cell;
}

function syncBulkEditHeaderBox(): void {
  const box = bottleListEl.querySelector<HTMLInputElement>('[data-testid="admin-bulk-edit-check-all"]');
  if (box === null) return;
  const state = bulkEditHeaderState();
  box.checked = state.checked;
  box.indeterminate = state.indeterminate;
  box.disabled = state.disabled;
}

function syncBulkEditBoxes(): void {
  for (const row of Array.from(bottleListEl.querySelectorAll<HTMLElement>("tr[data-liquor-product-id]"))) {
    const id = row.dataset["liquorProductId"] ?? "";
    const box = row.querySelector<HTMLInputElement>("input.inv-select-box");
    if (box !== null) box.checked = bulkEditSelected.has(id);
    row.classList.toggle("inv-row--selected", bulkEditSelected.has(id));
  }
  syncBulkEditHeaderBox();
}

function renderBulkEditStrip(): void {
  const count = bulkEditSelected.size;
  bulkEditStripEl.hidden = count === 0 || !bulkEditMayUseAny();
  bulkEditStripEl.replaceChildren();
  if (bulkEditStripEl.hidden) return;
  const label = document.createElement("span");
  label.className = "bv-strip-count";
  label.dataset["testid"] = "admin-bulk-edit-count";
  label.textContent = selectedLabel(count);
  bulkEditStripEl.append(label);
  for (const kind of BULK_EDIT_KINDS) {
    if (!bulkEditAllowed(kind)) continue;
    const button = document.createElement("button");
    button.type = "button";
    button.className = "fl-btn";
    button.dataset["testid"] = `admin-bulk-edit-${kind}`;
    button.textContent = BULK_EDIT_SPECS[kind].stripLabel;
    button.addEventListener("click", () => openBulkEditForSelection(kind));
    bulkEditStripEl.append(button);
  }
  const spacer = document.createElement("span");
  spacer.className = "bv-strip-spacer";
  const selectAll = document.createElement("button");
  selectAll.type = "button";
  selectAll.className = "fl-btn bv-strip-ghost";
  selectAll.dataset["testid"] = "admin-bulk-edit-select-all";
  selectAll.textContent = selectAllLabel(bulkEditShownIds.length);
  selectAll.disabled = count === bulkEditShownIds.length;
  selectAll.addEventListener("click", () => setBulkEditSelection(bulkEditShownIds));
  const clear = document.createElement("button");
  clear.type = "button";
  clear.className = "fl-btn bv-strip-ghost";
  clear.dataset["testid"] = "admin-bulk-edit-clear";
  clear.textContent = BULK_EDIT_COPY.clear;
  clear.addEventListener("click", () => setBulkEditSelection([]));
  bulkEditStripEl.append(spacer, selectAll, clear);
}

/** The choices of one action's sheet: the active vendors, the active shelf tiers, or the two fixed words. */
async function loadBulkEditOptions(
  kind: BulkEditKind,
  activeSession: Session,
): Promise<{ ok: true; options: { value: string; label: string }[] } | { ok: false; message: string }> {
  if (kind === "vendor") {
    const result = await listVendors(activeSession.apiBaseUrl, activeSession.tenantId, { activeOnly: true });
    if (result.status !== 200) {
      return { ok: false, message: capitalizeServerMessage(result.body.message ?? "Check your connection.") };
    }
    return {
      ok: true,
      options: result.body.vendors
        .map((vendor) => ({ value: vendor.id, label: vendor.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    };
  }
  if (kind === "shelfTier") {
    return {
      ok: true,
      options: resolveShelfTiers()
        .filter((tier) => tier.active)
        .map((tier) => ({ value: tier.tier, label: tier.name })),
    };
  }
  return { ok: true, options: [...(BULK_EDIT_FIXED_OPTIONS[kind] ?? [])] };
}

function openBulkEditForSelection(kind: BulkEditKind): void {
  const activeSession = sessionOrUndefined();
  if (activeSession === undefined || !bulkEditAllowed(kind) || bulkEditSelected.size === 0) return;
  const locationId = autoParSection.inventoryLocationId();
  const items = adminLiquorProducts
    .filter((bottle) => bulkEditSelected.has(bottle.id))
    .map((bottle) => ({ id: bottle.id, name: bottle.name }));
  const gen = loadGeneration;
  const where = locationId === undefined ? {} : { locationId };
  openBulkEditSheet({
    spec: BULK_EDIT_SPECS[kind],
    items,
    locationName: autoParSection.inventoryLocationName(),
    loadOptions: () => loadBulkEditOptions(kind, activeSession),
    applyBatch: async (value, itemIds) => {
      if (!loadIsCurrent(gen)) return { ok: false, message: "The session changed." };
      const result =
        kind === "vendor"
          ? await bulkSetItemVendor(activeSession.apiBaseUrl, activeSession.tenantId, {
              vendorId: value,
              ...where,
              liquorProductIds: itemIds,
            })
          : kind === "shelfTier"
            ? await bulkEditItems(activeSession.apiBaseUrl, activeSession.tenantId, {
                field: "shelfTier",
                shelfTier: value,
                ...where,
                liquorProductIds: itemIds,
              })
            : kind === "active"
              ? await bulkEditItems(activeSession.apiBaseUrl, activeSession.tenantId, {
                  field: "active",
                  active: value === "true",
                  ...where,
                  liquorProductIds: itemIds,
                })
              : await bulkEditItems(activeSession.apiBaseUrl, activeSession.tenantId, {
                  field: "stock86",
                  flagged: value === "true",
                  ...where,
                  liquorProductIds: itemIds,
                });
      if (result.status !== 200 || result.body.changed === undefined) {
        return { ok: false, message: capitalizeServerMessage(result.body.message ?? "Check your connection.") };
      }
      return { ok: true, result: result.body as BulkSetVendorResultDto };
    },
    onClosed: ({ touched, failedIds }) => {
      if (!loadIsCurrent(gen)) return;
      bulkEditSelected = new Set(failedIds);
      if (touched) {
        void autoParSection.refresh();
        void loadBottles();
      } else {
        syncBulkEditBoxes();
        renderBulkEditStrip();
      }
    },
  });
}

// ---------------------------------------------------------------------
// UX80 (DEC-298): Stock levels
// ---------------------------------------------------------------------
//
// The operational half of the old Inventory list: on hand, status, the 86
// button, and a sheet per item with On hand, Record movement and the Recent
// movements list (with Reverse). It loads and filters on its own; the sheet
// reuses the movement code that used to sit in the item editor, unchanged.

function renderStockFilterChips(): void {
  renderBottleFilterChipGroup(
    stockLevelsFilterEl,
    "admin-stock-filter",
    "admin-stock-filter-option",
    [
      { value: "", label: "All" },
      { value: "low", label: "Low or out" },
      { value: "86", label: "86'd only" },
      // INVLOC1 Slice 2 (DEC-326): the active items with no par at the shown location.
      { value: "noPar", label: "No par set" },
    ],
    stockLevelsFilter,
    (value) => {
      stockLevelsFilter = value === "low" || value === "86" || value === "noPar" ? value : "";
      renderStockLevelsList();
    },
  );
}

async function loadStockLevels(): Promise<void> {
  const gen = loadGeneration;
  const s = session();
  setStockListEmpty(false);
  if (!hasPermission(s, "admin.liquor_product.view")) {
    stockLevelsListEl.innerHTML =
      '<p class="tabs-empty">You do not have permission to view this.</p>';
    return;
  }
  stockLevelsListEl.innerHTML = '<p class="tabs-loading">Loading stock…</p>';
  // INVLOC1 Slice 2 (DEC-326): the locations first, so the list can name the one it shows and ask for a chosen location. A failed
  // locations read leaves the list for this tablet's own location, exactly as before, without a header.
  const locationsResult = await getPlanningLocations(s.apiBaseUrl, s.tenantId);
  if (!loadIsCurrent(gen)) return;
  planningLocations = locationsResult.status === 200 ? locationsResult.body.locations : [];
  const shownLocation = shownStockLocation();
  const result = await listLiquorProducts(s.apiBaseUrl, s.tenantId, {
    activeOnly: false,
    // Omitted for this tablet's own location (the server's default), named only for another location the person may plan for.
    ...(shownLocation !== undefined && !shownLocation.isThisTablet
      ? { locationId: shownLocation.id }
      : {}),
  });
  if (!loadIsCurrent(gen)) return;
  if (result.status !== 200) {
    stockLevelsListEl.innerHTML = `<p class="tabs-empty">Could not load stock levels (${result.body.message ?? result.body.error ?? result.status}).</p>`;
    adminToast(
      `Could not load stock levels. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`,
      "danger",
    );
    return;
  }
  stockLevelItems = [...result.body.liquorProducts].sort((a, b) => compareNames(a.name, b.name));
  // HIGFIX4A (F-23): decided here, from a list that really loaded, so a screen that cannot read the list never hides the callout.
  setStockListEmpty(
    stockLevelItems.length === 0 && stockLevelsSearchInput.value.trim().length === 0 && stockLevelsFilter === "",
  );
  renderStockLevelsList();
  // CORE7-F1: the drinks and ingredients follow the items. A failed read is shown as N/A, never as an empty list.
  if (canRecordStockFlag()) {
    void loadMenuStock86().then((ok) => {
      if (!loadIsCurrent(gen)) return;
      menu86LoadState = ok ? "loaded" : "failed";
      renderStockLevelsList();
    });
  }
}

/** The location the Stock list is showing: the chosen one if the person may still plan for it, else this tablet's (the first). */
function shownStockLocation(): PlanningLocationDto | undefined {
  return (
    planningLocations.find((siteLocation) => siteLocation.id === stockLevelsLocationId) ??
    planningLocations[0]
  );
}

/** Mockups 4a and 4b: the location the par and on hand are for, the switch (only with more than one location), and the note for
 * a location that has no pars for most items. */
function renderStockLocationHeader(): void {
  const siteLocation = shownStockLocation();
  // INV38 (owner, 2026-10-09): the "Par and on hand for <location> only" line is gone. The header now holds only the
  // location switch, so it is hidden outright (no blank space) unless the person may plan for more than one location.
  const showSwitch = locationSwitchVisible(planningLocations);
  stockLocationHeaderEl.hidden = !showSwitch;
  stockLocationSwitchEl.hidden = !showSwitch;
  stockLocationSwitchEl.innerHTML = "";
  if (showSwitch) {
    for (const option of sortByName(planningLocations, (listed) => listed.name, {
      pinned: (listed) => listed.isThisTablet,
    })) {
      const button = document.createElement("button");
      button.type = "button";
      button.setAttribute("role", "radio");
      button.setAttribute("aria-checked", option.id === siteLocation?.id ? "true" : "false");
      button.dataset["testid"] = `admin-stock-location-option-${option.id}`;
      button.textContent = option.name;
      button.addEventListener("click", () => {
        if (option.id === shownStockLocation()?.id) return;
        stockLevelsLocationId = option.id;
        void loadStockLevels();
      });
      stockLocationSwitchEl.append(button);
    }
  }
  const showNote = siteLocation !== undefined && showNoParNote(planningLocations, stockLevelItems);
  stockNoParNoteEl.hidden = !showNote;
  if (showNote && siteLocation !== undefined) {
    const copy = noParNoteCopy(siteLocation.name);
    stockNoParNoteTitleEl.textContent = copy.title;
    stockNoParNoteBodyEl.textContent = copy.body;
  }
}

function stockLevelsEmptyMessage(searchTerm: string): string {
  if (stockLevelsFilter === "noPar" && searchTerm.length === 0) {
    return "Every item has a par at this location.";
  }
  if (stockLevelsFilter === "86" && searchTerm.length === 0) {
    return "Nothing is 86'd right now.";
  }
  if (stockLevelsFilter === "low" && searchTerm.length === 0) {
    return "Nothing is low or out right now.";
  }
  if (searchTerm.length > 0) {
    return `No items match "${searchTerm}".`;
  }
  return "No inventory items for this location yet.";
}

function renderStockLevelsList(): void {
  const searchTerm = stockLevelsSearchInput.value.trim();
  const needle = searchTerm.toLowerCase();
  const shown = stockLevelItems.filter((bottle) => {
    if (needle.length > 0 && !bottle.name.toLowerCase().includes(needle)) {
      return false;
    }
    if (stockLevelsFilter === "86") {
      return bottle.flagged86;
    }
    if (stockLevelsFilter === "noPar") {
      return matchesNoParFilter(bottle);
    }
    if (stockLevelsFilter === "low") {
      const status = inventoryListStatus(
        bottle.quantityState,
        parseOptionalQuantity(bottle.onHandInCountUnit),
        bottle.flagged86,
        !hasNoPar(bottle),
        parseOptionalQuantity(bottle.par_level),
        hasNoStartingStock(bottle),
      );
      return status === "low" || status === "out" || status === "negative";
    }
    return true;
  });
  renderStockLocationHeader();
  const menuRows = stockMenuRowsShown(searchTerm);
  const menuHasRows =
    menuRows !== undefined && (menuRows.drinks.length > 0 || menuRows.ingredients.length > 0);
  if (shown.length === 0 && menuHasRows) {
    // The items are empty but a drink or ingredient matches: no "nothing here" sentence above it.
    stockLevelsListEl.innerHTML = "";
  } else {
    renderInventoryTable(stockLevelsListEl, shown, "stock", stockLevelsEmptyMessage(searchTerm));
  }
  renderStockMenuSections(menuRows);
}

// MGRDAYMAJ1 (manager-day-12): when the starting stock read lands or changes, the status words on the list change with it.
onNoStartingStockChanged(() => {
  if (stockLevelItems.length > 0) renderStockLevelsList();
});

/** CORE7-F1: the drinks and ingredients Stock levels lists after the items. Only for a session that may set an 86,
 * and only for this device's own location: the write is at the bound location, so another location's list must not offer it. */
function stockMenuRowsShown(
  searchTerm: string,
): { drinks: SettingsMenuRow[]; ingredients: SettingsMenuRow[] } | undefined {
  if (!canRecordStockFlag() || menu86LoadState !== "loaded") return undefined;
  const siteLocation = shownStockLocation();
  if (siteLocation !== undefined && !siteLocation.isThisTablet) return undefined;
  return settingsMenuRows(stock86MenuEntries(), { chip: stockLevelsFilter, query: searchTerm });
}

function buildMenuRowCell(text: string, className?: string): HTMLElement {
  const cell = document.createElement("td");
  if (className !== undefined) cell.className = className;
  cell.textContent = text;
  return cell;
}

/** One drink or ingredient row: Item, Package, On hand and Par are N/A (they have none), Status, and the Stock
 * button. Tapping the row opens the inline 86 panel. */
function buildStockMenuRow(row: SettingsMenuRow): HTMLElement {
  const tr = document.createElement("tr");
  tr.className = row.flagged86
    ? "admin-bottle-row inv-row inv-row--86"
    : "admin-bottle-row inv-row";
  tr.dataset["testid"] = "admin-stock-menu-row";
  tr.dataset["stock86Kind"] = row.kind;
  tr.dataset["stock86Key"] = row.key;
  tr.tabIndex = 0;
  const name = document.createElement("td");
  name.className = "inv-col-item";
  name.textContent = row.name;
  tr.append(name, buildMenuRowCell("-"), buildMenuRowCell("-"), buildMenuRowCell("-"));
  const status = document.createElement("td");
  const chip = document.createElement("span");
  chip.className = row.flagged86 ? "inv-status-chip inv-status-flagged86" : "inv-status-chip";
  chip.dataset["testid"] = "admin-stock-menu-status";
  chip.textContent = settingsMenuRowStatus(row);
  status.append(chip);
  tr.append(status);
  const cell = document.createElement("td");
  cell.className = "inv-col-stock";
  const action = settingsMenuRowAction(row);
  if (action.label === null) {
    // Inherited: cleared on the ingredient. Say which, as text; no button that could not do it.
    const via = document.createElement("span");
    via.className = "inv-stock-via";
    via.dataset["testid"] = "admin-stock-menu-via";
    via.textContent = `Via ${action.via}`;
    cell.append(via);
  } else {
    const button = document.createElement("button");
    button.type = "button";
    button.dataset["testid"] = "admin-stock-menu-button";
    button.className = action.flagged ? "inv-stock-btn" : "inv-stock-btn inv-stock-btn--back";
    button.textContent = action.label;
    button.setAttribute("aria-label", `${action.label}: ${row.name}`);
    button.addEventListener("keydown", (event) => event.stopPropagation());
    button.addEventListener("click", (event) => {
      event.stopPropagation();
      void setMenuRow86(row, action.flagged);
    });
    cell.append(buildAvailabilityState(!action.flagged, "admin-stock-menu-availability"), button);
  }
  tr.append(cell);
  const open = (): void => openMenu86Panel(row);
  tr.addEventListener("click", open);
  tr.addEventListener("keydown", (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      open();
    }
  });
  return tr;
}

function renderStockMenuSections(
  menuRows: { drinks: SettingsMenuRow[]; ingredients: SettingsMenuRow[] } | undefined,
): void {
  if (!canRecordStockFlag() || menu86LoadState === "idle") return;
  if (menu86LoadState === "failed") {
    const failed = document.createElement("p");
    failed.className = "tabs-empty";
    failed.dataset["testid"] = "admin-stock-menu-unavailable";
    failed.textContent = "Drinks and ingredients could not be loaded. Reload to try again.";
    stockLevelsListEl.append(failed);
    return;
  }
  if (menuRows === undefined) return;
  for (const [label, rows, testid] of [
    ["Drinks", menuRows.drinks, "admin-stock-menu-drinks"],
    ["Ingredients", menuRows.ingredients, "admin-stock-menu-ingredients"],
  ] as const) {
    if (rows.length === 0) continue;
    const table = document.createElement("table");
    table.className = "inv-table inv-table--menu";
    table.dataset["testid"] = testid;
    // The same column widths as the items table above, so the columns line up.
    const columns = document.createElement("colgroup");
    for (const width of ["25%", "13%", "14%", "12%", "14%", "22%"]) {
      const col = document.createElement("col");
      col.style.width = width;
      columns.append(col);
    }
    table.append(columns);
    const caption = document.createElement("caption");
    caption.className = "admin-bottle-group-heading";
    caption.textContent = `${label} (${rows.length})`;
    table.append(caption);
    const tbody = document.createElement("tbody");
    for (const row of rows) tbody.append(buildStockMenuRow(row));
    table.append(tbody);
    stockLevelsListEl.append(table);
  }
}

/** Sets or clears one drink's or ingredient's 86 from Settings through the shared floor write path. */
async function setMenuRow86(row: SettingsMenuRow, flagged: boolean): Promise<boolean> {
  return setMenuStockFlag({
    kind: row.kind === "drink" ? "menu_drink" : "ingredient",
    subjectId: row.subjectId,
    name: row.name,
    flagged,
    onLocalChange: () => {
      refreshMenu86Views();
    },
  });
}

/** Brings every Settings view of a drink's or ingredient's 86 in step: the Stock levels rows, the inline panel and
 * the drink editor's control. */
function refreshMenu86Views(): void {
  if (!panelStockLevels.hidden && menu86LoadState === "loaded") renderStockLevelsList();
  refreshMenu86Panel();
  refreshDrinkStockControl();
}

/** The current entry for an open row, read fresh so the panel never shows a state older than the list. */
function currentMenu86Row(open: SettingsMenuRow): SettingsMenuRow | undefined {
  return settingsMenuRows(stock86MenuEntries(), { chip: "", query: "" })[
    open.kind === "drink" ? "drinks" : "ingredients"
  ].find((row) => row.key === open.key);
}

function openMenu86Panel(row: SettingsMenuRow): void {
  menu86Open = row;
  menu86Panel.hidden = false;
  panelStockLevels.classList.add("admin-menu86-open");
  menu86TitleEl.textContent = row.name;
  refreshMenu86Panel();
}

/** Reads the history when the panel needs it and it is not current (first open, or after any write). One read at a
 * time; a failed read is not retried in a loop, the lines then say N/A. */
let menu86HistoryReading = false;
function ensureMenu86History(): void {
  if (menu86HistoryReading || stock86HistoryIsCurrent() || !canViewStock86History()) return;
  menu86HistoryReading = true;
  void ensureStock86History().then((ok) => {
    menu86HistoryReading = false;
    if (ok) refreshMenu86Panel();
  });
}

function closeMenu86Panel(): void {
  menu86Open = undefined;
  menu86Panel.hidden = true;
  panelStockLevels.classList.remove("admin-menu86-open");
}

function refreshMenu86Panel(): void {
  if (menu86Open === undefined) return;
  const row = currentMenu86Row(menu86Open);
  if (row === undefined) return;
  menu86InButton.setAttribute("aria-checked", row.own ? "false" : "true");
  menu86OutButton.setAttribute("aria-checked", row.own ? "true" : "false");
  const info = row.own ? menuEditorOutSinceText(stock86SinceFor(row.key)) : undefined;
  menu86SinceEl.hidden = info === undefined;
  menu86SinceEl.textContent = info ?? "";
  const withHistory = canViewStock86History();
  if (withHistory) ensureMenu86History();
  menu86HistoryHeadingEl.hidden = !withHistory;
  menu86HistoryEl.hidden = !withHistory;
  menu86HistoryEl.innerHTML = "";
  if (withHistory) {
    for (const line of stock86HistoryLinesFor(row.key)) {
      const item = document.createElement("li");
      item.textContent = line;
      menu86HistoryEl.append(item);
    }
  }
}

function handleMenu86Choice(flagged: boolean): void {
  if (menu86Open === undefined) return;
  const row = currentMenu86Row(menu86Open);
  if (row === undefined || row.own === flagged) return;
  void setMenuRow86(row, flagged);
}

menu86InButton.addEventListener("click", () => handleMenu86Choice(false));
menu86OutButton.addEventListener("click", () => handleMenu86Choice(true));
menu86CloseButton.addEventListener("click", closeMenu86Panel);

/** The drink editor's 86 block: shown for an existing drink, with inv.stock_flag.record. The read is made on
 * open, so a drink the list never showed (the editor is reached from Drinks) is still current. */
function refreshDrinkStockControl(): void {
  const drink = editingDrink;
  const visible = drink !== undefined && editingDrinkId !== undefined && canRecordStockFlag();
  drinkStockBlock.hidden = !visible;
  if (!visible || drink === undefined) return;
  const row = settingsMenuRows(stock86MenuEntries(), { chip: "", query: "" }).drinks.find(
    (candidate) => candidate.subjectId === drink.id,
  );
  if (row === undefined) {
    // Not read yet, or a drink this location does not sell: no control rather than a guess.
    drinkStockBlock.hidden = true;
    return;
  }
  drinkStockInButton.setAttribute("aria-checked", row.own ? "false" : "true");
  drinkStockOutButton.setAttribute("aria-checked", row.own ? "true" : "false");
  const info = row.own
    ? menuEditorOutSinceText(stock86SinceFor(stock86DrinkKey(drink.id)))
    : undefined;
  drinkStockSinceEl.hidden = info === undefined;
  drinkStockSinceEl.textContent = info ?? "";
}

function handleDrinkStockChoice(flagged: boolean): void {
  const drink = editingDrink;
  if (drink === undefined) return;
  const row = settingsMenuRows(stock86MenuEntries(), { chip: "", query: "" }).drinks.find(
    (candidate) => candidate.subjectId === drink.id,
  );
  if (row === undefined || row.own === flagged) return;
  void setMenuRow86(row, flagged);
}

drinkStockInButton.addEventListener("click", () => handleDrinkStockChoice(false));
drinkStockOutButton.addEventListener("click", () => handleDrinkStockChoice(true));

/** One item's stock, inline in Stock levels: On hand, Record movement, Recent
 * movements. It borrows editingBottleId/editingBottle (what the movement code
 * reads) while open, and gives them back when it closes. */
function openStockDetail(bottle: LiquorProductDto): void {
  editingBottleId = bottle.id;
  editingBottle = bottle;
  stockDetailTitleEl.textContent = bottle.name;
  stockDetailPanel.hidden = false;
  panelStockLevels.classList.add("admin-stock-detail-open");
  void loadBottleOnHand(bottle.id);
  void loadBottleMovements(bottle.id);
}

function closeStockDetail(options: { reload: boolean }): void {
  if (stockDetailPanel.hidden) {
    return;
  }
  stockDetailPanel.hidden = true;
  panelStockLevels.classList.remove("admin-stock-detail-open");
  editingBottleId = undefined;
  editingBottle = undefined;
  bottleOnHandRow.hidden = true;
  bottleOnHandWarningEl.hidden = true;
  recordMovementButton.hidden = true;
  bottleOnHandSnapshot = undefined;
  resetBottleMovements();
  // On hand may have changed (a movement, a reversal): show the new figures.
  if (options.reload && currentOperationsView === "stockLevels") {
    void loadStockLevels();
  }
}

stockLevelsSearchInput.addEventListener("input", () => renderStockLevelsList());
stockDetailCloseButton.addEventListener("click", () => closeStockDetail({ reload: true }));

function handleBottleSearchInput(): void {
  // Invalidate immediately, including while the next request is debounced.
  bottleListRequestSequence += 1;
  bottleListReady = false;
  bottleListEl.innerHTML = '<p class="tabs-loading">Loading bottles…</p>';
  if (bottleSearchDebounceTimer !== undefined) {
    clearTimeout(bottleSearchDebounceTimer);
  }
  bottleSearchDebounceTimer = setTimeout(() => {
    void loadBottles();
  }, 250);
}

function restoreSelectChoice(select: HTMLSelectElement, value: string): void {
  if (value !== "" && [...select.options].some((option) => option.value === value)) {
    select.value = value;
  }
}

function populateBottleCategorySelect(): void {
  bottleCategorySelect.innerHTML = "";
  bottleCategoryPicked = false;
  const offered = tenantLiquorCategories();
  // CT2: the same honest-retired-value merge CT1 gave container type and
  // SET7 gave the drink form's display category. Edit mode assigns
  // bottleCategorySelect.value = bottle.category after this runs, and a
  // <select> assignment with no matching option SILENTLY no-ops, so a
  // liquor bottle whose stored category was later retired would open
  // with no selection and a Save could silently change it. Liquor kind
  // only: the other kinds use their own select, populateBottleCategory-
  // OtherSelect below (CT3).
  const currentValue =
    editingBottle !== undefined && editingBottle.item_kind === "liquor"
      ? editingBottle.category
      : undefined;
  if (
    currentValue !== undefined &&
    currentValue.length > 0 &&
    !offered.some((entry) => entry.value === currentValue)
  ) {
    const storedEntry = (adminVocabularies["item_category"] ?? [])
      .filter((entry) => entry.itemKindScope === "liquor")
      .find((entry) => entry.value === currentValue);
    offered.push({ value: currentValue, label: storedEntry?.label ?? currentValue });
  }
  for (const category of sortOptions(offered)) {
    const option = document.createElement("option");
    option.value = category.value;
    option.textContent = category.label;
    bottleCategorySelect.append(option);
  }
}

// ITEM1 Slice 3 (DEC-147, DEC-153; migration 0111): every non-liquor kind
// now reads the ONE unified 'item_category' vocabulary, filtered to its
// own item_kind_scope, mirroring src/domain/catalog.ts's
// categorySourceForItemKind exactly (this browser bundle cannot import
// that server module directly, the same "hand-maintained mirror" shape
// ITEM_KINDS/CONTAINER_TYPES above already use). 'liquor' stays on
// bottleCategorySelect/populateBottleCategorySelect above, unchanged.
// 'packaged' resolves to a scope with ZERO rows, ever (DEC-153): its
// select ends up legitimately empty, not a bug.
function tenantItemCategories(itemKindScope: string): { value: string; label: string }[] {
  if ((adminVocabularies["item_category"] ?? []).length === 0) {
    return sortOptions([...(itemCategoriesByScopeFallback[itemKindScope] ?? [])]);
  }
  return sortOptions(
    (adminVocabularies["item_category"] ?? [])
      .filter((entry) => entry.active && entry.itemKindScope === itemKindScope)
      .map((entry) => ({ value: entry.value, label: entry.label })),
  );
}

// CT3, extended by ITEM1 Slice 3. Rebuilds the "other kinds" Category
// <select> from the CURRENTLY selected item kind's own scope (active
// values only), the same active-only + honest-retired-value-merge shape
// populateBottleCategorySelect above already uses for liquor. Called from
// resetBottleForm (kind 'liquor': a no-op, the group stays hidden) and
// from the item-kind chip's own change handler below, because unlike the
// liquor list this one genuinely depends on WHICH other kind is selected.
// Also restores this bottle's own stored value explicitly (`.value =
// currentValue`) when the kind being rendered is this bottle's own
// current kind, so switching the chip away and back (e.g. beer -> liquor
// -> beer while editing) does not silently rebuild the list onto its
// first option and leave a changed category for an unrelated Save to
// persist.
function populateBottleCategoryOtherSelect(): void {
  bottleCategoryOtherSelect.innerHTML = "";
  bottleCategoryPicked = false;
  if (bottleFormItemKind === "liquor") {
    return;
  }
  const offered = tenantItemCategories(bottleFormItemKind);
  const currentValue =
    editingBottle !== undefined && editingBottle.item_kind === bottleFormItemKind
      ? editingBottle.category
      : undefined;
  if (
    currentValue !== undefined &&
    currentValue.length > 0 &&
    !offered.some((entry) => entry.value === currentValue)
  ) {
    const storedEntry = (adminVocabularies["item_category"] ?? []).find(
      (entry) => entry.itemKindScope === bottleFormItemKind && entry.value === currentValue,
    );
    offered.push({ value: currentValue, label: storedEntry?.label ?? currentValue });
  }
  for (const category of sortOptions(offered)) {
    const option = document.createElement("option");
    option.value = category.value;
    option.textContent = category.label;
    bottleCategoryOtherSelect.append(option);
  }
  if (currentValue !== undefined) {
    bottleCategoryOtherSelect.value = currentValue;
  }
}

// CT1 (docs/backlog.md): container_type has been its own tenant
// vocabulary since migration 0045 (SET7), enforced at the database by a
// foreign key rather than the fixed CONTAINER_TYPES four; this form's own
// <select> was the one place still reading the hardcoded constant
// directly, a gap B4 found and named but did not fix. Mirrors
// tenantLiquorBottleSizes/tenantServingSizes below: active values only,
// CONTAINER_TYPES survives purely as the fallback for the short window
// before loadVocabularies() has returned.
function tenantContainerTypes(): { value: string; label: string }[] {
  return activeVocabularyOptions(
    adminVocabularies["container_type"] ?? [],
    CONTAINER_TYPES.map((value) => ({ value, label: containerTypeLabel(value) })),
  );
}

// ITEM1 Slice 2 (docs/build-briefs/ITEM1-item-kinds-are-a-configurable-
// vocabulary.md; DEC-130, DEC-145). item_kind is now the tenant's own
// vocabulary (migration 0110): the item-kind chip is driven from
// adminVocabularies["item_kind"], the same shape tenantContainerTypes
// and tenantLiquorCategories just above already use, ACTIVE VALUES ONLY
// so a retired kind ('packaged', seeded retired) is not offered for a
// NEW item (DEC-145 criterion 4). ITEM_KINDS survives purely as the
// fallback for the short window before loadVocabularies() has returned.
//
// The B7-S3/populateShelfTierSelect retirement-safety merge just below
// this file's own precedent: a bottle already open for editing whose
// stored item_kind has since been retired stays visible and selected on
// this chip group even though it is no longer offered for a NEW item
// (DEC-145 criterion 7), read from the module-level `editingBottle` (set
// before resetBottleForm/renderBottleItemKindChips run in
// openEditBottleForm, undefined in add mode) rather than a parameter,
// mirroring populateShelfTierSelect's own comment on why.
function tenantItemKinds(): { value: string; label: string }[] {
  const offered = activeVocabularyOptions(
    (adminVocabularies["item_kind"] ?? []).filter((entry) => isCurrentItemKind(entry.value)),
    ITEM_KINDS.map((kind) => ({ value: kind, label: itemKindLabel(kind) })),
  );
  const currentKind = editingBottle?.item_kind;
  if (currentKind !== undefined && isCurrentItemKind(currentKind) && !offered.some((entry) => entry.value === currentKind)) {
    const stored = (adminVocabularies["item_kind"] ?? []).find(
      (entry) => entry.value === currentKind,
    );
    offered.push({ value: currentKind, label: stored?.label ?? itemKindLabel(currentKind) });
  }
  return sortOptions(offered);
}

/** Resolves a kind's label from the tenant's own item_kind vocabulary row
 * first (so a renamed kind's label is honored everywhere it is shown),
 * falling back to the static ITEM_KIND_LABELS map for the short window
 * before loadVocabularies() has returned. */
function resolveItemKindLabel(itemKind: string): string {
  const entry = (adminVocabularies["item_kind"] ?? []).find(
    (candidate) => candidate.value === itemKind,
  );
  return entry?.label ?? itemKindLabel(itemKind);
}

/** CT1: rebuilds the Container type <select> from the tenant's own
 * container_type vocabulary (active values only), the same source B4
 * already wired the Size pickers to. A bottle already open for editing
 * whose stored container_type has since been retired (or predates this
 * tenant's current list) is merged back in by value, so the select shows
 * that bottle's real stored value honestly instead of silently falling
 * back to "Unspecified": the value keeps its own vocabulary label when
 * one still exists (even retired), or the raw stored string itself when
 * it does not, mirroring renderDrinkLiquorSlotChips' own merge-the-
 * current-value rule (SET6). editingBottle is already set by the time
 * this runs in edit mode (openEditBottleForm assigns it before calling
 * resetBottleForm, which is this function's only caller), and is
 * undefined in Add mode, so a brand-new bottle form is never affected. */
function populateBottleContainerTypeSelect(): void {
  bottleContainerTypeSelect.innerHTML = "";
  const unspecified = document.createElement("option");
  unspecified.value = "";
  unspecified.textContent = "Unspecified";
  bottleContainerTypeSelect.append(unspecified);
  const offered = tenantContainerTypes();
  const currentValue = editingBottle?.container_type ?? undefined;
  if (
    currentValue !== undefined &&
    currentValue.length > 0 &&
    !offered.some((entry) => entry.value === currentValue)
  ) {
    const storedEntry = (adminVocabularies["container_type"] ?? []).find(
      (entry) => entry.value === currentValue,
    );
    offered.push({ value: currentValue, label: storedEntry?.label ?? currentValue });
  }
  for (const containerType of sortOptions(offered)) {
    const option = document.createElement("option");
    option.value = containerType.value;
    option.textContent = containerType.label;
    bottleContainerTypeSelect.append(option);
  }
}

// INV8 Section 2.7 (DEC-176): a fixed, three-value tenant vocabulary
// (DEC-123), active values only for a NEW pick, mirroring
// tenantContainerTypes above exactly (no static fallback constant here,
// since keg_size has no pre-vocabulary history the way container_type
// does -- INV14 introduced it already as a tenant vocabulary).
function tenantKegSizes(): { value: string; label: string }[] {
  return (adminVocabularies["keg_size"] ?? [])
    .filter((entry) => entry.active)
    .map((entry) => ({ value: entry.value, label: entry.label }));
}

/** INV8 Section 2.7 (DEC-176): the keg-size picker's own <select>,
 * mirroring populateBottleContainerTypeSelect's retirement-safety merge
 * exactly: a bottle already assigned a since-retired keg size stays
 * visible and selected (never silently dropped), while a NEW pick only
 * ever offers an active one (validateDefaultKegSize's own rule, server
 * side). Called once from resetBottleForm, same as
 * populateBottleContainerTypeSelect; openEditBottleForm only sets
 * .value afterward, never repopulates. */
function populateBottleDefaultKegSizeSelect(): void {
  bottleDefaultKegSizeSelect.innerHTML = "";
  const unspecified = document.createElement("option");
  unspecified.value = "";
  unspecified.textContent = "Unspecified";
  bottleDefaultKegSizeSelect.append(unspecified);
  const offered = tenantKegSizes();
  const currentValue = editingBottle?.default_keg_size ?? undefined;
  if (
    currentValue !== undefined &&
    currentValue.length > 0 &&
    !offered.some((entry) => entry.value === currentValue)
  ) {
    const storedEntry = (adminVocabularies["keg_size"] ?? []).find(
      (entry) => entry.value === currentValue,
    );
    offered.push({ value: currentValue, label: storedEntry?.label ?? currentValue });
  }
  for (const kegSize of offered) {
    const option = document.createElement("option");
    option.value = kegSize.value;
    option.textContent = kegSize.label;
    bottleDefaultKegSizeSelect.append(option);
  }
}

/** INV8 Section 2.7: the keg-size row is only meaningful for a keg
 * container; hidden otherwise rather than offering a picker with nothing
 * to attach to. Called whenever Container type changes, by a real user
 * pick or by an item-kind default. */
function refreshBottleDefaultKegSizeVisibility(): void {
  bottleDefaultKegSizeRow.hidden = bottleContainerTypeSelect.value !== "keg";
}

/** INV8 Section 2.6 (DEC-176): rebuilds the read-only unit hint next to
 * Par level from whatever the form currently shows, live, on every
 * change to item kind, container type, size, or keg size -- never only on
 * open, since a person can change any of those before saving and the
 * hint should always describe THIS moment's own configuration.
 * itemKindSellsWholeUnit(bottleFormItemKind) mirrors Section 2.1's own
 * "sale mechanism whole_unit" case exactly (the same derivation
 * applyBottleItemKindVisibility already uses for this form). */
function refreshBottleParUnitHint(): void {
  const containerType =
    bottleContainerTypeSelect.value.length > 0 ? bottleContainerTypeSelect.value : null;
  const defaultKegSizeValue =
    bottleDefaultKegSizeSelect.value.length > 0 ? bottleDefaultKegSizeSelect.value : null;
  const defaultKegSizeLabel =
    defaultKegSizeValue !== null
      ? ((adminVocabularies["keg_size"] ?? []).find((entry) => entry.value === defaultKegSizeValue)
          ?.label ?? defaultKegSizeValue)
      : null;
  const sizeValue = bottleSizeValueInput.value.trim();
  const sizeUnit = bottleSizeUnitInput.value.trim();
  const hint = describeParUnitHint({
    saleMechanismIsWholeUnit: itemKindSellsWholeUnit(bottleFormItemKind),
    containerType,
    sizeValue: sizeValue.length > 0 ? sizeValue : null,
    sizeUnit: sizeUnit.length > 0 ? sizeUnit : null,
    defaultKegSizeLabel,
  });
  bottleParLevelHintEl.textContent = hint ?? "";
  bottleParLevelHintEl.hidden = hint === null;
  refreshBottleStartStock();
  // INVLOC1 Slice 2: the table layout shows the same hint under the table and the unit in its Par header.
  bottleParLevelHintTableEl.textContent = hint ?? "";
  bottleParLevelHintTableEl.hidden = hint === null || bottleStockTableEl.hidden;
  if (bottleStockParHeaderEl !== undefined) {
    bottleStockParHeaderEl.textContent = tableCopy(parUnitWord(currentParCountUnit())).parHeader;
  }
  // ADDITEM2: steps 2 and 3 read the same container, size, keg size and sale mechanism this hint reads.
  refreshAddItemSteps();
}

/** Finds the current item kind's own filtered size option matching a
 * stored (value, unit) pair, the same lookup populateBottleSizeSelect and
 * selectSizeForStoredValue both need: the first to decide whether an
 * off-list display option belongs on the render pass, the second to
 * pick it. A single shared function so the two cannot drift apart. */
function findMatchingSizeOption(
  sizeValue: string,
  sizeUnit: string,
): { value: string; label: string } | undefined {
  const normalizedUnit = sizeUnit.trim().toLowerCase();
  const numericValue = Number(sizeValue);
  return sizeOptionsForItemKind(bottleFormItemKind).find((option) => {
    const parsed = parseSizeToken(option.value);
    if (parsed === undefined) {
      return false;
    }
    return (
      Number(parsed.value) === numericValue &&
      (SIZE_UNIT_DISPLAY[parsed.unit] ?? parsed.unit).toLowerCase() === normalizedUnit
    );
  });
}

// INV15 (owner report 2026-09-10): the token this off-list display
// option's own value carries. Concatenated verbatim from the raw stored
// strings, never round-tripped through SIZE_TOKEN_PATTERN, so a
// genuinely unparseable historical unit (not ml/l/oz) is preserved
// exactly rather than silently dropped by display logic that assumes
// every value fits the pattern.
function offListSizeOptionValue(sizeValue: string, sizeUnit: string): string {
  return `${sizeValue}${sizeUnit}`;
}

function offListSizeOptionLabel(sizeValue: string, sizeUnit: string): string {
  return `Current: ${sizeValue} ${sizeUnit} (not in list)`;
}

/** Appends a DISABLED display-only option representing a stored size the
 * current kind's filtered list does not offer, so an existing value
 * predating this item (or this kind's vocabulary) is never dropped from
 * view, only made un-selectable going forward. Never runs in Add mode
 * (editingBottleId === undefined), since a brand-new item has no prior
 * stored value to preserve; runs only when at least one of the raw
 * value/unit inputs is non-empty and no real option matches them. */
function appendOffListSizeOptionIfNeeded(): void {
  if (editingBottleId === undefined) {
    return;
  }
  const sizeValueRaw = bottleSizeValueInput.value.trim();
  const sizeUnitRaw = bottleSizeUnitInput.value.trim();
  if (sizeValueRaw.length === 0 && sizeUnitRaw.length === 0) {
    return;
  }
  if (findMatchingSizeOption(sizeValueRaw, sizeUnitRaw) !== undefined) {
    return;
  }
  const el = document.createElement("option");
  el.value = offListSizeOptionValue(sizeValueRaw, sizeUnitRaw);
  el.textContent = offListSizeOptionLabel(sizeValueRaw, sizeUnitRaw);
  el.disabled = true;
  bottleSizeSelect.append(el);
}

/** Rebuilds the Size <select>'s options for whichever item kind the form
 * currently shows, filtered by sizeOptionsForItemKind. INV15 (owner
 * report 2026-09-10, "Remove 'Other' as an option from size. It pops
 * open hand typed fields again"): there is no longer a plain, always-
 * offered "Other" entry a person can pick to type a new value. The only
 * off-list option that ever appears is the disabled display option
 * appendOffListSizeOptionIfNeeded adds, and only to preserve a value
 * already on the item being edited. */
function populateBottleSizeSelect(): void {
  bottleSizeSelect.innerHTML = "";
  const unspecified = document.createElement("option");
  unspecified.value = "";
  unspecified.textContent = "Unspecified";
  bottleSizeSelect.append(unspecified);
  for (const option of sizeOptionsForItemKind(bottleFormItemKind)) {
    const el = document.createElement("option");
    el.value = option.value;
    el.textContent = option.label;
    bottleSizeSelect.append(el);
  }
  appendOffListSizeOptionIfNeeded();
}

/** Applies a Size <select> choice to the underlying (still authoritative)
 * #admin-bottle-size-value / #admin-bottle-size-unit inputs collect-
 * InventoryItemFields already reads on save. INV15: the disabled
 * off-list display option can never reach this function through a user
 * selection (a browser will not let a person choose a disabled option),
 * so every value this function ever receives from the "change" listener
 * is a real, in-list token or the blank "Unspecified" value. */
function applyBottleSizeSelection(selected: string): void {
  if (selected.length === 0) {
    bottleSizeValueInput.value = "";
    bottleSizeUnitInput.value = "";
    return;
  }
  const parsed = parseSizeToken(selected);
  bottleSizeValueInput.value = parsed?.value ?? "";
  bottleSizeUnitInput.value =
    parsed !== undefined ? (SIZE_UNIT_DISPLAY[parsed.unit] ?? parsed.unit) : "";
}

/** Picks the Size <select> option matching a (value, unit) pair already
 * sitting in the raw inputs, or falls back to the disabled off-list
 * display option with those raw values left exactly as they are. This is
 * what keeps B4's rule 2 honest across an item-kind change: a value the
 * current kind's filtered list no longer offers is never dropped, only
 * re-shown through the disabled option populateBottleSizeSelect already
 * appended on this same render pass. */
function selectSizeForStoredValue(sizeValue: string, sizeUnit: string): void {
  if (sizeValue.trim().length === 0) {
    bottleSizeSelect.value = "";
    return;
  }
  const match = findMatchingSizeOption(sizeValue, sizeUnit);
  if (match !== undefined) {
    bottleSizeSelect.value = match.value;
    return;
  }
  bottleSizeSelect.value = offListSizeOptionValue(sizeValue.trim(), sizeUnit.trim());
}

/** B4 bug-risk rule 1: "a default applies to a NEW bottle form only and
 * must never retroactively stamp 750 ml onto an existing bottle whose
 * size was deliberately left blank." isAddMode gates every branch below;
 * edit mode always falls through to re-deriving the <select> from
 * whatever is already in the raw inputs (selectSizeForStoredValue), never
 * writing a default into them. Rule 2 ("must not stomp a size... already
 * chosen on purpose") is bottleFormSizeUserSet: once true, an item-kind
 * change in Add mode also stops writing a default, for the same reason. */
function refreshBottleSizeForItemKind(): void {
  const isAddMode = editingBottleId === undefined;
  if (isAddMode && !bottleFormSizeUserSet) {
    const token = SIZE_DEFAULT_TOKEN_BY_ITEM_KIND[bottleFormItemKind];
    const parsed = token !== undefined ? parseSizeToken(token) : undefined;
    bottleSizeValueInput.value = parsed?.value ?? "";
    bottleSizeUnitInput.value =
      parsed !== undefined ? (SIZE_UNIT_DISPLAY[parsed.unit] ?? parsed.unit) : "";
  }
  populateBottleSizeSelect();
  selectSizeForStoredValue(bottleSizeValueInput.value, bottleSizeUnitInput.value);
}

/** Same two rules as refreshBottleSizeForItemKind, applied to Container
 * type instead: Add-mode-only, and only while the user has not already
 * picked a container type for themselves this session. */
function refreshBottleContainerTypeForItemKind(): void {
  const isAddMode = editingBottleId === undefined;
  if (isAddMode && !bottleFormContainerTypeUserSet) {
    bottleContainerTypeSelect.value = CONTAINER_TYPE_DEFAULT_BY_ITEM_KIND[bottleFormItemKind] ?? "";
  }
}

// TF3 (docs/backlog.md, owner ruling 2026-08-28): the read-only on-hand
// figure beside Par level. Only meaningful for an EXISTING bottle (a
// brand-new, unsaved one has no ledger history), so this is called from
// openEditBottleForm alone and the row stays hidden otherwise (see
// resetBottleForm, which hides it unconditionally before every open).
async function loadBottleOnHand(bottleId: string): Promise<void> {
  const gen = loadGeneration;
  const s = session();
  // Permission-driven, never role-driven (CLAUDE.md): the same
  // inv.onhand.view permission the on-hand routes themselves require.
  // Every role that can reach this Edit Bottle window today also carries
  // this permission (verified against src/domain/permissions.ts's role
  // grid), but the check stays here rather than being assumed, because a
  // future custom role (RT2) could separate the two.
  if (currentSession === undefined || !hasPermission(currentSession, "inv.onhand.view")) {
    bottleOnHandRow.hidden = true;
    bottleOnHandWarningEl.hidden = true;
    recordMovementButton.hidden = true;
    bottleOnHandSnapshot = undefined;
    return;
  }
  bottleOnHandRow.hidden = false;
  // INV1: the "Record movement" button lives beside on-hand and needs
  // its own permission (inv.movement.record), separate from viewing
  // on-hand: a future custom role (RT2) could hold one without the
  // other.
  recordMovementButton.hidden = !hasPermission(currentSession, "inv.movement.record");
  bottleOnHandValueEl.textContent = "Loading...";
  bottleOnHandValueEl.classList.remove("admin-onhand-value--unavailable");
  const result = await getLiquorProductOnHand(s.apiBaseUrl, s.tenantId, bottleId);
  // SET16-F4: this session's teardown ran while the fetch above was in
  // flight; discard rather than write on-hand text for a session no
  // longer on screen. A separate question from the stale-bottle guard
  // directly below, which answers "is this still the same BOTTLE" rather
  // than "is this still the same SESSION".
  if (!loadIsCurrent(gen)) return;
  // Stale-response guard: if the form has since been closed or switched to
  // a different bottle (or a new Add-bottle form), this row no longer
  // applies to what is on screen. bottleOnHandRow.hidden also becomes true
  // whenever resetBottleForm runs, so checking editingBottleId here is
  // enough to detect either case without a separate request-id token.
  if (editingBottleId !== bottleId) {
    return;
  }
  if (result.status !== 200) {
    bottleOnHandSnapshot = undefined;
    bottleOnHandValueEl.textContent = "Could not load on-hand.";
    bottleOnHandValueEl.classList.add("admin-onhand-value--unavailable");
    // UX16-F2: "Could not load" is an UNKNOWN state, not a known negative
    // one; the warning is specifically about a known negative figure.
    bottleOnHandWarningEl.hidden = true;
    return;
  }
  if (result.body.mixedUnits || result.body.quantityOnHand === null) {
    bottleOnHandSnapshot = undefined;
    // INV20 (DEC-190, DEC-206): the on-hand route now always converts
    // into one figure in the item's own count unit server-side
    // (src/domain/inventory.ts's convertLiquorProductOnHandToCountUnit),
    // so this branch should never be reached in practice. It stays as a
    // defensive fallback, never "Tracked in more than one unit" again
    // (this brief's own false-green control), styled the same as "could
    // not load" since it is genuinely an unavailable-figure case.
    bottleOnHandValueEl.textContent = "On-hand unavailable.";
    bottleOnHandValueEl.classList.add("admin-onhand-value--unavailable");
    // UX16-F2: an undetermined on-hand is UNKNOWN, not negative; see the
    // comment above.
    bottleOnHandWarningEl.hidden = true;
    return;
  }
  // DOCSBUG4: the figure the reverse confirm shows as "On hand now".
  bottleOnHandSnapshot = { quantity: result.body.quantityOnHand, unit: result.body.unit ?? "oz" };
  bottleOnHandValueEl.textContent = formatOnHandFigure(
    result.body.quantityOnHand,
    result.body.unit ?? "oz",
    editingBottle?.size_value ?? null,
    editingBottle?.size_unit ?? null,
  );
  bottleOnHandValueEl.classList.remove("admin-onhand-value--unavailable");
  // UX16-F2 (DEC-178): on-hand may go below zero; it is shown with a
  // warning and corrected by the next count, never by refusing a sale. The
  // warning names the cause (a sale or waste entry outran the recorded
  // receipts) and the remedy (the next count), rather than merely flagging
  // that the number looks wrong.
  const negative = isNegativeQuantity(result.body.quantityOnHand);
  bottleOnHandWarningEl.hidden = !negative;
  bottleOnHandWarningEl.textContent = negative
    ? "Warning: this bottle's on-hand is negative. A sale or waste entry was recorded without enough on hand; correct it at the next count."
    : "";
}

// ---------------------------------------------------------------------
// INV19 Slice 1 (docs/build-briefs/INV19-pour-size-price-override.md;
// ADR-010 revised text; DEC-127, DEC-128, DEC-139). A per-item,
// per-pour-size price override that follows its shelf tier: the delta is
// typed once (configuration, the money rule's own exception), the pour
// size it is typed against is PICKED from the item's own configured
// pourOunceOptions (never free text, never a separate picker), and the
// sign is picked through a two-option segmented control rather than
// typed, since this is this product's first signed money field
// (ui/src/money.ts carries no signed parser; see the build brief).
//
// The "effective price today" readout is NEVER computed here: every row
// renders exactly what the GET response returned
// (resolvePourPriceOverrideEffectiveCents, the one server-side place this
// sum is computed), so the admin screen cannot drift from the real sale
// path by construction (DEC-139, ADR-010 Section 5).
// ---------------------------------------------------------------------

/** The most recently loaded GET .../pour-price-overrides response for the
 * bottle currently open in the edit form, or undefined before the first
 * load / after the form closes. Cleared by invalidate() and
 * resetBottleForm, matching every other bottle-scoped module-level `let`
 * on this screen. */
let bottlePourPriceOverrides: PourPriceOverridesDto | undefined;

async function loadBottlePourPriceOverrides(bottleId: string): Promise<void> {
  const gen = loadGeneration;
  const s = session();
  // Permission-driven, never role-driven (CLAUDE.md): the identical
  // admin.liquor_product.manage permission both routes themselves
  // require (server.ts), so a user who cannot reach this section's own
  // writes never sees a section promising them.
  if (
    currentSession === undefined ||
    !hasPermission(currentSession, "admin.liquor_product.manage")
  ) {
    bottlePourPriceOverridesSection.hidden = true;
    return;
  }
  const result = await getPourPriceOverrides(s.apiBaseUrl, s.tenantId, bottleId, f3SellingUi.editor?.siteId);
  if (!loadIsCurrent(gen)) return;
  // Stale-response guard, matching loadBottleOnHand's identical pattern:
  // the form may have since closed or switched to a different bottle.
  if (editingBottleId !== bottleId) {
    return;
  }
  if (result.status !== 200) {
    bottlePourPriceOverrides = undefined;
    renderBottlePourPriceOverrides();
    return;
  }
  bottlePourPriceOverrides = result.body;
  renderBottlePourPriceOverrides();
}

// VOC1-F4-F2 (Section 2.3). This product's own current attachments,
// mirroring loadBottlePourPriceOverrides' own identical shape immediately
// above. Gated on admin.liquor_product.view (Section 2.7's own table: view
// is the permission for READING an attachment set; admin.liquor_product.
// manage, checked only at Save time by the server's own PATCH route, is
// for writing one), since GET .../liquor-products/:id itself is gated on
// view alone.
async function loadBottleModifiers(bottleId: string): Promise<void> {
  const gen = loadGeneration;
  const s = session();
  // Permission-driven, never role-driven (CLAUDE.md): the identical
  // admin.liquor_product.view permission the GET route itself requires, so
  // a user who cannot read this product at all never sees a section
  // promising an attachment list.
  if (currentSession === undefined || !hasPermission(currentSession, "admin.liquor_product.view")) {
    bottleModifiersSection.hidden = true;
    return;
  }
  const result = await getLiquorProduct(s.apiBaseUrl, s.tenantId, bottleId, f3SellingUi.editor?.siteId);
  if (!loadIsCurrent(gen)) return;
  // Stale-response guard, matching loadBottlePourPriceOverrides' identical
  // pattern: the form may have since closed or switched to a different
  // bottle.
  if (editingBottleId !== bottleId) {
    return;
  }
  if (result.status !== 200) {
    bottleFormModifiers = new Map();
    bottleModifiersLoaded = false;
    bottleModifiersSection.hidden = true;
    bottleModifiersEl.replaceChildren();
    return;
  }
  bottleFormModifiers = new Map(
    result.body.modifiers.map((modifier) => [modifier.value, modifier.required]),
  );
  loadAddonGroups(bottleFormModifiers, result.body.modifiers,result.body.unresolvedIngredientChoices??[]);
  bottleModifiersLoaded = true;
  refreshItemAddonFields();
}

/** One row per pour size ALREADY CONFIGURED on this item
 * (bottleFormPourOptions), never the tenant's full vocabulary: this is
 * the fixed-vocabulary-picker rule applied to the row set itself (the
 * brief's own Section 3), and it satisfies Q1's scoping (an override is
 * optional per size, never a forced full grid) at the same time. Hidden
 * entirely for a brand-new bottle (no id to fetch overrides for) or an
 * item with no configured pour sizes (packaged, or a kind not yet
 * saved with any size picked). */
function renderBottlePourPriceOverrides(): void {
  bottlePourPriceOverridesEl.innerHTML = "";
  if (
    bottleFormItemKind === "liquor" ||
    editingBottleId === undefined ||
    bottleFormPourOptions.size === 0 ||
    bottlePourPriceOverrides === undefined
  ) {
    bottlePourPriceOverridesSection.hidden = true;
    return;
  }
  bottlePourPriceOverridesSection.hidden = false;

  const data = bottlePourPriceOverrides;
  const sortedSizes = [...bottleFormPourOptions].sort((a, b) => Number(a) - Number(b));
  for (const pourSizeValue of sortedSizes) {
    bottlePourPriceOverridesEl.append(
      renderPourPriceOverrideRow(
        pourSizeValue,
        data,
        () => void loadBottlePourPriceOverrides(editingBottleId as string),
      ),
    );
  }
}

function renderPourPriceOverrideRow(
  pourSizeValue: string,
  data: PourPriceOverridesDto,
  reload: () => void,
): HTMLElement {
  const existing: PourPriceOverrideDto | undefined = data.overrides.find(
    (row) => row.pourSizeValue === pourSizeValue,
  );

  const row = document.createElement("div");
  row.className = "admin-pour-price-override-row";
  row.dataset["testid"] = "admin-pour-price-override-row";

  const label = document.createElement("span");
  label.className = "admin-pour-price-override-size";
  label.textContent = `${pourSizeValue} oz`;
  row.append(label);

  // The sign is PICKED, never typed (fixed-vocabulary-picker rule; this
  // is this product's first signed money field, ui/src/money.ts carries
  // no signed parser). A fixed, two-value vocabulary local to this row.
  const signGroupName = `admin-pour-price-override-sign-${pourSizeValue}`;
  const signContainer = document.createElement("div");
  signContainer.className = "admin-pour-price-override-sign";
  let sign: "above" | "below" =
    existing === undefined || existing.deltaCents >= 0 ? "above" : "below";

  const amountWrap = document.createElement("span");
  amountWrap.className = "field-input-affix field-input-affix--prefix";
  const amountMark = document.createElement("span");
  amountMark.className = "field-input-affix-mark";
  amountMark.setAttribute("aria-hidden", "true");
  amountMark.textContent = "$";
  const amountInput = document.createElement("input");
  amountInput.className = "field-input admin-pour-price-override-amount";
  amountInput.type = "number";
  amountInput.inputMode = "decimal";
  amountInput.step = "0.01";
  amountInput.min = "0";
  amountInput.value =
    existing === undefined ? "" : centsToAmountString(Math.abs(existing.deltaCents));
  amountInput.setAttribute("aria-label", `${pourSizeValue} oz override amount in dollars`);
  amountInput.dataset["testid"] = "admin-pour-price-override-amount";
  amountWrap.append(amountMark, amountInput);

  renderBottleFilterChipGroup(
    signContainer,
    signGroupName,
    "admin-pour-price-override-sign-option",
    [
      { value: "above", label: "Above tier" },
      { value: "below", label: "Below tier" },
    ],
    sign,
    (value) => {
      sign = value as "above" | "below";
    },
  );
  row.append(signContainer, amountWrap);

  const statusEl = document.createElement("span");
  statusEl.className = "admin-pour-price-override-status";
  statusEl.dataset["testid"] = "admin-pour-price-override-status";

  function renderEffectivePriceText(): void {
    if (existing === undefined) {
      statusEl.classList.remove("admin-pour-price-override-status--danger");
      const tierText =
        data.tierPriceCents === null ? "no tier assigned" : formatCents(data.tierPriceCents);
      statusEl.textContent = `No override, prices at tier (${tierText})`;
      return;
    }
    if (!existing.active) {
      statusEl.classList.remove("admin-pour-price-override-status--danger");
      statusEl.textContent = "Off, prices at tier";
      return;
    }
    if (existing.effectivePriceCents === null || existing.effectivePriceCents < 0) {
      statusEl.classList.add("admin-pour-price-override-status--danger");
      statusEl.textContent =
        existing.effectivePriceCents === null
          ? "Cannot be sold today: no tier assigned. Turn this override off or assign a tier."
          : `Cannot be sold today: resolves to ${formatCents(existing.effectivePriceCents)}. Turn this override off or lower it.`;
      return;
    }
    statusEl.classList.remove("admin-pour-price-override-status--danger");
    const tierText = data.tierLabel ?? "tier";
    const deltaText =
      existing.deltaCents >= 0
        ? `+${formatCents(existing.deltaCents)}`
        : formatCents(existing.deltaCents);
    statusEl.textContent = `${formatCents(existing.effectivePriceCents)} today (${tierText} ${data.tierPriceCents === null ? "" : formatCents(data.tierPriceCents)} ${deltaText})`;
  }
  renderEffectivePriceText();
  row.append(statusEl);

  const saveButton = document.createElement("button");
  saveButton.type = "button";
  saveButton.className = "btn btn-secondary";
  saveButton.textContent = existing === undefined ? "Add override" : "Save";
  saveButton.dataset["testid"] = "admin-pour-price-override-save";
  saveButton.addEventListener("click", () => {
    void (async () => {
      const raw = amountInput.value.trim();
      if (raw.length === 0) {
        statusEl.textContent = "Enter a dollar amount first.";
        statusEl.classList.add("admin-pour-price-override-status--danger");
        return;
      }
      let magnitudeCents: number;
      try {
        magnitudeCents = parseDollarsToCentsExact(raw, `${pourSizeValue} oz override amount`);
      } catch (error) {
        statusEl.textContent = error instanceof Error ? error.message : "Invalid amount.";
        statusEl.classList.add("admin-pour-price-override-status--danger");
        return;
      }
      const deltaCents = sign === "below" ? -magnitudeCents : magnitudeCents;
      saveButton.disabled = true;
      const bottleId = editingBottleId;
      if (bottleId === undefined) {
        return;
      }
      const s = session();
      // Independent-review finding (Codex, P1): `active: true` used to
      // be hardcoded here, so editing the AMOUNT on an override an admin
      // had deliberately switched Off, then saving, silently switched it
      // back On and immediately changed guest pricing -- while the
      // activation switch below presents itself as an independent
      // control. Saving an edit now preserves whatever this row's own
      // CURRENT activation state already is; `true` is reserved for
      // creating a brand-new override (`existing === undefined`), and
      // the activation switch itself remains the only control that ever
      // changes `active` on an existing row.
      const result = await putPourPriceOverride(s.apiBaseUrl, s.tenantId, bottleId, pourSizeValue, {
        deltaCents,
        active: existing?.active ?? true,
      });
      saveButton.disabled = false;
      if (result.status !== 200) {
        adminToast(
          result.body.message ?? result.body.error ?? "Could not save this override.",
          "danger",
        );
        return;
      }
      // UX48: no sibling save on this file leaves a manager guessing
      // whether it landed, and this row's own status text cannot show
      // that (it is fixed to "Off, prices at tier" while switched off,
      // by design). A save CONFIRMATION has no permanent on-screen home
      // (UX47 clause 1), so it belongs in the same toast every other
      // save on this screen already fires on its own 200.
      adminToast(`Saved ${pourSizeValue} oz override.`, "success");
      reload();
    })();
  });
  row.append(saveButton);

  // The activation switch only appears once an override row actually
  // exists: there is nothing yet to turn off for a pour size that has
  // never been saved. Turning an override off ALWAYS succeeds (the write
  // path's own NEW.active gate), even when its current delta is unsafe
  // against today's tier price -- this is the one guaranteed remedy after
  // a tier reprice pushes a delta negative (DEC-139).
  if (existing !== undefined) {
    row.append(
      renderActivationSwitch({
        active: existing.active,
        entityLabel: `${pourSizeValue} oz override`,
        toggleTestId: "admin-pour-price-override-active",
        statusTestId: "admin-pour-price-override-active-status",
        activeText: "On",
        inactiveText: "Off",
        confirmDeactivate: (entityLabel) => ({
          title: `Turn off ${entityLabel}?`,
          message: `${entityLabel} will stop pricing above or below the shelf tier and fall back to the plain tier price.`,
          confirmLabel: "Turn off",
        }),
        write: async (nextActive) => {
          const bottleId = editingBottleId;
          if (bottleId === undefined) {
            return { ok: false, message: "No bottle is open." };
          }
          const s = session();
          const result = await putPourPriceOverride(
            s.apiBaseUrl,
            s.tenantId,
            bottleId,
            pourSizeValue,
            { deltaCents: existing.deltaCents, active: nextActive },
          );
          if (result.status !== 200) {
            return {
              ok: false,
              message:
                result.body.message ?? result.body.error ?? "Could not update this override.",
            };
          }
          return { ok: true, active: result.body.override.active };
        },
        onChange: () => reload(),
      }),
    );
  }

  return row;
}

// ---------------------------------------------------------------------
// INV1 (docs/build-briefs/INV1-movement-types.md; P0.7): recording a
// waste/return-to-stock/vendor-return movement, and the reversal domain
// command this sheet does not (yet) surface. See this module's own
// header for the deferred-reversal-UI disclosure.
// ---------------------------------------------------------------------

type InventoryMovementFormType = "waste" | "return_to_stock" | "vendor_return";

const INVENTORY_MOVEMENT_TYPE_OPTIONS: { value: InventoryMovementFormType; label: string }[] = [
  { value: "waste", label: "Waste" },
  { value: "return_to_stock", label: "Return to stock" },
  { value: "vendor_return", label: "Vendor return" },
];

let inventoryMovementFormType: InventoryMovementFormType = "waste";
let inventoryMovementWasteReasons: WasteReasonDto[] = [];
let inventoryMovementSelectedReasonId: string | undefined;
// INV4-F2-F1 (docs/backlog.md; docs/build-briefs/INV4-F2-vendor-return-names-a-vendor.md):
// the vendor picker for a vendor-return movement, the same chip-radiogroup
// shape as the waste-reason row immediately above. Fetched once per sheet
// open (mirroring inventoryMovementWasteReasons' own fetch-at-open
// pattern), not re-fetched on every type-chip change.
let inventoryMovementVendors: VendorDto[] = [];
let inventoryMovementSelectedVendorId: string | undefined;

function renderInventoryMovementTypePicker(): void {
  renderChipRadioGroup(
    inventoryMovementTypePickerEl,
    "admin-inventory-movement-type-option",
    "admin-inventory-movement-type-option",
    INVENTORY_MOVEMENT_TYPE_OPTIONS,
    inventoryMovementFormType,
    (value) => {
      inventoryMovementFormType = value as InventoryMovementFormType;
      applyInventoryMovementTypeVisibility();
      renderInventoryMovementUnitPicker();
    },
  );
}

function applyInventoryMovementTypeVisibility(): void {
  inventoryMovementReasonRow.hidden = inventoryMovementFormType !== "waste";
  const vendorReturnSelected = inventoryMovementFormType === "vendor_return";
  inventoryMovementVendorRow.hidden = !vendorReturnSelected;
  inventoryMovementNoteRow.hidden = !vendorReturnSelected;
  // Cleared (not merely hidden) on any move away from Vendor return, so a
  // selection made before switching to Waste or Return to stock and back
  // never survives as a silent leftover default. renderInventoryMovementVendorPicker
  // rebuilds the picker from inventoryMovementVendors (already fetched at
  // sheet-open time) with no chip pre-checked, matching the waste-reason
  // row's own "no default" discipline.
  if (!vendorReturnSelected) {
    inventoryMovementSelectedVendorId = undefined;
    renderInventoryMovementVendorPicker();
  }
}

// INVUNIT1 (manager-day-03): the unit starts as the item's own count unit, the
// unit its on-hand reads in ("5.0 bottles"), for every movement type, so typing
// 1 for a broken bottle records one bottle and never one ounce. This supersedes
// the narrower DEC-262 rule (a vendor return alone started in Bottles): that case
// is now one instance of the general one. An item with no resolved count unit, or
// one the picker does not offer, keeps the first option, as before. Moving to
// another unit is a tap on its chip, and the line under the picker says which
// unit is in use.
function inventoryMovementDefaultUnit(options: { value: string }[]): string | undefined {
  const itemUnit = editingBottle?.countUnit ?? null;
  // Only when the item has a bottle size: an unsized item cannot be counted
  // in bottles (DOCSBUG1's "no bottle size yet" refusal), so it stays in oz.
  const hasBottleSize =
    editingBottle !== undefined &&
    editingBottle.size_value !== null &&
    editingBottle.size_unit !== null;
  if (
    itemUnit !== null &&
    options.some((option) => option.value === itemUnit) &&
    (itemUnit !== "bottle" || hasBottleSize)
  ) {
    return itemUnit;
  }
  return options[0]?.value;
}

function inventoryMovementUnitHelpText(selected: string, itemUnit: string | null): string {
  if (selected === "bottle") {
    return "Counting in bottles. Tap Ounces to enter part of a bottle.";
  }
  if (selected === "oz") {
    return itemUnit === "bottle"
      ? "Counting in ounces, not bottles. Tap Bottles to count whole bottles."
      : "Counting in ounces.";
  }
  if (selected === "unit") return "Counting in units.";
  return "";
}

function refreshInventoryMovementUnitCue(): void {
  const selected = selectedInventoryMovementUnit();
  const label = COUNT_UNIT_LABELS[selected];
  inventoryMovementQuantityLabelEl.textContent =
    label === undefined ? "Quantity" : `Quantity in ${label.toLowerCase()}`;
  inventoryMovementUnitHelpEl.textContent = inventoryMovementUnitHelpText(
    selected,
    editingBottle?.countUnit ?? null,
  );
}

function renderInventoryMovementUnitPicker(): void {
  const unitPriceCents = editingBottle?.unit_price_cents ?? null;
  const options = countUnitOptionsForItemKind(unitPriceCents);
  renderChipRadioGroup(
    inventoryMovementUnitPickerEl,
    "admin-inventory-movement-unit-option",
    "admin-inventory-movement-unit-option",
    options,
    inventoryMovementDefaultUnit(options),
    () => {
      /* read directly from the DOM at save time, matching the count walk's own pattern */
      refreshInventoryMovementUnitCue();
    },
  );
  refreshInventoryMovementUnitCue();
}

// No default reason, deliberately: matching this product's own "no
// default" discipline for a picker that decides a real business fact
// (CLAUDE.md: "Every applicable cocktail requires an explicit liquor
// selection with no default"). A waste reason is chosen, never
// pre-selected, so saveInventoryMovement's own guard has something real
// to refuse until a manager actually taps one.
function renderInventoryMovementReasonPicker(): void {
  inventoryMovementSelectedReasonId = undefined;
  const options = sortOptions(
    inventoryMovementWasteReasons.map((reason) => ({
      value: reason.id,
      label: reason.label,
    })),
  );
  renderChipRadioGroup(
    inventoryMovementReasonPickerEl,
    "admin-inventory-movement-reason-option",
    "admin-inventory-movement-reason-option",
    options,
    inventoryMovementSelectedReasonId,
    (value) => {
      inventoryMovementSelectedReasonId = value;
    },
  );
}

// No default vendor, deliberately, for the same "no default" reason
// renderInventoryMovementReasonPicker states above: a vendor is chosen,
// never pre-selected. Touch-first, fixed-vocabulary picker: names the
// vendor, never its id, and never offers free typing. Mirrors
// populateBottleVendorOfferVendorSelect's own permission-denied and
// empty-list honesty (ui/src/admin.ts): a session that cannot see vendors
// is told so plainly rather than shown a picker that silently has nothing
// in it.
function renderInventoryMovementVendorPicker(): void {
  inventoryMovementSelectedVendorId = undefined;
  const s = sessionOrUndefined();
  if (s === undefined || !hasPermission(s, "inv.vendor.view")) {
    inventoryMovementVendorPickerEl.hidden = true;
    inventoryMovementVendorPickerEl.innerHTML = "";
    inventoryMovementVendorEmptyEl.hidden = false;
    inventoryMovementVendorEmptyEl.textContent = "You do not have permission to view vendors.";
    return;
  }
  const activeVendors = inventoryMovementVendors.filter((vendor) => vendor.active);
  if (activeVendors.length === 0) {
    inventoryMovementVendorPickerEl.hidden = true;
    inventoryMovementVendorPickerEl.innerHTML = "";
    inventoryMovementVendorEmptyEl.hidden = false;
    inventoryMovementVendorEmptyEl.textContent =
      "No vendors yet. Add one in Settings > Purchasing.";
    return;
  }
  inventoryMovementVendorEmptyEl.hidden = true;
  inventoryMovementVendorEmptyEl.textContent = "";
  inventoryMovementVendorPickerEl.hidden = false;
  const options = sortOptions(
    activeVendors.map((vendor) => ({ value: vendor.id, label: vendor.name })),
  );
  renderChipRadioGroup(
    inventoryMovementVendorPickerEl,
    "admin-inventory-movement-vendor-option",
    "admin-inventory-movement-vendor-option",
    options,
    inventoryMovementSelectedVendorId,
    (value) => {
      inventoryMovementSelectedVendorId = value;
    },
  );
}

function selectedInventoryMovementUnit(): string {
  const checked = inventoryMovementUnitPickerEl.querySelector<HTMLInputElement>(
    'input[type="radio"]:checked',
  );
  return checked?.dataset["value"] ?? "oz";
}

async function openInventoryMovementSheet(): Promise<void> {
  if (editingBottleId === undefined || editingBottle === undefined) {
    return;
  }
  inventoryMovementErrorEl.hidden = true;
  inventoryMovementErrorEl.textContent = "";
  inventoryMovementFormType = "waste";
  inventoryMovementQuantityInput.value = "";
  inventoryMovementNoteInput.value = "";
  renderInventoryMovementTypePicker();
  renderInventoryMovementUnitPicker();
  applyInventoryMovementTypeVisibility();

  const s = session();
  const result = await listWasteReasons(s.apiBaseUrl, s.tenantId);
  inventoryMovementWasteReasons = result.status === 200 ? result.body.wasteReasons : [];
  renderInventoryMovementReasonPicker();

  // Fetched once per open, matching the waste-reason fetch immediately
  // above; only when the session can see vendors at all (mirroring
  // populateBottleVendorOfferVendorSelect's own permission gate), so a
  // session without inv.vendor.view never issues a request its own
  // GET /tenants/:tenantId/vendors would 403 anyway.
  if (hasPermission(s, "inv.vendor.view")) {
    const vendorResult = await listVendors(s.apiBaseUrl, s.tenantId, { activeOnly: true });
    inventoryMovementVendors = vendorResult.status === 200 ? vendorResult.body.vendors : [];
  } else {
    inventoryMovementVendors = [];
  }
  renderInventoryMovementVendorPicker();

  inventoryMovementScrim.hidden = false;
  lockBodyScroll();
}

function closeInventoryMovementSheet(): void {
  if (inventoryMovementScrim.hidden) {
    return;
  }
  inventoryMovementScrim.hidden = true;
  unlockBodyScroll();
}

async function saveInventoryMovement(): Promise<void> {
  const bottleId = editingBottleId;
  if (bottleId === undefined) {
    return;
  }
  inventoryMovementErrorEl.hidden = true;
  const rawQuantity = normalizeLeadingDot(inventoryMovementQuantityInput.value);
  if (!COUNT_QUANTITY_PATTERN.test(rawQuantity) || rawQuantity === "0") {
    inventoryMovementErrorEl.hidden = false;
    inventoryMovementErrorEl.textContent = "Enter a quantity greater than zero, e.g. 2 or 1.5.";
    return;
  }
  if (inventoryMovementFormType === "waste" && inventoryMovementSelectedReasonId === undefined) {
    inventoryMovementErrorEl.hidden = false;
    inventoryMovementErrorEl.textContent = "Pick a reason for this waste.";
    return;
  }
  if (
    inventoryMovementFormType === "vendor_return" &&
    inventoryMovementSelectedVendorId === undefined
  ) {
    inventoryMovementErrorEl.hidden = false;
    inventoryMovementErrorEl.textContent = "Pick a vendor for this return.";
    return;
  }
  const unit = selectedInventoryMovementUnit();
  const s = session();
  inventoryMovementSaveButton.disabled = true;

  const result = await (inventoryMovementFormType === "waste"
    ? recordWasteMovement(s.apiBaseUrl, s.tenantId, {
        idempotencyKey: crypto.randomUUID(),
        liquorProductId: bottleId,
        quantity: rawQuantity,
        unit,
        // Guarded above: inventoryMovementSelectedReasonId is defined
        // whenever inventoryMovementFormType is "waste".
        wasteReasonId: inventoryMovementSelectedReasonId as string,
        actorEmployeeId: s.employeeId,
      })
    : inventoryMovementFormType === "return_to_stock"
      ? recordReturnToStockMovement(s.apiBaseUrl, s.tenantId, {
          idempotencyKey: crypto.randomUUID(),
          liquorProductId: bottleId,
          quantity: rawQuantity,
          unit,
          actorEmployeeId: s.employeeId,
        })
      : recordVendorReturnMovement(s.apiBaseUrl, s.tenantId, {
          idempotencyKey: crypto.randomUUID(),
          liquorProductId: bottleId,
          quantity: rawQuantity,
          unit,
          // Guarded above: inventoryMovementSelectedVendorId is defined
          // whenever inventoryMovementFormType is "vendor_return".
          vendorId: inventoryMovementSelectedVendorId as string,
          note: inventoryMovementNoteInput.value.trim() || undefined,
          actorEmployeeId: s.employeeId,
        }));

  inventoryMovementSaveButton.disabled = false;
  if (result.status !== 200 && result.status !== 201) {
    inventoryMovementErrorEl.hidden = false;
    inventoryMovementErrorEl.textContent =
      result.body.message ?? result.body.error ?? "Could not record that movement. Try again.";
    return;
  }

  closeInventoryMovementSheet();
  adminToast("Movement recorded.", "success");
  void loadBottleOnHand(bottleId);
  void loadBottleMovements(bottleId);
}

// ---------------------------------------------------------------------
// DOCSBUG4 (docs/build-briefs/DOCSBUG4-reverse-a-movement.md; DEC-274;
// design/mockups/docsbug4-reverse-movement-notes.md): the Recent movements
// list on the Stock & vendor tab, and reversing a movement from it. What a
// row says is decided in ui/src/movementReversal.ts (pure, unit tested);
// this half owns the fetches, the DOM and the two dialogs. Touch only: no
// hover, no key handler, nothing typed.
// ---------------------------------------------------------------------

/** The latest rows shown before "Show older movements" (notes: the GET route
 * returns everything, the UI slices). */
const BOTTLE_MOVEMENTS_PAGE_SIZE = 6;

/** The on-hand figure the open bottle last loaded, kept so the reverse
 * confirm can show it before and after. Undefined whenever on-hand is
 * unavailable, hidden, or not yet loaded. */
let bottleOnHandSnapshot: { quantity: string; unit: string } | undefined;
let bottleMovements: InventoryMovementDto[] = [];
/** INV41: receipts, sales and count results for the open item, shown beside the manual movements. */
let bottleStockHistory: StockHistoryDto[] = [];
let bottleMovementsShowAll = false;
/** People by id, loaded once per open of the sheet; an id that is not here
 * (a retired user) reads "Someone" (notes question 3). */
let bottleMovementActorNames = new Map<string, string>();
let bottleMovementActorsLoaded = false;
/** Reversal rows made on this open of the sheet, shown as "Just now". */
let bottleFreshReversalIds = new Set<string>();
let reverseInFlight = false;

function resetBottleMovements(): void {
  bottleMovements = [];
  bottleMovementsShowAll = false;
  bottleMovementActorNames = new Map();
  bottleMovementActorsLoaded = false;
  bottleFreshReversalIds = new Set();
  bottleMovementsSection.hidden = true;
  bottleMovementsListEl.innerHTML = "";
  bottleMovementsStatusEl.hidden = true;
  bottleMovementsStatusEl.textContent = "";
  bottleMovementsEmptyEl.hidden = true;
  bottleMovementsOlderButton.hidden = true;
  bottleMovementsRetryButton.hidden = true;
  bottleMovementsCountEl.textContent = "";
}

function formatMovementDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatMovementDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function openBottleBottleOunces(): number | null {
  return ouncesPerBottle(editingBottle?.size_value ?? null, editingBottle?.size_unit ?? null);
}

async function loadBottleMovements(
  bottleId: string,
  options: { keepStatus?: boolean } = {},
): Promise<void> {
  const gen = loadGeneration;
  const s = sessionOrUndefined();
  if (s === undefined || !hasPermission(s, "inv.movement.view")) {
    bottleMovementsSection.hidden = true;
    return;
  }
  bottleMovementsSection.hidden = false;
  bottleMovementsRetryButton.hidden = true;
  if (options.keepStatus !== true) {
    bottleMovementsStatusEl.hidden = true;
    bottleMovementsStatusEl.textContent = "";
  }
  const [result, history, roster] = await Promise.all([
    listInventoryMovements(s.apiBaseUrl, s.tenantId, { liquorProductId: bottleId }),
    listStockHistory(s.apiBaseUrl, s.tenantId, { liquorProductId: bottleId }),
    bottleMovementActorsLoaded
      ? Promise.resolve(undefined)
      : listUsersForAdmin(s.apiBaseUrl, s.tenantId, { activeOnly: false }),
  ]);
  // Same two guards loadBottleOnHand applies: the session may have been torn
  // down, or the form switched to another bottle, while the fetch ran.
  if (!loadIsCurrent(gen)) return;
  if (editingBottleId !== bottleId) return;
  if (roster !== undefined) {
    bottleMovementActorsLoaded = true;
    if (roster.status === 200) {
      bottleMovementActorNames = new Map(
        roster.body.users.map((user) => [user.id, user.displayName] as const),
      );
    }
  }
  if (result.status !== 200 || history.status !== 200) {
    bottleMovements = [];
    bottleStockHistory = [];
    bottleMovementsListEl.innerHTML = "";
    bottleMovementsCountEl.textContent = "";
    bottleMovementsOlderButton.hidden = true;
    bottleMovementsEmptyEl.hidden = false;
    bottleMovementsEmptyEl.textContent = "Could not load recent movements.";
    bottleMovementsRetryButton.hidden = false;
    return;
  }
  bottleMovements = result.body.movements;
  bottleStockHistory = history.body.history;
  renderBottleMovements();
}

const MOVEMENT_ICON_CANNOT =
  '<svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="8" cy="8" r="6"/><path d="M4.5 11.5l7-7"/></svg>';
const MOVEMENT_ICON_REVERSED =
  '<svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 7h7a3 3 0 010 6H6"/><path d="M6 4L3 7l3 3"/></svg>';

function movementStateTag(state: "reversed" | "cannot"): HTMLElement {
  const tag = document.createElement("span");
  tag.className =
    state === "reversed" ? "inv-movement-tag inv-movement-tag--ok" : "inv-movement-tag";
  tag.dataset["testid"] = "admin-movement-state";
  // Static markup only: the icon constants above hold no server data.
  tag.insertAdjacentHTML(
    "beforeend",
    state === "reversed" ? MOVEMENT_ICON_REVERSED : MOVEMENT_ICON_CANNOT,
  );
  tag.append(document.createTextNode(state === "reversed" ? "Reversed" : "Can't reverse"));
  return tag;
}

function renderBottleMovementRow(view: MovementRowView, canReverse: boolean): HTMLElement {
  const item = document.createElement("li");
  item.className = `inv-movement inv-movement--${view.state}`;
  item.dataset["testid"] = "admin-movement-row";
  item.dataset["state"] = view.state;
  item.dataset["type"] = view.type;
  item.dataset["movementId"] = view.id;
  if (bottleFreshReversalIds.has(view.id)) {
    item.dataset["fresh"] = "true";
  }

  const main = document.createElement("div");
  main.className = "inv-movement-main";
  const top = document.createElement("div");
  top.className = "inv-movement-top";
  const typeTag = document.createElement("span");
  typeTag.className =
    view.type === "reversal" ? "inv-movement-tag inv-movement-tag--info" : "inv-movement-tag";
  typeTag.textContent = view.typeLabel;
  const when = document.createElement("span");
  when.className = "inv-movement-when";
  when.textContent = view.whenText;
  top.append(typeTag, when);
  const detail = document.createElement("div");
  detail.className = "inv-movement-detail";
  detail.dataset["testid"] = "admin-movement-detail";
  detail.textContent = view.detail;
  main.append(top, detail);

  const qty = document.createElement("div");
  qty.className = "inv-movement-qty";
  qty.dataset["testid"] = "admin-movement-qty";
  const primary = document.createElement("b");
  primary.textContent = view.quantityPrimary;
  qty.append(primary);
  if (view.quantitySecondary.length > 0) {
    const secondary = document.createElement("span");
    secondary.textContent = view.quantitySecondary;
    qty.append(secondary);
  }

  const action = document.createElement("div");
  action.className = "inv-movement-act";
  if (view.state === "reversible") {
    if (canReverse) {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "btn-secondary btn-compact inv-movement-reverse";
      button.dataset["testid"] = "admin-movement-reverse";
      button.textContent = "Reverse";
      button.setAttribute(
        "aria-label",
        `Reverse the ${view.typeLabel.toLowerCase()}, ${view.whenText}`,
      );
      button.addEventListener("click", () => void reverseMovementFlow(view.id));
      action.append(button);
    }
  } else if (view.state !== "history") {
    action.append(movementStateTag(view.state));
  }
  item.append(main, qty, action);
  return item;
}

function renderBottleMovements(): void {
  const s = sessionOrUndefined();
  const canReverse = s !== undefined && hasPermission(s, "inv.movement.reverse");
  const viewContext = {
    bottleOunces: openBottleBottleOunces(),
    actorNameById: bottleMovementActorNames,
    formatDateTime: formatMovementDateTime,
    formatDate: formatMovementDate,
    freshReversalIds: bottleFreshReversalIds,
  };
  // INV41: manual movements (with their reversal state) and the receipts, sales and count results, newest first.
  const views = [
    ...buildMovementRows(bottleMovements, viewContext),
    ...buildHistoryRows(bottleStockHistory, viewContext),
  ].sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
  const shown = bottleMovementsShowAll ? views : views.slice(0, BOTTLE_MOVEMENTS_PAGE_SIZE);
  bottleMovementsListEl.innerHTML = "";
  for (const view of shown) {
    bottleMovementsListEl.append(renderBottleMovementRow(view, canReverse));
  }
  bottleMovementsEmptyEl.hidden = views.length > 0;
  bottleMovementsEmptyEl.textContent = views.length > 0 ? "" : "No stock movements yet.";
  bottleMovementsCountEl.textContent =
    views.length > 0 ? `Showing ${shown.length} of ${views.length}` : "";
  bottleMovementsOlderButton.hidden = bottleMovementsShowAll || views.length <= shown.length;
  bottleMovementsRetryButton.hidden = true;
}

function movementActorName(movement: InventoryMovementDto): string {
  return bottleMovementActorNames.get(movement.actor_employee_id) ?? "Someone";
}

/** The unsized on-hand figure the confirm shows large ("5.1 bottles"); the
 * size parenthetical stays on the sheet's own on-hand line. */
function reverseConfirmFigure(quantity: number, unit: string): string {
  if (unit === "bottle") {
    // UX78: the shared UX76 bottle rule, so a small figure reads "0.04 bottles".
    const figure = bottleFigure(String(quantity));
    return `${figure} ${pluralizeCountUnitLabel(unit, figure)}`;
  }
  return formatOnHandFigure(String(quantity), unit, null, null);
}

function reverseConfirmText(tag: string, className: string, testId?: string): HTMLElement {
  const el = document.createElement(tag);
  el.className = className;
  if (testId !== undefined) {
    el.dataset["testid"] = testId;
  }
  return el;
}

/** AU14-F14 (DEC-316 a): the required reason on the Reverse confirm. The tenant's
 * active reversal reasons as a chip picker (a fixed vocabulary, picked and never
 * typed), nothing preselected, one amber line until a chip is picked. The
 * server refuses a missing, unknown or retired reason whatever this does. */
function buildReverseReasonPicker(
  reasons: readonly ActionReasonDto[],
  onPick: (reasonId: string) => void,
): HTMLElement {
  const block = reverseConfirmText("div", "admin-reverse-reason", "admin-reverse-reason");
  const heading = reverseConfirmText("p", "admin-reverse-reason-heading");
  heading.id = "admin-reverse-reason-heading";
  const headingText = document.createElement("span");
  headingText.textContent = "Why are you reversing it?";
  const state = reverseConfirmText("span", "po-required", "admin-reverse-reason-state");
  state.textContent = "Pick one";
  heading.append(headingText, state);
  const chips = reverseConfirmText(
    "div",
    "admin-reverse-reason-chips",
    "admin-reverse-reason-chips",
  );
  chips.setAttribute("role", "radiogroup");
  chips.setAttribute("aria-labelledby", heading.id);
  chips.setAttribute("aria-describedby", heading.id);
  const blocked = reverseConfirmText("p", "admin-onhand-warning", "admin-reverse-reason-blocked");
  blocked.textContent = "Pick a reason to continue.";
  blocked.setAttribute("role", "status");
  renderChipRadioGroup(
    chips,
    "admin-reverse-reason-option",
    "admin-reverse-reason-option",
    sortOptions(reasons.map((reason) => ({ value: reason.id, label: reason.label }))),
    undefined,
    (value) => {
      state.textContent = "Picked";
      state.classList.add("is-picked");
      blocked.hidden = true;
      setConfirmSheetBlocked(false);
      onPick(value);
    },
    heading.id,
  );
  block.append(heading, chips, blocked);
  return block;
}

/** The body of the confirm: on hand before and after, the warning for a
 * negative result, the original beneath, and the two plain notes. */
function buildReverseConfirmBody(
  movement: InventoryMovementDto,
  bottleOunces: number | null,
  approvalRequired: boolean,
  reasonPicker: HTMLElement,
): HTMLElement {
  const body = reverseConfirmText("div", "admin-reverse-body");

  // On hand before and after, shown large. Both are display only: the server
  // derives the reversal by negating the row.
  const snapshot = bottleOnHandSnapshot;
  const projected =
    snapshot === undefined
      ? null
      : projectedOnHand(snapshot.quantity, snapshot.unit, movement, bottleOunces);
  const beforeAfter = reverseConfirmText("div", "admin-reverse-ba");
  const figure = (label: string, value: string, testId: string): HTMLElement => {
    const cell = document.createElement("div");
    const labelEl = reverseConfirmText("span", "admin-reverse-ba-label");
    labelEl.textContent = label;
    const valueEl = reverseConfirmText("span", "admin-reverse-ba-value", testId);
    valueEl.textContent = value;
    cell.append(labelEl, valueEl);
    return cell;
  };
  const beforeText =
    snapshot === undefined
      ? (bottleOnHandValueEl.textContent ?? "")
      : reverseConfirmFigure(Number(snapshot.quantity), snapshot.unit);
  beforeAfter.append(figure("On\u00a0hand now", beforeText, "admin-reverse-confirm-before"));
  if (projected !== null) {
    const arrow = reverseConfirmText("span", "admin-reverse-ba-arrow");
    arrow.setAttribute("aria-hidden", "true");
    arrow.textContent = "\u2192";
    beforeAfter.append(
      arrow,
      figure(
        "After reversing",
        reverseConfirmFigure(projected.quantity, projected.unit),
        "admin-reverse-confirm-after",
      ),
    );
  }
  body.append(beforeAfter);

  const warning = negativeOnHandWarning(
    projected,
    projected === null ? "" : reverseConfirmFigure(projected.quantity, projected.unit),
  );
  if (warning !== null) {
    const warningEl = reverseConfirmText(
      "p",
      "admin-onhand-warning",
      "admin-reverse-confirm-warning",
    );
    warningEl.textContent = warning;
    body.append(warningEl);
  }

  const reason =
    movement.movement_type === "waste"
      ? movement.reason_label_at_movement
      : movement.movement_type === "vendor_return"
        ? [movement.vendor_name_at_movement, movement.note]
            .filter((part): part is string => part !== null && part.length > 0)
            .join(", ") || null
        : movement.note;
  const details = reverseConfirmText("dl", "admin-reverse-orig");
  const row = (term: string, value: string, testId: string): void => {
    const dt = document.createElement("dt");
    dt.textContent = term;
    const dd = document.createElement("dd");
    dd.dataset["testid"] = testId;
    dd.textContent = value;
    details.append(dt, dd);
  };
  row(
    "Original",
    `${movementTypeLabel(movement.movement_type)}, ${formatMovementDateTime(movement.created_at)}`,
    "admin-reverse-confirm-original",
  );
  if (reason !== null) {
    row("Reason", reason, "admin-reverse-confirm-reason");
  }
  row("Recorded by", movementActorName(movement), "admin-reverse-confirm-by");
  body.append(details);
  // AU14-F14: a reason is required before the button wakes.
  body.append(reasonPicker);

  const note = reverseConfirmText("p", "admin-reverse-note");
  note.textContent = "Both entries stay in the history. A reversal can't be reversed.";
  body.append(note);
  if (approvalRequired) {
    const approval = reverseConfirmText(
      "p",
      "admin-reverse-note",
      "admin-reverse-confirm-approval-note",
    );
    approval.textContent = "A manager will be asked to approve.";
    body.append(approval);
  }
  return body;
}

async function reloadBottleMovementsAndOnHand(
  bottleId: string,
  options: { keepStatus?: boolean } = {},
): Promise<void> {
  await Promise.all([loadBottleOnHand(bottleId), loadBottleMovements(bottleId, options)]);
}

/** Tap Reverse on a row: the confirm, then the request, then the result. The
 * shared confirm sheet carries every step, so this adds no overlay of its
 * own. */
async function reverseMovementFlow(movementId: string): Promise<void> {
  const bottleId = editingBottleId;
  const movement = bottleMovements.find((candidate) => candidate.id === movementId);
  const s = sessionOrUndefined();
  if (
    bottleId === undefined ||
    movement === undefined ||
    s === undefined ||
    reverseInFlight ||
    !hasPermission(s, "inv.movement.reverse")
  ) {
    return;
  }
  if (classifyMovement(movement, indexReversals(bottleMovements)) !== "reversible") {
    return;
  }
  const gen = loadGeneration;
  const bottleOunces = openBottleBottleOunces();
  const idempotencyKey = crypto.randomUUID();
  reverseInFlight = true;
  try {
    // AU14-F14 (DEC-316 a): the reasons are read first. A list that fails to
    // load, or is empty, stops here with a plain sentence and nothing is sent.
    const reasonsResult = await listActionReasons(s.apiBaseUrl, s.tenantId, {
      kind: "movement_reversal",
    });
    if (!loadIsCurrent(gen)) {
      return;
    }
    if (reasonsResult.status !== 200 || reasonsResult.body.actionReasons.length === 0) {
      adminToast(
        reasonsResult.status === 200
          ? "No reasons are set up for reversing a movement. Add one in Settings, under Corrections."
          : "Could not load the reasons. Check your connection and try again.",
        "danger",
      );
      return;
    }
    let pickedReasonId: string | undefined;
    const reasonPicker = buildReverseReasonPicker(reasonsResult.body.actionReasons, (id) => {
      pickedReasonId = id;
    });
    const confirmed = await showConfirmSheet({
      confirmBlocked: true,
      title: "Reverse this movement?",
      message: confirmSentence(
        movement,
        editingBottle?.name ?? "this item",
        bottleOunces,
        formatMovementDate,
      ),
      confirmLabel: "Reverse movement",
      cancelLabel: "Keep it as is",
      tone: "accent",
      extra: buildReverseConfirmBody(
        movement,
        bottleOunces,
        s.permissions["inv.movement.reverse"] === "approval_required",
        reasonPicker,
      ),
    });
    if (!confirmed || !loadIsCurrent(gen)) {
      return;
    }
    // The button was blocked until a chip was picked, so this is always set;
    // the guard keeps a request without one from ever leaving.
    const reasonId = pickedReasonId;
    if (reasonId === undefined) {
      return;
    }
    // The tap goes through the existing manager approval prompt only when the
    // server answers approval_required; a role that holds the permission
    // outright never sees it (requestApproval returns the first answer as is).
    for (;;) {
      const gated = await requestApproval(
        "Reverse a stock movement",
        (approval) =>
          reverseInventoryMovement(s.apiBaseUrl, s.tenantId, movement.id, {
            idempotencyKey,
            // AU14-F14: the same reason rides the first attempt and the retry
            // after a manager's PIN, under the same key.
            reasonId,
            ...approval,
          }),
        () => showAdminScreen(),
      );
      if (!loadIsCurrent(gen)) {
        return;
      }
      if (gated.canceled) {
        // The approval prompt was canceled: nothing changed.
        return;
      }
      showAdminScreen();
      const result = gated.result;
      if (result.status === 200 || result.status === 201) {
        bottleFreshReversalIds.add(result.body.movement.id);
        const words = quantityWords(
          Math.abs(Number(movement.quantity_delta)),
          movement.unit,
          bottleOunces,
        ).primary;
        const direction = Number(movement.quantity_delta) < 0 ? "added back" : "taken back out";
        await reloadBottleMovementsAndOnHand(bottleId, { keepStatus: true });
        if (!loadIsCurrent(gen) || editingBottleId !== bottleId) return;
        bottleMovementsStatusEl.textContent = `Reversed. ${words} ${direction}. On\u00a0hand is now ${bottleOnHandValueEl.textContent ?? ""}.`;
        bottleMovementsStatusEl.hidden = false;
        return;
      }
      if (result.networkError === true) {
        // Whether it happened is unknown, so the same key is kept: trying
        // again replays a success instead of reversing twice.
        const again = await showConfirmSheet({
          title: "Could not reach the server",
          message:
            "Whether the reversal went through is not known yet. Try again, or close this and check the list.",
          confirmLabel: "Try again",
          cancelLabel: "Close",
          tone: "accent",
        });
        if (!again || !loadIsCurrent(gen)) {
          await reloadBottleMovementsAndOnHand(bottleId);
          return;
        }
        continue;
      }
      // The one refusal that can only be learned after the tap: someone else
      // got there first. Any other refusal shows the domain's own sentence.
      const alreadyReversed = isAlreadyReversedRefusal(result.body.message);
      await showConfirmSheet({
        title: alreadyReversed ? "Already reversed" : "Could not reverse",
        message: alreadyReversed
          ? "Someone already reversed this movement. Nothing was changed. Tap below to see the latest list and on\u00a0hand."
          : `${plainRefusal(result.body.message).replace(/\.$/, "")}. Nothing was changed. Tap below to see the latest list and on\u00a0hand.`,
        confirmLabel: "Show the updated list",
        hideCancel: true,
        tone: "accent",
        refusal: true,
      });
      if (!loadIsCurrent(gen)) {
        return;
      }
      await reloadBottleMovementsAndOnHand(bottleId);
      return;
    }
  } finally {
    reverseInFlight = false;
  }
}

bottleMovementsOlderButton.addEventListener("click", () => {
  bottleMovementsShowAll = true;
  renderBottleMovements();
});
bottleMovementsRetryButton.addEventListener("click", () => {
  if (editingBottleId !== undefined) {
    void loadBottleMovements(editingBottleId);
  }
});

// ITEM2 Slice 5 (Section 3.4). Resolves the SELECTED (not yet necessarily
// saved) item kind's own sale_mechanism from the already-fetched item_kind
// vocabulary, the one fact the Add/Edit Bottle form's own field-group
// visibility, price parsing and count/receiving pickers all key off.
// 'liquor' is hardcoded (Section 3.5: its own row's sale_mechanism is
// NULL forever, since it sells as a 'pour', under neither of the two
// mechanisms this column names); every other kind resolves through its
// own fetched vocabulary row. This mirrors src/domain/catalog.ts's own
// saleMechanismForItemKind exactly, client-side.

function itemKindEntry(itemKind: string): AdminVocabularyEntry | undefined {
  return (adminVocabularies["item_kind"] ?? []).find((candidate) => candidate.value === itemKind);
}

function itemKindPolicyIsActive(itemKind: string): boolean {
  return itemKindEntry(itemKind)?.policyEnforcementActive === true;
}

function itemKindSaleMechanisms(itemKind: string): ProductSaleMechanism[] {
  return itemKindEntrySaleMechanisms(itemKind, itemKindEntry(itemKind));
}

// BEERMECH1: whether this kind's selling method is something the person picks on the form (and the form therefore sends
// on every save). True when policy enforcement is active, and also when it is off but the kind can be sold either way
// (Beer, Cider) and carries no single saved method to fall back on. Before this, enforcement off hid the picker and sent
// nothing, so the server refused a Beer keg it could not place.
function kindMechanismIsExplicit(itemKind: string): boolean {
  if (itemKindPolicyIsActive(itemKind)) return true;
  if (itemKind === "liquor") return false;
  const entry = itemKindEntry(itemKind);
  if (entry === undefined) return false;
  if (entry.saleMechanism === "per_ounce" || entry.saleMechanism === "whole_unit") return false;
  return itemKindSaleMechanisms(itemKind).length === 2;
}

function bottleKindMechanismIsExplicit(): boolean {
  return kindMechanismIsExplicit(bottleFormItemKind);
}

/** BEERMECH1: the selling method every Add item and Edit item write sends. It is sent whenever the form knows it,
 * whether or not policy enforcement is active; the server cannot place a kind that can be sold either way without it. */
function bottleSaleMechanismForWrite(): ProductSaleMechanism | undefined {
  return currentBottleSaleMechanism() ?? undefined;
}

function fixedItemKindSaleStatus(itemKind: string): string | undefined {
  if (itemKind === "beer" || itemKind === "cider") return "Draft pours and whole units.";
  if (itemKind === "wine" || itemKind === "liquor") return "By the pour.";
  return undefined;
}

function currentBottleSaleMechanism(): ProductSaleMechanism | null {
  if(catalogItemSetup!==undefined)return catalogItemSetup.selection.localSaleMechanism;
  if (bottleFormItemKind === "liquor") return "per_ounce";
  if (bottleKindMechanismIsExplicit()) return bottleFormSaleMechanism;
  const entry = itemKindEntry(bottleFormItemKind);
  // A session denied tenant settings reads no item kind vocabulary, so there is no entry to ask. Beer, wine and cider
  // are seeded as sold by the pour (ITEM_KIND_SEEDS in src/domain/vocabularies.ts), so their price and pour size fields
  // still show. Whole-unit beer and cider need the kind's selling methods, which this session cannot read.
  if (
    entry === undefined &&
    (bottleFormItemKind === "beer" || bottleFormItemKind === "wine" || bottleFormItemKind === "cider")
  ) {
    return "per_ounce";
  }
  const oldValue = entry?.saleMechanism;
  return oldValue === "per_ounce" || oldValue === "whole_unit" ? oldValue : null;
}

function inferBottleSaleMechanismFromContainer(): ProductSaleMechanism | null {
  if (bottleContainerTypeSelect.value === "keg") return "per_ounce";
  if (bottleContainerTypeSelect.value === "can" || bottleContainerTypeSelect.value === "bottle") {
    return "whole_unit";
  }
  return null;
}

function renderBottleSaleMechanismControls(): void {
  const policyActive = bottleKindMechanismIsExplicit();
  bottleSaleMechanismWrap.hidden = !policyActive;
  bottleSaleMechanismGroup.innerHTML = "";
  bottleSaleMechanismStatus.hidden = true;
  bottleSaleMechanismGroup.hidden = true;
  if (!policyActive) {
    return;
  }

  const allowed = itemKindSaleMechanisms(bottleFormItemKind);
  if (allowed.length === 1) {
    bottleSaleMechanismStatus.textContent =
      allowed[0] === "whole_unit" ? "Whole unit." : "By the pour.";
    bottleSaleMechanismStatus.hidden = false;
    return;
  }
  if (allowed.length !== 2) {
    bottleSaleMechanismStatus.textContent =
      "Selling methods are unavailable for this kind. Refresh the item kinds settings.";
    bottleSaleMechanismStatus.hidden = false;
    return;
  }

  bottleSaleMechanismStatus.textContent = "";
  bottleSaleMechanismGroup.hidden = false;
  // ADDITEM2 (DEC-610): "How do you sell it?" in the words of the container the person just picked.
  const container = bottleContainerTypeSelect.value;
  const choices: { value: ProductSaleMechanism; label: string }[] = [
    { value: "per_ounce", label: "Poured" },
    {
      value: "whole_unit",
      label: container === "can" ? "By the can" : container === "bottle" ? "By the bottle" : "Whole unit",
    },
  ];
  for (const choice of choices) {
    const label = document.createElement("label");
    label.className = "admin-policy-radio-option";
    const input = document.createElement("input");
    input.type = "radio";
    input.name = "admin-bottle-sale-mechanism";
    input.value = choice.value;
    input.required = true;
    input.dataset["testid"] = "admin-bottle-sale-mechanism-option";
    input.checked = bottleFormSaleMechanism === choice.value;
    input.addEventListener("change", () => {
      if (input.checked) {
        setBottleSaleMechanism(choice.value, true);
      }
    });
    const text = document.createElement("span");
    text.textContent = choice.label;
    label.append(input, text);
    bottleSaleMechanismGroup.append(label);
  }
}

// DEC-224 (owner ruling 2026-09-29, "Follow how it sells"): on the Add
// item form, a NEW beer's Container type defaults from the sale
// mechanism the manager just picked -- "By the pour" (per_ounce)
// defaults Container type to Keg, "Whole unit" (whole_unit) defaults it
// to Bottle. Add-mode only, and only while the manager has not already
// picked a Container type for themselves this session, the same two
// rules refreshBottleContainerTypeForItemKind already applies to the
// item-kind-driven default; a real manual pick (bottleContainerTypeSelect's
// own "change" listener sets bottleFormContainerTypeUserSet) is never
// overwritten. Scoped to beer specifically (DEC-224's own wording),
// never cider, which shares the identical two-mechanism shape but was
// not named in the ruling.
function defaultContainerTypeForSaleMechanism(
  mechanism: ProductSaleMechanism,
): "keg" | "bottle" | null {
  if (mechanism === "per_ounce") return "keg";
  if (mechanism === "whole_unit") return "bottle";
  return null;
}

function setBottleSaleMechanism(value: ProductSaleMechanism | null, touched: boolean): void {
  if (touched) bottleFormSaleMechanismTouched = true;
  if (value !== bottleFormSaleMechanism) {
    bottleCurrentPerOuncePriceCents = null;
    bottleUnitPriceInput.value = "";
    bottleFormPourOptions.clear();
    renderBottlePourOptionChips();
  }
  bottleFormSaleMechanism = value;
  // DEC-224: only on a real manager pick (touched), never on an
  // automatic item-kind-switch default (touched: false), and only
  // before the manager has typed/picked a Container type themselves.
  if (
    touched &&
    value !== null &&
    editingBottleId === undefined &&
    bottleFormItemKind === "beer" &&
    !bottleFormContainerTypeUserSet
  ) {
    const defaultContainerType = defaultContainerTypeForSaleMechanism(value);
    if (defaultContainerType !== null) {
      bottleContainerTypeSelect.value = defaultContainerType;
      // Mirrors bottleContainerTypeSelect's own "change" listener side
      // effects (minus refreshBottleSaleMechanismFromContainer, a no-op
      // here since bottleFormSaleMechanismTouched is already true by
      // this point, and minus marking bottleFormContainerTypeUserSet,
      // which must stay false: this is an automatic default, still
      // overwritable by a later real pick).
      refreshBottleDefaultKegSizeVisibility();
    }
  }
  renderBottleSaleMechanismControls();
  applyBottleItemKindVisibility();
  refreshBottleParUnitHint();
  bottleFormSave.disabled = bottleKindMechanismIsExplicit() && value === null;
}

function refreshBottleSaleMechanismForItemKind(): void {
  if (!bottleKindMechanismIsExplicit()) {
    renderBottleSaleMechanismControls();
    applyBottleItemKindVisibility();
    return;
  }
  const allowed = itemKindSaleMechanisms(bottleFormItemKind);
  if (allowed.length === 1) {
    setBottleSaleMechanism(allowed[0] ?? null, false);
    return;
  }
  if (!bottleFormSaleMechanismTouched) {
    setBottleSaleMechanism(inferBottleSaleMechanismFromContainer(), false);
    return;
  }
  if (bottleFormSaleMechanism !== null && !allowed.includes(bottleFormSaleMechanism)) {
    setBottleSaleMechanism(null, false);
    return;
  }
  renderBottleSaleMechanismControls();
  applyBottleItemKindVisibility();
}

function refreshBottleSaleMechanismFromContainer(): void {
  if (
    editingBottleId !== undefined ||
    bottleFormSaleMechanismTouched ||
    itemKindSaleMechanisms(bottleFormItemKind).length !== 2 ||
    !bottleKindMechanismIsExplicit()
  ) {
    return;
  }
  setBottleSaleMechanism(inferBottleSaleMechanismFromContainer(), false);
}

function bottleSaleMechanismRequiredButMissing(): boolean {
  return bottleKindMechanismIsExplicit() && currentBottleSaleMechanism() === null;
}

function itemKindSellsWholeUnit(itemKind: string): boolean {
  if (itemKind !== bottleFormItemKind && itemKindPolicyIsActive(itemKind)) {
    return (
      itemKindSaleMechanisms(itemKind).length === 1 &&
      itemKindSaleMechanisms(itemKind)[0] === "whole_unit"
    );
  }
  return currentBottleSaleMechanism() === "whole_unit";
}

// Inventory track Slice 3, Amendment 2026-08-22 (twelfth): which price
// fields apply, and which category SELECT is shown (CT3: both are
// pickers now, bottleCategorySelect for 'liquor' vs bottleCategoryOther-
// Select, repopulated per kind, for the rest), depends on the selected
// item kind.
// 'liquor' also needs the pour-options field (cocktail slot AND direct
// pour both use it); 'beer' shares the identical per-ounce/pour-
// size shape (see db/schema.ts's liquorProducts.itemKind comment) so it
// gets the same field group as 'liquor'; a whole-unit kind gets the flat
// unit price instead. WIN1: 'wine' takes the same field group as 'beer'
// here, and differs only in WHICH pour sizes are offered inside it (see
// pourOptionsForItemKind), which is why this function needs no wine
// branch at all.
//
// ITEM2 Slice 5: used to be `bottleFormItemKind === "packaged"`, which a
// tenant-added whole-unit kind's own value (never "packaged") could
// never match. Generalized to itemKindSellsWholeUnit above, since this
// runs on the SELECTED kind before any product with real prices exists
// (the create path), so there is no already-fetched product to derive it
// from the way bottleRowPriceText/openEditBottleForm below can.
function applyBottleItemKindVisibility(): void {
  const saleMechanism = currentBottleSaleMechanism();
  const isWholeUnit = saleMechanism === "whole_unit";
  const isLiquor = bottleFormItemKind === "liquor";
  bottleCategoryLiquorGroup.hidden = !isLiquor;
  bottleCategoryOtherGroup.hidden = isLiquor;
  bottleLiquorPriceGroup.hidden = saleMechanism !== "per_ounce";
  bottlePackagedPriceGroup.hidden = !isWholeUnit;
  renderBottleSaleMechanismControls();
  refreshItemAddonFields();
  applyBottlePriceLabelsForPricingModel();
}

// DEC-439: the saved rate is read-only; shelf pricing reads its assigned tier.
function applyBottlePriceLabelsForPricingModel(): void {
  const isShelf = settingsPricingModel === "shelf";
  bottleCurrentRateEl.hidden = isShelf;
  bottleCurrentRateEl.textContent =
    bottleCurrentPerOuncePriceCents === null
      ? "Price unavailable"
      : `${formatCents(bottleCurrentPerOuncePriceCents)} per fl oz`;
  bottleShelfTierLabelEl.textContent = "Shelf tier";
  const assigned = settingsShelfTiers.find((tier) => tier.tier === bottleShelfTierSelect.value);
  bottleShelfTierHelpEl.textContent = isShelf
    ? assigned === undefined
      ? "Price unavailable"
      : f3SellingUi.editor?.context.readiness.state === "enforced"
        ? (f3SellingUi.shelvesContext?.locationId === f3SellingUi.editor.siteId && f3SellingUi.shelves.get(assigned.tier)?.priceCents != null
          ? formatCents(f3SellingUi.shelves.get(assigned.tier)!.priceCents!) : "Price unavailable")
        : formatCents(assigned.priceCents)
    : "";
  bottleShelfTierGroupEl.hidden = !isShelf || currentBottleSaleMechanism() !== "per_ounce";
}

// B3 (docs/backlog.md, PRD P0.4): the Strength field's label and "%"
// suffix follow the item kind currently selected on this form ("Proof"
// for liquor, "ABV" with a "%" suffix for beer/wine/packaged; see
// ui/src/strength.ts's own header for the wine judgment call). Called
// from resetBottleForm, openEditBottleForm, and the item-kind chip's own
// change handler below, mirroring applyBottleItemKindVisibility's
// existing three call sites exactly.
function applyBottleStrengthLabelForItemKind(): void {
  bottleAbvLabelEl.textContent = strengthFieldLabel(bottleFormItemKind);
  bottleAbvSuffixEl.textContent = strengthFieldShowsPercentSuffix(bottleFormItemKind) ? "%" : "";
}

// ONB2-F1: the seeded Ingredient item kind's value (src/domain/vocabularies.ts INGREDIENT_ITEM_KIND_VALUE).
const INGREDIENT_KIND = "ingredient";

function renderBottleItemKindChips(): void {
  renderBottleFilterChipGroup(
    bottleItemKindEl,
    "admin-bottle-item-kind-option",
    "admin-bottle-item-kind-option",
    tenantItemKinds(),
    bottleFormItemKind,
    (value) => setBottleFormItemKind(value),
  );
}

/** The item kind chip's change, also used when a shared catalog product is picked (CATALOG1). */
function setBottleFormItemKind(value: string): void {
  // B3: capture the OLD kind before reassigning below, so the
  // Strength value on screen can be re-scaled FROM the unit it was
  // just displayed in TO the newly selected kind's unit, rather than
  // being reinterpreted as already being in the new unit (the
  // backlog row's own named bug risk: "not reinterpret 40 proof as
  // 40 percent").
  const previousItemKind = bottleFormItemKind;
  bottleFormItemKind = value as ItemKind;
  bottleAbvInput.value = rescaleStrengthDisplayString(
    bottleAbvInput.value,
    previousItemKind,
    bottleFormItemKind,
  );
  applyBottleStrengthLabelForItemKind();
  applyBottleSizeHelpTextForItemKind();
  applyBottleItemKindVisibility();
  // CT3: the OFFERED categories depend on which of the three "other"
  // kinds is now selected (beer/wine/packaged each have their
  // OWN vocabulary), so switching kind has to rebuild this select too,
  // the same reasoning the pour-size redraw just below already gives.
  populateBottleCategoryOtherSelect();
  // WIN1: the OFFERED pour sizes depend on the kind (wine has its
  // own list), so switching kind has to redraw them. Already-selected
  // values survive, because renderBottlePourOptionChips merges
  // bottleFormPourOptions in regardless of which list is showing.
  renderBottlePourOptionChips();
  // B4: the OFFERED size list and the container-type/size defaults
  // both depend on the kind too. See each function's own header for
  // the add-mode-only and already-chosen-by-the-user rules.
  refreshBottleContainerTypeForItemKind();
  refreshBottleSizeForItemKind();
  // INV8 Section 2.6/2.7 (DEC-176): a kind switch can change whether
  // this form is now whole-unit-priced (Section 2.1 case 1) or can
  // change container type's own default just above, both of which
  // change what the Par-level hint should say and whether the
  // keg-size row applies.
  refreshBottleSaleMechanismForItemKind();
  refreshBottleDefaultKegSizeVisibility();
  refreshBottleParUnitHint();
  // ONB2-F1: an Ingredient is never sold on its own, so adding one starts hidden from the menu at $0.00. Add
  // mode only: an edit keeps whatever the item already has.
  if (editingBottleId === undefined) {
    if (value === INGREDIENT_KIND) {
      bottleHideOnMenuInput.checked = true;
      if (bottleUnitPriceInput.value.trim() === "") bottleUnitPriceInput.value = "0.00";
    } else if (previousItemKind === INGREDIENT_KIND) {
      bottleHideOnMenuInput.checked = false;
    }
  }
}

// B7-S3 (build brief Section 2.3): a NEW assignment offers active tiers
// only, mirroring the server's own validateShelfTier (catalog.ts), which
// accepts a submitted shelfTier only when it is active OR equals the
// bottle's own pre-existing value. So this bottle's own current
// assignment stays visible and selectable here even if it has since been
// retired -- read from the module-level `editingBottle` (set before
// resetBottleForm/populateShelfTierSelect run in openEditBottleForm,
// undefined in add mode) rather than a parameter, since resetBottleForm
// itself calls this with no bottle in scope. Omitting this would let a
// retired assignment silently disappear from the <select> on open, and
// saving the form unchanged would then reassign the bottle off its real
// tier -- the data-loss risk this design explicitly avoids.
function populateShelfTierSelect(): void {
  bottleShelfTierSelect.innerHTML = "";
  const unassigned = document.createElement("option");
  unassigned.value = "";
  unassigned.textContent = "Unassigned";
  bottleShelfTierSelect.append(unassigned);
  const activeTiers = resolveShelfTiers().filter((tier) => tier.active);
  for (const tier of activeTiers) {
    const option = document.createElement("option");
    option.value = tier.tier;
    option.textContent = tier.name;
    bottleShelfTierSelect.append(option);
  }
  const currentTier = editingBottle?.shelf_tier ?? undefined;
  if (currentTier !== undefined && !activeTiers.some((tier) => tier.tier === currentTier)) {
    const option = document.createElement("option");
    option.value = currentTier;
    option.textContent = shelfTierDisplayName(currentTier);
    bottleShelfTierSelect.append(option);
  }
}

/** VER57-F2: the retry shared by the Try again button and the one automatic
 * retry when an item sheet opens while the failed latch is set. */
function retryVocabulariesFromPourArea(): void {
  vocabulariesLoadFailed = false;
  renderBottlePourOptionChips();
  void loadVocabularies({ forceFresh: true });
}

function renderBottlePourOptionChips(): void {
  bottlePourOptionsEl.innerHTML = "";
  const group = mustFind<HTMLElement>("#admin-bottle-pour-options-group");
  group.hidden = bottleFormItemKind === "liquor";
  refreshAddItemSteps();
  if (group.hidden) return;
  const allOptions = new Set(pourOptionsForItemKind(bottleFormItemKind));
  for (const selected of bottleFormPourOptions) {
    allOptions.add(selected);
  }
  const sorted = [...allOptions].sort((a, b) => Number(a) - Number(b));
  // ADDITEMNEXT1 (DEC-592): a new item cannot reach this step before its vocabularies have loaded (step 1's category list
  // needs them), and a saved item always has pour sizes, so the old "Loading pour sizes..." and "Could not load pour
  // sizes." lines can no longer show here and were removed. The saved-sizes case below keeps its own retry.
  for (const option of sorted) {
    const optionLabel = document.createElement("label");
    optionLabel.className = "admin-chip-option";
    const input = document.createElement("input");
    input.type = "checkbox";
    input.className = "admin-chip-option-input";
    input.dataset["testid"] = "admin-bottle-pour-option";
    input.dataset["ounces"] = option;
    input.checked = bottleFormPourOptions.has(option);
    input.addEventListener("change", () => {
      if (input.checked) {
        bottleFormPourOptions.add(option);
      } else {
        bottleFormPourOptions.delete(option);
      }
      refreshAddItemSteps();
    });
    const text = document.createElement("span");
    text.className = "admin-chip-option-label";
    text.textContent = `${option} oz`;
    optionLabel.append(input, text);
    bottlePourOptionsEl.append(optionLabel);
  }
  // VER57-F3: the load failed but the item's own saved sizes are painted,
  // so the area looks complete when it is not. Say the others are missing
  // and offer the same 44px retry after the chips. The saved chips keep
  // their checked state because it lives in bottleFormPourOptions, not in
  // the DOM this function rebuilds.
  if (sorted.length > 0 && !vocabulariesLoaded && vocabulariesLoadFailed) {
    const message = document.createElement("p");
    message.className = "admin-error";
    message.textContent = "Could not load other pour sizes.";
    const retry = document.createElement("button");
    retry.type = "button";
    retry.className = "btn-secondary";
    retry.textContent = "Try again";
    retry.addEventListener("click", retryVocabulariesFromPourArea);
    bottlePourOptionsEl.append(message, retry);
  }
}

// INV26 Slice 2 (DEC-206 item 3). Shows exactly one of the three panels
// and reflects the active tab on its own button (.active class,
// aria-selected), the same single-active-tab shape
// design/mockups/inv26-inventory-backbar.html's .item-tab/.item-tab.active
// establishes. Called from resetBottleForm (default "drink" for both Add
// and Edit; see the tab bar's own comment in index.html for why) and from
// each tab button's click handler below.
function switchBottleFormTab(tab: BottleFormTab): void {
  const formBody = document.querySelector<HTMLElement>("#admin-bottle-form-body");
  if (formBody !== null) formBody.scrollTop = 0;
  for (const candidate of BOTTLE_FORM_TABS) {
    const isActive = candidate === tab;
    bottleTabButtons[candidate].classList.toggle("active", isActive);
    bottleTabButtons[candidate].setAttribute("aria-selected", isActive ? "true" : "false");
    bottleTabPanels[candidate].hidden = !isActive;
  }
  refreshBottleFormFooter(tab);
  // INV26 Slice 3: the calculator reads the item's current preferred
  // vendor offer and size, both of which may have changed on another tab
  // since the sheet opened, so it re-renders on every tab switch rather
  // than only once when the sheet first opens.
  if (tab === "calculator") {
    renderBottleCalculator();
  }
  if (tab !== "drink") refreshAddItemSteps();
}

// ADDITEMNEXT1 (DEC-592, "Next for new, tabs to edit"). Adding a NEW item is a three-step flow: Next on steps 1 and 2
// checks only that step's fields, Back replaces Cancel on steps 2 and 3, and Save item shows on step 3 only (Save
// keeps CORE10's full validation). Editing keeps every tab and Save from any tab, with no step numbers on the labels.
function bottleFormIsNewItem(): boolean {
  return editingBottleId === undefined;
}

function bottleFormTabIndex(tab: BottleFormTab): number {
  return BOTTLE_FORM_TABS.indexOf(tab);
}

function activeBottleFormTab(): BottleFormTab {
  return BOTTLE_FORM_TABS.find((tab) => bottleTabButtons[tab].classList.contains("active")) ?? "drink";
}

// STARTSTOCK1 (DEC-601). The field shows only on a NEW item that tracks inventory, for a person who may record movements,
// and not for "Add from catalog" (a different create path). Its unit word follows the form, so the person sees what the
// number counts. Blank sends nothing; the field is cleared whenever it is hidden so a stale number is never saved.
function bottleStartStockApplies(): boolean {
  const s = sessionOrUndefined();
  return (
    bottleFormIsNewItem() &&
    catalogItemSetup === undefined &&
    itemTrackStockInput.checked &&
    s !== undefined &&
    hasPermission(s, "inv.movement.record")
  );
}

function currentBottleStartStockKind(): ReturnType<typeof startStockKindFor> {
  return startStockKindFor({
    wholeUnit: currentBottleSaleMechanism() === "whole_unit",
    containerType: bottleContainerTypeSelect.value,
    sizeValue: bottleSizeValueInput.value,
    sizeUnit: bottleSizeUnitInput.value,
    defaultKegSize: bottleDefaultKegSizeSelect.value,
    kegSizeVolumeOzByValue: kegSizeVolumeOzByValue(),
  });
}

function refreshBottleStartStock(): void {
  const applies = bottleStartStockApplies();
  bottleStartStockRow.hidden = !applies;
  if (!applies) {
    bottleStartStockInput.value = "";
    return;
  }
  bottleStartStockUnitEl.textContent = currentBottleStartStockKind().word;
}

/** The typed amount for a new item, or undefined when blank or not shown. Problems go to `issues` on the Stock & vendor tab. */
function collectBottleStartStock(issues: BottleFormIssue[]): string | undefined {
  if (!bottleStartStockApplies()) return undefined;
  const { kind, word } = currentBottleStartStockKind();
  const entry = parseStartStockText(kind, bottleStartStockInput.value);
  if (entry === "invalid") {
    issues.push({
      tab: "inventory",
      message:
        kind === "unit"
          ? "On hand now must be a whole number."
          : kind === "unsized"
            ? "On hand now must be a number of ounces, like 24 or 12.5."
            : `On hand now must be a number of ${word}, like 3 or 2.4 (tenths).`,
    });
    return undefined;
  }
  return entry === null ? undefined : normalizeLeadingDot(bottleStartStockInput.value);
}

/** ADDITEM2: after a new item is saved, creates the vendor option the person filled in on step 3, then sets the per-ounce price
 * through the existing calculated price path (preview, then a patch carrying the preview token). Returns the words for what
 * did not save, or undefined. The item itself is already saved either way. */
async function finishNewItemVendorAndPrice(
  s: NonNullable<ReturnType<typeof sessionOrUndefined>>,
  created: LiquorProductDto,
  sellingEditor: NonNullable<typeof f3SellingUi.editor>,
  pourOunceOptions: number[] | undefined,
): Promise<string | undefined> {
  const siteId = sellingEditor.siteId;
  const vendorId = bottleVendorOfferVendorSelect.value;
  let offerCost: number | undefined;
  if (vendorId.length > 0 && canManageVendorOfferStaged()) {
    if (siteId === undefined) return "its vendor option was not saved. Add it from the item.";
    const rawCost = bottleVendorOfferCostInput.value.trim();
    if (rawCost.length > 0) {
      try {
        offerCost = parseDollarsToCentsExact(rawCost, "Cost");
      } catch {
        return "its vendor option was not saved. Check the cost and add it from the item.";
      }
    }
    const orderUnit = bottleVendorOfferOrderUnitInput.value.trim();
    const rawPack = bottleVendorOfferPackSizeInput.value.trim();
    const packSize = orderUnit === "case" && rawPack.length > 0 ? Number(rawPack) : undefined;
    if (orderUnit === "case" && (packSize === undefined || !Number.isInteger(packSize) || packSize <= 0)) {
      return "its vendor option was not saved. Enter how many each case holds and add it from the item.";
    }
    const sku = bottleVendorOfferSkuInput.value.trim();
    const offer = await createVendorItemOffer(s.apiBaseUrl, s.tenantId, {
      ...buildVendorOfferRequest({
        vendorId,
        liquorProductId: created.id,
        vendorSku: sku.length === 0 || !ncCodeApplies() ? undefined : sku,
        orderUnit: orderUnit.length === 0 ? undefined : orderUnit,
        packSize,
        unitsPerCase: packSize,
        costCents: offerCost,
      }),
      locationId: siteId,
      requestId: crypto.randomUUID(),
    });
    if (offer.status !== 201) {
      return `its vendor option was not saved. ${offer.body.message ?? "Add it from the item."}`;
    }
  }
  // A per-ounce item with a cost and a markup gets its price now, from the server's own calculation.
  if (
    offerCost === undefined ||
    currentBottleSaleMechanism() !== "per_ounce" ||
    settingsPricingModel === "shelf" ||
    addItemMath === undefined
  ) {
    return undefined;
  }
  const targetMarkupPercent =
    addItemTypedPrice !== undefined && addItemMath.typed !== null
      ? addItemMath.typed.markupToSave
      : addItemMath.targetMarkupPercent;
  if (targetMarkupPercent === null) {
    return addItemTypedPrice !== undefined
      ? "its price was not saved. A price below cost cannot be set here; set it on the Price tab."
      : undefined;
  }
  const version = currentProductSelling(created, siteId)?.version ?? 0;
  const preview = await previewCalculatedCatalogPrice(s.apiBaseUrl, s.tenantId, created.id, {
    targetMarkupPercent,
    ...(siteId === undefined ? {} : { locationId: siteId, sellingVersion: version }),
  });
  if (preview.status !== 200 || preview.body.previewToken === null || preview.body.priceCents === null) {
    return "its price was not saved. Set it on the Price tab.";
  }
  const calculatedPrice = { targetMarkupPercent, previewToken: preview.body.previewToken };
  // BEERMECH1: the selling method is always sent when known, enforcement active or not (a keg is poured, so per ounce).
  const priceMechanism = bottleSaleMechanismForWrite();
  const mechanismFields =
    priceMechanism === undefined
      ? {}
      : { itemKind: bottleFormItemKind, saleMechanism: priceMechanism };
  const patch: UpdateLiquorProductInput =
    sellingEditor.context.readiness.state === "enforced" && siteId !== undefined
      ? {
          ...mechanismFields,
          locationId: siteId,
          sellingVersion: version,
          sellingRequestId: crypto.randomUUID(),
          sellingSettings: {
            source: "configured",
            unitPriceCents: null,
            calculatedPrice,
            pourOunceOptions: pourOunceOptions ?? null,
            shelfTier: bottleShelfTierSelect.value.length > 0 ? bottleShelfTierSelect.value : null,
            active: true,
            hideOnMenu: bottleHideOnMenuInput.checked,
            trackStock: itemTrackStockInput.checked,
          },
        }
      : { ...mechanismFields, calculatedPrice };
  const saved = await updateLiquorProduct(s.apiBaseUrl, s.tenantId, created.id, patch);
  return saved.status === 200 ? undefined : "its price was not saved. Set it on the Price tab.";
}

/** Records the starting stock of an item just created, through the very request Set starting stock sends. Returns the
 * words to show when it did not save, or undefined when it did. */
async function recordNewItemStartStock(
  s: NonNullable<ReturnType<typeof sessionOrUndefined>>,
  product: Parameters<typeof classifyStartingStockItem>[0],
  text: string,
  wantedKind: ReturnType<typeof startStockKindFor>["kind"],
): Promise<string | undefined> {
  // The create response carries no keg volume; it is the same keg size volume the form showed beside the field.
  const kegSize = product.default_keg_size;
  const kegVolume = kegSize === null ? undefined : kegSizeVolumeOzByValue().get(kegSize);
  const item = classifyStartingStockItem(
    kegVolume === undefined ? product : { ...product, default_keg_size_volume_oz: kegVolume },
  );
  const entry = parseStartStockText(item.kind, text);
  // The saved item must be counted in what the person saw beside the field; otherwise do not guess a unit.
  if (item.kind !== wantedKind || entry === null || entry === "invalid") {
    return "The unit on the saved item differs from the one shown.";
  }
  const request = buildStartingStockRequest(item, entry, `additem${Date.now().toString(36)}`);
  const result = await recordOpeningBalanceMovement(s.apiBaseUrl, s.tenantId, {
    idempotencyKey: request.idempotencyKey,
    liquorProductId: request.liquorProductId,
    quantity: request.quantity,
    unit: request.unit,
    note: request.note,
  });
  const outcome = classifyStartingStockResponse(result);
  return outcome.kind === "saved" ? undefined : outcome.message;
}

function refreshBottleFormFooter(tab: BottleFormTab): void {
  refreshBottleStartStock();
  const isNew = bottleFormIsNewItem();
  const index = bottleFormTabIndex(tab);
  const last = BOTTLE_FORM_TABS.length - 1;
  for (const candidate of BOTTLE_FORM_TABS) {
    const num = bottleTabButtons[candidate].querySelector<HTMLElement>(".tab-num");
    if (num !== null) num.hidden = !isNew;
  }
  bottleFormCancel.textContent = isNew && index > 0 ? "Back" : "Cancel";
  bottleFormNext.hidden = !(isNew && index < last);
  bottleFormSave.hidden = isNew && index < last;
}

// The same checks Save runs (see saveBottleForm), narrowed to the fields on one tab. Nothing is stored or sent.
function collectBottleStepIssues(tab: BottleFormTab): BottleFormIssue[] {
  const issues: BottleFormIssue[] = [];
  if (bottleNameInput.value.trim().length === 0) {
    issues.push({ tab: "drink", message: "Item name is required." });
  }
  const category =
    bottleFormItemKind === "liquor" ? bottleCategorySelect.value : bottleCategoryOtherSelect.value;
  if (category.length === 0) {
    issues.push({ tab: "drink", message: "Category is required." });
  }
  const saleMechanism = currentBottleSaleMechanism();
  const mechanismMissing = bottleKindMechanismIsExplicit() && saleMechanism === null;
  if (mechanismMissing) {
    issues.push({ tab: "drink", message: "Choose how this item sells." });
  } else if (saleMechanism === "whole_unit") {
    try {
      parseDollarsToCentsExact(bottleUnitPriceInput.value, "Sell price per unit");
    } catch (error) {
      issues.push({
        tab: "calculator",
        message: error instanceof Error ? error.message : "Invalid sell price.",
      });
    }
  } else if (bottleFormItemKind !== "liquor" && bottleFormPourOptions.size === 0) {
    issues.push({ tab: "inventory", message: "Pick at least one pour ounce option." });
  }
  collectInventoryItemFields(issues);
  return issues.filter((issue) => issue.tab === tab);
}

/** Moves a new item forward to `target`, one step at a time, stopping at the first step with a problem and showing
 * its messages there. Next and a forward tab click both come through here, so they block the same way. */
function advanceBottleFormTo(target: BottleFormTab): boolean {
  clearBottleFormIssues();
  for (let i = bottleFormTabIndex(activeBottleFormTab()); i < bottleFormTabIndex(target); i += 1) {
    const step = BOTTLE_FORM_TABS[i]!;
    const issues = collectBottleStepIssues(step);
    if (issues.length > 0) {
      showBottleFormIssues(issues);
      return false;
    }
  }
  switchBottleFormTab(target);
  return true;
}

function handleBottleFormTabClick(tab: BottleFormTab): void {
  if (
    bottleFormIsNewItem() &&
    bottleFormTabIndex(tab) > bottleFormTabIndex(activeBottleFormTab())
  ) {
    advanceBottleFormTo(tab);
    return;
  }
  switchBottleFormTab(tab);
}

/** STK1: shows or hides the editor's "Manual availability" block and
 * brings it in step with the item being edited. Hidden for a new item (no
 * flag exists before it is saved) and without inv.stock_flag.record. The
 * "since by" line shows only where this session knows it. */
function refreshBottleStockControl(): void {
  const bottle = editingBottle;
  const visible = bottle !== undefined && editingBottleId !== undefined && canRecordStockFlag();
  bottleStockBlock.hidden = !visible;
  bottleStockSavedEl.hidden = !visible || !bottleStockChangedThisOpen;
  if (!visible || bottle === undefined) {
    return;
  }
  bottleStockInButton.setAttribute("aria-checked", bottle.flagged86 ? "false" : "true");
  bottleStockOutButton.setAttribute("aria-checked", bottle.flagged86 ? "true" : "false");
  const line = bottle.flagged86 ? editorOutSinceText(stock86SinceFor(bottle.id)) : undefined;
  bottleStockSinceEl.hidden = line === undefined;
  bottleStockSinceEl.textContent = line ?? "";
}

/** The history read for the editor's line (inv.stock_flag.view only; a
 * session without it, or a failed read, simply has no line). Shares the
 * floor panel's own map, so Settings and the panel never disagree. */
async function loadBottleStockSince(): Promise<void> {
  const gen = loadGeneration;
  await loadStock86Since();
  if (!loadIsCurrent(gen)) {
    return;
  }
  refreshBottleStockControl();
}

function handleBottleStockChoice(flagged: boolean): void {
  const bottle = editingBottle;
  if (bottle === undefined || bottle.flagged86 === flagged) {
    return;
  }
  // INVUX1 (finding 18): 86 is written at once, so once it succeeds the editor says Cancel will not undo it.
  void setBottleStockFlag(bottle, flagged).then((ok) => {
    if (ok) {
      bottleStockChangedThisOpen = true;
      refreshBottleStockControl();
    }
  });
}

bottleStockInButton.addEventListener("click", () => handleBottleStockChoice(false));
bottleStockOutButton.addEventListener("click", () => handleBottleStockChoice(true));

function resetBottleForm(): void {
  bottleStockChangedThisOpen = false;
  bottleCategoryPicked = false;
  resetFindProduct();
  resetAddItemSteps();
  clearVendorOptionReturn();
  bottleExplicitLocationId = undefined;
  bottleVendorOffers = [];
  bottlePreferredByLocation = [];
  bottlePreferredByLocationEl.replaceChildren();
  bottlePreferredByLocationEl.hidden = true;
  bottleTermsContext = undefined;
  bottleEffectiveTerms.clear();
  bottleVendorReadState = "unverified";
  bottleVendorReadScope = undefined;
  bottlePreferredTermsByLocation.clear();
  clearBottleVendorAddFields();
  bottleTermsLoadSequence += 1;
  clearBottleFormIssues();
  bottleSizeConfirmRow.hidden = true;
  switchBottleFormTab("drink");
  bottleHideOnMenuInput.checked = false;
  itemTrackStockInput.checked = true;
  refreshItemAddonFields();
  bottleFormSave.disabled = false;
  bottleFormItemKind = "liquor";
  bottleFormSaleMechanism = null;
  bottleFormSaleMechanismTouched = false;
  renderBottleItemKindChips();
  bottleNameInput.value = "";
  populateBottleCategorySelect();
  populateBottleCategoryOtherSelect();
  bottleCurrentPerOuncePriceCents = null;
  bottleUnitPriceInput.value = "";
  // INV27: the calculator's own target-markup inputs start blank every
  // time the sheet (re)opens, never carrying a value forward from a
  // previously edited item; renderBottleCalculator's own
  // applyBottleCalcTargetMarkupDefaults then prefills each from Settings'
  // own default for the item's kind, once, from this true empty state
  // (DEC-227 item 3), and the touched flags reset so that prefill is
  // live again for the newly (re)opened sheet.
  bottleCalcOzTargetMarkupInput.value = "";
  bottleCalcBottleTargetMarkupInput.value = "";
  bottleCalcOzTargetMarkupTouched = false;
  bottleCalcBottleTargetMarkupTouched = false;
  bottleFormPourOptions = new Set();
  populateShelfTierSelect();
  bottleShelfTierSelect.value = "";
  renderBottlePourOptionChips();
  populateBottleContainerTypeSelect();
  // INV8 Section 2.7 (DEC-176): built once here, same as Container type
  // just above; openEditBottleForm only sets .value afterward.
  populateBottleDefaultKegSizeSelect();
  bottleDefaultKegSizeSelect.value = "";
  bottleStopOrderingInput.checked = false;
  bottleParLevelInput.disabled = false;
  bottleParLevelInput.value = "";
  // INVLOC1 Slice 2: nothing carries over from the last item; the section is drawn again when the locations have loaded.
  bottleStockLoaded = [];
  bottleStockLoadState = "idle";
  bottleStockLoadToken += 1;
  renderBottleStockSettings();
  bottleBarcodeInput.value = "";
  bottleCaseRow.hidden = true;
  bottleCaseBarcodeInput.value = "";
  bottleCaseSizeInput.value = "";
  // B4: neither field has been chosen by a user yet in this freshly
  // (re)opened form, so a default may apply once refreshBottleSizeFor-
  // ItemKind/refreshBottleContainerTypeForItemKind run below, for the
  // initial kind ("liquor") this function just set.
  bottleFormSizeUserSet = false;
  bottleFormContainerTypeUserSet = false;
  bottleSizeValueInput.value = "";
  bottleSizeUnitInput.value = "";
  bottleContainerTypeSelect.value = "";
  bottleStartStockInput.value = "";
  refreshBottleContainerTypeForItemKind();
  refreshBottleSizeForItemKind();
  // INV8 Section 2.6/2.7: visibility and hint both depend on the values
  // just set above, so both are refreshed once more at the end of this
  // function too (applyBottleItemKindVisibility below already follows
  // this same "recompute derived visibility after resetting the raw
  // fields" shape).
  refreshBottleDefaultKegSizeVisibility();
  refreshBottleParUnitHint();
  bottleAbvInput.value = "";
  applyBottleStrengthLabelForItemKind();
  applyBottleSizeHelpTextForItemKind();
  bottleProducerInput.value = "";
  bottleCountryRegionInput.value = "";
  bottleDescriptionInput.value = "";
  refreshBottleSaleMechanismForItemKind();
  applyBottleItemKindVisibility();
  // TF3: no product id exists for a form this function has just cleared
  // (Add mode) or is about to repopulate (Edit mode, immediately after
  // this call returns); either way there is nothing valid to show yet.
  bottleOnHandRow.hidden = true;
  bottleOnHandValueEl.textContent = "";
  bottleOnHandWarningEl.hidden = true;
  recordMovementButton.hidden = true;
  bottleOnHandSnapshot = undefined;
  resetBottleMovements();
  // INV19 Slice 1: same reasoning, one section earlier -- no product id
  // exists yet either way.
  bottlePourPriceOverrides = undefined;
  bottlePourPriceOverridesSection.hidden = true;
  bottlePourPriceOverridesEl.innerHTML = "";
  // VOC1-F4-F2 (Section 2.3): edit-only, see bottleFormModifiers' own
  // comment. Every reset (Add, and the start of every Edit before its own
  // fetch populates it) clears this and hides the section;
  // openEditBottleForm shows it once this product's real attachments are
  // loaded.
  bottleFormModifiers = new Map();
  bottleModifiersLoaded = false;
  bottleModifiersSection.hidden = true;
  bottleModifiersEl.replaceChildren();
  // STK1: shown for an existing item only (openEditBottleForm sets
  // editingBottle before calling this), hidden for a new one.
  refreshBottleStockControl();
}

async function openAddBottleForm(siteId = inventorySellingSiteId(), keepsDraft = true): Promise<boolean> {
  clearVendorOptionReturn();
  const active = session();
  const gen = loadGeneration;
  const opening = ++f3SellingUi.openSequence;
  adminToast("Loading item settings.", "neutral");
  const context = await loadLocalSellingShelves(siteId, gen, active);
  if (!context && opening === f3SellingUi.openSequence && loadIsCurrent(gen) && adminReadSessionIsCurrent(active) && (siteId === inventorySellingSiteId() || siteId === catalogPlanningLocationId)) {
    adminToast("Could not load item settings. Check your access and connection, then choose Add again.", "danger");
  }
  if (
    opening !== f3SellingUi.openSequence ||
    !context ||
    !loadIsCurrent(gen) ||
    !adminReadSessionIsCurrent(active) ||
    (siteId !== inventorySellingSiteId() && siteId !== catalogPlanningLocationId)
  )
    return false;

  const editorSession = session();
  clearCatalogItemSetup();
  editingBottleId = undefined;
  editingBottle = undefined;
  resetBottleForm();
  f3SellingUi.editor = {
    siteId: context.locationId ?? undefined,
    context,
    requestId: crypto.randomUUID(),
    generation: gen,
    session: editorSession,
    version: 0,
    uncertain: false,
  };
  bottleFormTitle.textContent = "Add item";
  showFindProduct();
  void loadBottleStockSettings(undefined);
  bottleActiveRow.hidden = true;
  // INV4: an offer always belongs to an EXISTING item; there is nothing
  // to assign a vendor to until Save has actually created a row.
  bottleVendorOffersEl.hidden = true;
  void ensureVendorsLoaded().then(() => {
    populateBottleVendorOfferVendorSelect();
    refreshAddItemSteps();
    rebaselineItemDraft();
  });
  refreshAddItemSteps();
  bottleFormScrim.hidden = false;
  lockBodyScroll();
  (findSection.hidden ? bottleNameInput : findInput).focus();
  if (vocabulariesLoadFailed) retryVocabulariesFromPourArea();
  // ADDITEMDRAFT1: a stored unsaved entry is offered back (not for Add from catalog, a different create path).
  if (keepsDraft) beginItemDraft();
  return true;
}

function applyCatalogLocalItemBase(): void {
  const setup = catalogItemSetup;
  if (!setup) return;
  resetFindProduct();
  const { product, kindKey, categoryKey, localSaleMechanism } = setup.selection;
  bottleFormItemKind = kindKey;
  bottleFormSaleMechanism = localSaleMechanism;
  bottleFormSaleMechanismTouched = true;
  renderBottleItemKindChips();
  populateBottleCategoryOtherSelect();
  bottleNameInput.value = product.name;
  const category =
    kindKey === "liquor" ? bottleCategorySelect : bottleCategoryOtherSelect;
  if (
    !Array.from(category.options).some((option) => option.value === categoryKey)
  )
    category.add(new Option(categoryKey, categoryKey));
  category.value = categoryKey;
  bottleFormSizeUserSet = true;
  bottleFormContainerTypeUserSet = true;
  bottleSizeValueInput.value = product.size_value;
  bottleSizeUnitInput.value = product.size_unit;
  refreshBottleSizeForItemKind();
  if (
    !Array.from(bottleContainerTypeSelect.options).some(
      (option) => option.value === product.package_class,
    )
  )
    bottleContainerTypeSelect.add(
      new Option(product.package_class, product.package_class),
    );
  bottleContainerTypeSelect.value = product.package_class;
  bottleBarcodeInput.value = product.upc ?? "";
  applyBottleItemKindVisibility();
  // Redraw from the actual kind while retaining the local pours already selected in the existing set.
  renderBottlePourOptionChips();
  applyBottleStrengthLabelForItemKind();
  applyBottleSizeHelpTextForItemKind();
  refreshBottleDefaultKegSizeVisibility();
  refreshBottleParUnitHint();
  const controls: [
    HTMLInputElement | HTMLSelectElement | HTMLButtonElement,
    ...(HTMLInputElement | HTMLSelectElement | HTMLButtonElement)[],
  ] = [
    bottleNameInput,
    bottleCategorySelect,
    bottleCategoryOtherSelect,
    bottleSizeValueInput,
    bottleSizeUnitInput,
    bottleSizeSelect,
    bottleContainerTypeSelect,
    bottleBarcodeInput,
  ];
  for (const parent of [bottleItemKindEl, bottleSaleMechanismGroup])
    controls.push(
      ...parent.querySelectorAll<HTMLInputElement | HTMLButtonElement>(
        "input,button",
      ),
    );
  for (const control of controls) {
    if (!catalogFixedControls.has(control))
      catalogFixedControls.set(control, control.disabled);
    control.disabled = true;
  }
}
async function openCatalogLocalItemSetup(
  selection: CatalogLocalCreateSelection,
  isCurrent: () => boolean,
  completed: () => void,
): Promise<void> {
  const siteId = catalogPlanningLocationId;
  if (!siteId || !isCurrent() || !hasPermission(session(), "admin.liquor_product.manage")) return;
  if (!(await openAddBottleForm(siteId, false)) || !isCurrent() || catalogPlanningLocationId !== siteId)
    return;
  catalogItemSetup = {
    selection,
    locationId: siteId,
    isCurrent,
    completed,
    uncertain: false,
  };
  applyCatalogLocalItemBase();
  bottleFormTitle.textContent = `Add ${selection.product.name} from catalog`;
  switchBottleFormTab(
    selection.localSaleMechanism === "whole_unit" || selection.kindKey !== "liquor"
      ? "calculator"
      : "inventory",
  );
}

bottleSizeConfirmButton.addEventListener("click", () => {
  void confirmBottleSize();
});

async function confirmBottleSize(): Promise<void> {
  const id = editingBottleId;
  if (id === undefined) return;
  const s = session();
  bottleSizeConfirmButton.disabled = true;
  try {
    const result = await confirmLiquorSize(s.apiBaseUrl, s.tenantId, id);
    if (result.status !== 200) {
      bottleFormError.textContent =
        result.body.message ?? result.body.error ?? "Could not confirm this size.";
      return;
    }
    bottleSizeConfirmRow.hidden = true;
    adminToast("Size confirmed.", "success");
    await loadBottles();
  } finally {
    bottleSizeConfirmButton.disabled = false;
  }
}

async function openEditBottleForm(
  bottle: LiquorProductDto,
  requestedLocationId?: string,
  isCurrent?: () => boolean,
): Promise<void> {
  clearVendorOptionReturn();
  const active = session();
  const gen = loadGeneration;
  const opening = ++f3SellingUi.openSequence;
  const siteId = requestedLocationId ?? inventorySellingSiteId();
  const reply = await getLiquorProduct(active.apiBaseUrl, active.tenantId, bottle.id, siteId);
  if (
    (isCurrent !== undefined && !isCurrent()) ||
    opening !== f3SellingUi.openSequence ||
    !loadIsCurrent(gen) ||
    sessionOrUndefined() !== active ||
    siteId !== (requestedLocationId ?? inventorySellingSiteId())
  )
    return;
  if (reply.status !== 200 || reply.body.sellingContext === undefined) {
    adminToast(reply.body.message ?? "Location selling settings unavailable.", "danger");
    return;
  }
  const context = reply.body.sellingContext;
  if (siteId !== undefined && context.locationId !== siteId) return;
  await loadLocalSellingShelves(context.locationId ?? undefined, gen, active);
  if (
    (isCurrent !== undefined && !isCurrent()) ||
    opening !== f3SellingUi.openSequence ||
    !loadIsCurrent(gen) ||
    sessionOrUndefined() !== active ||
    siteId !== (requestedLocationId ?? inventorySellingSiteId())
  )
    return;
  bottle = reply.body.liquorProduct;

  clearCatalogItemSetup();
  editingBottleId = bottle.id;
  editingBottle = bottle;
  resetBottleForm();
  bottleExplicitLocationId = requestedLocationId;
  f3SellingUi.editor = {
    siteId: context.locationId ?? undefined,
    context,
    requestId: crypto.randomUUID(),
    generation: gen,
    session: active,
    version: bottle.selling?.version ?? 0,
    uncertain: false,
  };
  const localSelling = currentProductSelling(bottle, context.locationId ?? undefined);
  bottleFormTitle.textContent = `Edit ${bottle.name}`;
  // P11-F1 (DEC-372): the confirm action is for a person who may manage items (permission, never a role name).
  bottleSizeConfirmRow.hidden =
    !bottle.size_needs_confirmation || !hasPermission(session(), "admin.liquor_product.manage");
  bottleFormItemKind = bottle.item_kind;
  renderBottleItemKindChips();
  bottleFormSaleMechanism = kindMechanismIsExplicit(bottle.item_kind)
    ? (bottle.saleMechanism ?? (bottle.unit_price_cents !== null ? "whole_unit" : "per_ounce"))
    : null;
  bottleFormSaleMechanismTouched = true;
  bottleNameInput.value = bottle.name;
  if (bottle.item_kind === "liquor") {
    bottleCategorySelect.value = bottle.category;
  } else {
    // populateBottleCategoryOtherSelect reads bottleFormItemKind (just set
    // above to this bottle's own real kind) and editingBottle (set above
    // too) to both offer the right vocabulary and restore this bottle's
    // own stored value, including a value that has since been retired.
    populateBottleCategoryOtherSelect();
  }
  // ITEM2 Slice 5 (Section 3.4): used to be `bottle.item_kind === "packaged"`;
  // generalized to the EXISTING product's own already-known field shape
  // (`unit_price_cents` non-null exactly on a whole-unit product), the
  // same derivation bottleRowPriceText above uses, since this is editing a
  // real, already-saved product rather than the create path.
  const savedSaleMechanism = itemKindPolicyIsActive(bottle.item_kind)
    ? bottle.saleMechanism
    : bottle.unit_price_cents !== null
      ? "whole_unit"
      : "per_ounce";
  if (savedSaleMechanism === "whole_unit") {
    bottleUnitPriceInput.value =
      localSelling?.unitPriceCents == null ? "" : (localSelling.unitPriceCents / 100).toFixed(2);
  } else {
    bottleCurrentPerOuncePriceCents = localSelling?.perOuncePriceCents ?? null;
    bottleFormPourOptions = new Set(
      (localSelling?.pourOunceOptions ?? []).map((value) => String(value)),
    );
    renderBottlePourOptionChips();
  }
  bottleShelfTierSelect.value = localSelling?.shelfTier ?? "";
  // INVLOC1 Slice 2 (DEC-326): par and bin are read per location, from the stock-settings route, not from the item row.
  void loadBottleStockSettings(bottle.id);
  bottleBarcodeInput.value = bottle.barcode ?? "";
  // B6-F1: permission, never a role name; create mode keeps the row hidden (resetBottleForm).
  bottleCaseRow.hidden = !hasPermission(session(), "admin.liquor_product.manage");
  bottleCaseBarcodeInput.value = bottle.case_barcode ?? "";
  bottleCaseSizeInput.value =
    bottle.case_size === null || bottle.case_size === undefined ? "" : String(bottle.case_size);
  bottleSizeValueInput.value = bottle.size_value ?? "";
  bottleSizeUnitInput.value = bottle.size_unit ?? "";
  // B4 rule 1 (never true here anyway, since editingBottleId is already
  // set above, but stated for the reader): edit mode never applies a
  // default. This only re-derives the <select> from the values just set,
  // filtered by this bottle's own item kind, falling back to a disabled
  // off-list display option with those exact values intact when the
  // stored size predates the vocabulary or belongs to a kind whose list
  // does not offer it (INV15).
  refreshBottleSizeForItemKind();
  bottleContainerTypeSelect.value = bottle.container_type ?? "";
  // INV8 Section 2.7 (DEC-176): same pattern as Container type just
  // above -- resetBottleForm already built the <select>'s options
  // (including a merged-back-in retired value) while editingBottle was
  // already set; this only picks the bottle's own stored value from it.
  bottleDefaultKegSizeSelect.value = bottle.default_keg_size ?? "";
  refreshBottleDefaultKegSizeVisibility();
  // B3: the stored fact is always ABV basis points; the displayed string
  // is converted to THIS bottle's own item kind's unit (Proof for
  // liquor, ABV % otherwise) at the presentation boundary.
  bottleAbvInput.value =
    bottle.abv_basis_points === null
      ? ""
      : abvBasisPointsToStrengthDisplayString(bottle.abv_basis_points, bottleFormItemKind);
  applyBottleStrengthLabelForItemKind();
  applyBottleSizeHelpTextForItemKind();
  bottleProducerInput.value = bottle.producer ?? "";
  bottleCountryRegionInput.value = bottle.country_region ?? "";
  bottleDescriptionInput.value = bottle.description_notes ?? "";
  renderBottleSaleMechanismControls();
  applyBottleItemKindVisibility();
  // INV8 Section 2.6: every input the hint depends on is set by this
  // point, so this is the last computation this function needs for it.
  refreshBottleParUnitHint();
  bottleActiveRow.hidden = false;
  bottleActiveInput.checked = localSelling?.active === true;
  // INV26 Slice 2 (DEC-207 item 3).
  bottleHideOnMenuInput.checked = localSelling?.hideOnMenu !== false;
  itemTrackStockInput.checked = localSelling?.trackStock === true;
  refreshItemAddonFields();
  // UX80: On hand and Recent movements moved to the Stock levels sheet
  // (openStockDetail); the item editor keeps size, par and vendors.
  // STK1: the "86'd since ... by ..." line, where this session may read it.
  void loadBottleStockSince();
  // INV19 Slice 1: this bottle's own pour-price overrides, now that its
  // id is known. Re-rendered once bottleFormPourOptions is populated just
  // above (renderBottlePourPriceOverrides reads bottleFormPourOptions
  // itself, so the ordering of these two async loads relative to each
  // other does not matter).
  void loadBottlePourPriceOverrides(bottle.id);
  // VOC1-F4-F2 (Section 2.3): this product's own attached modifiers, now
  // that its id is known, mirroring loadBottlePourPriceOverrides' own
  // identical fire-and-forget shape immediately above.
  void loadBottleModifiers(bottle.id);
  // INV4: this item's own vendor offers, now that its id is known.
  bottleVendorOffersEl.hidden = false;
  // VEN5: fire-and-forget, matching loadBottleOnHand/loadBottleVendor-
  // Offers immediately around it -- the form itself opens synchronously
  // below regardless of how long the vendor fetch takes, and the picker
  // fills in once ensureVendorsLoaded resolves (already-loaded is an
  // instant no-op; a session that has never visited Purchasing pays one
  // real fetch here instead of finding a permanently empty picker).
  void refreshBottleVendorOfferPicker();
  void loadBottleVendorOffers(bottle.id);
  // A saved item keeps Manual availability, Barcode, Case size and Hide on menu where they always were: in view, not behind a tap.
  bottleMoreOptionsEl.open = true;
  refreshAddItemSteps();
  bottleFormScrim.hidden = false;
  lockBodyScroll();
  // VER57-F3: same single automatic retry Add item makes (F2).
  if (vocabulariesLoadFailed) retryVocabulariesFromPourArea();
  // ADDITEMDRAFT1: a stored unsaved edit of this item is offered back.
  beginItemDraft();
}

function closeBottleForm(): void {
  const returnToCart = vendorOptionReturn;
  clearVendorOptionReturn();
  f3SellingUi.openSequence += 1;
  delete f3SellingUi.editor;
  clearCatalogItemSetup();
  if (bottleFormScrim.hidden) {
    return;
  }
  // ADDITEMDRAFT1: what was typed is written before the sheet goes; a closed sheet keeps its draft.
  endItemDraftSession();
  bottleFormScrim.hidden = true;
  if (bottleListVendorOfferWrites !== vendorOfferWrites() && sessionOrUndefined() !== undefined) void loadBottles();
  bottleTermsLoadSequence += 1;
  bottleStockLoadToken += 1;
  unlockBodyScroll();
  const returnToRecipe = recipeInventoryReturn;
  recipeInventoryReturn = undefined;
  returnToRecipe?.();
  if (returnToCart?.isCurrent()) returnToCart.onClose();
}

// ---------------------------------------------------------------------
// INVLOC1 Slice 2 (docs/build-briefs/INVLOC1-S2-par-and-bin-label-per-bar.md; DEC-326): the item editor's par and bin,
// per location. One location: the two fields, labelled "at <location>". More than one: a table, one row per location, this tablet's first.
// None: a sentence. One Save either way (collectInventoryItemFields reads whatever is on screen). The locations are the ones
// the server says the person may plan for, so the screen never offers a location the write would refuse.
// ---------------------------------------------------------------------

/** The unit the existing hint resolves, from the same fields it reads. Null when none resolves. */
function currentParCountUnit(): "unit" | "keg" | "bottle" | "can" | null {
  return resolveClientCountUnit({
    saleMechanismIsWholeUnit: itemKindSellsWholeUnit(bottleFormItemKind),
    containerType:
      bottleContainerTypeSelect.value.length > 0 ? bottleContainerTypeSelect.value : null,
    hasDefaultKegSize: bottleDefaultKegSizeSelect.value.length > 0,
  });
}

async function loadBottleStockSettings(productId: string | undefined): Promise<void> {
  const token = ++bottleStockLoadToken;
  const gen = loadGeneration;
  bottleStockLoaded = [];
  bottleStockLoadState = "loading";
  renderBottleStockSettings();
  const s = sessionOrUndefined();
  if (s === undefined) {
    return;
  }
  let loaded: LocationSettingsEntry[] | undefined;
  if (productId === undefined) {
    const result = await getPlanningLocations(s.apiBaseUrl, s.tenantId);
    if (result.status === 200) {
      loaded = result.body.locations.map((siteLocation) => ({
        locationId: siteLocation.id,
        locationName: siteLocation.name,
        isThisTablet: siteLocation.isThisTablet,
        parLevel: null,
        binLabel: null,
      }));
    }
  } else {
    const result = await getLiquorProductStockSettings(s.apiBaseUrl, s.tenantId, productId);
    if (result.status === 200) {
      loaded = result.body.stockSettings;
    }
  }
  if (token !== bottleStockLoadToken || !loadIsCurrent(gen)) {
    return;
  }
  // UX88 (DEC-339): the locations table lists this tablet's location first (deliberate, tagged "this tablet"), then the rest A to Z.
  bottleStockLoaded = sortByName(loaded ?? [], (siteLocation) => siteLocation.locationName, {
    pinned: (siteLocation) => siteLocation.isThisTablet,
  });
  bottleStockLoadState = loaded === undefined ? "failed" : "ready";
  renderBottleStockSettings();
}

function setStockSectionVisibility(layout: "none" | "single" | "table" | "status"): void {
  bottleStopOrderingHelpEl.hidden = layout !== "single" && layout !== "table";
  bottleStockSingleEl.hidden = layout !== "single";
  bottleStockTableEl.hidden = layout !== "table";
  bottleParLevelHintTableEl.hidden =
    layout !== "table" || bottleParLevelHintTableEl.textContent === "";
  bottleStockStatusEl.hidden = layout !== "status" && layout !== "none";
  bottleStockIntroEl.hidden = layout !== "single" && layout !== "table";
  if (layout !== "table") {
    bottleStockNoteEl.hidden = true;
  }
}

function renderBottleStockSettings(): void {
  bottleStockTableInputs = new Map();
  bottleStockParHeaderEl = undefined;
  bottleStockTableEl.innerHTML = "";
  if (bottleStockLoadState === "idle" || bottleStockLoadState === "loading") {
    bottleStockHeadingLabelEl.textContent = tableCopy(null).heading;
    bottleStockStatusEl.textContent =
      bottleStockLoadState === "loading" ? "Loading your locations…" : "";
    setStockSectionVisibility("status");
    bottleStockStatusEl.hidden = bottleStockLoadState === "idle";
    return;
  }
  if (bottleStockLoadState === "failed") {
    bottleStockHeadingLabelEl.textContent = tableCopy(null).heading;
    bottleStockStatusEl.textContent =
      "Could not load par for your locations. Close this and open the item again.";
    setStockSectionVisibility("status");
    return;
  }
  const layout = editorLayout(bottleStockLoaded);
  if (layout === "none") {
    bottleStockHeadingLabelEl.textContent = tableCopy(null).heading;
    bottleStockStatusEl.textContent = NO_LOCATION_COPY;
    setStockSectionVisibility("none");
    return;
  }
  if (layout === "single") {
    const siteLocation = bottleStockLoaded[0] as LocationSettingsEntry;
    const copy = singleLocationCopy(siteLocation.locationName);
    bottleStockHeadingLabelEl.textContent = copy.heading;
    bottleStockIntroEl.textContent = copy.intro;
    bottleParLevelLabelEl.textContent = copy.parLabel;
    bottleStopOrderingInput.checked = siteLocation.stopOrdering === true;
    bottleParLevelInput.disabled = bottleStopOrderingInput.checked;
    bottleParLevelInput.value = bottleStopOrderingInput.checked
      ? ""
      : (siteLocation.parLevel ?? "");
    refreshBottleParUnitHint();
    setStockSectionVisibility("single");
    applyPendingItemDraftStock();
    return;
  }
  const copy = tableCopy(parUnitWord(currentParCountUnit()));
  bottleStockHeadingLabelEl.textContent = copy.heading;
  bottleStockIntroEl.textContent = copy.intro;
  const head = document.createElement("div");
  head.className = "inv-locations-head";
  head.setAttribute("role", "row");
  for (const [text, isPar] of [
    [copy.locationHeader, false],
    [copy.parHeader, true],
    ["Stop ordering", false],
  ] as const) {
    const cell = document.createElement("span");
    cell.setAttribute("role", "columnheader");
    cell.textContent = text;
    if (isPar) bottleStockParHeaderEl = cell;
    head.append(cell);
  }
  bottleStockTableEl.append(head);
  for (const siteLocation of bottleStockLoaded) {
    const row = document.createElement("div");
    row.className = siteLocation.isThisTablet
      ? "inv-locations-row inv-locations-row--here"
      : "inv-locations-row";
    row.setAttribute("role", "row");
    row.dataset["testid"] = `admin-bottle-stock-row-${siteLocation.locationId}`;
    const nameCell = document.createElement("div");
    nameCell.setAttribute("role", "rowheader");
    const name = document.createElement("span");
    name.className = "inv-locations-location-name";
    name.textContent = siteLocation.locationName;
    nameCell.append(name);
    if (siteLocation.isThisTablet) {
      const tag = document.createElement("span");
      tag.className = "inv-locations-tag";
      tag.textContent = copy.thisTabletTag;
      nameCell.append(tag);
    }
    const par = document.createElement("input");
    par.className = "field-input";
    par.type = "number";
    par.inputMode = "decimal";
    par.min = "0";
    par.step = "0.01";
    par.placeholder = copy.parPlaceholder;
    par.value = siteLocation.parLevel ?? "";
    par.setAttribute("aria-label", `Par at ${siteLocation.locationName}`);
    describeSetting(par, "admin-bottle-stock-intro");
    par.dataset["testid"] = `admin-bottle-stock-par-${siteLocation.locationId}`;
    const parCell = document.createElement("div");
    parCell.setAttribute("role", "cell");
    parCell.append(par);
    const stopCell = document.createElement("div");
    stopCell.setAttribute("role", "cell");
    const stopLabel = document.createElement("label");
    stopLabel.className = "admin-toggle-label admin-field-toggle";
    const stopOrdering = document.createElement("input");
    stopOrdering.type = "checkbox";
    stopOrdering.className = "stock-ordering-switch";
    stopOrdering.setAttribute("role", "switch");
    stopOrdering.setAttribute("aria-label", `Stop ordering at ${siteLocation.locationName}`);
    stopOrdering.setAttribute("aria-describedby", "admin-bottle-stop-ordering-help");
    stopOrdering.checked = siteLocation.stopOrdering === true;
    par.disabled = stopOrdering.checked;
    if (stopOrdering.checked) par.value = "";
    stopOrdering.addEventListener("change", () => {
      par.disabled = stopOrdering.checked;
      if (stopOrdering.checked) par.value = "";
    });
    const stopTrack = document.createElement("span");
    stopTrack.className = "admin-toggle-track";
    stopLabel.append(stopOrdering, stopTrack);
    stopCell.append(stopLabel);
    row.append(nameCell, parCell, stopCell);
    bottleStockTableEl.append(row);
    bottleStockTableInputs.set(siteLocation.locationId, { par, stopOrdering });
  }
  // Mockup 3: a location with nothing set while another location has a par or bin gets the dashed note and the disabled
  // "Copy pars from" button, marked coming later.
  const note = emptyLocationNote(bottleStockLoaded);
  if (note !== null) {
    const noteCopy = emptyLocationNoteCopy(note);
    bottleStockNoteLeadEl.textContent = noteCopy.lead;
    bottleStockNoteBodyEl.textContent = ` ${noteCopy.body}`;
    bottleStockCopyButton.textContent = noteCopy.copyButton;
    bottleStockCopyLaterEl.textContent = noteCopy.later;
  }
  refreshBottleParUnitHint();
  setStockSectionVisibility("table");
  bottleStockNoteEl.hidden = note === null;
  applyPendingItemDraftStock();
}

/** What one Save sends for the locations: the changed locations only (see stockSettingsModel.buildStockSettingsPayload). While the
 * locations are loading, failed to load, or there are none, there is nothing typed to send. */
function collectBottleStockSettings(): StockSettingsPayload {
  if (bottleStockLoadState !== "ready") {
    return { ok: true, entries: [] };
  }
  const layout = editorLayout(bottleStockLoaded);
  if (layout === "none") {
    return { ok: true, entries: [] };
  }
  const typed: TypedLocationValues[] =
    layout === "single"
      ? [
          {
            locationId: (bottleStockLoaded[0] as LocationSettingsEntry).locationId,
            par: bottleParLevelInput.value,
            stopOrdering: bottleStopOrderingInput.checked,
            bin: "",
          },
        ]
      : bottleStockLoaded.map((siteLocation) => ({
          locationId: siteLocation.locationId,
          par: bottleStockTableInputs.get(siteLocation.locationId)?.par.value ?? "",
          stopOrdering:
            bottleStockTableInputs.get(siteLocation.locationId)?.stopOrdering.checked ?? false,
          bin: "",
        }));
  return buildStockSettingsPayload(bottleStockLoaded, typed, layout === "table", false);
}

// Inventory track Slice 3, Amendment 2026-08-22 (eleventh): the
// Inventory-group fields, shared unchanged by create and update (mirrors
// InventoryItemFieldsInput on the api.ts side). Every field is optional;
// this returns undefined and sets bottleFormError only when a FILLED-IN
// field fails to parse exactly (a blank field is simply omitted, never an
// error: "fill in as much as you have on hand" per the form's own help
// text). par level and size use the same exact, rejecting decimal parser
// dollars-and-cents fields use (parseDollarsToCentsExact's own
// parseExactDecimalToSmallestUnit), at 2 decimal places, so "1.005"
// bottles is rejected by name rather than silently truncated; ABV reuses
// the settings screen's own percent-to-basis-points parser unchanged,
// same exactness guarantee.
function collectInventoryItemFields(
  issues: BottleFormIssue[],
): InventoryItemFieldsInput | undefined {
  const issuesBefore = issues.length;
  const fields: InventoryItemFieldsInput = {
    trackStock: itemTrackStockInput.checked,
  };
  // INVLOC1 Slice 2 (DEC-326): par level and bin label are per location. This sends one entry per location whose typed values
  // differ from what was loaded (a blank field clears), parsed with the same exact, rejecting decimal parser as before;
  // a location the person did not touch sends nothing.
  const stock = collectBottleStockSettings();
  if (!stock.ok) {
    issues.push({ tab: "inventory", message: stock.message });
  } else if (stock.entries.length > 0) {
    fields.stockSettings = stock.entries;
  }
  collectBottleStartStock(issues);
  // ADDITEM2 (DEC-610): a new keg is described by its keg size and its pours; the Size field is not asked for a keg.
  const newKeg = bottleFormIsNewItem() && bottleContainerTypeSelect.value === "keg";
  const sizeValueRaw = newKeg ? "" : bottleSizeValueInput.value.trim();
  if (catalogItemSetup === undefined && sizeValueRaw.length > 0) {
    try {
      fields.sizeValue = parseExactDecimalToSmallestUnit(sizeValueRaw, 2, "Size", "0.01") / 100;
    } catch (error) {
      issues.push({
        tab: "inventory",
        message: error instanceof Error ? error.message : "Invalid size.",
      });
    }
  }
  const sizeUnit = newKeg ? "" : bottleSizeUnitInput.value.trim();
  if (sizeUnit.length > 0) {
    fields.sizeUnit = sizeUnit;
  }
  // ADDITEM2 (DEC-611): foam and waste for a keg, sent only when the person changed what the form showed.
  const kegWaste = collectBottleKegWaste(issues);
  if (kegWaste !== undefined) fields.kegWasteBasisPoints = kegWaste;
  if (bottleContainerTypeSelect.value.length > 0) {
    fields.containerType = bottleContainerTypeSelect.value;
  }
  // INV8 Section 2.7 (DEC-176): wired through the same shared Create/
  // Update fields object every other Inventory-group field already uses.
  // Gated on Container type being "keg" (not merely on the select having
  // a value): the row is hidden for any other container type, and a
  // residual selection left over from before a container-type switch
  // must never be silently resubmitted for a product that is no longer a
  // keg. Blank ("Unspecified") is simply omitted, the same "fill in as
  // much as you have" convention every other optional field here follows
  // -- never a forced clear of an already-stored value the person did not
  // touch.
  if (bottleContainerTypeSelect.value === "keg" && bottleDefaultKegSizeSelect.value.length > 0) {
    fields.defaultKegSize = bottleDefaultKegSizeSelect.value;
  }
  // B3: parsed under whichever unit the currently selected item kind
  // displays (Proof for liquor, ABV % otherwise), then converted back to
  // the one stored fact, ABV basis points.
  const abvRaw = bottleAbvInput.value.trim();
  if (abvRaw.length > 0) {
    try {
      fields.abvBasisPoints = parseStrengthDisplayStringToAbvBasisPoints(
        abvRaw,
        bottleFormItemKind,
      );
    } catch (error) {
      issues.push({
        tab: "drink",
        message:
          error instanceof Error
            ? error.message
            : `Invalid ${strengthFieldLabel(bottleFormItemKind)}.`,
      });
    }
  }
  const producer = bottleProducerInput.value.trim();
  if (producer.length > 0) {
    fields.producer = producer;
  }
  const countryRegion = bottleCountryRegionInput.value.trim();
  if (countryRegion.length > 0) {
    fields.countryRegion = countryRegion;
  }
  const descriptionNotes = bottleDescriptionInput.value.trim();
  if (descriptionNotes.length > 0) {
    fields.descriptionNotes = descriptionNotes;
  }
  return issues.length > issuesBefore ? undefined : fields;
}

// CORE10 (core walkthrough 2026-10-10, item 12): Add item used to report one error at a time, and often on a tab that
// was not showing. Save now collects every error across the three tabs, then jumps to the first tab (in tab order)
// that has one, marks every tab that has one, and shows every message together.
interface BottleFormIssue {
  readonly tab: BottleFormTab;
  readonly message: string;
}

function clearBottleFormIssues(): void {
  bottleFormError.textContent = "";
  for (const tab of BOTTLE_FORM_TABS) {
    bottleTabButtons[tab].querySelector(".item-tab-error-mark")?.remove();
    delete bottleTabButtons[tab].dataset["hasErrors"];
  }
}

function showBottleFormIssues(issues: readonly BottleFormIssue[]): void {
  clearBottleFormIssues();
  for (const issue of issues) {
    const line = document.createElement("span");
    line.className = "admin-form-error-line";
    line.dataset["testid"] = "admin-bottle-form-error-line";
    line.dataset["tab"] = issue.tab;
    line.textContent = issue.message;
    bottleFormError.append(line);
  }
  for (const tab of BOTTLE_FORM_TABS) {
    if (!issues.some((issue) => issue.tab === tab)) {
      continue;
    }
    const button = bottleTabButtons[tab];
    button.dataset["hasErrors"] = "true";
    const mark = document.createElement("span");
    mark.className = "item-tab-error-mark";
    mark.dataset["testid"] = "admin-bottle-tab-error-mark";
    const bang = document.createElement("span");
    bang.setAttribute("aria-hidden", "true");
    bang.textContent = "!";
    const words = document.createElement("span");
    words.className = "item-tab-error-words";
    words.textContent = "has errors";
    mark.append(bang, words);
    button.append(mark);
  }
  const first = BOTTLE_FORM_TABS.find((tab) => issues.some((issue) => issue.tab === tab));
  if (first !== undefined) {
    switchBottleFormTab(first);
  }
}

// B6-F1. The case size and case barcode of an existing item, as the PATCH body wants them: a number or a code when it
// changed, null when the person emptied a stored value, nothing when untouched. Returns undefined (issues filled) on a
// size that is not a whole number from 1 to 100000. Nothing is sent without admin.liquor_product.manage or on a new item.
function collectBottleCaseFields(
  issues: BottleFormIssue[],
  s: ReturnType<typeof session>,
): Pick<UpdateLiquorProductInput, "caseSize" | "caseBarcode" | "replaceCaseBarcode"> | undefined {
  const fields: Pick<UpdateLiquorProductInput, "caseSize" | "caseBarcode" | "replaceCaseBarcode"> =
    {};
  if (editingBottleId === undefined || editingBottle === undefined) return fields;
  if (!hasPermission(s, "admin.liquor_product.manage")) return fields;
  const storedSize = editingBottle.case_size ?? null;
  const sizeRaw = bottleCaseSizeInput.value.trim();
  let size: number | null = null;
  if (sizeRaw.length > 0) {
    const parsed = Number(sizeRaw);
    if (!/^\d+$/.test(sizeRaw) || parsed < 1 || parsed > MAX_BOTTLE_CASE_SIZE) {
      issues.push({
        tab: "inventory",
        message: `Case size must be a whole number from 1 to ${MAX_BOTTLE_CASE_SIZE}.`,
      });
      return undefined;
    }
    size = parsed;
  }
  if (size !== storedSize) fields.caseSize = size;
  const storedCode = editingBottle.case_barcode ?? null;
  const codeRaw = bottleCaseBarcodeInput.value.trim();
  const code = codeRaw.length > 0 ? codeRaw : null;
  if (code !== storedCode) {
    if (code !== null && size === null) {
      issues.push({ tab: "inventory", message: "Set a case size to add a case barcode." });
      return undefined;
    }
    fields.caseBarcode = code;
  }
  return fields;
}

/** ADDITEM2: on a saved item, a price typed on step 3 is set through the same calculated price path the calculator uses:
 * the server turns the price into the markup that lands on it, previews that markup, and the save carries the preview token.
 * Returns undefined when nothing was typed, null when the problem has been shown, or the calculation to save. */
async function prepareTypedPourPrice(): Promise<
  { targetMarkupPercent: number; previewToken: string } | undefined | null
> {
  const typed = addItemMath?.typed;
  const s = sessionOrUndefined();
  if (
    addItemTypedPrice === undefined ||
    typed === undefined ||
    typed === null ||
    editingBottleId === undefined ||
    s === undefined ||
    currentBottleSaleMechanism() !== "per_ounce" ||
    settingsPricingModel === "shelf"
  ) {
    return undefined;
  }
  if (typed.belowCost || typed.markupToSave === null) {
    bottleFormError.textContent = "That price is below what the item costs. Enter a price at or above cost.";
    return null;
  }
  const editor = f3SellingUi.editor;
  const preview = await previewCalculatedCatalogPrice(s.apiBaseUrl, s.tenantId, editingBottleId, {
    targetMarkupPercent: typed.markupToSave,
    ...(editor?.siteId === undefined ? {} : { locationId: editor.siteId, sellingVersion: editor.version }),
  });
  if (preview.status !== 200 || preview.body.previewToken === null || preview.body.priceCents === null) {
    bottleFormError.textContent = preview.body.message ?? "Cost unavailable.";
    return null;
  }
  return { targetMarkupPercent: typed.markupToSave, previewToken: preview.body.previewToken };
}

async function saveBottleForm(
  calculatedPrice?: { targetMarkupPercent: number; previewToken: string },
  calculationIsCurrent?: () => boolean,
): Promise<void> {
  clearBottleFormIssues();
  if (calculatedPrice === undefined && editingBottleId !== undefined) {
    const typedCalculation = await prepareTypedPourPrice();
    if (typedCalculation === null) return;
    calculatedPrice = typedCalculation;
  }
  if (currentVendorEdit() !== undefined || currentVendorAdd() !== undefined) {
    bottleFormError.textContent = "Finish or recover the original Vendor Option save before saving this item.";
    return;
  }
  if (!bottleSellingCurrent()) {
    bottleFormError.textContent = "Location changed. Close and reopen this item.";
    return;
  }
  const sellingEditor = f3SellingUi.editor!;
  const setup = catalogItemSetup;
  if (
    setup &&
    (!setup.isCurrent() || setup.uncertain || catalogPlanningLocationId !== setup.locationId)
  ) {
    bottleFormError.textContent =
      "Catalog context changed or save is unconfirmed. Close and reload the catalog before continuing.";
    return;
  }
  if (setup) applyCatalogLocalItemBase();
  const s = session();
  if (
    !isCurrentItemKind(bottleFormItemKind) ||
    (editingBottle !== undefined && !isCurrentItemKind(editingBottle.item_kind))
  ) {
    bottleFormError.textContent =
      "This item uses a removed kind. Owner mapping is required before it can be saved.";
    return;
  }
  // CORE10: every field is checked before anything is reported, so one Save shows every error at once.
  const issues: BottleFormIssue[] = [];
  const name = bottleNameInput.value.trim();
  if (name.length === 0) {
    issues.push({ tab: "drink", message: "Item name is required." });
  }
  // Inventory track Slice 3, Amendment 2026-08-22 (twelfth), extended by
  // CT3 (DEC-129, DEC-141): category is a PICKER for every item kind now,
  // the tenant's own Liquor-scoped item categories for 'liquor', and that
  // kind's own beer_category/wine_category/packaged_category list
  // for the other three (applyBottleItemKindVisibility swaps which select
  // is shown; populateBottleCategoryOtherSelect repopulates the "other"
  // one per kind). Closes DSC11/INV12's free-text gap.
  const isLiquorKind = bottleFormItemKind === "liquor";
  const category = isLiquorKind ? bottleCategorySelect.value : bottleCategoryOtherSelect.value;
  if (category.length === 0) {
    issues.push({ tab: "drink", message: "Category is required." });
  }

  const saleMechanism = currentBottleSaleMechanism();
  const mechanismMissing = bottleKindMechanismIsExplicit() && saleMechanism === null;
  if (mechanismMissing) {
    issues.push({ tab: "drink", message: "Choose how this item sells." });
  }

  // Amendment 2026-08-22 (twelfth): a whole-unit kind prices flat per
  // unit, no pour size; a per-ounce kind prices per ounce with at least
  // one pour size, exactly as before this slice.
  // ITEM2 Slice 5 (Section 3.4): used to be `bottleFormItemKind ===
  // "packaged"`; generalized to itemKindSellsWholeUnit, since this is the
  // CREATE-or-edit path where the mechanism must be resolved from the
  // selected kind's own vocabulary row, not from an existing product.
  // CORE10: with no sale mechanism chosen the pricing shape is unknown, so pricing is checked once it is chosen.
  let unitPriceCents: number | undefined;
  let pourOunceOptions: number[] | undefined;
  if (!mechanismMissing) {
    if (saleMechanism === "whole_unit") {
      try {
        unitPriceCents = parseDollarsToCentsExact(
          bottleUnitPriceInput.value,
          "Sell price per unit",
        );
      } catch (error) {
        issues.push({
          tab: "calculator",
          message: error instanceof Error ? error.message : "Invalid sell price.",
        });
      }
    } else if (bottleFormItemKind !== "liquor") {
      if (bottleFormPourOptions.size === 0) {
        issues.push({ tab: "inventory", message: "Pick at least one pour ounce option." });
      } else {
        pourOunceOptions = [...bottleFormPourOptions]
          .map((value) => Number(value))
          .sort((a, b) => a - b);
      }
    }
  }
  const shelfTier =
    bottleShelfTierSelect.value.length > 0 ? bottleShelfTierSelect.value : undefined;

  const inventoryFields = collectInventoryItemFields(issues);
  if (issues.length > 0 || inventoryFields === undefined) {
    showBottleFormIssues(issues);
    if (issues[0]?.message === "Choose how this item sells.") {
      bottleSaleMechanismGroup.focus();
    }
    return;
  }
  // B1 (docs/backlog.md, owner ruling 2026-08-27). Plain free text, no
  // parsing: an empty field is omitted (unchanged), never an error, the
  // same "fill in as much as you have on hand" convention every other
  // optional field on this form already uses. A collision is caught
  // server-side (DuplicateLiquorProductCodeError) and shown through the
  // same result.body.message fallback every other save error already
  // uses below.
  const barcodeRaw = bottleBarcodeInput.value.trim();
  const barcode = barcodeRaw.length > 0 ? barcodeRaw : undefined;
  // B6-F1: only the fields that changed are sent; an emptied field clears. Existing items only.
  const caseFields = collectBottleCaseFields(issues, s);
  if (issues.length > 0 || caseFields === undefined) {
    showBottleFormIssues(issues);
    return;
  }
  const replacedCaseBarcode = editingBottle?.case_barcode ?? null;
  if (
    typeof caseFields.caseBarcode === "string" &&
    replacedCaseBarcode !== null &&
    caseFields.caseBarcode !== replacedCaseBarcode
  ) {
    const confirmed = await showConfirmSheet({
      title: "Replace case barcode",
      message: replacedCaseBarcode,
      confirmLabel: "Replace",
      tone: "accent",
    });
    if (!confirmed || sessionOrUndefined() === undefined) {
      return;
    }
    caseFields.replaceCaseBarcode = true;
  }

  // Review 9 correction (both gates, Medium): a bottle-form deactivation
  // (Active checked -> unchecked on an existing, currently-active bottle)
  // is a destructive action and gets an explicit confirm step before
  // anything is saved, mirroring the drinks list toggle's own confirm
  // (handleDrinkToggleChange below). Reactivating, or editing any other
  // field on an already-inactive bottle, needs no confirmation: only the
  // active-true -> active-false transition is destructive. Cancelling
  // aborts the WHOLE save (nothing is written), the same "go back and
  // reconsider" shape the drinks list toggle uses, rather than saving
  // every other edited field while silently keeping the bottle active.
  const isDeactivating =
    editingBottleId !== undefined &&
    editingBottle !== undefined &&
    currentProductSelling(editingBottle, sellingEditor.siteId)?.active === true &&
    !bottleActiveInput.checked;
  if (isDeactivating && editingBottle !== undefined) {
    const warning = await computeBottleDeactivationWarning(editingBottle);
    const confirmed = await showConfirmSheet({
      title: `Make ${editingBottle.name} inactive?`,
      message:
        (warning ?? `Bartenders won't be able to select ${editingBottle.name} on a new ticket.`) +
        " It can be made active again later.",
      confirmLabel: "Make inactive",
    });
    if (!confirmed) {
      return;
    }
    // SET16-F1 follow-up, from independent review: this confirm sheet can
    // sit open long enough for the acting employee's session to die, and
    // the `s` captured above this await predates it. Without this guard
    // the continuation runs anyway and writes a success toast, an error
    // string, or a re-rendered list onto whatever is now on screen, which
    // after a teardown is the NEXT employee's screen. Same guard and same
    // reason as applyActivationSwitchChange's own chokepoint.
    if (!bottleSellingCurrent() || f3SellingUi.editor !== sellingEditor) return;
  }

  if (calculationIsCurrent !== undefined && !calculationIsCurrent()) return;
  bottleFormSave.disabled = true;
  try {
    if (editingBottleId === undefined) {
      // STARTSTOCK1 (DEC-601): read now; the sheet is cleared before anything below could read it.
      const startStockText = collectBottleStartStock([]);
      const startStockKind = currentBottleStartStockKind().kind;
      const startStockWord = currentBottleStartStockKind().word;
      const createBody: Parameters<typeof createLiquorProduct>[2] = {
        ...(sellingEditor.siteId === undefined ? {} : { locationId: sellingEditor.siteId }),
        sellingRequestId: sellingEditor.requestId,
        sellingVersion: 0,
        name,
        category,
        itemKind: bottleFormItemKind,
        saleMechanism: bottleSaleMechanismForWrite(),
        pourOunceOptions,
        unitPriceCents,
        shelfTier,
        barcode,
        // INV26 Slice 2 (DEC-207 item 3).
        hideOnMenu: bottleHideOnMenuInput.checked,
        ...inventoryFields,
      };
      const stamp = JSON.stringify(createBody);
      if (!setup && sellingEditor.uncertain && sellingEditor.createStamp !== stamp) {
        bottleFormError.textContent = "Previous create unconfirmed. Retry unchanged or reload.";
        return;
      }
      if (!setup && !sellingEditor.uncertain) {
        sellingEditor.createBody = structuredClone(createBody);
        sellingEditor.createStamp = stamp;
      }
      const result = setup
        ? await createCatalogLocalItem(s.apiBaseUrl, s.tenantId, setup.locationId, {
            productId: setup.selection.product.product_id,
            expectedVersion: setup.selection.product.revision,
            requestId: setup.selection.requestId,
            localSaleMechanism: setup.selection.localSaleMechanism,
            unitPriceCents,
            pourOunceOptions,
            shelfTier,
            hideOnMenu: bottleHideOnMenuInput.checked,
            trackStock: inventoryFields.trackStock,
            stockSettings: inventoryFields.stockSettings,
            defaultKegSize: inventoryFields.defaultKegSize,
            abvBasisPoints: inventoryFields.abvBasisPoints,
            producer: inventoryFields.producer,
            countryRegion: inventoryFields.countryRegion,
            descriptionNotes: inventoryFields.descriptionNotes,
          })
        : await createLiquorProduct(
            s.apiBaseUrl,
            s.tenantId,
            sellingEditor.uncertain ? sellingEditor.createBody! : createBody,
          );
      if (!bottleSellingCurrent() || f3SellingUi.editor !== sellingEditor) return;
      if (!setup) sellingEditor.uncertain = result.networkError || result.status >= 500;
      if (setup && (!setup.isCurrent() || catalogItemSetup !== setup)) return;
      if (calculationIsCurrent !== undefined && !calculationIsCurrent()) return;
      if (result.status !== 201) {
        if (
          setup &&
          (result.status === 0 ||
            result.status >= 500 ||
            result.status === 409 ||
            result.status === 403)
        )
          setup.uncertain = true;
        bottleFormError.textContent =
          result.body.message ?? result.body.error ?? "Could not save this item.";
        return;
      }
      // CATALOG1: read the NC code before the vendor option step; it is a fact about the product, not about this location.
      const confirmedNcCode = ncCodeApplies() ? bottleVendorOfferSkuInput.value.trim() : "";
      const vendorAndPriceProblem = setup
        ? undefined
        : await finishNewItemVendorAndPrice(s, result.body.liquorProduct, sellingEditor, pourOunceOptions);
      void contributeToSharedCatalog(s, result.body.liquorProduct, confirmedNcCode);
      if (vendorAndPriceProblem !== undefined) {
        adminToast(`${result.body.liquorProduct.name} added, but ${vendorAndPriceProblem}`, "danger");
      }
      if (startStockText === undefined) {
        if (vendorAndPriceProblem === undefined) adminToast(`${result.body.liquorProduct.name} added.`, "success");
      } else {
        const startStockProblem = await recordNewItemStartStock(
          s,
          result.body.liquorProduct,
          startStockText,
          startStockKind,
        );
        if (startStockProblem === undefined) {
          adminToast(`${result.body.liquorProduct.name} added with ${startStockText} ${startStockWord} on hand.`, "success");
        } else {
          adminToast(
            `${result.body.liquorProduct.name} added, but its starting stock was not saved. ${startStockProblem} Use Set starting stock.`,
            "danger",
          );
        }
      }
    } else {
      // Review 9 correction (architect gate, Critical): active is now
      // sent directly in this one PATCH, the same way saveDrinkForm sends
      // requiresLiquorSelection/active for menu drinks; there is no
      // longer a separate deactivate call or a "no dedicated activate
      // route" limitation (src/domain/catalog.ts's updateLiquorProduct
      // accepts active and no longer restricts its WHERE clause to
      // already-active rows), so a deactivated bottle can be edited AND
      // reactivated through this same call.
      const patch: UpdateLiquorProductInput = {
        calculatedPrice,
        name,
        category,
        itemKind: bottleFormItemKind,
        saleMechanism: bottleSaleMechanismForWrite(),
        pourOunceOptions,
        unitPriceCents,
        shelfTier,
        active: bottleActiveInput.checked,
        barcode,
        // INV26 Slice 2 (DEC-207 item 3).
        hideOnMenu: bottleHideOnMenuInput.checked,
        ...caseFields,
        // ADDONRM1: liquor edits leave existing attachments and groups untouched.
        ...(bottleModifiersLoaded ? ingredientChoiceBody(bottleFormModifiers) : {}),
        ...inventoryFields,
      };
      if (sellingEditor.context.readiness.state === "enforced") {
        delete patch.calculatedPrice;
        delete patch.pourOunceOptions;
        delete patch.unitPriceCents;
        delete patch.shelfTier;
        delete patch.active;
        delete patch.hideOnMenu;
        delete patch.trackStock;
        if (sellingEditor.siteId === undefined || editingBottle?.selling === undefined) {
          bottleFormError.textContent = "Location selling settings unavailable.";
          return;
        }
        patch.locationId = sellingEditor.siteId;
        patch.sellingVersion = sellingEditor.version;
        patch.sellingRequestId = sellingEditor.requestId;
        patch.sellingSettings = {
          source: "configured",
          unitPriceCents: unitPriceCents ?? null,
          ...(calculatedPrice === undefined ? {} : { calculatedPrice }),
          pourOunceOptions: pourOunceOptions ?? null,
          shelfTier: shelfTier ?? null,
          active: bottleActiveInput.checked,
          hideOnMenu: bottleHideOnMenuInput.checked,
          trackStock: itemTrackStockInput.checked,
        };
      }
      const stamp = JSON.stringify(patch);
      if (sellingEditor.uncertain && sellingEditor.bodyStamp !== stamp) {
        bottleFormError.textContent =
          "Previous save unconfirmed. Retry unchanged or reload the item.";
        return;
      }
      if (!sellingEditor.uncertain) {
        sellingEditor.body = structuredClone(patch);
        sellingEditor.bodyStamp = stamp;
      }
      const result = await updateLiquorProduct(
        s.apiBaseUrl,
        s.tenantId,
        editingBottleId,
        sellingEditor.uncertain ? sellingEditor.body! : patch,
      );
      if (!bottleSellingCurrent() || f3SellingUi.editor !== sellingEditor) return;
      sellingEditor.uncertain = result.networkError || result.status >= 500;

      if (calculationIsCurrent !== undefined && !calculationIsCurrent()) return;
      if (result.status !== 200) {
        bottleFormError.textContent =
          result.body.message ?? result.body.error ?? "Could not save this item.";
        return;
      }
      void contributeToSharedCatalog(
        s,
        result.body.liquorProduct,
        ncCodeApplies() ? bottleVendorOfferSkuInput.value.trim() : "",
      );
      adminToast(`${result.body.liquorProduct.name} updated.`, "success");
    }
    const catalogCompleted = setup?.completed;
    // ADDITEMDRAFT1: saved, so the draft is finished.
    finishItemDraft();
    closeBottleForm();
    catalogCompleted?.();
    await Promise.all([loadBottles(), loadVocabularies({ forceFresh: true })]);
  } finally {
    if (
      f3SellingUi.editor === sellingEditor &&
      bottleSellingCurrent() &&
      (setup === undefined || (catalogItemSetup === setup && setup.isCurrent())) &&
      (calculationIsCurrent === undefined || calculationIsCurrent())
    )
      bottleFormSave.disabled =
        catalogItemSetup?.uncertain === true || bottleSaleMechanismRequiredButMissing();
  }
}

// ---------------------------------------------------------------------
// Location settings: tax rate, tip presets, rounding (read-only), shelf
// tiers (data-plus-UI only, unused by pricing yet)
// ---------------------------------------------------------------------

function clearSettingsSaveStatuses(): void {
  settingsSaveStatusMoneyEl.textContent = "";
  settingsSaveStatusTabsPaymentEl.textContent = "";
  settingsSaveStatusOperationsEl.textContent = "";
  sessionInactivitySaveStatusEl.textContent = "";
  settingsSaveStatusAddressEl.textContent = "";
}

// PAY-LOC1 (docs/backlog.md) review finding. Writes the server's own
// LocationAddressDto straight to the DOM, the same "no separate
// module-level copy" shape every other Operations field already uses
// (saveLocationAddress below re-reads these same inputs on Save). Every
// field is nullable server-side; an empty string is what a blank field
// shows and what a blank field submits.
/** LOC3: fills the location select from the server's list (signed-in location first) and the form from the first location. */
function applyAddressLocations(
  locations: LocationWithAddressDto[],
  signedInLocationId: string | null,
): void {
  addressLocations = locations;
  settingsAddressLocationSelect.replaceChildren();
  for (const siteLocation of locations) {
    const option = document.createElement("option");
    option.value = siteLocation.id;
    option.textContent = siteLocation.name;
    settingsAddressLocationSelect.append(option);
  }
  const first =
    locations.find((siteLocation) => siteLocation.id === signedInLocationId) ?? locations[0];
  if (first !== undefined) {
    settingsAddressLocationSelect.value = first.id;
    applyLocationAddressToForm(first);
  }
  settingsAddressLocationRow.hidden = locations.length < 2;
}

/** LOC3: re-reads the locations after one is added, keeping the location being edited selected. */
async function refreshAddressLocations(): Promise<void> {
  const s = sessionOrUndefined();
  if (s === undefined || !hasPermission(s, "admin.tenant_settings.view")) return;
  const keep = settingsAddressLocationSelect.value;
  const result = await getLocationAddresses(s.apiBaseUrl, s.tenantId);
  if (sessionOrUndefined() !== s || result.status !== 200) return;
  applyAddressLocations(result.body.locations, keep === "" ? result.body.signedInLocationId : keep);
}

settingsAddressLocationSelect.addEventListener("change", () => {
  const siteLocation = addressLocations.find((b) => b.id === settingsAddressLocationSelect.value);
  if (siteLocation !== undefined) applyLocationAddressToForm(siteLocation);
  settingsSaveStatusAddressEl.textContent = "";
});

function applyLocationAddressToForm(address: LocationAddressDto): void {
  settingsAddressLine1Input.value = address.addressLine1 ?? "";
  settingsAddressLine2Input.value = address.addressLine2 ?? "";
  settingsAddressCityInput.value = address.city ?? "";
  settingsAddressStateInput.value = address.state ?? "";
  settingsAddressPostalCodeInput.value = address.postalCode ?? "";
  settingsAddressCountryInput.value = address.country;
}

/**
 * Saves ONLY this location's address (PAY-LOC1 review finding).
 *
 * Deliberately not routed through saveSettingsForm, mirroring
 * savePinPolicy's own comment just below for the identical reason: a
 * manager filling in the street address should never be blocked by an
 * unrelated field elsewhere on this screen, and vice versa.
 */
async function saveLocationAddress(): Promise<void> {
  const s = session();
  const statusEl = settingsSaveStatusAddressEl;
  settingsSaveButtonAddress.disabled = true;
  statusEl.textContent = "Saving…";
  try {
    const locationId = settingsAddressLocationSelect.value;
    if (locationId === "") {
      statusEl.textContent = "Could not save the address.";
      return;
    }
    const result = await putLocationAddress(s.apiBaseUrl, s.tenantId, locationId, {
      addressLine1: settingsAddressLine1Input.value.trim(),
      addressLine2: settingsAddressLine2Input.value.trim(),
      city: settingsAddressCityInput.value.trim(),
      state: settingsAddressStateInput.value.trim(),
      postalCode: settingsAddressPostalCodeInput.value.trim(),
      country: settingsAddressCountryInput.value.trim(),
    });
    if (result.status !== 200) {
      statusEl.textContent =
        result.body.message ?? result.body.error ?? "Could not save the address.";
      return;
    }
    // Re-read from the SERVER's response, not from what was typed.
    addressLocations = addressLocations.map((siteLocation) =>
      siteLocation.id === locationId ? result.body.location : siteLocation,
    );
    if (settingsAddressLocationSelect.value === locationId)
      applyLocationAddressToForm(result.body.location);
    // A present syncWarning means the card-reader location at the payment provider did not pick up this change
    // (PAY-LOC1-F1); that plain message replaces the ordinary "Saved." text.
    statusEl.textContent = result.body.syncWarning ?? "Saved.";
    adminToast("Address saved.", "success");
    void locationsSection.refresh();
  } catch (error) {
    statusEl.textContent = error instanceof Error ? error.message : "Could not save the address.";
  } finally {
    settingsSaveButtonAddress.disabled = false;
  }
}

/**
 * CL6-F1-F3 (docs/build-briefs/CL6-F1-F3-timezone-required.md; DEC-281,
 * DEC-286). Saves ONLY the time zone and cutoff, from the Business day card.
 * `confirm: true` is the deliberate "Confirm time zone and cutoff" tap and
 * sends confirmBusinessDay, so the values confirmed are exactly the values
 * saved, in one request. `confirm: false` is the ordinary "Save changes" after
 * confirming, which leaves the confirmation as it is. Never routed through
 * saveSettingsForm: that submits every field on the screen, and saving the
 * business day must not depend on, or carry, a different card's field.
 */
async function saveBusinessDay(options: { confirm: boolean }): Promise<void> {
  const s = session();
  const statusEl = settingsSaveStatusOperationsEl;
  const businessDayTimezone = settingsBusinessDayTimezoneInput.value.trim();
  const businessDayCutoffTimeLocal = settingsBusinessDayCutoffInput.value.trim();
  if (businessDayTimezone.length === 0) {
    statusEl.textContent = "Pick a time zone first.";
    return;
  }
  if (businessDayCutoffTimeLocal.length === 0) {
    statusEl.textContent = "Business day cutoff time can't be blank.";
    return;
  }
  const activeButton = options.confirm ? businessDayConfirmButton : settingsSaveButtonOperations;
  activeButton.disabled = true;
  statusEl.textContent = "Saving…";
  const gen = loadGeneration;
  try {
    const result = await updateSettings(s.apiBaseUrl, s.tenantId, {
      businessDayTimezone,
      businessDayCutoffTimeLocal,
      ...(options.confirm ? { confirmBusinessDay: true } : {}),
    });
    // SET16-F4: a sign-out that ran while this was in flight owns the screen now.
    if (!loadIsCurrent(gen)) return;
    if (result.status !== 200) {
      const reason =
        result.body.message ??
        result.body.error ??
        (options.confirm ? "Could not confirm." : "Could not save.");
      statusEl.textContent = reason;
      settingsBusinessDayHelpEl.textContent = reason;
      return;
    }
    applyBusinessDayFromSettings(result.body.settings);
    statusEl.textContent = options.confirm ? "Confirmed." : "Saved.";
    adminToast(
      options.confirm ? "Time zone and cutoff confirmed." : "Business day saved.",
      "success",
    );
    // Only this card's own part of the committed baseline moves: adopting the
    // whole form here would silently bless an unsaved edit in another group.
    if (settingsFormSnapshot !== undefined) {
      settingsFormSnapshot = {
        ...settingsFormSnapshot,
        businessDayCutoff: settingsBusinessDayCutoffInput.value,
        businessDayTimezone: settingsBusinessDayTimezoneInput.value,
      };
      scheduleSettingsDraftSave();
    }
    if (options.confirm) {
      // The setup checklist has a row for this; it reads done now.
      void loadSetupChecklist(s);
    }
  } catch (error) {
    statusEl.textContent =
      error instanceof Error ? error.message : "Could not save the business day.";
  } finally {
    activeButton.disabled = false;
    renderBusinessDayConfirm();
  }
}

/**
 * CL6-F1-F3. Saves ONLY session inactivity, from its own card, so saving it
 * never carries or confirms the business day (the design's own rule). Same
 * early range check the big form uses; the server stays the real enforcement.
 */
async function saveSessionInactivity(): Promise<void> {
  const s = session();
  const statusEl = sessionInactivitySaveStatusEl;
  const raw = settingsSessionInactivityInput.value.trim();
  const minutes = Number(raw);
  if (
    raw.length === 0 ||
    !Number.isInteger(minutes) ||
    minutes < MIN_SESSION_INACTIVITY_MINUTES ||
    minutes > MAX_SESSION_INACTIVITY_MINUTES
  ) {
    statusEl.textContent =
      `Session inactivity timeout must be a whole number of minutes, ` +
      `${boundRangeText(MIN_SESSION_INACTIVITY_MINUTES, MAX_SESSION_INACTIVITY_MINUTES, "minutes")}.`;
    return;
  }
  sessionInactivitySaveButton.disabled = true;
  statusEl.textContent = "Saving…";
  const gen = loadGeneration;
  try {
    const result = await updateSettings(s.apiBaseUrl, s.tenantId, {
      sessionInactivityMinutes: minutes,
    });
    if (!loadIsCurrent(gen)) return;
    if (result.status !== 200) {
      statusEl.textContent =
        result.body.message ?? result.body.error ?? "Could not save session inactivity.";
      return;
    }
    settingsSessionInactivityInput.value = String(result.body.settings.sessionInactivityMinutes);
    statusEl.textContent = "Saved.";
    adminToast("Session inactivity saved.", "success");
    // UX86: this card now belongs to People and access, so its committed
    // value is recorded in that domain's snapshot. Only this field is
    // refreshed, so an unsaved PIN policy edit beside it stays dirty.
    if (pinPolicySnapshot !== undefined) {
      pinPolicySnapshot = {
        ...pinPolicySnapshot,
        sessionInactivity: settingsSessionInactivityInput.value,
      };
      scheduleSettingsDraftSave();
    }
  } catch (error) {
    statusEl.textContent =
      error instanceof Error ? error.message : "Could not save session inactivity.";
  } finally {
    sessionInactivitySaveButton.disabled = false;
  }
}

/**
 * Saves ONLY the PIN policy (migration 0043).
 *
 * Deliberately not routed through saveSettingsForm, which submits every
 * field on the whole settings screen at once. That coupling was found by
 * a failing test rather than by reading: saving the PIN policy went out
 * with the tip presets attached and was refused because a DIFFERENT
 * group's setting was unconfigured. A manager in People and access should
 * never be blocked by a field in Money and pricing.
 */
async function savePinPolicy(): Promise<void> {
  const s = session();
  const statusEl = settingsSaveStatusPinPolicyEl;
  const values: Record<string, number> = {};
  for (const [key, input, low, high, label] of [
    [
      "pinMinLength",
      settingsPinMinLengthInput,
      PIN_LENGTH_MIN,
      PIN_LENGTH_MAX,
      "Shortest PIN allowed",
    ],
    [
      "pinMaxLength",
      settingsPinMaxLengthInput,
      PIN_LENGTH_MIN,
      PIN_LENGTH_MAX,
      "Longest PIN allowed",
    ],
    [
      "pinLockoutAttempts",
      settingsPinLockoutAttemptsInput,
      PIN_LOCKOUT_ATTEMPTS_MIN,
      PIN_LOCKOUT_ATTEMPTS_MAX,
      "Wrong PINs before locking",
    ],
    [
      "pinLockoutMinutes",
      settingsPinLockoutMinutesInput,
      PIN_LOCKOUT_MINUTES_MIN,
      PIN_LOCKOUT_MINUTES_MAX,
      "Lock duration",
    ],
  ] as const) {
    const raw = input.value.trim();
    const value = Number(raw);
    if (raw.length === 0 || !Number.isInteger(value) || value < low || value > high) {
      statusEl.textContent = `${label} must be a whole number from ${low} to ${high}.`;
      return;
    }
    values[key] = value;
  }
  const pinMinLength = values["pinMinLength"] ?? 4;
  const pinMaxLength = values["pinMaxLength"] ?? 6;
  if (pinMaxLength < pinMinLength) {
    statusEl.textContent = "The longest PIN allowed can't be shorter than the shortest.";
    return;
  }

  settingsSaveButtonPinPolicy.disabled = true;
  statusEl.textContent = "Saving…";
  try {
    const result = await updateSettings(s.apiBaseUrl, s.tenantId, {
      pinMinLength,
      pinMaxLength,
      pinLockoutAttempts: values["pinLockoutAttempts"] ?? 5,
      pinLockoutMinutes: values["pinLockoutMinutes"] ?? 5,
    });
    if (result.status !== 200) {
      statusEl.textContent =
        result.body.message ?? result.body.error ?? "Could not save the PIN policy.";
      return;
    }
    // Re-read from the SERVER's response rather than trusting what was
    // typed, so a value the server normalised shows as stored.
    settingsPinMinLengthInput.value = String(result.body.settings.pinMinLength);
    settingsPinMaxLengthInput.value = String(result.body.settings.pinMaxLength);
    settingsPinLockoutAttemptsInput.value = String(result.body.settings.pinLockoutAttempts);
    settingsPinLockoutMinutesInput.value = String(result.body.settings.pinLockoutMinutes);
    statusEl.textContent = "Saved.";
    // SET14: PIN policy's own committed snapshot, taken immediately after
    // its own successful save.
    pinPolicySnapshot = capturePinPolicySnapshot();
    notePinPolicyCommitted();
  } finally {
    settingsSaveButtonPinPolicy.disabled = false;
  }
}

// ITEM2 Slice 2 (docs/build-briefs/ITEM2-a-kind-sale-mechanism-is-picked-
// in-settings.md Section 4, "Slice 2"; DEC-158). The two mechanisms the
// picker offers, in the exact order DEC-158's own ruling lists them: sells
// per ounce (Wine's shape) or as a whole unit (Packaged's shape). One
// shared map so the chip label and the row's own status readout can never
// say two different things about the same value, the same "one home per
// fact" reason every other label map on this screen already follows.
const LEGACY_SALE_MECHANISM_LABELS: Record<string, string> = {
  per_ounce: "By the pour",
  whole_unit: "Whole unit",
};

const ITEM_KIND_POLICY_CHOICES: {
  value: ProductSaleMechanism[];
  label: string;
}[] = [
  { value: ["per_ounce"], label: "By the pour" },
  { value: ["whole_unit"], label: "Whole unit" },
  { value: ["per_ounce", "whole_unit"], label: "Both" },
];

/** The row's own plain-language readout of its current mechanism. Kept
 * separate from the chip labels below (rather than inferred from which
 * chip is checked) so a browser test can read the row's own state without
 * depending on radio-checked styling, and so "no default, ever" (this
 * product's liquor-selection precedent, DEC-158's own text) has a visible
 * word rather than an absence a test cannot assert on. */
function saleMechanismStatusText(saleMechanism: string | null): string {
  if (saleMechanism === null) {
    return "Not chosen yet";
  }
  return LEGACY_SALE_MECHANISM_LABELS[saleMechanism] ?? saleMechanism;
}

// ITEM2 Slice 3 (docs/build-briefs/ITEM2-a-kind-sale-mechanism-is-picked-
// in-settings.md Section 4's "Slice 3" paragraph; DEC-158). The brief's own
// proposed wording for a picker that has locked because the kind already
// has sold lines.
const SALE_MECHANISM_LOCKED_TEXT = "Already sold; this cannot change";

/** GET .../vocabularies carries no separate field for "this kind has sold
 * lines" (the brief's own Slice 3 text names no such field; see this
 * slice's own build notes), so the only signal the client has is the
 * route's own refusal MESSAGE when a write is actually attempted.
 * src/domain/vocabularies.ts's updateItemKindSaleMechanism and migration
 * 0116's own trigger both raise the identical "already has sold lines"
 * wording; this checks the substring both share rather than duplicating
 * either message verbatim, so the two staying in sync is the only thing
 * this match depends on. */
function isSaleMechanismSoldLinesMessage(message: string | undefined): boolean {
  return message !== undefined && message.includes("sold lines");
}

/** Appends the sale-mechanism picker to one item_kind row, or does nothing
 * for 'liquor' (Section 3.5 of the ITEM2 brief: liquor's own mechanism is
 * a permanent special case the picker never offers, not a gap). The
 * picker never renders a third option and never pre-checks either chip,
 * matching "no default, ever."
 *
 * Permission-driven, never role-driven (CLAUDE.md): the two chips are
 * DISABLED, never hidden, when the resolved session snapshot lacks
 * admin.location_settings.manage, resolved through hasPermission and never a
 * role name. Disabled rather than hidden so a manager-only view still
 * shows what has already been chosen, the same "informational, not
 * actionable" shape a disabled control gives elsewhere on this screen.
 */
function appendItemKindMechanismPicker(row: HTMLElement, entry: AdminVocabularyEntry): void {
  if (entry.policyEnforcementActive === true) {
    const fixedStatus = fixedItemKindSaleStatus(entry.value);
    if (fixedStatus !== undefined) {
      const status = document.createElement("span");
      status.className = "admin-field-help";
      status.dataset["testid"] = "admin-item-kind-policy-status";
      status.textContent = fixedStatus;
      row.append(status);
      return;
    }

    const wrap = document.createElement("div");
    wrap.className = "admin-item-kind-policy-wrap";
    wrap.dataset["testid"] = "admin-item-kind-policy-wrap";
    const error = document.createElement("p");
    error.className = "admin-field-help admin-item-kind-policy-error";
    error.dataset["testid"] = "admin-item-kind-policy-error";
    error.setAttribute("role", "alert");
    error.tabIndex = -1;

    const group = document.createElement("div");
    group.className = "admin-policy-radio-group";
    group.dataset["testid"] = "admin-item-kind-policy-group";
    group.setAttribute("role", "radiogroup");
    group.setAttribute("aria-label", `How ${entry.label} is sold`);
    const current = (entry.saleMechanisms ?? []).join(",");
    const activeSession = sessionOrUndefined();
    const canManage =
      activeSession !== undefined && hasPermission(activeSession, "admin.location_settings.manage");
    for (const choice of ITEM_KIND_POLICY_CHOICES) {
      const label = document.createElement("label");
      label.className = "admin-policy-radio-option";
      const input = document.createElement("input");
      input.type = "radio";
      input.name = `admin-item-kind-policy-${entry.value}`;
      input.required = true;
      input.value = choice.value.join(",");
      input.dataset["testid"] = "admin-item-kind-policy-option";
      input.dataset["value"] = choice.value.join(",");
      input.checked = current === choice.value.join(",");
      input.disabled = !canManage;
      input.addEventListener("change", () => {
        if (input.checked) {
          void setItemKindSaleMechanisms(entry, choice.value, error);
        }
      });
      const text = document.createElement("span");
      text.textContent = choice.label;
      label.append(input, text);
      group.append(label);
    }
    wrap.append(group);
    wrap.append(error);
    row.append(wrap);
    return;
  }

  if (entry.value === "liquor") {
    return;
  }

  const wrap = document.createElement("div");
  wrap.className = "admin-item-kind-mechanism-wrap";
  wrap.dataset["testid"] = "admin-item-kind-mechanism";

  const status = document.createElement("span");
  status.className = "admin-field-help";
  status.dataset["testid"] = "admin-item-kind-mechanism-status";
  wrap.append(status);

  const picker = document.createElement("div");
  picker.className = "admin-chip-select";
  picker.dataset["testid"] = "admin-item-kind-mechanism-picker";
  picker.setAttribute("role", "radiogroup");
  picker.setAttribute("aria-label", `How ${entry.label} is sold`);
  wrap.append(picker);

  const s = sessionOrUndefined();
  const canManage = s !== undefined && hasPermission(s, "admin.location_settings.manage");
  const options = Object.entries(LEGACY_SALE_MECHANISM_LABELS).map(([value, label]) => ({
    value,
    label,
  }));

  // ITEM2 Slice 3 (QA-lead Ruling B): locked is true either because the
  // FETCHED row already carries a sold line (entry.hasSoldLines, learned
  // from GET .../vocabularies before any tap) or because a write attempt
  // in THIS session already reported the lock (entry.saleMechanismLocked,
  // the reactive path from before). Read together so a row locks whichever
  // way it was learned, and so a fetched hasSoldLines row renders disabled
  // with the locked text on first paint, per the brief's own "becomes
  // disabled... rather than merely failing on submit" text, not only
  // after a failed write attempt. Recomputed on every call, never cached
  // in a closed-over const: entry.saleMechanismLocked can flip true
  // BETWEEN two calls to paintChips (the failure branch of
  // setItemKindSaleMechanism sets it and then calls paintChips again in
  // the same tick), and a stale cached value would repaint the chips
  // enabled on that exact repaint.
  const isLocked = (): boolean => entry.saleMechanismLocked === true || entry.hasSoldLines === true;

  // Repaints only the chip group from entry's own CURRENT saleMechanism.
  // Kept separate from the status text below (set by the caller instead)
  // so a failed write's own error message is not immediately overwritten
  // by a repaint that runs right after it. Called on first render, and
  // again after every write attempt (success or failure) so a failed
  // write's already-flipped native radio reverts to what the server
  // actually holds rather than what the tap merely aimed for.
  //
  // Once locked (either source above), the chips stay disabled regardless
  // of canManage: after the lock is known, a manager does not get to tap
  // the same locked control only to fail again.
  const paintChips = (): void => {
    renderBottleFilterChipGroup(
      picker,
      `admin-item-kind-mechanism-${entry.value}`,
      "admin-item-kind-mechanism-option",
      options,
      entry.saleMechanism ?? "",
      (value) => void setItemKindSaleMechanism(entry, value, status, paintChips),
    );
    if (!canManage || isLocked()) {
      for (const input of picker.querySelectorAll<HTMLInputElement>("input")) {
        input.disabled = true;
      }
    }
  };
  status.textContent = isLocked()
    ? SALE_MECHANISM_LOCKED_TEXT
    : saleMechanismStatusText(entry.saleMechanism ?? null);
  paintChips();
  row.append(wrap);
}

async function setItemKindSaleMechanisms(
  entry: AdminVocabularyEntry,
  saleMechanisms: ProductSaleMechanism[],
  error: HTMLElement,
): Promise<void> {
  const s = session();
  const radios = [
    ...(error.parentElement?.querySelectorAll<HTMLInputElement>(
      '[data-testid="admin-item-kind-policy-option"]',
    ) ?? []),
  ];
  for (const radio of radios) radio.disabled = true;
  error.textContent = "Saving…";
  const result = await updateItemKindSaleMechanisms(
    s.apiBaseUrl,
    s.tenantId,
    entry.value,
    saleMechanisms,
  );
  if (sessionOrUndefined() === undefined) return;
  if (result.status !== 200) {
    error.textContent = result.body.message ?? result.body.error ?? "Could not change that policy.";
    error.focus();
    for (const radio of radios) {
      radio.checked = (entry.saleMechanisms ?? []).join(",") === radio.value;
      radio.disabled = false;
    }
    return;
  }
  entry.saleMechanisms = result.body.value.saleMechanisms ?? saleMechanisms;
  error.textContent = "Saved.";
  for (const radio of radios) {
    radio.checked = (entry.saleMechanisms ?? []).join(",") === radio.value;
    radio.disabled = false;
  }
  radios.find((radio) => radio.checked)?.focus();
}

async function setItemKindSaleMechanism(
  entry: AdminVocabularyEntry,
  saleMechanism: string,
  status: HTMLElement,
  paintChips: () => void,
): Promise<void> {
  if (saleMechanism !== "per_ounce" && saleMechanism !== "whole_unit") {
    return;
  }
  const s = session();
  status.textContent = "Saving...";
  const result = await updateItemKindSaleMechanism(
    s.apiBaseUrl,
    s.tenantId,
    entry.value,
    saleMechanism,
  );
  if (result.status !== 200) {
    const message = result.body.message ?? result.body.error;
    if (isSaleMechanismSoldLinesMessage(message)) {
      // ITEM2 Slice 3: the route reported the sold-lines lock. Disable the
      // control from here on rather than merely showing this one error,
      // matching the brief's own "confirming or explaining before the
      // fact rather than only after" activation-control convention.
      entry.saleMechanismLocked = true;
      status.textContent = SALE_MECHANISM_LOCKED_TEXT;
    } else {
      status.textContent = message ?? "Could not change that.";
    }
    paintChips();
    return;
  }
  // Re-renders the row directly from the route's own response, rather
  // than a full loadVocabularies() reload: this is the one row that
  // changed, and the response already carries its new state.
  entry.saleMechanism = result.body.value.saleMechanism ?? null;
  status.textContent = saleMechanismStatusText(entry.saleMechanism);
  paintChips();
}

// ITEM2 Slice 5 (docs/build-briefs/ITEM2-a-kind-sale-mechanism-is-picked-
// in-settings.md Section 4's "Slice 5" paragraph; DEC-166). Whether one
// item_kind row sells per ounce and so has a pour-volume assignment to
// show at all: liquor always (Section 3.5, its own sale_mechanism row is
// NULL forever), every other kind only once its own picker (Slice 2,
// appendItemKindMechanismPicker above) has "per_ounce" chosen. A
// whole-unit kind or one with no mechanism chosen yet gets no picker,
// matching this section's own "each per-ounce item kind's row" wording.
function itemKindEntrySellsPerOunce(entry: AdminVocabularyEntry): boolean {
  return entry.value === "liquor" || entry.saleMechanism === "per_ounce";
}

/** Appends the pour-volume assignment picker to one item_kind row: a
 * checkbox SET, never an add-one-at-a-time list (the location's pour
 * volumes already exist independently of any kind, added on the "Pour
 * volumes" editor below, in the Liquor group of this same Menu tab; this
 * screen only toggles which of them this kind pours), the same kind of
 * grid renderItemCategoryEditor/renderItemCategoryList
 * already build for item_category's own kind picker. Does nothing for a
 * kind that does not sell per ounce (itemKindEntrySellsPerOunce above).
 *
 * Permission-driven, never role-driven (CLAUDE.md): the checkboxes are
 * DISABLED, never hidden, when the resolved session snapshot lacks
 * admin.location_settings.manage, the identical gating
 * appendItemKindMechanismPicker uses immediately above for the same
 * reason -- informational, not actionable, for a manager-only view.
 */
function appendItemKindPourVolumesPicker(row: HTMLElement, entry: AdminVocabularyEntry): void {
  if (entry.value === "liquor" || !itemKindEntrySellsPerOunce(entry)) {
    return;
  }

  const wrap = document.createElement("div");
  wrap.className = "admin-item-kind-pour-volumes-wrap";
  wrap.dataset["testid"] = "admin-item-kind-pour-volumes";
  wrap.dataset["itemKind"] = entry.value;

  const label = document.createElement("span");
  label.className = "admin-field-help";
  label.textContent = "Pour volumes";
  wrap.append(label);

  const picker = document.createElement("div");
  picker.className = "admin-chip-select";
  picker.dataset["testid"] = "admin-item-kind-pour-volumes-picker";
  picker.setAttribute("aria-label", `Which pour volumes ${entry.label} pours`);
  wrap.append(picker);

  const s = sessionOrUndefined();
  const canManage = s !== undefined && hasPermission(s, "admin.location_settings.manage");

  const paint = (): void => {
    picker.replaceChildren();
    // Recomputed on EVERY paint, not captured once outside it: this
    // function is called again after a toggle's own PUT lands (both to
    // show the disabled, in-flight state before it and the saved result
    // after), and a Set captured once at row-creation time would never
    // reflect entry.assignedPourVolumeValues being reassigned by
    // handleItemKindPourVolumeToggle below, leaving every repaint show
    // the row exactly as it looked when this row was first rendered.
    // Every ACTIVE pour_volume, plus any value this kind is already
    // assigned even if that value has since been retired (mirrors
    // renderBottlePourOptionChips' own "merge already-selected" rule just
    // above in this file): retiring a volume stops it being ASSIGNED
    // next, it never silently drops an existing assignment from view.
    const assigned = new Set(entry.assignedPourVolumeValues ?? []);
    const allVolumes = byPourVolumeAmount(adminVocabularies["pour_volume"] ?? []);
    const options = allVolumes.filter((volume) => volume.active || assigned.has(volume.value));
    const pending = pourVolumeTogglesPending.get(entry.value) ?? new Set<string>();
    for (const volume of options) {
      const optionLabel = document.createElement("label");
      optionLabel.className = "admin-chip-option";
      const input = document.createElement("input");
      input.type = "checkbox";
      input.className = "admin-chip-option-input";
      input.dataset["testid"] = "admin-item-kind-pour-volume-option";
      input.dataset["value"] = volume.value;
      input.checked = assigned.has(volume.value);
      // A checkbox whose OWN toggle is still queued or in flight (below)
      // stays disabled through this repaint too, even though a SIBLING
      // toggle's own response is what triggered it -- otherwise a second
      // rapid tap on the SAME checkbox could fire a second overlapping
      // PUT for it before the first has landed.
      input.disabled = !canManage || pending.has(volume.value);
      input.addEventListener(
        "change",
        () => void handleItemKindPourVolumeToggle(entry, volume.value, input.checked, wrap, paint),
      );
      const text = document.createElement("span");
      text.className = "admin-chip-option-label";
      text.textContent = volume.label;
      optionLabel.append(input, text);
      picker.append(optionLabel);
    }
    if (options.length === 0) {
      const empty = document.createElement("span");
      empty.className = "admin-field-help";
      empty.textContent = "Add a pour volume in Inventory Setup first.";
      picker.append(empty);
    }
  };
  paint();
  row.append(wrap);
}

// Guards handleItemKindPourVolumeToggle's own PUT against the race this
// route's full-set-replace shape makes possible when two toggles land for
// the SAME item kind close together: two rapid toggles on one kind's row
// used to each snapshot entry.assignedPourVolumeValues before the OTHER's
// PUT had landed, so whichever PUT reached the server last silently
// dropped the other's change. Both `const` Maps below hold transient
// UI-only race-guard state, not tenant configuration, the same shape
// vocabAmounts/vocabUnits above already use and check:admin-invalidate-
// coverage's own documented scope (module-level `let` only) excludes: a
// leftover entry here cannot leak the previous employee's own data to the
// next one, since a fresh session repaints straight from
// adminVocabularies' own real assignedPourVolumeValues regardless of
// whatever this map happens to hold.
//
// pourVolumeToggleQueue, per item kind: the PRIOR toggle's own promise for
// this same kind, chained onto rather than fired independently, so this
// toggle's PUT body is always built from a baseline that already includes
// every earlier toggle's own committed write for this kind -- not merely
// from entry.assignedPourVolumeValues as it stood at the moment THIS
// checkbox was clicked. Every toggle for one kind therefore both SENDS
// and APPLIES strictly in the order it was clicked: unlike
// countsLoadSequence's own out-of-order-arrival guard (a bare comparison
// against a value captured before an await, appropriate there because
// loadCounts' own fetches genuinely race each other), a monotonic
// sequence number compared at each response's own arrival would be WRONG
// here and was tried and reverted: it would compare an EARLIER toggle's
// own response against a counter already bumped by a LATER toggle's own
// CLICK, discarding the earlier one's perfectly-current write as "stale"
// even though the queue guarantees it is the one that must apply next. A
// DIFFERENT kind's own toggle never waits on this one.
const pourVolumeToggleQueue = new Map<string, Promise<void>>();

// pourVolumeTogglesPending: which pour_volume VALUES, per kind, currently
// have a toggle queued or in flight. Read by appendItemKindPourVolumesPicker's
// own paint() above to keep exactly those checkboxes disabled across a
// repaint triggered by a sibling toggle's own response, never the whole
// kind's row: a different checkbox for the SAME kind stays tappable while
// one of its siblings is mid-flight, which is what lets two real, ordinary
// taps on two different pour volumes for one kind (the race this item
// fixes) both actually register.
const pourVolumeTogglesPending = new Map<string, Set<string>>();

/** One checkbox toggled: computes the FULL next assigned set (this route
 * replaces the whole set atomically, never one add/remove call per
 * toggle) and saves it. Reverts the checkbox and shows the server's own
 * message on failure, the same "the response already carries its new
 * state" pattern setItemKindSaleMechanism above uses.
 *
 * Chains onto pourVolumeToggleQueue (above) rather than firing its own PUT
 * immediately: a second toggle for the SAME kind, started before the
 * first's PUT has landed, now waits for that first PUT's own result to be
 * applied to entry.assignedPourVolumeValues (and repainted) before
 * computing its own "current" set, so its own PUT body always includes
 * the earlier toggle's change, and the two can never apply out of order. */
async function handleItemKindPourVolumeToggle(
  entry: AdminVocabularyEntry,
  value: string,
  nextChecked: boolean,
  wrap: HTMLElement,
  repaint: () => void,
): Promise<void> {
  const pending = pourVolumeTogglesPending.get(entry.value) ?? new Set<string>();
  pending.add(value);
  pourVolumeTogglesPending.set(entry.value, pending);
  repaint();

  const priorForKind = pourVolumeToggleQueue.get(entry.value) ?? Promise.resolve();
  const thisToggle = priorForKind
    .catch(() => {
      // setItemKindPourVolumes never rejects (it resolves to a typed
      // ApiResult even on a non-200 status); this guards only against an
      // unforeseen rejection in a PRIOR queued toggle so it can never
      // block this one from running.
    })
    .then(async () => {
      // QA finding, post-verification review: the employee can sign out
      // (invalidate()) while THIS toggle is still queued behind an
      // earlier one, or while its own PUT is in flight. `pending.delete`
      // now runs in `finally` below no matter which of those happens, so
      // a leftover entry never leaves the NEXT employee's own checkbox on
      // this same kind permanently disabled. `session()` throws once
      // signed out (requireSession()'s own contract); using
      // sessionOrUndefined() and bailing out quietly instead -- the same
      // SET16-F1 shape sessionOrUndefined's own declaration comment
      // documents -- is what lets `finally` actually run rather than the
      // thrown error skipping straight to this promise's own rejection
      // (which pourVolumeToggleQueue's `.catch(() => {})` above would
      // swallow for the NEXT queued toggle, but would never clear
      // `pending` for this one).
      try {
        const s = sessionOrUndefined();
        if (s === undefined) {
          return;
        }
        const current = new Set(entry.assignedPourVolumeValues ?? []);
        if (nextChecked) {
          current.add(value);
        } else {
          current.delete(value);
        }
        const result = await setItemKindPourVolumes(
          s.apiBaseUrl,
          s.tenantId,
          entry.value,
          [...current],
          entry.pourVolumesVersion,
        );
        // The employee can ALSO sign out during the await immediately
        // above; re-check rather than trust the snapshot taken before it.
        if (sessionOrUndefined() === undefined) {
          return;
        }
        const status = wrap.querySelector(".admin-field-help");
        if (result.status === 409 && result.body.error === "pour_volumes_changed") {
          // P9-F2 (DEC-367): someone else changed this list after it was
          // loaded. Nothing was saved; reload the latest list and say so
          // on the repainted row. The in-flight mark is cleared first: the
          // reload builds a new row that reads it, and would otherwise
          // paint this size's checkbox disabled for good.
          pending.delete(value);
          await loadVocabularies({ forceFresh: true });
          if (sessionOrUndefined() === undefined) {
            return;
          }
          const fresh = document.querySelector(
            `[data-testid="admin-item-kind-pour-volumes"][data-item-kind="${CSS.escape(entry.value)}"] .admin-field-help`,
          );
          if (fresh !== null) {
            fresh.textContent = result.body.message ?? "Changed by someone else";
          }
          return;
        }
        if (result.status !== 200) {
          if (status !== null) {
            status.textContent = result.body.message ?? result.body.error ?? "Could not save that.";
          }
          return;
        }
        entry.assignedPourVolumeValues = result.body.pourVolumes.map((v) => v.value);
        entry.pourVolumesVersion = result.body.pourVolumesVersion;
        if (status !== null) {
          status.textContent = "Pour volumes";
        }
      } finally {
        pending.delete(value);
        // A repaint against a dead session's own torn-down DOM (or, worse,
        // against the NEXT employee's freshly rendered one) is exactly
        // what currentSession being present here rules out: invalidate()
        // sets it undefined before this toggle's own queued continuation
        // can resume, so this checks the REAL current session, not merely
        // whether one was present when this toggle started.
        if (currentSession !== undefined) {
          repaint();
        }
      }
    });
  pourVolumeToggleQueue.set(entry.value, thisToggle);
  await thisToggle;
}

/** UX37 (DEC-184): the text above each vocabulary list that says what its
 * On/Off switch does. Every editor in VOCAB_EDITORS has an entry. */
const VOCAB_SWITCH_HELP_IDS: Readonly<Record<string, string>> = {
  item_kind: "admin-vocab-item-kind-help",
  container_type: "admin-vocab-container-type-switch-help",
  pour_volume: "admin-vocab-pour-volume-switch-help",
  liquor_bottle_size: "admin-vocab-liquor-bottle-size-switch-help",
  serving_size: "admin-vocab-serving-size-switch-help",
  drink_display_category: "admin-vocab-drink-display-category-switch-help",
};

/** Renders one vocabulary list. Retired values stay visible with the
 * switch turned off, because retiring is reversible and a manager who
 * retired the wrong one needs a way back. UX11: built by the same shared
 * activation-switch helper the drink list uses, rather than its own
 * button that skipped confirmation. */
function applyVocabularyAddGating(editor: VocabEditor): void {
  const canEdit = canEditVocabulary(editor.vocabulary);
  editor.addButton.disabled = !canEdit;
  if (editor.displayInput !== null) {
    editor.displayInput.disabled = !canEdit;
  }
  if (editor.amountButton !== null) {
    editor.amountButton.disabled = !canEdit;
  }
  if (editor.unitPicker !== null) {
    for (const button of editor.unitPicker.querySelectorAll<HTMLButtonElement>("button")) {
      button.disabled = !canEdit;
    }
  }
}

function renderVocabulary(editor: VocabEditor): void {
  const values = (adminVocabularies[editor.vocabulary] ?? []).filter((entry) => editor.vocabulary !== "item_kind" || isCurrentItemKind(entry.value));
  const canEdit = canEditVocabulary(editor.vocabulary);
  applyVocabularyAddGating(editor);
  editor.listEl.replaceChildren();
  // ITEM2 Slice 2: item_kind rows carry an extra block (the mechanism
  // picker) that a bare label-plus-switch row was never built to hold, so
  // this editor's rows stack that extra block below a top line rather
  // than squeezing a third item into the existing space-between row.
  const isItemKindEditor = editor.vocabulary === "item_kind";
  // UX35: pour_volume is one vocabulary in this generic list whose values
  // are bare numeric-ounce strings with an intended numeric reading
  // order. UX43: liquor_bottle_size and serving_size are amount+unit
  // tokens ("750ml", "16oz") with the same intended real-magnitude
  // reading order, no longer left to their own stored insertion order
  // (Site 2: this branch reads adminVocabularies directly and bypasses
  // tenantLiquorBottleSizes/tenantServingSizes's own Site 1 sort
  // entirely). Every other vocabulary this function serves
  // (liquor_category, container_type, item_kind, drink_display_category)
  // is non-numeric text, so this stays a targeted branch rather than a
  // blanket sort.
  const orderedValues =
    editor.vocabulary === "pour_volume"
      ? byPourVolumeAmount(values)
      : editor.vocabulary === "liquor_bottle_size" || editor.vocabulary === "serving_size"
        ? bySizeTokenAmount(values)
        : // UX88 (DEC-339): every other vocabulary here is a list of names
          // with no reorder control, so it reads A to Z by the shown label.
          sortByName(values, (entry) => entry.label);
  for (const entry of orderedValues) {
    const row = document.createElement("li");
    row.className = "admin-void-reason-row";
    row.dataset["testid"] = "admin-vocab-row";
    row.dataset["value"] = entry.value;
    if (isItemKindEditor) {
      // Stacks the extra mechanism block below the label/switch top line
      // instead of squeezing a third item into the row's own existing
      // space-between layout.
      row.classList.add("admin-void-reason-row--stacked");
    }

    const topLine = isItemKindEditor ? document.createElement("div") : row;
    if (isItemKindEditor) {
      topLine.className = "admin-item-kind-mechanism-top-line";
      row.append(topLine);
    }

    const label = document.createElement("span");
    label.className = "admin-vocab-row-label";
    label.textContent = entry.active ? entry.label : `${entry.label} (off)`;
    topLine.append(label);

    topLine.append(
      renderActivationSwitch({
        active: entry.active,
        entityLabel: entry.label,
        toggleTestId: "admin-vocab-toggle",
        disabled: !canEdit,
        describedById: VOCAB_SWITCH_HELP_IDS[editor.vocabulary],
        statusTestId: "admin-vocab-status-text",
        activeText: "On",
        inactiveText: "Off",
        confirmDeactivate: (name) => ({
          title: `Turn off ${name}?`,
          message: `${name} stays exactly as it is on every drink and record that already uses it. It only stops appearing as a choice on anything new, and can be turned back on any time.`,
          confirmLabel: "Turn off",
        }),
        write: async (nextActive) => {
          const s = session();
          const result = await setVocabularyValueActive(
            s.apiBaseUrl,
            s.tenantId,
            editor.vocabulary,
            entry.value,
            nextActive,
          );
          if (result.status !== 200) {
            // The server refuses retiring the last active value; that
            // message is the useful one, so it is shown rather than a
            // generic failure.
            return {
              ok: false,
              message: result.body.message ?? result.body.error ?? "Could not change that.",
            };
          }
          return { ok: true, active: result.body.value.active };
        },
        onError: (message) => {
          editor.statusEl.textContent = message;
        },
        onChange: () => {
          editor.statusEl.textContent = "";
          void loadVocabularies({ forceFresh: true });
        },
      }),
    );
    if (isItemKindEditor) {
      appendItemKindMechanismPicker(row, entry);
      // The selected kind owns its pour assignments in Inventory Setup.
      if (!entry.active) row.classList.add("setia1-kind-off");
    }
    editor.listEl.append(row);
  }
}

function renderAllVocabularies(): void {
  for (const editor of VOCAB_EDITORS) {
    renderVocabulary(editor);
  }
  renderItemCategoryEditor();
  renderTargetMarkupCard();
  renderInventorySetupKind();
}

// ---------------------------------------------------------------------
// VOC1-F4 (docs/build-briefs/VOC1-F4-drink-modifier-catalog.md, Section
// 2.1). The tenant's drink-modifier catalog: label, a price adjustment,
// active/retire. Mirrors renderShelfTierRows' own row shape (name field,
// "$" money field, renderActivationSwitch), reusing its established CSS
// classes (admin-shelf-tier-row and siblings) rather than duplicating
// them under a new name for what is visually the identical row. See the
// drinkModifiersListEl declaration above for why this commits immediately
// rather than batching into "Save location settings" the way shelf_tier
// does.
// ---------------------------------------------------------------------

// CORE13. The routes behind these controls (add, rename, reprice, on/off) need
// admin.location_settings.manage. A session that can open Settings without it used to find out by
// tapping: the add row is not drawn and the existing rows are read-only for such a session.
// CORE13-F1. The write permission each configurable list needs, mirroring the
// POST and PATCH vocabularies routes in src/api/server.ts (ITEM_EDITOR_VOCABULARIES
// there): the lists the item editor offers are item data and need
// admin.liquor_product.manage (PERM1, so a Manager can add an item category);
// every other list is a location setting. Never a role name.
const ITEM_EDITOR_VOCABULARY_KEYS: ReadonlySet<string> = new Set([
  "item_category",
  "container_type",
  "drink_display_category",
  "liquor_bottle_size",
  "serving_size",
  "keg_size",
]);

function vocabularyWritePermission(vocabulary: string): string {
  return ITEM_EDITOR_VOCABULARY_KEYS.has(vocabulary)
    ? "admin.liquor_product.manage"
    : "admin.location_settings.manage";
}

function canEditVocabulary(vocabulary: string): boolean {
  const active = currentSession;
  return active !== undefined && hasPermission(active, vocabularyWritePermission(vocabulary));
}

function canManageLocationSettingsLists(): boolean {
  const active = currentSession;
  return active !== undefined && hasPermission(active, "admin.location_settings.manage");
}

// ITEM1 Slice 3 (DEC-147, DEC-153). The kinds this editor offers on its
// own picker: every ACTIVE item_kind value, including Liquor. A
// tenant-added kind appears here automatically the moment it is added on
// the Item kinds editor above, with no code change -- the concrete
// realization of DEC-147's own "a kind added later acquires its category
// list as pure data" promise.
function itemCategoryEditorKinds(): { value: string; label: string }[] {
  return sortOptions(
    (adminVocabularies["item_kind"] ?? [])
      .filter((entry) => entry.active && isCurrentItemKind(entry.value))
      .map((entry) => ({ value: entry.value, label: entry.label })),
  );
}

// Rebuilds the kind picker, then the category list for whichever kind is
// currently selected. Picks a default (the first offered kind) the first
// time this renders after a load, mirroring how the item-kind chip's own
// default ('liquor') is chosen; a kind that stops being offered (retired
// since the last render) falls back to the new first kind rather than
// leaving the picker showing a now-invalid selection with no matching
// chip checked.
function renderItemCategoryEditor(): void {
  const kinds = itemCategoryEditorKinds();
  if (
    itemCategoryEditorScope === null ||
    !kinds.some((kind) => kind.value === itemCategoryEditorScope)
  ) {
    itemCategoryEditorScope = kinds[0]?.value ?? null;
  }
  renderBottleFilterChipGroup(
    itemCategoryKindEl,
    "admin-item-category-kind-option",
    "admin-item-category-kind-option",
    kinds,
    itemCategoryEditorScope ?? "",
    (value) => {
      itemCategoryEditorScope = value;
      renderItemCategoryList();
    },
  );
  renderItemCategoryList();
}

function renderItemCategoryList(): void {
  itemCategoryListEl.replaceChildren();
  const canEdit = canEditVocabulary("item_category");
  itemCategoryAddButton.disabled = !canEdit;
  itemCategoryDisplayInput.disabled = !canEdit;
  if (itemCategoryEditorScope === null) {
    return;
  }
  const scope = itemCategoryEditorScope;
  const values = (adminVocabularies["item_category"] ?? []).filter(
    (entry) => entry.itemKindScope === scope,
  );
  for (const entry of sortByName(values, (listed) => listed.label)) {
    const row = document.createElement("li");
    row.className = "admin-void-reason-row";
    row.dataset["testid"] = "admin-item-category-row";
    row.dataset["value"] = entry.value;

    const label = document.createElement("span");
    label.className = "admin-vocab-row-label";
    label.textContent = entry.active ? entry.label : `${entry.label} (off)`;
    row.append(label);

    row.append(
      renderActivationSwitch({
        active: entry.active,
        entityLabel: entry.label,
        toggleTestId: "admin-item-category-toggle",
        disabled: !canEdit,
        statusTestId: "admin-item-category-status-text",
        activeText: "On",
        inactiveText: "Off",
        confirmDeactivate: (name) => ({
          title: `Turn off ${name}?`,
          message: `${name} stays exactly as it is on every item that already uses it. It only stops appearing as a choice on anything new, and can be turned back on any time.`,
          confirmLabel: "Turn off",
        }),
        write: async (nextActive) => {
          const s = session();
          const result = await setVocabularyValueActive(
            s.apiBaseUrl,
            s.tenantId,
            "item_category",
            entry.value,
            nextActive,
          );
          if (result.status !== 200) {
            return {
              ok: false,
              message: result.body.message ?? result.body.error ?? "Could not change that.",
            };
          }
          return { ok: true, active: result.body.value.active };
        },
        onError: (message) => {
          itemCategoryStatusEl.textContent = message;
        },
        onChange: () => {
          itemCategoryStatusEl.textContent = "";
          void loadVocabularies({ forceFresh: true });
        },
      }),
    );
    itemCategoryListEl.append(row);
  }
}

async function addItemCategoryEntry(): Promise<void> {
  if (itemCategoryEditorScope === null) {
    itemCategoryStatusEl.textContent = "Add an item kind first.";
    return;
  }
  const displayText = itemCategoryDisplayInput.value.trim();
  if (displayText.length === 0) {
    itemCategoryStatusEl.textContent = "Type what should appear on screen.";
    return;
  }
  const s = session();
  itemCategoryAddButton.disabled = true;
  try {
    const result = await addVocabularyValue(s.apiBaseUrl, s.tenantId, "item_category", {
      displayText,
      itemKindScope: itemCategoryEditorScope,
    });
    if (result.status !== 201) {
      itemCategoryStatusEl.textContent =
        result.body.message ?? result.body.error ?? "Could not add that.";
      return;
    }
    itemCategoryDisplayInput.value = "";
    itemCategoryStatusEl.textContent = `Added ${displayText}.`;
    await loadVocabularies({ forceFresh: true });
  } finally {
    itemCategoryAddButton.disabled = false;
  }
}

itemCategoryAddButton.addEventListener("click", () => void addItemCategoryEntry());

// UX58 (revised, qa-lead finding 1): a single in-flight vocabulary fetch,
// shared by every caller, TAGGED with the session generation it was
// started under. showSection's own "Menu tapped again while the opening
// load is still in flight" case, and a direct addVocabularyEntry()
// refresh landing at the same moment, both used to start their own
// independent GET and redraw every list twice from an identical response.
// A caller that finds one already running for THIS SAME session awaits
// THAT one instead of starting a second.
//
// The gen tag is load-bearing, not decorative. Without it: employee A
// starts a fetch (gen G1), signs out mid-fetch (invalidate() bumps
// loadGeneration to G2, but deliberately does not touch this binding, see
// the allowlist entry below), and employee B opens Menu before A's fetch
// settles. B's own loadVocabularies() found the SAME in-flight promise
// and merged into it; when A's fetch's response finally lands,
// loadVocabulariesInner's own loadIsCurrent(gen) check (gen=G1) correctly
// discards it as A's own stale response, so it resolves as a no-op --
// but B's merged call resolves right along with it, `vocabulariesLoaded`
// stays false, and B is left looking at whatever A's session last
// painted. Comparing the entry's OWN gen against the CURRENT
// loadGeneration at merge time is what stops that: B's own call sees
// existing.gen (G1) !== loadGeneration (G2) and starts its own fresh
// fetch instead of merging into a fetch that can never paint anything
// for B's own session.
//
// Cleared only once the running fetch itself settles, in loadVocabularies'
// own finally below -- and ONLY if this entry is still that same fetch's
// own entry (`vocabulariesLoadInFlight?.promise === promise`), so a STALE
// fetch's finally can never clear a NEWER fetch's own reference out from
// under it (the newer fetch may have replaced this slot already, exactly
// the A-then-B sequence above: A's finally must not erase B's own
// in-flight entry).
let vocabulariesLoadInFlight: { readonly gen: number; readonly promise: Promise<void> } | undefined;

// UX57 (revised, qa-lead finding 2): guards loadVocabulariesInner's own
// response against an OLDER refresh painting over a NEWER one, the same
// shape countsLoadSequence already uses for loadCounts (see that
// binding's own comment). Bumped at the start of every call that actually
// starts a fetch (never on a merge, which reuses its target's own
// sequence number rather than claiming a new one); a response whose own
// captured sequence number is no longer the latest by the time it lands
// is discarded rather than applied.
//
// This guard is reachable again, not merely decorative, because
// loadVocabularies' own forceFresh option (below) can genuinely have TWO
// fetches in flight for the same session at once: a post-write refresh
// (addVocabularyEntry, after its own POST resolves) always starts a fresh
// GET rather than merging into whatever GET was already running, because
// that older GET may have been issued BEFORE the write committed and
// would otherwise repaint the list from a response that does not contain
// the value just added. When that older GET's own response lands after
// the newer, forced one already painted, this sequence check is what
// discards it.
let vocabulariesLoadSequence = 0;

/** `forceFresh`: never merge into an in-flight fetch, even one started
 * under the SAME session generation. A post-write caller (addVocabulary-
 * Entry, after its own POST resolves) passes this, because merging into a
 * GET that was already in flight before the write committed would repaint
 * the list from a response that predates the write -- UX56's own defect
 * in a new shape, now that a same-session merge is otherwise legitimate. */
async function loadVocabularies(options?: { readonly forceFresh?: boolean }): Promise<void> {
  const gen = loadGeneration;
  const existing = vocabulariesLoadInFlight;
  if (options?.forceFresh !== true && existing !== undefined && existing.gen === gen) {
    return existing.promise;
  }
  const promise: Promise<void> = loadVocabulariesInner().finally(() => {
    if (vocabulariesLoadInFlight?.promise === promise) {
      vocabulariesLoadInFlight = undefined;
    }
  });
  vocabulariesLoadInFlight = { gen, promise };
  return promise;
}

async function loadVocabulariesInner(): Promise<void> {
  const gen = loadGeneration;
  const sequenceAtStart = ++vocabulariesLoadSequence;
  vocabulariesLoadFailed = false;
  const s = session();
  // SET17: resolve the permission this loader's own route requires,
  // rather than inheriting whatever the section that reached it happens
  // to demand. This runs on the RECIPES and MENU tabs (SET10: shared
  // between the Add/Edit Drink sheet's own pickers on Recipes and the
  // eight vocabulary editors on Menu), whose union is
  // admin.menu_drink.manage and does not mention tenant settings at all.
  // No seeded role holds manage without view today, so this was LATENT
  // rather than live, and it is exactly the coincidence SET17 exists to
  // stop relying on: DEC-023 makes roles configurable and a per-user
  // override is enough to break the alignment with no code change.
  if (!hasPermission(s, "admin.tenant_settings.view")) {
    // SET16-F1: same gap as loadDrinks' own guard -- returning before any
    // placeholder write leaves a denied session looking at whatever the
    // previous employee's session had painted here. Unlike loadDrinks/
    // loadBottles this loader has no single placeholder write of its own
    // (renderAllVocabularies, skipped below, is what normally repaints
    // every vocab list), so every one of VOCAB_EDITORS' own list
    // elements is cleared here instead.
    for (const editor of VOCAB_EDITORS) {
      const row = document.createElement("li");
      row.className = "tabs-empty";
      row.textContent = "You do not have permission to view this.";
      editor.listEl.replaceChildren(row);
    }
    // INV27 (coordinator finding, re-review 2026-09-29): the "Default
    // target markup %, by item kind" card is neither one of VOCAB_EDITORS'
    // own list elements ; it needs the identical
    // clear-on-denial treatment for the identical reason -- without it, a
    // session denied admin.tenant_settings.view who reaches Settings >
    // Inventory on a shared tablet (its own landing tab for a plain
    // inventory_manager, per settings-fetch-gated-on-tenant-settings-
    // permission.spec.ts's own header comment) would still see whatever
    // rows the PREVIOUS employee's session left painted there, since
    // renderAllVocabularies (the only other caller of
    // renderTargetMarkupCard) is skipped on this branch.
    const targetMarkupsDenied = document.createElement("p");
    targetMarkupsDenied.className = "admin-field-help";
    targetMarkupsDenied.textContent = "You do not have permission to view this.";
    targetMarkupsListEl.replaceChildren(targetMarkupsDenied);
    // VER57-F4 (DEC-273): a role that may manage liquor items still needs
    // the pour sizes for Add item. Read them through the narrow route (the
    // sizes only), never the tenant settings. vocabulariesLoaded stays
    // false here on purpose; see pourOptionsByItemKindFallback's comment.
    if (hasPermission(s, "admin.liquor_product.manage")) {
      const pourResult = await getLiquorProductPourOptions(s.apiBaseUrl, s.tenantId);
      if (!loadIsCurrent(gen) || sequenceAtStart !== vocabulariesLoadSequence) return;
      if (pourResult.status === 200) {
        pourOptionsByItemKindFallback = pourResult.body.pourOptionsByItemKind;
        vocabulariesLoadFailed = false;
      } else {
        vocabulariesLoadFailed = true;
      }
      const categoryResult = await getLiquorProductItemCategories(s.apiBaseUrl, s.tenantId);
      if (!loadIsCurrent(gen) || sequenceAtStart !== vocabulariesLoadSequence) return;
      if (categoryResult.status === 200) {
        itemCategoriesByScopeFallback = categoryResult.body.itemCategoriesByScope;
      }
      if (!bottleFormScrim.hidden) {
        const pickedLiquor = bottleCategorySelect.value;
        const pickedOther = bottleCategoryOtherSelect.value;
        populateBottleCategorySelect();
        populateBottleCategoryOtherSelect();
        restoreSelectChoice(bottleCategorySelect, pickedLiquor);
        restoreSelectChoice(bottleCategoryOtherSelect, pickedOther);
        renderBottlePourOptionChips();
      }
    }
    return;
  }
  const result = await getVocabularies(s.apiBaseUrl, s.tenantId);
  // SET16-F4: this session's teardown ran while the fetch above was in
  // flight; discard rather than write over what invalidate() already
  // cleared or what the session now on screen has painted.
  if (!loadIsCurrent(gen)) return;
  if (result.status !== 200) {
    // VER57-F2: only the newest load may declare failure; an older GET
    // that fails after a newer one started must not overwrite its state.
    if (sequenceAtStart === vocabulariesLoadSequence) {
      vocabulariesLoadFailed = true;
      if (!bottleFormScrim.hidden) {
        renderBottlePourOptionChips();
      }
    }
    return;
  }
  if (sequenceAtStart !== vocabulariesLoadSequence) {
    // UX57: a newer refresh has already started (or already landed) while
    // this response was in flight. Painting this older response would
    // hide whatever the newer one already wrote, or is about to write, so
    // this one discards itself instead.
    return;
  }
  // SET15/SET10: set only on success, the same shape drinksLoaded already
  // uses, so a failed load leaves the latch false and the next visit to
  // either Recipes or Menu retries instead of skipping the fetch for the
  // rest of the visit.
  vocabulariesLoaded = true;
  vocabulariesLoadFailed = false;
  adminVocabularies = result.body.vocabularies;
  // INV27 (DEC-227 item 3): Settings default target markup %, fetched
  // alongside vocabularies (same permission gate above, same item_kind
  // vocabulary entries its own grouped card below is built from) and
  // awaited BEFORE renderAllVocabularies paints that card, so
  // renderTargetMarkupCard sees current data on its first paint rather
  // than an empty map that only fills in on a later re-render. A failed
  // fetch here leaves itemKindTargetMarkupDefaults
  // at whatever it already was (never a fabricated figure); the item
  // editor's own prefill simply finds nothing to prefill from, and each
  // row's own input renders blank.
  if (sequenceAtStart === vocabulariesLoadSequence) {
    const markupsResult = await getItemKindTargetMarkups(s.apiBaseUrl, s.tenantId);
    if (
      loadIsCurrent(gen) &&
      sequenceAtStart === vocabulariesLoadSequence &&
      markupsResult.status === 200
    ) {
      itemKindTargetMarkupDefaults = {};
      for (const row of markupsResult.body.targetMarkups) {
        if (isCurrentItemKind(row.itemKind) && row.targetMarkupPercent !== null) {
          itemKindTargetMarkupDefaults[row.itemKind] = row.targetMarkupPercent;
        }
      }
    }
  }
  renderAllVocabularies();
  // The item editor's own Target markup % inputs prefill from this store;
  // repaint the calculator if the sheet happens to be open already.
  if (!bottleFormScrim.hidden) {
    renderBottleCalculator();
  }
  // The Add/Edit Bottle sheet's own category
  // pickers (populateBottleCategorySelect for 'liquor', populateBottle-
  // CategoryOtherSelect for every other kind) are built imperatively at
  // item-kind-chip click time, reading adminVocabularies as it stood at
  // that instant, with nothing subscribed to a later vocabulary load. The
  // "menu" section's own initial loadVocabularies() call, awaited inside
  // openAdminScreen/showSection before the admin screen's caller ever
  // resolves, races the admin screen becoming visible: a manager who
  // opens Add Bottle before that fetch lands gets a category select built
  // empty, and it stays empty forever, since nothing else ever calls
  // these two functions again for the same kind. Re-run both here, but
  // only while the sheet is actually open (bottleFormScrim.hidden false),
  // so a vocab load that lands while the sheet is closed does not touch
  // a select nobody is looking at.
  // VER57: the pour-size chips have the same shape (built from
  // adminVocabularies when the sheet opens), so a sheet opened before this
  // load landed showed none until the kind chip was switched. Repainted
  // here too. bottleFormPourOptions holds the manager's own selection and
  // the renderer reads checked state from it, so a repaint keeps a checked
  // chip checked and a cleared chip cleared.
  // The rebuild clears each <select>, so a category the manager already
  // picked would silently reset to the first option and the wrong
  // category would save (admin.spec add-bottle, 2026-10-07). Keep the
  // manager's choice when it is still offered.
  if (!bottleFormScrim.hidden) {
    const pickedLiquor = bottleCategorySelect.value;
    const pickedOther = bottleCategoryOtherSelect.value;
    populateBottleCategorySelect();
    populateBottleCategoryOtherSelect();
    restoreSelectChoice(bottleCategorySelect, pickedLiquor);
    restoreSelectChoice(bottleCategoryOtherSelect, pickedOther);
    renderBottlePourOptionChips();
  }
}

// INV27 (docs/build-briefs/INV27-item-editor-redesign.md; DEC-227 item 3;
// mockup Artboard D). Settings > Inventory's own default target markup %,
// "one row per active item_kind vocabulary entry" realized as its own
// grouped card (#admin-target-markups-list, index.html's Inventory group
// panel, after the Past counts section), rather than an input inline on
// each item_kind row (the first correction, 2026-09-29) or attached to
// the Menu group at all (the second correction, same day: the approved
// mockup places this card under Settings > Inventory, not Menu). Reads
// the identical adminVocabularies["item_kind"] list and
// itemKindTargetMarkupDefaults store every other item-kind control on
// this screen already reads; nothing new is fetched here. showSection's
// own loadVocabularies() gate was widened to fire on "inventory" too, so
// this card has real data even when a manager never visits Menu or
// Recipes first.
function renderTargetMarkupCard(): void {
  const s = session();
  const canManage = hasPermission(s, "admin.location_settings.manage");
  const entries = sortByName(
    (adminVocabularies["item_kind"] ?? []).filter((entry) => entry.active && isCurrentItemKind(entry.value)),
    (entry) => entry.label,
  );

  targetMarkupsListEl.innerHTML = "";
  if (entries.length === 0) {
    return;
  }

  const card = document.createElement("div");
  card.className = "admin-target-markups-card";

  for (const entry of entries) {
    const row = document.createElement("div");
    row.className = "admin-target-markup-row";
    row.dataset["testid"] = `admin-target-markup-row-${entry.value}`;

    const label = document.createElement("span");
    label.className = "admin-target-markup-row-label";
    label.textContent = entry.label;

    const inputWrap = document.createElement("span");
    inputWrap.className = "admin-target-markup-row-input";
    const input = document.createElement("input");
    describeSetting(input, "admin-target-markup-help");
    input.type = "number";
    input.inputMode = "decimal";
    input.min = "0";
    input.step = "0.1";
    input.dataset["testid"] = `admin-target-markup-input-${entry.value}`;
    input.className = "field-input";
    input.placeholder = "No default";
    const current = itemKindTargetMarkupDefaults[entry.value];
    input.value = current === undefined ? "" : String(current);
    input.disabled = !canManage;
    const mark = document.createElement("span");
    mark.setAttribute("aria-hidden", "true");
    mark.textContent = "%";
    inputWrap.append(input, mark);

    const status = document.createElement("p");
    status.className = "admin-target-markup-row-status";
    status.dataset["testid"] = `admin-target-markup-status-${entry.value}`;

    input.addEventListener("change", () => {
      void saveItemKindTargetMarkup(entry.value, input, status);
    });

    row.append(label, inputWrap, status);
    card.append(row);
  }

  targetMarkupsListEl.append(card);
}

async function saveItemKindTargetMarkup(
  itemKind: string,
  input: HTMLInputElement,
  status: HTMLElement,
): Promise<void> {
  const s = session();
  const trimmed = input.value.trim();
  const targetMarkupPercent = trimmed.length > 0 ? Number(trimmed) : null;
  if (
    targetMarkupPercent !== null &&
    (!Number.isFinite(targetMarkupPercent) || targetMarkupPercent < 0)
  ) {
    status.textContent = "Enter a non-negative number, or leave blank for no default.";
    return;
  }
  const result = await setItemKindTargetMarkup(
    s.apiBaseUrl,
    s.tenantId,
    itemKind,
    targetMarkupPercent,
  );
  if (result.status !== 200) {
    status.textContent = result.body.message ?? result.body.error ?? "Could not save that.";
    return;
  }
  status.textContent = "";
  itemKindTargetMarkupDefaults = {};
  for (const row of result.body.targetMarkups) {
    if (isCurrentItemKind(row.itemKind) && row.targetMarkupPercent !== null) {
      itemKindTargetMarkupDefaults[row.itemKind] = row.targetMarkupPercent;
    }
  }
}

/** The amount currently tapped for each numeric editor, keyed by
 * vocabulary. Held here rather than in the DOM so the pad is the only
 * thing that can set it: there is no text field to type into. */
const vocabAmounts = new Map<string, string>();
const vocabUnits = new Map<string, string>();

function renderVocabAmount(editor: VocabEditor): void {
  if (editor.amountButton === null) {
    return;
  }
  const amount = vocabAmounts.get(editor.vocabulary);
  editor.amountButton.textContent = amount ?? "";
  if (amount === undefined) {
    const placeholder = document.createElement("span");
    placeholder.className = "admin-vocab-amount-placeholder";
    placeholder.textContent = "Tap to enter a number";
    editor.amountButton.appendChild(placeholder);
  }
}

function renderVocabUnitPicker(editor: VocabEditor): void {
  if (editor.unitPicker === null) {
    return;
  }
  const selected = vocabUnits.get(editor.vocabulary);
  for (const button of editor.unitPicker.querySelectorAll<HTMLButtonElement>(
    ".admin-chip-option",
  )) {
    const unit = button.dataset["unit"] ?? "";
    const isSelected = unit === selected;
    button.classList.toggle("is-selected", isSelected);
    button.setAttribute("aria-checked", isSelected ? "true" : "false");
  }
}

// VOC1 (DEC-044): the shared numeric pad. One instance for all four
// numeric lists; the editor it was opened for is held here rather than
// passed around, mirroring how the other admin sheets work.
const vocabPadScrim = mustFind<HTMLElement>("#admin-vocab-pad-scrim");
const vocabPadDisplay = mustFind<HTMLElement>("#admin-vocab-pad-display");
const vocabPadTitle = mustFind<HTMLElement>("#admin-vocab-pad-title");
let vocabPadEditor: VocabEditor | null = null;
let vocabPadEntry = "";

function renderVocabPad(): void {
  vocabPadDisplay.textContent = vocabPadEntry.length === 0 ? "0" : vocabPadEntry;
}

function openVocabPad(editor: VocabEditor): void {
  vocabPadEditor = editor;
  vocabPadEntry = vocabAmounts.get(editor.vocabulary) ?? "";
  vocabPadTitle.textContent = "Enter a number";
  renderVocabPad();
  vocabPadScrim.hidden = false;
}

function closeVocabPad(): void {
  vocabPadEditor = null;
  vocabPadEntry = "";
  vocabPadScrim.hidden = true;
}

function vocabPadPress(digit: string): void {
  // One decimal point, at most three decimals, and no leading run of
  // zeros. The server validates this too; this is so the pad cannot
  // compose something the server will only refuse afterwards.
  if (digit === ".") {
    if (vocabPadEntry.includes(".")) {
      return;
    }
    vocabPadEntry = vocabPadEntry.length === 0 ? "0." : `${vocabPadEntry}.`;
    renderVocabPad();
    return;
  }
  const dot = vocabPadEntry.indexOf(".");
  if (dot >= 0 && vocabPadEntry.length - dot > 3) {
    return;
  }
  if (vocabPadEntry === "0") {
    vocabPadEntry = digit;
  } else {
    vocabPadEntry = `${vocabPadEntry}${digit}`;
  }
  renderVocabPad();
}

async function addVocabularyEntry(editor: VocabEditor): Promise<void> {
  const s = session();
  // VOC1 (DEC-044): nothing here reads a typed label. A numeric list
  // sends the tapped amount and the picked unit; every other list sends
  // the display text and the server derives the identifier from it.
  let body:
    | { amount: string; unit: string }
    | { displayText: string }
    | { displayText: string; saleMechanisms: ProductSaleMechanism[] };
  let describe: string;
  if (editor.amountButton !== null) {
    const amount = vocabAmounts.get(editor.vocabulary);
    const unit = vocabUnits.get(editor.vocabulary);
    if (amount === undefined || amount.length === 0) {
      editor.statusEl.textContent = "Tap a number first.";
      return;
    }
    if (unit === undefined) {
      editor.statusEl.textContent = "Pick a unit.";
      return;
    }
    body = { amount, unit };
    describe = `${amount} ${unit === "l" ? "L" : unit}`;
  } else {
    const displayText = editor.displayInput?.value.trim() ?? "";
    if (displayText.length === 0) {
      editor.statusEl.textContent = "Type what should appear on screen.";
      return;
    }
    if (editor.vocabulary === "item_kind") {
      const selected = document.querySelector<HTMLInputElement>(
        'input[name="admin-item-kind-initial-policy"]:checked',
      );
      const saleMechanisms =
        selected?.value === "per_ounce"
          ? (["per_ounce"] as ProductSaleMechanism[])
          : selected?.value === "whole_unit"
            ? (["whole_unit"] as ProductSaleMechanism[])
            : selected?.value === "both"
              ? (["per_ounce", "whole_unit"] as ProductSaleMechanism[])
              : undefined;
      if (saleMechanisms === undefined) {
        editor.statusEl.textContent = "Choose at least one selling method for this kind.";
        return;
      }
      body = { displayText, saleMechanisms };
    } else {
      body = { displayText };
    }
    describe = displayText;
  }

  editor.addButton.disabled = true;
  try {
    const result = await addVocabularyValue(s.apiBaseUrl, s.tenantId, editor.vocabulary, body);
    if (result.status !== 201) {
      editor.statusEl.textContent =
        result.body.message ?? result.body.error ?? "Could not add that.";
      return;
    }
    vocabAmounts.delete(editor.vocabulary);
    vocabUnits.delete(editor.vocabulary);
    renderVocabAmount(editor);
    renderVocabUnitPicker(editor);
    if (editor.displayInput !== null) {
      editor.displayInput.value = "";
    }
    // UX56: wait for the refresh to land before saying it worked, so the
    // status text never claims a value the list and the item-kind picker
    // do not show yet.
    await loadVocabularies({ forceFresh: true });
    editor.statusEl.textContent = `Added ${describe}.`;
  } finally {
    editor.addButton.disabled = false;
  }
}

/**
 * TESTDEBT8-F1 (owner ruling 2026-10-10, "Lock until loaded"). The inputs loadSettings fills, and the Save
 * buttons that send them, are disabled until its read has returned, so nothing typed during the await is
 * overwritten and no Save reads an empty field. Business-day, Operations Save and the chip groups keep
 * their own enabled logic and are not listed here (see the brief's follow-up list).
 */
function setSettingsLoadLocked(locked: boolean): void {
  const controls: readonly (HTMLInputElement | HTMLSelectElement | HTMLButtonElement)[] = [
    settingsPinMinLengthInput,
    settingsPinMaxLengthInput,
    settingsPinLockoutAttemptsInput,
    settingsPinLockoutMinutesInput,
    settingsSaveButtonPinPolicy,
    settingsSessionInactivityInput,
    sessionInactivitySaveButton,
    settingsHoldAmountInput,
    settingsHoldIncrementThresholdInput,
    settingsAbandonedTabGratuityInput,
    settingsAbandonedTabGratuityDisclosureInput,
    settingsCloseCardTicketsInput,
    settingsDualPricingInput,
    settingsDualPricingPercentInput,
    settingsTipOnPreDiscountInput,
    settingsSaveButtonMoney,
    settingsSaveButtonTabsPayment,
    settingsAddressLine1Input,
    settingsAddressLine2Input,
    settingsAddressCityInput,
    settingsAddressStateInput,
    settingsAddressPostalCodeInput,
    settingsAddressCountryInput,
    settingsSaveButtonAddress,
  ];
  for (const control of controls) control.disabled = locked;
  settingsStandardPourOuncesEl.disabled = locked || !canManageBusinessDay();
  if (locked) settingsLoadRetryButton.hidden = true;
}
setSettingsLoadLocked(true);
settingsLoadRetryButton.addEventListener("click", () => void loadSettings(true));

async function loadSettings(reconcile = false): Promise<void> {
  const gen = loadGeneration;
  const s = session();
  const settingsSiteId = inventorySellingSiteId();
  if (f3SellingUi.pendingSettings?.session === s) {
    const pending = f3SellingUi.pendingSettings;
    settingsLoadRetryButton.hidden = false;
    if (!reconcile || pending.siteId !== settingsSiteId) {
      settingsSaveStatusMoneyEl.textContent = "Previous save unconfirmed. Retry unchanged, or reload current settings before reviewing a new save.";
      return;
    }
    const current = await getSettings(s.apiBaseUrl, s.tenantId, settingsSiteId);
    const shelves = pending.body.localShelfUpdates?.length
      ? await getLocalSellingShelves(s.apiBaseUrl, s.tenantId, settingsSiteId)
      : undefined;
    if (!loadIsCurrent(gen) || sessionOrUndefined() !== s || inventorySellingSiteId() !== settingsSiteId || f3SellingUi.pendingSettings !== pending) return;
    const context = current.body.sellingContext;
    if (current.status !== 200 || !context || context.locationId !== (settingsSiteId ?? null) ||
        (shelves !== undefined && (shelves.status !== 200 || shelves.body.sellingContext?.locationId !== context.locationId || shelves.body.sellingContext.readiness.version !== context.readiness.version || pending.body.localShelfUpdates?.some(update => !shelves.body.shelves.some(tier => tier.value === update.shelfTier && tier.selling !== undefined))))) {
      settingsSaveStatusMoneyEl.textContent = "Could not reconcile current settings. Your edits and original request are retained. Retry unchanged or reload again.";
      return;
    }
    (f3SellingUi.reconciledSettings ??= []).push(pending);
    delete f3SellingUi.pendingSettings;
    const previous = f3SellingUi.settings;
    f3SellingUi.settings = {
      siteId: settingsSiteId, context,
      shelves: shelves?.status === 200 ? new Map(shelves.body.shelves.flatMap(tier => tier.selling === undefined ? [] : [[tier.value, tier.selling] as const])) : new Map(previous?.shelves),
      requestIds: new Map(),
    };
    settingsSaveStatusMoneyEl.textContent = "Current settings reloaded. Your edits are retained. Review them before saving a new request. The previous save outcome remains unconfirmed.";
    settingsLoadRetryButton.hidden = true;
    return;
  }
  clearSettingsSaveStatuses();
  setSettingsLoadLocked(true);
  applyReasonAddGating();
  const result = await getSettings(s.apiBaseUrl, s.tenantId, settingsSiteId);
  // SET16-F4: this session's teardown ran while the fetch above was in
  // flight; discard rather than write the previous employee's tenant PIN
  // policy, Money/pricing scalars and business-day fields back into the
  // DOM a teardown just cleared. The five loaders fired below each carry
  // their own identical guard, so guarding only this await here is
  // sufficient for THIS function's own body -- see each of their own
  // SET16-F4 comments for their half.
  if (!loadIsCurrent(gen) || !adminReadSessionIsCurrent(s)) return;
  if (result.status !== 200) {
    adminToast(
      `Could not load location settings. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`,
      "danger",
    );
    // TESTDEBT8-F1: everything stays locked, so offer the retry.
    settingsLoadRetryButton.hidden = false;
    return;
  }
  const context = result.body.sellingContext;
  if (context !== undefined) {
    await loadLocalSellingShelves(settingsSiteId, gen, s);
    if (
      !loadIsCurrent(gen) ||
      !adminReadSessionIsCurrent(s) ||
      settingsSiteId !== inventorySellingSiteId()
    )
      return;
    f3SellingUi.settings = {
      siteId: settingsSiteId,
      context,
      shelves: new Map(
        f3SellingUi.shelvesContext?.locationId === context.locationId ? f3SellingUi.shelves : [],
      ),
      requestIds: new Map(),
    };
  } else delete f3SellingUi.settings;
  // SET15: set only on success; see loadDrinks's own comment above.
  settingsLoaded = true;
  const settings = result.body.settings;
  settingsTaxRateBasisPoints = settings.taxRateBasisPoints;
  settingsTipPresets = [...settings.tipPresetsBasisPoints];
  settingsShelfTiers = settings.shelfTiers.map((tier) => ({ ...tier }));
  settingsHoldAmountCents = settings.holdAmountCents;
  settingsHoldIncrementThresholdBasisPoints = settings.holdIncrementThresholdBasisPoints;
  fillDiscountApprovalSelect(
    settingsDiscountApprovalPercentSelect,
    "percent",
    settings.discountApprovalPercentBasisPoints,
  );
  fillDiscountApprovalSelect(
    settingsDiscountApprovalAmountSelect,
    "dollars",
    settings.discountApprovalAmountCents,
  );
  const canEditDiscountApproval = canManageLocationSettingsLists();
  for (const control of [
    settingsDiscountApprovalPercentSelect,
    settingsDiscountApprovalAmountSelect,
    settingsDiscountApprovalSaveButton,
  ]) {
    control.disabled = !canEditDiscountApproval;
  }
  settingsAbandonedTabGratuityBasisPoints = settings.abandonedTabGratuityBasisPoints;
  settingsAbandonedTabGratuityDisclosureConfirmed =
    settings.abandonedTabGratuityDisclosureConfirmed;
  settingsCloseCardTicketsAtDayEnd = settings.closeCardTicketsAtDayEnd;
  settingsDualPricingEnabled = settings.dualPricingEnabled;
  settingsDualPricingBasisPoints = settings.dualPricingBasisPoints;
  settingsTipOnPreDiscountPrice = settings.tipOnPreDiscountPrice;
  settingsRoundingMode = (settings.roundingMode as RoundingMode) ?? "half-up-cent";
  settingsPricingModel = (settings.pricingModel as PricingModel) ?? "per_ounce";
  // INV27-F2 (DEC-253): relabels/reorders the item editor's Pricing tab
  // fields for the model just loaded, so an item editor opened before
  // Settings has ever been visited still shows the correct label.
  applyBottlePriceLabelsForPricingModel();
  renderBottleCalculator();
  settingsStandardPourOunces = settings.standardPourOunces;
  // Operations group (SET1). Always present once bootstrapped
  // (getEffectiveTenantSettings never returns a partial row). Written
  // straight to the DOM, same as every other field on this form; there
  // is no separate module-level copy to keep in sync (saveSettingsForm
  // re-reads these three straight from the inputs, exactly like tax rate
  // and the rest already do).
  settingsSessionInactivityInput.value = String(settings.sessionInactivityMinutes);
  settingsPinMinLengthInput.value = String(settings.pinMinLength);
  settingsPinMaxLengthInput.value = String(settings.pinMaxLength);
  settingsPinLockoutAttemptsInput.value = String(settings.pinLockoutAttempts);
  settingsPinLockoutMinutesInput.value = String(settings.pinLockoutMinutes);
  // CL6-F1-F3: also draws the warning, the rail tag and the Confirm state.
  applyBusinessDayFromSettings(settings);
  const addressRead = await getLocationAddresses(s.apiBaseUrl, s.tenantId);
  if (!loadIsCurrent(gen)) return;
  if (addressRead.status !== 200) {
    adminToast(
      `Could not load the address. ${capitalizeServerMessage(addressRead.body.message ?? "Check your connection.")}`,
      "danger",
    );
    settingsLoadRetryButton.hidden = false;
    return;
  }
  applyAddressLocations(addressRead.body.locations, addressRead.body.signedInLocationId);
  renderSettings();
  void loadLocationSettingsPanel(s);
  // TAX2-S1-F1: the Sales tax section. A session that may change the rate reads the parts and the
  // history; any other session is shown the combined rate from this read and never calls the route.
  void taxRateSection.refresh();
  // SET14: both domains' own committed snapshots, taken immediately
  // after this successful load writes their fields, so a later edit
  // that never reaches Save can be detected and, on Discard, restored
  // without a second network round trip.
  settingsFormSnapshot = captureSettingsFormSnapshot();
  pinPolicySnapshot = capturePinPolicySnapshot();
  // UX108: what an earlier page was typing, once, on the first load of this page.
  restoreSettingsDrafts();
  // TESTDEBT8-F1: after the draft, so the person's own typing resumes on top of it.
  setSettingsLoadLocked(false);
  // PAY1 Slice 3: this location's card payments state (hidden unless the server answers).
  void cardPaymentsSection.refresh();
  // TERM8-F1: this location's reader tip choices.
  void readerTipsSection.refresh();
  // ID1: this location's ID Verified switch.
  void idVerifySection.refresh();
  void receiptSettingSection.refresh();
  // CASH1-S2-U1: this location's Cash switch.
  void cashSettingSection.refresh();
  // ACCTEXPORT1 and PAYEXPORT1: which outside connections are on, and the Connections rail item with them.
  void refreshConnections(session());
  // VEN5: no longer prefetched here. This fired unconditionally, racing
  // Purchasing's own permission-gated, latch-gated call (showSection),
  // and was reachable by any session holding admin.tenant_settings.view
  // regardless of inv.vendor.view -- an actor with the former but not
  // the latter still triggered a vendor fetch the server refuses.
  // Purchasing's own call is the single owner now; the Bottles
  // vendor-offer picker gets its own ensureVendorsLoaded() below, and
  // both share the vendorsLoaded latch so neither can double-fetch the
  // other.
  void loadVoidReasons();
  void loadDiscounts();
  void loadDiscountReasons();
  void loadPriceChangeReasons();
  for (const editor of actionReasonEditors) {
    void loadActionReasons(editor);
  }
  void loadRefundReasons();
}

// ---------------------------------------------------------------------
// INV4 (docs/backlog.md, owner ruling 2026-09-03, verbatim: "Part of the
// inventory feature is also Vendor Management as we have to be able to
// assign an inventory item to a vendor we purchase from."). Settings >
// Purchasing > Vendors: create, edit every field, and retire (never
// delete) a vendor record. Vendor OFFERS (per-item assignment) live on
// the Inventory item editor itself, further below.
//
// Retire-never-delete, the SAME shape void reasons/discounts use above:
// no delete route exists, and a retired vendor's own row and its past
// offers stay readable. Unlike void reasons, deactivate has its own
// dedicated route (deactivateVendor) rather than a plain PATCH
// active:false, matching src/domain/vendors.ts's own shape; reactivating
// goes back through the ordinary PATCH (updateVendor with active: true).
//
// This is an ADMIN surface (a keyboard and pointer are assumed), so every
// free-text field here (name, account reference, notes, contact name/
// email/phone) is a legitimate text input. Delivery days are the one
// exception CLAUDE.md's data-quality rule actually requires a picker for:
// a small, universal, FIXED vocabulary (the seven days of the week),
// never free text, so it renders as seven toggle chips rather than a
// text field.
// ---------------------------------------------------------------------

let settingsVendors: VendorDto[] = [];

const DELIVERY_DAY_OPTIONS: readonly { value: string; label: string }[] = [
  { value: "monday", label: "Mon" },
  { value: "tuesday", label: "Tue" },
  { value: "wednesday", label: "Wed" },
  { value: "thursday", label: "Thu" },
  { value: "friday", label: "Fri" },
  { value: "saturday", label: "Sat" },
  { value: "sunday", label: "Sun" },
];

// VEN7-F1: the shared vendor status line names the day it could not
// save (design/mockups/ven7-f1-vendor-save-error-persistence.html,
// artboard 2, "Could not save Tuesday for Acme Beverage."), spelled out
// rather than the chip's own three-letter abbreviation.
const DELIVERY_DAY_FULL_LABEL: Record<string, string> = {
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
  sunday: "Sunday",
};

// VEN4: hides the add-vendor input and button for a session that lacks
// inv.vendor.manage. Called on every entry to Purchasing (showSection),
// not only after a load, since it depends on nothing but the current
// session's own permission snapshot.
function applyVendorAddControlGating(): void {
  const manageAllowed = purchasingVendorsManageAllowed(session());
  settingsVendorNameInput.hidden = !manageAllowed;
  settingsVendorAddButton.hidden = !manageAllowed;
}

// PO2, owner ruling 2026-09-29 (DEC-247). Read-only, not hidden, for a
// viewer who holds purchasing.order.view but not purchasing.approval_
// threshold.manage: the field and its current value stay on screen (the
// ruling's own point is that this is visible information about how the
// Purchase orders flow behaves), only the write affordance is withdrawn.
function applyPurchasingApprovalThresholdGating(): void {
  const manageAllowed = purchasingApprovalThresholdManageAllowed(session());
  purchasingApprovalThresholdInput.disabled = !manageAllowed;
  purchasingApprovalThresholdSaveButton.hidden = !manageAllowed;
}

async function loadPurchaseApprovalThreshold(): Promise<void> {
  const gen = loadGeneration;
  const s = session();
  const result = await getPurchaseApprovalThreshold(s.apiBaseUrl, s.tenantId);
  if (!loadIsCurrent(gen)) return;
  if (result.status !== 200) {
    purchasingApprovalThresholdStatusEl.textContent = `Could not load the order approval threshold. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`;
    return;
  }
  purchasingApprovalThresholdLoaded = true;
  purchasingApprovalThresholdStatusEl.textContent = "";
  purchasingApprovalThresholdInput.value = centsToAmountString(result.body.thresholdCents);
}

async function handlePurchasingApprovalThresholdSave(): Promise<void> {
  const raw = purchasingApprovalThresholdInput.value.trim();
  let thresholdCents: number;
  try {
    thresholdCents = parseDollarsToCentsExact(raw, "Approval required at or above");
  } catch (error) {
    purchasingApprovalThresholdStatusEl.textContent =
      error instanceof Error ? error.message : String(error);
    return;
  }
  const s = session();
  const result = await updatePurchaseApprovalThreshold(s.apiBaseUrl, s.tenantId, thresholdCents);
  if (result.status !== 200) {
    purchasingApprovalThresholdStatusEl.textContent = `Could not save this change. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`;
    return;
  }
  purchasingApprovalThresholdInput.value = centsToAmountString(result.body.thresholdCents);
  purchasingApprovalThresholdStatusEl.textContent = "Saved. Takes effect on the next order placed.";
  adminToast("Saved the order approval threshold.", "success");
}

async function loadVendors(recoverCreation = false): Promise<void> {
  const gen = loadGeneration;
  const sequence = ++vendorReadSequence;
  const revision = vendorCreateRevision;
  if (vendorCreatePending) {
    vendorCreateRecoveryNeeded = !vendorsLoaded;
    return;
  }
  const s = session();
  // UX80: resolves its own route's permission rather than inheriting a section gate.
  if (!hasPermission(s, "inv.vendor.view")) return;
  // activeOnly:false: an Admin needs to see a retired vendor in order to
  // turn it back on, and its past offers stay attributed to a real name
  // rather than disappearing from the list entirely.
  const result = await listVendors(s.apiBaseUrl, s.tenantId, { activeOnly: false });
  // SET16-F4: this session's teardown ran while the fetch above was in
  // flight; discard rather than write over what invalidate() already
  // cleared.
  if (!loadIsCurrent(gen) || sequence !== vendorReadSequence ||
    revision !== vendorCreateRevision || vendorCreatePending) return;
  if (result.status !== 200) {
    if (!recoverCreation || settingsVendorStatusEl.textContent === "") settingsVendorStatusEl.textContent = `Could not load vendors. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`;
    return;
  }
  // SET15: set only on success; see loadDrinks's own comment in showSection.
  vendorsLoaded = true;
  if (!recoverCreation) settingsVendorStatusEl.textContent = "";
  if (recoverCreation) {
    // Retain objects captured by existing edit listeners and all authored fields.
    const existingIds = new Set(settingsVendors.map((vendor) => vendor.id));
    settingsVendors.push(...result.body.vendors.filter((vendor) => !existingIds.has(vendor.id)));
  } else {
    settingsVendors = result.body.vendors;
  }
  // INVLOC1 Slice 5: whether to offer "Account number by location" on each vendor card (only with more than one location).
  vendorPlanningLocationCount = result.body.planningLocationCount ?? 0;
  renderVendorList(recoverCreation);
  if (recoverCreation) populateBottleVendorOfferVendorSelect();
}

// VEN5: the Bottles vendor-offer picker's own load, the second explicit
// consumer of settingsVendors alongside Purchasing's own call in
// showSection above. Before this, the ONLY thing that ever populated
// settingsVendors was an unconditional prefetch inside loadSettings,
// gated on admin.tenant_settings.view rather than inv.vendor.view; an
// actor who opened a bottle's vendor offers without ever visiting
// Purchasing first, or who held tenant-settings view without vendor
// view, either got a stale fetch the server refused or a picker with
// nothing in it. This mirrors ensureShelfTierSettingsLoaded's own shape
// exactly, including SET18's lesson: resolve the permission the route
// itself requires BEFORE issuing the request, not after a refusal.
// Shares the vendorsLoaded latch with loadVendors above, so Purchasing
// and Bottles cannot double-fetch each other -- each is simply a no-op
// once the other has already loaded it successfully.
async function ensureVendorsLoaded(): Promise<void> {
  if (vendorsLoaded) {
    return;
  }
  const s = session();
  if (!hasPermission(s, "inv.vendor.view")) {
    return;
  }
  await loadVendors();
}

// VEN5: the Bottles item editor's own entry point into the load above,
// called fire-and-forget from openEditBottleForm so opening the form
// itself never waits on a network round trip. populateBottleVendor-
// OfferVendorSelect re-renders honestly whether or not the load actually
// ran (see its own permission check).
//
// INV27 coordinator review, 2026-09-29: openEditBottleForm fires this
// function and loadBottleVendorOffers (the item's own EXISTING offers)
// fire-and-forget IN PARALLEL. When the offers land first, renderBottle-
// VendorOfferList's own vendorNameById lookup ran against a still-empty
// settingsVendors and rendered "(unknown vendor)" for every offer's own
// summary text -- the normal flow for a manager opening Settings >
// Inventory > Edit item directly, without visiting Purchasing first, a
// real shipped defect (not merely a picker-population gap: the ADD-offer
// picker was already covered by this file's other tests, the rendered
// summary of an EXISTING offer was not). Re-rendering the offer list
// here, once the SAME already-in-flight vendor fetch this function
// already owns resolves, fixes it with no second fetcher: still exactly
// one vendor GET per screen, per VEN5's own single-owner rule
// (e2e/ven5-one-owner-for-the-vendor-fetch.spec.ts). Harmless to call
// unconditionally: renderBottleVendorOfferList reads module-level
// bottleVendorOffers (empty until loadBottleVendorOffers itself resolves,
// in which case this repaints "No vendor offers yet." and
// loadBottleVendorOffers repaints again moments later with the real
// list, already vendor-name-resolved either way).
async function refreshBottleVendorOfferPicker(): Promise<void> {
  await ensureVendorsLoaded();
  populateBottleVendorOfferVendorSelect();
  renderBottleVendorOfferList();
}

/** UX66 part C, design review 2026-09-29: renderVendorList replaces
 * every row's own DOM node, including whichever one currently holds
 * focus. RT3's background permission-snapshot refresh (main.ts's
 * refreshPermissionSnapshotOnNavigation, fired on every screen
 * navigation including showAdminScreen's own) can land and trigger this
 * exact rebuild moments after a caller focuses a field in this same
 * list -- found live by openVendorContactEditor's own "Add a contact"
 * fix link, whose scrollIntoView-and-focus was silently losing the
 * focus it had just set. Captured by test id (stable across a rebuild)
 * plus the row's own vendor id, never by DOM node identity, since the
 * node this call captures is about to be destroyed. */
function captureVendorRowFocus(): { vendorId: string; testId: string } | null {
  const active = document.activeElement;
  if (!(active instanceof HTMLElement) || !settingsVendorsEl.contains(active)) {
    return null;
  }
  const row = active.closest<HTMLElement>('[data-testid="admin-vendor-row"]');
  const vendorId = row?.dataset["vendorId"];
  const testId = active.dataset["testid"];
  if (vendorId === undefined || testId === undefined) {
    return null;
  }
  return { vendorId, testId };
}

/** Restores what captureVendorRowFocus captured, once the rebuilt row
 * for the same vendor id exists again. A vendor removed mid-rebuild (or
 * a field whose own write gate hid it) simply finds nothing and leaves
 * focus wherever the browser already put it, the same as any other
 * disappeared focus target. */
function restoreVendorRowFocus(captured: { vendorId: string; testId: string } | null): void {
  if (captured === null) {
    return;
  }
  const row = settingsVendorsEl.querySelector<HTMLElement>(
    `[data-vendor-id="${captured.vendorId}"]`,
  );
  const field = row?.querySelector<HTMLElement>(`[data-testid="${captured.testId}"]`);
  field?.focus();
}

function buildVendorLocationAccountControl(vendor: VendorDto, manageAllowed: boolean): HTMLElement {
  return buildVendorLocationAccounts({
    vendorId: vendor.id,
    vendorName: vendor.name,
    canManage: manageAllowed,
    load: async () => {
      const s = session();
      const result = await getVendorLocationAccounts(s.apiBaseUrl, s.tenantId, vendor.id);
      return result.status === 200
        ? { ok: true, accounts: result.body.accounts }
        : {
            ok: false,
            message: capitalizeServerMessage(result.body.message ?? "Check your connection."),
          };
    },
    save: async (entries) => {
      const s = session();
      const result = await putVendorLocationAccounts(
        s.apiBaseUrl,
        s.tenantId,
        vendor.id,
        entries,
      );
      return result.status === 200
        ? { ok: true, accounts: result.body.accounts }
        : {
            ok: false,
            message: capitalizeServerMessage(result.body.message ?? "Check your connection."),
          };
    },
  });
}

function renderVendorList(preserveExistingRows = false): void {
  const focused = captureVendorRowFocus();
  const existingRows = new Map<string, HTMLElement>();
  if (preserveExistingRows) {
    for (const row of settingsVendorsEl.querySelectorAll<HTMLElement>("[data-vendor-id]")) {
      existingRows.set(row.dataset["vendorId"]!, row);
    }
    settingsVendorsEl.querySelector(":scope > .tabs-empty")?.remove();
  } else {
    settingsVendorsEl.innerHTML = "";
  }
  if (settingsVendors.length === 0) {
    settingsVendorsEl.innerHTML = '<p class="tabs-empty">No vendors yet.</p>';
    return;
  }
  // VEN4: view (purchasingVendorsVisible) decides whether this list
  // renders at all; manage decides whether the row it renders is
  // writable. Read once per render rather than per field so every write
  // affordance in a row -- the nine fields below, the delivery-day
  // chips, and the active switch -- agrees with the SAME session
  // snapshot, and re-evaluated on every render (including the
  // every-Purchasing-entry re-render showSection triggers) so a manage
  // downgrade mid-visit (RT3, DEC-038) reaches rows already on screen.
  const manageAllowed = purchasingVendorsManageAllowed(session());
  const sortedVendors = sortByName(settingsVendors, (listed) => listed.name);
  const vendorsHavePace = settingsVendors.some((listed) => listed.order_pace_days !== null);
  for (const [index, vendor] of sortedVendors.entries()) {
    const existingRow = existingRows.get(vendor.id);
    if (existingRow !== undefined) {
      const accounts = existingRow.querySelector<HTMLElement>("[data-vendor-location-accounts]");
      if (accounts !== null && accounts.childElementCount === 0 &&
        locationCountShowsChoices(vendorPlanningLocationCount)) {
        accounts.append(buildVendorLocationAccountControl(vendor, manageAllowed));
        accounts.hidden = false;
      }
      continue;
    }
    const row = document.createElement("li");
    row.className = "admin-vendor-row";
    row.dataset["testid"] = "admin-vendor-row";
    row.dataset["vendorId"] = vendor.id;
    // UX120: one summary button per vendor and one editor under it. Closed editors stay in the page,
    // hidden, so focus capture, the price list load and every field id behave as before.
    const expanded = expandedVendorId === vendor.id;
    row.dataset["expanded"] = expanded ? "true" : "false";
    const editor = document.createElement("div");
    editor.className = "admin-vendor-editor";
    editor.dataset["testid"] = "admin-vendor-editor";
    editor.id = `admin-vendor-${vendor.id}-editor`;
    editor.hidden = !expanded;
    const head = document.createElement("div");
    head.className = "admin-vendor-head";
    const ordering = buildVendorGroup("Ordering");
    const contact = buildVendorGroup("Contact");
    const notesGroup = buildVendorGroup("Notes");

    // settings-described-exempt: vendor record field (data about one vendor, not a setting)
    const nameInput = document.createElement("input");
    nameInput.type = "text";
    nameInput.className = "field-input admin-vendor-name";
    nameInput.dataset["testid"] = "admin-vendor-name";
    nameInput.value = vendor.name;
    nameInput.setAttribute("value", vendor.name);
    nameInput.maxLength = 120;
    nameInput.setAttribute("aria-label", `Name for ${vendor.name}`);
    nameInput.id = `admin-vendor-${vendor.id}-name`;
    // VEN4: a field that accepts typing and then silently discards it on
    // blur is worse than one that never accepted it (see
    // handleVendorFieldCommit's own no-op-on-unchanged-value path).
    nameInput.disabled = !manageAllowed;
    nameInput.addEventListener(
      "blur",
      () => void handleVendorFieldCommit(vendor.id, "name", nameInput),
    );
    // Name and activation stay independent of ordering preferences.
    head.append(vendorField("Vendor name", nameInput.id, nameInput, "admin-vendor-field--name"));
    ordering.wide.append(buildVendorOrderingFields(vendor, manageAllowed));
    // Price imports use this vendor's configured format and the import permission.
    const priceListEntry = buildPriceListCardEntry(session(), vendor);
    const priceGroup = buildVendorGroup("Price list");
    if (priceListEntry !== null) {
      priceGroup.wide.append(priceListEntry);
    }

    // settings-described-exempt: vendor record field (data about one vendor, not a setting)
    const accountInput = document.createElement("input");
    accountInput.type = "text";
    accountInput.className = "field-input admin-vendor-account-reference";
    // An account number is a code: no forced capitals, nothing remembered by the browser.
    accountInput.inputMode = "text";
    accountInput.setAttribute("autocapitalize", "off");
    accountInput.autocomplete = "off";
    accountInput.dataset["testid"] = "admin-vendor-account-reference";
    // A person without inv.vendor.manage is told a number is on file, never what it is (lead ruling 2026-10-09).
    accountInput.placeholder =
      vendor.account_reference === null && vendor.has_account_reference === true
        ? "On file"
        : "Account reference";
    accountInput.value = vendor.account_reference ?? "";
    accountInput.setAttribute("aria-label", `Account reference for ${vendor.name}`);
    accountInput.id = `admin-vendor-${vendor.id}-account-reference`;
    accountInput.disabled = !manageAllowed;
    accountInput.addEventListener(
      "blur",
      () => void handleVendorFieldCommit(vendor.id, "accountReference", accountInput),
    );
    ordering.grid.append(vendorField("Account reference", accountInput.id, accountInput));
    // INVLOC1 Slice 5: the account number each location holds with this vendor (the box above stays the vendor-wide one).
    const locationAccounts = document.createElement("div");
    locationAccounts.dataset["vendorLocationAccounts"] = "";
    locationAccounts.hidden = true;
    ordering.wide.append(locationAccounts);
    if (locationCountShowsChoices(vendorPlanningLocationCount)) {
      locationAccounts.append(buildVendorLocationAccountControl(vendor, manageAllowed));
      locationAccounts.hidden = false;
    }

    // VEN2 (docs/build-briefs/VEN2-vendor-contact-fields.md): where a
    // manager places an order with this vendor outside the product. A
    // vendor-level reference fact, the same class as account reference
    // above, not a contact person's own detail, so it sits before the
    // contact group rather than inside it. Accepts a bare host (see
    // src/domain/vendors.ts's validateOrderWebsiteUrl); inputMode "url"
    // only tailors the on-screen keyboard, it enforces nothing.
    // settings-described-exempt: vendor record field (data about one vendor, not a setting)
    const orderWebsiteInput = document.createElement("input");
    orderWebsiteInput.type = "text";
    orderWebsiteInput.inputMode = "url";
    orderWebsiteInput.className = "field-input admin-vendor-order-website";
    orderWebsiteInput.dataset["testid"] = "admin-vendor-order-website";
    orderWebsiteInput.placeholder = "Order website (e.g. acmebeverage.com)";
    orderWebsiteInput.value = vendor.order_website_url ?? "";
    orderWebsiteInput.setAttribute("aria-label", `Order website for ${vendor.name}`);
    orderWebsiteInput.id = `admin-vendor-${vendor.id}-order-website`;
    orderWebsiteInput.disabled = !manageAllowed;
    orderWebsiteInput.addEventListener(
      "blur",
      () => void handleVendorFieldCommit(vendor.id, "orderWebsiteUrl", orderWebsiteInput),
    );
    ordering.grid.append(vendorField("Order website", orderWebsiteInput.id, orderWebsiteInput));

    // settings-described-exempt: vendor record field (data about one vendor, not a setting)
    const contactNameInput = document.createElement("input");
    contactNameInput.type = "text";
    contactNameInput.className = "field-input admin-vendor-contact-name";
    contactNameInput.dataset["testid"] = "admin-vendor-contact-name";
    contactNameInput.placeholder = "Contact name";
    contactNameInput.value = vendor.contact_name ?? "";
    contactNameInput.setAttribute("aria-label", `Contact name for ${vendor.name}`);
    contactNameInput.id = `admin-vendor-${vendor.id}-contact-name`;
    contactNameInput.disabled = !manageAllowed;
    contactNameInput.addEventListener(
      "blur",
      () => void handleVendorFieldCommit(vendor.id, "contactName", contactNameInput),
    );
    contact.grid.append(vendorField("Contact name", contactNameInput.id, contactNameInput));

    // settings-described-exempt: vendor record field (data about one vendor, not a setting)
    const contactEmailInput = document.createElement("input");
    contactEmailInput.type = "email";
    contactEmailInput.className = "field-input admin-vendor-contact-email";
    contactEmailInput.dataset["testid"] = "admin-vendor-contact-email";
    contactEmailInput.placeholder = "Contact email";
    contactEmailInput.value = vendor.contact_email ?? "";
    contactEmailInput.setAttribute("aria-label", `Contact email for ${vendor.name}`);
    contactEmailInput.id = `admin-vendor-${vendor.id}-contact-email`;
    contactEmailInput.disabled = !manageAllowed;
    contactEmailInput.addEventListener(
      "blur",
      () => void handleVendorFieldCommit(vendor.id, "contactEmail", contactEmailInput),
    );
    contact.grid.append(vendorField("Contact email", contactEmailInput.id, contactEmailInput));

    // settings-described-exempt: vendor record field (data about one vendor, not a setting)
    const contactPhoneInput = document.createElement("input");
    contactPhoneInput.type = "tel";
    contactPhoneInput.className = "field-input admin-vendor-contact-phone";
    contactPhoneInput.dataset["testid"] = "admin-vendor-contact-phone";
    // VEN2: relabeled from "Reference phone". The stored field
    // (contact_phone) and its position in this row, directly beside
    // contact name and contact email, were already the contact person's
    // own phone number; only the displayed wording obscured that.
    contactPhoneInput.placeholder = "Contact phone";
    contactPhoneInput.value = vendor.contact_phone ?? "";
    contactPhoneInput.setAttribute("aria-label", `Contact phone for ${vendor.name}`);
    contactPhoneInput.id = `admin-vendor-${vendor.id}-contact-phone`;
    contactPhoneInput.disabled = !manageAllowed;
    contactPhoneInput.addEventListener(
      "blur",
      () => void handleVendorFieldCommit(vendor.id, "contactPhone", contactPhoneInput),
    );
    contact.grid.append(vendorField("Contact phone", contactPhoneInput.id, contactPhoneInput));

    // VEN2: this field previously carried no visible label at all, only
    // an aria-hidden "$" affix, which the product owner reported as
    // unreadable ("I have no idea what that field is for"). "Minimum
    // order warning" is P0.12's own name for this fact (see the vendors
    // criterion in docs/product-requirements.md); reused here rather
    // than inventing new wording.
    const minimumOrderId = `admin-vendor-${vendor.id}-minimum-order`;
    const minimumOrderWrap = document.createElement("span");
    minimumOrderWrap.className = "field-input-affix field-input-affix--prefix";
    const minimumOrderMark = document.createElement("span");
    minimumOrderMark.className = "field-input-affix-mark";
    minimumOrderMark.setAttribute("aria-hidden", "true");
    minimumOrderMark.textContent = "$";
    const minimumOrderInput = document.createElement("input");
    minimumOrderInput.type = "number";
    minimumOrderInput.inputMode = "decimal";
    minimumOrderInput.min = "0";
    minimumOrderInput.step = "0.01";
    minimumOrderInput.className = "field-input admin-vendor-minimum-order";
    minimumOrderInput.dataset["testid"] = "admin-vendor-minimum-order";
    minimumOrderInput.id = minimumOrderId;
    minimumOrderInput.value =
      vendor.minimum_order_cents === null ? "" : centsToAmountString(vendor.minimum_order_cents);
    minimumOrderInput.setAttribute("aria-label", `Minimum order for ${vendor.name}`);
    minimumOrderInput.disabled = !manageAllowed;
    minimumOrderInput.addEventListener(
      "blur",
      () => void handleVendorMinimumOrderCommit(vendor.id, minimumOrderInput),
    );
    minimumOrderWrap.append(minimumOrderMark, minimumOrderInput);
    ordering.grid.append(vendorField("Minimum order warning", minimumOrderId, minimumOrderWrap));

    const cutoffInput = document.createElement("input");

    cutoffInput.type = "time";
    cutoffInput.className = "field-input admin-vendor-cutoff-time";
    cutoffInput.dataset["testid"] = "admin-vendor-cutoff-time";
    cutoffInput.value = vendor.order_cutoff_time ?? "";
    cutoffInput.setAttribute("aria-label", `Order cutoff time for ${vendor.name}`);
    cutoffInput.id = `admin-vendor-${vendor.id}-cutoff-time`;
    cutoffInput.disabled = !manageAllowed;
    cutoffInput.addEventListener(
      "change",
      () => void handleVendorFieldCommit(vendor.id, "orderCutoffTime", cutoffInput),
    );
    ordering.grid.append(vendorField("Order cutoff", cutoffInput.id, cutoffInput));

    const deliveryDaysWrap = document.createElement("div");
    deliveryDaysWrap.className = "admin-chip-select";
    deliveryDaysWrap.dataset["testid"] = "admin-vendor-delivery-days";
    const currentDays = new Set(vendor.delivery_days ?? []);
    for (const day of DELIVERY_DAY_OPTIONS) {
      const chip = document.createElement("button");
      chip.type = "button";
      chip.className = "admin-chip";
      chip.dataset["testid"] = "admin-vendor-delivery-day-chip";
      chip.dataset["day"] = day.value;
      chip.textContent = day.label;
      chip.setAttribute("aria-pressed", currentDays.has(day.value) ? "true" : "false");
      chip.classList.toggle("admin-chip--selected", currentDays.has(day.value));
      // VEN4: same write gate as the fields above -- the server route
      // this chip drives (PATCH .../vendors/:id) requires
      // inv.vendor.manage exactly like the rest of this row.
      chip.disabled = !manageAllowed;
      chip.addEventListener(
        "click",
        () => void handleVendorDeliveryDayToggle(vendor.id, day.value),
      );
      deliveryDaysWrap.append(chip);
    }
    ordering.wide.append(buildVendorFieldLabel("Delivery days"), deliveryDaysWrap);
    // AUTOPAR1: the order pace of this vendor (Change pace) and the way to its items' par levels.
    ordering.wide.append(buildVendorPaceBlock(vendor, manageAllowed));

    // settings-described-exempt: vendor record field (data about one vendor, not a setting)
    const notesInput = document.createElement("textarea");
    notesInput.className = "field-input admin-vendor-notes";
    notesInput.dataset["testid"] = "admin-vendor-notes";
    notesInput.placeholder = "Internal notes";
    notesInput.rows = 2;
    notesInput.value = vendor.notes ?? "";
    notesInput.setAttribute("aria-label", `Internal notes for ${vendor.name}`);
    notesInput.id = `admin-vendor-${vendor.id}-notes`;
    notesInput.disabled = !manageAllowed;
    notesInput.addEventListener(
      "blur",
      () => void handleVendorFieldCommit(vendor.id, "notes", notesInput),
    );
    notesGroup.wide.append(buildVendorFieldLabel("Internal notes", notesInput.id), notesInput);

    // DEC-052: the same switch and confirm-before-deactivating flow as
    // every other reversible activate/deactivate control in Settings.
    // Confirm copy reuses this list's own existing consequence wording
    // (handleVendorToggle's prior success toast said the same thing).
    const vendorActivationSwitch = renderActivationSwitch({
      active: vendor.active,
      entityLabel: vendor.name,
      toggleTestId: "admin-vendor-toggle",
      statusTestId: "admin-vendor-status-text",
      activeText: "On",
      inactiveText: "Off",
      confirmDeactivate: (name) => ({
        title: `Turn off ${name}?`,
        message: `Past offers stay readable, but no new offer can be assigned to ${name} once it's off.`,
        confirmLabel: "Turn off",
      }),
      write: async (nextActive) => {
        const s = session();
        // deactivateVendor is its OWN dedicated route (src/domain/
        // vendors.ts), not a plain PATCH active:false, matching that
        // module's own shape; turning a retired vendor back ON goes
        // through the ordinary PATCH.
        const result = nextActive
          ? await updateVendor(s.apiBaseUrl, s.tenantId, vendor.id, { active: true })
          : await deactivateVendor(s.apiBaseUrl, s.tenantId, vendor.id);
        if (result.status !== 200) {
          return {
            ok: false,
            message: result.body.message ?? "Check your connection.",
          };
        }
        vendor.active = result.body.vendor.active;
        return { ok: true, active: result.body.vendor.active };
      },
      onError: (message) => {
        // VEN7-F1: routed through the same sticky/Dismiss machinery as
        // every other write to this shared line, so a manager sees the
        // identical affordance no matter which vendor action failed.
        writeVendorStatusError(
          `Could not change this vendor. ${capitalizeServerMessage(message)}`,
          vendorStatusKey(vendor.id, "active"),
        );
      },
      onChange: (active) => {
        resolveVendorStatusSuccess(vendorStatusKey(vendor.id, "active"));
        adminToast(
          active
            ? `${vendor.name} is back on for new Vendor Options.`
            : `${vendor.name} is off. Past offers stay readable, but no new offer can be assigned to it.`,
          "success",
        );
        // A vendor's active state governs the offer picker's own
        // honest-merge behavior on the Inventory item editor; re-render
        // it if it happens to be open on this exact vendor.
        populateBottleVendorOfferVendorSelect();
        // UX120: the collapsed row's Off chip follows the switch.
        renderVendorList();
      },
    });
    if (!manageAllowed) {
      // VEN4: renderActivationSwitch's own config carries no disabled
      // option (it is the shared control every activate/deactivate
      // toggle in Settings builds through, per check:activation-control,
      // and this is the only row on this screen that needs one gated
      // read-only); disabling the checkbox it produced, rather than
      // widening that shared config for one caller, keeps the write path
      // itself unchanged for every other screen using it.
      const vendorToggleInput =
        vendorActivationSwitch.querySelector<HTMLInputElement>(".admin-toggle-input");
      if (vendorToggleInput !== null) {
        vendorToggleInput.disabled = true;
      }
    }
    // Label the vendor activation independently from ordering preferences.
    const vendorActiveRow = document.createElement("div");
    vendorActiveRow.className = "admin-vendor-active-row";
    const vendorActiveText = document.createElement("span");
    vendorActiveText.className = "admin-vendor-active-label";
    vendorActiveText.textContent = "Vendor active";
    vendorActiveRow.append(vendorActiveText, vendorActivationSwitch);
    vendorActivationSwitch
      .querySelector<HTMLInputElement>(".admin-toggle-input")
      ?.setAttribute("aria-label", `Vendor active, ${vendor.name}`);
    head.append(vendorActiveRow);

    editor.append(head, ordering.el, contact.el);
    if (priceListEntry !== null) {
      editor.append(priceGroup.el);
    }
    editor.append(notesGroup.el);
    row.append(buildVendorSummary(session(), vendor, editor.id, expanded, vendorsHavePace), editor);
    const nextExisting = sortedVendors.slice(index + 1)
      .map((listed) => existingRows.get(listed.id)).find((listed) => listed !== undefined);
    settingsVendorsEl.insertBefore(row, nextExisting ?? null);
  }
  if (!preserveExistingRows) restoreVendorRowFocus(focused);
}

// UX120: the open or closed state of the Settings, Vendors list. One vendor open at a time (provisional);
// reset on sign-out (invalidate) and on leaving Purchasing (showSection).
let expandedVendorId: string | undefined = undefined;

/** Opens one vendor's editor and closes the rest, or closes all when given undefined. Updates the rows in
 * place so a half-typed field in another row is not thrown away by a redraw. */
function setExpandedVendor(vendorId: string | undefined): void {
  expandedVendorId = vendorId;
  for (const listed of settingsVendorsEl.querySelectorAll<HTMLElement>(
    '[data-testid="admin-vendor-row"]',
  )) {
    const open = listed.dataset["vendorId"] === vendorId;
    listed.dataset["expanded"] = open ? "true" : "false";
    const editor = listed.querySelector<HTMLElement>('[data-testid="admin-vendor-editor"]');
    if (editor !== null) {
      editor.hidden = !open;
    }
    listed
      .querySelector<HTMLElement>('[data-testid="admin-vendor-summary"]')
      ?.setAttribute("aria-expanded", open ? "true" : "false");
  }
}

const VENDOR_CHEVRON_SVG =
  '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" ' +
  'stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<path d="M5 8l5 5 5-5"/></svg>';

/** AUTOPAR1: the "Order pace" block of an open vendor editor: what the pace is, Change pace, See par levels. */
function buildVendorPaceBlock(vendor: VendorDto, manageAllowed: boolean): HTMLElement {
  const block = document.createElement("div");
  block.className = "admin-vendor-pace";
  block.dataset["testid"] = "admin-vendor-pace";
  block.append(buildVendorFieldLabel("Order pace"));
  const row = document.createElement("div");
  row.className = "admin-vendor-pace-row";
  const chips = document.createElement("span");
  chips.className = "admin-vendor-summary-chips";
  const parts: string[] =
    vendor.order_pace_days === null
      ? ["No order pace yet"]
      : [
          everyLabel(vendor.order_pace_days),
          ...(vendor.order_pace_weekday === null
            ? []
            : [`Orders ${weekdayName(vendor.order_pace_weekday) ?? ""}`]),
          `Delivery ${vendor.lead_days} ${vendor.lead_days === 1 ? "day" : "days"}`,
        ];
  for (const text of parts) {
    const chip = document.createElement("span");
    chip.className = "admin-vendor-pace-chip admin-vendor-pace-chip--set";
    chip.dataset["testid"] = "admin-vendor-pace-detail-chip";
    chip.textContent = text;
    chips.append(chip);
  }
  row.append(chips);
  const actions = document.createElement("span");
  actions.className = "admin-vendor-pace-actions";
  const change = document.createElement("button");
  change.type = "button";
  change.className = "btn-secondary btn-compact";
  change.dataset["testid"] = "admin-vendor-pace-change";
  change.textContent = "Change pace";
  change.disabled = !manageAllowed;
  change.addEventListener("click", () =>
    autoParSection.openVendorPace(
      {
        id: vendor.id,
        name: vendor.name,
        paceDays: vendor.order_pace_days,
        orderWeekday: vendor.order_pace_weekday,
        leadDays: vendor.lead_days,
      },
      (saved) => {
        settingsVendors = settingsVendors.map((listed) =>
          listed.id === saved.id
            ? {
                ...listed,
                order_pace_days: saved.order_pace_days,
                order_pace_weekday: saved.order_pace_weekday,
                lead_days: saved.lead_days,
              }
            : listed,
        );
        renderVendorList();
      },
    ),
  );
  actions.append(change);
  if (hasPermission(session(), "inv.stock_state.view") && sectionVisible(session(), "inventory")) {
    const levels = document.createElement("button");
    levels.type = "button";
    levels.className = "btn-secondary btn-compact";
    levels.dataset["testid"] = "admin-vendor-see-par-levels";
    levels.textContent = "See par levels";
    levels.addEventListener("click", () => void openParLevelsFor(vendor.id));
    actions.append(levels);
  }
  row.append(actions);
  block.append(row);
  return block;
}

/** AUTOPAR1: Vendors, See par levels. Opens Inventory items on its Par levels tab for that vendor. */
async function openParLevelsFor(vendorId: string): Promise<void> {
  if (!(await showSection("inventory"))) return;
  await autoParSection.showLevels(vendorId);
}

/** Collapsed vendor row with its pace, active state and price update value. */
function buildVendorSummary(
  activeSession: Session,
  vendor: VendorDto,
  editorId: string,
  expanded: boolean,
  vendorsHavePace: boolean,
): HTMLElement {
  const summary = document.createElement("button");
  summary.type = "button";
  summary.className = "admin-vendor-summary";
  summary.dataset["testid"] = "admin-vendor-summary";
  summary.setAttribute("aria-expanded", expanded ? "true" : "false");
  summary.setAttribute("aria-controls", editorId);
  const name = document.createElement("span");
  name.className = "admin-vendor-summary-name";
  name.dataset["testid"] = "admin-vendor-summary-name";
  name.textContent = vendor.name;
  summary.append(name);
  const chips = document.createElement("span");
  chips.className = "admin-vendor-summary-chips";
  // AUTOPAR1: the order pace beside the other chips, or Pace N/A while none is set.
  // HIGFIX4B (F-39): "No pace" is the default, so it is drawn only when another vendor has a pace and this one does not.
  const paceChip = document.createElement("span");
  paceChip.className =
    vendor.order_pace_days === null
      ? "admin-vendor-off-chip admin-vendor-pace-chip"
      : "admin-vendor-pace-chip admin-vendor-pace-chip--set";
  paceChip.dataset["testid"] = "admin-vendor-pace-chip";
  paceChip.textContent =
    vendor.order_pace_days === null ? "No pace" : everyLabel(vendor.order_pace_days);
  if (vendor.order_pace_days !== null || vendorsHavePace) chips.append(paceChip);
  const paceWeekday = weekdayName(vendor.order_pace_weekday);
  if (paceWeekday !== null) {
    const dayChip = document.createElement("span");
    dayChip.className = "admin-vendor-pace-chip admin-vendor-pace-chip--day";
    dayChip.dataset["testid"] = "admin-vendor-pace-day-chip";
    dayChip.textContent = `Orders ${paceWeekday}`;
    chips.append(dayChip);
  }
  if (!vendor.active) {
    const off = document.createElement("span");
    off.className = "admin-vendor-off-chip";
    off.dataset["testid"] = "admin-vendor-off-chip";
    off.textContent = "Off";
    chips.append(off);
  }
  summary.append(chips);
  const prices = buildPriceListSummary(activeSession, vendor);
  if (prices !== null) {
    summary.append(prices);
  }
  const chevron = document.createElement("span");
  chevron.className = "admin-vendor-chevron";
  chevron.innerHTML = VENDOR_CHEVRON_SVG;
  summary.append(chevron);
  summary.addEventListener("click", () =>
    setExpandedVendor(expandedVendorId === vendor.id ? undefined : vendor.id),
  );
  return summary;
}

/** UX120: a titled group of fields in the open editor. `grid` is the two-column field grid, `wide`
 * holds full-width content (delivery days, location accounts, the price list, notes). */
function buildVendorGroup(title: string): {
  el: HTMLElement;
  grid: HTMLElement;
  wide: HTMLElement;
} {
  const el = document.createElement("div");
  el.className = "admin-vendor-group";
  el.setAttribute("role", "group");
  el.setAttribute("aria-label", title);
  const heading = document.createElement("span");
  heading.className = "admin-vendor-group-title";
  heading.textContent = title;
  const grid = document.createElement("div");
  grid.className = "admin-vendor-grid";
  const wide = document.createElement("div");
  wide.className = "admin-vendor-wide";
  el.append(heading, grid, wide);
  return { el, grid, wide };
}

/** UX120: one label over one control, a cell of the field grid. */
function vendorField(
  label: string,
  forId: string,
  control: HTMLElement,
  extraClass?: string,
): HTMLElement {
  const cell = document.createElement("div");
  cell.className =
    extraClass === undefined ? "admin-vendor-field" : `admin-vendor-field ${extraClass}`;
  cell.append(buildVendorFieldLabel(label, forId), control);
  return cell;
}

/** UX78: a persistent visible label above a vendor contact field. The
 * placeholder stays, but it vanishes once the field is filled. */
function buildVendorFieldLabel(text: string, forId?: string): HTMLElement {
  // UX105: a real <label for> where there is one field to name, so the name stays on screen (and is clickable)
  // once the field is filled; a plain span for a heading over a group (Delivery days).
  const label = document.createElement(forId === undefined ? "span" : "label");
  label.className = "field-label admin-vendor-field-label";
  label.textContent = text;
  if (forId !== undefined) (label as HTMLLabelElement).htmlFor = forId;
  return label;
}

interface VendorOrderingDraft {
  orderMethod: VendorOrderMethod | null;
  orderFileLayout: VendorOrderFileLayout | null;
  priceFileLayout: VendorPriceFileLayout | null;
  saving: boolean;
  error: string;
}

// Keep unsaved preferences when another vendor field refreshes the list.
const vendorOrderingDrafts = new Map<string, VendorOrderingDraft>();

function buildVendorOrderingFields(vendor: VendorDto, manageAllowed: boolean): HTMLElement {
  const saved = (): VendorOrderingDraft => ({
    orderMethod: vendor.order_method ?? null,
    orderFileLayout: vendor.order_file_layout ?? null,
    priceFileLayout: vendor.price_file_layout ?? null,
    saving: false,
    error: "",
  });
  const draft = vendorOrderingDrafts.get(vendor.id) ?? saved();
  const block = document.createElement("div");
  block.className = "admin-vendor-ordering-preferences";
  block.dataset["testid"] = "admin-vendor-ordering-preferences";
  const grid = document.createElement("div");
  grid.className = "admin-vendor-grid";
  const select = (
    suffix: string,
    choices: [string, string][],
    value: string,
  ): HTMLSelectElement => {
    const control = document.createElement("select");
    control.id = `admin-vendor-${vendor.id}-${suffix}`;
    control.className = "field-input";
    control.dataset["testid"] = `admin-vendor-${suffix}`;
    control.append(...choices.map(([label, key]) => new Option(label, key)));
    control.value = value;
    control.disabled = !manageAllowed || draft.saving;
    return control;
  };
  const method = select(
    "order-method",
    [
      ["Not set", ""],
      ["Upload file", "upload_file"],
      ["Email", "email"],
      ["Website", "website"],
      ["Phone", "phone"],
    ],
    draft.orderMethod ?? "",
  );
  const orderLayout = select(
    "order-file-layout",
    [
      ["Select layout", ""],
      ["ABC2GO: Item code and qty", "abc2go_item_code_qty"],
    ],
    draft.orderFileLayout ?? "",
  );
  const priceLayout = select(
    "price-file-layout",
    [
      ["None", ""],
      ["NC ABC", "nc_abc"],
    ],
    draft.priceFileLayout ?? "",
  );
  const orderField = vendorField("Order file layout", orderLayout.id, orderLayout);
  orderField.hidden = draft.orderMethod !== "upload_file";
  const methodField = vendorField("How we order", method.id, method);
  hoverHelp(
    methodField.querySelector("label") ?? methodField,
    "Sets this vendor's ordering method.",
    "Upload file enables an order file for this vendor.",
    "More than one vendor can use Upload file.",
  );
  const priceField = vendorField("Price file layout", priceLayout.id, priceLayout);
  hoverHelp(
    priceField.querySelector("label") ?? priceField,
    "Sets this vendor's price import format.",
    "Price imports are independent of order files.",
    "NC ABC prices can be imported for a Phone vendor.",
  );
  grid.append(methodField, orderField, priceField);
  const actions = document.createElement("div");
  actions.className = "admin-vendor-ordering-actions";
  const save = document.createElement("button");
  save.type = "button";
  save.className = "btn-primary btn-compact";
  save.textContent = draft.saving ? "Saving..." : "Save";
  save.dataset["testid"] = "admin-vendor-ordering-save";
  const cancel = document.createElement("button");
  cancel.type = "button";
  cancel.className = "btn-secondary btn-compact";
  cancel.textContent = "Cancel";
  cancel.dataset["testid"] = "admin-vendor-ordering-cancel";
  const status = document.createElement("p");
  status.className = "admin-field-error";
  status.setAttribute("role", "status");
  status.textContent = draft.error;
  const refreshActions = (): void => {
    const dirty =
      draft.orderMethod !== (vendor.order_method ?? null) ||
      draft.orderFileLayout !== (vendor.order_file_layout ?? null) ||
      draft.priceFileLayout !== (vendor.price_file_layout ?? null);
    save.disabled =
      !manageAllowed ||
      draft.saving ||
      !dirty ||
      (draft.orderMethod === "upload_file" && draft.orderFileLayout === null);
    cancel.disabled = !manageAllowed || draft.saving || !dirty;
    orderLayout.required = draft.orderMethod === "upload_file";
    orderField.hidden = draft.orderMethod !== "upload_file";
  };
  const hold = (): void => {
    draft.error = "";
    status.textContent = "";
    vendorOrderingDrafts.set(vendor.id, draft);
    refreshActions();
  };
  method.addEventListener("change", () => {
    draft.orderMethod = method.value === "" ? null : (method.value as VendorOrderMethod);
    if (draft.orderMethod !== "upload_file") {
      draft.orderFileLayout = null;
      orderLayout.value = "";
    }
    hold();
  });
  orderLayout.addEventListener("change", () => {
    draft.orderFileLayout =
      orderLayout.value === "" ? null : (orderLayout.value as VendorOrderFileLayout);
    hold();
  });
  priceLayout.addEventListener("change", () => {
    draft.priceFileLayout =
      priceLayout.value === "" ? null : (priceLayout.value as VendorPriceFileLayout);
    hold();
  });
  cancel.addEventListener("click", () => {
    vendorOrderingDrafts.delete(vendor.id);
    renderVendorList();
  });
  save.addEventListener("click", () => {
    if (save.disabled || !purchasingVendorsManageAllowed(session())) return;
    const gen = loadGeneration;
    const activeSession = session();
    draft.saving = true;
    vendorOrderingDrafts.set(vendor.id, draft);
    renderVendorList();
    void (async () => {
      try {
        const result = await updateVendor(
          activeSession.apiBaseUrl,
          activeSession.tenantId,
          vendor.id,
          {
            orderMethod: draft.orderMethod,
            orderFileLayout: draft.orderFileLayout,
            priceFileLayout: draft.priceFileLayout,
          },
        );
        if (!loadIsCurrent(gen)) return;
        if (result.status !== 200) {
          draft.error = capitalizeServerMessage(
            result.body.message ?? "Could not save. Check your connection.",
          );
        } else {
          settingsVendors = settingsVendors.map((listed) =>
            listed.id === vendor.id ? result.body.vendor : listed,
          );
          vendorOrderingDrafts.delete(vendor.id);
          populateBottleVendorOfferVendorSelect();
        }
      } catch {
        if (!loadIsCurrent(gen)) return;
        draft.error = "Could not save. Check your connection.";
      } finally {
        if (loadIsCurrent(gen)) {
          draft.saving = false;
          renderVendorList();
        }
      }
    })();
  });
  refreshActions();
  actions.append(save, cancel);
  block.append(grid, actions, status);
  return block;
}

async function handleVendorFieldCommit(
  vendorId: string,
  field:
    | "name"
    | "accountReference"
    | "contactName"
    | "contactEmail"
    | "contactPhone"
    | "notes"
    | "orderCutoffTime"
    | "orderWebsiteUrl",
  input: HTMLInputElement | HTMLTextAreaElement,
): Promise<void> {
  const vendor = settingsVendors.find((candidate) => candidate.id === vendorId);
  if (vendor === undefined) {
    return;
  }
  const raw = input.value.trim();
  const current =
    field === "name"
      ? vendor.name
      : field === "accountReference"
        ? (vendor.account_reference ?? "")
        : field === "contactName"
          ? (vendor.contact_name ?? "")
          : field === "contactEmail"
            ? (vendor.contact_email ?? "")
            : field === "contactPhone"
              ? (vendor.contact_phone ?? "")
              : field === "orderCutoffTime"
                ? (vendor.order_cutoff_time ?? "")
                : field === "orderWebsiteUrl"
                  ? (vendor.order_website_url ?? "")
                  : (vendor.notes ?? "");
  if (raw === current) {
    return;
  }
  const key = vendorStatusKey(vendorId, field);
  if (field === "name" && raw.length === 0) {
    input.value = vendor.name;
    writeVendorStatusError("A vendor needs a name.", key);
    return;
  }
  // VEN7-F1: no pre-emptive clear here anymore. The shared line is
  // written only from this call's own settled outcome below.
  const s = session();
  const patch: Record<string, string> = {};
  // Every field but name is CLEARED (sent as empty string) rather than
  // omitted when a person deletes its contents; updateVendor's own
  // COALESCE convention means an omitted field is left unchanged, so an
  // empty string has to travel as a real value here. name itself never
  // clears (blocked above): a vendor without a name cannot be found again
  // in this list.
  if (raw.length > 0 || field === "name") {
    patch[field] = raw;
  } else {
    // src/domain/vendors.ts's updateVendor treats `undefined` as "leave
    // alone" via `??`, so there is no server-side way to CLEAR a field
    // back to null through this route today; an emptied field simply
    // keeps its last saved value until a real replacement is typed. Named
    // here rather than silently accepted as full clear support.
    input.value = current;
    writeVendorStatusError("This field can be changed but not cleared to blank yet.", key);
    return;
  }
  const result = await updateVendor(s.apiBaseUrl, s.tenantId, vendorId, patch);
  if (result.status !== 200) {
    input.value = current;
    writeVendorStatusError(
      `Could not save this change. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`,
      key,
    );
    return;
  }
  Object.assign(vendor, result.body.vendor);
  if (field === "name") {
    // UX120: the collapsed row shows the name.
    const summaryName = settingsVendorsEl.querySelector<HTMLElement>(
      `[data-vendor-id="${vendorId}"] [data-testid="admin-vendor-summary-name"]`,
    );
    if (summaryName !== null) {
      summaryName.textContent = vendor.name;
    }
  }
  resolveVendorStatusSuccess(key);
  adminToast(`Saved ${vendor.name}.`, "success");
}

async function handleVendorMinimumOrderCommit(
  vendorId: string,
  input: HTMLInputElement,
): Promise<void> {
  const vendor = settingsVendors.find((candidate) => candidate.id === vendorId);
  if (vendor === undefined) {
    return;
  }
  const raw = input.value.trim();
  if (raw.length === 0) {
    input.value =
      vendor.minimum_order_cents === null ? "" : centsToAmountString(vendor.minimum_order_cents);
    return;
  }
  const key = vendorStatusKey(vendorId, "minimumOrder");
  let minimumOrderCents: number;
  try {
    minimumOrderCents = parseDollarsToCentsExact(raw, "Minimum order");
  } catch (error) {
    writeVendorStatusError(error instanceof Error ? error.message : String(error), key);
    input.value =
      vendor.minimum_order_cents === null ? "" : centsToAmountString(vendor.minimum_order_cents);
    return;
  }
  if (minimumOrderCents === vendor.minimum_order_cents) {
    return;
  }
  // VEN7-F1: no pre-emptive clear here anymore. The shared line is
  // written only from this call's own settled outcome below.
  const s = session();
  const result = await updateVendor(s.apiBaseUrl, s.tenantId, vendorId, { minimumOrderCents });
  if (result.status !== 200) {
    input.value =
      vendor.minimum_order_cents === null ? "" : centsToAmountString(vendor.minimum_order_cents);
    writeVendorStatusError(
      `Could not save this change. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`,
      key,
    );
    return;
  }
  Object.assign(vendor, result.body.vendor);
  resolveVendorStatusSuccess(key);
  adminToast(`Saved ${vendor.name}.`, "success");
}

// VEN7: one save chain per vendor id, so two delivery-day taps on the
// SAME vendor never read-modify-write from the same stale snapshot. A
// vendor absent from this map has no save in flight; its tail is treated
// as already resolved.
const vendorDeliveryDaySaveChains = new Map<string, Promise<void>>();

async function handleVendorDeliveryDayToggle(vendorId: string, day: string): Promise<void> {
  const previous = vendorDeliveryDaySaveChains.get(vendorId) ?? Promise.resolve();
  // A prior link's own failure already reported itself via
  // settingsVendorStatusEl (see saveVendorDeliveryDay below) and must not
  // abort this link's turn; swallow it here so the chain keeps running.
  const next = previous.catch(() => undefined).then(() => saveVendorDeliveryDay(vendorId, day));
  vendorDeliveryDaySaveChains.set(vendorId, next);
  await next;
}

/** Does the actual read-modify-write, but only ever runs with the
 * previous save for this SAME vendor already settled, so `vendor.
 * delivery_days` below is the latest SAVED state (this save's own
 * predecessor's Object.assign, if it succeeded) rather than a snapshot
 * from before either tap. This is the whole fix: nothing about the
 * read-modify-write shape itself changes, only when each instance of it
 * is allowed to read. */
async function saveVendorDeliveryDay(vendorId: string, day: string): Promise<void> {
  const vendor = settingsVendors.find((candidate) => candidate.id === vendorId);
  if (vendor === undefined) {
    return;
  }
  const currentDays = new Set(vendor.delivery_days ?? []);
  if (currentDays.has(day)) {
    currentDays.delete(day);
  } else {
    currentDays.add(day);
  }
  const nextDays = DELIVERY_DAY_OPTIONS.map((option) => option.value).filter((value) =>
    currentDays.has(value),
  );
  // VEN7-F1: no pre-emptive clear here anymore, including for a save
  // already queued behind a failed one in the same vendor's chain -- the
  // shared line is written only from this call's own settled outcome.
  // Keyed by DAY, not just vendor: mockup artboard 2's own worked example
  // requires that Wednesday succeeding leaves a still-unread Tuesday
  // failure exactly as it was, "because Wednesday is a DIFFERENT thing
  // than the one that failed" -- a coarser vendor-only key would let any
  // day's success paper over a different day's unread failure.
  const key = vendorStatusKey(vendorId, `deliveryDay:${day}`);
  const s = session();
  const result = await updateVendor(s.apiBaseUrl, s.tenantId, vendorId, { deliveryDays: nextDays });
  if (result.status !== 200) {
    const dayLabel = DELIVERY_DAY_FULL_LABEL[day] ?? day;
    writeVendorStatusError(
      `Could not save ${dayLabel} for ${vendor.name}. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`,
      key,
    );
    return;
  }
  Object.assign(vendor, result.body.vendor);
  resolveVendorStatusSuccess(key);
  renderVendorList();
}

async function handleVendorAdd(): Promise<void> {
  if (vendorCreatePending) return;
  const name = settingsVendorNameInput.value.trim();
  if (name.length === 0) {
    settingsVendorStatusEl.textContent = "A vendor needs a name.";
    return;
  }
  settingsVendorStatusEl.textContent = "";
  settingsVendorAddButton.disabled = true;
  const s = session();
  const gen = loadGeneration;
  vendorCreatePending = true;
  vendorCreateRevision += 1;
  vendorCreateRecoveryNeeded = !vendorsLoaded;
  const result = await createVendor(s.apiBaseUrl, s.tenantId, { name });
  if (!loadIsCurrent(gen)) return;
  vendorCreatePending = false;
  vendorCreateRevision += 1;
  const recover = vendorCreateRecoveryNeeded;
  vendorCreateRecoveryNeeded = false;
  settingsVendorAddButton.disabled = false;
  if (result.status !== 201) {
    settingsVendorStatusEl.textContent = `Could not add this vendor. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`;
    if (recover) void loadVendors(true);
    return;
  }
  settingsVendorNameInput.value = "";
  settingsVendors.push(result.body.vendor);
  // UX120: a new vendor opens expanded, so its fields are right there.
  expandedVendorId = result.body.vendor.id;
  renderVendorList();
  settingsVendorsEl
    .querySelector<HTMLElement>(`[data-vendor-id="${result.body.vendor.id}"]`)
    ?.scrollIntoView({ block: "nearest" });
  populateBottleVendorOfferVendorSelect();
  adminToast(`Added ${result.body.vendor.name}.`, "success");
  if (recover) void loadVendors(true);
}

// ---------------------------------------------------------------------
// INV4: the Inventory item editor's own "Vendor offers" panel. Multiple
// offers per item, exactly one preferred (money.line-style state, refused
// at the domain layer and enforced by the database's own partial unique
// index if the pre-check ever regresses); see
// src/domain/vendors.ts's own module header. Vendor is a PICKER (a closed
// set: this tenant's own vendor list), never free text, and a retired
// vendor already assigned to this item's history renders honestly rather
// than vanishing (the CT1 merge pattern) while being refused as a NEW
// selection (createVendorItemOffer's own VendorInactiveError, surfaced
// here as a plain-language refusal from the server, never pre-empted by
// hiding the row -- the picker itself never OFFERS a retired vendor for a
// NEW assignment, matching populateBottleContainerTypeSelect's own
// "active options plus the currently-relevant one merged in" shape).
// ---------------------------------------------------------------------

let bottleVendorOffers: VendorItemOfferDto[] = [];
let bottleTermsContext: LocationVendorOfferTermsContextDto | undefined;
const bottleEffectiveTerms = new Map<string, EffectiveLocationVendorOfferTermsDto>();
let bottleTermsLoadSequence = 0;
let bottleVendorReadState: "unverified" | "authorized" | "denied" = "unverified";
let bottleVendorReadScope: string | undefined;
const bottlePreferredTermsByLocation = new Map<
  string,
  Map<string, EffectiveLocationVendorOfferTermsDto>
>();
function clearBottleVendorAddFields(): void {
  for (const input of [
    bottleVendorOfferVendorSelect,
    bottleVendorOfferSkuInput,
    bottleVendorOfferOrderUnitInput,
    bottleVendorOfferPackSizeInput,
    bottleVendorOfferCostInput,
  ])
    input.value = "";
  refreshVendorOfferFormCues();
}
function bottleVendorTermsReadable(): boolean {
  return (
    bottleVendorReadState === "authorized" &&
    bottleVendorReadScope !== undefined &&
    bottleVendorReadScope === vendorScopeKey()
  );
}
function redactBottleVendorTerms(denied: boolean): void {
  bottleVendorReadState = denied ? "denied" : "unverified";
  bottleVendorReadScope = vendorScopeKey();
  bottleTermsLoadSequence += 1;
  bottleTermsContext = undefined;
  bottleEffectiveTerms.clear();
  bottlePreferredTermsByLocation.clear();
  clearBottleVendorAddFields();
  updateBottlePreferredLocationLabels();
}

type VendorActor = Pick<Session, "apiBaseUrl" | "tenantId" | "userId">;
interface VendorTermsEdit {
  actor: VendorActor;
  productId: string;
  vendorId: string;
  offerId: string;
  locationId: string;
  expectedVersion: number | null;
  sku: string;
  cost: string;
  /** VENDORCOST1: set in shared (preparation) mode, where the edit writes the shared offer for every location. */
  shared?: { orderUnit: string; pack: string; body?: UpdateVendorItemOfferInput };
  body?: SaveLocationVendorOfferTermsInput;
  busy: boolean;
  notApplied: boolean;
  message: string;
}
interface VendorAddIntent {
  actor: VendorActor;
  body: CreateVendorItemOfferInput & { locationId: string; requestId: string };
  busy: boolean;
  message: string;
}
// Original commands outlive the sheet and session teardown. Keys exclude tokens so
// renewed sign-in can recover only the same actual actor, tenant, API and scope.
const vendorTermsEdits = new Map<string, VendorTermsEdit>();
const vendorAddIntents = new Map<string, VendorAddIntent>();
function vendorActor(active: Session): VendorActor {
  return { apiBaseUrl: active.apiBaseUrl, tenantId: active.tenantId, userId: active.userId };
}
function vendorScopeKey(): string | undefined {
  const active = sessionOrUndefined();
  const locationId = f3SellingUi.editor?.siteId;
  return active === undefined ||
    locationId === undefined ||
    editingBottleId === undefined ||
    locationId !== (bottleExplicitLocationId ?? inventorySellingSiteId())
    ? undefined
    : JSON.stringify([
        active.apiBaseUrl,
        active.tenantId,
        active.userId,
        locationId,
        editingBottleId,
      ]);
}
function currentVendorEdit(): VendorTermsEdit | undefined {
  const key = vendorScopeKey();
  return key === undefined ? undefined : vendorTermsEdits.get(key);
}
function currentVendorAdd(): VendorAddIntent | undefined {
  const key = vendorScopeKey();
  return key === undefined ? undefined : vendorAddIntents.get(key);
}
function vendorActorMatches(actor: VendorActor): boolean {
  const active = sessionOrUndefined();
  return (
    active !== undefined &&
    active.apiBaseUrl === actor.apiBaseUrl &&
    active.tenantId === actor.tenantId &&
    active.userId === actor.userId
  );
}
async function loadBottleEffectiveTerms(productId: string): Promise<boolean> {
  const active = sessionOrUndefined();
  const locationId = f3SellingUi.editor?.siteId;
  const scope = vendorScopeKey();
  const seq = ++bottleTermsLoadSequence;
  const gen = loadGeneration;
  if (active === undefined || locationId === undefined || scope === undefined) {
    bottleVendorOfferStatusEl.textContent = "Choose an authorized location to view Vendor Options.";
    return false;
  }
  const result = await getLocationVendorOfferTerms(
    active.apiBaseUrl,
    active.tenantId,
    locationId,
    bottleVendorOffers.map((offer) => offer.id),
  );
  if (
    seq !== bottleTermsLoadSequence ||
    !loadIsCurrent(gen) ||
    sessionOrUndefined() !== active ||
    vendorScopeKey() !== scope ||
    editingBottleId !== productId
  )
    return false;
  bottleTermsContext = undefined;
  bottleEffectiveTerms.clear();
  bottleVendorReadState = "unverified";
  bottleVendorReadScope = scope;
  if (
    result.status !== 200 ||
    result.body.context?.locationId !== locationId ||
    !Array.isArray(result.body.terms)
  ) {
    bottleVendorOfferStatusEl.textContent =
      result.body.message ?? "Vendor Option terms unavailable. Reload this item to try again.";
    redactBottleVendorTerms(result.status === 403);
    renderBottleVendorOfferList();
    renderBottleCalculator();
    return false;
  }
  if (
    result.body.terms.some(
      (terms) =>
        terms.locationId !== locationId ||
        terms.productId !== productId ||
        !bottleVendorOffers.some(
          (offer) => offer.id === terms.offerId && offer.vendor_id === terms.vendorId,
        ),
    )
  )
    return false;
  bottleTermsContext = result.body.context;
  bottleVendorReadState = "authorized";
  bottleVendorReadScope = scope;
  for (const terms of result.body.terms) bottleEffectiveTerms.set(terms.offerId, terms);
  bottlePreferredTermsByLocation.set(locationId, new Map(bottleEffectiveTerms));
  updateBottlePreferredLocationLabels();
  bottleVendorOfferStatusEl.textContent =
    bottleTermsContext.state === "preparation"
      ? "Vendor Option changes here apply to every location while details are shared."
      : "";
  renderBottleVendorOfferList();
  renderBottleCalculator();
  return true;
}
function vendorOptionButton(
  label: string,
  action: () => void,
  disabled = false,
): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "btn-secondary btn-compact";
  button.textContent = label;
  button.disabled = disabled;
  button.addEventListener("click", action);
  return button;
}
function startVendorTermsEdit(offer: VendorItemOfferDto): void {
  const active = sessionOrUndefined();
  const key = vendorScopeKey();
  const terms = bottleEffectiveTerms.get(offer.id);
  const locationId = f3SellingUi.editor?.siteId;
  if (
    active === undefined ||
    key === undefined ||
    currentVendorEdit() !== undefined ||
    currentVendorAdd() !== undefined ||
    !hasPermission(active, "inv.vendor_offer.manage")
  )
    return;
  if (bottleTermsContext?.state === "preparation" && locationId !== undefined) {
    // VENDORCOST1: shared mode edits the vendor's own offer, one price for every location.
    vendorTermsEdits.set(key, {
      actor: vendorActor(active),
      productId: offer.liquor_product_id,
      vendorId: offer.vendor_id,
      offerId: offer.id,
      locationId,
      expectedVersion: null,
      sku: offer.vendor_sku ?? "",
      cost: offer.cost_cents === null ? "" : (offer.cost_cents / 100).toFixed(2),
      shared: {
        orderUnit: (offer.order_unit ?? "").trim().toLowerCase(),
        pack: offer.pack_size ?? "",
      },
      busy: false,
      notApplied: false,
      message: "",
    });
    renderBottleVendorOfferList();
    return;
  }
  if (terms === undefined || bottleTermsContext?.state !== "enforced") return;
  vendorTermsEdits.set(key, {
    actor: vendorActor(active),
    productId: terms.productId,
    vendorId: terms.vendorId,
    offerId: terms.offerId,
    locationId: terms.locationId,
    expectedVersion: terms.localVersion,
    sku: terms.vendorSku ?? "",
    cost: terms.costCents === null ? "" : (terms.costCents / 100).toFixed(2),
    busy: false,
    notApplied: false,
    message: "",
  });
  renderBottleVendorOfferList();
}
async function saveVendorTermsEdit(edit: VendorTermsEdit): Promise<void> {
  const key = vendorScopeKey();
  if (
    key === undefined ||
    currentVendorEdit() !== edit ||
    edit.busy ||
    edit.notApplied ||
    !vendorActorMatches(edit.actor) ||
    f3SellingUi.editor?.siteId !== edit.locationId ||
    editingBottleId !== edit.productId
  )
    return;
  if (edit.shared !== undefined) {
    await saveSharedVendorOfferEdit(key, edit, edit.shared);
    return;
  }
  if (edit.body === undefined) {
    let costCents: number | null = null;
    try {
      if (edit.cost.trim() !== "") costCents = parseDollarsToCentsExact(edit.cost.trim(), "Cost");
    } catch (error) {
      edit.message = error instanceof Error ? error.message : String(error);
      renderBottleVendorOfferList();
      return;
    }
    edit.body = {
      locationId: edit.locationId,
      requestId: crypto.randomUUID(),
      expectedVersion: edit.expectedVersion,
      vendorSku: edit.sku.trim() || null,
      costCents,
    };
  }
  edit.busy = true;
  edit.message = "Saving original Vendor Option.";
  renderBottleVendorOfferList();
  const result = await saveLocationVendorOfferTerms(
    edit.actor.apiBaseUrl,
    edit.actor.tenantId,
    edit.offerId,
    edit.body,
  );
  edit.busy = false;
  const outcome = result.body;
  const matches =
    outcome.locationId === edit.body.locationId &&
    outcome.offerId === edit.offerId &&
    outcome.requestId === edit.body.requestId &&
    outcome.expectedVersion === edit.body.expectedVersion;
  if (
    matches &&
    result.status === 200 &&
    outcome.outcome === "applied" &&
    outcome.terms !== undefined &&
    outcome.terms.locationId === edit.locationId &&
    outcome.terms.offerId === edit.offerId &&
    outcome.terms.productId === edit.productId &&
    outcome.terms.vendorId === edit.vendorId &&
    outcome.terms.vendorSku === edit.body.vendorSku &&
    outcome.terms.costCents === edit.body.costCents
  ) {
    vendorTermsEdits.delete(key);
    if (vendorScopeKey() === key && vendorActorMatches(edit.actor)) {
      if (bottleVendorTermsReadable()) {
        bottleEffectiveTerms.set(edit.offerId, outcome.terms);
        bottlePreferredTermsByLocation.get(edit.locationId)?.set(edit.offerId, outcome.terms);
        updateBottlePreferredLocationLabels();
      }
      renderBottleCalculator();
    }
  } else if (
    matches &&
    result.status === 409 &&
    outcome.outcome === "not_applied" &&
    ["preparation", "revision_changed", "basis_changed"].includes(outcome.reason)
  ) {
    edit.notApplied = true;
    edit.message =
      "The original save was not applied. Reload current values before making a new edit.";
  } else {
    edit.message = `Original save is unconfirmed. ${result.body.message ?? "Retry the original save. Current values do not confirm its outcome."}`;
  }
  if (vendorScopeKey() === key && vendorActorMatches(edit.actor) && !bottleFormScrim.hidden)
    renderBottleVendorOfferList();
}
// VENDORCOST1: shared (preparation) mode. The cost, SKU, order unit and pack live on the vendor's offer for the item, so
// one save is the existing shared-offer update and applies to every location. A blank cost leaves the stored cost alone.
async function saveSharedVendorOfferEdit(
  key: string,
  edit: VendorTermsEdit,
  shared: NonNullable<VendorTermsEdit["shared"]>,
): Promise<void> {
  if (shared.body === undefined) {
    let costCents: number | undefined;
    if (edit.cost.trim() !== "") {
      try {
        costCents = parseDollarsToCentsExact(edit.cost.trim(), "Cost");
      } catch (error) {
        edit.message = error instanceof Error ? error.message : String(error);
        renderBottleVendorOfferList();
        return;
      }
    }
    const isCase = shared.orderUnit === "case";
    const rawPack = shared.pack.trim();
    const packSize = isCase && rawPack.length > 0 ? Number(rawPack) : undefined;
    if (isCase && (packSize === undefined || !Number.isInteger(packSize) || packSize <= 0)) {
      edit.message = `Each case holds a whole number of ${vendorOfferItemNoun()}s, 1 or more.`;
      renderBottleVendorOfferList();
      return;
    }
    const body: UpdateVendorItemOfferInput = {};
    if (edit.sku.trim() !== "") body.vendorSku = edit.sku.trim();
    if (shared.orderUnit !== "") body.orderUnit = shared.orderUnit;
    if (packSize !== undefined) {
      body.packSize = packSize;
      body.unitsPerCase = packSize;
    }
    if (costCents !== undefined) body.costCents = costCents;
    shared.body = body;
  }
  edit.busy = true;
  edit.message = "Saving.";
  renderBottleVendorOfferList();
  const result = await updateVendorItemOffer(
    edit.actor.apiBaseUrl,
    edit.actor.tenantId,
    edit.offerId,
    shared.body,
  );
  edit.busy = false;
  const saved = result.body.vendorItemOffer;
  if (result.status === 200 && saved?.id === edit.offerId) {
    vendorTermsEdits.delete(key);
    if (vendorScopeKey() === key && vendorActorMatches(edit.actor) && !bottleFormScrim.hidden) {
      await loadBottleVendorOffers(edit.productId);
      adminToast(`Saved ${vendorNameById(edit.vendorId)} for every location.`, "success");
    }
    return;
  }
  if (result.status === 400 || result.status === 422) {
    // The server refused the values, so nothing changed: let the person correct them.
    shared.body = undefined;
    edit.message = result.body.message ?? "That change was not accepted. Check the values and save again.";
  } else {
    edit.message = `Save is unconfirmed. ${result.body.message ?? "Retry the save. Current values do not confirm its outcome."}`;
  }
  if (vendorScopeKey() === key && vendorActorMatches(edit.actor) && !bottleFormScrim.hidden)
    renderBottleVendorOfferList();
}
function renderSharedVendorTermsEditor(edit: VendorTermsEdit, shared: NonNullable<VendorTermsEdit["shared"]>): HTMLElement {
  const form = document.createElement("div");
  form.className = "add-offer-card";
  form.dataset["testid"] = "admin-vendor-offer-shared-editor";
  const locked = edit.busy || shared.body !== undefined;
  const field = (label: string, control: HTMLElement): void => {
    const caption = document.createElement("label");
    caption.className = "field-label";
    caption.textContent = label;
    caption.append(control);
    form.append(caption);
  };
  const sku = document.createElement("input");
  sku.className = "field-input";
  sku.type = "text";
  sku.value = edit.sku;
  sku.disabled = locked;
  sku.addEventListener("input", () => {
    edit.sku = sku.value;
  });
  field("Vendor SKU", sku);
  const unit = document.createElement("select");
  unit.className = "field-input";
  const legacy = ORDER_UNIT_OPTIONS.some((option) => option.value === shared.orderUnit);
  const choices = legacy || shared.orderUnit === ""
    ? ORDER_UNIT_OPTIONS
    : [...ORDER_UNIT_OPTIONS, { value: shared.orderUnit, label: orderUnitDisplayLabel(shared.orderUnit) }];
  for (const option of choices) unit.append(new Option(option.label, option.value));
  unit.value = shared.orderUnit;
  unit.disabled = locked;
  unit.addEventListener("change", () => {
    shared.orderUnit = unit.value;
    renderBottleVendorOfferList();
  });
  field("Order unit", unit);
  if (shared.orderUnit === "case") {
    const pack = document.createElement("input");
    pack.className = "field-input";
    pack.type = "text";
    pack.inputMode = "numeric";
    pack.value = shared.pack;
    pack.disabled = locked;
    pack.addEventListener("input", () => {
      shared.pack = pack.value;
    });
    field(`Each case holds (${vendorOfferItemNoun()}s)`, pack);
  }
  const cost = document.createElement("input");
  cost.className = "field-input";
  cost.type = "text";
  cost.inputMode = "decimal";
  cost.value = edit.cost;
  cost.disabled = locked;
  cost.addEventListener("input", () => {
    edit.cost = cost.value;
  });
  field(shared.orderUnit === "" ? "Cost per order unit" : `Price for one ${shared.orderUnit}`, cost);
  const message = document.createElement("p");
  message.className = "admin-field-help";
  message.textContent =
    edit.message ||
    "Changes apply to this vendor's offer at every location. Selling prices are saved separately in Pricing.";
  form.append(message);
  const active = sessionOrUndefined();
  const allowed = active !== undefined && hasPermission(active, "inv.vendor_offer.manage");
  form.append(
    vendorOptionButton(
      edit.busy ? "Saving" : shared.body === undefined ? "Save" : "Retry save",
      () => void saveVendorTermsEdit(edit),
      edit.busy || !allowed,
    ),
  );
  form.append(
    vendorOptionButton(
      "Cancel",
      () => {
        const key = vendorScopeKey();
        if (key !== undefined) vendorTermsEdits.delete(key);
        renderBottleVendorOfferList();
      },
      edit.busy,
    ),
  );
  return form;
}
function renderVendorTermsEditor(edit: VendorTermsEdit): HTMLElement {
  const form = document.createElement("div");
  form.className = "add-offer-card";
  const viewer = sessionOrUndefined();
  if (
    viewer === undefined ||
    !hasPermission(viewer, "inv.vendor_offer.view") ||
    !bottleVendorTermsReadable()
  ) {
    const note = document.createElement("p");
    note.textContent =
      "The original Vendor Option edit is retained. Restore access at its original location to view and recover it.";
    form.append(note);
    form.append(
      vendorOptionButton(
        edit.notApplied ? "Reload current values" : "Check current values",
        () => {
          const key = vendorScopeKey();
          void loadBottleVendorOffers(edit.productId).then(() => {
            if (
              edit.notApplied &&
              key !== undefined &&
              vendorScopeKey() === key &&
              currentVendorEdit() === edit &&
              bottleVendorTermsReadable()
            ) {
              vendorTermsEdits.delete(key);
              renderBottleVendorOfferList();
            }
          });
        },
        edit.busy,
      ),
    );
    if (edit.body !== undefined && !edit.notApplied)
      form.append(
        vendorOptionButton(
          "Retry original save",
          () => void saveVendorTermsEdit(edit),
          edit.busy || viewer === undefined || !hasPermission(viewer, "inv.vendor_offer.manage"),
        ),
      );
    return form;
  }
  if (edit.shared !== undefined) return renderSharedVendorTermsEditor(edit, edit.shared);
  for (const [label, field] of [
    ["Vendor SKU", "sku"],
    ["Cost per order unit", "cost"],
  ] as const) {
    const caption = document.createElement("label");
    caption.className = "field-label";
    caption.textContent = label;
    const input = document.createElement("input");
    input.className = "field-input";
    input.type = "text";
    if (field === "cost") input.inputMode = "decimal";
    input.value = edit[field];
    input.disabled = edit.body !== undefined;
    input.addEventListener("input", () => {
      edit[field] = input.value;
    });
    caption.append(input);
    form.append(caption);
  }
  const message = document.createElement("p");
  message.className = "admin-field-help";
  message.textContent =
    edit.message ||
    "Changes apply to this location only. Selling prices are saved separately in Pricing.";
  form.append(message);
  const active = sessionOrUndefined();
  const allowed = active !== undefined && hasPermission(active, "inv.vendor_offer.manage");
  if (edit.notApplied) {
    form.append(
      vendorOptionButton("Reload current values", () => {
        const key = vendorScopeKey();
        void loadBottleEffectiveTerms(edit.productId).then((loaded) => {
          if (loaded && key !== undefined && currentVendorEdit() === edit) {
            vendorTermsEdits.delete(key);
            renderBottleVendorOfferList();
          }
        });
      }),
    );
  } else {
    form.append(
      vendorOptionButton(
        edit.busy ? "Saving" : edit.body === undefined ? "Save" : "Retry original save",
        () => void saveVendorTermsEdit(edit),
        edit.busy || !allowed,
      ),
    );
    if (edit.body === undefined)
      form.append(
        vendorOptionButton("Cancel", () => {
          const key = vendorScopeKey();
          if (key !== undefined) vendorTermsEdits.delete(key);
          renderBottleVendorOfferList();
        }),
      );
    else
      form.append(
        vendorOptionButton(
          "Check current values",
          () => void loadBottleEffectiveTerms(edit.productId),
          edit.busy,
        ),
      );
  }
  return form;
}

// INVLOC1 Slice 5: what the server said about the open item's preferred vendor at each location the person may plan for, the
// franchisee default's offer, and a token that drops a slow answer arriving after the form closed or reopened.
let bottlePreferredByLocation: PreferredOfferLocationDto[] = [];
let bottlePreferredDefaultOfferId: string | null = null;
let bottlePreferredToken = 0;
// INVLOC1 Slice 5: how many locations the person may plan for, read once per vendor list load. The vendor card's "Account
// number by location" block appears only when this is above one.
let vendorPlanningLocationCount = 0;

function vendorNameById(vendorId: string): string {
  const vendor = settingsVendors.find((candidate) => candidate.id === vendorId);
  return vendor === undefined ? "(unknown vendor)" : vendor.name;
}

// The picker's own option list: every ACTIVE vendor, plus (edit mode
// only) any vendor already referenced by one of this item's own offers
// even if that vendor has since been retired -- so an admin reviewing a
// retired vendor's historical offer sees its real name in the list, never
// a blank or "(unknown vendor)", while the picker still refuses to offer
// that same retired vendor as the target for a brand-new assignment
// (createVendorItemOffer's own VendorInactiveError does that refusal
// server-side; this merge is about HONEST DISPLAY, not about permitting
// the assignment).
function populateBottleVendorOfferVendorSelect(): void {
  // VEN5: rendered honestly rather than left blank when this session
  // cannot see vendors at all -- an empty select with no options reads
  // as "this item has no vendors," which is a different fact from "you
  // can't see them," the same distinction loadBottles' own permission
  // branch already draws for the Bottles list itself. The other callers
  // of this function only ever fire from Purchasing or after a
  // successful vendor fetch, both of which already imply inv.vendor
  // .view; this branch exists for the path that does not, opening a
  // bottle's own vendor offers without having visited Purchasing first.
  const s = sessionOrUndefined();
  if (s === undefined || !hasPermission(s, "inv.vendor.view")) {
    bottleVendorOfferVendorSelect.innerHTML = "";
    const deniedOption = document.createElement("option");
    deniedOption.value = "";
    deniedOption.textContent = "You do not have permission to view vendors.";
    deniedOption.disabled = true;
    bottleVendorOfferVendorSelect.append(deniedOption);
    return;
  }
  const referencedVendorIds = new Set(bottleVendorOffers.map((offer) => offer.vendor_id));
  const options = sortByName(
    settingsVendors.filter((vendor) => vendor.active || referencedVendorIds.has(vendor.id)),
    (vendor) => vendor.name,
  );
  const previousValue = bottleVendorOfferVendorSelect.value;
  const hadBlank = bottleVendorOfferVendorSelect.querySelector('option[value=""]:not([disabled])') !== null;
  bottleVendorOfferVendorSelect.innerHTML = "";
  // ADDITEM2: a new item's picker keeps its "no vendor yet" choice (see renderAddItemStep3).
  if (hadBlank) {
    const blank = document.createElement("option");
    blank.value = "";
    blank.textContent = "Choose a vendor";
    bottleVendorOfferVendorSelect.append(blank);
  }
  for (const vendor of options) {
    const option = document.createElement("option");
    option.value = vendor.id;
    option.textContent = vendor.active ? vendor.name : `${vendor.name} (retired)`;
    option.disabled = !vendor.active;
    bottleVendorOfferVendorSelect.append(option);
  }
  if (options.some((vendor) => vendor.id === previousValue)) {
    bottleVendorOfferVendorSelect.value = previousValue;
  } else if (hadBlank) {
    bottleVendorOfferVendorSelect.value = "";
  }
}

async function loadBottleVendorOffers(liquorProductId: string): Promise<void> {
  const gen = loadGeneration;
  const s = session();
  const scope = vendorScopeKey();
  const editorGeneration = f3SellingUi.openSequence;
  vendorAddFormOpen = false;
  redactBottleVendorTerms(false);
  renderBottleVendorOfferList();
  renderBottleCalculator();
  const result = await listVendorItemOffers(s.apiBaseUrl, s.tenantId, {
    liquorProductId,
    activeOnly: false,
  });
  // SET16-F4: this session's teardown ran while the fetch above was in
  // flight; discard rather than write over what invalidate() already
  // cleared.
  if (
    !loadIsCurrent(gen) ||
    editorGeneration !== f3SellingUi.openSequence ||
    editingBottleId !== liquorProductId ||
    vendorScopeKey() !== scope
  )
    return;
  if (result.status !== 200) {
    bottleVendorOfferStatusEl.textContent = `Could not load Vendor Options. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`;
    redactBottleVendorTerms(result.status === 403);
    renderBottleVendorOfferList();
    renderBottleCalculator();
    return;
  }
  bottleVendorOfferStatusEl.textContent = "";
  bottleVendorOffers = result.body.vendorItemOffers;
  populateBottleVendorOfferVendorSelect();
  renderBottleVendorOfferList();
  await loadBottleEffectiveTerms(liquorProductId);
  if (
    !loadIsCurrent(gen) ||
    editorGeneration !== f3SellingUi.openSequence ||
    vendorScopeKey() !== scope
  )
    return;
  // INV26 Slice 3: the calculator's own Cost row rides the same fetch.
  renderBottleCalculator();
  // INVLOC1 Slice 5: the same item's preferred vendor at each location.
  void loadBottlePreferredByLocation(liquorProductId);
}

// INVLOC1 Slice 5 (DEC-326): the item's preferred vendor at each location the person may plan for. The table is built only when
// the server names more than one location; with one location the Preferred toggle above IS that location's choice and nothing is added.
async function loadBottlePreferredByLocation(liquorProductId: string): Promise<void> {
  const token = ++bottlePreferredToken;
  const gen = loadGeneration;
  const scope = vendorScopeKey();
  const s = sessionOrUndefined();
  if (s === undefined || scope === undefined || !hasPermission(s, "inv.vendor_offer.view")) return;
  const isCurrent = (): boolean =>
    token === bottlePreferredToken &&
    loadIsCurrent(gen) &&
    sessionOrUndefined() === s &&
    vendorScopeKey() === scope &&
    editingBottleId === liquorProductId;
  const result = await getPreferredOffersByLocation(s.apiBaseUrl, s.tenantId, liquorProductId);
  if (!isCurrent()) return;
  bottlePreferredByLocation = result.status === 200 ? result.body.preferredByLocation : [];
  bottlePreferredDefaultOfferId = result.status === 200 ? result.body.defaultOfferId : null;
  if (result.status === 403) {
    redactBottleVendorTerms(true);
    renderBottleVendorOfferList();
  }
  renderBottlePreferredByLocation(liquorProductId);
  if (bottleSellingCurrent()) renderBottleCalculator();
  if (result.status !== 200) return;
  // Every location has its own authoritative terms. The shared renderer first
  // receives relationship/package-only choices, so no legacy cost/code flashes.
  const authoritySequence = bottleTermsLoadSequence;
  const offerIds = bottleVendorOffers.map((offer) => offer.id);
  await Promise.all(
    bottlePreferredByLocation.map(async (siteLocation) => {
      if (siteLocation.locationId === f3SellingUi.editor?.siteId && bottleVendorTermsReadable())
        return;
      const response = await getLocationVendorOfferTerms(
        s.apiBaseUrl,
        s.tenantId,
        siteLocation.locationId,
        offerIds,
      );
      if (!isCurrent() || authoritySequence !== bottleTermsLoadSequence) return;
      if (response.status === 403) {
        redactBottleVendorTerms(true);
        renderBottleVendorOfferList();
        renderBottleCalculator();
        return;
      }
      bottlePreferredTermsByLocation.delete(siteLocation.locationId);
      if (
        response.status === 200 &&
        response.body.context?.locationId === siteLocation.locationId &&
        Array.isArray(response.body.terms) &&
        response.body.terms.every(
          (terms) =>
            terms.locationId === siteLocation.locationId &&
            terms.productId === liquorProductId &&
            bottleVendorOffers.some(
              (offer) => offer.id === terms.offerId && offer.vendor_id === terms.vendorId,
            ),
        )
      ) {
        bottlePreferredTermsByLocation.set(
          siteLocation.locationId,
          new Map(response.body.terms.map((terms) => [terms.offerId, terms])),
        );
      }
      updateBottlePreferredLocationLabels();
    }),
  );
}

/** Keep the shared controls and their save semantics; specialize each option's
 * label using only that row's current authorized local terms. */
function updateBottlePreferredLocationLabels(): void {
  for (const siteLocation of bottlePreferredByLocation) {
    const picker = bottlePreferredByLocationEl.querySelector<HTMLSelectElement>(
      `[data-testid="admin-preferred-by-location-select-${siteLocation.locationId}"]`,
    );
    if (picker === null) continue;
    const termsByOffer = bottlePreferredTermsByLocation.get(siteLocation.locationId);
    for (const option of picker.options) {
      const offer = bottleVendorOffers.find((candidate) => candidate.id === option.value);
      if (offer === undefined) continue; // The shared default choice contains no SKU/cost.
      const terms = termsByOffer?.get(offer.id);
      option.textContent =
        offerPickerLabel(
          {
            ...offer,
            vendor_sku: terms?.vendorSku ?? null,
            cost_cents: terms?.costCents ?? null,
            order_unit: terms === undefined ? offer.order_unit : terms.orderUnit,
          },
          vendorNameById(offer.vendor_id),
          formatCents,
        ) +
        (terms === undefined
          ? " · SKU/cost unavailable"
          : `${terms.vendorSku === null ? " · SKU not configured" : ""}${terms.costCents === null ? " · Cost not configured" : ""}`);
    }
  }
}

function renderBottlePreferredByLocation(liquorProductId: string): void {
  if (!locationChoicesVisible(bottlePreferredByLocation)) {
    bottlePreferredByLocationEl.innerHTML = "";
    bottlePreferredByLocationEl.hidden = true;
    return;
  }
  bottlePreferredByLocationEl.hidden = false;
  renderPreferredByLocation(bottlePreferredByLocationEl, {
    locations: bottlePreferredByLocation,
    defaultOfferId: bottlePreferredDefaultOfferId,
    offers: bottleVendorOffers.map((offer) => ({ ...offer, vendor_sku: null, cost_cents: null })),
    vendorName: vendorNameById,
    formatCents,
    canManage: hasPermission(session(), "inv.vendor_offer.manage"),
    save: async (entries) => {
      const s = session();
      const scope = vendorScopeKey();
      const result = await putPreferredOffersByLocation(s.apiBaseUrl, s.tenantId, {
        liquorProductId,
        entries,
      });
      if (sessionOrUndefined() !== s || vendorScopeKey() !== scope)
        return { ok: false, message: "Location or session changed. Reopen this item." };
      if (result.status !== 200) {
        return {
          ok: false,
          message: capitalizeServerMessage(result.body.message ?? "Check your connection."),
        };
      }
      bottlePreferredByLocation = result.body.preferredByLocation;
      bottlePreferredDefaultOfferId = result.body.defaultOfferId;
      return { ok: true };
    },
  });
  updateBottlePreferredLocationLabels();
}

// ---------------------------------------------------------------------
// INV27 (docs/build-briefs/INV27-item-editor-redesign.md; DEC-227 item
// 2), superseding INV26 Slice 3's pour-cost-% framing. The Pricing tab's
// calculator: cost per oz/bottle (read-only, derived from the item's
// preferred Vendor Option and its own size), Markup today (read-only,
// derived from the item's CURRENT sell price), Target markup % (the one
// editable input per column, prefilled once from Settings' own default
// for this item's kind), and a derived, rounded-up-to-$0.25 new price and
// profit. See harness/ui/src/priceCalculator.ts for the pure math.
//
// Save writes the calculated price back through the existing catalog
// PATCH, gated client-side on the item not being on shelf pricing and on
// admin.liquor_product.manage (the same permission the shared "Save
// item" footer button's own PATCH route already requires server-side).
// ---------------------------------------------------------------------

/** A lenient dollars-string-to-cents read for the calculator's own
 * "Markup today" display only, distinct from parseDollarsToCentsExact
 * (used at Save time elsewhere in this file), which THROWS on a
 * malformed amount -- correct for a Save-time validator, wrong for a
 * read that runs on every keystroke while a field may be mid-edit (e.g.
 * "1." or briefly empty). Returns null rather than throwing or
 * fabricating a figure for anything that is not a finite, non-negative
 * number. */
function lenientDollarsToCents(raw: string): number | null {
  const amount = Number(raw);
  if (!Number.isFinite(amount) || amount < 0) {
    return null;
  }
  return Math.round(amount * 100);
}

/** Reads the calculator's two editable target-markup inputs, the item's
 * current preferred-offer cost and size, and its own current sell
 * price(s), and returns the pure computed result, or null when there is
 * not enough data to compute anything (CLAUDE.md's "never fall back to a
 * hardcoded figure": no preferred Vendor Option, or a size this calculator
 * cannot convert to ounces). */
function currentBottleCalculatorResult(): PriceCalculatorResult | null {
  const activeOffers = bottleVendorOffers.filter((offer) => offer.active);
  const siteId = f3SellingUi.editor?.siteId;
  const resolvedOfferId = bottlePreferredByLocation.find(
    (entry) => entry.locationId === siteId,
  )?.resolvedOfferId;
  const preferred = activeOffers.find((offer) => offer.id === resolvedOfferId);
  const effective =
    preferred === undefined || bottleTermsContext?.locationId !== siteId
      ? undefined
      : bottleEffectiveTerms.get(preferred.id);
  const sizeValue = bottleSizeValueInput.value.trim();
  const sizeUnit = bottleSizeUnitInput.value.trim();
  const ozTargetMarkupRaw = bottleCalcOzTargetMarkupInput.value.trim();
  const bottleTargetMarkupRaw = bottleCalcBottleTargetMarkupInput.value.trim();
  const currentUnitPriceRaw = bottleUnitPriceInput.value.trim();
  return computePriceCalculator({
    unitCostCents: effective?.costCents ?? null,
    bottlesPerOrderUnit:
      effective === undefined
        ? null
        : bottlesPerOrderUnitOf(effective.orderUnit, effective.packSize),
    sizeValue: sizeValue.length > 0 ? sizeValue : null,
    sizeUnit: sizeUnit.length > 0 ? sizeUnit : null,
    containerType: bottleContainerTypeSelect.value,
    defaultKegSize:
      bottleDefaultKegSizeSelect.value.length > 0 ? bottleDefaultKegSizeSelect.value : null,
    kegSizeVolumeOzByValue: kegSizeVolumeOzByValue(),
    currentPricePerOunceCents: bottleCurrentPerOuncePriceCents,
    currentPricePerBottleCents:
      currentUnitPriceRaw.length > 0 ? lenientDollarsToCents(currentUnitPriceRaw) : null,
    targetMarkupPercentPerOunce: ozTargetMarkupRaw.length > 0 ? Number(ozTargetMarkupRaw) : null,
    targetMarkupPercentPerBottle:
      bottleTargetMarkupRaw.length > 0 ? Number(bottleTargetMarkupRaw) : null,
  });
}

/** The column this item's own Save writes, per the brief: a per-ounce
 * item (pour/beer/wine) writes perOuncePriceCents from the "per fl oz"
 * column; a packaged/whole-unit item writes unitPriceCents from the
 * "per bottle" column. Null when the item's own selling method is not
 * yet resolved (matches currentBottleSaleMechanism's own null case). */
function bottleCalculatorSaveMechanism(): ProductSaleMechanism | null {
  return currentBottleSaleMechanism();
}

/** Prefills a target-markup input from Settings' own default for this
 * item's kind, but ONLY from the field's true initial empty state: once
 * the manager has touched a column (bottleCalcOzTargetMarkupTouched /
 * bottleCalcBottleTargetMarkupTouched), a Settings default is never
 * re-applied over it, the same "a real user pick is never silently
 * overwritten by a default" rule DEC-224 established for Container type.
 * A kind with no configured Settings default leaves the field exactly as
 * it was (usually blank), never a hardcoded fallback. */
function applyBottleCalcTargetMarkupDefaults(): void {
  const kindDefault = itemKindTargetMarkupDefaults[bottleFormItemKind];
  if (kindDefault === undefined) {
    return;
  }
  if (!bottleCalcOzTargetMarkupTouched && bottleCalcOzTargetMarkupInput.value.trim() === "") {
    bottleCalcOzTargetMarkupInput.value = String(kindDefault);
  }
  if (
    !bottleCalcBottleTargetMarkupTouched &&
    bottleCalcBottleTargetMarkupInput.value.trim() === ""
  ) {
    bottleCalcBottleTargetMarkupInput.value = String(kindDefault);
  }
}

function formatMarkupPercent(percent: number): string {
  return `${percent.toFixed(0)}%`;
}

function renderBottleCalculator(): void {
  refreshAddItemSteps();
  const isEditing = editingBottleId !== undefined;
  applyBottleCalcTargetMarkupDefaults();
  const result = isEditing ? currentBottleCalculatorResult() : null;
  // ADDCOST1: Add item shows cost and price in the ADDITEM2 "Price for each pour" box, so the older section is Edit item only.
  bottleCostTargetSectionEl.hidden = !isEditing;
  applyBottlePriceLabelsForPricingModel();

  bottleCalcUnavailableEl.hidden = result !== null;
  bottleCalcGridEl.hidden = result === null;
  bottleCalcHelpEl.hidden = result === null;

  if (result === null) {
    bottleCalcOzCostEl.textContent = "";
    bottleCalcBottleCostEl.textContent = "";
    bottleCalcOzMarkupTodayEl.textContent = "";
    bottleCalcBottleMarkupTodayEl.textContent = "";
    bottleCalcOzNewPriceEl.textContent = "";
    bottleCalcBottleNewPriceEl.textContent = "";
    bottleCalcOzProfitEl.textContent = "";
    bottleCalcBottleProfitEl.textContent = "";
    bottleCalcHelpEl.textContent = "";
  } else {
    // Cost is rounded to the nearest cent for DISPLAY only, never up:
    // only the new price rounds up to the next $0.25.
    bottleCalcOzCostEl.textContent = formatCents(Math.round(result.costPerOunceCentsExact));
    bottleCalcBottleCostEl.textContent = formatCents(result.costPerBottleCents);
    bottleCalcOzMarkupTodayEl.textContent =
      result.todayMarkupPercentPerOunce === null
        ? ""
        : formatMarkupPercent(result.todayMarkupPercentPerOunce);
    bottleCalcBottleMarkupTodayEl.textContent =
      result.todayMarkupPercentPerBottle === null
        ? ""
        : formatMarkupPercent(result.todayMarkupPercentPerBottle);
    bottleCalcOzNewPriceEl.textContent =
      result.perOunce === null ? "" : formatCents(result.perOunce.newPriceCents);
    bottleCalcBottleNewPriceEl.textContent =
      result.perBottle === null ? "" : formatCents(result.perBottle.newPriceCents);
    bottleCalcOzProfitEl.textContent =
      result.perOunce === null ? "" : formatCents(Math.round(result.perOunce.profitCents));
    bottleCalcBottleProfitEl.textContent =
      result.perBottle === null ? "" : formatCents(Math.round(result.perBottle.profitCents));
    const ounces = result.ouncesPerBottle.toFixed(2);
    bottleCalcHelpEl.textContent =
      `Unit cost ${formatCents(result.costPerBottleCents)}, ${ounces} fl oz per bottle. ` +
      `New price = cost x (1 + target markup % / 100), rounded up to the next $0.25.`;
  }

  // DEC-227 item 1: "Saves here" badge on whichever column Save actually
  // writes for this item's own sale mechanism; the other column carries
  // no badge at all.
  const savingMechanism = editingBottleId === undefined ? null : bottleCalculatorSaveMechanism();
  const shelfReference = settingsPricingModel === "shelf" && savingMechanism === "per_ounce";
  bottleCalcOzSavesBadgeEl.hidden = savingMechanism !== "per_ounce" || shelfReference;
  bottleCalcBottleSavesBadgeEl.hidden = savingMechanism !== "whole_unit";

  // INV27 coordinator review, 2026-09-29: the column that does NOT save
  // dims and shows a plain-language reason instead of a live-looking
  // dollar figure ("a $268.50 that reads like a real price," the
  // reviewer's own words). Only dims when the mechanism actually
  // resolves to the OTHER column; with no mechanism resolved yet,
  // neither column is dimmed, matching the Saves-here badge's own "no
  // mechanism, no badge either" behavior above.
  const ozUnused = savingMechanism === "whole_unit" || shelfReference;
  const bottleUnused = savingMechanism === "per_ounce";
  for (const cell of bottleCalcOzDimmableCells) {
    cell.classList.toggle("dimmed", ozUnused);
  }
  for (const cell of bottleCalcBottleDimmableCells) {
    cell.classList.toggle("dimmed", bottleUnused);
  }
  bottleCalcOzNotUsedNoteEl.hidden = !ozUnused;
  bottleCalcOzNotUsedNoteEl.textContent = shelfReference
    ? "Reference"
    : ozUnused
      ? "Not used, this item sells by the whole unit"
      : "";
  bottleCalcBottleNotUsedNoteEl.hidden = !bottleUnused;
  // INV27-F2 (DEC-253): under shelf pricing the shelf tier, not this
  // calculator, sets the flat price, so the not-used reason names that
  // instead of the generic per-ounce-selling reason (matching the
  // mockup's own Artboard B "Not used, shelf tier price is set above").
  bottleCalcBottleNotUsedNoteEl.textContent = bottleUnused
    ? settingsPricingModel === "shelf"
      ? "Reference"
      : "Not used, this item sells by the ounce"
    : "";

  // Shelf per-ounce prices are controlled by the current-price card.
  // Keep the calculator visible as a reference without implying it saves.
  bottleCalcSubtitleEl.hidden = !shelfReference;

  refreshBottleCalculatorSaveState(result);
}

/** Design principle 3, "blocked is a state you see before you touch it":
 * the Save button's disabled reason is always shown before anyone taps
 * it, never discovered afterward. */
function refreshBottleCalculatorSaveState(result: PriceCalculatorResult | null): void {
  if (settingsPricingModel === "shelf" && currentBottleSaleMechanism() === "per_ounce") {
    bottleCalcSaveButton.disabled = true;
    bottleCalcSaveReasonEl.textContent =
      "This item uses its shelf price. The calculator is for reference only.";
    return;
  }
  if (editingBottleId === undefined) {
    bottleCalcSaveButton.disabled = true;
    bottleCalcSaveReasonEl.textContent = "Save the item once before using the calculator.";
    return;
  }
  if (result === null) {
    bottleCalcSaveButton.disabled = true;
    bottleCalcSaveReasonEl.textContent = "";
    return;
  }
  // INV27-F2 (DEC-253): this used to disable Save outright whenever
  // bottleShelfTierSelect.value was set, on DEC-216's premise that a
  // shelf-tier item's price never comes from the calculator. That premise
  // is still true for a STRAIGHT POUR (inventorySale.ts's shelf branch
  // never reads per_ounce_price_cents), but the calculator's per-ounce
  // column writes exactly that field, and a cocktail's liquor slot reads
  // it under EVERY pricing model (lineResolution.ts ~327), shelf tier
  // assigned or not. So a shelf tier alone is no longer a reason to
  // disable Save; the checks below (permission, mechanism/column
  // resolved) already gate it correctly, and the per-bottle column stays
  // dimmed/unused by the savingMechanism logic in renderBottleCalculator
  // above regardless of pricing model.
  // Permission-driven, never role-driven (CLAUDE.md): the identical
  // admin.liquor_product.manage permission the shared "Save item"
  // footer button's own PATCH route already requires, resolved through
  // hasPermission and never a role name.
  const permitted =
    currentSession !== undefined && hasPermission(currentSession, "admin.liquor_product.manage");
  if (!permitted) {
    bottleCalcSaveButton.disabled = true;
    bottleCalcSaveReasonEl.textContent = "You don't have permission to change this item's price.";
    return;
  }
  const mechanism = bottleCalculatorSaveMechanism();
  const column = mechanism === "whole_unit" ? result.perBottle : result.perOunce;
  if (mechanism === null || column === null) {
    bottleCalcSaveButton.disabled = true;
    bottleCalcSaveReasonEl.textContent = "Enter a target markup % to calculate a price to save.";
    return;
  }
  bottleCalcSaveButton.disabled = false;
  bottleCalcSaveReasonEl.textContent = "";
}

/** Writes the calculated price into the same Sell-price field the shared
 * "Save item" footer button already reads (bottlePriceInput for a
 * per-ounce item, bottleUnitPriceInput for a whole-unit item), then runs
 * the ordinary saveBottleForm() flow: the identical PATCH, the identical
 * server-side admin.liquor_product.manage check, and the identical error
 * handling every other field on this sheet already gets. No new route,
 * no new permission, no new money path (CLAUDE.md's money-never-typed
 * rule does not apply to a catalog price field, per the brief). */
let bottleCalculatorSaveSequence = 0;

async function saveBottleCalculatorPrice(): Promise<void> {
  if (bottleCalcSaveButton.disabled) return;
  const editor = f3SellingUi.editor;
  if (editor?.uncertain) {
    const originalCalculation =
      editor.body?.sellingSettings?.calculatedPrice ?? editor.body?.calculatedPrice;
    await saveBottleForm(originalCalculation ?? undefined);
    return;
  }
  const result = currentBottleCalculatorResult();
  const itemId = editingBottleId;
  const formVisit = bottleStockLoadToken;
  const requestId = ++bottleCalculatorSaveSequence;
  const loadGen = loadGeneration;
  const activeSession = sessionOrUndefined();
  if (result === null || itemId === undefined || activeSession === undefined) return;
  const mechanism = bottleCalculatorSaveMechanism();
  if (mechanism === "whole_unit" && result.perBottle !== null) {
    bottleUnitPriceInput.value = centsToAmountString(result.perBottle.newPriceCents);
    await saveBottleForm();
    return;
  }
  if (mechanism !== "per_ounce" || result.perOunce === null || settingsPricingModel === "shelf")
    return;
  const targetMarkupPercent = Number(bottleCalcOzTargetMarkupInput.value);
  const sizeValue = Number(bottleSizeValueInput.value);
  const sizeUnit = bottleSizeUnitInput.value;
  bottleCalcSaveButton.disabled = true;
  const preview = await previewCalculatedCatalogPrice(
    activeSession.apiBaseUrl,
    activeSession.tenantId,
    itemId,
    {
      targetMarkupPercent,
      sizeValue,
      sizeUnit,
      ...(f3SellingUi.editor?.siteId === undefined
        ? {}
        : { locationId: f3SellingUi.editor.siteId, sellingVersion: f3SellingUi.editor.version }),
    },
  );
  const isCurrent = (): boolean =>
    requestId === bottleCalculatorSaveSequence &&
    loadIsCurrent(loadGen) &&
    !bottleFormScrim.hidden &&
    bottleStockLoadToken === formVisit &&
    sessionOrUndefined() === activeSession &&
    editingBottleId === itemId &&
    bottleSellingCurrent();
  if (!isCurrent()) return;
  if (
    preview.status !== 200 ||
    preview.body.previewToken === null ||
    preview.body.priceCents === null
  ) {
    bottleFormError.textContent = preview.body.message ?? "Cost unavailable.";
    renderBottleCalculator();
    return;
  }
  if (
    preview.body.priceCents !== result.perOunce.newPriceCents ||
    Number(bottleCalcOzTargetMarkupInput.value) !== targetMarkupPercent ||
    Number(bottleSizeValueInput.value) !== sizeValue ||
    bottleSizeUnitInput.value !== sizeUnit
  ) {
    bottleFormError.textContent = "The calculator inputs changed. Calculate the price again.";
    await loadBottleVendorOffers(itemId);
    return;
  }
  await saveBottleForm({ targetMarkupPercent, previewToken: preview.body.previewToken }, isCurrent);
}

// VEN6 (docs/build-briefs/VEN6-remainder-preferred-and-order-unit.md;
// design/mockups/ven6-preferred-consolidation-and-order-unit.html,
// owner-approved 2026-09-29). A fixed, five-option vocabulary: an order
// unit is a physical-world constant ("a case is a case" everywhere), not
// a per-tenant preference, so it is a closed enum here rather than an
// admin-editable vocabulary the way liquor/vendor lists are (same class
// as DELIVERY_DAY_OPTIONS above). Value is the canonical lowercase form
// stored on the offer; label is what the picker and any display show.
const ORDER_UNIT_OPTIONS: readonly { value: string; label: string }[] = [
  { value: "bottle", label: "Bottle" },
  { value: "case", label: "Case" },
  { value: "keg", label: "Keg" },
  { value: "can", label: "Can" },
  { value: "unit", label: "Unit" },
];

/** A vendor offer written before this picker existed can hold ANY free
 * text in order_unit (the field's old shape). Mapped case-insensitively
 * onto the fixed vocabulary's own label for display; a value that does
 * not match any of the five is shown exactly as stored rather than
 * silently coerced or hidden -- CLAUDE.md's "never fall back to a
 * hardcoded figure" reasoning applied to vocabulary display, not just
 * money. Never used to CHOOSE what gets saved: a legacy value on an
 * existing offer is kept as-is unless the manager changes it (this
 * function is display-only; there is no edit-existing-offer route to
 * plug the canonical value back into today). */
function orderUnitDisplayLabel(rawOrderUnit: string): string {
  const match = ORDER_UNIT_OPTIONS.find(
    (option) => option.value === rawOrderUnit.trim().toLowerCase(),
  );
  return match === undefined ? rawOrderUnit : match.label;
}

function renderBottleVendorOfferList(): void {
  bottleVendorOfferListEl.innerHTML = "";
  const active = sessionOrUndefined();
  const canManage = active !== undefined && hasPermission(active, "inv.vendor_offer.manage");
  const edit = currentVendorEdit();
  const pendingAdd = currentVendorAdd();
  const locked = edit !== undefined || pendingAdd !== undefined;
  const readable = bottleVendorTermsReadable();
  if (!readable) clearBottleVendorAddFields();
  for (const input of [
    bottleVendorOfferVendorSelect,
    bottleVendorOfferSkuInput,
    bottleVendorOfferOrderUnitInput,
    bottleVendorOfferPackSizeInput,
    bottleVendorOfferCostInput,
  ])
    input.disabled = locked || !canManage || !readable;
  bottleVendorOfferAddButton.textContent =
    pendingAdd?.busy === true
      ? "Adding"
      : pendingAdd === undefined
        ? "Add Vendor Option"
        : "Retry original Add";
  bottleVendorOfferAddButton.disabled =
    !canManage || edit !== undefined || pendingAdd?.busy === true || (!readable && pendingAdd === undefined);
  if (
    pendingAdd !== undefined &&
    active !== undefined &&
    hasPermission(active, "inv.vendor_offer.view") &&
    readable
  ) {
    bottleVendorOfferStatusEl.textContent =
      pendingAdd.message ||
      "The original Add and its location are retained until its outcome is confirmed.";
    bottleVendorOfferVendorSelect.value = pendingAdd.body.vendorId;
    bottleVendorOfferSkuInput.value = pendingAdd.body.vendorSku ?? "";
    bottleVendorOfferOrderUnitInput.value = pendingAdd.body.orderUnit ?? "";
    bottleVendorOfferPackSizeInput.value = pendingAdd.body.packSize?.toString() ?? "";
    bottleVendorOfferCostInput.value =
      pendingAdd.body.costCents === undefined ? "" : (pendingAdd.body.costCents / 100).toFixed(2);
  }
  refreshVendorOfferFormCues();
  // VENDORTABLE1: the add form stays closed while the item already has vendors, so the vendors are the first thing seen.
  // It opens for an item with no vendors yet, while an Add is retained, or when the person taps "+ Add another vendor".
  const addCard = document.querySelector<HTMLElement>("#admin-bottle-vendor-offer-add-card");
  const addToggle = document.querySelector<HTMLButtonElement>("#admin-bottle-vendor-offer-add-open");
  if (addCard !== null && addToggle !== null) {
    const forced = bottleVendorOffers.length === 0 || pendingAdd !== undefined;
    const showAdd = forced || vendorAddFormOpen;
    addCard.hidden = !showAdd;
    addToggle.hidden = forced;
    addToggle.setAttribute("aria-expanded", showAdd ? "true" : "false");
    addToggle.textContent = showAdd ? "Close the add form" : "+ Add another vendor";
    addToggle.disabled = !canManage || edit !== undefined;
  }
  if (edit !== undefined && !bottleVendorOffers.some((offer) => offer.id === edit.offerId)) {
    const recovery = document.createElement("div");
    recovery.append(renderVendorTermsEditor(edit));
    bottleVendorOfferListEl.append(recovery);
  }
  if (bottleVendorOffers.length === 0) {
    if (edit === undefined)
      bottleVendorOfferListEl.innerHTML = '<p class="tabs-empty">No Vendor Options yet.</p>';
    return;
  }
  const table = document.createElement("div");
  table.className = "admin-vendor-offer-table";
  table.setAttribute("role", "table");
  table.setAttribute("aria-label", "Vendor Options");
  const head = document.createElement("div");
  head.className = "admin-vendor-offer-head";
  head.setAttribute("role", "row");
  for (const label of ["Vendor", "Cost", "Pack", "SKU", ""]) {
    const cell = document.createElement("span");
    cell.setAttribute("role", "columnheader");
    cell.textContent = label;
    if (label === "") cell.setAttribute("aria-label", "Actions");
    head.append(cell);
  }
  table.append(head);
  const notSet = (): HTMLSpanElement => {
    const badge = document.createElement("span");
    badge.className = "admin-vendor-offer-notset";
    badge.dataset["testid"] = "admin-vendor-offer-notset";
    badge.textContent = "Not set";
    return badge;
  };
  const cellOf = (): HTMLDivElement => {
    const cell = document.createElement("div");
    cell.className = "admin-vendor-offer-cell";
    cell.setAttribute("role", "cell");
    return cell;
  };
  for (const offer of sortByName(bottleVendorOffers, (listed) =>
    vendorNameById(listed.vendor_id),
  )) {
    const row = document.createElement("div");
    row.className = "admin-vendor-offer-row";
    if (offer.preferred && offer.active) row.classList.add("admin-vendor-offer-row--preferred");
    if (!offer.active) row.classList.add("admin-vendor-offer-row--retired");
    row.setAttribute("role", "row");
    row.dataset["testid"] = "admin-vendor-offer-row";
    row.dataset["vendorOfferId"] = offer.id;
    const terms = bottleEffectiveTerms.get(offer.id);

    const grid = document.createElement("div");
    grid.className = "admin-vendor-offer-grid";
    row.append(grid);

    // Vendor: the name, with its status badges beneath it.
    const vendorCell = cellOf();
    const name = document.createElement("span");
    name.className = "admin-vendor-offer-name";
    name.dataset["testid"] = "admin-vendor-offer-summary";
    name.textContent = vendorNameById(offer.vendor_id);
    vendorCell.append(name);
    if (offer.preferred) {
      const badge = document.createElement("span");
      badge.className = "admin-vendor-offer-preferred-badge";
      badge.dataset["testid"] = "admin-vendor-offer-preferred-badge";
      badge.textContent = "Preferred";
      vendorCell.append(badge);
    }
    if (!offer.active) {
      const retiredBadge = document.createElement("span");
      retiredBadge.className = "admin-vendor-offer-retired-badge";
      retiredBadge.dataset["testid"] = "admin-vendor-offer-retired-badge";
      retiredBadge.textContent = "Retired";
      vendorCell.append(retiredBadge);
    }
    grid.append(vendorCell);

    // Cost: the price, with the order unit beneath it. A missing price is an amber "Not set", never blank.
    const costCell = cellOf();
    if (terms === undefined) {
      const unavailable = document.createElement("span");
      unavailable.className = "admin-vendor-offer-muted";
      unavailable.textContent = "–";
      costCell.append(unavailable);
    } else if (terms.costCents === null) costCell.append(notSet());
    else {
      const cost = document.createElement("span");
      cost.className = "admin-vendor-offer-cost";
      cost.textContent = formatCents(terms.costCents);
      costCell.append(cost);
    }
    if (offer.order_unit !== null) {
      const unit = document.createElement("span");
      unit.className = "admin-vendor-offer-muted";
      unit.textContent = `per ${orderUnitDisplayLabel(offer.order_unit)}`;
      costCell.append(unit);
    }
    grid.append(costCell);

    // Pack. INVUNIT1: a case reads "12 bottles per case" (the same pack size the cost is divided by). The older
    // "units per case" is shown only when it disagrees with the pack size, so a mismatch stays visible.
    const packCell = cellOf();
    if (offer.pack_size === null) packCell.append(notSet());
    else {
      const pack = document.createElement("span");
      pack.textContent =
        offer.order_unit?.trim().toLowerCase() === "case"
          ? `${offer.pack_size} ${vendorOfferItemNoun()}s per case`
          : offer.pack_size;
      packCell.append(pack);
    }
    if (offer.units_per_case !== null && String(offer.units_per_case) !== offer.pack_size) {
      const upc = document.createElement("span");
      upc.className = "admin-vendor-offer-muted";
      upc.textContent = `${offer.units_per_case} units per case`;
      packCell.append(upc);
    }
    grid.append(packCell);

    // SKU.
    const skuCell = cellOf();
    if (terms === undefined) {
      const unavailable = document.createElement("span");
      unavailable.className = "admin-vendor-offer-muted";
      unavailable.textContent = "–";
      skuCell.append(unavailable);
    } else if (terms.vendorSku === null) skuCell.append(notSet());
    else skuCell.append(document.createTextNode(terms.vendorSku));
    grid.append(skuCell);

    // Action: one Edit button. Make preferred and Retire live inside the Edit panel.
    const actionCell = cellOf();
    actionCell.classList.add("admin-vendor-offer-action");
    if (edit?.offerId !== offer.id && offer.active && canManage) {
      const editButton = vendorOptionButton(
        "Edit",
        () => startVendorTermsEdit(offer),
        locked ||
          bottleTermsContext === undefined ||
          (bottleTermsContext.state === "enforced" && terms === undefined),
      );
      editButton.dataset["testid"] = "admin-vendor-offer-edit";
      editButton.setAttribute("aria-label", `Edit ${vendorNameById(offer.vendor_id)}`);
      actionCell.append(editButton);
    }
    grid.append(actionCell);

    if (edit?.offerId === offer.id) {
      const panel = document.createElement("div");
      panel.className = "admin-vendor-offer-edit-panel";
      panel.append(renderVendorTermsEditor(edit));
      const settled =
        !edit.busy && edit.body === undefined && edit.shared?.body === undefined && !edit.notApplied;
      if (offer.active && canManage) panel.append(renderVendorOfferEditActions(offer, !settled));
      row.append(panel);
    }
    table.append(row);
  }
  bottleVendorOfferListEl.append(table);
}

// VENDORTABLE1: Make preferred and Retire live inside the Edit panel, one button per row in the table. The preferred
// toggle is the same VEN6 switch (same write, confirm and testids); Retire is the same confirm sheet and write.
function renderVendorOfferEditActions(offer: VendorItemOfferDto, disabled: boolean): HTMLElement {
  const box = document.createElement("div");
  box.className = "admin-vendor-offer-edit-actions";
  if (offer.active) {
    const toggle = renderVendorOfferPreferredToggle(offer);
    const control = toggle.querySelector<HTMLInputElement>("input");
    if (control !== null && disabled) control.disabled = true;
    box.append(toggle);
  }
  const retireButton = document.createElement("button");
  retireButton.type = "button";
  retireButton.className = "btn-secondary btn-compact";
  retireButton.dataset["testid"] = "admin-vendor-offer-retire";
  retireButton.textContent = "Retire";
  retireButton.disabled = disabled;
  retireButton.addEventListener("click", () => void handleBottleVendorOfferRetire(offer.id));
  box.append(retireButton);
  return box;
}

// VENDORTABLE1: whether the person has opened the "+ Add another vendor" form. Reset when an offer is added.
let vendorAddFormOpen = false;

// VEN6: built through the SHARED renderActivationSwitch (check:
// activation-control's own Rule 3 refuses a hand-built .admin-toggle-
// input outside that helper, for exactly the reason this item exists to
// fix: a switch wired straight to its write function with no shared
// guarantees). "Active" here means "preferred." The deactivate direction
// (config.write called with nextActive === false) is defensive only --
// the row that IS preferred is disabled immediately below, so a manager
// can never reach it through the UI; there is no "unpreferred with no
// replacement" state, matching the partial unique index's own "at most
// one preferred, never zero once one exists." The activate direction
// (nextActive === true) reuses the SAME incumbent-clear-then-set swap
// handleBottleVendorOfferMakePreferred already established.
function renderVendorOfferPreferredToggle(offer: VendorItemOfferDto): HTMLLabelElement {
  const toggle = renderActivationSwitch({
    active: offer.preferred,
    entityLabel: `${vendorNameById(offer.vendor_id)} as the preferred Vendor Option`,
    toggleTestId: "admin-vendor-offer-preferred-toggle",
    statusTestId: "admin-vendor-offer-preferred-status",
    activeText: "Preferred",
    inactiveText: "Not preferred",
    confirmDeactivate: (entityLabel) => ({
      title: `Remove ${entityLabel}?`,
      message: "This item would then have no preferred Vendor Option.",
      confirmLabel: "Remove",
    }),
    write: async (nextActive) => {
      if (!nextActive) {
        // Unreachable through the UI (the input is disabled below while
        // offer.preferred is true), kept only so this config is honest
        // about what it refuses rather than silently no-op-ing.
        return { ok: false, message: "Assign a different preferred Vendor Option instead." };
      }
      const s = session();
      const incumbent = bottleVendorOffers.find(
        (candidate) => candidate.preferred && candidate.id !== offer.id,
      );
      if (incumbent !== undefined) {
        const clearResult = await updateVendorItemOffer(s.apiBaseUrl, s.tenantId, incumbent.id, {
          preferred: false,
        });
        if (clearResult.status !== 200) {
          return {
            ok: false,
            message: clearResult.body.message ?? "Check your connection.",
          };
        }
      }
      const result = await updateVendorItemOffer(s.apiBaseUrl, s.tenantId, offer.id, {
        preferred: true,
      });
      if (result.status !== 200) {
        // A genuine race (a second admin made a DIFFERENT offer
        // preferred between the clear above and this call) surfaces here
        // in plain language rather than being pre-empted; this list is
        // the honest current state, not a guarantee against a concurrent
        // edit.
        return {
          ok: false,
          message: result.body.message ?? "Check your connection.",
        };
      }
      bottleVendorOffers = bottleVendorOffers.map((candidate) => ({
        ...candidate,
        preferred: candidate.id === offer.id,
      }));
      return { ok: true, active: true };
    },
    onError: (message) => {
      bottleVendorOfferStatusEl.textContent = `Could not make this the preferred Vendor Option. ${capitalizeServerMessage(message)}`;
    },
    onChange: () => {
      bottleVendorOfferStatusEl.textContent = "";
      // Full re-render rather than patching this one row in place: the
      // INCUMBENT row's own toggle (now not-preferred, no longer
      // disabled) needs to change too, and a fresh render is the same
      // "rebuild the whole list from bottleVendorOffers" shape every
      // other write on this list already uses.
      renderBottleVendorOfferList();
      // INVLOC1 Slice 5: the franchisee default moved, so each location on the default now resolves differently.
      if (editingBottleId !== undefined) void loadBottlePreferredByLocation(editingBottleId);
    },
  });
  // An inactive (retired), not-yet-preferred Vendor Option cannot be made
  // preferred at all (createVendorItemOffer's own VendorInactiveError
  // reasoning applied to updates too); the currently preferred Vendor Option is
  // locked on, per the comment above.
  const input = toggle.querySelector<HTMLInputElement>(".admin-toggle-input");
  if (input !== null) {
    input.disabled = offer.preferred || !offer.active;
  }
  return toggle;
}

// VEN6: the partial unique index (migration 0076) allows at most ONE
// preferred Vendor Option per product at a time, so "make this one preferred"
// while another already is has to be a two-step swap: clear the
// incumbent first, then set the new one. This is now inlined in
// renderVendorOfferPreferredToggle's own `write` (the toggle IS the one
// control that can trigger it, per VEN6's consolidation), rather than a
// separate function -- the standalone "Make preferred" button this used
// to serve no longer exists. Found live by
// e2e/inv4-vendor-management.spec.ts, which recorded the exact server
// refusal a single-step call produces ("this product already has a
// preferred Vendor Option; unset it before assigning a new one") before
// the original INV4 fix.

async function handleBottleVendorOfferRetire(offerId: string): Promise<void> {
  // VENDORTABLE1: Retire is pressed from inside this offer's own Edit panel, so that one edit is allowed (and ends with
  // the retire); any other open edit or a retained Add still blocks it, as before.
  const openEdit = currentVendorEdit();
  if (openEdit !== undefined && (openEdit.offerId !== offerId ||
      openEdit.busy ||
      openEdit.body !== undefined ||
      openEdit.shared?.body !== undefined))
    return;
  if (currentVendorAdd() !== undefined) return;
  const s = session();
  const scope = vendorScopeKey();
  const confirmed = await showConfirmSheet({
    title: "Retire this Vendor Option?",
    message:
      "The option remains in history and cannot be selected for new orders. Locations using it return to their default preference.",
    confirmLabel: "Retire",
    cancelLabel: "Keep option",
    tone: "accent",
  });
  if (!confirmed || vendorScopeKey() !== scope || sessionOrUndefined() !== s) return;
  bottleVendorOfferStatusEl.textContent = "";
  const result = await deactivateVendorItemOffer(s.apiBaseUrl, s.tenantId, offerId);
  if (vendorScopeKey() !== scope || sessionOrUndefined() !== s) return;
  if (result.status !== 200) {
    bottleVendorOfferStatusEl.textContent = `Could not retire this Vendor Option. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`;
    return;
  }
  const index = bottleVendorOffers.findIndex((candidate) => candidate.id === offerId);
  if (index !== -1) {
    bottleVendorOffers[index] = result.body.vendorItemOffer;
  }
  if (openEdit !== undefined) {
    const editKey = vendorScopeKey();
    if (editKey !== undefined && currentVendorEdit() === openEdit) vendorTermsEdits.delete(editKey);
  }
  renderBottleVendorOfferList();
  // INVLOC1 Slice 5: retiring an offer sends every location that chose it back to the default, so re-read the locations' choices.
  if (editingBottleId !== undefined) void loadBottlePreferredByLocation(editingBottleId);
}

// INVUNIT1 (settings-setup-01): set to the offer the manager has already been asked about, so the second press of
// Add saves it as typed. Any change to the order unit, count or price changes the key and asks again.
let vendorOfferCostConfirmedKey: string | undefined;

function vendorOfferItemNoun(): string {
  const container = bottleContainerTypeSelect.value;
  return container === "can" || container === "keg" ? container : "bottle";
}

/** Keeps the Vendor Option form's wording and its live price line true to what is typed. Pure display: nothing here
 * changes what is sent. */
function refreshVendorOfferFormCues(): void {
  const orderUnit = bottleVendorOfferOrderUnitInput.value.trim();
  const noun = vendorOfferItemNoun();
  const isCase = orderUnit === "case";
  bottleVendorOfferCaseRow.hidden = !isCase;
  bottleVendorOfferPackNounEl.textContent = `${noun}s`;
  const sizeValue = bottleSizeValueInput.value.trim();
  const sizeUnit = bottleSizeUnitInput.value.trim();
  if (bottleContainerTypeSelect.value === "keg") {
    // KEGMATH1: a keg is the keg size, not the Size field (that is the serving).
    const kegSize = bottleDefaultKegSizeSelect.value;
    const kegEntry = (adminVocabularies["keg_size"] ?? []).find((entry) => entry.value === kegSize);
    const kegVolume = kegSizeVolumeOzByValue().get(kegSize);
    bottleVendorOfferSizeNoteEl.textContent =
      kegEntry !== undefined && kegVolume !== undefined
        ? `Each keg is a ${kegEntry.label.toLowerCase()} (${Math.round(Number(kegVolume))} oz), from the item.`
        : "";
  } else {
    bottleVendorOfferSizeNoteEl.textContent =
      sizeValue.length > 0 && sizeUnit.length > 0
        ? `Each ${noun} is ${sizeValue} ${sizeUnit}, from the item.`
        : "";
  }
  const orderLabel = orderUnit.length === 0 ? "order unit" : orderUnit;
  bottleVendorOfferCostLabelEl.textContent = `Price for one ${orderLabel}`;
  let costCents: number | undefined;
  const rawCost = bottleVendorOfferCostInput.value.trim();
  if (rawCost.length > 0) {
    const asNumber = Number(rawCost);
    if (Number.isFinite(asNumber) && asNumber >= 0) costCents = Math.round(asNumber * 100);
  }
  const rawPack = bottleVendorOfferPackSizeInput.value.trim();
  const pack = rawPack.length > 0 && Number(rawPack) > 0 ? Number(rawPack) : undefined;
  const reading = readVendorOfferCost({
    orderUnit,
    costCents,
    bottlesPerCase: pack,
    containerType: bottleContainerTypeSelect.value,
  });
  if (reading === null || costCents === undefined) {
    bottleVendorOfferCostLineEl.textContent = "";
    return;
  }
  const priced = `${formatCents(costCents)} per ${reading.orderUnitNoun}`;
  if (reading.orderUnitNoun === "case") {
    bottleVendorOfferCostLineEl.textContent =
      reading.perItemCents === null
        ? `${priced}. Enter how many ${reading.itemNoun}s each case holds to see the price of one.`
        : `${priced} = ${formatCents(reading.perItemCents)} per ${reading.itemNoun}`;
  } else {
    bottleVendorOfferCostLineEl.textContent = priced;
  }
  if (
    reading.perItemCents !== null &&
    reading.orderUnitNoun === "case" &&
    (bottleFormItemKind === "liquor" || bottleFormItemKind === "wine") &&
    isImplausibleBottleCost(reading.perItemCents)
  ) {
    bottleVendorOfferCostLineEl.textContent += `. That looks off for one ${reading.itemNoun}: check the price and the count.`;
  }
}
for (const control of [
  bottleVendorOfferOrderUnitInput,
  bottleVendorOfferPackSizeInput,
  bottleVendorOfferCostInput,
]) {
  control.addEventListener("input", () => refreshVendorOfferFormCues());
  control.addEventListener("change", () => refreshVendorOfferFormCues());
}

async function handleAddBottleVendorOffer(): Promise<void> {
  if (editingBottleId === undefined) {
    return;
  }
  const s = sessionOrUndefined();
  const locationId = f3SellingUi.editor?.siteId;
  const key = vendorScopeKey();
  if (
    s === undefined ||
    key === undefined ||
    locationId === undefined ||
    currentVendorEdit() !== undefined
  ) {
    bottleVendorOfferStatusEl.textContent =
      "Choose an authorized location and finish any open Vendor Option edit first.";
    return;
  }
  const pending = currentVendorAdd();
  if (pending !== undefined) {
    await sendOriginalVendorAdd(key, pending);
    return;
  }
  if (!bottleVendorTermsReadable()) return;
  const vendorId = bottleVendorOfferVendorSelect.value;
  if (vendorId.length === 0) {
    bottleVendorOfferStatusEl.textContent = "Add a vendor in Settings > Purchasing first.";
    return;
  }
  let costCents: number | undefined;
  const rawCost = bottleVendorOfferCostInput.value.trim();
  if (rawCost.length > 0) {
    try {
      costCents = parseDollarsToCentsExact(rawCost, "Cost");
    } catch (error) {
      bottleVendorOfferStatusEl.textContent =
        error instanceof Error ? error.message : String(error);
      return;
    }
  }
  const orderUnitChoice = bottleVendorOfferOrderUnitInput.value.trim();
  const isCase = orderUnitChoice === "case";
  const rawPackSize = bottleVendorOfferPackSizeInput.value.trim();
  // INVUNIT1: "Each case holds N bottles" is asked only for a case, and N is saved as BOTH the pack size (the
  // divisor of the per-bottle cost) and the units per case, so the two can no longer disagree.
  const packSize = isCase && rawPackSize.length > 0 ? Number(rawPackSize) : undefined;
  bottleVendorOfferPackSizeInput.removeAttribute("aria-invalid");
  if (isCase && rawPackSize.length === 0) {
    bottleVendorOfferPackSizeInput.setAttribute("aria-invalid", "true");
    bottleVendorOfferStatusEl.textContent = `Enter how many ${vendorOfferItemNoun()}s each case holds.`;
    return;
  }
  if (packSize !== undefined && (!Number.isInteger(packSize) || packSize <= 0)) {
    bottleVendorOfferPackSizeInput.setAttribute("aria-invalid", "true");
    bottleVendorOfferStatusEl.textContent = `Each case holds a whole number of ${vendorOfferItemNoun()}s, 1 or more.`;
    return;
  }
  const unitsPerCase = packSize;
  const reading = readVendorOfferCost({
    orderUnit: orderUnitChoice,
    costCents,
    bottlesPerCase: packSize,
    containerType: bottleContainerTypeSelect.value,
  });
  if (
    reading?.perItemCents != null &&
    (bottleFormItemKind === "liquor" || bottleFormItemKind === "wine") &&
    isImplausibleBottleCost(reading.perItemCents)
  ) {
    const confirmKey = `${orderUnitChoice}|${packSize ?? ""}|${costCents}`;
    if (vendorOfferCostConfirmedKey !== confirmKey) {
      vendorOfferCostConfirmedKey = confirmKey;
      bottleVendorOfferStatusEl.textContent =
        `That is ${formatCents(reading.perItemCents)} a ${reading.itemNoun}. Is that right? ` +
        `Check the price and how many ${reading.itemNoun}s each case holds, then press Add Vendor Option again to save it as is.`;
      return;
    }
  }
  const vendorSku =
    bottleVendorOfferSkuInput.value.trim().length === 0
      ? undefined
      : bottleVendorOfferSkuInput.value.trim();
  const orderUnit =
    bottleVendorOfferOrderUnitInput.value.trim().length === 0
      ? undefined
      : bottleVendorOfferOrderUnitInput.value.trim();

  // Omit preferred so the first active relationship keeps automatic preference.
  const intent: VendorAddIntent = {
    actor: vendorActor(s),
    body: {
      ...buildVendorOfferRequest({
        vendorId,
        liquorProductId: editingBottleId,
        vendorSku,
        orderUnit,
        packSize,
        unitsPerCase,
        costCents,
      }),
      locationId,
      requestId: crypto.randomUUID(),
    },
    busy: false,
    message: "",
  };
  vendorAddIntents.set(key, intent);
  await sendOriginalVendorAdd(key, intent);
}
async function sendOriginalVendorAdd(key: string, intent: VendorAddIntent): Promise<void> {
  if (intent.busy || vendorScopeKey() !== key || !vendorActorMatches(intent.actor)) return;
  intent.busy = true;
  renderBottleVendorOfferList();
  const result = await createVendorItemOffer(
    intent.actor.apiBaseUrl,
    intent.actor.tenantId,
    intent.body,
  );
  intent.busy = false;
  const created = result.body.vendorItemOffer;
  if (
    result.status === 201 &&
    created?.liquor_product_id === intent.body.liquorProductId &&
    created.vendor_id === intent.body.vendorId
  ) {
    vendorAddIntents.delete(key);
    if (vendorScopeKey() !== key || !vendorActorMatches(intent.actor) || bottleFormScrim.hidden)
      return;
    bottleVendorOfferSkuInput.value = "";
    bottleVendorOfferOrderUnitInput.value = "";
    bottleVendorOfferPackSizeInput.value = "";
    bottleVendorOfferCostInput.value = "";
    vendorOfferCostConfirmedKey = undefined;
    refreshVendorOfferFormCues();
    await loadBottleVendorOffers(intent.body.liquorProductId);
    adminToast(`Added ${vendorNameById(intent.body.vendorId)} as a Vendor Option.`, "success");
  } else if (result.status === 400 || result.status === 422) {
    // VENDADDFIX1: a 400/422 is the server's validation refusal, so nothing was created and
    // the requestId was never used. Drop the intent, keep what was typed, and let the person
    // correct it; the next Add builds a fresh intent and requestId. Every other failure
    // (network, 401/403/409, 5xx, a 2xx that does not match) leaves the outcome unknown and
    // keeps the retry lock so the same request is re-sent unchanged.
    vendorAddIntents.delete(key);
    if (vendorScopeKey() !== key || !vendorActorMatches(intent.actor) || bottleFormScrim.hidden)
      return;
    renderBottleVendorOfferList();
    const rejection = result.body.message ?? "The server did not accept this Vendor Option.";
    bottleVendorOfferStatusEl.textContent = `Not added. ${capitalizeServerMessage(rejection)}`;
    if (rejection.includes('"packSize"'))
      bottleVendorOfferPackSizeInput.setAttribute("aria-invalid", "true");
    if (rejection.includes('"unitsPerCase"'))
      bottleVendorOfferPackSizeInput.setAttribute("aria-invalid", "true");
    return;
  } else {
    intent.message = `Original Add is unconfirmed. ${result.body.message ?? "Retry the original Add before changing these fields."}`;
  }
  if (vendorScopeKey() === key && vendorActorMatches(intent.actor) && !bottleFormScrim.hidden)
    renderBottleVendorOfferList();
}

// ---------------------------------------------------------------------
// M0: Settings > Void reasons. Owner ruling 2026-08-27, the reason list
// "should also be part of the site settings and configurable. Admins
// should be able to add and remove void reasons, but there should be
// some basic defaults added."
//
// "Remove" is DEACTIVATE, and there is no delete control in this file at
// all. A reason that months of past voids reference cannot be removed
// without orphaning every one of them and breaking the reporting M0
// exists to produce, which is the same distinction the owner already
// drew for user accounts. Not merely a missing button either: no delete
// route exists to call, and migration 0030's own trigger refuses a
// DELETE against this table outright.
//
// This is an ADMIN surface, so a keyboard and pointer are assumed (the
// owner's 2026-08-27 scoping of touch-only to the floor). An inline text
// field per row is legitimate here; a reason's name is genuinely free
// text, since this list IS the vocabulary rather than a choice from one,
// so the separate data-quality picker rule does not apply to it.
//
// Every action here acts immediately over its own API call, like
// Terminals below, and is NOT part of "Save location settings".
// ---------------------------------------------------------------------

/** Invalidates reads across this write without changing the shared switch contract. */
function beginVoidReasonMutation(): () => void {
  const gen = loadGeneration;
  settingsVoidReasonStatusEl.textContent = "";
  voidReasonMutationRevision += 1;
  voidReasonPendingWrites += 1;
  return () => {
    if (!loadIsCurrent(gen)) return;
    voidReasonMutationRevision += 1;
    voidReasonPendingWrites -= 1;
    // A successful local mutation already updates a complete list. Only an
    // interrupted initial read needs recovery, coalesced after the last write.
    if (voidReasonPendingWrites === 0 && !voidReasonListLoaded) void loadVoidReasons(true);
  };
}

async function loadVoidReasons(preserveMutationStatus = false): Promise<void> {
  applyReasonAddGating();
  if (voidReasonPendingWrites > 0) return;
  const gen = loadGeneration;
  const sequence = ++voidReasonReadSequence;
  const revision = voidReasonMutationRevision;
  const s = session();
  const result = await listVoidReasons(s.apiBaseUrl, s.tenantId, { activeOnly: false });
  // SET16-F4: this session's teardown ran while the fetch above was in
  // flight; discard rather than write over what invalidate() already
  // cleared.
  if (
    !loadIsCurrent(gen) ||
    sequence !== voidReasonReadSequence ||
    revision !== voidReasonMutationRevision ||
    voidReasonPendingWrites > 0
  )
    return;
  if (result.status !== 200) {
    if (!preserveMutationStatus || settingsVoidReasonStatusEl.textContent === "") {
      settingsVoidReasonStatusEl.textContent = `Could not load void reasons. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`;
    }
    return;
  }
  if (!preserveMutationStatus) settingsVoidReasonStatusEl.textContent = "";
  // A recovering initial read may finish while a newly added row is being
  // edited. Carry its unsaved text and focus across the complete-list render.
  const drafts = new Map<
    string,
    { value: string; focused: boolean; start: number | null; end: number | null }
  >();
  for (const row of settingsVoidReasonsEl.querySelectorAll<HTMLElement>("[data-void-reason-id]")) {
    const id = row.dataset["voidReasonId"]!;
    const input = row.querySelector<HTMLInputElement>("input[type=text]");
    const current = settingsVoidReasons.find((reason) => reason.id === id);
    if (input && (input.value !== current?.label || document.activeElement === input)) {
      drafts.set(id, {
        value: input.value,
        focused: document.activeElement === input,
        start: input.selectionStart,
        end: input.selectionEnd,
      });
    }
  }
  settingsVoidReasons = result.body.voidReasons;
  voidReasonListLoaded = true;
  renderVoidReasonList();
  for (const row of settingsVoidReasonsEl.querySelectorAll<HTMLElement>("[data-void-reason-id]")) {
    const draft = drafts.get(row.dataset["voidReasonId"]!);
    const input = row.querySelector<HTMLInputElement>("input[type=text]");
    if (!draft || !input) continue;
    input.value = draft.value;
    if (draft.focused) {
      input.focus({ preventScroll: true });
      input.setSelectionRange(draft.start, draft.end);
    }
  }
}

function applyReasonAddGating(): void {
  const canEdit = canManageLocationSettingsLists();
  settingsVoidReasonInput.disabled = !canEdit;
  settingsVoidReasonAddButton.disabled = !canEdit;
  settingsRefundReasonInput.disabled = !canEdit;
  settingsRefundReasonAddButton.disabled = !canEdit;
  // CASH1-S5: the shared action reason editors follow the same rule (CORE13-F1).
  for (const editor of actionReasonEditors) {
    editor.nameInput.disabled = !canEdit;
    editor.addButton.disabled = !canEdit;
  }
}

function renderVoidReasonList(): void {
  settingsVoidReasonsEl.innerHTML = "";
  applyReasonAddGating();
  const canEdit = canManageLocationSettingsLists();
  if (settingsVoidReasons.length === 0) {
    settingsVoidReasonsEl.innerHTML = '<p class="tabs-empty">No void reasons yet.</p>';
    return;
  }
  for (const reason of sortByName(settingsVoidReasons, (reason) => reason.label)) {
    const row = document.createElement("li");
    row.className = "admin-void-reason-row";
    row.dataset["testid"] = "admin-void-reason-row";
    row.dataset["voidReasonId"] = reason.id;

    const fields = document.createElement("div");
    fields.className = "admin-void-reason-fields";
    const nameInput = document.createElement("input");
    describeSetting(nameInput, "admin-settings-void-reasons-help");
    nameInput.type = "text";
    nameInput.className = "field-input admin-void-reason-name";
    nameInput.dataset["testid"] = "admin-void-reason-name";
    nameInput.value = reason.label;
    // The ATTRIBUTE as well as the property. The property is what an
    // Admin edits; the attribute is the row's stable, inspectable
    // identity for a test locator (and for anything reading the DOM),
    // and it is kept in step on rename below.
    nameInput.setAttribute("value", reason.label);
    nameInput.maxLength = 60;
    nameInput.setAttribute("aria-label", `Name for ${reason.label}`);
    nameInput.disabled = !canEdit;
    // Renames on commit (blur or Enter), never per keystroke: a
    // half-typed name must not become the reason a bartender sees or a
    // report groups by.
    nameInput.addEventListener("blur", () => void handleVoidReasonRename(reason.id, nameInput));
    nameInput.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        nameInput.blur();
      }
    });
    fields.append(nameInput);
    if (reason.is_shipped_default) {
      const shipped = document.createElement("span");
      shipped.className = "admin-void-reason-shipped";
      shipped.dataset["testid"] = "admin-void-reason-shipped";
      shipped.textContent = "Default";
      fields.append(shipped);
    }
    row.append(fields);

    // DEC-052: the same switch and confirm-before-deactivating flow as
    // every other reversible activate/deactivate control in Settings.
    // Genuinely two-directional: a retired reason can be turned back on,
    // because nothing depends on it having been continuously active.
    // Confirm copy reuses this section's own existing help-text wording.
    row.append(
      renderActivationSwitch({
        active: reason.active,
        entityLabel: reason.label,
        toggleTestId: "admin-void-reason-toggle",
        resolveCurrent: () => {
          if (!canManageLocationSettingsLists()) return undefined;
          const current = settingsVoidReasons.find((candidate) => candidate.id === reason.id);
          const currentRow = Array.from(settingsVoidReasonsEl.children).find(
            (candidate) => (candidate as HTMLElement).dataset["voidReasonId"] === reason.id,
          );
          const currentInput = currentRow?.querySelector<HTMLInputElement>('[data-testid="admin-void-reason-toggle"]');
          const currentStatus = currentRow?.querySelector<HTMLElement>('[data-testid="admin-void-reason-status-text"]');
          if (current === undefined || currentInput === null || currentInput === undefined ||
            currentStatus === null || currentStatus === undefined) return undefined;
          return { input: currentInput, status: currentStatus, active: current.active };
        },

        disabled: !canEdit,
        describedById: "admin-settings-void-reasons-help",
        statusTestId: "admin-void-reason-status-text",
        activeText: "On",
        inactiveText: "Off",
        confirmDeactivate: (name) => ({
          title: `Turn off ${name}?`,
          message: `Turning ${name} off takes it off the bartender's picker and leaves past voids readable.`,
          confirmLabel: "Turn off",
        }),
        write: async (nextActive) => {
          const s = session();
          const gen = loadGeneration;
          const finish = beginVoidReasonMutation();
          try {
            const result = await updateVoidReason(s.apiBaseUrl, s.tenantId, reason.id, {
              active: nextActive,
            });
            if (!loadIsCurrent(gen)) return { ok: false };
            if (result.status !== 200) {
              return { ok: false, message: result.body.message ?? "Check your connection." };
            }
            return { ok: true, active: result.body.voidReason.active };
          } finally {
            finish();
          }
        },
        onError: (message) => {
          settingsVoidReasonStatusEl.textContent = `Could not change this reason. ${capitalizeServerMessage(message)}`;
        },
        onChange: (active) => {
          const current = settingsVoidReasons.find((candidate) => candidate.id === reason.id);
          if (current !== undefined) current.active = active;
          settingsVoidReasonStatusEl.textContent = "";
          adminToast(
            active
              ? `${reason.label} is back on the bartender's list.`
              : `${reason.label} is off the bartender's list. Past voids still show it.`,
            "success",
          );
        },
      }),
    );

    settingsVoidReasonsEl.append(row);
  }
}

async function handleVoidReasonRename(
  voidReasonId: string,
  input: HTMLInputElement,
): Promise<void> {
  const reason = settingsVoidReasons.find((candidate) => candidate.id === voidReasonId);
  if (reason === undefined) {
    return;
  }
  const label = input.value.trim();
  if (label === reason.label) {
    input.value = reason.label;
    return;
  }
  if (label.length === 0) {
    input.value = reason.label;
    settingsVoidReasonStatusEl.textContent = "A void reason needs a name.";
    return;
  }
  settingsVoidReasonStatusEl.textContent = "";
  const s = session();
  const gen = loadGeneration;
  const finish = beginVoidReasonMutation();
  try {
    const result = await updateVoidReason(s.apiBaseUrl, s.tenantId, voidReasonId, { label });
    if (!loadIsCurrent(gen)) return;
    if (result.status !== 200) {
      input.value = reason.label;
      settingsVoidReasonStatusEl.textContent = `Could not rename this reason. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`;
      return;
    }
    reason.label = result.body.voidReason.label;
    input.value = reason.label;
    input.setAttribute("value", reason.label);
    // Renaming does not rewrite history: past voids keep the reference AND
    // the label they were taken under. Said plainly here because it is the
    // question an Admin actually has at this moment.
    adminToast(`Renamed to ${reason.label}. Past voids keep the old wording.`, "success");
  } finally {
    finish();
  }
}

async function handleVoidReasonAdd(): Promise<void> {
  const label = settingsVoidReasonInput.value.trim();
  if (label.length === 0) {
    settingsVoidReasonStatusEl.textContent = "Type the reason first.";
    return;
  }
  settingsVoidReasonStatusEl.textContent = "";
  settingsVoidReasonAddButton.disabled = true;
  const s = session();
  const gen = loadGeneration;
  const finish = beginVoidReasonMutation();
  try {
    const result = await createVoidReason(s.apiBaseUrl, s.tenantId, { label });
    if (!loadIsCurrent(gen)) return;
    settingsVoidReasonAddButton.disabled = !canManageLocationSettingsLists();
    if (result.status !== 201 && result.status !== 200) {
      settingsVoidReasonStatusEl.textContent = `Could not add this reason. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`;
      return;
    }
    settingsVoidReasons.push(result.body.voidReason);
    renderVoidReasonList();
    settingsVoidReasonInput.value = "";
    adminToast(`${result.body.voidReason.label} is on the bartender's list.`, "success");
  } finally {
    finish();
  }
}

// ---------------------------------------------------------------------
// Payments-track Slice 3: Settings > Payments > Terminals. See
// src/domain/terminals.ts's module header. The simulated provider
// self-provisions a simulated terminal with no external setup; register
// simply names it.
// ---------------------------------------------------------------------

async function loadTerminals(): Promise<void> {
  const gen = loadGeneration;
  const s = session();
  const result = await listTerminals(s.apiBaseUrl, s.tenantId);
  // SET16-F4: this session's teardown ran while the fetch above was in
  // flight; discard rather than write over what invalidate() already
  // cleared.
  if (!loadIsCurrent(gen)) return;
  if (result.status !== 200) {
    settingsTerminalStatusEl.textContent = `Could not load card readers. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`;
    return;
  }
  settingsTerminals = result.body.terminals;
  renderTerminalList();
}

function renderTerminalList(): void {
  settingsTerminalsEl.innerHTML = "";
  if (settingsTerminals.length === 0) {
    settingsTerminalsEl.innerHTML = '<p class="tabs-empty">No card readers registered yet.</p>';
    return;
  }
  for (const terminal of sortByName(settingsTerminals, (listed) => listed.display_name)) {
    const row = document.createElement("li");
    row.className = "admin-terminal-row";
    row.dataset["testid"] = "admin-terminal-row";
    row.dataset["terminalId"] = terminal.id;

    const info = document.createElement("div");
    info.className = "admin-terminal-info";
    const nameRow = document.createElement("div");
    nameRow.className = "admin-terminal-name-row";
    const name = document.createElement("span");
    name.className = "admin-terminal-name";
    name.textContent = terminal.display_name;
    name.dataset["testid"] = "admin-terminal-name-text";
    nameRow.append(name);
    // Amendment 2026-08-22 (ninth): touch-first edit affordance, a
    // separate tappable icon button rather than making the name text
    // itself the tap target (the row already has one whole-row action
    // pattern elsewhere in this file, .admin-toggle-label's own
    // stopPropagation; a dedicated icon avoids any ambiguity about what
    // a tap on the name does). State-gated to active terminals to match
    // src/domain/terminals.ts's renameTerminal precondition: an
    // inactive terminal's edit affordance is disabled, the same
    // one-directional "register a new one" disposition the Active
    // toggle already carries for deactivation.
    const renameButton = document.createElement("button");
    renameButton.type = "button";
    renameButton.className = "btn-icon admin-terminal-rename-button";
    renameButton.dataset["testid"] = "admin-terminal-rename-button";
    renameButton.setAttribute("aria-label", `Rename ${terminal.display_name}`);
    renameButton.textContent = "✎";
    renameButton.disabled = !terminal.active;
    renameButton.title = terminal.active
      ? ""
      : "Inactive card readers can't be renamed. Register a new one instead.";
    renameButton.addEventListener("click", () => openTerminalRenameSheet(terminal));
    nameRow.append(renameButton);
    const provider = document.createElement("div");
    provider.className = "admin-terminal-provider";
    // TERM2: device type and serial on one line, then the live connection
    // state as a chip that always carries its word. "Status unavailable" offers
    // a Check again button that reloads the list.
    provider.textContent = readerDetailLine(
      terminal.reader,
      terminal.provider_name === "simulated" ? "Simulated reader" : terminal.provider_name,
    );
    provider.dataset["testid"] = "admin-terminal-reader-line";
    const providerLine = document.createElement("div");
    providerLine.className = "admin-terminal-provider-line";
    const chipView = readerStatusChip(terminal.reader);
    const chip = document.createElement("span");
    chip.className = `reader-chip reader-chip-${chipView.tone}`;
    chip.dataset["testid"] = "admin-terminal-reader-chip";
    chip.textContent = chipView.label;
    providerLine.append(provider, chip);
    if (chipView.canRecheck && terminal.active) {
      const recheck = document.createElement("button");
      recheck.type = "button";
      recheck.className = "btn-secondary btn-compact reader-recheck-button";
      recheck.dataset["testid"] = "admin-terminal-reader-recheck";
      recheck.textContent = "Check again";
      recheck.addEventListener("click", () => {
        recheck.disabled = true;
        void loadTerminals();
      });
      providerLine.append(recheck);
    }
    // TERM4: which location this reader belongs to. A reader is picked and charged only at its own location.
    const locationLine = document.createElement("div");
    locationLine.className = "admin-terminal-provider";
    locationLine.dataset["testid"] = "admin-terminal-location-line";
    const locationWrap = document.createElement("div");
    locationWrap.className = "admin-terminal-provider-line";
    locationWrap.append(locationLine);
    locationLine.textContent =
      terminal.location_name === undefined || terminal.location_name === null
        ? ""
        : `Location: ${terminal.location_name}`;
    locationWrap.hidden = locationLine.textContent.length === 0;
    info.append(nameRow, providerLine, locationWrap);
    row.append(info);

    // Same toggle visual language as the Drinks/Bottles Active switch
    // (.admin-toggle-*), but one-directional: there is no "reactivate a
    // terminal" command in this slice (register a new one instead), so an
    // inactive terminal's toggle is permanently off and disabled.
    const toggleLabel = document.createElement("label");
    toggleLabel.className = "admin-toggle-label";
    toggleLabel.addEventListener("click", (event) => event.stopPropagation());
    const toggleInput = document.createElement("input");
    toggleInput.type = "checkbox";
    toggleInput.className = "admin-toggle-input";
    toggleInput.dataset["testid"] = "admin-terminal-toggle";
    toggleInput.checked = terminal.active;
    toggleInput.disabled = !terminal.active;
    toggleInput.setAttribute("aria-label", `${terminal.display_name} active`);
    const toggleTrack = document.createElement("span");
    toggleTrack.className = "admin-toggle-track";
    const toggleStatusText = document.createElement("span");
    toggleStatusText.className = "admin-toggle-status";
    toggleStatusText.dataset["testid"] = "admin-terminal-status";
    toggleStatusText.textContent = terminal.active ? "Active" : "Inactive";
    toggleInput.addEventListener(
      "change",
      () => void handleTerminalToggleChange(terminal.id, toggleInput),
    );
    toggleLabel.append(toggleInput, toggleTrack, toggleStatusText);
    row.append(toggleLabel);

    settingsTerminalsEl.append(row);
  }
}

async function handleTerminalToggleChange(
  terminalId: string,
  input: HTMLInputElement,
): Promise<void> {
  // Only the active -> inactive direction ever reaches here (the
  // checkbox is disabled once already inactive, per renderTerminalList),
  // but this mirrors handleDrinkToggleChange's own revert-then-confirm
  // shape for consistency: the checkbox has already flipped by the time
  // "change" fires, so it is reverted immediately and only actually
  // deactivates once the manager confirms.
  input.checked = true;
  const terminal = settingsTerminals.find((candidate) => candidate.id === terminalId);
  const terminalName = terminal?.display_name ?? "this card reader";
  const confirmed = await showConfirmSheet({
    title: `Turn off ${terminalName}?`,
    message: `${terminalName} will no longer be able to take a payment. This cannot be undone; register a new card reader to replace it.`,
    confirmLabel: "Turn off",
  });
  if (!confirmed) {
    return;
  }

  input.disabled = true;
  // SET16-F1: the confirm sheet above can sit open long enough for the
  // acting employee's session to actually die; session() would throw
  // rather than quietly return a stale one once it does, so this bails
  // out instead of surfacing as an unhandled rejection.
  const s = sessionOrUndefined();
  if (s === undefined) {
    input.disabled = false;
    return;
  }
  const result = await deactivateTerminal(s.apiBaseUrl, s.tenantId, terminalId, {
    idempotencyKey: crypto.randomUUID(),
  });
  if (result.status !== 200) {
    input.checked = true;
    input.disabled = false;
    adminToast(
      `Could not turn off ${terminalName}. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`,
      "danger",
    );
    return;
  }
  if (terminal !== undefined) {
    terminal.active = false;
    terminal.deactivated_at = result.body.terminal.deactivated_at;
  }
  input.checked = false;
  const row = settingsTerminalsEl.querySelector<HTMLElement>(
    `[data-terminal-id="${CSS.escape(terminalId)}"]`,
  );
  const statusText = row?.querySelector<HTMLElement>('[data-testid="admin-terminal-status"]');
  if (statusText !== null && statusText !== undefined) {
    statusText.textContent = "Inactive";
  }
  // Amendment 2026-08-22 (ninth): rename is state-gated to active
  // terminals, so the edit affordance disables the instant a terminal
  // goes inactive, matching what a full renderTerminalList() would have
  // produced without paying for a full re-render here.
  const renameButtonEl = row?.querySelector<HTMLButtonElement>(
    '[data-testid="admin-terminal-rename-button"]',
  );
  if (renameButtonEl !== null && renameButtonEl !== undefined) {
    renameButtonEl.disabled = true;
    renameButtonEl.title = "Inactive card readers can't be renamed. Register a new one instead.";
  }
  adminToast(`${terminalName} is now off.`, "success");
}

async function handleTerminalRegister(): Promise<void> {
  const displayName = settingsTerminalNameInput.value.trim();
  if (displayName.length === 0) {
    settingsTerminalStatusEl.textContent = "Enter a name for the card reader first.";
    return;
  }
  settingsTerminalStatusEl.textContent = "";
  settingsTerminalRegisterButton.disabled = true;
  const s = session();
  // TERM2: blank means the simulated reader, so the field is omitted rather
  // than sent empty. The code is a pairing code: never logged, never kept
  // after the request.
  const registrationCode = settingsTerminalCodeInput.value.trim();
  const result = await registerTerminal(s.apiBaseUrl, s.tenantId, {
    idempotencyKey: crypto.randomUUID(),
    displayName,
    ...(registrationCode.length === 0 ? {} : { registrationCode }),
  });
  settingsTerminalRegisterButton.disabled = false;
  if (result.status !== 201 && result.status !== 200) {
    settingsTerminalStatusEl.textContent = `Could not register this card reader. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`;
    return;
  }
  settingsTerminalNameInput.value = "";
  settingsTerminalCodeInput.value = "";
  // Re-read the list so the new reader's device, serial and status come from
  // the provider rather than from this response.
  await loadTerminals();
  adminToast(`${result.body.terminal.display_name} is registered and active.`, "success");
  // PAY1-S4: lets the floor take the location's only reader on this device (walkthrough item 14).
  window.dispatchEvent(new Event(CARD_READER_REGISTERED_EVENT));
}

// ---------------------------------------------------------------------
// Amendment 2026-08-22 (ninth): renameTerminal's touch-first edit
// affordance. A small dedicated sheet (not the full terminal-registration
// row's own inline controls), the same "one field, not a full form" shape
// #admin-confirm-scrim already establishes for a single-purpose sheet.
// ---------------------------------------------------------------------

function openTerminalRenameSheet(terminal: TerminalDto): void {
  terminalBeingRenamedId = terminal.id;
  terminalRenameTitleEl.textContent = `Rename ${terminal.display_name}`;
  terminalRenameInput.value = terminal.display_name;
  terminalRenameErrorEl.textContent = "";
  terminalRenameScrim.hidden = false;
  lockBodyScroll();
  // Selects the existing name so a manager can either type straight over
  // it or tap to append, mirroring the drink/bottle name field's own
  // first-tap behavior on this touch-only product.
  terminalRenameInput.focus();
  terminalRenameInput.select();
}

function closeTerminalRenameSheet(): void {
  if (terminalRenameScrim.hidden) {
    return;
  }
  terminalRenameScrim.hidden = true;
  unlockBodyScroll();
  terminalBeingRenamedId = undefined;
}

async function saveTerminalRename(): Promise<void> {
  const terminalId = terminalBeingRenamedId;
  if (terminalId === undefined) {
    return;
  }
  terminalRenameErrorEl.textContent = "";
  const displayName = terminalRenameInput.value.trim();
  if (displayName.length === 0) {
    terminalRenameErrorEl.textContent = "Card reader name is required.";
    return;
  }

  terminalRenameSaveButton.disabled = true;
  const s = session();
  const result = await renameTerminal(s.apiBaseUrl, s.tenantId, terminalId, {
    idempotencyKey: crypto.randomUUID(),
    displayName,
  });
  terminalRenameSaveButton.disabled = false;
  if (result.status !== 200) {
    terminalRenameErrorEl.textContent =
      result.body.message ?? result.body.error ?? "Could not rename this card reader.";
    return;
  }

  const terminal = settingsTerminals.find((candidate) => candidate.id === terminalId);
  if (terminal !== undefined) {
    terminal.display_name = result.body.terminal.display_name;
  }
  renderTerminalList();
  closeTerminalRenameSheet();
  adminToast(`Card reader renamed to ${result.body.terminal.display_name}.`, "success");
}

// R12 (DEC-025): changing the tax rate is its own right,
// admin.tax_rate.manage, separate from editing the rest of the location
// settings. Presentation only: the server refuses a rate change from a
// session without it regardless of what this screen shows.
function canChangeTaxRate(): boolean {
  const s = session();
  return s !== undefined && hasPermission(s, "admin.tax_rate.manage");
}

function renderSettings(): void {
  renderTipPresetChips();
  renderRoundingModeChips();
  renderPricingModelChips();
  renderStandardPourOuncesInput();
  renderShelfTierRows();

  // Payments-track Slice 4 (DEC-002), Finding 4. Empty (not "0.00") when
  // no hold amount is configured yet, matching holdAmountCents' own null-
  // until-set convention: an empty field reads as "not set", never a
  // silent zero-dollar hold.
  settingsHoldAmountInput.value =
    settingsHoldAmountCents === null ? "" : centsToAmountString(settingsHoldAmountCents);
  settingsHoldIncrementThresholdInput.value = basisPointsToPercentString(
    settingsHoldIncrementThresholdBasisPoints,
  );
  settingsAbandonedTabGratuityInput.value = basisPointsToPercentString(
    settingsAbandonedTabGratuityBasisPoints,
  );
  settingsAbandonedTabGratuityDisclosureInput.checked =
    settingsAbandonedTabGratuityDisclosureConfirmed;
  settingsCloseCardTicketsInput.checked = settingsCloseCardTicketsAtDayEnd;
  settingsDualPricingInput.checked = settingsDualPricingEnabled;
  settingsDualPricingPercentInput.value = (settingsDualPricingBasisPoints / 100).toFixed(2);
  settingsTipOnPreDiscountInput.checked = settingsTipOnPreDiscountPrice;
}

// VOC1-F9 (docs/build-briefs/VOC1-F9-price-rounding-mode-location-
// configurable.md Section 2.4). Display labels for api.ts's
// ROUNDING_MODES, kept local to this file (rather than catalogDisplay.ts's
// own PRICING_MODEL_LABELS home) since this item's edit scope is admin.ts
// itself; the identical shape either way.
const ROUNDING_MODE_LABELS: Readonly<Record<string, string>> = {
  "half-up-cent": "Half up to the cent",
};

function roundingModeLabel(roundingMode: string): string {
  return ROUNDING_MODE_LABELS[roundingMode] ?? roundingMode;
}

// VOC1-F9 (docs/build-briefs/VOC1-F9-price-rounding-mode-location-
// configurable.md Section 2.4). Same radiogroup chip pattern
// renderPricingModelChips below already uses: a fixed-vocabulary value is
// picked, never typed, even though ROUNDING_MODES carries exactly one
// legal value today (check:fixed-vocabulary-picker's own rule applies on
// admin screens too, per CLAUDE.md's non-negotiable data-quality rule).
function renderRoundingModeChips(): void {
  renderBottleFilterChipGroup(
    settingsRoundingModeEl,
    "admin-settings-rounding-mode-option",
    "admin-settings-rounding-mode-option",
    ROUNDING_MODES.map((mode) => ({ value: mode, label: roundingModeLabel(mode) })),
    settingsRoundingMode,
    (value) => {
      settingsRoundingMode = value as RoundingMode;
    },
  );
}

// Inventory track Slice 3, Amendment 2026-08-22 (thirteenth). Same
// radiogroup chip pattern renderBottleItemKindChips uses above (browser-
// enforced single-select, no hand-rolled state sync).
function renderPricingModelChips(): void {
  renderBottleFilterChipGroup(
    settingsPricingModelEl,
    "admin-settings-pricing-model-option",
    "admin-settings-pricing-model-option",
    PRICING_MODELS.map((model) => ({ value: model, label: pricingModelLabel(model) })),
    settingsPricingModel,
    (value) => {
      settingsPricingModel = value as PricingModel;
    },
    "admin-pricing-model-label",
  );
}

settingsStandardPourOuncesEl.addEventListener("input", () => {
  settingsStandardPourOunces = settingsStandardPourOuncesEl.value;
});

function renderStandardPourOuncesInput(): void {
  settingsStandardPourOuncesEl.value = settingsStandardPourOunces;
  settingsStandardPourOuncesEl.disabled = !settingsLoaded || !canManageBusinessDay();
}

// Client-side mirror of src/domain/pricing.ts's own parseOuncesToHundredths,
// the same "client mirror" precedent COUNT_QUANTITY_PATTERN's own comment
// already establishes elsewhere in this file. Returns null rather than
// throwing for a value this display-only comparison cannot parse (an
// empty string before settings load, or a pour_volume value carrying more
// than two decimal places, which a numeric(4,2) stored standard pour could
// never match anyway); null never equals another null result in the
// comparisons above, since each such value falls back to its own raw
// string key instead, matching the ORIGINAL string-identity behavior only
// for the cases parsed-amount comparison cannot apply to.
function parseOuncesToHundredthsClient(value: string): bigint | null {
  const trimmed = normalizeLeadingDot(value);
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) {
    return null;
  }
  const [wholePart, fractionalPartRaw] = trimmed.split(".");
  const fractionalPart = (fractionalPartRaw ?? "").padEnd(2, "0");
  return BigInt(wholePart as string) * 100n + BigInt(fractionalPart);
}

function renderTipPresetChips(): void {
  settingsTipPresetsEl.innerHTML = "";
  if (settingsTipPresets.length === 0) {
    const empty = document.createElement("p");
    empty.className = "admin-field-help";
    empty.textContent = "No tip choices yet. Add one to offer it at payment.";
    settingsTipPresetsEl.append(empty);
    return;
  }
  for (const preset of settingsTipPresets) {
    const chip = document.createElement("span");
    chip.className = "admin-chip";
    chip.dataset["testid"] = "admin-settings-tip-preset-chip";
    const percentText = preset % 100 === 0 ? String(preset / 100) : basisPointsToPercentString(preset);
    const label = document.createElement("span");
    label.textContent = `${percentText}%`;
    const removeButton = document.createElement("button");
    removeButton.type = "button";
    removeButton.className = "admin-chip-remove";
    removeButton.setAttribute("aria-label", `Remove ${percentText}% preset`);
    removeButton.dataset["testid"] = "admin-settings-tip-preset-remove";
    removeButton.textContent = "✕";
    removeButton.addEventListener("click", () => {
      settingsTipPresets = settingsTipPresets.filter((value) => value !== preset);
      renderTipPresetChips();
    });
    chip.append(label, removeButton);
    settingsTipPresetsEl.append(chip);
  }
}

function handleAddTipPreset(): void {
  const raw = settingsTipPresetInput.value.trim();
  if (raw.length === 0) {
    adminToast("Enter a percentage, e.g. 20 for 20%.", "danger");
    return;
  }
  let value: number;
  try {
    value = parsePercentToBasisPoints(raw, "Tip preset");
  } catch (error) {
    adminToast(error instanceof Error ? error.message : "Invalid tip preset.", "danger");
    return;
  }
  if (value % 100 !== 0) {
    adminToast("Tip choices must be whole percents, such as 20 or 22. The card reader cannot show 22.5%.", "danger");
    return;
  }
  if (value > MAX_TIP_PRESET_BASIS_POINTS) {
    adminToast("A tip preset can't be more than 100%.", "danger");
    return;
  }
  if (settingsTipPresets.length >= MAX_TIP_CHOICES_ON_SAVE) {
    adminToast(
      `You can only have up to ${MAX_TIP_CHOICES_ON_SAVE} tip choices. The same choices show on the tablet Pay screen and the card reader.`,
      "danger",
    );
    return;
  }
  if (settingsTipPresets.includes(value)) {
    adminToast("That preset is already in the list.", "danger");
    return;
  }
  settingsTipPresets = [...settingsTipPresets, value].sort((a, b) => a - b);
  settingsTipPresetInput.value = "";
  renderTipPresetChips();
}

function renderShelfTierRows(): void {
  settingsShelfTiersEl.innerHTML = "";
  const canEdit = canEditVocabulary("shelf_tier");
  for (const [tierIndex, tier] of settingsShelfTiers.entries()) {
    const positionLabel = shelfTierPositionLabel(tierIndex);
    const row = document.createElement("div");
    row.className = "admin-shelf-tier-row";
    row.dataset["testid"] = "admin-settings-shelf-tier-row";
    // ONB2 S4: the stored key rides on the row as data; the person reads "Tier 1", never "tier_1".
    row.dataset["tier"] = tier.tier;

    const key = document.createElement("span");
    key.className = "admin-shelf-tier-key";
    key.textContent = positionLabel;
    row.append(key);

    const nameInput = document.createElement("input");
    describeSetting(nameInput, "admin-settings-shelf-tiers-help");
    nameInput.className = "field-input admin-shelf-tier-name-input";
    nameInput.type = "text";
    nameInput.value = tier.name;
    nameInput.setAttribute("aria-label", `${positionLabel} name`);
    nameInput.dataset["testid"] = "admin-settings-shelf-tier-name";
    nameInput.disabled = !canEdit;
    nameInput.addEventListener("input", () => {
      tier.name = nameInput.value;
    });
    row.append(nameInput);

    // Amendment 2026-08-22 (second): dollars and cents, never raw
    // integer cents, with a visible "$" affordance and exactness on
    // blur (reformats to the canonical two-decimal string on success;
    // an entry that cannot convert losslessly to integer cents, e.g. a
    // third decimal digit, is rejected in settingsSaveStatusMoneyEl by name
    // rather than silently rounded, matching the tax-rate field's own
    // pattern). tier.priceCents only ever changes on a SUCCESSFUL parse,
    // so a rejected in-progress edit never corrupts the saved value;
    // saveSettingsForm below re-parses straight from this input's live
    // DOM value at save time too, so an edit that never blurred (Save
    // tapped directly) is still caught rather than silently saving a
    // stale number underneath an unaccepted display string.
    const priceWrap = document.createElement("span");
    priceWrap.className = "field-input-affix field-input-affix--prefix";
    const priceMark = document.createElement("span");
    priceMark.className = "field-input-affix-mark";
    priceMark.setAttribute("aria-hidden", "true");
    priceMark.textContent = "$";
    const priceInput = document.createElement("input");
    describeSetting(priceInput, "admin-settings-shelf-tiers-help");
    priceInput.className = "field-input admin-shelf-tier-price-input";
    priceInput.type = "number";
    priceInput.inputMode = "decimal";
    priceInput.step = "0.01";
    priceInput.min = "0";
    const settingScope = f3SellingUi.settings;
    const localPrice = settingScope?.shelves.get(tier.tier)?.priceCents;
    priceInput.value = settingScope?.context.readiness.state === "enforced"
      ? (localPrice == null ? "" : centsToAmountString(localPrice))
      : settingScope?.context.readiness.state === "preparation" ? centsToAmountString(tier.priceCents) : "";
    priceInput.setAttribute("aria-label", `${positionLabel} price in dollars`);
    priceInput.dataset["testid"] = "admin-settings-shelf-tier-price";
    priceInput.disabled = !canEdit || f3SellingUi.settings === undefined ||
      (f3SellingUi.settings.context.readiness.state === "enforced" && (!hasPermission(session(), "admin.liquor_product.manage") || f3SellingUi.settings.context.locationId === null));
    priceInput.addEventListener("blur", () => {
      const raw = priceInput.value.trim();
      if (raw.length === 0) {
        if (f3SellingUi.settings?.context.readiness.state !== "enforced") priceInput.value = centsToAmountString(tier.priceCents);
        return;
      }
      try {
        const label = `${tier.name.trim().length > 0 ? tier.name.trim() : shelfTierKeyLabel(tier.tier)} price`;
        tier.priceCents = parseDollarsToCentsExact(raw, label);
        priceInput.value = centsToAmountString(tier.priceCents);
        settingsSaveStatusMoneyEl.textContent = "";
      } catch (error) {
        settingsSaveStatusMoneyEl.textContent =
          error instanceof Error ? error.message : "Invalid shelf tier price.";
      }
    });
    priceWrap.append(priceMark, priceInput);
    row.append(priceWrap);

    // B7-S3 (build brief Section 2.2): the same shared activation
    // switch renderVocabulary already uses, not a hand-built toggle
    // (required by check:activation-control). Unlike renderVocabulary,
    // the tier's name <input> above is left untouched on retirement: it
    // is a live, editable field a manager can still rename while the
    // tier is retired, and injecting "(off)" into its value risks that
    // decoration being typed back as part of the real name on save. The
    // switch's own status text is sufficient state signal.
    row.append(
      renderActivationSwitch({
        active: tier.active,
        entityLabel: tier.name,
        toggleTestId: "admin-settings-shelf-tier-toggle",
        disabled: !canEdit,
        describedById: "admin-settings-shelf-tier-switch-help",
        statusTestId: "admin-settings-shelf-tier-status-text",
        activeText: "On",
        inactiveText: "Off",
        confirmDeactivate: (name) => ({
          title: `Turn off ${name}?`,
          message: `${name} stops being offered when assigning a new item's shelf. Any item already on ${name} keeps pricing from it until someone reassigns it. You can turn ${name} back on any time.`,
          confirmLabel: "Turn off",
        }),
        write: async (nextActive) => {
          const s = session();
          const result = await setVocabularyValueActive(
            s.apiBaseUrl,
            s.tenantId,
            "shelf_tier",
            tier.tier,
            nextActive,
          );
          if (result.status !== 200) {
            // The server refuses retiring the last active shelf tier;
            // that message is the useful one to show, matching
            // renderVocabulary's own identical pattern.
            return {
              ok: false,
              message: result.body.message ?? result.body.error ?? "Could not change that.",
            };
          }
          return { ok: true, active: result.body.value.active };
        },
        onError: (message) => {
          settingsSaveStatusMoneyEl.textContent = message;
        },
        onChange: (active) => {
          settingsSaveStatusMoneyEl.textContent = "";
          tier.active = active;
          renderShelfTierRows();
          renderBottleFilterChips();
          populateShelfTierSelect();
          refreshShelfTierSnapshot(tier.tier);
        },
      }),
    );

    settingsShelfTiersEl.append(row);
  }

  if (canEdit) {
    settingsShelfTiersEl.append(renderAddShelfTierRow());
  }
}

// B7-S3 (build brief Section 2.1). A small extension of this file's
// existing money-input pattern (parseDollarsToCentsExact/
// centsToAmountString), not a ninth VOCAB_EDITORS entry: shelf_tier is
// not in NUMERIC_VOCABULARIES (src/domain/vocabularies.ts), so the
// shared numeric pad has no slot for a name and no currency affordance,
// and building one there would be a new control, not a reuse. Free text
// for the name is correct here (not a picker): a tier name is a
// genuinely new value the admin is inventing, not drawn from an existing
// fixed vocabulary. Rebuilt on every renderShelfTierRows() call, so a
// successful Add (which triggers that same re-render) clears these
// inputs for free, the same way every other vocabulary's addVocabulary-
// Entry clears its own displayInput after a successful add.
function renderAddShelfTierRow(): HTMLElement {
  const row = document.createElement("div");
  row.className = "admin-shelf-tier-row admin-shelf-tier-add-row";
  row.dataset["testid"] = "admin-settings-shelf-tier-add-row";

  const nameInput = document.createElement("input");
  describeSetting(nameInput, "admin-settings-shelf-tiers-help");
  nameInput.className = "field-input admin-shelf-tier-name-input";
  nameInput.type = "text";
  nameInput.placeholder = "New shelf tier name";
  nameInput.setAttribute("aria-label", "New shelf tier name");
  nameInput.dataset["testid"] = "admin-settings-shelf-tier-add-name";
  row.append(nameInput);

  const priceWrap = document.createElement("span");
  priceWrap.className = "field-input-affix field-input-affix--prefix";
  const priceMark = document.createElement("span");
  priceMark.className = "field-input-affix-mark";
  priceMark.setAttribute("aria-hidden", "true");
  priceMark.textContent = "$";
  const priceInput = document.createElement("input");
  describeSetting(priceInput, "admin-settings-shelf-tiers-help");
  priceInput.className = "field-input admin-shelf-tier-price-input";
  priceInput.type = "number";
  priceInput.inputMode = "decimal";
  priceInput.step = "0.01";
  priceInput.min = "0";
  priceInput.setAttribute("aria-label", "New shelf tier price in dollars");
  priceInput.dataset["testid"] = "admin-settings-shelf-tier-add-price";
  priceWrap.append(priceMark, priceInput);
  row.append(priceWrap);

  const statusEl = document.createElement("span");
  statusEl.className = "field-status";
  statusEl.dataset["testid"] = "admin-settings-shelf-tier-add-status";
  row.append(statusEl);

  const addButton = document.createElement("button");
  addButton.type = "button";
  addButton.className = "btn btn-secondary";
  addButton.textContent = "Add a shelf tier";
  addButton.dataset["testid"] = "admin-settings-shelf-tier-add-button";
  addButton.addEventListener(
    "click",
    () => void handleAddShelfTier(nameInput, priceInput, statusEl, addButton),
  );
  row.append(addButton);

  return row;
}

async function handleAddShelfTier(
  nameInput: HTMLInputElement,
  priceInput: HTMLInputElement,
  statusEl: HTMLElement,
  addButton: HTMLButtonElement,
): Promise<void> {
  const name = nameInput.value.trim();
  if (name.length === 0) {
    statusEl.textContent = "Type a name for the new shelf tier.";
    return;
  }
  let priceCents: number;
  try {
    priceCents = parseDollarsToCentsExact(priceInput.value, `${name} price`);
  } catch (error) {
    statusEl.textContent = error instanceof Error ? error.message : "Invalid shelf tier price.";
    return;
  }

  addButton.disabled = true;
  try {
    const s = session();
    const result = await addVocabularyValue(s.apiBaseUrl, s.tenantId, "shelf_tier", {
      displayText: name,
      priceCents,
    });
    if (result.status !== 201) {
      statusEl.textContent = result.body.message ?? result.body.error ?? "Could not add that.";
      return;
    }
    const added = result.body.value;
    settingsShelfTiers = [
      ...settingsShelfTiers,
      {
        tier: added.value,
        name: added.label,
        priceCents: added.priceCents ?? 0,
        active: added.active,
      },
    ];
    renderShelfTierRows();
    renderBottleFilterChips();
    populateShelfTierSelect();
    refreshShelfTierSnapshot(added.value);
  } finally {
    addButton.disabled = false;
  }
}

// Settings rebuild SET1: shared by all three Save buttons (Money and
// pricing, Tabs and payment, Operations), so `button`/`statusEl` are
// whichever pair the operator actually clicked. The function still
// reads and validates EVERY field on the form and submits ONE combined
// PATCH, exactly as the single old "Save location settings" button did
// -- no change to what gets validated, saved, or in what order; only to
// where the trigger and its feedback live.
async function saveSettingsForm(button: HTMLButtonElement, statusEl: HTMLElement): Promise<void> {
  const s = session();
  const settingsGeneration = loadGeneration;
  const savedScope = f3SellingUi.settings;
  if (savedScope === undefined || savedScope.siteId !== inventorySellingSiteId()) {
    statusEl.textContent = "Location selling context unavailable. Reload settings.";
    return;
  }
  settingsStandardPourOunces = settingsStandardPourOuncesEl.value;
  const standardHundredths = parseOuncesToHundredthsClient(settingsStandardPourOunces);
  if (standardHundredths === null || standardHundredths <= 0n || standardHundredths > 1000n) {
    statusEl.textContent =
      "Standard pour must be from 0.01 to 10 oz with at most two decimal places.";
    return;
  }

  // Amendment 2026-08-22 (second): Save re-parses straight from every
  // field's live DOM value (never from a possibly-stale closure variable
  // a blur handler set earlier), so an edit that never blurred, e.g. Save
  // tapped directly after typing, is caught here rather than silently
  // saving the last-accepted number underneath an unaccepted display
  // string. Every parse is the exact, rejecting kind (parsePercentTo-
  // BasisPoints / parseDollarsToCentsExact): a value that cannot convert
  // losslessly to the stored unit stops the save with a plain-language
  // message naming the finest step, never a silent round.
  //
  // TAX2-S1-F1: the tax rate is no longer a field of this form; the Sales tax section records it
  // (parts and a start day). This save sends no rate, which the server reads as no change.

  // Payments-track Slice 4 (DEC-002), Finding 4. Same live-DOM, exact-
  // parse discipline as tax rate above. Hold amount is the one OPTIONAL
  // field here (an empty field means "leave whatever is already
  // configured unchanged", matching holdAmountCents' own null-until-set
  // convention); the other three always carry a real value once
  // settings have loaded, so an empty field there is a genuine parse
  // failure, not "leave unchanged".
  let holdAmountCents: number | undefined;
  const holdAmountRaw = settingsHoldAmountInput.value.trim();
  if (holdAmountRaw.length > 0) {
    try {
      holdAmountCents = parseDollarsToCentsExact(holdAmountRaw, "Hold amount");
    } catch (error) {
      statusEl.textContent = error instanceof Error ? error.message : "Invalid hold amount.";
      return;
    }
    if (holdAmountCents <= 0) {
      statusEl.textContent = "Hold amount must be more than $0.";
      return;
    }
    if (holdAmountCents > MAX_HOLD_AMOUNT_CENTS) {
      statusEl.textContent = "Hold amount can't be more than $100,000.";
      return;
    }
  }

  let holdIncrementThresholdBasisPoints: number;
  try {
    holdIncrementThresholdBasisPoints = parsePercentToBasisPoints(
      settingsHoldIncrementThresholdInput.value,
      "Hold increase threshold",
    );
  } catch (error) {
    statusEl.textContent =
      error instanceof Error ? error.message : "Invalid hold increase threshold.";
    return;
  }
  if (
    holdIncrementThresholdBasisPoints < MIN_HOLD_INCREMENT_THRESHOLD_BASIS_POINTS ||
    holdIncrementThresholdBasisPoints > MAX_HOLD_INCREMENT_THRESHOLD_BASIS_POINTS
  ) {
    statusEl.textContent = "Hold increase threshold must be between 0.01% and 100%.";
    return;
  }

  let abandonedTabGratuityBasisPoints: number;
  try {
    abandonedTabGratuityBasisPoints = parsePercentToBasisPoints(
      settingsAbandonedTabGratuityInput.value,
      "Abandoned tab gratuity",
    );
  } catch (error) {
    statusEl.textContent =
      error instanceof Error ? error.message : "Invalid abandoned tab gratuity.";
    return;
  }
  if (abandonedTabGratuityBasisPoints > MAX_ABANDONED_TAB_GRATUITY_BASIS_POINTS) {
    statusEl.textContent = "Abandoned tab gratuity can't be more than 100%.";
    return;
  }
  // DUALPRICE1: 0.00% to 4.00%. The server holds the same bound (tenant_settings_dual_pricing_check).
  let dualPricingBasisPoints: number;
  try {
    dualPricingBasisPoints = parsePercentToBasisPoints(
      settingsDualPricingPercentInput.value,
      "Card price difference",
    );
  } catch (error) {
    statusEl.textContent = error instanceof Error ? error.message : "Invalid card price difference.";
    return;
  }
  if (dualPricingBasisPoints > 400) {
    statusEl.textContent = "Card price difference can't be more than 4.00%.";
    return;
  }
  const abandonedTabGratuityDisclosureConfirmed =
    settingsAbandonedTabGratuityDisclosureInput.checked;
  // DEC-002's own condition (the signage/disclosure amendment): a
  // nonzero gratuity is only ever allowed once disclosure is confirmed,
  // same request or already stored. Caught here in plain language before
  // the round trip, though the server (src/domain/tenantSettings.ts's
  // requireDisclosureBeforeNonzeroGratuity) is the real enforcement
  // point either way.
  if (abandonedTabGratuityBasisPoints > 0 && !abandonedTabGratuityDisclosureConfirmed) {
    statusEl.textContent =
      'Turn on "Signage is posted" before setting an abandoned tab gratuity above 0%.';
    return;
  }

  const shelfTierPriceInputs = [
    ...settingsShelfTiersEl.querySelectorAll<HTMLInputElement>(
      '[data-testid="admin-settings-shelf-tier-price"]',
    ),
  ];
  if (shelfTierPriceInputs.length !== settingsShelfTiers.length) {
    statusEl.textContent = "Shelf tier rows did not match; reopen Location settings and try again.";
    return;
  }
  for (const [index, tier] of settingsShelfTiers.entries()) {
    const input = shelfTierPriceInputs[index];
    if (input === undefined) {
      statusEl.textContent =
        "Shelf tier rows did not match; reopen Location settings and try again.";
      return;
    }
    try {
      const label = `${tier.name.trim().length > 0 ? tier.name.trim() : shelfTierKeyLabel(tier.tier)} price`;
      if (
        savedScope.context.readiness.state === "enforced" &&
        input.value.trim() === "" &&
        savedScope.shelves.get(tier.tier)?.priceCents == null
      )
        continue;
      tier.priceCents = parseDollarsToCentsExact(input.value, label);
    } catch (error) {
      statusEl.textContent = error instanceof Error ? error.message : "Invalid shelf tier price.";
      return;
    }
  }

  // Operations group (SET1). Same live-DOM, exact-parse discipline as
  // every field above. UX86: session inactivity is no longer on this form;
  // it moved to People and access and is saved only by its own card
  // (saveSessionInactivity), so a Location save neither carries nor
  // validates it.
  const businessDayCutoffTimeLocal = settingsBusinessDayCutoffInput.value.trim();
  const businessDayTimezone = settingsBusinessDayTimezoneInput.value.trim();
  if (businessDayCutoffTimeLocal.length === 0) {
    statusEl.textContent = "Business day cutoff time can't be blank.";
    return;
  }
  // CL6-F1-F3: while the location is unconfirmed and still on the default,
  // the picker shows "Pick your time zone" and its value is "". That is not
  // an error for a Money, Tabs or Location-address save, which resubmit every
  // field on this form: the time zone is simply not sent, so it stays as the
  // server has it until the manager picks one and taps Confirm.
  button.disabled = true;
  statusEl.textContent = "Saving…";
  try {
    // SET19 (docs/build-briefs/SET19-transactional-settings-save.md):
    // one transactional server call covering every shelf tier row and
    // the rest of the Location settings form together, so a partial
    // failure (a bad value elsewhere in the request, the disclosure-gate
    // check) can never leave a shelf tier price committed while the save
    // reports as failed. Supersedes B7 Slice 1's interim per-tier-PATCH-
    // then-combined-PATCH sequence and its own "already in effect"
    // partial-success messaging: once this is one transaction, that
    // failure mode cannot occur, so a message describing it would be
    // actively wrong if it ever fired. Unconditional (every row, not
    // only a changed one), matching this form's own pre-existing
    // behavior of always resubmitting all four rows on every save
    // regardless of which actually changed.
    const shelfTiers = settingsShelfTiers.map((tier) => ({
      value: tier.tier,
      label: tier.name,
      ...(savedScope.context.readiness.state === "preparation"
        ? { priceCents: tier.priceCents }
        : {}),
    }));
    const settingsBody: SaveLocationSettingsInput = {
      tipPresetsBasisPoints: settingsTipPresets,
      holdAmountCents,
      holdIncrementThresholdBasisPoints,
      abandonedTabGratuityBasisPoints,
      abandonedTabGratuityDisclosureConfirmed,
      closeCardTicketsAtDayEnd: settingsCloseCardTicketsInput.checked,
      dualPricingEnabled: settingsDualPricingInput.checked,
      dualPricingBasisPoints: dualPricingBasisPoints,
      tipOnPreDiscountPrice: settingsTipOnPreDiscountInput.checked,
      roundingMode: settingsRoundingMode,
      pricingModel: settingsPricingModel,
      standardPourOunces: settingsStandardPourOunces,
      businessDayCutoffTimeLocal,
      ...(businessDayTimezone.length === 0 ? {} : { businessDayTimezone }),
      shelfTiers,
    };
    if (savedScope.context.readiness.state === "enforced") {
      const localShelfUpdates = settingsShelfTiers.flatMap((tier, index) => {
        const priceText = shelfTierPriceInputs[index]?.value.trim();
        const original = savedScope.shelves.get(tier.tier);
        if (!priceText || original?.priceCents === tier.priceCents) return [];
        let requestId = savedScope.requestIds.get(tier.tier);
        if (requestId === undefined) {
          requestId = crypto.randomUUID();
          savedScope.requestIds.set(tier.tier, requestId);
        }
        return [
          {
            shelfTier: tier.tier,
            requestId,
            expectedVersion: original?.version ?? 0,
            source: "configured" as const,
            priceCents: tier.priceCents,
          },
        ];
      });
      if (localShelfUpdates.length > 0) {
        if (!savedScope.context.locationId || !hasPermission(s, "admin.liquor_product.manage")) {
          statusEl.textContent = "Local price changes are not available.";
          return;
        }
        settingsBody.locationId = savedScope.context.locationId;
        settingsBody.localShelfUpdates = localShelfUpdates;
      }
    }
    const stamp = JSON.stringify(settingsBody);
    const pending = f3SellingUi.pendingSettings;
    if (
      pending &&
      (pending.session !== s || pending.siteId !== savedScope.siteId || pending.stamp !== stamp)
    ) {
      statusEl.textContent = "Previous save unconfirmed. Retry the unchanged settings request, or reload current settings before reviewing a new save.";
      settingsLoadRetryButton.hidden = false;
      return;
    }
    const originalBody = pending?.body ?? structuredClone(settingsBody);
    const result = await saveLocationSettings(s.apiBaseUrl, s.tenantId, originalBody);
    if (
      !loadIsCurrent(settingsGeneration) ||
      sessionOrUndefined() !== s ||
      f3SellingUi.settings !== savedScope ||
      savedScope.siteId !== inventorySellingSiteId()
    )
      return;
    if (result.networkError || result.status >= 500)
      f3SellingUi.pendingSettings = {
        session: s,
        siteId: savedScope.siteId,
        body: originalBody,
        stamp,
      };

    if (result.status !== 200) {
      const reason = result.body.message ?? result.body.error ?? "Could not save settings.";
      statusEl.textContent = f3SellingUi.pendingSettings ? `${reason} Previous save unconfirmed. Retry unchanged or reload current settings.` : reason;
      if (f3SellingUi.pendingSettings) settingsLoadRetryButton.hidden = false;
      // The server's own validators are the real enforcement for the
      // three Operations fields; surface a rejection against the
      // Operations field help text too, so an error caused by THOSE
      // fields is visible there even when a different group's Save
      // button was the one clicked (every button submits every field).
      settingsBusinessDayHelpEl.textContent = result.body.message
        ?.toLowerCase()
        .includes("business day")
        ? (result.body.message ?? "")
        : "";
      return;
    }
    settingsTaxRateBasisPoints = result.body.settings.taxRateBasisPoints;
    settingsTipPresets = [...result.body.settings.tipPresetsBasisPoints];
    delete f3SellingUi.pendingSettings;
    savedScope.requestIds.clear();
    if (result.body.localShelves !== undefined)
      savedScope.shelves = new Map(result.body.localShelves.map((tier) => [tier.shelfTier, tier]));
    if (result.body.sellingContext !== undefined) savedScope.context = result.body.sellingContext;
    settingsShelfTiers = result.body.settings.shelfTiers.map((tier) => ({ ...tier }));
    settingsHoldAmountCents = result.body.settings.holdAmountCents;
    settingsHoldIncrementThresholdBasisPoints =
      result.body.settings.holdIncrementThresholdBasisPoints;
    settingsAbandonedTabGratuityBasisPoints = result.body.settings.abandonedTabGratuityBasisPoints;
    settingsAbandonedTabGratuityDisclosureConfirmed =
      result.body.settings.abandonedTabGratuityDisclosureConfirmed;
    settingsCloseCardTicketsAtDayEnd = result.body.settings.closeCardTicketsAtDayEnd;
    settingsDualPricingEnabled = result.body.settings.dualPricingEnabled;
    settingsDualPricingBasisPoints = result.body.settings.dualPricingBasisPoints;
    settingsTipOnPreDiscountPrice = result.body.settings.tipOnPreDiscountPrice;
    settingsRoundingMode = (result.body.settings.roundingMode as RoundingMode) ?? "half-up-cent";
    settingsPricingModel = (result.body.settings.pricingModel as PricingModel) ?? "per_ounce";
    // INV27-F2 (DEC-253): a saved pricing-model change relabels/reorders
    // an already-open item editor's Pricing tab live, matching the
    // mockup's own "the label always says who charges the rate today"
    // instruction.
    applyBottlePriceLabelsForPricingModel();
    renderBottleCalculator();
    settingsStandardPourOunces = result.body.settings.standardPourOunces;
    settingsPinMinLengthInput.value = String(result.body.settings.pinMinLength);
    settingsPinMaxLengthInput.value = String(result.body.settings.pinMaxLength);
    settingsPinLockoutAttemptsInput.value = String(result.body.settings.pinLockoutAttempts);
    settingsPinLockoutMinutesInput.value = String(result.body.settings.pinLockoutMinutes);
    applyBusinessDayFromSettings(result.body.settings);
    renderSettings();
    void loadLocationSettingsPanel(s);
    statusEl.textContent = "Saved.";
    adminToast("Location settings saved.", "success");
    // SET14: both domains' own committed snapshots, taken immediately
    // after this successful save. This save also echoes the PIN policy
    // fields from the server's own response (above), so that domain's
    // snapshot is refreshed here too, not only settingsForm's.
    settingsFormSnapshot = captureSettingsFormSnapshot();
    pinPolicySnapshot = capturePinPolicySnapshot();
    noteSettingsFormCommitted();
    notePinPolicyCommitted();
  } finally {
    button.disabled = false;
  }
}

// ---------------------------------------------------------------------
// Inventory track (track/inventory) Slice 2: Counts
//
// "Grouped by count section" is realized in the REVIEW screen and the
// history detail screen, both of which group by the count_section_id an
// observation was ACTUALLY recorded under (real per-observation data,
// src/domain/counts.ts's own model), section sort_order first, no-section
// last. The WALK itself has no persisted per-product section to
// pre-group by (count_sections is per-observation, not a standing
// liquor_products attribute; see db/schema.ts's own inventory-track
// comment), so the walk instead makes grouping a real physical workflow:
// a sticky "counting section" chip the counter sets once and changes only
// when they move shelves, while every count they save carries whatever
// section is currently selected. This is a deliberate, disclosed UX
// choice, not a gap; see this track's work-log entry for the full
// write-up.
//
// Units: per-item-kind selectable human units, defaulting to that
// product's own current on-hand unit when it has exactly one and that
// unit is still offered for this item's kind, or the kind's own first
// option for a fresh product or one with no single-unit history. No
// conversion between units is ever performed (matches src/domain/
// inventory.ts's own "units are never converted or mixed" rule); picking
// a unit that does not match a product's existing history is a legitimate
// choice this screen allows, and the review/history screens surface a
// mixed-unit product honestly rather than silently combining it.
//
// Adversarial gate Finding 2 (2026-08-22): a liquor/beer item counts
// in "oz" or "bottle" (CLAUDE.md's own "bottles/ounces" wording), unchanged
// from before this fix. A whole-unit item depletes and holds on-hand in
// unit "unit" (src/domain/inventorySale.ts's whole_unit branch: `unit:
// "unit"`), never ounces or bottles, so offering only "Ounces"/"Bottles"
// here gave a manager counting whole-unit stock no correct option at all
// -- every count of a can or bottle of beer/wine/RTD had to be forced into
// the wrong unit. This picks the option set by the PRODUCT'S OWN
// unit_price_cents (countUnitOptionsForItemKind below), keeping the
// domain layer itself completely unit-agnostic (recordCount's `unit`
// param is, and stays, a plain string; see ui/src/api.ts): this is a UI
// SELECTION fix only, not a new server-side unit vocabulary.
//
// ITEM2 Slice 5 (Section 3.4): used to dispatch on the item_kind literal
// ("packaged" versus everything else), which a tenant-added whole-unit
// kind's own item_kind (its own value, never "packaged") could never
// match. Generalized to the already-fetched PRODUCT's own
// `unit_price_cents` (non-null exactly on a whole-unit product, the same
// derivation bottleRowPriceText/tilePriceLabelForNonCocktailItem use),
// keeping the function name (its own call sites both already read from a
// real product) but not its parameter shape.
// ---------------------------------------------------------------------

const LIQUOR_COUNT_UNITS: { value: "oz" | "bottle"; label: string }[] = [
  { value: "oz", label: "Ounces" },
  { value: "bottle", label: "Bottles" },
];

const PACKAGED_COUNT_UNITS: { value: "unit"; label: string }[] = [
  { value: "unit", label: "Units" },
];

const COUNT_UNIT_LABELS: Record<string, string> = {
  oz: "Ounces",
  bottle: "Bottles",
  unit: "Units",
};

function countUnitOptionsForWalkItem(
  product: CountWalkProduct,
): { value: string; label: string }[] {
  return product.countUnits.flatMap((unit) => {
    const label = COUNT_UNIT_LABELS[unit];
    return label === undefined ? [] : [{ value: unit, label }];
  });
}

function defaultCountUnitFor(product: CountWalkProduct, preferredUnit?: string): string {
  const options = countUnitOptionsForWalkItem(product);
  return options.some((option) => option.value === preferredUnit)
    ? (preferredUnit as string)
    : (options[0]?.value ?? "oz");
}

/** Which unit chips the count-walk unit picker shows for one product,
 * keyed on that product's own unit_price_cents. See this section's own
 * header comment for why a whole-unit item gets a different, disjoint set
 * rather than an extra option added to the liquor/beer set. */
function countUnitOptionsForItemKind(
  unitPriceCents: number | null,
): { value: string; label: string }[] {
  return unitPriceCents !== null ? PACKAGED_COUNT_UNITS : LIQUOR_COUNT_UNITS;
}

// Client-side mirror of src/domain/inventoryQuantity.ts's parseQuantity
// pattern (non-negative, up to four decimal places): a staff-friendly
// early rejection, matching settings' own MAX_TAX_RATE_BASIS_POINTS
// comment on why this is not itself the enforcement point. The server is
// still authoritative; a value this screen lets through unrejected but
// the server still refuses shows the server's own message instead.
const COUNT_QUANTITY_PATTERN = /^\d+(\.\d{1,4})?$/;
// INV37 (DEC-337): the same, narrowed to two decimal places for Bottles.
const COUNT_BOTTLE_QUANTITY_PATTERN = /^\d+(\.\d{1,2})?$/;

function formatQuantityUnit(quantity: string, unit: string): string {
  const label =
    unit === "oz"
      ? "oz"
      : unit === "bottle"
        ? quantity === "1"
          ? "bottle"
          : "bottles"
        : unit === "unit"
          ? quantity === "1"
            ? "unit"
            : "units"
          : unit;
  return `${quantity} ${label}`;
}

// INV20 (docs/build-briefs/INV20-single-par-and-onhand.md; DEC-190,
// DEC-206), owner-approved 2026-09-28
// (design/reviews/inv20-inventory-details.png): the item-detail on-hand
// figure. Always one figure in the item's own count unit now (the
// server-side conversion in src/domain/inventory.ts's
// convertLiquorProductOnHandToCountUnit), rounded to one decimal place
// with the unit word and, when this item carries a size, its
// parenthetical size, e.g. "2.5 bottles (750 ml)". Signed when negative
// (UX16-F2/DEC-178) on the figure itself, never a separate minus label.
function formatOnHandFigure(
  quantity: string,
  unit: string,
  sizeValue: string | null,
  sizeUnit: string | null,
): string {
  const rounded = roundQuantityToOneDecimalPlace(quantity);
  const label = pluralizeCountUnitLabel(unit, rounded);
  const size =
    sizeValue !== null && sizeUnit !== null ? ` (${formatSizeValueUnit(sizeValue, sizeUnit)})` : "";
  return `${rounded} ${label}${size}`;
}

/** Rounded to tenths, sign preserved, "0.0" rather than "-0.0" for a
 * value that rounds to exactly zero. No source document narrows "rounded
 * to tenths" further than one decimal place (the mockup's own sourcing
 * note), applied consistently here for every on-hand figure.
 *
 * design/mockups/inv26-list-fit.html's second defect: a genuinely nonzero
 * value that rounds to zero (inside (-0.05, 0.05)) used to render the
 * same literal "0.0" as an actually-empty on-hand, contradicting that
 * row's own Status chip (OK vs Out). A nonzero value in that band now
 * reads "<0.1" (positive) or ">-0.1" (negative) instead, so "0.0" is
 * reserved for an on-hand of exactly zero. Both callers
 * (inventoryOnHandDisplayText and formatOnHandFigure) pass this string
 * straight into pluralizeCountUnitLabel, which already falls through to
 * its plural branch for anything other than the literal "1.0"/"-1.0", so
 * the unit word still follows correctly ("<0.1 bottles") with no change
 * needed there. */
function roundQuantityToOneDecimalPlace(quantity: string): string {
  const value = Number(quantity);
  const rounded = Math.round(value * 10) / 10;
  if (rounded === 0) {
    if (value === 0) {
      return "0.0";
    }
    return value > 0 ? "<0.1" : ">-0.1";
  }
  return rounded.toFixed(1);
}

function pluralizeCountUnitLabel(unit: string, roundedQuantity: string): string {
  if (unit === "oz") {
    return "oz";
  }
  const singular = roundedQuantity === "1.0" || roundedQuantity === "-1.0";
  switch (unit) {
    case "bottle":
      return singular ? "bottle" : "bottles";
    case "can":
      return singular ? "can" : "cans";
    case "keg":
      return singular ? "keg" : "kegs";
    case "unit":
      return singular ? "unit" : "units";
    default:
      return unit;
  }
}

function formatSizeValueUnit(sizeValue: string, sizeUnit: string): string {
  return `${sizeValue} ${SIZE_UNIT_DISPLAY[sizeUnit] ?? sizeUnit}`;
}

/** A chip-radiogroup renderer shared by the section and unit pickers.
 * Mirrors renderBottleFilterChipGroup's own shape one panel up in this
 * file (kept as its own small copy rather than a shared export, the same
 * "no single source for a four-line helper" precedent
 * LIQUOR_SLOT_CATEGORIES's database-CHECK mirror already established). */
function renderChipRadioGroup(
  container: HTMLElement,
  radioGroupName: string,
  testId: string,
  options: { value: string; label: string }[],
  selectedValue: string | undefined,
  onSelect: (value: string) => void,
  describedById?: string,
): void {
  container.innerHTML = "";
  for (const option of options) {
    const optionLabel = document.createElement("label");
    optionLabel.className = "admin-chip-option";
    const input = document.createElement("input");
    describeSetting(input, describedById);
    input.type = "radio";
    input.name = radioGroupName;
    input.className = "admin-chip-option-input";
    input.dataset["testid"] = testId;
    input.dataset["value"] = option.value;
    input.checked = option.value === selectedValue;
    input.addEventListener("change", () => {
      if (input.checked) {
        onSelect(option.value);
      }
    });
    const text = document.createElement("span");
    text.className = "admin-chip-option-label";
    text.textContent = option.label;
    optionLabel.append(input, text);
    container.append(optionLabel);
  }
}

function sortedCountSectionsForGrouping(): CountSectionDto[] {
  // Every section (not only active ones): a past count recorded under a
  // section since deactivated must still be able to show that section's
  // name in review/history, never fall back to "No section" for a
  // section that genuinely existed at count time.
  return [...adminCountSections].sort(
    (a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name),
  );
}

// ---------------------------------------------------------------------
// Count sections (plain CRUD, the panel's top block)
// ---------------------------------------------------------------------

// Guards loadCounts' own fetch against clobbering NEWER local state: a
// section added (or toggled) while the panel-open fetch was still in
// flight used to be silently wiped when that older response finally
// landed and did `adminCountSections = ...` last-write-wins. On a slow
// connection a manager saw their just-added area vanish; under load the
// count-flow e2e saw its "Well" chip never render. Every local mutation
// bumps the sequence, and a fetch result older than the latest sequence
// is discarded rather than applied.
let countsLoadSequence = 0;

async function loadCounts(): Promise<void> {
  const gen = loadGeneration;
  const s = session();
  // UX80: resolves its own route's permission rather than inheriting a section gate.
  if (!hasPermission(s, "inv.count_session.run")) return;
  const sequenceAtStart = ++countsLoadSequence;
  const [sectionsResult] = await Promise.all([
    listCountSections(s.apiBaseUrl, s.tenantId, { activeOnly: false }),
    refreshCountsStartRow(gen),
    loadCountHistory(),
  ]);
  // SET16-F4: this session's teardown ran while the fetch above was in
  // flight; discard rather than write over what invalidate() already
  // cleared. A separate question from countsLoadSequence directly below,
  // which answers "is this the most recent COUNT fetch" rather than "is
  // this still the same SESSION"; the two guards are not interchangeable
  // and neither replaces the other.
  if (!loadIsCurrent(gen)) return;
  if (sectionsResult.status !== 200) {
    adminToast(
      `Could not load count sections. ${capitalizeServerMessage(sectionsResult.body.message ?? "Check your connection.")}`,
      "danger",
    );
    return;
  }
  if (sequenceAtStart !== countsLoadSequence) {
    // A section was added or toggled (or a newer load started) while
    // this response was in flight; applying it would erase that newer
    // state. The mutation path already rendered the truth it wrote.
    return;
  }
  // SET15: set only on success; see loadDrinks's own comment above.
  countsLoaded = true;
  adminCountSections = sectionsResult.body.countSections;
  renderCountSectionsList();
}

/** UX80: Settings, Inventory items only needs the count AREAS (their walking
 * order); the open session and past counts load with the Counts view
 * (loadCounts above). Same latch, same sequence guard. */
async function loadCountAreas(): Promise<void> {
  const gen = loadGeneration;
  const s = session();
  if (!hasPermission(s, "inv.count_session.run")) return;
  const sequenceAtStart = ++countsLoadSequence;
  const sectionsResult = await listCountSections(s.apiBaseUrl, s.tenantId, { activeOnly: false });
  if (!loadIsCurrent(gen)) return;
  if (sectionsResult.status !== 200) {
    adminToast(
      `Could not load count areas. ${capitalizeServerMessage(sectionsResult.body.message ?? "Check your connection.")}`,
      "danger",
    );
    return;
  }
  if (sequenceAtStart !== countsLoadSequence) return;
  countsLoaded = true;
  adminCountSections = sectionsResult.body.countSections;
  renderCountSectionsList();
}

function renderCountSectionsList(): void {
  countsSectionsListEl.innerHTML = "";
  if (adminCountSections.length === 0) {
    // Amendment 2026-08-22 (eighth), item 3: the empty state teaches by
    // example rather than only saying nothing exists yet, the same
    // worked example the explainer paragraph above uses, so a manager
    // who skips straight past the paragraph still sees what a
    // reasonable set of areas looks like.
    const empty = document.createElement("p");
    empty.className = "tabs-empty";
    empty.dataset["testid"] = "admin-counts-sections-empty";
    empty.textContent =
      "No count areas yet. Add areas like Well, Back bar, and Storage to count in that order, " +
      "or skip this and count everything in one pass.";
    countsSectionsListEl.append(empty);
    return;
  }
  for (const section of sortedCountSectionsForGrouping()) {
    const chip = document.createElement("button");
    chip.type = "button";
    chip.className = section.active
      ? "admin-count-section-chip"
      : "admin-count-section-chip inactive";
    chip.dataset["testid"] = "admin-counts-section-chip";
    chip.dataset["countSectionId"] = section.id;
    chip.textContent = section.active ? section.name : `${section.name} (deleted)`;
    chip.setAttribute(
      "aria-label",
      `${section.name}, ${section.active ? "active" : "deleted"}. Tap to ${section.active ? "delete" : "restore"}.`,
    );
    chip.addEventListener("click", () => void handleToggleCountSectionActive(section));
    countsSectionsListEl.append(chip);
  }
}

// ---------------------------------------------------------------------
// T14 (docs/backlog.md): the MyTime ambiguous-reconciliation queue.
// docs/build-briefs/T14-reconciliation-queue-ui.md is the full write-up;
// src/domain/mytimeClientSync.ts's own Slice 3 section is the domain
// design this screen surfaces read-only-context plus two commands for.
//
// UX6's refresh rule: after a resolve write, this screen re-fetches the
// list rather than merely removing the resolved row locally, so it never
// shows a stale queue if another manager resolved something else in the
// same window (loadReconciliationCandidates re-renders from a fresh
// server read every time, exactly like loadCounts/loadUsers already do
// elsewhere on this screen).
// ---------------------------------------------------------------------

async function loadReconciliationCandidates(): Promise<void> {
  const gen = loadGeneration;
  const s = session();
  // UX80: resolves its own route's permission rather than inheriting a section gate.
  if (!hasPermission(s, "guest.reconciliation.view")) return;
  const result = await listReconciliationCandidates(s.apiBaseUrl, s.tenantId);
  // SET16-F4: this session's teardown ran while the fetch above was in
  // flight; discard rather than write over what invalidate() already
  // cleared.
  if (!loadIsCurrent(gen)) return;
  if (result.status !== 200) {
    adminToast(
      `Could not load the reconciliation queue. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`,
      "danger",
    );
    return;
  }
  adminReconciliationCandidates = result.body.candidates;
  renderReconciliationList();
}

const RECONCILIATION_REASON_LABELS: Record<string, string> = {
  ambiguous_multiple_phone_matches: "Matched more than one MyTime client by phone",
  name_only_match: "Matched a MyTime client by name only, not phone",
};

function reconciliationReasonLabel(reason: string): string {
  return RECONCILIATION_REASON_LABELS[reason] ?? reason;
}

function renderReconciliationList(): void {
  reconciliationListEl.innerHTML = "";
  const canResolve = reconciliationResolveAllowed(session());
  if (adminReconciliationCandidates.length === 0) {
    reconciliationEmptyEl.hidden = false;
    return;
  }
  reconciliationEmptyEl.hidden = true;
  for (const candidate of adminReconciliationCandidates) {
    const row = document.createElement("div");
    row.className = "admin-reconciliation-row";
    row.dataset["testid"] = "admin-reconciliation-row";
    row.dataset["candidateId"] = candidate.id;

    const header = document.createElement("div");
    header.className = "admin-reconciliation-row-header";
    const name = document.createElement("span");
    name.className = "admin-reconciliation-guest-name";
    name.dataset["testid"] = "admin-reconciliation-guest-name";
    name.textContent = candidate.provisionalDisplayName;
    const reason = document.createElement("span");
    reason.className = "admin-reconciliation-reason";
    reason.dataset["testid"] = "admin-reconciliation-reason";
    reason.textContent = reconciliationReasonLabel(candidate.reason);
    header.append(name, reason);

    const created = document.createElement("p");
    created.className = "admin-field-help";
    created.textContent = `Flagged ${new Date(candidate.createdAt).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    })}`;

    const matchList = document.createElement("div");
    matchList.className = "admin-reconciliation-matches";
    for (const match of candidate.candidateMatches) {
      const matchButton = document.createElement("button");
      matchButton.type = "button";
      matchButton.className = "btn-secondary btn-compact";
      matchButton.dataset["testid"] = "admin-reconciliation-link-button";
      matchButton.dataset["candidateId"] = candidate.id;
      matchButton.dataset["mytimeClientId"] = match.mytimeClientId;
      matchButton.textContent = `Link to ${match.displayName}${match.membershipActive ? " (member)" : ""}`;
      matchButton.disabled = !canResolve || reconciliationRowBusyId === candidate.id;
      matchButton.addEventListener(
        "click",
        () =>
          void handleResolveReconciliationCandidate(
            candidate,
            { resolution: "link", mytimeClientId: match.mytimeClientId },
            match.displayName,
          ),
      );
      matchList.append(matchButton);
    }

    const keepSeparateButton = document.createElement("button");
    keepSeparateButton.type = "button";
    keepSeparateButton.className = "btn-secondary btn-compact";
    keepSeparateButton.dataset["testid"] = "admin-reconciliation-keep-separate-button";
    keepSeparateButton.dataset["candidateId"] = candidate.id;
    keepSeparateButton.textContent = "Keep separate";
    keepSeparateButton.disabled = !canResolve || reconciliationRowBusyId === candidate.id;
    keepSeparateButton.addEventListener(
      "click",
      () =>
        void handleResolveReconciliationCandidate(
          candidate,
          { resolution: "keep_separate" },
          undefined,
        ),
    );
    matchList.append(keepSeparateButton);

    row.append(header, created, matchList);
    reconciliationListEl.append(row);
  }
}

async function handleResolveReconciliationCandidate(
  candidate: ReconciliationCandidateDto,
  params: { resolution: "link"; mytimeClientId: string } | { resolution: "keep_separate" },
  linkedDisplayName: string | undefined,
): Promise<void> {
  const s = session();
  const confirmed = await showConfirmSheet({
    title: params.resolution === "link" ? "Link this guest?" : "Keep these guests separate?",
    message:
      params.resolution === "link"
        ? `${candidate.provisionalDisplayName} will be permanently linked to ${linkedDisplayName ?? "the selected MyTime client"}. This cannot be undone from this screen.`
        : `${candidate.provisionalDisplayName} stays a separate local guest record, not linked to any MyTime client shown here.`,
    confirmLabel: params.resolution === "link" ? "Link" : "Keep separate",
  });
  if (!confirmed) {
    return;
  }
  // SET16-F1 follow-up, from independent review: this confirm sheet can
  // sit open long enough for the acting employee's session to die, and
  // the `s` captured above this await predates it. Without this guard
  // the continuation runs anyway and writes a success toast, an error
  // string, or a re-rendered list onto whatever is now on screen, which
  // after a teardown is the NEXT employee's screen. Same guard and same
  // reason as applyActivationSwitchChange's own chokepoint.
  if (sessionOrUndefined() === undefined) {
    return;
  }

  reconciliationRowBusyId = candidate.id;
  renderReconciliationList();
  const result = await resolveReconciliationCandidate(
    s.apiBaseUrl,
    s.tenantId,
    candidate.id,
    params,
  );
  reconciliationRowBusyId = undefined;

  if (result.status !== 200) {
    // The server's own real refusal path (e.g. a candidate someone else
    // already resolved) surfaces here rather than a silent failure or a
    // stale row that still looks actionable.
    adminToast(
      result.body.message ?? result.body.error ?? "Could not resolve that candidate.",
      "danger",
    );
    // UX6: refresh regardless of outcome, so a refusal caused by someone
    // else already resolving this candidate immediately drops it from
    // view instead of leaving a now-dead row on screen.
    await loadReconciliationCandidates();
    return;
  }
  adminToast(
    result.body.resolution.outcome === "linked"
      ? `${candidate.provisionalDisplayName} linked.`
      : `${candidate.provisionalDisplayName} kept separate.`,
    "success",
  );
  // UX6's own refresh rule: re-fetch from the server rather than only
  // splicing this row out locally.
  await loadReconciliationCandidates();
}

// ---------------------------------------------------------------------
// MT1 (docs/backlog.md), P0.11: the MyTime client-sync freshness check.
// docs/build-briefs/MT1-sync-freshness-check.md is the full write-up;
// src/domain/mytimeClientSync.ts's getMyTimeClientSyncFreshness is the
// domain design this screen surfaces read-only. No controls: this
// screen never writes anything, matching this item's own scope (a
// missed-sync surface is read-only reporting; it must not fabricate sync
// runs or mutate sync state).
// ---------------------------------------------------------------------

function formatMyTimeSyncAge(hoursSinceLastSuccess: number | null): string {
  if (hoursSinceLastSuccess === null) {
    return "never";
  }
  if (hoursSinceLastSuccess < 1) {
    return "less than an hour ago";
  }
  const wholeHours = Math.floor(hoursSinceLastSuccess);
  return wholeHours === 1 ? "1 hour ago" : `${wholeHours} hours ago`;
}

function renderMyTimeSyncFreshness(freshness: MyTimeSyncFreshnessDto): void {
  mytimeSyncFreshnessBodyEl.innerHTML = "";

  const statusLine = document.createElement("p");
  statusLine.dataset["testid"] = "admin-mytime-sync-freshness-status";
  statusLine.className = freshness.stale
    ? "admin-mytime-sync-freshness-status admin-mytime-sync-freshness-stale"
    : "admin-mytime-sync-freshness-status admin-mytime-sync-freshness-fresh";
  statusLine.textContent =
    freshness.lastSucceededAt === null
      ? "Last client sync: never"
      : `Last client sync: ${formatMyTimeSyncAge(freshness.hoursSinceLastSuccess)}${
          freshness.stale ? ", overdue" : ""
        }`;
  mytimeSyncFreshnessBodyEl.append(statusLine);

  if (freshness.lastAttemptStatus === "failed") {
    const failureLine = document.createElement("p");
    failureLine.dataset["testid"] = "admin-mytime-sync-freshness-last-attempt-failed";
    failureLine.className = "admin-field-help";
    failureLine.textContent = `Most recent attempt (batch ${
      freshness.lastAttemptBatchDate ?? "unknown"
    }) failed: ${freshness.lastAttemptErrorMessage ?? "no error message recorded"}.`;
    mytimeSyncFreshnessBodyEl.append(failureLine);
  }
}

async function loadMyTimeSyncFreshness(): Promise<void> {
  const gen = loadGeneration;
  const s = session();
  // UX80: resolves its own route's permission rather than inheriting a section gate.
  if (!hasPermission(s, "admin.mytime_sync.view")) return;
  const result = await getMyTimeSyncFreshness(s.apiBaseUrl, s.tenantId);
  // SET16-F4: this session's teardown ran while the fetch above was in
  // flight; discard rather than write over what invalidate() already
  // cleared.
  if (!loadIsCurrent(gen)) return;
  if (result.status !== 200) {
    adminToast(
      `Could not load the MyTime sync status. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`,
      "danger",
    );
    return;
  }
  renderMyTimeSyncFreshness(result.body.freshness);
}

async function handleAddCountSection(): Promise<void> {
  const s = session();
  const name = countsSectionNameInput.value.trim();
  if (name.length === 0) {
    adminToast("Enter an area name first.", "danger");
    return;
  }
  const result = await createCountSection(s.apiBaseUrl, s.tenantId, {
    name,
    sortOrder: adminCountSections.length,
  });
  if (result.status !== 201) {
    adminToast(result.body.message ?? result.body.error ?? "Could not add that area.", "danger");
    return;
  }
  adminCountSections.push(result.body.countSection);
  countsLoadSequence++;
  countsSectionNameInput.value = "";
  renderCountSectionsList();
  adminToast(`${result.body.countSection.name} added.`, "success");
}

// Amendment 2026-08-22 (eighth), item 4: section deletion must exist in
// the UI, following the confirm pattern (Review 9's shared
// showConfirmSheet, the same one the drinks/bottles Active toggles and
// the terminal Active toggle already use for their own destructive
// direction). "Delete" here IS this screen's pre-existing
// active/inactive toggle, worded for what a manager actually means by
// it: a deleted area drops out of the picker for NEW counts, but
// deleting an area never touches history -- every past count keeps its
// own recorded area, which is exactly why this is a state flip rather
// than a real row deletion (sortedCountSectionsForGrouping's own comment
// already explains why a deleted section still needs to render by name in
// review/history). Restoring (the reverse direction) is not destructive
// and needs no confirmation, matching every other Active toggle in this
// file.
async function handleToggleCountSectionActive(section: CountSectionDto): Promise<void> {
  const s = session();
  if (section.active) {
    const confirmed = await showConfirmSheet({
      title: `Delete ${section.name}?`,
      message:
        `${section.name} will no longer be offered when starting a new count. Past counts keep ` +
        `their own recorded area; nothing about them changes.`,
      confirmLabel: "Delete",
    });
    if (!confirmed) {
      return;
    }
    // SET16-F1 follow-up, from independent review: this confirm sheet can
    // sit open long enough for the acting employee's session to die, and
    // the `s` captured above this await predates it. Without this guard
    // the continuation runs anyway and writes a success toast, an error
    // string, or a re-rendered list onto whatever is now on screen, which
    // after a teardown is the NEXT employee's screen. Same guard and same
    // reason as applyActivationSwitchChange's own chokepoint.
    if (sessionOrUndefined() === undefined) {
      return;
    }
  }
  const result = await updateCountSection(s.apiBaseUrl, s.tenantId, section.id, {
    active: !section.active,
  });
  if (result.status !== 200) {
    adminToast(result.body.message ?? result.body.error ?? "Could not update that area.", "danger");
    return;
  }
  const index = adminCountSections.findIndex((existing) => existing.id === section.id);
  if (index !== -1) {
    adminCountSections[index] = result.body.countSection;
  }
  countsLoadSequence++;
  renderCountSectionsList();
}

// ---------------------------------------------------------------------
// Starting or resuming a count session
// ---------------------------------------------------------------------

const COUNT_START_MODE_OPTIONS = [
  { value: "visible", label: "Quantity visible" },
  { value: "blind", label: "Blind count" },
];

let countStartReadSequence = 0;
let countStartSelectedMode: "visible" | "blind" = "visible";
let countStartScope: string | undefined;
function resetCountStartSelection(): void {
  countStartReadSequence += 1;
  countStartSelectedMode = "visible";
  countStartScope = undefined;
}
function countStartScopeKey(activeSession: Session): string {
  return JSON.stringify([activeSession.token, activeSession.tenantId, activeSession.userId,
    activeSession.apiBaseUrl, loadTerminalConfig()?.locationId]);
}

function selectedCountStartMode(): "visible" | "blind" {
  const checked = countsStartModePickerEl.querySelector<HTMLInputElement>(
    'input[type="radio"]:checked',
  );
  return checked?.dataset["value"] === "blind" ? "blind" : "visible";
}

/** SET16-F4 follow-up (DEC-083 independent review): takes the CALLER's
 * own captured (or freshly read) `gen` as a parameter, for the same
 * reason ensureShelfTierSettingsLoaded does above -- reading loadGeneration
 * internally would always compare equal to itself. Every call site either
 * already has a `gen` in scope (loadCounts) or is a synchronous function
 * with none of its own, in which case it reads loadGeneration directly at
 * the call expression itself (pauseCountSession,
 * handleConfirmCloseCountSession), which captures the same live value a
 * named local would. */
async function refreshCountsStartRow(gen: number): Promise<void> {
  const s = session();
  const scope = countStartScopeKey(s);
  if (countStartScope !== scope) {
    resetCountStartSelection();
    countStartScope = scope;
  }
  const sequence = ++countStartReadSequence;
  const [sessionsResult, countItemsResult] = await Promise.all([
    listCountSessions(s.apiBaseUrl, s.tenantId, { status: "open" }),
    listCountItems(s.apiBaseUrl, s.tenantId, loadTerminalConfig()?.locationId),
  ]);
  // SET16-F4 follow-up: this session's teardown ran while the fetch above
  // was in flight; discard rather than write over what invalidate()
  // already cleared.
  const live = sessionOrUndefined();
  if (!loadIsCurrent(gen) || sequence !== countStartReadSequence || live === undefined ||
    countStartScopeKey(live) !== scope) return;
  const openSession = sessionsResult.status === 200 ? sessionsResult.body.sessions[0] : undefined;
  const hasActiveProducts =
    countItemsResult.status === 200 && countItemsResult.body.items.length > 0;
  // UX102 (click-through review, Count stock): the resume row names whose count it is.
  countsStartButton.textContent =
    openSession !== undefined
      ? `Resume count, ${resolveActorDisplayName(openSession.assigned_counter_display_name)}`
      : "Start a count";
  countsStartButton.dataset["countSessionId"] = openSession?.id ?? "";
  // CORE8: an open count held by someone else shows who has it and, with the reassign right, Take over right here.
  const heldByOther =
    openSession !== undefined && openSession.assigned_counter_employee_id !== s.userId;
  const canTakeOver = hasPermission(s, "inv.count_session.reassign");
  const holderName = resolveActorDisplayName(openSession?.assigned_counter_display_name ?? null);
  countsTakeOverButton.hidden = !(heldByOther && canTakeOver);
  countsTakeOverButton.dataset["countSessionId"] = openSession?.id ?? "";
  countsTakeOverButton.dataset["holderName"] = holderName;
  countsHeldNoteEl.hidden = !(heldByOther && !canTakeOver);
  countsHeldNoteEl.textContent = heldByOther && !canTakeOver ? `Held by ${holderName}` : "";
  // Empty state, honest: no active bottles means no button to start with,
  // never a button that opens onto a walk with nothing in it (unless a
  // count is already open, e.g. every bottle counted was since
  // deactivated; a resumable session is always allowed through).
  const canStart = hasActiveProducts || openSession !== undefined;
  countsStartButton.hidden = !canStart;
  countsStartEmptyEl.hidden = canStart;
  // INV2 (docs/backlog.md, PRD P0.7): "A manager configures each session
  // as blind or quantity-visible before counting begins." Only
  // meaningful for a NEW session; an already-open session to RESUME had
  // its own mode chosen at ITS start, so this picker is hidden rather
  // than shown-but-ignored.
  countsStartModeLabelEl.hidden = openSession !== undefined;
  countsStartModePickerEl.hidden = openSession !== undefined;
  if (openSession === undefined) {
    renderChipRadioGroup(
      countsStartModePickerEl,
      "admin-counts-start-mode-picker-option",
      "admin-counts-start-mode-picker-option",
      COUNT_START_MODE_OPTIONS,
      countStartSelectedMode,
      (value) => { countStartSelectedMode = value === "blind" ? "blind" : "visible"; },
    );
  }
}

async function loadCountHistory(): Promise<void> {
  const gen = loadGeneration;
  const s = session();
  const [result, summaries] = await Promise.all([
    listCountSessions(s.apiBaseUrl, s.tenantId, { status: "closed" }),
    getCountHistorySummaries(s.apiBaseUrl, s.tenantId),
  ]);
  // SET16-F4: this session's teardown ran while the fetch above was in
  // flight; discard rather than write over what invalidate() already
  // cleared.
  if (!loadIsCurrent(gen)) return;
  // INVPOLISH1: a failed or refused summaries read leaves the plain time rows, never an empty list.
  adminCountHistorySummaries = new Map(
    summaries.status === 200 ? summaries.body.summaries.map((summary) => [summary.sessionId, summary] as const) : [],
  );
  if (result.status !== 200) {
    countsHistoryListEl.innerHTML = "";
    const errorEl = document.createElement("p");
    errorEl.className = "tabs-empty";
    errorEl.textContent = "Could not load past counts.";
    countsHistoryListEl.append(errorEl);
    return;
  }
  adminCountHistory = [...result.body.sessions].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );
  renderCountHistoryList();
}

function renderCountHistoryList(): void {
  countsHistoryListEl.innerHTML = "";
  if (adminCountHistory.length === 0) {
    const empty = document.createElement("p");
    empty.className = "tabs-empty";
    empty.dataset["testid"] = "admin-counts-history-empty";
    empty.textContent = "No past counts yet.";
    countsHistoryListEl.append(empty);
    return;
  }
  for (const pastSession of adminCountHistory) {
    const row = document.createElement("button");
    row.type = "button";
    row.className = "admin-count-history-row";
    row.dataset["testid"] = "admin-count-history-row";
    row.dataset["countSessionId"] = pastSession.id;
    const closedAt = pastSession.closed_at !== null ? new Date(pastSession.closed_at) : undefined;
    const when = document.createElement("strong");
    when.className = "admin-count-history-when";
    when.textContent =
      closedAt !== undefined
        ? closedAt.toLocaleString(undefined, {
            dateStyle: "medium",
            timeStyle: "short",
          })
        : "Closed count";
    row.append(when);
    // INVPOLISH1: who counted, what it covered and how far off it was, from the one batched summaries read.
    const summary = adminCountHistorySummaries.get(pastSession.id);
    if (summary !== undefined) {
      const line = countHistoryLine({ counterName: pastSession.assigned_counter_display_name, summary });
      for (const [text, testid] of [
        [line.who, "admin-count-history-who"],
        [line.scope, "admin-count-history-scope"],
        [line.off, "admin-count-history-off"],
      ] as const) {
        if (text === null) continue;
        const detail = document.createElement("span");
        detail.className = "admin-count-history-detail";
        detail.dataset["testid"] = testid;
        detail.textContent = text;
        row.append(detail);
      }
    }
    row.addEventListener("click", () => void openCountHistoryDetail(pastSession));
    countsHistoryListEl.append(row);
  }
}

async function openCountHistoryDetail(pastSession: CountSessionDto): Promise<void> {
  const s = session();
  countHistoryDetailTitleEl.textContent =
    pastSession.closed_at !== null
      ? new Date(pastSession.closed_at).toLocaleString(undefined, {
          dateStyle: "medium",
          timeStyle: "short",
        })
      : "Count";
  countHistoryDetailListEl.innerHTML = "";
  countHistoryDetailEmptyEl.hidden = true;
  countHistoryDetailScrim.hidden = false;
  lockBodyScroll();

  const result = await getCountSessionVariances(s.apiBaseUrl, s.tenantId, pastSession.id);
  if (result.status !== 200) {
    adminToast(
      result.body.message ?? result.body.error ?? "Could not load this count's detail.",
      "danger",
    );
    return;
  }
  adminCountHistoryContainerTypes = result.body.containerTypes ?? {};
  renderVarianceRows(countHistoryDetailListEl, result.body.variances);
  countHistoryDetailEmptyEl.hidden = result.body.variances.length > 0;
}

function closeCountHistoryDetail(): void {
  countHistoryDetailScrim.hidden = true;
  unlockBodyScroll();
}

/** Shared by the history detail sheet and (with a live, not-yet-posted
 * set) the close-confirm sheet: one row per counted product, grouped by
 * section (sort_order first, no section last), each showing counted vs.
 * on-hand-at-start and the resulting variance in plain quantity-and-unit
 * terms. A zero-variance row still renders ("No change"), matching
 * closeCountSession's own "a zero-variance row is still meaningful" rule
 * rather than hiding it. */
function renderVarianceRows(container: HTMLElement, variances: CountSessionVarianceDto[]): void {
  container.innerHTML = "";
  const sections = sortedCountSectionsForGrouping();
  const sectionGroups = new Map<string, CountSessionVarianceDto[]>();
  const noSectionGroup: CountSessionVarianceDto[] = [];
  for (const variance of variances) {
    if (variance.count_section_id === null) {
      noSectionGroup.push(variance);
      continue;
    }
    const existing = sectionGroups.get(variance.count_section_id) ?? [];
    existing.push(variance);
    sectionGroups.set(variance.count_section_id, existing);
  }

  function renderGroup(label: string, rows: CountSessionVarianceDto[]): void {
    if (rows.length === 0) {
      return;
    }
    const heading = document.createElement("p");
    heading.className = "admin-count-review-section-heading";
    heading.textContent = label;
    container.append(heading);
    for (const variance of [...rows].sort((a, b) =>
      a.component_reference.localeCompare(b.component_reference),
    )) {
      const row = document.createElement("div");
      row.className = "admin-count-review-row";
      row.dataset["testid"] = "admin-count-variance-row";

      const name = document.createElement("span");
      name.className = "admin-count-review-row-name";
      name.textContent = variance.component_reference;

      const detail = document.createElement("span");
      detail.className = "admin-count-review-row-detail";
      // CORE8: each figure reads to its unit's precision (one decimal oz, whole or two-decimal bottles).
      // INVPOLISH1: a keg's figure is kegs; the item's own type names the unit instead of "bottles".
      const unit = historyUnitFor(variance.unit, adminCountHistoryContainerTypes[variance.liquor_product_id] ?? null);
      const varianceText = formatCountVarianceForDisplay(variance.variance_quantity, unit);
      detail.textContent = `Counted ${formatCountQuantityForDisplay(variance.counted_quantity, unit)}, was ${formatCountQuantityForDisplay(variance.on_hand_at_start_quantity, unit)} · ${varianceText}`;

      row.append(name, detail);
      container.append(row);
    }
  }

  for (const section of sections) {
    renderGroup(section.name, sectionGroups.get(section.id) ?? []);
  }
  renderGroup("No section", noSectionGroup);
}

async function handleStartOrResumeCount(): Promise<void> {
  const s = session();
  const gen = loadGeneration;
  const existingSessionId = countsStartButton.dataset["countSessionId"];
  if (existingSessionId !== undefined && existingSessionId.length > 0) {
    if (!loadIsCurrent(gen) || sessionOrUndefined() !== s) return;
    await openCountSessionSheet(existingSessionId);
    return;
  }

  const selectedMode = selectedCountStartMode();
  const result = await startCountSession(s.apiBaseUrl, s.tenantId, {
    idempotencyKey: crypto.randomUUID(),
    openedByEmployeeId: s.employeeId,
    blindMode: selectedMode === "blind",
  });
  if (!loadIsCurrent(gen) || sessionOrUndefined() !== s) return;
  if (result.status !== 201 && result.status !== 200) {
    adminToast(plainCountMessage(result.body.message, "Could not start a count."), "danger");
    return;
  }
  resetCountStartSelection();
  await openCountSessionSheet(result.body.session.id);
}

/** INVDESIGN1-S2: main.ts registers where a closed count goes next (the Inventory home's after-count screen). */
let countClosedHandler: ((summary: CountClosedSummary) => void) | undefined;
export function setCountClosedHandler(handler: (summary: CountClosedSummary) => void): void {
  countClosedHandler = handler;
}

/** INVDESIGN1-S2: the Inventory home's Count, Resume and Recount. Opens the one open count (or starts a quantity-visible one
 * when none is open) in the area layout, on `areaId` (null is the Unassigned area; undefined is the first area with something
 * left), optionally on one item. It is the same session, the same writes and the same close as the Counts panel's Start; the
 * panel itself is still behind Count options for a blind start or a take-over. False when nothing could be opened. */
export async function startCountFromHome(
  activeSession: Session,
  options: { areaId?: string | null; itemId?: string } = {},
): Promise<boolean> {
  await openOperationsView(activeSession, "counts");
  const gen = loadGeneration;
  const s = session();
  if (!loadIsCurrent(gen) || sessionOrUndefined() !== s) return false;
  const existing = countsStartButton.dataset["countSessionId"];
  let sessionId = existing !== undefined && existing.length > 0 ? existing : undefined;
  if (sessionId === undefined) {
    const result = await startCountSession(s.apiBaseUrl, s.tenantId, {
      idempotencyKey: crypto.randomUUID(),
      openedByEmployeeId: s.employeeId,
      blindMode: false,
    });
    if (!loadIsCurrent(gen) || sessionOrUndefined() !== s) return false;
    if (result.status !== 201 && result.status !== 200) {
      adminToast(plainCountMessage(result.body.message, "Could not start a count."), "danger");
      return false;
    }
    resetCountStartSelection();
    sessionId = result.body.session.id;
  }
  return openCountTable(countTableContext(), sessionId, { layout: "area", ...options });
}

/** INVCOUNT2-S2. What the count table needs from this screen: the session, permissions, the approval flow behind
 * Close and Take over, and a way back to the walk. The table never reaches into this file itself. */
function countTableContext(): CountTableContext {
  const opened = session();
  const gen = loadGeneration;
  return {
    session: () => sessionOrUndefined(),
    hasPermission: (permission) => hasPermission(session(), permission),
    isCurrent: () =>
      loadIsCurrent(gen) &&
      sessionOrUndefined() !== undefined &&
      sessionOrUndefined()?.token === opened.token,
    toast: (message, variant) => adminToast(message, variant),
    itemKindLabel: (kind) => resolveItemKindLabel(kind),
    actorName: (displayName) => resolveActorDisplayName(displayName),
    mount: countTableMountEl,
    lockScroll: lockBodyScroll,
    unlockScroll: unlockBodyScroll,
    takeOver: (sessionId, holderName) =>
      takeOverCountSession(sessionId, holderName, (message) => adminToast(message, "danger"), {
        confirm: false,
      }),
    closeCount: (sessionId) => closeCountFromTable(sessionId),
    onExit: () => {
      void refreshCountsStartRow(loadGeneration);
    },
    onClosed: (summary) => {
      void refreshCountsStartRow(loadGeneration);
      // INVDESIGN1-S2: the count is closed, so the person lands on "Count closed" and what to order, not back on Count stock.
      countClosedHandler?.(summary);
    },
    openWalk: (sessionId) => {
      void openCountSessionSheet(sessionId);
    },
    publishRoute: () => requestRoutePublish(),
  };
}

/** INVCOUNT2-S2. Close from the table: the same approval-aware close as the walk's, with the same follow-ups. The table
 * shows its own confirmation first and its own close report after. */
async function closeCountFromTable(
  sessionId: string,
): Promise<{ adjustmentCount: number } | undefined> {
  const s = session();
  const idempotencyKey = crypto.randomUUID();
  const gated = await requestApproval(
    "Close this count session",
    (approval) =>
      closeCountSession(s.apiBaseUrl, s.tenantId, sessionId, {
        idempotencyKey,
        closedByEmployeeId: s.employeeId,
        ...approval,
      }),
    () => showAdminScreen(),
  );
  if (gated.canceled) return undefined;
  showAdminScreen();
  const result = gated.result;
  if (result.status !== 201 && result.status !== 200) {
    adminToast(
      plainCountMessage(result.body.message ?? result.body.error, "Could not close this count."),
      "danger",
    );
    return undefined;
  }
  lastRecordedCountSectionIdBySession.delete(sessionId);
  const adjustmentCount = result.body.adjustments.length;
  const summary =
    adjustmentCount === 0
      ? "Count closed. Everything matched; nothing was adjusted."
      : `Count closed. ${adjustmentCount} adjustment${adjustmentCount === 1 ? "" : "s"} posted.`;
  adminToast(summary, "success");
  // CORE7-F1: a count that touched an 86'd item offers to clear the 86 (one tap, never automatic).
  void offerClearStock86(
    [
      ...result.body.variances.map((row) => row.liquor_product_id),
      ...result.body.adjustments.map((row) => row.liquor_product_id),
    ],
    adjustmentCount === 0
      ? "Count closed."
      : `Count closed. ${adjustmentCount} adjustment${adjustmentCount === 1 ? "" : "s"} posted.`,
  );
  await refreshCountsStartRow(loadGeneration);
  await loadCountHistory();
  return { adjustmentCount };
}

async function openCountSessionSheet(
  sessionId: string,
  options: { restoreDraft?: boolean } = {},
): Promise<void> {
  const s = session();
  // INVCOUNT2-S2 (DEC-409): the table is the default; the walk is a hidden option a device chooses from the table.
  if (readCountViewPreference() === "table") {
    await openCountTable(countTableContext(), sessionId);
    return;
  }
  // UX108: read before the sheet is built; building it clears the quantity box, and a close forgets the draft.
  const typedDraft = options.restoreDraft === true ? readCountEntryDraft(sessionId) : undefined;
  const gen = loadGeneration;
  const openSequence = ++countOperationSequence;
  countErrorEl.hidden = true;
  countReassignPanelEl.hidden = true;
  countQuantityInput.value = "";

  const result = await getCountSessionWalk(s.apiBaseUrl, s.tenantId, sessionId);
  if (
    !loadIsCurrent(gen) ||
    sessionOrUndefined() !== s ||
    countOperationSequence !== openSequence
  ) {
    return;
  }
  if (result.status !== 200) {
    adminToast(result.body.message ?? result.body.error ?? "Could not load this count.", "danger");
    return;
  }

  const products: CountWalkProduct[] = result.body.items;
  const entries = new Map<string, CountWalkEntry>();
  for (const observation of result.body.observations) {
    entries.set(observation.liquor_product_id, countWalkEntryFromLine(observation));
  }
  // Resuming picks up wherever the counter left off: the first
  // not-yet-counted product, or the very first product if everything (or
  // nothing) has been counted already.
  const firstUncountedIndex = products.findIndex((product) => !entries.has(product.id));

  // Gate finding B fix. A fresh session (zero observations, entries.size
  // === 0) always starts on "No section", unchanged. A RESUMED session
  // (at least one observation already exists) seeds the sticky chip from
  // the last section this browser tab actually recorded a count under,
  // instead of unconditionally clearing it back to "No section" the way
  // this line used to. See lastRecordedCountSectionIdBySession's own
  // comment for why that map, not the observations response, is the
  // source here.
  const selectedSectionId =
    entries.size > 0 ? lastRecordedCountSectionIdBySession.get(sessionId) : undefined;

  countWalkState = {
    sessionId,
    products,
    index: firstUncountedIndex === -1 ? 0 : firstUncountedIndex,
    selectedSectionId,
    entries,
    view: "walk",
    assignedCounterEmployeeId: result.body.session.assigned_counter_employee_id,
    assignedCounterDisplayName: result.body.session.assigned_counter_display_name,
    blindMode: result.body.session.blind_mode,
  };

  countSessionScrim.hidden = false;
  lockBodyScroll();
  renderCountGovernanceRow();
  renderCountWalkView();
  restoreCountEntryDraft(typedDraft);
  // UX108: the open count is part of the screen's address.
  requestRoutePublish();
}

// UX108: the quantity typed for the product on screen, and the unit chip chosen, before it is saved. Saved counts
// come back from the server and the walk resumes at the first product not yet counted; this is only the one box
// in progress. Keyed by count and product so it can never land on a different one.
const COUNT_ENTRY_DRAFT = "countEntry";

interface CountEntryDraft {
  readonly productId: string;
  readonly quantity: string;
  readonly unit: string;
}

function saveCountEntryDraft(): void {
  const scope = adminDraftScope();
  if (scope === undefined) return;
  const product = currentWalkProduct();
  if (
    countWalkState === undefined ||
    countWalkState.view !== "walk" ||
    product === undefined ||
    countQuantityInput.value.trim() === ""
  ) {
    clearDraft(scope, COUNT_ENTRY_DRAFT);
    return;
  }
  saveDraft(scope, COUNT_ENTRY_DRAFT, {
    sessionId: countWalkState.sessionId,
    productId: product.id,
    quantity: countQuantityInput.value,
    unit: selectedCountUnit(),
  });
}

function readCountEntryDraft(sessionId: string): CountEntryDraft | undefined {
  const scope = adminDraftScope();
  if (scope === undefined) return undefined;
  const record = asRecord(loadDraft(scope, COUNT_ENTRY_DRAFT));
  const productId = asString(record?.["productId"]);
  const quantity = shortText(record?.["quantity"], 20);
  const unit = shortText(record?.["unit"], 20);
  if (asString(record?.["sessionId"]) !== sessionId) return undefined;
  if (productId === undefined || quantity === undefined || unit === undefined) return undefined;
  return { productId, quantity, unit };
}

function restoreCountEntryDraft(draft: CountEntryDraft | undefined): void {
  if (draft === undefined || currentWalkProduct()?.id !== draft.productId) return;
  countQuantityInput.value = draft.quantity;
  const radio = Array.from(
    countUnitPickerEl.querySelectorAll<HTMLInputElement>('input[type="radio"]'),
  ).find((candidate) => candidate.dataset["value"] === draft.unit);
  if (radio !== undefined && !radio.checked) {
    radio.checked = true;
    radio.dispatchEvent(new Event("change", { bubbles: true }));
  }
  saveCountEntryDraft();
}

countQuantityInput.addEventListener("input", saveCountEntryDraft);
countUnitPickerEl.addEventListener("change", saveCountEntryDraft);

// INV2 (docs/backlog.md, PRD P0.7): "Each location count session has one
// assigned counting employee" and "a manager configures each session as
// blind or quantity-visible." Shared across the walk and review views
// (both live under this same sheet header), so this renders once,
// independently of which view is currently showing.
function renderCountGovernanceRow(): void {
  if (countWalkState === undefined) {
    return;
  }
  // PO6-S1-F3 (docs/backlog.md): a resolved display name, or the plain
  // "Former employee" fallback, never countWalkState.assignedCounterEmployeeId
  // (the raw id) itself.
  countAssignedCounterEl.textContent = `Assigned counter: ${resolveActorDisplayName(countWalkState.assignedCounterDisplayName)}`;
  countBlindToggleButton.textContent =
    countWalkState.modeTransition === "reveal"
      ? "Revealing quantities..."
      : countWalkState.modeTransition === "hide"
        ? "Hiding quantities..."
        : countWalkState.modeRefreshRequired
          ? "Try loading quantities again"
          : countWalkState.blindMode
            ? "Blind counting (tap to reveal on-hand)"
            : "Blind count";
  countReassignButton.hidden = !hasPermission(session(), "inv.count_session.reassign");
  // CORE8: someone else holds this count, so Take over sits beside the counter line.
  countSheetTakeOverButton.hidden =
    !hasPermission(session(), "inv.count_session.reassign") ||
    countWalkState.assignedCounterEmployeeId === session().userId;
  countBlindToggleButton.hidden = !hasPermission(session(), "inv.count_session.run");
}

/** Erases rendered expected-stock data and the in-memory review projection.
 * Call before a count leaves Review or becomes blind, and during teardown. */
function purgeCountSensitiveDisplay(state: typeof countWalkState = countWalkState): void {
  if (state !== undefined) {
    state.reviewRows = undefined;
    state.reviewReadAt = undefined;
  }
  countReviewListEl.replaceChildren();
  countCloseConfirmListEl.replaceChildren();
  countReviewHonestyNoteEl.textContent = "";
  countReviewHonestyNoteEl.hidden = true;
  countReviewUncountedNoteEl.textContent = "";
  countReviewEmptyEl.hidden = true;
  countCloseConfirmMessageEl.textContent = "";
  countProductStatusEl.textContent = "";
  countCloseButton.disabled = true;
}

function pauseCountSession(): void {
  countOperationSequence += 1;
  const pausedScope = adminDraftScope();
  if (pausedScope !== undefined) clearDraft(pausedScope, COUNT_ENTRY_DRAFT);
  setCountSheetBusy(false);
  purgeCountSensitiveDisplay();
  countSessionScrim.hidden = true;
  unlockBodyScroll();
  countWalkState = undefined;
  countReassignPanelEl.hidden = true;
  // UX108: the count sheet is closed, so the address goes back to Count stock.
  requestRoutePublish();
  // pauseCountSession has no gen of its own (a synchronous function): the
  // current loadGeneration is read directly at this call, which is
  // correct whether this runs from an ordinary Close tap or from
  // dismissOpenAdminSheets during invalidate() itself, since invalidate()
  // increments loadGeneration BEFORE calling dismissOpenAdminSheets.
  void refreshCountsStartRow(loadGeneration);
}

function currentWalkProduct(): CountWalkProduct | undefined {
  return countWalkState?.products[countWalkState.index];
}

function renderCountProgress(): void {
  if (countWalkState === undefined) {
    return;
  }
  const counted = countWalkState.entries.size;
  const total = countWalkState.products.length;
  countProgressEl.textContent = `${counted} of ${total} counted`;
  countProgressFillEl.style.width = total === 0 ? "0%" : `${Math.round((counted / total) * 100)}%`;
}

function renderCountSectionPicker(): void {
  if (countWalkState === undefined) {
    return;
  }
  // UX88 (DEC-339): a picker of named sections reads A to Z with "No
  // section" first. The count WALK sequence through sections is the
  // configured order and is not touched.
  const options = countSectionPickerOptions(adminCountSections);
  renderChipRadioGroup(
    countSectionPickerEl,
    "admin-count-section-picker-option",
    "admin-count-section-picker-option",
    options,
    countWalkState.selectedSectionId ?? "",
    (value) => {
      if (countWalkState !== undefined) {
        countWalkState.selectedSectionId = value.length > 0 ? value : undefined;
      }
    },
  );
}

function renderCountUnitPicker(
  units: string[],
  defaultUnit: string,
  onUnitChange?: (unit: string) => void,
): void {
  renderChipRadioGroup(
    countUnitPickerEl,
    "admin-count-unit-picker-option",
    "admin-count-unit-picker-option",
    units.flatMap((unit) => {
      const label = COUNT_UNIT_LABELS[unit];
      return label === undefined ? [] : [{ value: unit, label }];
    }),
    defaultUnit,
    (unit) => {
      applyCountQuantityHint(unit);
      onUnitChange?.(unit);
    },
  );
  applyCountQuantityHint(defaultUnit);
}

// INV37 (DEC-337): every unit takes the typed field. Bottles take up to two
// decimal places (1.5, 2.25); the hint says so before the counter guesses.
function applyCountQuantityHint(unit: string): void {
  countQuantityInput.placeholder = unit === "bottle" ? "e.g. 1.5" : "";
}

/** INV37. "1.5 bottles (38.04 oz)", or "1.5 bottles" when no ounce figure
 * is known. Quantities arrive already rounded and trimmed by the server. */
function formatBottlesWithOunces(bottles: string, ounces: string | null): string {
  const base = formatQuantityUnit(bottles, "bottle");
  return ounces === null ? base : `${base} (${roundOuncesForDisplay(ounces)} oz)`;
}

function renderCountWalkView(): void {
  if (countWalkState === undefined) {
    return;
  }
  countWalkState.view = "walk";
  countWalkViewEl.hidden = false;
  countReviewViewEl.hidden = true;
  countWalkFooterEl.hidden = false;
  countReviewFooterEl.hidden = true;
  countGoReviewButton.hidden = false;
  renderCountProgress();

  const product = currentWalkProduct();
  if (product === undefined) {
    countWalkEmptyEl.hidden = false;
    countWalkContentEl.hidden = true;
    return;
  }
  countWalkEmptyEl.hidden = true;
  countWalkContentEl.hidden = false;

  renderCountSectionPicker();

  countProductNameEl.textContent = product.name;
  const existingEntry = countWalkState.entries.get(product.id);
  let statusForUnit: ((unit: string) => string) | undefined;
  if (existingEntry !== undefined) {
    // A counter's own already-saved entry is never withheld by blind
    // mode: blind mode hides the EXPECTED (on-hand) figure from the
    // counter, never their own past work.
    const countedText =
      existingEntry.unit === "bottle"
        ? formatBottlesWithOunces(existingEntry.countedQuantity, existingEntry.storedOunces ?? null)
        : formatQuantityUnit(existingEntry.countedQuantity, existingEntry.unit);
    countProductStatusEl.textContent = `Already counted this session: ${countedText}. Saving again replaces it.`;
    countQuantityInput.value = existingEntry.countedQuantity;
    renderCountUnitPicker(product.countUnits, defaultCountUnitFor(product, existingEntry.unit));
  } else {
    const expectedOnHand = product.expectedOnHand;
    // INV2: blind mode hides the walk's own on-hand figure entirely (see
    // ui/src/countWalk.ts's own header on why this is the counting WALK
    // only, never the review). buildCountWalkProductStatus is the single
    // gate this decision goes through: undefined means "render nothing
    // at all," which covers BOTH blind mode and the pre-existing
    // genuinely-unknown (mixed-unit) case, so this DOM assignment can
    // never disagree with that function's own tested contract. The
    // pluralized "bottle(s)"/"unit(s)" label formatQuantityUnit already
    // establishes is reused for the one case there IS something to show.
    const walkStatus = buildCountWalkProductStatus({
      onHandQuantity: expectedOnHand?.quantityOnHand ?? null,
      onHandUnit: expectedOnHand?.unit ?? null,
      blindMode: countWalkState.blindMode,
    });
    if (walkStatus !== undefined) {
      // expectedOnHand.quantityOnHand is non-null whenever buildCountWalkProductStatus
      // returns a defined result and blindMode is false; see its own
      // contract.
      const onHandQuantity = expectedOnHand?.quantityOnHand as string;
      const onHandUnit = expectedOnHand?.unit ?? "oz";
      const ouncesText = `Currently on hand: ${formatQuantityUnit(
        onHandUnit === "oz" ? roundOuncesForDisplay(onHandQuantity) : onHandQuantity,
        onHandUnit,
      )}.`;
      // INV37: for an item with a bottle size the line reads in bottles
      // while the Bottles chip is selected, with the stored ounces beside
      // it; the Ounces chip shows the plain ounce line.
      const inBottles = product.bottleOunces !== null ? (expectedOnHand?.inBottles ?? null) : null;
      statusForUnit = (unit: string): string =>
        unit === "bottle" && inBottles !== null
          ? `Currently on hand: ${formatBottlesWithOunces(inBottles, onHandUnit === "oz" ? onHandQuantity : null)}.`
          : ouncesText;
      countProductStatusEl.textContent = ouncesText;
    } else if (
      !countWalkState.blindMode &&
      expectedOnHand?.quantityOnHand === null &&
      expectedOnHand.expectedByUnit !== undefined
    ) {
      // INV43: an item held in more than one unit. The server's INV42 expected figure is shown as it came, per unit.
      const byUnit = expectedOnHand.expectedByUnit;
      const ounces = `${roundOuncesForDisplay(byUnit.oz)} oz`;
      statusForUnit = (unit: string): string =>
        unit === "bottle"
          ? `Currently on hand: ${formatBottlesWithOunces(byUnit.bottle, byUnit.oz)}.`
          : `Currently on hand: ${ounces}.`;
      countProductStatusEl.textContent = statusForUnit("bottle");
    } else if (!countWalkState.blindMode && expectedOnHand?.quantityOnHand === null) {
      countProductStatusEl.textContent =
        "On hand is tracked in more than one unit for this item; pick the unit you are counting in.";
    } else {
      countProductStatusEl.textContent = "";
    }
    countQuantityInput.value = "";
    // INV37 (DEC-337): an item with a usable bottle size starts on Bottles;
    // an item without one keeps the unit its on-hand is held in, else the
    // first option, exactly as before.
    const startUnit = defaultCountUnitFor(
      product,
      product.bottleOunces !== null ? "bottle" : (expectedOnHand?.unit ?? undefined),
    );
    renderCountUnitPicker(product.countUnits, startUnit, (unit) => {
      if (statusForUnit !== undefined) {
        countProductStatusEl.textContent = statusForUnit(unit);
      }
    });
    if (statusForUnit !== undefined) {
      countProductStatusEl.textContent = statusForUnit(startUnit);
    }
  }
  countProgressEl.setAttribute(
    "aria-label",
    `Product ${countWalkState.index + 1} of ${countWalkState.products.length}: ${product.name}`,
  );
}

function selectedCountUnit(): string {
  const checked = countUnitPickerEl.querySelector<HTMLInputElement>('input[type="radio"]:checked');
  return checked?.dataset["value"] ?? "oz";
}

function handleCountPrev(): void {
  if (countWalkState === undefined || countWalkState.index === 0) {
    return;
  }
  countWalkState.index -= 1;
  renderCountWalkView();
}

// INV2: "reassignment requires an audited manager action." Populates the
// picker from the live user roster (never a typed employee id, per the
// fixed-vocabulary-picker rule) and opens the panel; the actual POST
// goes through requestApproval exactly like handleConfirmCloseCountSession
// above, so an assistant_manager/inventory_manager caller sees the SAME
// PIN-prompt flow the close route already uses.
/** CORE8: Take over an open count held by someone else. Asks first (a count moves between people, and the move is
 * audited), then reassigns to the signed-in person through the same permissioned, approval-aware route Reassign uses.
 * Returns the reassigned session, or undefined when cancelled or refused (the reason is shown in `showError`). */
async function takeOverCountSession(
  sessionId: string,
  holderName: string,
  showError: (message: string) => void,
  options: { confirm: boolean },
): Promise<CountSessionDto | undefined> {
  const s = session();
  // The count sheet sits above the confirm sheet, so a take over tapped inside the sheet (an explicit, named button
  // on the count itself) goes straight to the audited reassignment; the start row asks first.
  if (options.confirm) {
    const confirmed = await showConfirmSheet({
      title: "Take over this count?",
      message: `${holderName} is counting. It becomes yours; ${holderName}'s saved counts stay.`,
      confirmLabel: "Take over",
      tone: "accent",
    });
    if (!confirmed || sessionOrUndefined() !== s) {
      return undefined;
    }
  }
  const idempotencyKey = crypto.randomUUID();
  // More than one attempt means the approval picker took over the screen and must be left; a plain success never
  // left Count stock, so nothing is re-shown (re-showing the admin screen would drop the count sheet about to open).
  let attempts = 0;
  const gated = await requestApproval(
    "Take over this count",
    (approval) => {
      attempts += 1;
      return reassignCountSession(s.apiBaseUrl, s.tenantId, sessionId, {
        idempotencyKey,
        newCounterEmployeeId: s.userId,
        reassignedByEmployeeId: s.employeeId,
        ...approval,
      });
    },
    () => showAdminScreen(),
  );
  if (gated.canceled) {
    return undefined;
  }
  if (attempts > 1) {
    showAdminScreen();
  }
  const result = gated.result;
  if (result.status !== 201 && result.status !== 200) {
    showError(plainCountMessage(result.body.message, "Could not take over this count."));
    return undefined;
  }
  adminToast("You have this count.", "success");
  return result.body.session;
}

async function handleStartRowTakeOver(): Promise<void> {
  const sessionId = countsTakeOverButton.dataset["countSessionId"];
  if (sessionId === undefined || sessionId.length === 0) {
    return;
  }
  const taken = await takeOverCountSession(
    sessionId,
    countsTakeOverButton.dataset["holderName"] ?? "Another counter",
    (message) => adminToast(message, "danger"),
    { confirm: true },
  );
  if (taken === undefined) {
    return;
  }
  await openCountSessionSheet(sessionId);
}

async function handleSheetTakeOver(): Promise<void> {
  if (countWalkState === undefined) {
    return;
  }
  const taken = await takeOverCountSession(
    countWalkState.sessionId,
    resolveActorDisplayName(countWalkState.assignedCounterDisplayName),
    (message) => {
      countErrorEl.hidden = false;
      countErrorEl.textContent = message;
    },
    { confirm: false },
  );
  if (taken === undefined || countWalkState === undefined) {
    return;
  }
  countWalkState.assignedCounterEmployeeId = taken.assigned_counter_employee_id;
  countWalkState.assignedCounterDisplayName = taken.assigned_counter_display_name;
  countErrorEl.hidden = true;
  renderCountGovernanceRow();
}

async function handleOpenReassignPanel(): Promise<void> {
  const s = session();
  countReassignErrorEl.hidden = true;
  const result = await listUsersForAdmin(s.apiBaseUrl, s.tenantId, { activeOnly: true });
  if (result.status !== 200) {
    adminToast(
      result.body.message ?? result.body.error ?? "Could not load the staff roster.",
      "danger",
    );
    return;
  }
  countReassignSelect.innerHTML = "";
  for (const user of sortByName(result.body.users, (listed) => listed.displayName)) {
    const option = document.createElement("option");
    option.value = user.id;
    option.textContent = user.displayName;
    countReassignSelect.append(option);
  }
  countReassignPanelEl.hidden = false;
}

function handleCancelReassign(): void {
  countReassignPanelEl.hidden = true;
  countReassignErrorEl.hidden = true;
}

async function handleConfirmReassign(): Promise<void> {
  const s = session();
  if (countWalkState === undefined) {
    return;
  }
  const newCounterEmployeeId = countReassignSelect.value;
  if (newCounterEmployeeId.length === 0) {
    countReassignErrorEl.hidden = false;
    countReassignErrorEl.textContent = "Pick who this count is being reassigned to.";
    return;
  }
  const sessionId = countWalkState.sessionId;
  const idempotencyKey = crypto.randomUUID();
  const gated = await requestApproval(
    "Reassign this count session's counter",
    (approval) =>
      reassignCountSession(s.apiBaseUrl, s.tenantId, sessionId, {
        idempotencyKey,
        newCounterEmployeeId,
        reassignedByEmployeeId: s.employeeId,
        ...approval,
      }),
    () => showAdminScreen(),
  );
  if (gated.canceled) {
    return;
  }
  showAdminScreen();
  const result = gated.result;
  if (result.status !== 201 && result.status !== 200) {
    countReassignErrorEl.hidden = false;
    countReassignErrorEl.textContent = plainCountMessage(
      result.body.message,
      "Could not reassign this count.",
    );
    return;
  }
  if (countWalkState !== undefined) {
    countWalkState.assignedCounterEmployeeId = result.body.session.assigned_counter_employee_id;
    countWalkState.assignedCounterDisplayName = result.body.session.assigned_counter_display_name;
  }
  countReassignPanelEl.hidden = true;
  renderCountGovernanceRow();
  adminToast("Count session reassigned.", "success");
}

// INV2: "any later change is audited." No approval fields at all (see
// this route's own server-side comment); the toggle just flips and the
// server writes its own audit row unconditionally.
function applyCountWalkProjection(body: {
  session: CountSessionDto;
  observations: {
    liquor_product_id: string;
    counted_quantity: string;
    unit: string;
    entered_quantity?: string | null;
    entered_unit?: string | null;
    count_section_id: string | null;
  }[];
  items: CountWalkItemDto[];
}): void {
  if (countWalkState === undefined) return;
  const previousProductId = countWalkState.products[countWalkState.index]?.id;
  const entries = new Map<string, CountWalkEntry>();
  for (const observation of body.observations) {
    entries.set(observation.liquor_product_id, countWalkEntryFromLine(observation));
  }
  countWalkState.products = body.items;
  countWalkState.entries = entries;
  countWalkState.blindMode = body.session.blind_mode;
  countWalkState.index =
    previousProductId !== undefined && body.items.some((item) => item.id === previousProductId)
      ? body.items.findIndex((item) => item.id === previousProductId)
      : Math.max(
          0,
          body.items.findIndex((item) => !entries.has(item.id)),
        );
  countWalkState.view = "walk";
  purgeCountSensitiveDisplay(countWalkState);
  renderCountGovernanceRow();
  renderCountWalkView();
}

async function refreshCountWalkAfterModeChange(
  gen: number,
  operation: number,
  sessionId: string,
  sessionSnapshot: Session,
): Promise<void> {
  const result = await getCountSessionWalk(
    sessionSnapshot.apiBaseUrl,
    sessionSnapshot.tenantId,
    sessionId,
  );
  if (!countAsyncIsCurrent(gen, operation, sessionId, sessionSnapshot)) return;
  const state = countWalkState;
  if (state === undefined || state.sessionId !== sessionId) return;
  if (result.status !== 200) {
    state.modeTransition = undefined;
    state.modeRefreshRequired = true;
    // The audited mode now says visible, but expected values are not in
    // memory. Keep blind presentation until a retry gets the projection.
    state.blindMode = true;
    countErrorEl.hidden = false;
    countErrorEl.textContent =
      "Counting mode changed, but quantities did not load. Check your connection and try again.";
    setCountSheetModeRetryOnly();
    renderCountGovernanceRow();
    return;
  }

  applyCountWalkProjection(result.body);
  state.modeRefreshRequired = false;
  state.modeTransition = undefined;
  countErrorEl.hidden = true;
  setCountSheetBusy(false);
  renderCountGovernanceRow();
  renderCountWalkView();
}

async function handleToggleBlindMode(): Promise<void> {
  const s = session();
  const initialState = countWalkState;
  if (initialState === undefined || initialState.modeTransition !== undefined) return;

  const sessionId = initialState.sessionId;
  const gen = loadGeneration;
  const operation = ++countOperationSequence;

  if (initialState.modeRefreshRequired) {
    initialState.modeTransition = "reveal";
    countErrorEl.hidden = true;
    setCountSheetBusy(true);
    renderCountGovernanceRow();
    await refreshCountWalkAfterModeChange(gen, operation, sessionId, s);
    return;
  }

  const wasBlind = initialState.blindMode;
  const newBlindMode = !wasBlind;
  initialState.modeTransition = newBlindMode ? "hide" : "reveal";
  countErrorEl.hidden = true;
  setCountSheetBusy(true);
  renderCountGovernanceRow();

  const result = await setCountSessionMode(s.apiBaseUrl, s.tenantId, sessionId, {
    idempotencyKey: crypto.randomUUID(),
    blindMode: newBlindMode,
    changedByEmployeeId: s.employeeId,
  });
  if (!countAsyncIsCurrent(gen, operation, sessionId, s)) return;
  if (result.status !== 201 && result.status !== 200) {
    initialState.modeTransition = undefined;
    countErrorEl.hidden = false;
    countErrorEl.textContent = wasBlind
      ? "Could not reveal quantities. This count is still blind. Check your connection and try again."
      : "Could not hide quantities. This count is still quantity-visible. Check your connection and try again.";
    setCountSheetBusy(false);
    renderCountGovernanceRow();
    return;
  }

  if (newBlindMode) {
    // Remove every expected value before the blind walk or its controls
    // become available again.
    for (const product of initialState.products) delete product.expectedOnHand;
    initialState.blindMode = true;
    initialState.modeTransition = undefined;
    initialState.modeRefreshRequired = false;
    purgeCountSensitiveDisplay(initialState);
    initialState.view = "walk";
    countErrorEl.hidden = true;
    renderCountGovernanceRow();
    renderCountWalkView();
    setCountSheetBusy(false);
    return;
  }

  // Do not expose values from the old blind projection or enable walk
  // controls until the audited visible-mode projection has loaded.
  initialState.modeTransition = "reveal";
  await refreshCountWalkAfterModeChange(gen, operation, sessionId, s);
}

function handleCountSkip(): void {
  if (countWalkState === undefined) {
    return;
  }
  if (countWalkState.index + 1 < countWalkState.products.length) {
    countWalkState.index += 1;
    renderCountWalkView();
  } else {
    void goToCountReview();
  }
}

async function handleCountSaveNext(): Promise<void> {
  const s = session();
  if (countWalkState === undefined) {
    return;
  }
  const product = currentWalkProduct();
  if (product === undefined) {
    return;
  }
  const rawQuantity = normalizeLeadingDot(countQuantityInput.value);
  if (!COUNT_QUANTITY_PATTERN.test(rawQuantity)) {
    countErrorEl.hidden = false;
    countErrorEl.textContent = "Enter the counted quantity as a number, e.g. 12 or 8.5.";
    return;
  }
  const unit = selectedCountUnit();
  // INV37 (DEC-337): bottles take up to two decimal places. The server is
  // authoritative; this is the early, plain-words refusal.
  if (unit === "bottle" && !COUNT_BOTTLE_QUANTITY_PATTERN.test(rawQuantity)) {
    countErrorEl.hidden = false;
    countErrorEl.textContent =
      "Bottles take up to two decimal places, e.g. 1.5 or 2.25. Count in ounces for a finer reading.";
    return;
  }
  countErrorEl.hidden = true;

  const result = await recordCount(s.apiBaseUrl, s.tenantId, countWalkState.sessionId, {
    idempotencyKey: crypto.randomUUID(),
    liquorProductId: product.id,
    countedQuantity: rawQuantity,
    unit,
    countSectionId: countWalkState.selectedSectionId,
    recordedByEmployeeId: s.employeeId,
  });
  if (result.status !== 201 && result.status !== 200) {
    countErrorEl.hidden = false;
    // CORE8: a count held by someone else is a plain sentence naming them, never the server's message with its id.
    countErrorEl.textContent =
      result.body.error === "count_session_wrong_counter"
        ? countHeldByOtherMessage(
            resolveActorDisplayName(countWalkState.assignedCounterDisplayName),
            hasPermission(s, "inv.count_session.reassign"),
          )
        : plainCountMessage(result.body.message, "Could not save that count. Try again.");
    renderCountGovernanceRow();
    return;
  }

  countWalkState.entries.set(product.id, countWalkEntryFromLine(result.body.line));
  const savedScope = adminDraftScope();
  if (savedScope !== undefined) clearDraft(savedScope, COUNT_ENTRY_DRAFT);
  // Gate finding B fix: remember the section this save actually used, so
  // a later pause/resume of this same session (within this same browser
  // tab) can seed the sticky chip honestly instead of resetting it.
  lastRecordedCountSectionIdBySession.set(
    countWalkState.sessionId,
    result.body.line.count_section_id ?? undefined,
  );

  if (countWalkState.index + 1 < countWalkState.products.length) {
    countWalkState.index += 1;
    renderCountWalkView();
  } else {
    void goToCountReview();
  }
}

async function goToCountReview(): Promise<void> {
  const initialState = countWalkState;
  if (initialState === undefined || initialState.view !== "walk") return;

  const sessionId = initialState.sessionId;
  const gen = loadGeneration;
  const operation = ++countOperationSequence;
  countErrorEl.hidden = true;
  countGoReviewButton.textContent = "Loading review...";
  setCountSheetBusy(true);

  const s = session();
  const result = await getCountSessionReview(s.apiBaseUrl, s.tenantId, sessionId);
  // Bug fix (docs writer finding, 2026-09-29): Close count sometimes
  // stayed disabled after entering Review. Root cause: this function is
  // the only caller that turns the whole sheet "busy" (setCountSheetBusy
  // (true) above, disabling every control including Close count) BEFORE
  // its own await, then relied on a SINGLE combined guard
  // (countAsyncIsCurrent) to decide whether to restore it. That guard
  // OR's four unrelated conditions together, so a discard for ANY of
  // them (a session/tenant change, or invalidate()'s own loadGeneration
  // bump from leaving the admin screen entirely) took the exact same
  // silent `return` as a discard because a NEWER goToCountReview call
  // had already superseded this one -- but only the second case is safe
  // to leave uncleaned, because that newer call owns its own cleanup.
  // The first case left this call's setCountSheetBusy(true) permanently
  // in effect: nothing else ever calls setCountSheetBusy(false) for a
  // count session that is not being torn down. Split the single guard
  // into what it actually means: a strictly newer count operation means
  // someone else will finish the job, so bail with no cleanup; anything
  // else means THIS is still the most recent count operation and must
  // restore the busy toggle itself before giving up, exactly like the
  // result.status !== 200 branch two lines below already does.
  if (countOperationSequence !== operation) return;
  if (!countAsyncIsCurrent(gen, operation, sessionId, s)) {
    setCountSheetBusy(false);
    countGoReviewButton.textContent = "Review and close →";
    return;
  }
  if (result.status !== 200) {
    countGoReviewButton.textContent = "Review and close →";
    countErrorEl.hidden = false;
    countErrorEl.textContent =
      "Could not load the review. Your saved counts are still here. Check your connection and try again.";
    setCountSheetBusy(false);
    return;
  }

  initialState.reviewRows = result.body.rows;
  initialState.reviewReadAt = new Date();
  initialState.view = "review";
  countWalkViewEl.hidden = true;
  countReviewViewEl.hidden = false;
  countWalkFooterEl.hidden = true;
  countReviewFooterEl.hidden = false;
  countGoReviewButton.hidden = true;
  countGoReviewButton.textContent = "Review and close →";
  setCountSheetBusy(false);
  // Restore the controls captured before the request, then apply the
  // review-specific disabled state. Otherwise the busy-state restoration
  // can re-enable Close count when the review has no saved rows.
  renderCountReview();
}

/**
 * The variance PREVIEW: counted minus the current expected figure fetched
 * when the counter opens Review, never the authoritative number.
 * The real, posted variance (closeCountSession's own computation) uses
 * on-hand AS OF THE SESSION'S OWN OPENING instant, which can differ from
 * this preview if a receipt or another Prepared depletion happened while
 * this count was in progress. That gap is disclosed here in plain
 * language rather than presented as exact, per this project's "never
 * state something as fact that has not been verified" rule; the
 * close-confirm sheet repeats the same caveat right before the real post.
 *
 * Gate finding A fix: that disclosure previously existed only as this
 * comment. It is now a real, always-visible paragraph
 * (#admin-count-review-honesty-note, countReviewHonestyNoteEl below),
 * never a tooltip or title attribute (CLAUDE.md's touch-only rule rules
 * those out as the sole recovery for hidden content anyway), shown
 * whenever there is at least one counted product to preview.
 */
function renderCountReview(): void {
  if (countWalkState === undefined) {
    return;
  }
  renderCountProgress();
  const reviewRows = countWalkState.reviewRows ?? [];
  countReviewEmptyEl.hidden = reviewRows.length > 0;
  countReviewListEl.innerHTML = "";
  // Design principle 3, "blocked is a state you see before you touch
  // it": Close count is visibly disabled, not silently a no-op, whenever
  // there is nothing yet to close.
  countCloseButton.disabled = reviewRows.length === 0;
  if (reviewRows.length === 0) {
    countReviewHonestyNoteEl.hidden = true;
    countReviewUncountedNoteEl.textContent = "";
    return;
  }
  countReviewHonestyNoteEl.hidden = false;
  const reviewReadAt = countWalkState.reviewReadAt;
  countReviewHonestyNoteEl.textContent =
    `This preview was read at ${reviewReadAt?.toLocaleString() ?? "just now"}. ` +
    "The adjustment that actually posts when you close this count uses on-hand as of the moment this count started, so they can differ if a drink was poured while you were counting.";
  renderCountSessionReviewRows(countReviewListEl, reviewRows);

  const uncountedCount = countWalkState.products.length - reviewRows.length;
  countReviewUncountedNoteEl.textContent =
    uncountedCount > 0
      ? `${uncountedCount} item${uncountedCount === 1 ? "" : "s"} not counted. Nothing posts for those; on-hand stays exactly as it is.`
      : "Every active item was counted.";
}

function renderCountSessionReviewRows(
  container: HTMLElement,
  rows: CountSessionReviewRowDto[],
): void {
  container.innerHTML = "";
  const groups = new Map<string | null, CountSessionReviewRowDto[]>();
  for (const row of rows) {
    const group = groups.get(row.sectionName) ?? [];
    group.push(row);
    groups.set(row.sectionName, group);
  }

  function renderGroup(label: string, groupRows: CountSessionReviewRowDto[]): void {
    if (groupRows.length === 0) return;
    const heading = document.createElement("p");
    heading.className = "admin-count-review-section-heading";
    heading.textContent = label;
    container.append(heading);
    for (const rowData of [...groupRows].sort((a, b) =>
      compareNames(a.liquorProductName, b.liquorProductName),
    )) {
      const row = document.createElement("div");
      row.className = "admin-count-review-row";
      row.dataset["testid"] = "admin-count-variance-row";

      const name = document.createElement("span");
      name.className = "admin-count-review-row-name";
      name.textContent = rowData.liquorProductName;

      const detail = document.createElement("span");
      detail.className = "admin-count-review-row-detail";
      const variance = Number(rowData.previewVariance);
      const view = rowData.bottleView;
      if (view !== null) {
        // INV37: a line counted in bottles reads in bottles, with the
        // counted ounces beside the counted figure.
        const shownVariance =
          view.previewVariance === "0" && variance !== 0
            ? variance > 0
              ? "<0.01"
              : ">-0.01"
            : view.previewVariance;
        const varianceText =
          variance === 0
            ? "No change"
            : `${variance > 0 ? "+" : ""}${formatQuantityUnit(shownVariance, "bottle")}`;
        detail.textContent = `Counted ${formatBottlesWithOunces(view.countedQuantity, view.countedOunces)}, was ${formatCountQuantityForDisplay(view.expectedQuantity, "bottle")} · ${varianceText}`;
      } else {
        // CORE8: the review read "-25.3606 oz"; every figure now reads to its unit's precision.
        const varianceText = formatCountVarianceForDisplay(rowData.previewVariance, rowData.unit);
        detail.textContent = `Counted ${formatCountQuantityForDisplay(rowData.countedQuantity, rowData.unit)}, was ${formatCountQuantityForDisplay(rowData.expectedQuantity, rowData.unit)} · ${varianceText}`;
      }
      row.append(name, detail);
      container.append(row);
    }
  }

  const sectionNames = [...groups.keys()].filter((name): name is string => name !== null);
  const configuredOrder = sortedCountSectionsForGrouping().map((section) => section.name);
  const orderedNames = [
    ...configuredOrder.filter((name) => groups.has(name)),
    ...sectionNames.filter((name) => !configuredOrder.includes(name)).sort(compareNames),
  ];
  for (const name of orderedNames) renderGroup(name, groups.get(name) ?? []);
  renderGroup("No section", groups.get(null) ?? []);
}

function backToCountWalk(): void {
  if (countWalkState === undefined) {
    return;
  }
  purgeCountSensitiveDisplay(countWalkState);
  renderCountWalkView();
}

function handleOpenCloseConfirm(): void {
  if (countWalkState === undefined || countWalkState.entries.size === 0) {
    return;
  }
  const nonZeroCount = [...countWalkState.entries.values()].length;
  countCloseConfirmMessageEl.textContent = `${nonZeroCount} item${nonZeroCount === 1 ? "" : "s"} counted. Closing posts an adjustment for any item whose count differs from on-hand at the moment this count started; a match posts nothing. This cannot be undone.`;
  // Reuses the same preview rows renderCountReview just built, so the
  // confirm sheet and the review screen never disagree with each other.
  countCloseConfirmListEl.innerHTML = countReviewListEl.innerHTML;
  countCloseConfirmScrim.hidden = false;
  lockBodyScroll();
}

function closeCountCloseConfirm(): void {
  countCloseConfirmScrim.hidden = true;
  unlockBodyScroll();
}

// RBAC track: inv.count_session.close reaches this flow whenever the
// server answers approval_required. As with main.ts's own
// handleRemoveSavedCard, the wiring here is a property of the permission
// MODEL rather than of any one seeded default: an Admin can set any
// permission to approval_required at runtime once the Edit Role screen
// ships, so a guarded call site stays approval-aware regardless of what
// DEFAULT_ROLE_PERMISSION_TEMPLATE happens to say today. See
// main.ts's handleReopenClick comment on requestApproval's "only shows
// the picker if the first, no-approval attempt actually comes back
// approval_required" contract; every other role's own Close flow behaves
// completely unchanged. #admin-count-close-confirm-scrim is nested inside
// #admin-screen, so the returnTo/success calls to showAdminScreen below
// simply un-hide it again exactly as it was, rather than needing to
// reconstruct any of its own state.
async function handleConfirmCloseCountSession(): Promise<void> {
  const s = session();
  if (countWalkState === undefined) {
    return;
  }
  const sessionId = countWalkState.sessionId;
  const idempotencyKey = crypto.randomUUID();
  const closedByEmployeeId = s.employeeId;
  countCloseConfirmConfirmButton.disabled = true;
  try {
    const gated = await requestApproval(
      "Close this count session",
      (approval) =>
        closeCountSession(s.apiBaseUrl, s.tenantId, sessionId, {
          idempotencyKey,
          closedByEmployeeId,
          ...approval,
        }),
      () => showAdminScreen(),
    );
    if (gated.canceled) {
      // requestApproval's own returnTo already re-shows the admin screen
      // with this confirm sheet untouched; nothing changed, no toast.
      return;
    }
    showAdminScreen();
    const result = gated.result;
    if (result.status !== 201 && result.status !== 200) {
      adminToast(
        result.body.message ?? result.body.error ?? "Could not close this count.",
        "danger",
      );
      return;
    }
    closeCountCloseConfirm();
    countSessionScrim.hidden = true;
    unlockBodyScroll();
    countWalkState = undefined;
    // UX108: the count is closed, so the address goes back to Count stock.
    requestRoutePublish();
    // Gate finding B fix: this session is closed and can never be resumed
    // again, so its sticky-section memory is done being useful. Dropping
    // it here keeps the map bounded over a long-lived tab.
    lastRecordedCountSectionIdBySession.delete(sessionId);
    const adjustmentCount = result.body.adjustments.length;
    adminToast(
      adjustmentCount === 0
        ? "Count closed. Everything matched; nothing was adjusted."
        : `Count closed. ${adjustmentCount} adjustment${adjustmentCount === 1 ? "" : "s"} posted.`,
      "success",
    );
    // handleConfirmCloseCountSession has no gen of its own; loadGeneration
    // is read directly here for the identical reason pauseCountSession's
    // own call above does.
    // CORE7-F1: a count that touched an 86'd item offers to clear the 86 (one tap, never automatic).
    void offerClearStock86(
      [
        ...result.body.variances.map((row) => row.liquor_product_id),
        ...result.body.adjustments.map((row) => row.liquor_product_id),
      ],
      adjustmentCount === 0
        ? "Count closed."
        : `Count closed. ${adjustmentCount} adjustment${adjustmentCount === 1 ? "" : "s"} posted.`,
    );
    await refreshCountsStartRow(loadGeneration);
    await loadCountHistory();
  } finally {
    countCloseConfirmConfirmButton.disabled = false;
  }
}

// ---------------------------------------------------------------------
// Auth track (track/auth) Slice 1: Settings > Users
//
// A list of every user (active and inactive both, unlike the pre-login
// tile picker's activeOnly=true read - a manager needs to see who left
// and who is locked out too), an Add User mini-form, and per-row
// Deactivate/Reset PIN/Unlock actions. createUser and resetPin both mint
// a system-random temporary PIN server-side (never a caller-chosen one,
// src/domain/users.ts's own createUser comment), returned exactly once
// and shown through the shared #pin-reveal-scrim; nothing about it is
// stored or logged after the dismiss.
// ---------------------------------------------------------------------

function isUserLocked(user: UserDto): boolean {
  return user.lockedUntil !== null && new Date(user.lockedUntil).getTime() > Date.now();
}

// U7: the Add user sheet's own single-select role chip row (DEC-103's
// "the edit form's fields plus the role picker" read as the EXISTING
// single-select chip, not the edit form's own multi-select -- createUser
// takes one role: string, and multi-role-at-creation is a capability
// increase no ruling has asked for; see the build brief's "Which role
// picker" section).
function renderAddUserRolePicker(): void {
  renderChipRadioGroup(
    addUserRolePickerEl,
    "admin-add-user-role",
    "admin-add-user-role-option",
    roleOptions(),
    addUserRole,
    (value) => {
      addUserRole = value;
    },
  );
}

/** Multi-select chip group (checkboxes, not radios), the SAME
 * .admin-chip-select/.admin-chip-option markup and CSS renderChipRadioGroup
 * uses -- that CSS already documents itself as "a real checkbox under the
 * hood" (ui/styles.css), so no new styling is needed for this to render
 * correctly, touch-first, in all four themes. `onChange` receives the
 * FULL resulting checked set on every toggle, matching setUserRoles'
 * own "send the complete intended set" contract. */
// USR3: per-container save chain, so two chips tapped in quick succession
// invoke onChange (and whatever save it starts) strictly in click order,
// never concurrently. Keyed by the container element itself rather than
// by any caller-specific id, because every consumer of this shared helper
// re-renders that SAME container in place; two taps on chips inside it are
// the identical race VEN7's own brief named as a sibling
// (docs/build-briefs/VEN7-vendor-delivery-day-save-race.md, Section 4)
// but did not build. A container absent from this map has no onChange
// call in flight.
const chipCheckboxGroupSaveChains = new WeakMap<HTMLElement, Promise<void>>();

function renderChipCheckboxGroup(
  container: HTMLElement,
  testId: string,
  options: { value: string; label: string }[],
  selectedValues: readonly string[],
  onChange: (nextValues: string[], toggledValue: string, nowChecked: boolean) => Promise<void>,
  describedById?: string,
): void {
  container.innerHTML = "";
  for (const option of options) {
    const optionLabel = document.createElement("label");
    optionLabel.className = "admin-chip-option";
    const input = document.createElement("input");
    describeSetting(input, describedById);
    input.type = "checkbox";
    input.className = "admin-chip-option-input";
    input.dataset["testid"] = testId;
    input.dataset["value"] = option.value;
    input.checked = selectedValues.includes(option.value);
    input.addEventListener("change", () => {
      // USR3: read every checkbox actually checked in THIS container
      // right now, rather than deriving `next` from `selectedValues` (a
      // snapshot closed over at the LAST render of this container). Two
      // chips tapped before either save has re-rendered the group both
      // close over that identical, now-stale `selectedValues`; reading
      // the live DOM instead means the second tap's own `next` already
      // reflects the first tap's own checkbox, because the browser
      // applies a checkbox's own `.checked` synchronously on click,
      // independent of any pending network round trip or re-render.
      const next = options
        .map((candidate) => candidate.value)
        .filter((value) => {
          if (value === option.value) return input.checked;
          const sibling = container.querySelector<HTMLInputElement>(`input[data-value="${value}"]`);
          return sibling?.checked ?? selectedValues.includes(value);
        });
      if (next.length === 0) {
        // Client-side mirror of setUserRoles' own "at least one role"
        // invariant (src/domain/permissions.ts): block the toggle that
        // would leave zero roles checked, rather than letting the PATCH
        // round-trip and 400 back. Revert the checkbox visually.
        input.checked = true;
        adminToast("A person must keep at least one role.", "danger");
        return;
      }
      // USR3: chain onto this container's own prior call, so a second
      // tap's own onChange (and the save/re-render it starts) never runs
      // concurrently with the first's -- otherwise whichever save's
      // response is processed last would repaint over the other's,
      // regardless of how correct `next` is on the wire.
      const previous = chipCheckboxGroupSaveChains.get(container) ?? Promise.resolve();
      const call = previous
        .catch(() => undefined)
        .then(() => onChange(next, option.value, input.checked));
      chipCheckboxGroupSaveChains.set(container, call);
    });
    const text = document.createElement("span");
    text.className = "admin-chip-option-label";
    text.textContent = option.label;
    optionLabel.append(input, text);
    container.append(optionLabel);
  }
}

function showPinReveal(title: string, pin: string): void {
  pinRevealTitleEl.textContent = title;
  pinRevealValueEl.textContent = pin;
  pinRevealScrim.hidden = false;
  lockBodyScroll();
}

function closePinReveal(): void {
  pinRevealScrim.hidden = true;
  // Nothing about a shown PIN is kept once dismissed (this panel's own
  // header comment, and the migration/module-header "shown once" rule);
  // clearing the text content is belt-and-suspenders alongside the scrim
  // simply being hidden, in case something outside this module ever
  // reads the DOM directly.
  pinRevealValueEl.textContent = "";
  unlockBodyScroll();
}

async function loadUsers(): Promise<void> {
  const gen = loadGeneration;
  const s = session();
  userListEl.innerHTML = '<p class="tabs-loading">Loading people…</p>';
  const result = await listUsersForAdmin(s.apiBaseUrl, s.tenantId, { activeOnly: false });
  // SET16-F4: this session's teardown ran while the fetch above was in
  // flight; discard rather than write A's own roster back over whatever
  // invalidate() already cleared for B.
  if (!loadIsCurrent(gen)) return;
  if (result.status !== 200) {
    userListEl.innerHTML = `<p class="tabs-empty">Could not load people (${result.body.message ?? result.body.error ?? result.status}).</p>`;
    adminToast(
      `Could not load people. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`,
      "danger",
    );
    return;
  }
  // SET15: set only on success; see loadDrinks's own comment above.
  usersLoaded = true;
  adminUsers = result.body.users;
  renderUserList();
  // RT1/RT2: the role rows carry a holder count read from this same list,
  // so they render from here. Loading users is already what opening
  // People and access does, so the two can never disagree about who holds
  // what.
  renderRolesList();
  // SET17-F3: loadTenantRoles() used to fire from here (`void
  // loadTenantRoles()`), which made it invisible to
  // check:loader-permission-coupling's text scan of showSection: the gate
  // reads loaders out of showSection's own text, and a loader reached only
  // through this kind of intermediate call is out of reach for it. Moved to
  // showSection's own "people" branch instead, called directly alongside
  // loadUsers(), the same fix SET10 made for loadVocabularies. Not called
  // from here anymore.
}

function renderUserList(): void {
  userListEl.innerHTML = "";
  if (adminUsers.length === 0) {
    const empty = document.createElement("p");
    empty.className = "tabs-empty";
    empty.dataset["testid"] = "admin-user-list-empty";
    empty.textContent = "No people yet.";
    userListEl.append(empty);
    return;
  }
  // SET5: the server orders this list now (active first, then name, with
  // a created_at tiebreak), so this render walks it as given rather than
  // re-sorting. Two surfaces outside this file read the same route and
  // did NOT sort, which is why the order moved into the query.
  // UX88 (DEC-339): re-sorted here with the shared name sort so case and
  // accents never reorder a name the way the database collation does.
  const activeUsers = sortByName(
    adminUsers.filter((user) => user.active),
    (user) => user.displayName,
  );
  const inactiveUsers = sortByName(
    adminUsers.filter((user) => !user.active),
    (user) => user.displayName,
  );

  for (const user of activeUsers) {
    userListEl.append(buildUserRow(user));
  }

  if (inactiveUsers.length > 0) {
    userListEl.append(buildInactiveUserGroup(inactiveUsers));
  }
}

/* SET5. Disabled accounts are COLLAPSED, never dropped: every row keeps
 * the actions it had, because reactivating someone is done from here.
 * Collapsed by default, since a disabled account is the one you are not
 * looking for. Reuses the permission grid's own collapsible classes
 * rather than inventing a second visual language for the same idea. */
function buildInactiveUserGroup(inactiveUsers: readonly AdminUserDto[]): HTMLElement {
  const groupEl = document.createElement("div");
  groupEl.className = "admin-permission-group";
  groupEl.dataset["testid"] = "admin-inactive-user-group";

  const header = document.createElement("button");
  header.type = "button";
  header.className = "admin-permission-group-header";
  header.dataset["testid"] = "admin-inactive-user-group-header";
  header.setAttribute("aria-expanded", inactiveUsersCollapsed ? "false" : "true");

  const caret = document.createElement("span");
  caret.className = "admin-permission-group-caret";
  caret.setAttribute("aria-hidden", "true");
  caret.textContent = inactiveUsersCollapsed ? "▸" : "▾";

  const title = document.createElement("span");
  title.textContent = "Disabled accounts";

  const count = document.createElement("span");
  count.className = "admin-permission-group-count";
  count.dataset["testid"] = "admin-inactive-user-group-count";
  count.textContent = inactiveUsers.length === 1 ? "1 account" : `${inactiveUsers.length} accounts`;

  header.append(caret, title, count);
  header.addEventListener("click", () => {
    inactiveUsersCollapsed = !inactiveUsersCollapsed;
    renderUserList();
  });
  groupEl.append(header);

  if (!inactiveUsersCollapsed) {
    const rowsEl = document.createElement("div");
    rowsEl.className = "admin-permission-rows";
    rowsEl.dataset["testid"] = "admin-inactive-user-rows";
    for (const user of inactiveUsers) {
      rowsEl.append(buildUserRow(user));
    }
    groupEl.append(rowsEl);
  }
  return groupEl;
}

function buildUserRow(user: AdminUserDto): HTMLElement {
  const locked = isUserLocked(user);

  const row = document.createElement("div");
  row.className = "admin-user-row";
  row.dataset["testid"] = "admin-user-row";
  row.dataset["userId"] = user.id;

  const info = document.createElement("div");
  info.className = "admin-user-info";
  const name = document.createElement("div");
  name.className = "admin-user-name";
  name.textContent = user.displayName;
  info.append(name);

  // U1: role editing moved OFF the row and into the Edit User modal's
  // User Information tab, alongside the profile fields it belongs
  // with. The row keeps a read-only summary so the list still answers
  // "who does what here" at a glance without opening anything, and the
  // per-row Reset PIN / Deactivate / Unlock actions are unchanged.
  const roleSummary = document.createElement("div");
  roleSummary.className = "admin-permission-planned";
  roleSummary.dataset["testid"] = "admin-user-role-summary";
  roleSummary.textContent = (
    user.roles !== undefined && user.roles.length > 0 ? user.roles : [user.role]
  )
    .map(roleLabel)
    .join(", ");
  info.append(roleSummary);

  const badges = document.createElement("div");
  badges.className = "admin-user-badges";

  const statusBadge = document.createElement("span");
  statusBadge.className = user.active
    ? "admin-user-badge admin-user-badge-active"
    : "admin-user-badge admin-user-badge-inactive";
  statusBadge.dataset["testid"] = "admin-user-status-badge";
  statusBadge.textContent = user.active ? "Active" : "Inactive";
  badges.append(statusBadge);

  if (locked) {
    const lockedBadge = document.createElement("span");
    lockedBadge.className = "admin-user-badge admin-user-badge-locked";
    lockedBadge.dataset["testid"] = "admin-user-locked-badge";
    lockedBadge.textContent = "Locked";
    badges.append(lockedBadge);
  }
  info.append(badges);
  row.append(info);

  const actions = document.createElement("div");
  actions.className = "admin-user-actions";

  const editButton = document.createElement("button");
  editButton.type = "button";
  editButton.className = "btn-secondary btn-compact";
  editButton.dataset["testid"] = "admin-user-edit";
  editButton.textContent = "Edit";
  editButton.addEventListener("click", () => openEditUser(user));
  actions.append(editButton);

  if (user.active) {
    const resetButton = document.createElement("button");
    resetButton.type = "button";
    resetButton.className = "btn-secondary btn-compact";
    resetButton.dataset["testid"] = "admin-user-reset-pin";
    resetButton.textContent = "Reset PIN";
    // T9: opens the generate-or-choose sheet; the write happens there.
    resetButton.addEventListener("click", () => openResetPinSheet(user));
    actions.append(resetButton);

    const deactivateButton = document.createElement("button");
    deactivateButton.type = "button";
    deactivateButton.className = "btn-secondary btn-compact";
    deactivateButton.dataset["testid"] = "admin-user-deactivate";
    deactivateButton.textContent = "Deactivate";
    deactivateButton.addEventListener("click", () => void handleDeactivateUser(user));
    actions.append(deactivateButton);
  }
  if (locked) {
    const unlockButton = document.createElement("button");
    unlockButton.type = "button";
    unlockButton.className = "btn-secondary btn-compact";
    unlockButton.dataset["testid"] = "admin-user-unlock";
    unlockButton.textContent = "Unlock";
    unlockButton.addEventListener("click", () => void handleUnlockUser(user));
    actions.append(unlockButton);
  }
  // UIFIX4 (SF-17): Edit stays in view; Reset PIN, Deactivate and Unlock share one More menu.
  collapseRowActions(actions, { subject: user.displayName });
  row.append(actions);

  return row;
}

// ---------------------------------------------------------------------
// U7 (DEC-103, DEC-107): + Add user opens a full-screen sheet, not an
// inline mini-form. The profile step and the PIN-choice step are ONE
// client-held flow: no network write happens between them, and exactly
// ONE call to createUser fires, when the PIN choice is submitted.
// Closing the sheet at any point before that creates nothing. See the
// U7 build brief's "Create timing" section for why (the rejected
// alternative would have minted and then sometimes discarded a live,
// usable generated PIN).
// ---------------------------------------------------------------------

function openAddUserSheet(): void {
  addUserDisplayNameInput.value = "";
  addUserFirstNameInput.value = "";
  addUserLastNameInput.value = "";
  addUserEmailInput.value = "";
  addUserPhoneInput.value = "";
  addUserRole = "lounger";
  addUserDisplayNameFollowsFirstName = true;
  renderAddUserRolePicker();
  setAddUserProfileError(undefined);
  resetAddUserPinPanel();
  showAddUserProfileStep();
  // Only one of the two sheets nested in the shared scrim is ever
  // unhidden at a time; see index.html's own comment on
  // #admin-add-user-sheet for why they share one backdrop.
  userEditSheetEl.hidden = true;
  addUserSheetEl.hidden = false;
  userEditScrim.hidden = false;
  lockBodyScroll();
}

function closeAddUserSheet(): void {
  userEditScrim.hidden = true;
  addUserSheetEl.hidden = true;
  userEditSheetEl.hidden = false;
  // Never leave a typed PIN sitting in the DOM after the sheet closes,
  // matching closeResetPinSheet's own clearing.
  addUserPinValueInput.value = "";
  addUserPinConfirmInput.value = "";
  unlockBodyScroll();
}

/** UX64 (DEC-211, corrected). Called on every First name keystroke: while
 * Display name is still following (addUserDisplayNameFollowsFirstName),
 * mirror First name's value into it. Once the person has typed into
 * Display name directly (handleAddUserDisplayNameInput below detaches
 * it), this does nothing, even if First name is edited again afterward --
 * an override is never silently re-overwritten by First name. */
function handleAddUserFirstNameInput(): void {
  if (!addUserDisplayNameFollowsFirstName) return;
  addUserDisplayNameInput.value = addUserFirstNameInput.value;
}

/** UX64 (DEC-211, corrected). Called on every Display name keystroke,
 * including a programmatic one this module fires itself (input events
 * this function itself is listening for). Typing a value that differs
 * from First name's own current value detaches Display name from it;
 * clearing Display name back to empty resumes following, per the
 * owner's ruling that "override" is not one-way. */
function handleAddUserDisplayNameInput(): void {
  if (addUserDisplayNameInput.value.length === 0) {
    addUserDisplayNameFollowsFirstName = true;
    return;
  }
  if (addUserDisplayNameInput.value !== addUserFirstNameInput.value) {
    addUserDisplayNameFollowsFirstName = false;
  }
}

function showAddUserProfileStep(): void {
  addUserPanelProfile.hidden = false;
  addUserPanelPin.hidden = true;
}

function showAddUserPinStep(): void {
  addUserPanelProfile.hidden = true;
  addUserPanelPin.hidden = false;
}

function setAddUserProfileError(message: string | undefined): void {
  if (message === undefined) {
    addUserProfileErrorEl.hidden = true;
    addUserProfileErrorEl.textContent = "";
    return;
  }
  addUserProfileErrorEl.textContent = message;
  addUserProfileErrorEl.hidden = false;
}

/** Resets the PIN-choice step's own controls to their opening state
 * (Generate and Choose both showing, the chosen-PIN fields hidden),
 * mirroring openResetPinSheet's own reset shape for the sibling reset
 * flow, but against this sheet's own admin-add-user-pin-* ids. */
function resetAddUserPinPanel(): void {
  addUserPinChooseFields.hidden = true;
  addUserPinSaveButton.hidden = true;
  addUserPinChooseButton.hidden = false;
  addUserPinGenerateButton.hidden = false;
  addUserPinValueInput.value = "";
  addUserPinConfirmInput.value = "";
  addUserPinErrorEl.hidden = true;
  addUserPinErrorEl.textContent = "";
}

/** Reveals the two PIN fields, mirroring revealResetPinFields, including
 * that function's own T16/DEC-133 comment on why the label and the native
 * `maxlength` both need to be set here, not only the validation. */
function revealAddUserPinFields(): void {
  addUserPinChooseFields.hidden = false;
  addUserPinSaveButton.hidden = false;
  addUserPinChooseButton.hidden = true;
  addUserPinGenerateButton.hidden = true;
  const { pinMinLength, pinMaxLength } = session();
  addUserPinValueLabel.textContent = `New PIN (${pinLengthRangeText(pinMinLength, pinMaxLength)})`;
  addUserPinValueInput.maxLength = pinMaxLength;
  addUserPinConfirmInput.maxLength = pinMaxLength;
  addUserPinValueInput.focus();
}

function showAddUserPinError(message: string): void {
  addUserPinErrorEl.textContent = message;
  addUserPinErrorEl.hidden = false;
}

/** Step 1 -> step 2. The SAME client-side presence check the prior
 * inline handleAddUser already performed (a non-blank display name), but
 * making NO request (DEC-107): the entered values stay held in the still-
 * open form's own inputs. */
function handleAddUserNext(): void {
  const displayName = addUserDisplayNameInput.value.trim();
  if (displayName.length === 0) {
    setAddUserProfileError("Enter a display name first.");
    return;
  }
  setAddUserProfileError(undefined);
  addUserPinIntroEl.textContent = `Choose how ${displayName} signs in for the first time.`;
  resetAddUserPinPanel();
  showAddUserPinStep();
}

/** The ONLY write in this flow (DEC-107): fires when the PIN choice is
 * made, carrying the profile fields still held in the open sheet's own
 * inputs plus either no chosenPin (Generate) or a client-validated one.
 * No account exists in any form before this call succeeds. */
async function submitAddUser(chosenPin?: string): Promise<void> {
  const displayName = addUserDisplayNameInput.value.trim();
  if (displayName.length === 0) {
    // Defensive: Next already refused an empty name, so this branch is
    // reachable only if the field was cleared after Next was accepted.
    showAddUserProfileStep();
    setAddUserProfileError("Enter a display name first.");
    return;
  }
  const s = session();
  addUserPinGenerateButton.disabled = true;
  addUserPinSaveButton.disabled = true;
  const result = await createUser(s.apiBaseUrl, s.tenantId, {
    idempotencyKey: crypto.randomUUID(),
    displayName,
    role: addUserRole,
    firstName: addUserFirstNameInput.value.trim(),
    lastName: addUserLastNameInput.value.trim(),
    email: addUserEmailInput.value.trim(),
    phone: addUserPhoneInput.value.trim(),
    ...(chosenPin === undefined ? {} : { chosenPin }),
  });
  addUserPinGenerateButton.disabled = false;
  addUserPinSaveButton.disabled = false;
  if (result.status !== 201 && result.status !== 200) {
    const message = result.body.message ?? result.body.error ?? "Could not add that person.";
    // T9's own rule, mirrored here (planned test 8): a rejected chosen
    // PIN reports INSIDE the open sheet, where the fields are, rather
    // than as a toast behind it, and the entered profile fields are not
    // lost or re-prompted since nothing in this branch touches them.
    if (chosenPin === undefined) {
      adminToast(message, "danger");
    } else {
      showAddUserPinError(message);
    }
    return;
  }
  adminUsers.push(result.body.user);
  closeAddUserSheet();
  renderUserList();
  // Defensive, matching the prior inline flow's own comment: temporaryPin
  // is undefined only on a genuine idempotency-key replay, which a
  // single submit here never produces, but this never assumes that.
  if (result.body.temporaryPin !== undefined) {
    showPinReveal(`${result.body.user.displayName}'s temporary PIN`, result.body.temporaryPin);
  } else {
    // T9's own "never shown back" rule, mirrored here: a manager-chosen
    // PIN is never revealed, since the manager already typed it.
    adminToast(`${result.body.user.displayName} added.`, "success");
  }
}

/** Validates the pair in the sheet before any request goes out, mirroring
 * submitChosenResetPin's own pre-check exactly. The server re-validates
 * the format regardless; this is message quality, not the guard. */
async function submitChosenAddUserPin(): Promise<void> {
  const pin = addUserPinValueInput.value.trim();
  const confirm = addUserPinConfirmInput.value.trim();
  // T16/DEC-133: reads this tenant's resolved policy instead of the old
  // hardcoded 4-6 literal, so a manager can choose a PIN at whatever
  // length the tenant has configured (up to 12) rather than being refused
  // for a length the settings screen itself allowed. SET16-F1: session()
  // can now throw once the acting employee's session has actually ended
  // (invalidate() clears currentSession on sign-out), so this being safe
  // is no longer "both sheets only ever open from an authenticated
  // session" alone -- it depends on this call landing before this
  // function's own first await, which it does. submitAddUser, the one
  // await this flow reaches, captures its own session before that await
  // for the same reason, rather than calling session() again after it.
  const { pinMinLength, pinMaxLength } = session();
  if (!/^\d+$/.test(pin) || pin.length < pinMinLength || pin.length > pinMaxLength) {
    showAddUserPinError(`A PIN must be ${pinLengthRangeText(pinMinLength, pinMaxLength)}.`);
    return;
  }
  if (pin !== confirm) {
    showAddUserPinError("Those two PINs do not match.");
    return;
  }
  addUserPinErrorEl.hidden = true;
  await submitAddUser(pin);
}

async function handleDeactivateUser(user: UserDto): Promise<void> {
  const confirmed = await showConfirmSheet({
    title: `Deactivate ${user.displayName}?`,
    message: `${user.displayName} will no longer be able to sign in or take orders.`,
    confirmLabel: "Deactivate",
  });
  if (!confirmed) {
    return;
  }
  // SET16-F1: the confirm sheet above can sit open long enough for the
  // acting employee's session to actually die; session() would throw
  // rather than quietly return a stale one once it does, so this bails
  // out instead of surfacing as an unhandled rejection.
  const s = sessionOrUndefined();
  if (s === undefined) {
    return;
  }
  const result = await deactivateUser(s.apiBaseUrl, s.tenantId, user.id, {
    idempotencyKey: crypto.randomUUID(),
  });
  if (result.status !== 201 && result.status !== 200) {
    adminToast(
      result.body.message ?? result.body.error ?? "Could not deactivate that person.",
      "danger",
    );
    return;
  }
  const index = adminUsers.findIndex((existing) => existing.id === user.id);
  if (index !== -1) {
    adminUsers[index] = result.body.user;
  }
  renderUserList();
  adminToast(`${user.displayName} is deactivated.`, "success");
}

// T9, owner request 2026-08-29: Reset PIN now offers a choice rather than
// generating unconditionally. Opening the sheet performs no write, so
// cancelling leaves the account exactly as it was.
function openResetPinSheet(user: UserDto): void {
  resetPinTarget = user;
  resetPinTitleEl.textContent = `Reset ${user.displayName}'s PIN`;
  resetPinChooseFields.hidden = true;
  resetPinSaveButton.hidden = true;
  resetPinChooseButton.hidden = false;
  resetPinGenerateButton.hidden = false;
  resetPinValueInput.value = "";
  resetPinConfirmInput.value = "";
  resetPinErrorEl.hidden = true;
  resetPinErrorEl.textContent = "";
  resetPinScrim.hidden = false;
}

function closeResetPinSheet(): void {
  resetPinScrim.hidden = true;
  // Never leave a typed PIN sitting in the DOM after the sheet closes.
  resetPinValueInput.value = "";
  resetPinConfirmInput.value = "";
  resetPinTarget = undefined;
}

/** Reveals the two PIN fields. Kept as a second step rather than shown
 * alongside Generate, so the common path stays a single tap.
 *
 * T16/DEC-133: the label and the native `maxlength` both used to be the
 * hardcoded literal "4 to 6 digits"/6 in index.html, which is a SEPARATE
 * defect from the regex fix in submitChosenResetPin below: a native
 * `maxlength` truncates a typed value in the browser before any JavaScript
 * ever sees it, so leaving it at 6 would silently cap a manager's
 * keystrokes at the old default even after the validation itself reads
 * the tenant's real policy. Both are set here, at the moment the fields
 * become interactive, from the same resolved session policy the
 * validation itself reads. */
function revealResetPinFields(): void {
  resetPinChooseFields.hidden = false;
  resetPinSaveButton.hidden = false;
  resetPinChooseButton.hidden = true;
  resetPinGenerateButton.hidden = true;
  const { pinMinLength, pinMaxLength } = session();
  resetPinValueLabel.textContent = `New PIN (${pinLengthRangeText(pinMinLength, pinMaxLength)})`;
  resetPinValueInput.maxLength = pinMaxLength;
  resetPinConfirmInput.maxLength = pinMaxLength;
  resetPinValueInput.focus();
}

function showResetPinError(message: string): void {
  resetPinErrorEl.textContent = message;
  resetPinErrorEl.hidden = false;
}

async function handleResetPinUser(user: UserDto, chosenPin?: string): Promise<void> {
  const s = session();
  const result = await resetPin(s.apiBaseUrl, s.tenantId, user.id, {
    idempotencyKey: crypto.randomUUID(),
    ...(chosenPin === undefined ? {} : { chosenPin }),
  });
  if (result.status !== 201 && result.status !== 200) {
    const message = result.body.message ?? result.body.error ?? "Could not reset that PIN.";
    // T9: a rejected chosen PIN reports INSIDE the open sheet, where the
    // fields are, rather than as a toast behind it that leaves the manager
    // looking at an unexplained form.
    if (chosenPin === undefined) {
      adminToast(message, "danger");
    } else {
      showResetPinError(message);
    }
    return;
  }
  const index = adminUsers.findIndex((existing) => existing.id === user.id);
  if (index !== -1) {
    adminUsers[index] = result.body.user;
  }
  closeResetPinSheet();
  renderUserList();
  if (result.body.temporaryPin !== undefined) {
    showPinReveal(`${user.displayName}'s new temporary PIN`, result.body.temporaryPin);
  } else {
    // T9: a chosen PIN is deliberately never revealed back. The manager
    // typed it, and repeating it on screen only widens where it appears.
    adminToast(
      `${user.displayName}'s PIN was reset. They must change it at next sign in.`,
      "success",
    );
  }
}

/** Validates the pair in the sheet before any request goes out, so an
 * obvious mistype never burns a reset (which revokes every session the
 * person has). The server re-validates the format regardless; this is the
 * message quality, not the guard. */
async function submitChosenResetPin(): Promise<void> {
  const user = resetPinTarget;
  if (user === undefined) {
    return;
  }
  const pin = resetPinValueInput.value.trim();
  const confirm = resetPinConfirmInput.value.trim();
  // T16/DEC-133: same fix, same reasoning, as submitChosenAddUserPin's own
  // identical check above -- see that function's comment.
  const { pinMinLength, pinMaxLength } = session();
  if (!/^\d+$/.test(pin) || pin.length < pinMinLength || pin.length > pinMaxLength) {
    showResetPinError(`A PIN must be ${pinLengthRangeText(pinMinLength, pinMaxLength)}.`);
    return;
  }
  if (pin !== confirm) {
    showResetPinError("Those two PINs do not match.");
    return;
  }
  resetPinErrorEl.hidden = true;
  await handleResetPinUser(user, pin);
}

/** Shared by handleSetUserRoles' own non-200 branch and its thrown-
 * exception catch below: reverts this row's chips to the last known-good
 * server state and reports why, so both failure shapes leave the manager
 * looking at the same honest, reverted screen rather than one of them
 * silently orphaning the tap. */
function revertUserRoleChips(user: AdminUserDto, container: HTMLElement, message: string): void {
  adminToast(message, "danger");
  renderUserRoleChips(
    container,
    user,
    user.roles !== undefined && user.roles.length > 0 ? user.roles : [user.role],
  );
}

/** One place that draws a person's role chips. A chip that ADDS a role first shows what the
 * role would let them do and waits for a yes (R6, DEC-313); taking a role off saves at once, since
 * that can only narrow access. */
function renderUserRoleChips(
  container: HTMLElement,
  user: AdminUserDto,
  selected: readonly string[],
  testId = "admin-user-role-option-row",
): void {
  renderChipCheckboxGroup(
    container,
    testId,
    roleOptions(),
    selected,
    async (nextRoles, toggledRole, nowChecked) => {
      if (nowChecked) {
        const confirmed = await confirmAddRole({ user, role: toggledRole });
        if (!confirmed) {
          renderUserRoleChips(container, user, selected, testId);
          return;
        }
      }
      await handleSetUserRoles(user, nextRoles, container);
    },
  );
}

/** RBAC track (DEC-023): saves a user's complete role set after a chip
 * toggle. Re-renders that one row's chip group from the SERVER's
 * response on both success and failure, rather than trusting the
 * optimistic client-side toggle: a 400 (the zero-roles guard should
 * already prevent this client-side, but the server is the actual source
 * of truth) or a 403 (a non-admin caller somehow reaching this control)
 * must visibly revert rather than leave the UI showing a change that
 * never actually saved.
 *
 * USR3 (qa-lead finding 3): setUserRoles' own network-error path already
 * returns a typed, non-200 result rather than throwing, but this function
 * is reached through chipCheckboxGroupSaveChains' own save chain (see
 * renderChipCheckboxGroup), which chains one call's OWN promise onto the
 * next tap's. A genuine thrown exception here (session() throwing because
 * a teardown raced this call, or any other synchronous failure) used to
 * reject that chained promise with nothing awaiting it directly and no
 * catch of its own: an orphaned rejection, no toast, and the chip left
 * showing the un-saved, un-reverted optimistic state. Caught here now,
 * with the identical toast-and-revert treatment the non-200 path already
 * gives. */
async function handleSetUserRoles(
  user: AdminUserDto,
  nextRoles: string[],
  container: HTMLElement,
): Promise<void> {
  let result: Awaited<ReturnType<typeof setUserRoles>>;
  try {
    const s = session();
    result = await setUserRoles(s.apiBaseUrl, s.tenantId, user.id, nextRoles);
  } catch (error) {
    revertUserRoleChips(
      user,
      container,
      error instanceof Error ? error.message : `Could not update ${user.displayName}'s roles.`,
    );
    return;
  }
  if (result.status !== 200) {
    revertUserRoleChips(
      user,
      container,
      result.body.message ?? result.body.error ?? `Could not update ${user.displayName}'s roles.`,
    );
    return;
  }
  const index = adminUsers.findIndex((existing) => existing.id === user.id);
  if (index !== -1) {
    adminUsers[index] = result.body.user;
  }
  // PATCH .../users/:id/roles always answers with the FULL user record
  // (src/api/server.ts's toUserResponse with a resolved role set), so
  // this fallback is a type-level formality after UserDto.roles became
  // optional for the minimal roster shape only -- see that field's own
  // comment in ui/src/api.ts.
  const savedRoles = result.body.user.roles ?? [result.body.user.role];
  adminToast(`${user.displayName} now holds: ${savedRoles.map(roleLabel).join(", ")}.`, "success");
  renderUserRoleChips(container, result.body.user, savedRoles);
  if (editingUser?.id === user.id) {
    userAiSettings.reset();
    void userAiSettings.load();
  }
  // UX6: re-read the whole view, not just the chips that were tapped.
  //
  // Owner report, 2026-08-30: removing Willy's Admin role left his own
  // row still listing the old set AND left the Admin group header reading
  // "3 people". Patching adminUsers in place and re-rendering this one
  // chip group fixes neither: the LIST rows are rendered from that array
  // by a different pass, and the role headcounts are derived from it by
  // another. Both are showing the same fact that just changed.
  //
  // A per-row update was the tempting fix and is exactly half the bug: it
  // would have corrected the row and left the headcount wrong, which is
  // the half nobody would have noticed. loadUsers re-reads from the
  // server and re-renders both, so they cannot disagree.
  await loadUsers();
}

async function handleUnlockUser(user: UserDto): Promise<void> {
  const s = session();
  const result = await unlockUser(s.apiBaseUrl, s.tenantId, user.id, {
    idempotencyKey: crypto.randomUUID(),
  });
  if (result.status !== 201 && result.status !== 200) {
    adminToast(result.body.message ?? result.body.error ?? "Could not unlock that person.", "danger");
    return;
  }
  const index = adminUsers.findIndex((existing) => existing.id === user.id);
  if (index !== -1) {
    adminUsers[index] = result.body.user;
  }
  renderUserList();
  adminToast(`${user.displayName} is unlocked.`, "success");
}

// ---------------------------------------------------------------------
// U1/U2/U3/U5: the three-tab Edit User modal
// (docs/backlog.md, owner 2026-08-27, modelled on Shift4's Edit User).
//
// User Information / Locations / User Permissions, with the Active
// toggle in the footer beside Cancel and Update User.
//
// TWO SAVE MODELS, DELIBERATELY, because the two halves of this modal
// are not the same kind of edit:
//
//   - The User Information fields and the Active toggle are a FORM.
//     Nothing is written until Update User, and Cancel throws the edits
//     away. That is what the reference does and what the footer buttons
//     promise.
//   - Roles and individual permissions save IMMEDIATELY on toggle, each
//     one its own request, matching the auto-saving role chips this
//     panel already shipped. Two reasons, and the second is the
//     important one: batching 52 permission rows would mean inventing a
//     bulk write and a partial-failure story for it, and a permission
//     write can be REFUSED server-side by DEC-025's escalation cap or
//     the last-Admin guard, which the Admin needs to see against the row
//     they touched rather than as one opaque failure at the end of a
//     long save.
//
// Neither the search box nor the dense grid is a touch-only violation:
// CLAUDE.md's rule was scoped to the floor by the owner on 2026-08-27,
// and this is an admin surface run from a laptop.
// ---------------------------------------------------------------------

type UserEditTab = "info" | "locations" | "permissions";

/** The three-way leaf control's own vocabulary, in plain language. Never
 * the stored keys ("approval_required" is not something to put in front
 * of a person) and never a raw permission id. */
const PERMISSION_GRANT_OPTIONS: { value: PermissionGrant; label: string }[] = [
  { value: "allowed", label: "Allowed" },
  { value: "approval_required", label: "Needs manager approval" },
  { value: "denied", label: "Not allowed" },
];

function grantLabel(grant: PermissionGrant): string {
  return PERMISSION_GRANT_OPTIONS.find((option) => option.value === grant)?.label ?? grant;
}

let editingUser: AdminUserDto | undefined;
let editingUserPermissions: UserPermissionRowDto[] = [];
let editingUserPermissionsLoaded = false;
/** Groups start COLLAPSED. 52 rows expanded is a wall; the role editor's grid keeps its own
 * collapsed set here. */
const collapsedPermissionGroups = new Set<string>();
/** R7: the areas the person has opened with Show details on the Access tab. Everything starts closed. */
let accessOpenAreas = new Set<string>();
let editingUserActive = true;

function setUserEditError(message: string | undefined): void {
  if (message === undefined) {
    userEditErrorEl.hidden = true;
    userEditErrorEl.textContent = "";
    return;
  }
  userEditErrorEl.hidden = false;
  userEditErrorEl.textContent = message;
}

function showUserEditTab(tab: UserEditTab): void {
  setTabState(userEditTabInfo, tab === "info");
  setTabState(userEditTabLocations, tab === "locations");
  setTabState(userEditTabPermissions, tab === "permissions");
  // R4-F1-F3: the item-tab look (the item editor's tabs) keys off .active.
  userEditTabInfo.classList.toggle("active", tab === "info");
  userEditTabLocations.classList.toggle("active", tab === "locations");
  userEditTabPermissions.classList.toggle("active", tab === "permissions");
  userEditPanelInfo.hidden = tab !== "info";
  userEditPanelLocations.hidden = tab !== "locations";
  userEditPanelPermissions.hidden = tab !== "permissions";
  // R7: Update User saves the Info form and the Active switch; access saves when confirmed. On
  // the Access tab the footer says so and offers only a way out.
  const onAccess = tab === "permissions";
  userEditSaveButton.hidden = onAccess;
  userEditActiveInput.closest("label")?.toggleAttribute("hidden", onAccess);
  userEditActiveNoteEl.hidden = onAccess;
  userEditAccessNoteEl.hidden = !onAccess;
  userEditCancelButton.textContent = onAccess ? "Done" : "Cancel";
  if (tab === "permissions" && !editingUserPermissionsLoaded && mayReadAccessMatrix()) {
    void loadEditingUserPermissions();
  } else if (tab === "permissions" && mayReadUserAiLimits()) {
    void userAiSettings.load();
  }
}

const userAiSettingsHost = document.createElement("div");
userEditAccessReadoutEl.before(userAiSettingsHost);
const userAiSettings = createReportAiPersonSettings(userAiSettingsHost, {
  active: () => editingUser !== undefined && currentSession !== undefined && !userEditScrim.hidden && !userEditPanelPermissions.hidden && mayReadUserAiLimits(),
  read: async () => {
    const active = currentSession;
    const user = editingUser;
    if (active === undefined || user === undefined || !mayReadUserAiLimits()) return { status: 403, body: null };
    const result = await getUserReportAi(active.apiBaseUrl, active.tenantId, user.id);
    return active === currentSession && editingUser?.id === user.id ? result : { status: 401, body: null };
  },
  save: async ({ capDollars }) => {
    const active = currentSession;
    const user = editingUser;
    if (active === undefined || user === undefined || !mayReadUserAiLimits() || !mayReadAccessMatrix()) return { status: 403, body: null };
    const result = await setUserReportAi(active.apiBaseUrl, active.tenantId, user.id, capDollars);
    return active === currentSession && editingUser?.id === user.id ? result : { status: 401, body: null };
  },
});

/** The person editor's Locations tab. TEN12 (DEC-338): the rows are the tenant's locations read BY NAME from the server (GET
 * .../locations); the label is only ever a location's name, and a location that cannot be read shows a plain message, never an id.
 * LOC2 (docs/build-briefs/LOC2-second-bar-user-locations.md): the ticks are the person's real locations (user_locations, carried
 * on the directory as `locationIds`). Someone holding admin.location.manage changes them and Update User saves the set; for
 * anyone else the ticks show the truth and are fixed. */
let userEditLocations: { id: string; name: string }[] | undefined;
let userEditLocationsFailed = false;
/** The locations ticked right now, and the locations the person had when the editor opened. */
let userEditTickedLocations = new Set<string>();
let userEditOriginalLocations: string[] = [];

function mayEditUserLocations(): boolean {
  return (
    hasPermission(session(), "admin.location.manage") && editingUser?.locationIds !== undefined
  );
}

async function loadUserEditLocations(user: AdminUserDto): Promise<void> {
  const s = session();
  userEditLocations = undefined;
  userEditLocationsFailed = false;
  userEditOriginalLocations = [...(user.locationIds ?? [])];
  userEditTickedLocations = new Set(userEditOriginalLocations);
  renderUserEditLocations();
  const result = await listTenantLocations(s.apiBaseUrl, s.tenantId);
  // The editor was closed or opened for someone else while the request ran.
  if (editingUser?.id !== user.id) {
    return;
  }
  if (result.status === 200) {
    userEditLocations = result.body.locations.map((siteLocation) => ({
      id: siteLocation.id,
      name: siteLocation.name,
    }));
  } else {
    userEditLocationsFailed = true;
  }
  renderUserEditLocations();
}

function renderUserEditLocations(): void {
  userEditLocationsEl.innerHTML = "";
  if (userEditLocations === undefined || userEditLocations.length === 0) {
    const message = document.createElement("p");
    message.className = "admin-permission-planned";
    message.dataset["testid"] = "admin-user-location-status";
    message.textContent = userEditLocationsFailed
      ? "Could not load the locations. Close this and open it again."
      : userEditLocations === undefined
        ? "Loading locations…"
        : "No locations are set up yet.";
    userEditLocationsEl.append(message);
    return;
  }
  const editable = mayEditUserLocations();
  const loadedIds = userEditLocations.map((siteLocation) => siteLocation.id);
  const checkboxes: HTMLInputElement[] = [];
  let refreshBulkControls = (): void => {};
  if (editable) {
    const toolbar = document.createElement("div");
    toolbar.className = "admin-permission-toolbar";
    const selectAll = document.createElement("button");
    selectAll.type = "button";
    selectAll.className = "btn-secondary btn-compact";
    selectAll.textContent = "Select all";
    selectAll.dataset["testid"] = "admin-user-locations-select-all";
    const selectNone = document.createElement("button");
    selectNone.type = "button";
    selectNone.className = "btn-secondary btn-compact";
    selectNone.textContent = "Select none";
    selectNone.dataset["testid"] = "admin-user-locations-select-none";
    refreshBulkControls = (): void => {
      selectAll.disabled = loadedIds.every((id) => userEditTickedLocations.has(id));
      selectNone.disabled = loadedIds.every((id) => !userEditTickedLocations.has(id));
    };
    const setLoadedSelections = (selected: boolean): void => {
      if (!mayEditUserLocations()) return;
      for (const id of loadedIds) {
        if (selected) userEditTickedLocations.add(id);
        else userEditTickedLocations.delete(id);
      }
      for (const checkbox of checkboxes) checkbox.checked = selected;
      refreshBulkControls();
    };
    selectAll.addEventListener("click", () => setLoadedSelections(true));
    selectNone.addEventListener("click", () => setLoadedSelections(false));
    toolbar.append(selectAll, selectNone);
    userEditLocationsEl.append(toolbar);
    refreshBulkControls();
  }
  for (const location of userEditLocations) {
    const row = document.createElement("label");
    row.className = "admin-user-location-row";
    row.dataset["testid"] = "admin-user-location-row";
    row.dataset["locationId"] = location.id;

    const input = document.createElement("input");
    input.type = "checkbox";
    input.checked = userEditTickedLocations.has(location.id);
    input.disabled = !editable;
    input.dataset["testid"] = "admin-user-location-checkbox";
    input.addEventListener("change", () => {
      if (input.checked) {
        userEditTickedLocations.add(location.id);
      } else {
        userEditTickedLocations.delete(location.id);
      }
      refreshBulkControls();
    });
    checkboxes.push(input);

    const name = document.createElement("span");
    name.className = "admin-user-location-name";
    name.dataset["testid"] = "admin-user-location-name";
    name.textContent = location.name;

    row.append(input, name);
    userEditLocationsEl.append(row);
  }
}

/** True when the ticks differ from the locations the person had when the editor opened. */
function userEditLocationsChanged(): boolean {
  if (userEditTickedLocations.size !== userEditOriginalLocations.length) {
    return true;
  }
  return userEditOriginalLocations.some((id) => !userEditTickedLocations.has(id));
}

function renderUserEditRoles(): void {
  const user = editingUser;
  if (user === undefined) {
    return;
  }
  renderUserRoleChips(
    userEditRolesEl,
    user,
    user.roles !== undefined && user.roles.length > 0 ? user.roles : [user.role],
    "admin-user-edit-role-option",
  );
}

function openEditUser(user: AdminUserDto): void {
  userAiSettings.reset();
  editingUser = user;
  editingUserPermissions = [];
  editingUserPermissionsLoaded = false;
  accessOpenAreas = new Set();
  editingUserActive = user.active;

  userEditTitleEl.textContent = `Edit ${user.displayName}`;
  userEditDisplayNameInput.value = user.displayName;
  userEditFirstNameInput.value = user.firstName ?? "";
  userEditLastNameInput.value = user.lastName ?? "";
  userEditEmailInput.value = user.email ?? "";
  userEditPhoneInput.value = user.phone ?? "";
  userEditAccessReadoutEl.replaceChildren();
  setUserEditError(undefined);

  userEditActiveInput.checked = user.active;
  // U6 (docs/backlog.md), DEC-041 item 4: the toggle's ON half now exists
  // (POST .../reactivate, same permission as deactivate), so it is no
  // longer disabled in either direction. A display-name collision with
  // an active account is surfaced as a Save-time error rather than
  // prevented here, matching how every other Save-time write on this
  // form reports its own server refusal.
  userEditActiveInput.disabled = false;
  userEditActiveNoteEl.textContent = "";

  renderUserEditRoles();
  void loadUserEditLocations(user);
  // The Access tab holds the role chips (needs admin.user.create) and the readout of what the
  // person can do (needs admin.user.permissions.manage, which an Admin holds and a Manager does
  // not). Hide the tab only when neither is held, rather than showing one that 403s as it opens.
  userEditTabPermissions.hidden =
    !mayReadAccessMatrix() &&
    !mayReadUserAiLimits() &&
    !(currentSession !== undefined && hasPermission(currentSession, "admin.user.create"));

  showUserEditTab("info");
  // U7: the shared backdrop hosts two mutually exclusive sheets.
  addUserSheetEl.hidden = true;
  userEditSheetEl.hidden = false;
  userEditScrim.classList.remove("rl-centered");
  userEditScrim.classList.add("rl-drawer");
  userEditScrim.hidden = false;
  lockBodyScroll();
}

function closeEditUser(): void {
  userAiSettings.reset();
  userEditScrim.hidden = true;
  userEditScrim.classList.remove("rl-drawer", "rl-centered");
  editingUser = undefined;
  editingUserPermissions = [];
  editingUserPermissionsLoaded = false;
  accessOpenAreas = new Set();
  unlockBodyScroll();
}

/** Whether the signed-in person may read another person's access matrix. The server decides again. */
function mayReadAccessMatrix(): boolean {
  return (
    currentSession !== undefined && hasPermission(currentSession, "admin.user.permissions.manage")
  );
}

function mayReadUserAiLimits(): boolean {
  return currentSession !== undefined && hasPermission(currentSession, "admin.user.view") && hasPermission(currentSession, "billing.ai.manage");
}

async function loadEditingUserPermissions(): Promise<void> {
  const gen = loadGeneration;
  const user = editingUser;
  if (user === undefined) {
    return;
  }
  const s = session();
  renderAccessReadoutNow();
  const result = await getUserPermissions(s.apiBaseUrl, s.tenantId, user.id);
  // SET16-F4: this session's teardown ran while the fetch above was in
  // flight; discard rather than write another employee's access readout
  // over whatever invalidate() already cleared.
  if (!loadIsCurrent(gen) || editingUser?.id !== user.id) return;
  if (result.status !== 200) {
    userEditAccessReadoutEl.replaceChildren();
    const failed = document.createElement("p");
    failed.className = "admin-permission-empty";
    failed.dataset["testid"] = "admin-permission-load-failed";
    failed.textContent =
      result.body.message ?? result.body.error ?? "Could not load what this person can do.";
    userEditAccessReadoutEl.append(failed);
    return;
  }
  editingUserPermissions = result.body.permissions;
  editingUserPermissionsLoaded = true;
  renderAccessReadoutNow();
  void userAiSettings.load();
}

/** R7: draws the Access readout from the rows already loaded. Until they load it says so, which
 * is a different fact from "this person can do nothing". */
function renderAccessReadoutNow(): void {
  const user = editingUser;
  if (user === undefined) {
    return;
  }
  if (!editingUserPermissionsLoaded) {
    userEditAccessReadoutEl.replaceChildren();
    const loading = document.createElement("p");
    loading.className = "admin-permission-empty";
    loading.dataset["testid"] = "admin-permission-grid-loading";
    loading.textContent = "Loading what this person can do…";
    userEditAccessReadoutEl.append(loading);
    return;
  }
  renderAccessReadout(userEditAccessReadoutEl, {
    person: user,
    rows: editingUserPermissions,
    openAreas: accessOpenAreas,
    rolesHeld: (user.roles ?? [user.role]).length,
    onUseRoleSetting: (row) => void handleSetPermissionOverride(row, null),
    onAddException: () => void handleAddException(),
    onToggleArea: (group) => {
      if (accessOpenAreas.has(group)) {
        accessOpenAreas.delete(group);
      } else {
        accessOpenAreas.add(group);
      }
      renderAccessReadoutNow();
    },
  });
}

/** R7: "Change one thing for <name>". The dialog picks the area, the task and the setting; the save
 * goes through the same per-person write as ever, so the same caps and guards apply. */
async function handleAddException(): Promise<void> {
  const user = editingUser;
  if (user === undefined) {
    return;
  }
  const choice = await pickException({ person: user, rows: editingUserPermissions });
  if (choice === undefined || editingUser?.id !== user.id) {
    return;
  }
  const row = editingUserPermissions.find((r) => r.permissionId === choice.permissionId);
  if (row === undefined) {
    return;
  }
  await handleSetPermissionOverride(row, choice.grant);
}

/** AU17: a row whose permission has no approval moment (reading a screen) offers two choices. */
function grantOptionsFor(row: { allowsApproval?: boolean }): typeof PERMISSION_GRANT_OPTIONS {
  return PERMISSION_GRANT_OPTIONS.filter(
    (option) => row.allowsApproval !== false || option.value !== "approval_required",
  );
}

/** AU17: a short line under a permission's name where the name alone does not say what it opens. */
const PERMISSION_ROW_HINTS: Readonly<Record<string, string>> = {
  "admin.change_history.view":
    "Opens Change history under Reports. Admin has this by default, and no other role does.",
};

// ---------------------------------------------------------------------
// RT1: the role-template editor.
//
// Owner requirement 2026-08-30: "manage the roles as templates and be
// able to configure the rights of that role so if we need to make a
// change, it immediately changes for all users that have that role."
//
// SHARES THE GRID SHAPE WITH THE PER-USER TAB ON PURPOSE. It is the same
// decision at a different width, and an Admin who has learned one grid
// should not have to learn a second. What differs is only what is
// genuinely different: no source badge and no "Use role setting" button,
// because a role's grant IS the baseline and has nothing underneath it.
//
// It does NOT share the grid CODE, and that is deliberate rather than
// lazy. buildPermissionRow reads override/effectiveGrant/source, three
// fields a role has no meaning for. Making one function serve both would
// mean optional fields and a branch per field, and the next person would
// have to work out which half they were reading. Two small readers, each
// honest about its own shape, beat one that is honest about neither.
// ---------------------------------------------------------------------

let editingRole: string | undefined;
let editingRolePermissions: RolePermissionRowDto[] = [];
/** R6: settings picked in the role editor and not yet saved, by permission id. A setting picked
 * back to what is saved leaves this map, so its size is the number of real changes waiting. */
let stagedRoleGrants = new Map<string, PermissionGrant>();

// RT2: the roles come from the tenant now, not from ROLE_OPTIONS.
let adminTenantRoles: TenantRoleDto[] = [];

/** RT2: a role NAME is an identifier the database constrains (lower case,
 * digits and underscore only). Asking a manager to type that correctly is
 * the same defect as the old free-text time-zone box, so it is derived
 * from the label they actually meant to write: "Location Back" becomes
 * "bar_back". Anything the rule cannot salvage is refused by the server
 * with its own message rather than guessed at here. */
function roleNameFromLabel(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function holderCountFor(role: string): number {
  return adminUsers.filter((user) => user.active && (user.roles ?? [user.role]).includes(role))
    .length;
}

function renderRolesList(): void {
  rolesListEl.innerHTML = "";
  for (const tenantRole of sortByName(adminTenantRoles, (listed) => listed.label)) {
    const row = document.createElement("div");
    row.className = tenantRole.active ? "admin-role-row" : "admin-role-row admin-role-row-retired";
    row.dataset["testid"] = "admin-role-row";
    row.dataset["role"] = tenantRole.role;
    row.dataset["active"] = String(tenantRole.active);

    const open = document.createElement("button");
    open.type = "button";
    open.className = "admin-role-row-open";
    open.dataset["testid"] = `admin-role-open-${tenantRole.role}`;

    const name = document.createElement("span");
    name.className = "admin-role-row-name";
    name.textContent = tenantRole.label;
    // R5: a built-in role says so (its name cannot be changed), and a switched-off one says so.
    if (tenantRole.isShipped) {
      const tag = document.createElement("span");
      tag.className = "rl-tag rl-role admin-role-row-tag";
      tag.dataset["testid"] = `admin-role-built-in-${tenantRole.role}`;
      tag.textContent = "Built in";
      name.append(tag);
    }
    if (!tenantRole.active) {
      const tag = document.createElement("span");
      tag.className = "rl-tag rl-role admin-role-row-tag";
      tag.textContent = "Switched off";
      name.append(tag);
    }

    // How many people a change would reach, shown BEFORE the editor
    // opens. An Admin about to widen a role should know whether that is
    // one person or eleven, and the count is already in memory.
    const holders = holderCountFor(tenantRole.role);
    const count = document.createElement("span");
    count.className = "admin-role-row-count";
    count.dataset["testid"] = `admin-role-holders-${tenantRole.role}`;
    count.textContent = !tenantRole.active
      ? "Switched off"
      : holders === 0
        ? "Nobody has this role"
        : holders === 1
          ? "1 person"
          : `${holders} people`;

    open.append(name, count);
    open.addEventListener("click", () => void openRoleEditor(tenantRole.role));
    row.append(open);

    // R5 (DEC-313): one Options button replaces the inline Switch off, so a row stays calm as the
    // things you can do to a role grow (Open and edit, Rename, Make a copy, Switch off). A role
    // that is switched off offers only the way back. The server refuses what is not allowed
    // (a built-in rename, switching off the role every location keeps) whatever the screen shows.
    if (tenantRole.active) {
      const options = document.createElement("button");
      options.type = "button";
      options.className = "rl-btn rl-small";
      options.dataset["testid"] = `admin-role-options-${tenantRole.role}`;
      options.textContent = "Options";
      options.addEventListener("click", () => openRoleOptions(tenantRole));
      row.append(options);
    } else {
      const switchBack = document.createElement("button");
      switchBack.type = "button";
      switchBack.className = "rl-btn rl-small";
      switchBack.dataset["testid"] = `admin-role-toggle-${tenantRole.role}`;
      switchBack.textContent = "Switch back on";
      switchBack.addEventListener("click", () => void switchBackOn(tenantRole));
      row.append(switchBack);
    }

    rolesListEl.append(row);
  }
}

async function loadTenantRoles(): Promise<void> {
  const gen = loadGeneration;
  const s = session();
  const result = await listTenantRoles(s.apiBaseUrl, s.tenantId);
  // SET16-F4: this session's teardown ran while the fetch above was in
  // flight; discard rather than write over what invalidate() already
  // cleared.
  if (!loadIsCurrent(gen)) return;
  if (result.status !== 200) {
    // USR2: brought up to the same honest-failure shape loadUsers already
    // uses just above, instead of returning silently. A genuine 401 never
    // reaches here: this loader is only ever called from loadUsers's own
    // 200 success branch or after a role mutation that itself required a
    // working session moments earlier, and DEC-132/USR1 already route a
    // revoked session to the PIN screen before either loader's non-200
    // branch would run. So the only non-200 this branch ever sees is a
    // real permission denial, a network error, or a 5xx, and it must say
    // so rather than leaving the roles area looking like a tenant that
    // genuinely configured none.
    rolesListEl.innerHTML = `<p class="tabs-empty">Could not load roles (${result.body.message ?? result.body.error ?? result.status}).</p>`;
    adminToast(
      `Could not load roles. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`,
      "danger",
    );
    return;
  }
  adminTenantRoles = result.body.roles;
  renderRolesList();
  // R5 (DEC-313 ruling 5): the people list names roles by the tenant's current names, so it is
  // drawn again once they are known (a renamed role then shows its new name on every person).
  if (usersLoaded) {
    renderUserList();
  }
}

async function handleCreateOrRestoreRole(label: string): Promise<void> {
  const trimmed = label.trim();
  const role = roleNameFromLabel(trimmed);
  if (trimmed.length === 0 || role.length === 0) {
    roleAddStatusEl.textContent = "Type a name for the role first.";
    return;
  }
  const s = session();
  const result = await createTenantRole(s.apiBaseUrl, s.tenantId, role, trimmed);
  if (result.status !== 201 && result.status !== 200) {
    roleAddStatusEl.textContent =
      result.body.message ?? result.body.error ?? "Could not add that role.";
    return;
  }
  roleAddStatusEl.textContent = "";
  roleLabelInput.value = "";
  // This same function also restores a retired role from its own
  // "Switch back on" toggle (handleRetireRole's sibling below), which
  // never opens the Add role popup; only close it here if it was the
  // one that called this.
  if (!roleAddSheetEl.hidden) closeAddRoleSheet();
  // "Restored" rather than "added", because announcing a new role that is
  // actually an old one returning would misdescribe what just happened to
  // the tenant's history.
  adminToast(
    result.body.restored
      ? `${result.body.role.label} is switched back on. Nobody holds it yet.`
      : `${result.body.role.label} added. It starts with nothing allowed.`,
    "success",
  );
  await loadTenantRoles();
}

/** MGRROLE1 (DEC-599). True while the open role grid is a read-only view: the person can see what a role may do
 * (admin.user.view) but cannot change roles (admin.user.permissions.manage is Admin). */
let roleEditReadOnly = false;

async function openRoleEditor(role: string): Promise<void> {
  const s = session();
  const result = await getRolePermissions(s.apiBaseUrl, s.tenantId, role);
  if (result.status !== 200) {
    // The refusal an Admin most likely meets here is the outranking rule:
    // a delegated permissions administrator may not read the matrix of a
    // role whose holders can do things they cannot. Show the server's own
    // sentence rather than inventing one, since it already says why.
    adminToast(result.body.message ?? result.body.error ?? "Could not open that role.", "danger");
    return;
  }
  editingRole = role;
  editingRolePermissions = result.body.permissions;
  roleEditReadOnly = !mayReadAccessMatrix();
  roleEditTitleEl.textContent = roleLabel(role);

  const holders = adminUsers.filter(
    (user) => user.active && (user.roles ?? [user.role]).includes(role),
  ).length;
  roleEditHoldersEl.textContent =
    holders === 0
      ? "Nobody has this role right now. Changes still apply to anyone given it later."
      : holders === 1
        ? "1 person has this role. Nothing changes for them until you save."
        : `${holders} people have this role. Nothing changes for them until you save.`;
  if (roleEditReadOnly) {
    roleEditHoldersEl.textContent =
      "You can see what this role can do. Only an Admin can change it, so ask your Admin for a change.";
  }
  stagedRoleGrants = new Map();
  renderRoleSaveBar();

  collapsedPermissionGroups.clear();
  for (const row of editingRolePermissions) {
    collapsedPermissionGroups.add(sectionKeyForPermission(row.permissionId));
  }
  roleEditPermissionSearchEl.value = "";
  renderRolePermissionGrid();
  // Only one of this sheet and #admin-role-add-sheet is ever unhidden at
  // a time; they share the one #admin-role-edit-scrim backdrop (UX64,
  // chrome-ratchet).
  roleAddSheetEl.hidden = true;
  roleEditSheetEl.hidden = false;
  roleEditScrim.classList.remove("rl-centered");
  roleEditScrim.classList.add("rl-drawer");
  roleEditScrim.hidden = false;
  lockBodyScroll();
}

function closeRoleEditor(): void {
  roleEditPermissionSearchEl.value = "";
  roleEditScrim.hidden = true;
  roleEditScrim.classList.remove("rl-drawer", "rl-centered");
  unlockBodyScroll();
  editingRole = undefined;
  editingRolePermissions = [];
  stagedRoleGrants = new Map();
}

/** The close button and a tap on the backdrop. Staged changes are not saved by closing, so a
 * person with some waiting is asked first rather than losing them silently. */
async function requestCloseRoleEditor(): Promise<void> {
  if (stagedRoleGrants.size > 0) {
    const discard = await showConfirmSheet({
      title: "Close without saving?",
      message: `${unsavedText(stagedRoleGrants.size)}. Closing now throws them away.`,
      confirmLabel: "Discard changes",
      cancelLabel: "Keep editing",
    });
    if (!discard) {
      return;
    }
  }
  closeRoleEditor();
}

/** What a row shows: the staged setting when there is one, otherwise what is saved. */
function shownRoleGrant(row: RolePermissionRowDto): PermissionGrant {
  return stagedRoleGrants.get(row.permissionId) ?? row.grant;
}

function renderRoleSaveBar(): void {
  const count = stagedRoleGrants.size;
  roleEditSaveBarEl.hidden = count === 0;
  roleEditSaveBarTextEl.textContent = unsavedText(count);
}

function stageRoleGrant(row: RolePermissionRowDto, grant: PermissionGrant): void {
  if (grant === row.grant) {
    stagedRoleGrants.delete(row.permissionId);
  } else {
    stagedRoleGrants.set(row.permissionId, grant);
  }
  renderRolePermissionGrid();
  renderRoleSaveBar();
}

function discardStagedRoleChanges(): void {
  stagedRoleGrants = new Map();
  renderRolePermissionGrid();
  renderRoleSaveBar();
}

/** Review and save: the mandatory check (count, changes in plain words, people by name), then one
 * atomic save. Back from the review leaves every staged change where it was. */
async function reviewStagedRoleChanges(): Promise<void> {
  const role = editingRole;
  if (role === undefined || stagedRoleGrants.size === 0) {
    return;
  }
  const outcome = await reviewRoleSave({
    role,
    roleLabel: roleLabel(role),
    changes: [...stagedRoleGrants].map(([permissionId, grant]) => ({ permissionId, grant })),
  });
  if (outcome.kind !== "saved" || editingRole !== role) {
    return;
  }
  // The server's answer is the new saved state; reload the rows so every figure is the server's.
  const s = sessionOrUndefined();
  if (s === undefined) {
    return;
  }
  const fresh = await getRolePermissions(s.apiBaseUrl, s.tenantId, role);
  if (editingRole !== role) {
    return;
  }
  if (fresh.status === 200) {
    editingRolePermissions = fresh.body.permissions;
  } else {
    for (const applied of outcome.applied ?? []) {
      const index = editingRolePermissions.findIndex(
        (r) => r.permissionId === applied.permissionId,
      );
      const previous = editingRolePermissions[index];
      if (index !== -1 && previous !== undefined) {
        editingRolePermissions[index] = { ...previous, grant: applied.to };
      }
    }
  }
  stagedRoleGrants = new Map();
  renderRolePermissionGrid();
  renderRoleSaveBar();
}

// UX64 (DEC-209): + Add role opens this popup instead of reading an
// inline text box. Carries exactly the one field role creation takes
// (the label; the storage key is derived from it, never typed).
function openAddRoleSheet(): void {
  roleLabelInput.value = "";
  roleAddStatusEl.textContent = "";
  roleEditSheetEl.hidden = true;
  roleAddSheetEl.hidden = false;
  roleEditScrim.classList.remove("rl-drawer");
  roleEditScrim.classList.add("rl-centered");
  roleEditScrim.hidden = false;
  lockBodyScroll();
  roleLabelInput.focus();
}

function closeAddRoleSheet(): void {
  roleEditScrim.hidden = true;
  roleEditScrim.classList.remove("rl-drawer", "rl-centered");
  roleAddSheetEl.hidden = true;
  roleEditSheetEl.hidden = false;
  roleLabelInput.value = "";
  roleAddStatusEl.textContent = "";
  unlockBodyScroll();
}

/** PERMSEARCH1: the heading icon is the Settings rail's own icon for that section, cloned so the two never drift. */
function sectionIconFor(railSection: string | undefined): SVGElement | null {
  if (railSection === undefined) return null;
  const tab = ADMIN_SECTION_TABS[railSection as AdminSection];
  const svg = tab?.querySelector("svg");
  if (svg === null || svg === undefined) return null;
  const copy = svg.cloneNode(true) as SVGElement;
  copy.setAttribute("aria-hidden", "true");
  copy.classList.add("admin-permission-group-icon");
  return copy;
}

/** Text with the searched word wrapped in <mark>, built from text nodes only. */
function highlightedText(parent: HTMLElement, text: string, query: string): void {
  const parts = splitForHighlight(text, query);
  if (parts === null) {
    parent.append(document.createTextNode(text));
    return;
  }
  const mark = document.createElement("mark");
  mark.textContent = parts.match;
  parent.append(document.createTextNode(parts.before), mark, document.createTextNode(parts.after));
}

function renderRolePermissionGrid(): void {
  roleEditPermissionGridEl.innerHTML = "";
  const query = roleEditPermissionSearchEl.value;
  const searching = query.trim() !== "";
  const sections = searchPermissionSections(editingRolePermissions, query);
  roleEditPermissionCountEl.textContent =
    searching && sections.length === 0
      ? noMatchText(query).headline
      : permissionCountText(sections, searching);
  if (searching && sections.length === 0) {
    const empty = document.createElement("p");
    empty.className = "admin-permission-empty";
    empty.dataset["testid"] = "admin-role-permission-no-match";
    empty.textContent = noMatchText(query).hint;
    roleEditPermissionGridEl.append(empty);
    return;
  }
  let dividerShown = false;

  for (const section of sections) {
    if (section.outsideSettings && !dividerShown) {
      dividerShown = true;
      const divider = document.createElement("div");
      divider.className = "admin-permission-divider";
      divider.dataset["testid"] = "admin-role-permission-outside-label";
      divider.textContent = "Outside the Settings menu";
      roleEditPermissionGridEl.append(divider);
    }
    const groupKey = section.key;
    const allRows = editingRolePermissions.filter(
      (r) => sectionKeyForPermission(r.permissionId) === groupKey,
    );
    // A search opens every section that has a match; browsing keeps the person's own open and closed choices.
    const collapsed = !searching && collapsedPermissionGroups.has(groupKey);
    const groupEl = document.createElement("div");
    groupEl.className = "admin-permission-group";
    groupEl.dataset["testid"] = "admin-role-permission-group";
    groupEl.dataset["group"] = groupKey;

    const header = document.createElement("button");
    header.type = "button";
    header.className = "admin-permission-group-header";
    header.dataset["testid"] = `admin-role-permission-group-header-${groupKey}`;
    header.setAttribute("aria-expanded", collapsed ? "false" : "true");
    header.disabled = searching;

    const caret = document.createElement("span");
    caret.className = "admin-permission-group-caret";
    caret.setAttribute("aria-hidden", "true");
    caret.textContent = collapsed ? "\u25b8" : "\u25be";

    const title = document.createElement("span");
    highlightedText(title, section.label, query);

    const count = document.createElement("span");
    count.className = "admin-permission-group-count";
    count.textContent = searching
      ? `${section.hits.length} ${section.hits.length === 1 ? "match" : "matches"}`
      : `${allRows.filter((row) => shownRoleGrant(row) === "allowed").length} of ${allRows.length} allowed`;

    const icon = sectionIconFor(sectionDef(groupKey).railSection);
    if (icon !== null) header.append(caret, icon, title, count);
    else header.append(caret, title, count);
    header.addEventListener("click", () => {
      if (collapsedPermissionGroups.has(groupKey)) {
        collapsedPermissionGroups.delete(groupKey);
      } else {
        collapsedPermissionGroups.add(groupKey);
      }
      renderRolePermissionGrid();
    });
    groupEl.append(header);

    if (!collapsed) {
      const rowsEl = document.createElement("div");
      rowsEl.className = "admin-permission-rows";
      for (const hit of section.hits) {
        rowsEl.append(buildRolePermissionRow(hit.row, query, hit.why));
      }
      groupEl.append(rowsEl);
    }
    roleEditPermissionGridEl.append(groupEl);
  }
}

function buildRolePermissionRow(
  row: RolePermissionRowDto,
  query = "",
  why: string | null = null,
): HTMLElement {
  const rowEl = document.createElement("div");
  rowEl.className = "admin-permission-row";
  rowEl.dataset["testid"] = `admin-role-permission-row-${row.permissionId}`;
  rowEl.dataset["permissionId"] = row.permissionId;

  const main = document.createElement("div");
  main.className = "admin-permission-row-main";
  const label = document.createElement("div");
  label.className = "admin-permission-row-label";
  label.id = `admin-role-permission-label-${row.permissionId}`;
  highlightedText(label, row.label, query);
  const changed = stagedRoleGrants.has(row.permissionId);
  if (changed) {
    // R6: a row with a staged change wears a tag and says what is saved, so nothing waiting is
    // ever mistaken for something already in force.
    rowEl.classList.add("rl-changed-row");
    const tag = document.createElement("span");
    tag.className = "rl-tag rl-warn";
    tag.dataset["testid"] = `admin-role-permission-changed-${row.permissionId}`;
    tag.textContent = "Changed";
    tag.style.marginLeft = "var(--space-2)";
    label.append(tag);
  }
  main.append(label);
  if (changed) {
    const was = document.createElement("div");
    was.className = "rl-was";
    was.dataset["testid"] = `admin-role-permission-saved-${row.permissionId}`;
    was.textContent = `Saved setting: ${grantLabel(row.grant)}`;
    main.append(was);
  }
  const description = PERMISSION_DESCRIPTIONS[row.permissionId];
  if (description !== undefined) {
    const descEl = document.createElement("div");
    descEl.className = "admin-permission-hint";
    descEl.dataset["testid"] = `admin-role-permission-description-${row.permissionId}`;
    highlightedText(descEl, description, query);
    main.append(descEl);
  }
  if (why !== null) {
    const whyEl = document.createElement("span");
    whyEl.className = "rl-tag rl-role";
    whyEl.dataset["testid"] = `admin-role-permission-why-${row.permissionId}`;
    whyEl.textContent = why;
    main.append(whyEl);
  }
  const hint = PERMISSION_ROW_HINTS[row.permissionId];
  if (hint !== undefined) {
    const hintEl = document.createElement("div");
    hintEl.className = "admin-permission-hint";
    hintEl.dataset["testid"] = `admin-role-permission-hint-${row.permissionId}`;
    hintEl.id = `admin-role-permission-hint-${row.permissionId}`;
    hintEl.textContent = hint;
    main.append(hintEl);
  }
  if (row.planned) {
    const planned = document.createElement("div");
    planned.className = "admin-permission-planned";
    planned.textContent = "Not built yet. You can set it now; it takes effect when it ships.";
    main.append(planned);
  }
  if (!row.actorMayGrant && !roleEditReadOnly) {
    const capped = document.createElement("div");
    capped.className = "admin-permission-capped";
    capped.dataset["testid"] = `admin-role-permission-capped-${row.permissionId}`;
    capped.textContent = row.actorMayModify
      ? "You cannot give this out, because you do not have it yourself."
      : "You cannot change this, because this role has it and you do not.";
    main.append(capped);
  }
  rowEl.append(main);

  const controls = document.createElement("div");
  controls.className = "admin-permission-row-controls";
  const picker = document.createElement("div");
  picker.className = "admin-chip-select";
  for (const option of grantOptionsFor(row)) {
    const optionLabel = document.createElement("label");
    optionLabel.className = "admin-chip-option";
    const input = document.createElement("input");
    describeSetting(
      input,
      hint === undefined
        ? `admin-role-permission-label-${row.permissionId}`
        : `admin-role-permission-label-${row.permissionId} admin-role-permission-hint-${row.permissionId}`,
    );
    input.type = "radio";
    input.name = `admin-role-permission-${row.permissionId}`;
    input.className = "admin-chip-option-input";
    input.dataset["testid"] = `admin-role-permission-option-${row.permissionId}-${option.value}`;
    input.dataset["value"] = option.value;
    input.checked = option.value === shownRoleGrant(row);
    // The same two caps the per-user picker reflects, and the same
    // caveat: disabling a control is a courtesy, never the check. The
    // server re-derives both on every write.
    input.disabled =
      roleEditReadOnly || !row.actorMayModify || (!row.actorMayGrant && option.value !== "denied");
    input.addEventListener("change", () => {
      if (input.checked) {
        stageRoleGrant(row, option.value);
      }
    });
    const text = document.createElement("span");
    text.className = "admin-chip-option-label";
    text.textContent = option.label;
    optionLabel.append(input, text);
    picker.append(optionLabel);
  }
  controls.append(picker);
  rowEl.append(controls);
  return rowEl;
}

/** One row, one request. A refusal (DEC-025's cap, or the last-Admin
 * guard) re-renders from the LAST KNOWN SERVER STATE rather than leaving
 * the optimistic click on screen, the same revert discipline
 * handleSetUserRoles already uses. */
async function handleSetPermissionOverride(
  row: UserPermissionRowDto,
  grant: PermissionGrant | null,
): Promise<void> {
  const user = editingUser;
  if (user === undefined) {
    return;
  }
  const s = session();
  const gen = loadGeneration;
  const result = await setUserPermissionOverride(
    s.apiBaseUrl,
    s.tenantId,
    user.id,
    row.permissionId,
    grant,
  );
  if (!loadIsCurrent(gen) || editingUser?.id !== user.id) return;
  if (result.status !== 200) {
    adminToast(
      result.body.message ?? result.body.error ?? "Could not change that permission.",
      "danger",
    );
    renderAccessReadoutNow();
    return;
  }
  const index = editingUserPermissions.findIndex(
    (existing) => existing.permissionId === row.permissionId,
  );
  if (index !== -1) {
    const previous = editingUserPermissions[index];
    if (previous !== undefined) {
      editingUserPermissions[index] = {
        ...previous,
        override: result.body.grant,
        roleGrant: result.body.roleGrant,
        effectiveGrant: result.body.effectiveGrant,
        source: result.body.source,
      };
    }
  }
  renderAccessReadoutNow();
  void userAiSettings.load();
  adminToast(
    grant === null
      ? `${row.label}: back to the role setting.`
      : `${row.label}: saved for ${user.displayName}.`,
    "success",
  );
}

async function handleUpdateUser(): Promise<void> {
  const user = editingUser;
  if (user === undefined) {
    return;
  }
  const s = session();
  setUserEditError(undefined);
  // LOC2: an active person works at at least one location. Checked before anything is saved, so a refusal leaves no half save.
  const savingLocations = mayEditUserLocations() && userEditLocationsChanged();
  if (savingLocations && userEditTickedLocations.size === 0 && userEditActiveInput.checked) {
    setUserEditError("Choose at least one location for this person.");
    return;
  }
  userEditSaveButton.disabled = true;
  try {
    // Named userEmail/userPhone rather than email/phone so
    // scripts/check-no-client-pii-in-logs.ts's closed vocabulary can see
    // them: these are staff PII and must never reach a log or an error
    // string. See that script's own header.
    const userEmail = userEditEmailInput.value.trim();
    const userPhone = userEditPhoneInput.value.trim();
    const result = await updateUserProfile(s.apiBaseUrl, s.tenantId, user.id, {
      displayName: userEditDisplayNameInput.value.trim(),
      firstName:
        userEditFirstNameInput.value.trim() === "" ? null : userEditFirstNameInput.value.trim(),
      lastName:
        userEditLastNameInput.value.trim() === "" ? null : userEditLastNameInput.value.trim(),
      email: userEmail === "" ? null : userEmail,
      phone: userPhone === "" ? null : userPhone,
    });
    if (result.status !== 200) {
      setUserEditError(result.body.message ?? result.body.error ?? "Could not save those changes.");
      return;
    }
    const saved = result.body.user;
    const index = adminUsers.findIndex((existing) => existing.id === user.id);
    if (index !== -1) {
      adminUsers[index] = saved;
    }
    editingUser = saved;

    // LOC2: where the person works is its own command (PUT .../users/:id/locations, admin.location.manage), so it runs
    // as a step after the profile save, only when the ticks moved.
    if (savingLocations) {
      const locations = await setUserLocations(s.apiBaseUrl, s.tenantId, user.id, [
        ...userEditTickedLocations,
      ]);
      if (locations.status !== 200) {
        setUserEditError(
          locations.body.message ??
            locations.body.error ??
            "The details were saved, but the locations did not save.",
        );
        await loadUsers();
        return;
      }
    }

    // The Active toggle is part of this same Save, but it is a different
    // command server-side (POST .../deactivate or, U6, POST .../
    // reactivate; its own permission and its own ledger row), so it runs
    // as a second step only when the toggle actually moved.
    if (editingUserActive && !userEditActiveInput.checked) {
      const deactivated = await deactivateUser(s.apiBaseUrl, s.tenantId, user.id, {
        idempotencyKey: crypto.randomUUID(),
      });
      if (deactivated.status !== 200 && deactivated.status !== 201) {
        setUserEditError(
          deactivated.body.message ??
            deactivated.body.error ??
            "The details were saved, but turning this person off did not work.",
        );
        // UX6: the profile half DID save, so the view is stale here too.
        // Refreshing on the failure path matters more than on the happy
        // one: this is the message that tells somebody a write partly
        // landed, and it must not be read against a stale screen.
        await loadUsers();
        return;
      }
      const deactivatedIndex = adminUsers.findIndex((existing) => existing.id === user.id);
      if (deactivatedIndex !== -1) {
        adminUsers[deactivatedIndex] = deactivated.body.user;
      }
    } else if (!editingUserActive && userEditActiveInput.checked) {
      // U6 (docs/backlog.md), DEC-041 item 4: the toggle's ON half. A
      // display-name collision with an active account (the server's own
      // 409, DuplicateActiveNameError) surfaces here exactly like any
      // other Save-time refusal -- never an automatic suffix, per the
      // ruling: a manager renames one of the two people first.
      const reactivated = await reactivateUser(s.apiBaseUrl, s.tenantId, user.id, {
        idempotencyKey: crypto.randomUUID(),
      });
      if (reactivated.status !== 200 && reactivated.status !== 201) {
        setUserEditError(
          reactivated.body.message ??
            reactivated.body.error ??
            "The details were saved, but turning this person back on did not work.",
        );
        // UX6: the same reasoning as the deactivate failure path above.
        await loadUsers();
        return;
      }
      const reactivatedIndex = adminUsers.findIndex((existing) => existing.id === user.id);
      if (reactivatedIndex !== -1) {
        adminUsers[reactivatedIndex] = reactivated.body.user;
      }
    }

    // UX6: the same rule as handleSetUserRoles. The Active toggle above
    // moves a person in or out of the active roster, which changes both
    // the list and every role headcount that counted them.
    await loadUsers();
    closeEditUser();
    adminToast(`${saved.displayName} is saved.`, "success");
  } finally {
    userEditSaveButton.disabled = false;
  }
}

// ---------------------------------------------------------------------
// Wiring
// ---------------------------------------------------------------------

initializeInventorySetup();

for (const section of ADMIN_SECTION_ORDER) {
  ADMIN_SECTION_TABS[section].addEventListener("click", () => {
    clearSettingsSearch();
    void showSection(section);
  });
}

// SET2, the hard requirement stated twice by the owner: filters on
// EVERY keystroke, no submit and no debounce. "input" fires for typing,
// backspace, paste and the native search-field clear (x) button alike.
railSearchInput.addEventListener("input", () => renderSettingsSearch());

// U1: the three-tab Edit User modal.
userEditTabInfo.addEventListener("click", () => showUserEditTab("info"));
userEditTabLocations.addEventListener("click", () => showUserEditTab("locations"));
userEditTabPermissions.addEventListener("click", () => showUserEditTab("permissions"));
userEditCloseButton.addEventListener("click", () => closeEditUser());
userEditCancelButton.addEventListener("click", () => closeEditUser());
userEditSaveButton.addEventListener("click", () => void handleUpdateUser());
userEditScrim.addEventListener("click", (event) => {
  if (event.target === userEditScrim) {
    // U7: the shared backdrop hosts two mutually exclusive sheets; close
    // whichever one is actually showing.
    if (addUserSheetEl.hidden) {
      closeEditUser();
    } else {
      closeAddUserSheet();
    }
  }
});

// U7 (DEC-103, DEC-107): the Add user sheet, nested in the same
// scrim above.
addUserCloseButton.addEventListener("click", () => closeAddUserSheet());
addUserCancelButton.addEventListener("click", () => closeAddUserSheet());
addUserNextButton.addEventListener("click", () => handleAddUserNext());
// UX64 (DEC-211, corrected): the Display name / First name follow-until-
// overridden relationship.
addUserFirstNameInput.addEventListener("input", () => handleAddUserFirstNameInput());
addUserDisplayNameInput.addEventListener("input", () => handleAddUserDisplayNameInput());
addUserPinBackButton.addEventListener("click", () => showAddUserProfileStep());
addUserPinGenerateButton.addEventListener("click", () => void submitAddUser());
addUserPinChooseButton.addEventListener("click", () => revealAddUserPinFields());
addUserPinSaveButton.addEventListener("click", () => void submitChosenAddUserPin());

addDrinkButton.addEventListener("click", () => void openAddDrinkForm());
// RECIPEAI1 (DEC-590): the AI recipe import is a full page of its own; closing it after an import re-reads the recipe list.
importRecipesButton.addEventListener("click", () => {
  if (!hasPermission(session(), RECIPE_IMPORT_PERMISSION)) return;
  openRecipeImport({
    getSession: () => sessionOrUndefined(),
    onClose: (imported) => {
      if (imported !== undefined && sessionOrUndefined() !== undefined) void loadDrinks();
    },
    onOpenInventory: () => void showSection("inventory"),
  });
});
drinkSearchInput.addEventListener("input", () => handleDrinkSearchInput());
drinkFormClose.addEventListener("click", () => closeDrinkForm());
// UIFIX2 (SF-8): Cancel beside Save does exactly what the close control does.
mustFind<HTMLButtonElement>("#admin-drink-form-cancel").addEventListener("click", () => closeDrinkForm());
drinkFormScrim.addEventListener("click", (event) => {
  if (event.target === drinkFormScrim) {
    closeDrinkForm();
  }
});
drinkRequiresLiquorInput.addEventListener("change", () => updateDrinkRequiresLiquorVisibility());
drinkBuildChargeInput.addEventListener("input", () => updateBuildChargeHint());
drinkAddComponentButton.addEventListener("click", () => {
  const draft = blankDraft();
  drinkFormComponents.push(draft);
  renderDrinkComponents();
  openRecipePicker(draft, true);
});
drinkFormSave.addEventListener(
  "click",
  () => void (recipeFormSharingSelected ? saveRecipeSharing() : saveDrinkForm()),
);
mustFind<HTMLButtonElement>("#admin-recipe-tab-recipe").addEventListener("click", () =>
  selectRecipeFormTab(false),
);
mustFind<HTMLButtonElement>("#admin-recipe-tab-sharing").addEventListener("click", () =>
  selectRecipeFormTab(true),
);

addBottleButton.addEventListener("click", () => openAddBottleForm());
// INVIMP1: the Import screen is a full page of its own; closing it after an import re-reads the item list.
importInventoryButton.addEventListener("click", () => {
  openInventoryImport({
    getSession: () => sessionOrUndefined(),
    getPlanningLocationId: inventorySellingSiteId,
    onClose: (imported, followUp) => {
      if (imported !== undefined && sessionOrUndefined() !== undefined) {
        // INVIMPORT2: "Show them" on the Done step lands on the items that still need a shelf.
        if (followUp === "no_shelf") bottleFilterShelfTier = "none";
        void loadBottles();
      }
    },
  });
});
const vendorOptionsHeading = mustFind<HTMLElement>("#admin-bottle-who-we-buy-heading");
hoverHelp(
  vendorOptionsHeading,
  "Sets who supplies this item and the cost per actual order unit.",
  "SKU and cost belong to the selected location. Package and preference use their existing controls. Selling prices are saved separately in Pricing.",
  "A case cost is divided by its recorded pack size by the shared calculator.",
);
for (const [selector, , what, where] of [
  [
    "#admin-bottle-stock-heading",
    "How much we keep",
    "Par and Stop ordering are set separately for each authorized location.",
    "Planning quantities do not change physical stock.",
  ],
  [
    "#admin-bottle-package-heading",
    "What it comes in",
    "Size, container and package identifiers describe the inventory item.",
    "Recorded package and size feed the existing price calculator.",
  ],
  [
    "#admin-bottle-floor-heading",
    "Manual availability",
    "Manual 86 changes apply immediately.",
    "The bound device determines physical stock authority.",
  ],
  [
    "#admin-bottle-cost-target-heading",
    "Cost & target markup",
    "The calculator uses the selected location's preferred Vendor Option and actual package size.",
    "Save this price explicitly saves the applicable selling price. A purchase cost change does not save it.",
  ],
  [
    "#admin-bottle-current-price-heading",
    "What we charge today",
    "Current selling settings depend on the item's selling method and shelf tier.",
    "Liquor per-ounce rates are changed through target markup and Save this price.",
  ],
] as const)
  hoverHelp(mustFind<HTMLElement>(selector), what, where);
bottleFormClose.addEventListener("click", () => closeBottleFormOnPurpose());
bottleFormCancel.addEventListener("click", () => {
  // ADDITEMNEXT1: on steps 2 and 3 of a new item this button reads Back.
  const index = bottleFormTabIndex(activeBottleFormTab());
  if (bottleFormIsNewItem() && index > 0) {
    clearBottleFormIssues();
    switchBottleFormTab(BOTTLE_FORM_TABS[index - 1]!);
    return;
  }
  closeBottleFormOnPurpose();
});
bottleFormNext.addEventListener("click", () => {
  const index = bottleFormTabIndex(activeBottleFormTab());
  const next = BOTTLE_FORM_TABS[index + 1];
  if (next !== undefined) advanceBottleFormTo(next);
});
bottleFormScrim.addEventListener("click", (event) => {
  if (event.target === bottleFormScrim) {
    closeBottleForm();
  }
});
bottleSearchInput.addEventListener("input", () => handleBottleSearchInput());
bottleFormSave.addEventListener("click", () => void saveBottleForm());

// ===== ADDITEM2 (DEC-610, DEC-611; design/mockups/additem2-add-item-steps.html) =====
// Step 2 "How it comes and pours" asks what the item comes in first and lets that answer decide the rest; step 3 "Price" shows
// the math. The old Size, Container type and Keg size selects stay in the DOM (hidden) as the form's state, exactly as before;
// the chips below drive them, so every rule that reads them (sale mechanism from container, par hint, vendor cues, save) is
// unchanged. Every ounce, pour and price figure shown comes from the server (POST .../liquor-products/item-math); nothing
// is computed in the browser except counting units (kegs to pours).
const bottleContainerChipsEl = mustFind<HTMLElement>("#admin-bottle-container-chips");
const bottleKegSizeBlock = mustFind<HTMLElement>("#admin-bottle-keg-size-block");
const bottleKegSizeChipsEl = mustFind<HTMLElement>("#admin-bottle-keg-size-chips");
const bottleSizeBlock = mustFind<HTMLElement>("#admin-bottle-size-block");
const bottleSizeChipsEl = mustFind<HTMLElement>("#admin-bottle-size-chips");
const bottleSizeChipsLabel = mustFind<HTMLElement>("#admin-bottle-size-chips-label");
const bottleOrderAsBlock = mustFind<HTMLElement>("#admin-bottle-order-as-block");
const bottleOrderAsChipsEl = mustFind<HTMLElement>("#admin-bottle-order-as-chips");
const bottleKegWasteBlock = mustFind<HTMLElement>("#admin-bottle-keg-waste-block");
const bottleKegWasteInput = mustFind<HTMLInputElement>("#admin-bottle-keg-waste");
const bottleKegWasteHelp = mustFind<HTMLElement>("#admin-bottle-keg-waste-help");
const bottleStep2MathEl = mustFind<HTMLElement>("#admin-bottle-step2-math");
const bottlePourOptionsBlock = mustFind<HTMLElement>("#admin-bottle-liquor-price-group-pours");
const bottlePriceMathEl = mustFind<HTMLElement>("#admin-bottle-price-math");
const bottlePriceMathNoteEl = mustFind<HTMLElement>("#admin-bottle-price-math-note");
const bottlePriceRowsEl = mustFind<HTMLElement>("#admin-bottle-price-rows");
const bottlePriceHeadingEl = mustFind<HTMLElement>("#admin-bottle-price-heading");
const bottleBuyHeadingEl = mustFind<HTMLElement>("#admin-bottle-buy-heading");
const bottleMoreOptionsEl = mustFind<HTMLDetailsElement>("#admin-bottle-more-options");
const bottleMoreStopOrderingHost = mustFind<HTMLElement>("#admin-bottle-more-stop-ordering");

/** The last good answer from the server for the draft item, and the request that produced it. */
let addItemMath: ItemMathDto | undefined;
let addItemMathKey = "";
let addItemMathSequence = 0;
let addItemMathTimer: number | undefined;
/** The pour size whose price the person typed on step 3 (the item has one per-ounce rate; the other sizes follow it). */
let addItemTypedPrice: { ounces: string; text: string } | undefined;
let addItemKegWasteInitial: string | undefined;
let addItemOrderUnitTouched = false;
let addItemUnitPriceTouched = false;
let addItemOrderAs: string | undefined;
let addItemLocationStates: Map<string, string | null> | undefined;
let addItemLocationStatesLoading = false;

/** Rebuilds a single-choice chip group only when its options changed; otherwise just moves the checked mark, so a tap on a
 * chip is not undone by the redraw it causes. */
function syncAddItemChips(
  host: HTMLElement,
  name: string,
  options: { value: string; label: string }[],
  selected: string,
  onSelect: (value: string) => void,
): void {
  const signature = options.map((option) => `${option.value}=${option.label}`).join("|");
  if (host.dataset["sig"] !== signature) {
    renderBottleFilterChipGroup(host, name, name, options, selected, onSelect);
    host.dataset["sig"] = signature;
    return;
  }
  for (const input of host.querySelectorAll<HTMLInputElement>("input")) {
    input.checked = input.dataset["value"] === selected;
  }
}

function pickIntoSelect(select: HTMLSelectElement, value: string): void {
  select.value = value;
  select.dispatchEvent(new Event("change", { bubbles: true }));
}

function addItemContainerOptions(): { value: string; label: string }[] {
  return Array.from(bottleContainerTypeSelect.options)
    .filter((option) => option.value.length > 0)
    .map((option) => ({ value: option.value, label: option.textContent ?? option.value }));
}

/** The sizes a location can order a bottle or can in, for this item kind (the lists the Size select always offered). */
function addItemSizeOptions(container: string): { value: string; label: string }[] {
  if (container === "can") return tenantServingSizes();
  return sizeOptionsForItemKind(bottleFormItemKind);
}

/** The pour sizes this draft is served in: the picked ones, or a straight pour's standard size for the Liquor kind. */
function addItemPourOunces(): string[] {
  if (currentBottleSaleMechanism() !== "per_ounce" || bottleFormItemKind === "liquor") return [];
  return [...bottleFormPourOptions].sort((a, b) => Number(a) - Number(b));
}

/** The sizes the form shows pours and prices for: the picked ones, or (Liquor) the standard pour the server named. */
function addItemShownPours(): string[] {
  const picked = addItemPourOunces();
  if (picked.length > 0 || bottleFormItemKind !== "liquor" || currentBottleSaleMechanism() !== "per_ounce") return picked;
  return addItemMath?.rows.map((row) => row.ounces) ?? [];
}

function addItemWastePercentToBasisPoints(text: string): number | undefined {
  const trimmed = text.trim();
  if (!/^\d{1,2}(\.\d{1,2})?$/.test(trimmed)) return undefined;
  const basisPoints = Math.round(Number(trimmed) * 100);
  return basisPoints >= 0 && basisPoints <= 5000 ? basisPoints : undefined;
}

function addItemBasisPointsToPercentText(basisPoints: number): string {
  return String(basisPoints / 100);
}

/** What the vendor option on this draft says: staged fields on a new item, the preferred option's terms on a saved one. */
function addItemOfferTerms(): {
  costCents: number | null;
  orderUnit: string | null;
  packSize: string | null;
} {
  if (editingBottleId === undefined) {
    const rawCost = bottleVendorOfferCostInput.value.trim();
    const asNumber = Number(rawCost);
    const costCents =
      rawCost.length > 0 && Number.isFinite(asNumber) && asNumber >= 0 ? Math.round(asNumber * 100) : null;
    const orderUnit = bottleVendorOfferOrderUnitInput.value.trim();
    const rawPack = bottleVendorOfferPackSizeInput.value.trim();
    return {
      costCents,
      orderUnit: orderUnit.length === 0 ? null : orderUnit,
      packSize: orderUnit === "case" && rawPack.length > 0 ? rawPack : null,
    };
  }
  const siteId = f3SellingUi.editor?.siteId;
  const resolvedOfferId = bottlePreferredByLocation.find((entry) => entry.locationId === siteId)?.resolvedOfferId;
  const preferred = bottleVendorOffers.find((offer) => offer.active && offer.id === resolvedOfferId);
  const effective =
    preferred === undefined || bottleTermsContext?.locationId !== siteId
      ? undefined
      : bottleEffectiveTerms.get(preferred.id);
  return {
    costCents: effective?.costCents ?? null,
    orderUnit: effective?.orderUnit ?? null,
    packSize: effective?.packSize ?? null,
  };
}

function addItemMathRequest(): ItemMathRequest | undefined {
  const mechanism = currentBottleSaleMechanism();
  if (mechanism === null) return undefined;
  const container = bottleContainerTypeSelect.value;
  const terms = addItemOfferTerms();
  const sizeValue = bottleSizeValueInput.value.trim();
  const sizeUnit = bottleSizeUnitInput.value.trim();
  const wasteText = bottleKegWasteInput.value;
  const waste = container === "keg" ? addItemWastePercentToBasisPoints(wasteText) : undefined;
  const typed = addItemTypedPrice;
  const typedCents = typed === undefined ? undefined : lenientDollarsToCents(typed.text);
  const unitText = bottleUnitPriceInput.value.trim();
  const unitCents = mechanism === "whole_unit" && unitText.length > 0 ? lenientDollarsToCents(unitText) : undefined;
  return {
    ...(f3SellingUi.editor?.siteId === undefined ? {} : { locationId: f3SellingUi.editor.siteId }),
    itemKind: bottleFormItemKind,
    saleMechanism: mechanism,
    ...(container.length === 0 ? {} : { containerType: container }),
    ...(bottleDefaultKegSizeSelect.value.length === 0 ? {} : { defaultKegSize: bottleDefaultKegSizeSelect.value }),
    ...(container === "keg" || sizeValue.length === 0 || sizeUnit.length === 0
      ? {}
      : { sizeValue: Number(sizeValue), sizeUnit }),
    ...(waste === undefined ? {} : { kegWasteBasisPoints: waste }),
    pourOunces: addItemPourOunces(),
    ...(terms.costCents === null ? {} : { costCents: terms.costCents }),
    ...(terms.orderUnit === null ? {} : { orderUnit: terms.orderUnit }),
    ...(terms.packSize === null ? {} : { packSize: terms.packSize }),
    ...(typed !== undefined && typedCents !== null && typedCents !== undefined
      ? { typedPrice: { ounces: typed.ounces, priceCents: typedCents } }
      : unitCents !== undefined && unitCents !== null
        ? { typedPrice: { priceCents: unitCents } }
        : {}),
  };
}

function scheduleAddItemMath(): void {
  if (addItemMathTimer !== undefined) window.clearTimeout(addItemMathTimer);
  addItemMathTimer = window.setTimeout(() => void loadAddItemMath(), 150);
}

async function loadAddItemMath(): Promise<void> {
  addItemMathTimer = undefined;
  const s = sessionOrUndefined();
  const request = addItemMathRequest();
  if (s === undefined || request === undefined || bottleFormScrim.hidden) return;
  if (!hasPermission(s, "admin.liquor_product.manage")) return;
  const key = JSON.stringify(request);
  if (key === addItemMathKey && addItemMath !== undefined) return;
  const sequence = ++addItemMathSequence;
  const result = await postItemMath(s.apiBaseUrl, s.tenantId, request);
  if (sequence !== addItemMathSequence || sessionOrUndefined() !== s || bottleFormScrim.hidden) return;
  if (result.status !== 200) {
    // Absent is honest: a number is never shown that the server did not give.
    addItemMath = undefined;
    addItemMathKey = "";
  } else {
    addItemMath = result.body;
    addItemMathKey = key;
  }
  renderAddItemSteps();
}

function ozText(value: string | null, places = 0): string {
  if (value === null) return "";
  const asNumber = Number(value);
  return Number.isFinite(asNumber) ? asNumber.toFixed(places) : "";
}

function centsText(value: string | number | null): string {
  if (value === null) return "";
  const cents = Number(value);
  return Number.isFinite(cents) ? formatCents(Math.round(cents)) : "";
}

function addItemMathFresh(): ItemMathDto | undefined {
  return addItemMath;
}

/** Counts of the item's own count unit turned into pours, from the server's usable ounces for one container. */
function addItemPoursFor(count: number): number | null {
  const math = addItemMathFresh();
  const pour = Number(addItemShownPours()[0] ?? "");
  const usable = Number(math?.usableOuncesPerContainer ?? "");
  if (math === undefined || !Number.isFinite(pour) || pour <= 0 || !Number.isFinite(usable)) return null;
  return Math.floor((count * usable) / pour);
}

function renderAddItemPoursHint(host: HTMLElement | null, text: string, unitWord: string): void {
  if (host === null) return;
  const count = Number(text.trim());
  const pours = text.trim().length > 0 && Number.isFinite(count) && count > 0 ? addItemPoursFor(count) : null;
  const sellsPoured = currentBottleSaleMechanism() === "per_ounce";
  if (pours === null || !sellsPoured) {
    host.hidden = true;
    host.textContent = "";
    return;
  }
  host.hidden = false;
  host.textContent = `${text.trim()} ${unitWord} is about ${pours} pours of ${addItemShownPours()[0]} oz.`;
}

function addItemCountUnitWord(): string {
  const container = bottleContainerTypeSelect.value;
  return container === "keg" ? "kegs" : container === "can" ? "cans" : "bottles";
}

function renderAddItemStep2(): void {
  const container = bottleContainerTypeSelect.value;
  const isKeg = container === "keg";
  const isNew = bottleFormIsNewItem();
  syncAddItemChips(bottleContainerChipsEl, "admin-bottle-container-chip", addItemContainerOptions(), container, (value) =>
    pickIntoSelect(bottleContainerTypeSelect, value),
  );
  // Keg: which keg is ordered (what is ordered), never the serving size.
  bottleKegSizeBlock.hidden = !isKeg;
  if (isKeg) {
    syncAddItemChips(
      bottleKegSizeChipsEl,
      "admin-bottle-keg-size-chip",
      Array.from(bottleDefaultKegSizeSelect.options)
        .filter((option) => option.value.length > 0)
        .map((option) => ({ value: option.value, label: option.textContent ?? option.value })),
      bottleDefaultKegSizeSelect.value,
      (value) => pickIntoSelect(bottleDefaultKegSizeSelect, value),
    );
  }
  // Bottle, can or an unspecified container: the size of the container.
  bottleSizeBlock.hidden = isKeg;
  if (!isKeg) {
    bottleSizeChipsLabel.textContent =
      container === "can" ? "Can size" : container === "bottle" ? "Bottle size" : "Size";
    const sizeOptions = addItemSizeOptions(container);
    const current = bottleSizeSelect.value;
    const offered = sizeOptions.some((option) => option.value === current)
      ? sizeOptions
      : current.length > 0 && !current.startsWith("Current")
        ? sizeOptions.concat(
            Array.from(bottleSizeSelect.options)
              .filter((option) => option.value === current)
              .map((option) => ({ value: option.value, label: option.textContent ?? option.value })),
          )
        : sizeOptions;
    syncAddItemChips(bottleSizeChipsEl, "admin-bottle-size-chip", offered, current, (value) =>
      pickIntoSelect(bottleSizeSelect, value),
    );
  }
  // A can on a new item is ordered single, in a pack or by the case; the choice seeds the first vendor option.
  const showOrderAs = isNew && catalogItemSetup === undefined && container === "can" && canManageVendorOfferStaged();
  bottleOrderAsBlock.hidden = !showOrderAs;
  if (showOrderAs) {
    syncAddItemChips(
      bottleOrderAsChipsEl,
      "admin-bottle-order-as-chip",
      [
        { value: "single", label: "Single" },
        { value: "4", label: "4-pack" },
        { value: "6", label: "6-pack" },
        { value: "12", label: "Case of 12" },
        { value: "24", label: "Case of 24" },
      ],
      addItemOrderAs ?? "",
      (value) => chooseAddItemOrderAs(value),
    );
  }
  // Pours belong to an item sold by the pour; the Liquor kind pours its location's standard size.
  const pours = currentBottleSaleMechanism() === "per_ounce" && bottleFormItemKind !== "liquor";
  bottlePourOptionsBlock.hidden = !pours;
  // Foam and waste is a keg question.
  bottleKegWasteBlock.hidden = !isKeg;
  if (isKeg) {
    const tenantDefault = addItemMath?.tenantDefaultWasteBasisPoints;
    if (bottleKegWasteInput.value.trim().length === 0 && addItemKegWasteInitial === undefined && tenantDefault !== undefined) {
      const stored = editingBottle?.keg_waste_basis_points ?? null;
      bottleKegWasteInput.value = addItemBasisPointsToPercentText(stored ?? tenantDefault);
      addItemKegWasteInitial = bottleKegWasteInput.value;
    }
    bottleKegWasteHelp.textContent =
      tenantDefault === undefined
        ? ""
        : `New kegs start at ${addItemBasisPointsToPercentText(tenantDefault)}%, from Settings. Change it for this item if it foams more or less.`;
  }
  // The green math line, from the server's numbers.
  const math = addItemMathFresh();
  const firstPour = addItemShownPours()[0];
  const firstRow = math?.rows[0];
  let line = "";
  if (math !== undefined && math.ouncesPerContainer !== null) {
    const containerWord = isKeg
      ? (Array.from(bottleDefaultKegSizeSelect.options).find((option) => option.value === bottleDefaultKegSizeSelect.value)
          ?.textContent ?? "keg").toLowerCase()
      : container === "can"
        ? "can"
        : "bottle";
    if (isKeg && math.usableOuncesPerContainer !== null) {
      const waste = addItemBasisPointsToPercentText(math.wasteBasisPoints);
      line = `1 ${containerWord} = ${ozText(math.ouncesPerContainer)} oz, less ${waste}% waste = ${ozText(math.usableOuncesPerContainer)} oz`;
      if (firstPour !== undefined && firstRow?.poursPerContainer != null) {
        line += ` ≈ ${firstRow.poursPerContainer} pours of ${firstPour} oz`;
      }
    } else if (container === "can") {
      const terms = addItemOfferTerms();
      const par = Number(bottleParLevelInput.value.trim());
      const caseSize = terms.orderUnit === "case" && terms.packSize !== null ? Number(terms.packSize) : undefined;
      if (caseSize !== undefined && caseSize > 0) {
        line =
          Number.isFinite(par) && par > 0
            ? `Par ${par} cans = ${Math.ceil(par / caseSize)} ${Math.ceil(par / caseSize) === 1 ? "case" : "cases"}. Orders round up to whole cases.`
            : `Ordered by the case of ${caseSize}. Orders round up to whole cases.`;
      } else {
        line = `1 can = ${ozText(math.ouncesPerContainer, 1)} oz.`;
      }
    } else {
      line = `1 ${containerWord} = ${ozText(math.ouncesPerContainer, 1)} oz`;
      if (firstPour !== undefined && firstRow?.poursPerContainer != null) {
        line += ` ≈ ${firstRow.poursPerContainer} pours of ${firstPour} oz`;
      }
    }
  }
  bottleStep2MathEl.hidden = line.length === 0;
  bottleStep2MathEl.textContent = line;
  // About-pours helpers under Par and On hand now.
  renderAddItemPoursHint(
    document.querySelector<HTMLElement>("#admin-bottle-par-pours"),
    bottleParLevelInput.value,
    addItemCountUnitWord(),
  );
  renderAddItemPoursHint(
    document.querySelector<HTMLElement>("#admin-bottle-start-stock-pours"),
    bottleStartStockInput.value,
    addItemCountUnitWord(),
  );
  placeStopOrderingInMoreOptions();
}

/** On one location Stop ordering sits in More options; the many-location table keeps its own column. */
function placeStopOrderingInMoreOptions(): void {
  const label = bottleStopOrderingInput.closest<HTMLElement>("label");
  const single = document.querySelector<HTMLElement>("#admin-bottle-stock-single");
  const help = document.querySelector<HTMLElement>("#admin-bottle-stop-ordering-help");
  if (label === null || single === null || help === null) return;
  const tableMode = !bottleStockTableEl.hidden;
  if (!tableMode && label.parentElement !== bottleMoreStopOrderingHost) {
    bottleMoreStopOrderingHost.append(label, help);
  } else if (tableMode && label.parentElement === bottleMoreStopOrderingHost) {
    single.append(label);
    bottleStockTableEl.insertAdjacentElement("afterend", help);
  }
}

/** The vendor option fields on a new item exist only for a session that may manage vendor options. */
function canManageVendorOfferStaged(): boolean {
  const s = sessionOrUndefined();
  return s !== undefined && hasPermission(s, "inv.vendor_offer.manage") && hasPermission(s, "inv.vendor.view");
}

function chooseAddItemOrderAs(value: string): void {
  addItemOrderAs = value;
  addItemOrderUnitTouched = true;
  if (value === "single") {
    bottleVendorOfferOrderUnitInput.value = "can";
    bottleVendorOfferPackSizeInput.value = "";
  } else {
    bottleVendorOfferOrderUnitInput.value = "case";
    bottleVendorOfferPackSizeInput.value = value;
  }
  refreshVendorOfferFormCues();
  refreshAddItemSteps();
}

/** A new item's vendor option starts on the order unit the container suggests, until the person picks one. */
function applyAddItemOrderUnitDefault(): void {
  if (!bottleFormIsNewItem() || addItemOrderUnitTouched) return;
  const container = bottleContainerTypeSelect.value;
  const wanted = container === "keg" ? "keg" : container === "bottle" ? "bottle" : container === "can" ? "can" : "";
  if (bottleVendorOfferOrderUnitInput.value !== wanted) {
    bottleVendorOfferOrderUnitInput.value = wanted;
    refreshVendorOfferFormCues();
  }
}

/** NC code is the vendor code ABC uses; it matters only for liquor sold in North Carolina, so it shows for nothing else. */
function ncCodeApplies(): boolean {
  return ncCodeAppliesToKind(bottleFormItemKind);
}

function ncCodeAppliesToKind(itemKind: string): boolean {
  if (itemKind !== "liquor") return false;
  const siteId = f3SellingUi.editor?.siteId;
  if (siteId === undefined || addItemLocationStates === undefined) return false;
  return (addItemLocationStates.get(siteId) ?? "").trim().toUpperCase() === "NC";
}

async function loadAddItemLocationStates(): Promise<void> {
  const s = sessionOrUndefined();
  if (s === undefined || addItemLocationStates !== undefined || addItemLocationStatesLoading) return;
  if (!hasPermission(s, "admin.tenant_settings.view")) return;
  addItemLocationStatesLoading = true;
  const result = await getLocationAddresses(s.apiBaseUrl, s.tenantId);
  addItemLocationStatesLoading = false;
  if (sessionOrUndefined() !== s || result.status !== 200) return;
  addItemLocationStates = new Map(result.body.locations.map((entry) => [entry.id, entry.state]));
  renderAddItemSteps();
}

// ===== CATALOG1 (DEC-613, DEC-614; design/mockups/additem2-add-item-steps.html screens 1A and 1B) =====
// "Find a product": typing a producer or a product lists what every Snip & Sip location already knows (the shared product
// catalog, facts only). Picking one shows each value that would be filled and where it came from; nothing is filled until the
// person says so. A product the customer already carries opens that item instead of making a duplicate. Add only.
const findProduct: {
  sequence: number;
  timer: number | undefined;
  picked: SharedCatalogProductDto | undefined;
  /** The AI-found product the person continued with; the save feeds the catalog with source "ai" only while the name still matches. */
  aiFilled: { producer: string; productName: string } | undefined;
} = { sequence: 0, timer: undefined, picked: undefined, aiFilled: undefined };

function resetFindProduct(): void {
  if (findProduct.timer !== undefined) window.clearTimeout(findProduct.timer);
  findProduct.timer = undefined;
  findProduct.sequence += 1;
  findProduct.picked = undefined;
  findProduct.aiFilled = undefined;
  findInput.value = "";
  findResultsEl.replaceChildren();
  findResultsEl.hidden = true;
  findPreviewRowsEl.replaceChildren();
  findPreviewEl.hidden = true;
  findSection.hidden = true;
}

function showFindProduct(): void {
  const s = sessionOrUndefined();
  if (s === undefined || !hasPermission(s, "admin.liquor_product.manage")) return;
  findSection.hidden = false;
  // The NC code is shown only for liquor at a North Carolina location, so the location's state is read now.
  void loadAddItemLocationStates();
}

function findKindLabel(itemKind: string): string {
  return tenantItemKinds().find((entry) => entry.value === itemKind)?.label ?? itemKindLabel(itemKind);
}

function findCategoryOptions(itemKind: string): { value: string; label: string }[] {
  return itemKind === "liquor" ? tenantLiquorCategories() : tenantItemCategories(itemKind);
}

/** CATALOGAI2: the location's own category for this product (name words, then the carried category, then a style), or undefined. */
function findCategoryOption(product: SharedCatalogProductDto): { value: string; label: string } | undefined {
  return chooseLocationCategory(findCategoryOptions(product.itemKind), product);
}

function findPreviewContext(product: SharedCatalogProductDto) {
  const kindOffered = tenantItemKinds().some((entry) => entry.value === product.itemKind);
  const category = findCategoryOption(product);
  return {
    kindLabel: findKindLabel(product.itemKind),
    categoryLabel: kindOffered && category !== undefined ? category.label : null,
    kindOffered,
    showNcCode: product.itemKind === "liquor" && ncCodeAppliesToKind("liquor"),
  };
}

function renderFindResults(products: readonly SharedCatalogProductDto[], typed: string): void {
  findResultsEl.replaceChildren();
  const groups = groupByProducer(products);
  for (const group of groups) {
    const heading = document.createElement("div");
    heading.className = "add-item-find-group";
    heading.textContent = group.producer;
    findResultsEl.append(heading);
    for (const product of group.products) {
      const row = document.createElement("button");
      row.type = "button";
      row.className = "add-item-find-option";
      row.dataset["testid"] = "admin-bottle-find-option";
      const name = document.createElement("strong");
      name.textContent = product.productName;
      const hint = document.createElement("span");
      hint.className = "add-item-find-hint";
      hint.textContent = ` \u00b7 ${resultHint(product, findKindLabel(product.itemKind))}`;
      row.append(name, hint);
      if (product.onYourList !== undefined && product.onYourList !== null) {
        const badge = document.createElement("span");
        badge.className = "add-item-find-own";
        badge.textContent = "On your list";
        row.append(badge);
      }
      row.addEventListener("click", () => void pickFindProduct(product));
      findResultsEl.append(row);
    }
  }
  if (groups.length === 0) {
    const none = document.createElement("p");
    none.className = "admin-field-help";
    none.dataset["testid"] = "admin-bottle-find-none";
    none.textContent = "Nothing in the catalog matches yet. Add it yourself and the next location will find it.";
    findResultsEl.append(none);
  }
  const fresh = document.createElement("button");
  fresh.type = "button";
  fresh.className = "add-item-find-option add-item-find-new";
  fresh.dataset["testid"] = "admin-bottle-find-new";
  const freshProducer = groups.length === 1 ? groups[0]!.producer : "";
  fresh.textContent = groups.length === 0 ? "+ New product" : newProductLabel(groups);
  fresh.addEventListener("click", () => {
    if (freshProducer.length > 0) bottleProducerInput.value = freshProducer;
    findResultsEl.hidden = true;
    bottleNameInput.focus();
    refreshAddItemSteps();
  });
  findResultsEl.append(fresh);
  const ai = document.createElement("button");
  ai.type = "button";
  ai.className = "add-item-find-option add-item-find-new";
  ai.dataset["testid"] = "admin-bottle-find-ai";
  const aiName = document.createElement("strong");
  aiName.textContent = "Not listed? Look it up with AI";
  const aiHint = document.createElement("span");
  aiHint.className = "add-item-find-hint";
  aiHint.textContent = " \u00b7 you see what it finds first";
  ai.append(aiName, aiHint);
  ai.addEventListener("click", () => void runAiLookup(typed, ai));
  findResultsEl.append(ai);
  findResultsEl.hidden = typed.length < FIND_MIN_CHARS;
}

function scheduleFindSearch(): void {
  findProduct.picked = undefined;
  findPreviewEl.hidden = true;
  if (findProduct.timer !== undefined) window.clearTimeout(findProduct.timer);
  const typed = findInput.value.trim();
  findProduct.sequence += 1;
  if (typed.length < FIND_MIN_CHARS) {
    findResultsEl.replaceChildren();
    findResultsEl.hidden = true;
    return;
  }
  findProduct.timer = window.setTimeout(() => void runFindSearch(typed), FIND_DEBOUNCE_MS);
}

async function runFindSearch(typed: string): Promise<void> {
  const s = sessionOrUndefined();
  if (s === undefined) return;
  const sequence = ++findProduct.sequence;
  const reply = await searchSharedCatalogRequest(s.apiBaseUrl, s.tenantId, typed);
  if (sequence !== findProduct.sequence || sessionOrUndefined() !== s) return;
  if (reply.status !== 200) {
    findResultsEl.replaceChildren();
    const failed = document.createElement("p");
    failed.className = "admin-field-help";
    failed.dataset["testid"] = "admin-bottle-find-error";
    failed.textContent = "The catalog could not be searched just now. You can still fill the item in yourself.";
    findResultsEl.append(failed);
    findResultsEl.hidden = false;
    return;
  }
  renderFindResults(reply.body.products, typed);
}

async function pickFindProduct(product: SharedCatalogProductDto): Promise<void> {
  const own = product.onYourList;
  if (own !== undefined && own !== null) {
    const s = sessionOrUndefined();
    if (s === undefined) return;
    const reply = await getLiquorProduct(s.apiBaseUrl, s.tenantId, own.itemId, f3SellingUi.editor?.siteId);
    if (sessionOrUndefined() !== s) return;
    if (reply.status !== 200) {
      adminToast("Could not open that item.", "danger");
      return;
    }
    resetFindProduct();
    await openEditBottleForm(reply.body.liquorProduct, f3SellingUi.editor?.siteId);
    return;
  }
  const context = findPreviewContext(product);
  findProduct.picked = product;
  findPreviewTitle.textContent = `${product.producer} ${product.productName}`.trim();
  findPreviewNotice.textContent = previewNotice(product, context);
  findPreviewSources.hidden = true;
  findPreviewSources.replaceChildren();
  findPreviewEl.classList.remove("add-item-find-preview--ai");
  findUseButton.textContent = "Use these and continue";
  findPreviewRowsEl.replaceChildren();
  for (const row of previewRows(product, context)) {
    const tr = document.createElement("tr");
    const field = document.createElement("td");
    field.textContent = row.field;
    const value = document.createElement("td");
    value.textContent = row.value;
    const from = document.createElement("td");
    const badge = document.createElement("span");
    badge.className = `add-item-find-source add-item-find-source--${row.from === "AI" ? "ai" : "catalog"}`;
    badge.textContent = row.from;
    from.append(badge);
    tr.append(field, value, from);
    findPreviewRowsEl.append(tr);
  }
  findResultsEl.hidden = true;
  findPreviewEl.hidden = false;
  findUseButton.focus();
}

/** CATALOGAI1: asks the server to look the typed product up with AI, then shows what it found with its sources. Fills nothing. */
async function runAiLookup(typed: string, button: HTMLButtonElement): Promise<void> {
  const s = sessionOrUndefined();
  if (s === undefined) return;
  const sequence = ++findProduct.sequence;
  button.disabled = true;
  button.textContent = "Looking it up...";
  const reply = await lookupProductWithAiRequest(s.apiBaseUrl, s.tenantId, typed);
  if (sequence !== findProduct.sequence || sessionOrUndefined() !== s) return;
  button.disabled = false;
  button.replaceChildren(Object.assign(document.createElement("strong"), { textContent: "Not listed? Look it up with AI" }));
  const say = (message: string, testid: string): void => {
    findResultsEl.querySelector(`[data-testid="${testid}"]`)?.remove();
    const note = document.createElement("p");
    note.className = "admin-field-help";
    note.dataset["testid"] = testid;
    note.setAttribute("role", "alert");
    note.textContent = message;
    findResultsEl.append(note);
  };
  if (reply.status !== 200) {
    say(reply.body.message ?? "The AI lookup did not finish. Try again, or fill the item in yourself.", "admin-bottle-find-ai-error");
    return;
  }
  const lookup = reply.body.lookup;
  if (!lookup.found || lookup.product === null) {
    say("The AI could not find that product on a page it can point to. Fill it in yourself.", "admin-bottle-find-ai-error");
    return;
  }
  const product: SharedCatalogProductDto = { ...lookup.product, id: "", ncCode: null, source: "ai", onYourList: null };
  await pickFindProduct(product);
  findProduct.aiFilled = { producer: product.producer, productName: product.productName };
  findPreviewEl.classList.add("add-item-find-preview--ai");
  findPreviewNotice.textContent = `AI found these for "${typed}". Check each one.`;
  findUseButton.textContent = "These are right, continue";
  findPreviewSources.replaceChildren(document.createTextNode("Sources: "));
  lookup.sources.forEach((source, index) => {
    if (index > 0) findPreviewSources.append(document.createTextNode(", "));
    const link = document.createElement("a");
    link.href = source.url;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.textContent = (() => {
      try {
        return new URL(source.url).hostname.replace(/^www\./, "");
      } catch {
        return source.title;
      }
    })();
    findPreviewSources.append(link);
  });
  findPreviewSources.hidden = false;
}

/** Fills the Step 1 fields from the picked product. Only called after the person has seen the preview and said so. */
function applyFindProduct(product: SharedCatalogProductDto): void {
  const context = findPreviewContext(product);
  if (context.kindOffered && bottleFormItemKind !== product.itemKind) {
    setBottleFormItemKind(product.itemKind);
    renderBottleItemKindChips();
  }
  bottleNameInput.value = product.productName;
  if (product.producer.trim() !== "") bottleProducerInput.value = product.producer;
  const category = findCategoryOption(product);
  // CATALOGAI2: a category the person already picked is never replaced; the form starts on its first option, which is not a pick.
  if (context.categoryLabel !== null && category !== undefined && !bottleCategoryPicked) {
    const select = bottleFormItemKind === "liquor" ? bottleCategorySelect : bottleCategoryOtherSelect;
    if (Array.from(select.options).some((option) => option.value === category.value)) select.value = category.value;
  }
  if (product.abvBasisPoints !== null) {
    bottleAbvInput.value = abvBasisPointsToStrengthDisplayString(product.abvBasisPoints, bottleFormItemKind);
  }
  if (product.countryRegion !== null) bottleCountryRegionInput.value = product.countryRegion;
  if (product.upc !== null) bottleBarcodeInput.value = product.upc;
  if (product.ncCode !== null && ncCodeApplies()) bottleVendorOfferSkuInput.value = product.ncCode;
  findInput.value = `${product.producer} ${product.productName}`.trim();
  findPreviewEl.hidden = true;
  findResultsEl.hidden = true;
  refreshAddItemSteps();
}

findInput.addEventListener("input", () => scheduleFindSearch());
findBackButton.addEventListener("click", () => {
  findProduct.picked = undefined;
  findProduct.aiFilled = undefined;
  findPreviewEl.hidden = true;
  findResultsEl.hidden = findResultsEl.childElementCount === 0;
  findInput.focus();
});
findUseButton.addEventListener("click", () => {
  const product = findProduct.picked;
  if (product === undefined) return;
  applyFindProduct(product);
  bottleFormNext.click();
});

/** After an item is saved, its product facts fill blank shared catalog fields. The item is already saved either way. */
async function contributeToSharedCatalog(
  s: NonNullable<ReturnType<typeof sessionOrUndefined>>,
  item: LiquorProductDto,
  ncCode: string,
): Promise<void> {
  const producer = item.producer?.trim() ?? "";
  if (producer.length === 0) return;
  const reply = await confirmSharedCatalogProductRequest(s.apiBaseUrl, s.tenantId, {
    producer,
    productName: item.name,
    itemKind: item.item_kind,
    ...(item.category.length > 0 ? { category: item.category } : {}),
    ...(item.abv_basis_points === null ? {} : { abvBasisPoints: item.abv_basis_points }),
    ...(item.country_region === null || item.country_region.trim() === "" ? {} : { countryRegion: item.country_region }),
    ...(item.barcode === null || item.barcode.trim() === "" ? {} : { upc: item.barcode }),
    ...(ncCode.length > 0 && item.item_kind === "liquor" ? { ncCode } : {}),
    // Facts the AI found and the person kept as found are marked AI in the catalog; anything they changed is their confirmation.
    ...(findProduct.aiFilled !== undefined &&
    findProduct.aiFilled.producer === producer &&
    findProduct.aiFilled.productName === item.name
      ? { source: "ai" as const }
      : {}),
  });
  if (sessionOrUndefined() !== s) return;
  if (reply.status !== 200) adminToast(`${item.name} was saved. The shared catalog could not be updated just now.`, "neutral");
}

function renderAddItemNcCode(isNew: boolean): void {
  // A saved item keeps its Vendor SKU field exactly as it was; a new item asks only for the NC code, and only where it applies.
  const applies = !isNew || ncCodeApplies();
  const label = document.querySelector<HTMLElement>('label[for="admin-bottle-vendor-offer-sku"]');
  if (label !== null) {
    label.hidden = !applies;
    label.textContent = isNew ? "NC code" : "Vendor SKU";
  }
  bottleVendorOfferSkuInput.hidden = !applies;
}

function addItemMarkupText(percent: number | null, belowCost: boolean): string {
  if (belowCost) return "Below cost";
  if (percent === null) return "";
  return `${percent}%`;
}

/** True while the last step 3 render was for a new item, whose staged vendor fields hide the saved-item list and add card. */
let addItemStep3RenderedAsNew = false;

function renderAddItemStep3(): void {
  const isNew = bottleFormIsNewItem();
  const mechanism = currentBottleSaleMechanism();
  const math = addItemMathFresh();
  const shelf = settingsPricingModel === "shelf" && mechanism === "per_ounce";
  bottleBuyHeadingEl.textContent = "Where you buy it";
  bottlePriceHeadingEl.textContent = mechanism === "whole_unit" ? "Price" : "Price for each pour";
  // A new item shows its first vendor option as plain fields; a saved one keeps the list it always had.
  const staged = isNew && catalogItemSetup === undefined && canManageVendorOfferStaged();
  if (isNew) {
    bottleVendorOffersEl.hidden = !staged;
    bottleVendorOfferListEl.hidden = true;
    bottlePreferredByLocationEl.hidden = true;
    const addOpen = document.querySelector<HTMLElement>("#admin-bottle-vendor-offer-add-open");
    const addCard = document.querySelector<HTMLElement>("#admin-bottle-vendor-offer-add-card");
    if (addOpen !== null) addOpen.hidden = true;
    if (addCard !== null) addCard.hidden = !staged;
    bottleVendorOfferAddButton.hidden = true;
    bottleVendorOfferStatusEl.hidden = true;
    const addHeading = document.querySelector<HTMLElement>(".add-offer-heading");
    if (addHeading !== null) addHeading.hidden = true;
    for (const tag of document.querySelectorAll<HTMLElement>(
      '[data-testid="admin-bottle-vendor-scope-note"], [data-testid="admin-bottle-vendor-scope-tag"], #admin-bottle-who-we-buy-heading',
    )) {
      tag.hidden = true;
    }
  } else {
    bottleVendorOfferListEl.hidden = false;
    bottleVendorOfferAddButton.hidden = false;
    bottleVendorOfferStatusEl.hidden = false;
    const addHeading = document.querySelector<HTMLElement>(".add-offer-heading");
    if (addHeading !== null) addHeading.hidden = false;
    for (const tag of document.querySelectorAll<HTMLElement>(
      '[data-testid="admin-bottle-vendor-scope-note"], [data-testid="admin-bottle-vendor-scope-tag"], #admin-bottle-who-we-buy-heading',
    )) {
      tag.hidden = false;
    }
  }
  // A new item starts with no vendor chosen, so saving it without one never invents a vendor option; a saved item's picker
  // has no such blank (it only lists real vendors).
  const blankVendor = bottleVendorOfferVendorSelect.querySelector<HTMLOptionElement>('option[value=""]:not([disabled])');
  if (isNew && staged) {
    if (blankVendor === null) {
      const blank = document.createElement("option");
      blank.value = "";
      blank.textContent = "Choose a vendor";
      bottleVendorOfferVendorSelect.prepend(blank);
      bottleVendorOfferVendorSelect.value = "";
    }
  } else if (blankVendor !== null) {
    const wasBlank = bottleVendorOfferVendorSelect.value === "";
    blankVendor.remove();
    if (wasBlank && bottleVendorOfferVendorSelect.options.length > 0) bottleVendorOfferVendorSelect.selectedIndex = 0;
  }
  if (!isNew && addItemStep3RenderedAsNew) renderBottleVendorOfferList();
  addItemStep3RenderedAsNew = isNew;
  renderAddItemNcCode(isNew);

  // The math box, from the server.
  bottlePriceMathEl.replaceChildren();
  const terms = addItemOfferTerms();
  let note = "";
  if (math === undefined || terms.costCents === null) {
    bottlePriceMathEl.hidden = true;
    note =
      terms.costCents === null
        ? "Enter what the vendor charges and the price math shows here."
        : "The price math is not available yet.";
  } else {
    const list = document.createElement("dl");
    list.className = "add-item-calc";
    const add = (key: string, value: string): void => {
      const dt = document.createElement("dt");
      dt.textContent = key;
      const dd = document.createElement("dd");
      dd.textContent = value;
      list.append(dt, dd);
    };
    const first = math.rows[0];
    if (mechanism === "per_ounce" && math.costPerUsableOunceCents !== null) {
      const usableText = ozText(math.usableOuncesPerContainer);
      add(`Cost per usable oz (${centsText(terms.costCents)} ÷ ${usableText} oz)`, centsText(math.costPerUsableOunceCents));
      if (first !== undefined) {
        add(`${first.ounces} oz pour cost`, centsText(first.pourCostCents));
      }
    } else if (mechanism === "whole_unit" && math.costPerUnitCents !== null) {
      add("Cost per unit", centsText(math.costPerUnitCents));
    }
    if (math.targetMarkupPercent !== null) add(`Target markup (${itemKindEntry(bottleFormItemKind)?.label ?? "kind"} default)`, `${math.targetMarkupPercent}%`);
    if (mechanism === "per_ounce" && first?.suggestedPriceCents != null) {
      add(`Suggested ${first.ounces} oz price`, centsText(first.suggestedPriceCents));
    } else if (mechanism === "whole_unit" && math.suggestedUnitPriceCents !== null) {
      add("Suggested price", centsText(math.suggestedUnitPriceCents));
    }
    bottlePriceMathEl.hidden = list.childElementCount === 0;
    bottlePriceMathEl.append(list);
  }
  bottlePriceMathNoteEl.textContent = note;
  bottlePriceMathNoteEl.hidden = note.length === 0;

  // A whole-unit item prices one unit: start the field at the server's suggestion (the person can change it) and say what
  // markup the price in the field carries.
  if (mechanism === "whole_unit") {
    if (isNew && !addItemUnitPriceTouched && bottleUnitPriceInput.value.trim() === "" && math?.suggestedUnitPriceCents != null) {
      bottleUnitPriceInput.value = centsToAmountString(math.suggestedUnitPriceCents);
    }
    let markupEl = document.querySelector<HTMLElement>("#admin-bottle-unit-markup");
    if (markupEl === null) {
      markupEl = document.createElement("p");
      markupEl.id = "admin-bottle-unit-markup";
      markupEl.className = "admin-field-help";
      markupEl.dataset["testid"] = "admin-bottle-unit-markup";
      bottleUnitPriceInput.closest("span")?.insertAdjacentElement("afterend", markupEl);
    }
    const typedUnit = math?.typed;
    markupEl.textContent =
      typedUnit != null
        ? `Markup at this price: ${addItemMarkupText(typedUnit.markupPercent, typedUnit.belowCost)}${
            !typedUnit.belowCost && typedUnit.markupPercent !== null && math?.targetMarkupPercent != null
              ? typedUnit.markupPercent < math.targetMarkupPercent
                ? " (below target)"
                : " (at or above target)"
              : ""
          }`
        : "";
  }
  // One price input per pour size. The item has one per-ounce rate, so the sizes follow the one typed.
  const rowsHost = bottlePriceRowsEl;
  const typingIn = document.activeElement instanceof HTMLInputElement ? document.activeElement : undefined;
  const sizes = addItemShownPours();
  const showRows = mechanism === "per_ounce" && !shelf && sizes.length > 0;
  rowsHost.hidden = !showRows;
  if (!showRows) {
    rowsHost.replaceChildren();
    return;
  }
  const signature = sizes.join("|");
  if (rowsHost.dataset["sig"] !== signature) {
    rowsHost.replaceChildren();
    rowsHost.dataset["sig"] = signature;
    for (const ounces of sizes) rowsHost.append(buildAddItemPriceRow(ounces));
  }
  for (const ounces of sizes) {
    const row = rowsHost.querySelector<HTMLElement>(`[data-ounces="${ounces}"]`);
    if (row === null) continue;
    const input = row.querySelector<HTMLInputElement>("input");
    const markupEl = row.querySelector<HTMLElement>('[data-role="markup"]');
    if (input === null || markupEl === null) continue;
    const mathRow = math?.rows.find((entry) => entry.ounces === ounces);
    if (input !== typingIn) {
      let shown = "";
      if (addItemTypedPrice !== undefined && addItemTypedPrice.ounces !== ounces && mathRow?.chargedPriceCents != null) {
        shown = centsToAmountString(mathRow.chargedPriceCents);
      } else if (addItemTypedPrice !== undefined && addItemTypedPrice.ounces === ounces) {
        shown = addItemTypedPrice.text;
      } else if (bottleCurrentPerOuncePriceCents !== null && !isNew) {
        shown = centsToAmountString(Math.round(bottleCurrentPerOuncePriceCents * Number(ounces)));
      } else if (mathRow?.suggestedPriceCents != null) {
        shown = centsToAmountString(mathRow.suggestedPriceCents);
      }
      input.value = shown;
    }
    input.placeholder = mathRow?.suggestedPriceCents != null ? centsToAmountString(mathRow.suggestedPriceCents) : "";
    const typed = math?.typed;
    markupEl.textContent =
      typed != null && addItemTypedPrice !== undefined
        ? `Markup at this price: ${addItemMarkupText(typed.markupPercent, typed.belowCost)}${
            typed.belowCost ? "" : typed.markupPercent !== null && math?.targetMarkupPercent != null
              ? typed.markupPercent < math.targetMarkupPercent
                ? " (below target)"
                : " (at or above target)"
              : ""
          }`
        : math?.targetMarkupPercent != null && mathRow?.suggestedPriceCents != null
          ? `Suggested at ${math.targetMarkupPercent}% markup`
          : "";
  }
}

function buildAddItemPriceRow(ounces: string): HTMLElement {
  const row = document.createElement("div");
  row.className = "add-item-price-row";
  row.dataset["ounces"] = ounces;
  const field = document.createElement("div");
  const label = document.createElement("label");
  label.className = "field-label";
  label.style.marginTop = "0";
  label.textContent = `${ounces} oz price`;
  const wrap = document.createElement("span");
  wrap.className = "field-input-affix field-input-affix--prefix";
  const mark = document.createElement("span");
  mark.className = "field-input-affix-mark";
  mark.setAttribute("aria-hidden", "true");
  mark.textContent = "$";
  const input = document.createElement("input");
  input.className = "field-input";
  input.type = "number";
  input.inputMode = "decimal";
  input.min = "0";
  input.step = "0.01";
  input.id = `admin-bottle-pour-price-${ounces.replace(".", "-")}`;
  input.dataset["testid"] = "admin-bottle-pour-price";
  input.dataset["ounces"] = ounces;
  label.htmlFor = input.id;
  input.addEventListener("input", () => {
    addItemTypedPrice = input.value.trim().length === 0 ? undefined : { ounces, text: input.value.trim() };
    scheduleAddItemMath();
  });
  wrap.append(mark, input);
  field.append(label, wrap);
  const markup = document.createElement("p");
  markup.className = "admin-field-help";
  markup.dataset["role"] = "markup";
  markup.dataset["testid"] = "admin-bottle-pour-markup";
  row.append(field, markup);
  return row;
}

/** One place that redraws steps 2 and 3 from the form's state, and asks the server for the numbers they show. */
function renderAddItemSteps(): void {
  renderAddItemStep2();
  renderAddItemStep3();
}

function refreshAddItemSteps(): void {
  applyAddItemOrderUnitDefault();
  void loadAddItemLocationStates();
  renderAddItemSteps();
  scheduleAddItemMath();
}

function resetAddItemSteps(): void {
  addItemMath = undefined;
  addItemMathKey = "";
  addItemMathSequence += 1;
  addItemTypedPrice = undefined;
  addItemKegWasteInitial = undefined;
  addItemOrderUnitTouched = false;
  addItemUnitPriceTouched = false;
  addItemOrderAs = undefined;
  bottleKegWasteInput.value = "";
  bottleMoreOptionsEl.open = false;
  bottlePriceRowsEl.replaceChildren();
  delete bottlePriceRowsEl.dataset["sig"];
}

/** The keg waste to send: only when it is a keg and the person changed what the form showed; undefined leaves it alone. */
function collectBottleKegWaste(issues: BottleFormIssue[]): number | undefined {
  if (bottleContainerTypeSelect.value !== "keg") return undefined;
  const text = bottleKegWasteInput.value.trim();
  if (text === addItemKegWasteInitial || (addItemKegWasteInitial === undefined && text.length === 0)) return undefined;
  const basisPoints = addItemWastePercentToBasisPoints(text);
  if (basisPoints === undefined) {
    issues.push({ tab: "inventory", message: "Foam and waste must be a percent from 0 to 50, like 10 or 7.5." });
    return undefined;
  }
  return basisPoints;
}

bottleKegWasteInput.addEventListener("input", () => {
  refreshAddItemSteps();
});
for (const input of [
  bottleVendorOfferCostInput,
  bottleVendorOfferPackSizeInput,
  bottleVendorOfferSkuInput,
]) {
  input.addEventListener("input", () => refreshAddItemSteps());
}
bottleVendorOfferOrderUnitInput.addEventListener("change", () => {
  addItemOrderUnitTouched = true;
  refreshAddItemSteps();
});
bottleParLevelInput.addEventListener("input", () => renderAddItemSteps());
bottleStartStockInput.addEventListener("input", () => renderAddItemSteps());
bottleUnitPriceInput.addEventListener("input", () => {
  addItemUnitPriceTouched = true;
  scheduleAddItemMath();
});
// ===== end ADDITEM2 =====

// ===== ADDITEMDRAFT1 (docs/build-briefs/ADDITEMDRAFT1-add-item-keeps-draft.md) =====
// Add item and Edit item keep an unsaved draft in localStorage (ui/src/itemDraft.ts), so a refresh AND an inactivity sign-out
// do not cost what was typed. The next time the same person opens the same form at the same location, the confirm sheet asks
// "Pick up where you left off?". Nothing is written until the person has touched the form, nothing is written while that
// question is open, and a successful Save or Discard deletes the draft. Sign-out does not (that is the point).
interface ItemDraftSession {
  readonly scope: ItemDraftScope;
  readonly editor: object | undefined;
  baseline: ItemDraft;
  /** The person has typed or tapped on this open form; only then is a draft written. */
  touched: boolean;
  /** False while the Resume or Discard question is open. */
  enabled: boolean;
  /** The form was saved: nothing more is written for it. */
  finished: boolean;
  pendingStock: ItemDraft | undefined;
}
let itemDraftSession: ItemDraftSession | undefined;
let itemDraftSaveTimer: number | undefined;

function itemDraftStorage(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

function itemDraftScopeNow(): ItemDraftScope | undefined {
  const current = sessionOrUndefined();
  if (current === undefined) return undefined;
  return {
    tenantId: current.tenantId,
    locationId: f3SellingUi.editor?.siteId ?? "none",
    userId: current.userId,
    productId: editingBottleId,
  };
}

function captureItemDraft(): ItemDraft {
  const isNew = bottleFormIsNewItem();
  const stockTable = Array.from(bottleStockTableInputs.entries()).map(([locationId, inputs]) => ({
    locationId,
    par: inputs.par.value,
    stopOrdering: inputs.stopOrdering.checked,
  }));
  const fields: ItemDraft["fields"] = {
    ...emptyItemDraftFields(),
    itemKind: bottleFormItemKind,
    saleMechanism: bottleFormSaleMechanism ?? "",
    name: bottleNameInput.value,
    category: bottleCategorySelect.value,
    categoryOther: bottleCategoryOtherSelect.value,
    unitPrice: bottleUnitPriceInput.value,
    shelfTier: bottleShelfTierSelect.value,
    sizeValue: bottleSizeValueInput.value,
    sizeUnit: bottleSizeUnitInput.value,
    size: bottleSizeSelect.value,
    containerType: bottleContainerTypeSelect.value,
    defaultKegSize: bottleDefaultKegSizeSelect.value,
    parLevel: bottleParLevelInput.value,
    stopOrdering: bottleStopOrderingInput.checked,
    locationStock: stockTable,
    barcode: bottleBarcodeInput.value,
    startStock: bottleStartStockInput.value,
    abv: bottleAbvInput.value,
    producer: bottleProducerInput.value,
    countryRegion: bottleCountryRegionInput.value,
    description: bottleDescriptionInput.value,
    kegWaste: bottleKegWasteInput.value,
    // A saved item's vendor card is its own save with its own button; only a new item's staged vendor option is a draft.
    vendorId: isNew ? bottleVendorOfferVendorSelect.value : "",
    vendorCost: isNew ? bottleVendorOfferCostInput.value : "",
    vendorPackSize: isNew ? bottleVendorOfferPackSizeInput.value : "",
    vendorSku: isNew ? bottleVendorOfferSkuInput.value : "",
    vendorOrderUnit: isNew ? bottleVendorOfferOrderUnitInput.value : "",
    typedPriceOunces: addItemTypedPrice?.ounces ?? "",
    typedPriceText: addItemTypedPrice?.text ?? "",
    orderAs: addItemOrderAs ?? "",
    caseBarcode: bottleCaseBarcodeInput.value,
    caseSize: bottleCaseSizeInput.value,
    calcOzTarget: bottleCalcOzTargetMarkupInput.value,
    calcBottleTarget: bottleCalcBottleTargetMarkupInput.value,
    hideOnMenu: bottleHideOnMenuInput.checked,
    trackStock: itemTrackStockInput.checked,
    active: bottleActiveInput.checked,
    saleMechanismTouched: bottleFormSaleMechanismTouched,
    sizeUserSet: bottleFormSizeUserSet,
    containerUserSet: bottleFormContainerTypeUserSet,
    orderUnitTouched: addItemOrderUnitTouched,
    unitPriceTouched: addItemUnitPriceTouched,
    calcOzTouched: bottleCalcOzTargetMarkupTouched,
    calcBottleTouched: bottleCalcBottleTargetMarkupTouched,
    pourOptions: [...bottleFormPourOptions],
  };
  const stepNow: ItemDraftStep = activeBottleFormTab();
  return { step: ITEM_DRAFT_STEPS.includes(stepNow) ? stepNow : "drink", fields };
}

/** Writes (or removes) the draft for the open form. Silent: a draft that cannot be stored never disturbs the form. */
function writeItemDraft(): void {
  const open = itemDraftSession;
  const storage = itemDraftStorage();
  if (open === undefined || storage === undefined || !open.enabled || open.finished || !open.touched) return;
  const now = captureItemDraft();
  if (itemDraftFieldsEqual(now, open.baseline)) {
    clearItemDraft(storage, open.scope);
  } else {
    saveItemDraft(storage, open.scope, now, Date.now());
  }
}

function scheduleItemDraftSave(): void {
  if (itemDraftSession === undefined) return;
  if (itemDraftSaveTimer !== undefined) window.clearTimeout(itemDraftSaveTimer);
  itemDraftSaveTimer = window.setTimeout(() => {
    itemDraftSaveTimer = undefined;
    writeItemDraft();
  }, 150);
}

/** Writes at once what a pending debounce was about to write (a refresh, a close, a sign-out). */
function flushItemDraft(): void {
  if (itemDraftSaveTimer === undefined) return;
  window.clearTimeout(itemDraftSaveTimer);
  itemDraftSaveTimer = undefined;
  writeItemDraft();
}

/** The form was saved, or the person chose Discard: the draft is finished and nothing more is written for this open form. */
function finishItemDraft(): void {
  const open = itemDraftSession;
  if (itemDraftSaveTimer !== undefined) window.clearTimeout(itemDraftSaveTimer);
  itemDraftSaveTimer = undefined;
  if (open === undefined) return;
  open.finished = true;
  const storage = itemDraftStorage();
  if (storage !== undefined) clearItemDraft(storage, open.scope);
}

/** The person closed the sheet with its Close or Cancel button: they chose to leave it, so there is nothing to pick up
 * later. (A tap beside the sheet, a sign-out and a refresh all keep the draft.) */
function closeBottleFormOnPurpose(): void {
  finishItemDraft();
  closeBottleForm();
}

/** The sheet is closed or the session is gone: stop watching the form. The stored draft stays. */
function endItemDraftSession(): void {
  flushItemDraft();
  itemDraftSession = undefined;
}

for (const eventName of ["input", "change", "click"] as const) {
  bottleFormScrim.addEventListener(eventName, () => {
    const open = itemDraftSession;
    if (open === undefined || !open.enabled || open.finished) return;
    open.touched = true;
    scheduleItemDraftSave();
  });
}
window.addEventListener("pagehide", () => flushItemDraft());

/** Called when Add item or Edit item has just opened: starts watching, and offers a stored draft back. */
function beginItemDraft(): void {
  endItemDraftSession();
  const scope = itemDraftScopeNow();
  const storage = itemDraftStorage();
  if (scope === undefined || storage === undefined || catalogItemSetup !== undefined) return;
  const open: ItemDraftSession = {
    scope,
    editor: f3SellingUi.editor,
    baseline: captureItemDraft(),
    touched: false,
    enabled: true,
    finished: false,
    pendingStock: undefined,
  };
  itemDraftSession = open;
  const stored = loadItemDraft(storage, scope, Date.now());
  if (stored === undefined) return;
  open.enabled = false;
  void offerItemDraft(open, stored);
}

async function offerItemDraft(open: ItemDraftSession, stored: ItemDraft): Promise<void> {
  const resume = await showConfirmSheet({
    title: "Pick up where you left off?",
    message: bottleFormIsNewItem()
      ? "You started adding an item here and did not save it. Resume brings back everything you entered."
      : "You started changing this item and did not save it. Resume brings back everything you entered.",
    confirmLabel: "Resume",
    cancelLabel: "Discard",
    tone: "accent",
  });
  if (itemDraftSession !== open || bottleFormScrim.hidden || f3SellingUi.editor !== open.editor) return;
  if (!resume) {
    const storage = itemDraftStorage();
    if (storage !== undefined) clearItemDraft(storage, open.scope);
    open.enabled = true;
    return;
  }
  const applied = await applyItemDraft(open, stored);
  if (itemDraftSession !== open) return;
  open.enabled = true;
  if (applied) {
    open.touched = true;
    scheduleItemDraftSave();
  } else {
    const storage = itemDraftStorage();
    if (storage !== undefined) clearItemDraft(storage, open.scope);
    adminToast("That unfinished entry no longer fits this form, so it was discarded.", "neutral");
  }
}

function setSelectValueKeeping(select: HTMLSelectElement, value: string): void {
  if (value.length > 0 && !Array.from(select.options).some((option) => option.value === value)) {
    select.add(new Option(value, value));
  }
  select.value = value;
}

/** Puts the Par and Stop ordering a draft holds on the locations table, once the table exists. */
function applyItemDraftStock(draft: ItemDraft): boolean {
  if (bottleStockLoadState !== "ready") return false;
  const layout = editorLayout(bottleStockLoaded);
  if (layout === "single") {
    bottleStopOrderingInput.checked = draft.fields.stopOrdering;
    bottleParLevelInput.disabled = draft.fields.stopOrdering;
    bottleParLevelInput.value = draft.fields.stopOrdering ? "" : draft.fields.parLevel;
    refreshBottleParUnitHint();
    return true;
  }
  if (layout === "table") {
    for (const entry of draft.fields.locationStock) {
      const inputs = bottleStockTableInputs.get(entry.locationId);
      if (inputs === undefined) continue;
      inputs.stopOrdering.checked = entry.stopOrdering;
      inputs.par.disabled = entry.stopOrdering;
      inputs.par.value = entry.stopOrdering ? "" : entry.par;
    }
    return true;
  }
  return true;
}

/** Puts every field of a draft back on the open form and shows the step the person was on. False when the draft does not
 * fit this form (an item type that no longer exists). */
async function applyItemDraft(open: ItemDraftSession, draft: ItemDraft): Promise<boolean> {
  const f = draft.fields;
  if (!isCurrentItemKind(f.itemKind)) return false;
  const isNew = bottleFormIsNewItem();
  resetFindProduct();
  // Kind first: it decides which category list, size list and pour list the rest of the form offers.
  if (f.itemKind !== bottleFormItemKind) setBottleFormItemKind(f.itemKind);
  renderBottleItemKindChips();
  bottleFormSaleMechanismTouched = f.saleMechanismTouched;
  bottleFormSaleMechanism =
    f.saleMechanism === "per_ounce" || f.saleMechanism === "whole_unit" ? f.saleMechanism : null;
  // Defaults must not overwrite what was typed while the raw values go back.
  bottleFormSizeUserSet = true;
  bottleFormContainerTypeUserSet = true;
  setSelectValueKeeping(bottleCategorySelect, f.category);
  setSelectValueKeeping(bottleCategoryOtherSelect, f.categoryOther);
  bottleNameInput.value = f.name;
  bottleBarcodeInput.value = f.barcode;
  bottleAbvInput.value = f.abv;
  bottleProducerInput.value = f.producer;
  bottleCountryRegionInput.value = f.countryRegion;
  bottleDescriptionInput.value = f.description;
  bottleSizeValueInput.value = f.sizeValue;
  bottleSizeUnitInput.value = f.sizeUnit;
  setSelectValueKeeping(bottleContainerTypeSelect, f.containerType);
  setSelectValueKeeping(bottleDefaultKegSizeSelect, f.defaultKegSize);
  refreshBottleSizeForItemKind();
  if (f.size.length > 0) setSelectValueKeeping(bottleSizeSelect, f.size);
  bottleFormSizeUserSet = f.sizeUserSet;
  bottleFormContainerTypeUserSet = f.containerUserSet;
  bottleFormPourOptions = new Set(f.pourOptions);
  renderBottlePourOptionChips();
  bottleUnitPriceInput.value = f.unitPrice;
  setSelectValueKeeping(bottleShelfTierSelect, f.shelfTier);
  bottleHideOnMenuInput.checked = f.hideOnMenu;
  itemTrackStockInput.checked = f.trackStock;
  if (!isNew) bottleActiveInput.checked = f.active;
  refreshItemAddonFields();
  bottleStartStockInput.value = f.startStock;
  bottleCaseBarcodeInput.value = f.caseBarcode;
  // The barcode lives under More options on step 2; a restored one is shown, not hidden behind a closed disclosure.
  if (f.barcode.length > 0) bottleMoreOptionsEl.open = true;
  bottleCaseSizeInput.value = f.caseSize;
  bottleKegWasteInput.value = f.kegWaste;
  bottleCalcOzTargetMarkupInput.value = f.calcOzTarget;
  bottleCalcBottleTargetMarkupInput.value = f.calcBottleTarget;
  bottleCalcOzTargetMarkupTouched = f.calcOzTouched;
  bottleCalcBottleTargetMarkupTouched = f.calcBottleTouched;
  addItemOrderUnitTouched = f.orderUnitTouched;
  addItemUnitPriceTouched = f.unitPriceTouched;
  addItemOrderAs = f.orderAs.length > 0 ? f.orderAs : undefined;
  addItemTypedPrice =
    f.typedPriceOunces.length > 0 && f.typedPriceText.length > 0
      ? { ounces: f.typedPriceOunces, text: f.typedPriceText }
      : undefined;
  renderBottleSaleMechanismControls();
  applyBottleItemKindVisibility();
  refreshBottleDefaultKegSizeVisibility();
  refreshBottleParUnitHint();
  refreshBottleStartStock();
  // The vendor and the staged vendor option (a new item only). The vendor list loads on its own schedule.
  if (isNew) {
    await ensureVendorsLoaded();
    if (itemDraftSession !== open || bottleFormScrim.hidden) return true;
    populateBottleVendorOfferVendorSelect();
  }
  renderAddItemSteps();
  if (isNew) {
    setSelectValueKeeping(bottleVendorOfferVendorSelect, f.vendorId);
    setSelectValueKeeping(bottleVendorOfferOrderUnitInput, f.vendorOrderUnit);
    bottleVendorOfferCostInput.value = f.vendorCost;
    bottleVendorOfferPackSizeInput.value = f.vendorPackSize;
    bottleVendorOfferSkuInput.value = f.vendorSku;
    refreshVendorOfferFormCues();
  }
  if (!applyItemDraftStock(draft)) open.pendingStock = draft;
  refreshAddItemSteps();
  switchBottleFormTab(draft.step);
  return true;
}

/** Late loads (the locations table, the vendor list) change what the untouched form shows; that is not an edit. */
function rebaselineItemDraft(): void {
  const open = itemDraftSession;
  if (open !== undefined && open.enabled && !open.touched && !open.finished) open.baseline = captureItemDraft();
}

/** After the locations table is drawn: a draft waiting for it puts its Par and Stop ordering back. */
function applyPendingItemDraftStock(): void {
  rebaselineItemDraft();
  const open = itemDraftSession;
  if (open?.pendingStock === undefined) return;
  if (applyItemDraftStock(open.pendingStock)) open.pendingStock = undefined;
}
// ===== end ADDITEMDRAFT1 =====

// INV26 Slice 2 (DEC-206 item 3): the three tab buttons.
for (const tab of BOTTLE_FORM_TABS) {
  bottleTabButtons[tab].addEventListener("click", () => handleBottleFormTabClick(tab));
}
// INV26 Slice 3: the calculator's two pour-cost inputs recompute live as
// typed; a shelf-tier change (on the Inventory information tab) can
// change whether Save is even offered, so it also re-renders the
// calculator's save state.
bottleCalcOzTargetMarkupInput.addEventListener("input", () => {
  bottleCalcOzTargetMarkupTouched = true;
  renderBottleCalculator();
});
bottleCalcBottleTargetMarkupInput.addEventListener("input", () => {
  bottleCalcBottleTargetMarkupTouched = true;
  renderBottleCalculator();
});
bottleShelfTierSelect.addEventListener("change", () => renderBottleCalculator());
bottleCalcSaveButton.addEventListener("click", () => void saveBottleCalculatorPrice());

// B4: a genuine user pick on either control marks it "chosen on purpose"
// for the rest of this open form, so a later item-kind change never
// overwrites it (bug-risk rule 2). Setting .value programmatically
// (refreshBottleSizeForItemKind, refreshBottleContainerTypeForItemKind,
// resetBottleForm) never fires a 'change' event, so only a real tap here
// sets these flags; that asymmetry is the whole mechanism.
bottleSizeSelect.addEventListener("change", () => {
  bottleFormSizeUserSet = true;
  applyBottleSizeSelection(bottleSizeSelect.value);
  // INV8 Section 2.6: a bottle/can's own count-unit hint reads size_value/
  // size_unit, which applyBottleSizeSelection just updated above.
  refreshBottleParUnitHint();
});
itemTrackStockInput.addEventListener("change", () => refreshBottleStartStock());
bottleContainerTypeSelect.addEventListener("change", () => {
  bottleFormContainerTypeUserSet = true;
  refreshBottleSaleMechanismFromContainer();
  renderBottleSaleMechanismControls();
  // INV8 Section 2.6/2.7: switching to or from "keg" changes both whether
  // the keg-size row applies and what the Par-level hint should say.
  refreshBottleDefaultKegSizeVisibility();
  refreshBottleParUnitHint();
});
// INV8 Section 2.6/2.7: a keg-size pick changes only the hint (the row's
// own visibility is driven by Container type, not by this select).
bottleDefaultKegSizeSelect.addEventListener("change", () => {
  refreshBottleParUnitHint();
  refreshVendorOfferFormCues();
});

settingsTipPresetAddButton.addEventListener("click", () => handleAddTipPreset());
// Three triggers, one save: every group carrying a tenant_settings field
// calls the exact same saveSettingsForm, which reads and submits every
// field on the form regardless of which button fired it (see that
// function's own comment). Each button's OWN status element is passed
// through so feedback appears next to whichever group the operator is
// actually looking at.
settingsSaveButtonMoney.addEventListener(
  "click",
  () => void saveSettingsForm(settingsSaveButtonMoney, settingsSaveStatusMoneyEl),
);
settingsSaveButtonTabsPayment.addEventListener(
  "click",
  () => void saveSettingsForm(settingsSaveButtonTabsPayment, settingsSaveStatusTabsPaymentEl),
);
// A "Confirmed." or "Saved." line belongs to the person's own last tap and goes at
// their next edit.
settingsBusinessDayTimezoneInput.addEventListener("change", () => {
  settingsSaveStatusOperationsEl.textContent = "";
  renderBusinessDayConfirm();
});
settingsBusinessDayCutoffInput.addEventListener("input", () => {
  settingsSaveStatusOperationsEl.textContent = "";
  renderBusinessDayConfirm();
});
businessDayConfirmButton.addEventListener("click", () => void saveBusinessDay({ confirm: true }));
sessionInactivitySaveButton.addEventListener("click", () => void saveSessionInactivity());
// CL6-F1-F3: this button is now the Business day card's "Save changes", shown
// only after the location confirmed. It saves the time zone and cutoff alone.
settingsSaveButtonOperations.addEventListener(
  "click",
  () => void saveBusinessDay({ confirm: false }),
);
settingsSaveButtonPinPolicy.addEventListener("click", () => void savePinPolicy());
settingsSaveButtonAddress.addEventListener("click", () => void saveLocationAddress());
for (const vocabEditor of VOCAB_EDITORS) {
  vocabEditor.addButton.addEventListener("click", () => void addVocabularyEntry(vocabEditor));
  // VOC1 (DEC-044). The amount opens the shared pad; the unit is a chip
  // with no default selected, the same no-default discipline the reason
  // and liquor pickers already follow.
  vocabEditor.amountButton?.addEventListener("click", () => {
    openVocabPad(vocabEditor);
  });
  vocabEditor.unitPicker?.addEventListener("click", (event) => {
    const chip = (event.target as HTMLElement).closest<HTMLButtonElement>(".admin-chip-option");
    const unit = chip?.dataset["unit"];
    if (unit === undefined) {
      return;
    }
    vocabUnits.set(vocabEditor.vocabulary, unit);
    renderVocabUnitPicker(vocabEditor);
  });
  renderVocabAmount(vocabEditor);
  renderVocabUnitPicker(vocabEditor);
}

mustFind<HTMLElement>("#admin-vocab-pad-grid").addEventListener("click", (event) => {
  const button = (event.target as HTMLElement).closest<HTMLButtonElement>(".pin-pad-digit");
  if (button === null) {
    return;
  }
  if (button.id === "admin-vocab-pad-back") {
    vocabPadEntry = vocabPadEntry.slice(0, -1);
    renderVocabPad();
    return;
  }
  const digit = button.dataset["digit"];
  if (digit !== undefined) {
    vocabPadPress(digit);
  }
});
mustFind<HTMLButtonElement>("#admin-vocab-pad-cancel").addEventListener("click", () => {
  closeVocabPad();
});
mustFind<HTMLButtonElement>("#admin-vocab-pad-done").addEventListener("click", () => {
  const editor = vocabPadEditor;
  if (editor === null) {
    return;
  }
  const entry = vocabPadEntry.replace(/\.$/, "");
  if (entry.length > 0) {
    vocabAmounts.set(editor.vocabulary, entry);
  } else {
    vocabAmounts.delete(editor.vocabulary);
  }
  renderVocabAmount(editor);
  closeVocabPad();
});
settingsTerminalRegisterButton.addEventListener("click", () => void handleTerminalRegister());
settingsVendorAddButton.addEventListener("click", () => void handleVendorAdd());
purchasingApprovalThresholdSaveButton.addEventListener(
  "click",
  () => void handlePurchasingApprovalThresholdSave(),
);
bottleVendorOfferAddButton.addEventListener("click", () => void handleAddBottleVendorOffer());
mustFind<HTMLButtonElement>("#admin-bottle-vendor-offer-add-open").addEventListener("click", () => {
  vendorAddFormOpen = !vendorAddFormOpen;
  renderBottleVendorOfferList();
});
settingsVoidReasonAddButton.addEventListener("click", () => void handleVoidReasonAdd());
settingsDiscountAddButton.addEventListener("click", () =>
  void (discountEditId === undefined ? handleDiscountAdd() : handleDiscountEditSave()),
);
discountEditClosesTicketInput.addEventListener("change", () => {
  discountEditClosesTicketHint.hidden = !discountEditClosesTicketInput.checked;
});
discountAddOpenButton.addEventListener("click", () => openDiscountAddSheet());
discountAddCloseButton.addEventListener("click", () => closeDiscountAddSheet());
discountAddCancelButton.addEventListener("click", () => closeDiscountAddSheet());
discountAddScrim.addEventListener("click", (event) => {
  if (event.target === discountAddScrim) closeDiscountAddSheet();
});
// Escape closes the popout; Tab and Shift+Tab stay inside it (aria-modal promises as much).
document.addEventListener("keydown", (event) => {
  if (discountAddScrim.hidden) return;
  if (event.key === "Escape") {
    event.preventDefault();
    closeDiscountAddSheet();
  } else if (event.key === "Tab") {
    trapDiscountAddFocus(event);
  }
});
settingsDiscountReasonAddButton.addEventListener("click", () => void handleDiscountReasonAdd());
settingsPriceChangeReasonAddButton.addEventListener(
  "click",
  () => void handlePriceChangeReasonAdd(),
);
settingsPriceChangeFlagSaveButton.addEventListener("click", () => void handlePriceChangeFlagSave());
settingsDiscountApprovalSaveButton.addEventListener("click", () => void handleDiscountApprovalSave());
for (const editor of actionReasonEditors) {
  editor.addButton.addEventListener("click", () => void handleActionReasonAdd(editor));
}
settingsRefundReasonAddButton.addEventListener("click", () => void handleRefundReasonAdd());
terminalRenameCloseButton.addEventListener("click", () => closeTerminalRenameSheet());
terminalRenameCancelButton.addEventListener("click", () => closeTerminalRenameSheet());
terminalRenameSaveButton.addEventListener("click", () => void saveTerminalRename());
terminalRenameScrim.addEventListener("click", (event) => {
  if (event.target === terminalRenameScrim) {
    closeTerminalRenameSheet();
  }
});

// INV1: waste/return-to-stock/vendor-return recording sheet.
recordMovementButton.addEventListener("click", () => void openInventoryMovementSheet());
inventoryMovementCloseButton.addEventListener("click", () => closeInventoryMovementSheet());
inventoryMovementCancelButton.addEventListener("click", () => closeInventoryMovementSheet());
inventoryMovementSaveButton.addEventListener("click", () => void saveInventoryMovement());
inventoryMovementScrim.addEventListener("click", (event) => {
  if (event.target === inventoryMovementScrim) {
    closeInventoryMovementSheet();
  }
});

// Review 9 correction (both gates, Medium): the shared confirm sheet.
// Cancel and tapping the scrim both resolve false (dismiss without
// confirming), matching every other sheet's own scrim-tap-to-close
// convention in this file; the confirm button resolves true.
confirmCancelButton.addEventListener("click", () => resolveConfirmSheet(false));
confirmConfirmButton.addEventListener("click", () => resolveConfirmSheet(true));
confirmScrim.addEventListener("click", (event) => {
  if (event.target === confirmScrim) {
    resolveConfirmSheet(false);
  }
});

// Inventory track (track/inventory) Slice 2: Counts panel and its sheets.
countsSectionAddButton.addEventListener("click", () => void handleAddCountSection());
roleAddOpenButton.addEventListener("click", () => openAddRoleSheet());
roleAddCloseButton.addEventListener("click", () => closeAddRoleSheet());
roleAddCancelButton.addEventListener("click", () => closeAddRoleSheet());
roleAddConfirmButton.addEventListener(
  "click",
  () => void handleCreateOrRestoreRole(roleLabelInput.value),
);
roleEditCloseButton.addEventListener("click", () => void requestCloseRoleEditor());
roleEditDiscardButton.addEventListener("click", () => discardStagedRoleChanges());
roleEditReviewButton.addEventListener("click", () => void reviewStagedRoleChanges());
// PERMSEARCH1: typing redraws the role's permissions filtered; clearing the box shows every section again.
roleEditPermissionSearchEl.addEventListener("input", () => renderRolePermissionGrid());
roleEditScrim.addEventListener("click", (event) => {
  // Tapping the backdrop closes the editor (asking first when changes are waiting) and the Add
  // role popup. It never closes one of the role dialogs: those make a decision that lands on
  // other people, so they close only by their own buttons.
  if (event.target !== roleEditScrim) {
    return;
  }
  if (!roleEditSheetEl.hidden) {
    void requestCloseRoleEditor();
  } else if (!roleAddSheetEl.hidden) {
    closeAddRoleSheet();
  }
});
// R5, R6, R7 (DEC-313): what the role dialogs need from this screen, in one place.
initRolesManagement({
  session: () => sessionOrUndefined(),
  roles: () => adminTenantRoles,
  users: () => adminUsers,
  reload: async () => {
    await loadUsers();
    await loadTenantRoles();
  },
  openEditor: (role) => openRoleEditor(role),
  mayChangeRoles: () => mayReadAccessMatrix(),
  toast: (message, variant) => adminToast(message, variant),
  lockScroll: () => lockBodyScroll(),
  unlockScroll: () => unlockBodyScroll(),
});

countsStartButton.addEventListener("click", () => void handleStartOrResumeCount());

countSessionCloseButton.addEventListener("click", () => pauseCountSession());
countSessionScrim.addEventListener("click", (event) => {
  if (event.target === countSessionScrim) {
    pauseCountSession();
  }
});
countPrevButton.addEventListener("click", () => handleCountPrev());
countSkipButton.addEventListener("click", () => handleCountSkip());
countSaveNextButton.addEventListener("click", () => void handleCountSaveNext());
// UX85 (owner directive 2026-10-09): Enter in the counted quantity box does what Save & next does.
// Going through the button keeps one path: the same validation, in-flight guard and error text.
countQuantityInput.addEventListener("keydown", (event) => {
  if (event.key !== "Enter" || event.isComposing || event.repeat) {
    return;
  }
  event.preventDefault();
  if (!countSaveNextButton.disabled) {
    countSaveNextButton.click();
  }
});
countGoReviewButton.addEventListener("click", () => void goToCountReview());
countBackToWalkButton.addEventListener("click", () => backToCountWalk());
countCloseButton.addEventListener("click", () => handleOpenCloseConfirm());

// INV2 (docs/backlog.md, PRD P0.7).
countReassignButton.addEventListener("click", () => void handleOpenReassignPanel());
countsTakeOverButton.addEventListener("click", () => void handleStartRowTakeOver());
countSheetTakeOverButton.addEventListener("click", () => void handleSheetTakeOver());
countReassignCancelButton.addEventListener("click", () => handleCancelReassign());
countReassignConfirmButton.addEventListener("click", () => void handleConfirmReassign());
countBlindToggleButton.addEventListener("click", () => void handleToggleBlindMode());
// INVCOUNT2-S2: back to the table from the walk. The device remembers the choice until the other one is picked.
countTableViewButton.addEventListener("click", () => {
  const sessionId = countWalkState?.sessionId;
  if (sessionId === undefined) return;
  writeCountViewPreference("table");
  pauseCountSession();
  void openCountSessionSheet(sessionId);
});

countCloseConfirmCancelButton.addEventListener("click", () => closeCountCloseConfirm());
countCloseConfirmConfirmButton.addEventListener(
  "click",
  () => void handleConfirmCloseCountSession(),
);
countCloseConfirmScrim.addEventListener("click", (event) => {
  if (event.target === countCloseConfirmScrim) {
    closeCountCloseConfirm();
  }
});

countHistoryDetailCloseButton.addEventListener("click", () => closeCountHistoryDetail());
countHistoryDetailScrim.addEventListener("click", (event) => {
  if (event.target === countHistoryDetailScrim) {
    closeCountHistoryDetail();
  }
});

// Auth track (track/auth) Slice 1: Users panel. The role picker renders
// once here, at module load, rather than per section-open: unlike every
// other panel's own render function it needs no server data, only the
// fixed AUTH_ROLES pair (src/domain/users.ts), so there is nothing to
// reload.
renderAddUserRolePicker();
// U7 (DEC-103, DEC-107): the button opens the full-screen sheet; it
// creates nothing itself.
userAddButton.addEventListener("click", () => openAddUserSheet());
pinRevealDismissButton.addEventListener("click", () => closePinReveal());

// T9: the generate-or-choose reset sheet.
resetPinGenerateButton.addEventListener("click", () => {
  const user = resetPinTarget;
  if (user !== undefined) {
    void handleResetPinUser(user);
  }
});
resetPinChooseButton.addEventListener("click", () => revealResetPinFields());
resetPinSaveButton.addEventListener("click", () => void submitChosenResetPin());
resetPinCancelButton.addEventListener("click", () => closeResetPinSheet());
resetPinScrim.addEventListener("click", (event) => {
  if (event.target === resetPinScrim) {
    closeResetPinSheet();
  }
});
pinRevealScrim.addEventListener("click", (event) => {
  if (event.target === pinRevealScrim) {
    closePinReveal();
  }
});

// ---------------------------------------------------------------------
// Discounts, slice A: Settings > Discounts, and Settings > Discount
// reasons.
//
// This is the Shift4 "Edit Discount" modal's own fields, cut to this
// slice, laid out one discount per row. It is an ADMIN surface, so a
// keyboard and pointer are assumed (product-owner ruling 2026-08-27) and
// a text field is not a defect here. The data-quality rule still applies
// and is separate from touch: the markdown TYPE is a picker rather than a
// typed word, because it comes from a fixed vocabulary and a typo in it
// would be a pricing defect.
//
// Every action acts immediately over its own API call, like Terminals and
// Void reasons above, and is NOT part of "Save location settings".
//
// There is no delete control in this file at all. A discount that past
// applications reference is turned off, never removed; the owner's own
// Shift4 account demonstrates the need, with two dated promotions sitting
// configured and switched off.
// ---------------------------------------------------------------------

/** The value column means different things per markdown type, so it is
 * formatted and parsed in one place rather than at each call site. */
function discountValueText(discount: DiscountDto): string {
  if (discount.markdown_type === "percentage") {
    return basisPointsToPercentString(discount.markdown_basis_points ?? 0);
  }
  if (discount.markdown_type === "amount") {
    return centsToAmountString(discount.markdown_amount_cents ?? 0);
  }
  return "";
}

/** Turns the type picker plus the value box into the three fields the API
 * takes. Throws a plain-sentence Error the caller shows as status text;
 * the server validates the same rules again and is the real enforcement
 * point. */
function readDiscountMarkdown(
  markdownType: string,
  rawValue: string,
): {
  markdownType: "percentage" | "amount";
  markdownBasisPoints?: number;
  markdownAmountCents?: number;
} {
  // DISCAMT1 (Kerry, 2026-11-01): an empty Amount says so plainly. The shared
  // parsers below call an empty string "not a plain number", which read as if
  // the manager had typed something wrong.
  if (rawValue.trim() === "") {
    throw new Error("Enter an amount.");
  }
  // B2/C0, 2026-08-29: a comp is a percentage at 10000 basis points, not
  // its own markdown type. An Admin who wants a comp picks "Percent off"
  // and types 100; this branch handles it like any other percentage.
  if (markdownType === "percentage") {
    return {
      markdownType: "percentage",
      markdownBasisPoints: parsePercentToBasisPoints(rawValue, "Percent off"),
    };
  }
  // DSC23 (DEC-112): the Add-discount form's Comp option is removed, so
  // ordinary use can no longer reach this branch with anything other than
  // "amount". Refuse rather than default, so a future third option, a
  // stale cached page, or a direct DOM value set in a test cannot silently
  // become a dollars-off discount the way the old unconditional fallthrough
  // let "comp" become one.
  if (markdownType === "amount") {
    return {
      markdownType: "amount",
      markdownAmountCents: parseDollarsToCentsExact(rawValue, "Dollars off"),
    };
  }
  throw new Error(`unknown discount type "${markdownType}"`);
}

let discountListMutationRevision = 0;
// DSC35-S5: the schedule editor's unsaved drafts and which rows have it open,
// both kept here so a redraw of the list (any other in-row write rebuilds
// every row) does not throw away a half-built schedule. Reset in invalidate().
const discountEditorDrafts = new Map<string, SpecialEditorDraft>();
const discountEditorOpen = new Set<string>();
// The location's zone, kept for the editor's unset-zone guard. "UTC" is the
// unset default, so it stays the pre-load value: blocking is the safe side.
let discountBusinessDayTimezone = "UTC";
// DSC35-F1: the signed-in location's effective zone, from the discounts list. A schedule is judged and labelled in it
// (the ring uses the location's zone); null until the list has loaded, then the tenant zone above stands in.
let discountLocationTimezone: string | null = null;
function discountScheduleZone(): string {
  return discountLocationTimezone ?? discountBusinessDayTimezone;
}
let discountListRefresh: { generation: number; promise: Promise<void> } | undefined;

async function loadDiscounts(): Promise<void> {
  const gen = loadGeneration;
  applyLocalDiscountPolicy(undefined);
  renderDiscountList();
  // AU7: the scope editor's Shelf group is labeled with the tenant's own
  // tier names, so they have to be in hand before it renders. Idempotent,
  // and a no-op on the ordinary path (loadSettings sets them just before
  // calling this).
  await ensureShelfTierSettingsLoaded(gen);
  // SET16-F4: checked after THIS await too, before the filter-chip
  // render below, not only after the loader's own final fetch further
  // down -- a write between two awaits is exactly as discardable as one
  // after the last (the brief's own loadSettings/loadBottles shape).
  if (!loadIsCurrent(gen)) return;
  // DSC22 (DEC-097): rendered every time this panel's data is (re)loaded,
  // matching the sibling renderBottleFilterChips/renderDrinkFilterChips
  // call sites -- so a tenant switch or sign-out (invalidate() resets the
  // three module variables above) shows every group's own default chip
  // checked again on the next load, not whatever was checked before.
  renderDiscountFilterChips();
  // UX92: the Add Discount button does not wait for the list to arrive; a manager can add while
  // the first read is still in flight (VER55).
  syncDiscountAddOpenButton();
  // SET16-F1: ensureShelfTierSettingsLoaded above is a real network
  // round trip the session can die during; session() would throw
  // rather than quietly return a stale one once it does, so this bails
  // out instead of surfacing as an unhandled rejection.
  const s = sessionOrUndefined();
  if (s === undefined) {
    return;
  }
  // TEN18: what the franchise network offers this location, drawn above the list. Independent of the list read.
  void networkOffers.refresh();
  const outcome = await fetchAndApplyDiscounts(s, gen);
  if (outcome === "obsolete") {
    await refreshDiscountsAfterObsoleteRead(s, gen);
  }
}

async function fetchAndApplyDiscounts(
  s: NonNullable<ReturnType<typeof sessionOrUndefined>>,
  gen: number,
): Promise<"applied" | "obsolete" | "stopped"> {
  if (!loadIsCurrent(gen)) return "stopped";
  const requestedRevision = discountListMutationRevision;
  // activeOnly:false: an Admin needs to see a retired seasonal promotion
  // in order to switch it back on, which the floor picker never shows.
  const result = await listDiscounts(s.apiBaseUrl, s.tenantId, { activeOnly: false });
  // SET16-F4: this session's teardown ran while the fetch above was in
  // flight; discard rather than write over what invalidate() already
  // cleared.
  if (!loadIsCurrent(gen)) return "stopped";
  // A successful local write is newer than the collection snapshot this
  // request started with, even when its response arrives first on the wire.
  if (requestedRevision !== discountListMutationRevision) return "obsolete";
  if (result.status !== 200) {
    settingsDiscountStatusEl.textContent = `Could not load discounts. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`;
    return "stopped";
  }
  settingsDiscountStatusEl.textContent = "";
  applyLocalDiscountPolicy(result.body.localDiscountPolicy?.allowed);
  settingsDiscounts = result.body.discounts;
  discountLocationTimezone = result.body.businessDayTimezone ?? null;
  renderDiscountList();
  return "applied";
}

async function refreshDiscountsAfterObsoleteRead(
  s: NonNullable<ReturnType<typeof sessionOrUndefined>>,
  gen: number,
): Promise<void> {
  if (!loadIsCurrent(gen)) return;
  const existing = discountListRefresh;
  if (existing?.generation === gen) {
    await existing.promise;
    return;
  }
  const refresh = {
    generation: gen,
    promise: (async () => {
      // A mutation may finish during this refresh. Its old response is
      // discarded by the same guard, then a newer full snapshot is fetched.
      while (loadIsCurrent(gen)) {
        const outcome = await fetchAndApplyDiscounts(s, gen);
        if (outcome !== "obsolete") return;
      }
    })(),
  };
  discountListRefresh = refresh;
  try {
    await refresh.promise;
  } finally {
    if (discountListRefresh === refresh) discountListRefresh = undefined;
  }
}

// DSC22 (DEC-097): three single-select radio-chip groups, the same shape
// renderBottleFilterChips already uses for Bottles, reused wholesale
// rather than reinvented. Sort carries a third, default-checked option
// ("Shop order", value "") alongside the two name directions, playing the
// same role "All" already plays in the two filter groups: always present,
// one tap away, so a native radio group (which cannot be unchecked by
// tapping it again) always has a way back to the unsorted, as-loaded view.
function renderDiscountFilterChips(): void {
  renderBottleFilterChipGroup(
    discountSortEl,
    "admin-discount-sort",
    "admin-discount-sort-option",
    [
      { value: "", label: "Shop order" },
      { value: "name-asc", label: "Name A to Z" },
      { value: "name-desc", label: "Name Z to A" },
    ],
    discountSortDirection,
    (value) => {
      discountSortDirection = value as "" | "name-asc" | "name-desc";
      renderDiscountList();
    },
  );
  renderBottleFilterChipGroup(
    discountFilterTypeEl,
    "admin-discount-filter-type",
    "admin-discount-filter-type-option",
    [
      { value: "", label: "All" },
      { value: "percentage", label: "Percent off" },
      { value: "amount", label: "Dollars off" },
    ],
    discountFilterMarkdownType,
    (value) => {
      discountFilterMarkdownType = value as "" | "percentage" | "amount";
      renderDiscountList();
    },
  );
  renderBottleFilterChipGroup(
    discountFilterStateEl,
    "admin-discount-filter-state",
    "admin-discount-filter-state-option",
    [
      { value: "", label: "All" },
      { value: "true", label: "On" },
      { value: "false", label: "Off" },
    ],
    discountFilterActive,
    (value) => {
      discountFilterActive = value as "" | "true" | "false";
      renderDiscountList();
    },
  );
}

// DSC22 (DEC-097): filter first (Type and State combine as AND, matching
// the Bottles filters' own stated combination rule), then sort over
// whatever the filter left. Sort applies only when a direction chip is
// actually selected; "Shop order" (discountSortDirection === "") returns
// the filtered subsequence in settingsDiscounts's own loaded order
// (sort_order, name) with no re-sort, so filtering alone never silently
// imposes an ordering nobody asked for.
function filteredSortedDiscounts(): DiscountDto[] {
  const filtered = settingsDiscounts.filter((discount) => {
    if (
      discountFilterMarkdownType.length > 0 &&
      discount.markdown_type !== discountFilterMarkdownType
    ) {
      return false;
    }
    if (discountFilterActive.length > 0 && String(discount.active) !== discountFilterActive) {
      return false;
    }
    return true;
  });
  if (discountSortDirection === "name-asc") {
    return [...filtered].sort((a, b) => compareNames(a.name, b.name));
  }
  if (discountSortDirection === "name-desc") {
    return [...filtered].sort((a, b) => compareNames(b.name, a.name));
  }
  return filtered;
}

// DSC22 (DEC-097): mirrors bottleListEmptyMessage's own shape -- an empty
// result under an active filter says plainly what was filtered for, Type
// named before State whenever both are set, never a bare "No matches."
// Unreachable with neither filter set: a non-empty settingsDiscounts
// filtered by nothing cannot produce zero rows, so the plain "No
// discounts yet." fallback below stays the only text for a genuinely
// empty, unfiltered tenant.
function discountListEmptyMessage(): string {
  const hasType = discountFilterMarkdownType.length > 0;
  const hasState = discountFilterActive.length > 0;
  if (!hasType && !hasState) {
    return "No discounts yet.";
  }
  const typeLabel = discountFilterMarkdownType === "amount" ? "Dollars off" : "Percent off";
  const subject = hasType ? `${typeLabel} discounts` : "discounts";
  const statePhrase = hasState ? ` that are ${discountFilterActive === "true" ? "On" : "Off"}` : "";
  return `No ${subject}${statePhrase}.`;
}

// DSC35-S6 (DEC-257): the schedule in words and its status, both decided on
// the server in the location's zone (a wrong tablet clock cannot show
// "Running now"). Status is word plus shape plus colour, never colour alone:
// a filled dot for Running now, an open ring for Opens, a square for Off or
// Ended, nothing for a staff-applied discount. Off, Ended and No schedule
// use the tertiary text token, not opacity, so they keep their contrast.
const DISCOUNT_STATUS_SHAPE: Record<string, string> = {
  running: "\u25CF",
  opens: "\u25CB",
  off: "\u25A0",
  ended: "\u25A0",
};

function renderDiscountScheduleStatus(discount: DiscountDto): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "admin-discount-schedule";
  const status = discount.scheduleStatus;
  if (status === undefined) {
    return wrap;
  }
  const summary = document.createElement("span");
  summary.className = "admin-discount-schedule-summary";
  summary.dataset["testid"] = "admin-discount-schedule-summary";
  summary.textContent = discount.scheduleSummary ?? "";
  const chip = document.createElement("span");
  chip.className = `admin-discount-schedule-status admin-discount-schedule-status-${status.state}`;
  chip.dataset["testid"] = "admin-discount-schedule-status";
  chip.dataset["state"] = status.state;
  const shape = DISCOUNT_STATUS_SHAPE[status.state];
  if (shape !== undefined) {
    const mark = document.createElement("span");
    mark.className = "admin-discount-schedule-status-shape";
    mark.setAttribute("aria-hidden", "true");
    mark.textContent = shape;
    chip.append(mark);
  }
  const word = document.createElement("span");
  word.textContent = status.label;
  chip.append(word);
  wrap.append(chip);
  if (discount.scheduleSummary !== null && discount.scheduleSummary !== undefined) {
    wrap.append(summary);
  }
  return wrap;
}

function applyLocalDiscountPolicy(allowed: boolean | undefined): void {
  localDiscountsAllowed = allowed;
  if (allowed !== true) {
    closeDiscountAddSheet(); discountEditorDrafts.clear(); discountEditorOpen.clear();
  }
  syncDiscountAddOpenButton();
}
async function refreshLocalDiscountPolicy(): Promise<void> {
  const current = sessionOrUndefined();
  if (current === undefined || adminScreenEl.hidden || !hasPermission(current, "admin.location_settings.manage")) return;
  const sequence = ++discountPolicyReadSequence, gen = loadGeneration;
  try {
    const result = await readLocalDiscountPolicy(current.apiBaseUrl, current.tenantId);
    if (sequence !== discountPolicyReadSequence || !loadIsCurrent(gen) || sessionOrUndefined()?.token !== current.token) return;
    const allowed = result.status === 200 ? result.body.policy?.allowed : undefined;
    const changed = localDiscountsAllowed !== allowed;
    applyLocalDiscountPolicy(allowed);
    if (changed && allowed !== undefined) { await loadDiscounts(); return; }
  } catch {
    if (sequence !== discountPolicyReadSequence || !loadIsCurrent(gen)) return;
    applyLocalDiscountPolicy(undefined);
  }
  renderDiscountList();
}
window.addEventListener("focus", () => { void refreshLocalDiscountPolicy(); });
setInterval(() => { if (!document.hidden) void refreshLocalDiscountPolicy(); }, 60_000);

function renderDiscountList(): void {
  syncDiscountAddOpenButton();
  settingsDiscountsEl.innerHTML = "";
  if (settingsDiscounts.length === 0) {
    settingsDiscountsEl.innerHTML = '<p class="tabs-empty">No discounts yet.</p>';
    return;
  }
  const visibleDiscounts = filteredSortedDiscounts();
  if (visibleDiscounts.length === 0) {
    const empty = document.createElement("p");
    empty.className = "tabs-empty";
    empty.dataset["testid"] = "admin-discount-empty";
    empty.textContent = discountListEmptyMessage();
    settingsDiscountsEl.append(empty);
    return;
  }
  for (const discount of visibleDiscounts) {
    // TEN18 (P0.21 criterion 3): a copy of the franchise network's published discount shows its terms as plain text.
    // Only the On and Off switch is a control; the server refuses every other change as well.
    if (isNetworkCopy(discount)) {
      settingsDiscountsEl.append(renderNetworkDiscountRow(discount));
      continue;
    }
    if (localDiscountsAllowed !== true || !hasPermission(session(), "admin.location_settings.manage")) {
      const locked = document.createElement("li"); locked.className = "admin-discount-row";
      const name = document.createElement("span"); name.textContent = discount.name;
      const status = document.createElement("span"); status.textContent = discount.retiredByNetwork === true ? "Retired by franchisor" : discount.active ? "Active" : "Inactive";
      const reason = document.createElement("span"); reason.textContent = localDiscountsAllowed === false ? "Controlled by franchisor" : "Read only";
      const terms = document.createElement("span");
      const value = discount.markdown_type === "amount" ? `$${discountValueText(discount)}` : `${discountValueText(discount)}%`;
      const applies = [discount.applies_to_tickets ? "Tickets" : "", discount.applies_to_items ? "Items" : ""].filter(Boolean).join(" and ");
      const scopeLabels = discountScopeGroups(discount).flatMap(group => {
        const labels = discount.scope.filter(clause => clause.dimension === group.dimension)
          .map(clause => group.values.find(([value]) => value === clause.value)?.[1] ?? clause.value);
        return labels.length === 0 ? [] : [`${group.heading}: ${labels.join(", ")}`];
      });
      const specials = (discount.specialScope ?? []).map(entry => entry.name ?? entry.value);
      terms.textContent = [value, applies, ...scopeLabels, ...specials,
        discount.requires_approval ? "Needs approval" : "", discount.requires_reason ? "Needs a reason" : "",
        discount.auto_apply_on_membership ? "Auto-apply on membership" : ""].filter(Boolean).join(" · ");
      locked.append(name, discountKindChip(discount), terms, status, renderDiscountScheduleStatus(discount), reason);
      settingsDiscountsEl.append(locked); continue;
    }
    settingsDiscountsEl.append(renderCompactDiscountRow(discount));
  }
}

/** LOCSET1 (B9): the words a discount's compact row reads as one line: amount, where it applies, the narrowing scope and the
 * rules. */
function discountTermsSummary(discount: DiscountDto): string {
  // Whole percentages read as 25%, not 25.00%; a part of a percent keeps its decimals.
  const percentText = discountValueText(discount).replace(/\.00$/, "");
  const value = discount.markdown_type === "amount" ? `$${discountValueText(discount)} off` : `${percentText}% off`;
  const applies = [discount.applies_to_tickets ? "Tickets" : "", discount.applies_to_items ? "Items" : ""].filter(Boolean).join(" and ");
  const scopeLabels = discountScopeGroups(discount).flatMap(group => {
    const labels = discount.scope.filter(clause => clause.dimension === group.dimension)
      .map(clause => group.values.find(([value]) => value === clause.value)?.[1] ?? clause.value);
    return labels.length === 0 ? [] : [`${group.heading}: ${labels.join(", ")}`];
  });
  const specialKinds = discountSpecialKindOptions();
  const specials = (discount.specialScope ?? []).map(entry => {
    if (entry.name !== undefined) return entry.name;
    const known = entry.kind === "item_kind" ? specialKinds.find(option => option.value === entry.value)?.label : undefined;
    return known ?? entry.value.charAt(0).toUpperCase() + entry.value.slice(1);
  });
  const where = scopeLabels.length === 0 && specials.length === 0 && discount.schedule == null ? ["Everything"] : [];
  return [value, applies, ...scopeLabels, ...specials, ...where,
    discount.requires_approval ? "Needs approval" : "", discount.requires_reason ? "Needs a reason" : "",
    discount.auto_apply_on_membership ? "Auto-apply on membership" : "",
    discount.closes_ticket ? "Closes the ticket" : ""].filter(Boolean).join(" · ");
}

/** LOCSET1 (B9): one compact row per discount, name, amount and scope in words, On or Off, and Edit. Edit opens the existing
 * Add Discount sheet for this discount; nothing is saved until Save there. The On and Off switch keeps its own confirm. */
function renderCompactDiscountRow(discount: DiscountDto): HTMLElement {
  const row = document.createElement("li");
  row.className = "admin-discount-row admin-discount-row-compact";
  row.dataset["testid"] = "admin-discount-row";
  row.dataset["discountId"] = discount.id;

  const main = document.createElement("div");
  main.className = "admin-discount-row-main";
  const titleLine = document.createElement("div");
  titleLine.className = "admin-discount-row-title";
  const name = document.createElement("span");
  name.className = "admin-discount-row-name";
  name.dataset["testid"] = "admin-discount-row-name";
  name.textContent = discount.name;
  titleLine.append(name);
  // DSC21: every row still says Local or Corporate.
  titleLine.append(discountKindChip(discount));
  if (discount.is_shipped_default) {
    const shipped = document.createElement("span");
    shipped.className = "admin-void-reason-shipped";
    shipped.dataset["testid"] = "admin-discount-shipped";
    shipped.textContent = "Default";
    titleLine.append(shipped);
  }
  const status = discount.scheduleStatus;
  if (status !== undefined) {
    const chip = document.createElement("span");
    chip.className = `admin-discount-schedule-status admin-discount-schedule-status-${status.state}`;
    chip.dataset["testid"] = "admin-discount-schedule-status";
    chip.dataset["state"] = status.state;
    const shape = DISCOUNT_STATUS_SHAPE[status.state];
    if (shape !== undefined) {
      const mark = document.createElement("span");
      mark.className = "admin-discount-schedule-status-shape";
      mark.setAttribute("aria-hidden", "true");
      mark.textContent = shape;
      chip.append(mark);
    }
    const word = document.createElement("span");
    word.textContent = status.label;
    chip.append(word);
    titleLine.append(chip);
  }
  main.append(titleLine);
  const terms = document.createElement("div");
  terms.className = "admin-discount-row-terms";
  terms.dataset["testid"] = "admin-discount-terms";
  terms.textContent = discountTermsSummary(discount);
  main.append(terms);
  if (discount.scheduleSummary !== null && discount.scheduleSummary !== undefined && discount.schedule != null) {
    const summary = document.createElement("div");
    summary.className = "admin-discount-schedule-summary";
    summary.dataset["testid"] = "admin-discount-schedule-summary";
    summary.textContent = discount.scheduleSummary;
    main.append(summary);
  }
  row.append(main);

  const actions = document.createElement("div");
  actions.className = "admin-discount-row-actions";
  // DEC-052: the same switch and confirm-before-deactivating flow as every other reversible activate/deactivate control in Settings.
  actions.append(discountActivationSwitch(discount));
  const edit = document.createElement("button");
  edit.type = "button";
  edit.className = "btn-secondary btn-compact";
  edit.dataset["testid"] = "admin-discount-edit";
  edit.textContent = "Edit";
  edit.setAttribute("aria-label", `Edit ${discount.name}`);
  edit.addEventListener("click", () => openDiscountEditSheet(discount.id));
  actions.append(edit);
  row.append(actions);
  return row;
}

/** The On and Off switch of one discount row, with the confirm before turning it off. Shared by a location's own rows and
 * the read-only rows for a copy of the franchise network's discount (TEN18), so there is one write path. */
function discountActivationSwitch(discount: DiscountDto): HTMLLabelElement {
  const switchLabel: HTMLLabelElement = renderActivationSwitch({
    active: discount.active,
    entityLabel: discount.name,
    toggleTestId: "admin-discount-toggle",
    describedById: "admin-settings-discounts-help",
    statusTestId: "admin-discount-status-text",
    activeText: "On",
    inactiveText: "Off",
    confirmDeactivate: (name) => ({
      title: `Turn off ${name}?`,
      message: `Turning ${name} off takes it off the bartender's picker and leaves past tickets readable.`,
      confirmLabel: "Turn off",
    }),
    write: async (nextActive) => {
      const gen = loadGeneration;
      const s = session();
      const result = await updateDiscount(s.apiBaseUrl, s.tenantId, discount.id, {
        active: nextActive,
      });
      if (!discountWriteIsCurrent(gen, s)) return { ok: false };
      if (result.status !== 200) {
        return { ok: false, message: result.body.message ?? "Check your connection." };
      }
      const saved = applyDiscountMutation(result.body.discount);
      return { ok: true, active: saved.active };
    },
    onError: (message) => {
      settingsDiscountStatusEl.textContent = `Could not change this discount. ${capitalizeServerMessage(message)}`;
      // DSC37-F1-V1: the refusal also stands out in red beside the switch, like every other discount save.
      const switchInput = switchLabel.querySelector<HTMLInputElement>("input");
      if (switchInput !== null) {
        showDiscountFieldError("flag", message, switchInput, switchLabel);
      }
    },
    onChange: (active) => {
      settingsDiscountStatusEl.textContent = "";
      adminToast(
        active
          ? `${discount.name} is back on the bartender's list.`
          : `${discount.name} is off the bartender's list. Past tickets still show it.`,
        "success",
      );
      redrawDiscountListAfterEdit(discount.id, '[data-testid="admin-discount-toggle"]');
      // The PATCH carries no status, so a scheduled discount's chip
      // (Running now, Opens, Off) is re-read from the server.
      if (discount.schedule !== null && discount.schedule !== undefined) {
        void loadDiscounts();
      }
    },
  });
  return switchLabel;
}

/** DSC21: the Local or Corporate chip on every discount row. */
function discountKindChip(discount: DiscountDto): HTMLElement {
  const chip = document.createElement("span");
  chip.className = "so-badge so-badge-neutral admin-discount-kind-chip";
  chip.dataset["testid"] = "admin-discount-kind";
  chip.textContent = discountKindLabel(discount);
  return chip;
}

/** TEN18: the row for a copy of the network's published discount. Terms are text, never inputs; the chip names the
 * network; a withdrawn copy says so and its switch is disabled. TEN18-F1: a Required copy shows Required and the locations
 * it applies at and has no switch at all. */
function renderNetworkDiscountRow(discount: DiscountDto): HTMLElement {
  const view = copyRowView(discount);
  const row = document.createElement("li");
  row.className = "admin-discount-row admin-discount-row-network";
  row.dataset["testid"] = "admin-discount-row";
  row.dataset["discountId"] = discount.id;
  row.dataset["networkCopy"] = "true";
  const name = document.createElement("span");
  name.className = "admin-network-copy-name";
  name.dataset["testid"] = "admin-discount-name-text";
  name.textContent = discount.name;
  const chip = document.createElement("span");
  chip.className = "so-badge so-badge-neutral admin-network-copy-chip";
  chip.dataset["testid"] = "admin-discount-network-chip";
  chip.textContent = view.chipText;
  row.append(name, discountKindChip(discount), chip);
  if (view.requiredLabel !== "") {
    const required = document.createElement("span");
    required.className = "so-badge so-badge-good admin-network-copy-chip";
    required.dataset["testid"] = "admin-discount-required";
    required.textContent = view.requiredLabel;
    row.append(required);
  }
  const terms = document.createElement("span");
  terms.className = "admin-network-copy-terms";
  terms.dataset["testid"] = "admin-discount-network-terms";
  terms.textContent = view.termsText;
  row.append(terms);
  if (view.locationsText !== "") {
    const locations = document.createElement("span");
    locations.className = "admin-network-copy-terms";
    locations.dataset["testid"] = "admin-discount-network-locations";
    locations.textContent = view.locationsText;
    row.append(locations);
  }
  // TEN18-F5: the network's window and stacking declaration, read-only text (N/A when the window is open).
  const windowEl = document.createElement("span");
  windowEl.className = "admin-network-copy-terms";
  windowEl.dataset["testid"] = "admin-discount-network-window";
  windowEl.textContent = view.windowText;
  const stackingEl = document.createElement("span");
  stackingEl.className = "admin-network-copy-terms";
  stackingEl.dataset["testid"] = "admin-discount-network-stacking";
  stackingEl.textContent = view.stackingText;
  row.append(windowEl, stackingEl);
  if (view.note !== "") {
    const note = document.createElement("span");
    note.className = "admin-field-help admin-network-copy-note";
    note.dataset["testid"] = "admin-discount-network-note";
    note.textContent = view.note;
    row.append(note);
  }
  if (view.hasSwitch) {
    const switchLabel = discountActivationSwitch(discount);
    if (!view.canSwitch) {
      const input = switchLabel.querySelector("input");
      if (input !== null) input.disabled = true;
    }
    row.append(switchLabel);
  }
  return row;
}

// ---------------------------------------------------------------------
// DSC35-S5: the Schedule and Applies-to editor, opened from a discount's row.
// The draft and the DOM live in ./discountSpecialEditor.ts; this is the wiring
// (what Save sends, where names come from, where the time zone is set).
// ---------------------------------------------------------------------
function renderDiscountSpecialSection(discount: DiscountDto): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "admin-discount-special-wrap";
  const toggle = document.createElement("button");
  toggle.type = "button";
  toggle.className = "btn btn-secondary admin-discount-special-toggle";
  toggle.dataset["testid"] = "admin-discount-special-toggle";
  const isOpen = discountEditorOpen.has(discount.id);
  toggle.setAttribute("aria-expanded", String(isOpen));
  toggle.textContent = isOpen ? "Close schedule" : "Edit schedule";
  toggle.setAttribute("aria-label", `${isOpen ? "Close" : "Edit"} schedule for ${discount.name}`);
  wrap.append(toggle);
  const body = document.createElement("div");
  body.className = "admin-discount-special-body";
  body.hidden = !isOpen;
  wrap.append(body);
  const build = (): void => {
    let draft = discountEditorDrafts.get(discount.id);
    if (draft === undefined) {
      draft = draftFromDiscount(discount);
      discountEditorDrafts.set(discount.id, draft);
    }
    body.replaceChildren(
      renderSpecialEditor({
        discount,
        draft,
        getZone: () => discountScheduleZone(),
        kindOptions: discountSpecialKindOptions(),
        categoryOptions: tenantLiquorCategories(),
        loadPickable: loadSpecialPickable,
        onGoToZone: goToBusinessDayTimezone,
        onSave: (payload) => saveDiscountSpecial(discount.id, payload),
      }),
    );
  };
  if (isOpen) {
    build();
  }
  toggle.addEventListener("click", () => {
    if (discountEditorOpen.has(discount.id)) {
      discountEditorOpen.delete(discount.id);
      body.hidden = true;
      toggle.setAttribute("aria-expanded", "false");
      toggle.textContent = "Edit schedule";
      toggle.setAttribute("aria-label", `Edit schedule for ${discount.name}`);
      return;
    }
    discountEditorOpen.add(discount.id);
    body.hidden = false;
    toggle.setAttribute("aria-expanded", "true");
    toggle.textContent = "Close schedule";
    toggle.setAttribute("aria-label", `Close schedule for ${discount.name}`);
    build();
  });
  return wrap;
}

/** Beer and Wine come through as item kinds (the menu has no Beer category);
 * Liquor is left out because the spirit categories already say it. */
function discountSpecialKindOptions(): Array<{ value: string; label: string }> {
  const source = activeVocabularyOptions(
    (adminVocabularies["item_kind"] ?? []).filter((entry) => isCurrentItemKind(entry.value)),
    ITEM_KINDS.map((kind) => ({ value: kind, label: itemKindLabel(kind) })),
  );
  return source.filter((entry) => entry.value !== "liquor");
}

/** Names for the "Pick specific drinks" panel, read when it is opened. */
async function loadSpecialPickable(): Promise<
  | {
      ok: true;
      drinks: Array<{ id: string; name: string }>;
      bottles: Array<{ id: string; name: string }>;
    }
  | { ok: false; message: string }
> {
  const gen = loadGeneration;
  const s = session();
  const [drinks, bottles] = await Promise.all([
    listMenuDrinks(s.apiBaseUrl, s.tenantId, { activeOnly: true }),
    listLiquorProducts(s.apiBaseUrl, s.tenantId, { activeOnly: true }),
  ]);
  if (!loadIsCurrent(gen)) {
    return { ok: false, message: "Your session changed. Open the editor again." };
  }
  if (drinks.status !== 200) {
    return {
      ok: false,
      message: capitalizeServerMessage(drinks.body.message ?? "Check your connection."),
    };
  }
  if (bottles.status !== 200) {
    return {
      ok: false,
      message: capitalizeServerMessage(bottles.body.message ?? "Check your connection."),
    };
  }
  return {
    ok: true,
    drinks: drinks.body.menuDrinks.map((drink) => ({ id: drink.id, name: drink.name })),
    bottles: bottles.body.liquorProducts
      .filter((product) => !product.hide_on_menu)
      .map((product) => ({ id: product.id, name: product.name })),
  };
}

/** The words "time zone" in the editor's warning go to the field itself. */
function goToBusinessDayTimezone(): void {
  // LOCSET1 (B9): the schedule editor sits in the Edit discount sheet; leaving for the time zone closes it first.
  closeDiscountAddSheet();
  const entry = SETTINGS_SEARCH_INDEX.find(
    (candidate) => candidate.anchorSelector === "#admin-settings-business-day-timezone",
  );
  if (entry !== undefined) {
    void goToSettingsSearchResult(entry);
  }
}

async function saveDiscountSpecial(
  discountId: string,
  payload: {
    schedule: DiscountScheduleDto | null;
    specialScope: Array<{ kind: DiscountSpecialScopeEntryDto["kind"]; value: string }> | null;
  },
): Promise<{ ok: true } | { ok: false; message: string }> {
  const gen = loadGeneration;
  const s = session();
  if (payload.schedule !== null && zoneIsUnset(discountScheduleZone())) {
    return {
      ok: false,
      message:
        "Set the location's time zone before saving a schedule. It is still the default time zone.",
    };
  }
  const result = await updateDiscount(s.apiBaseUrl, s.tenantId, discountId, payload);
  if (!discountWriteIsCurrent(gen, s)) {
    return { ok: false, message: "Your session changed. Nothing was saved." };
  }
  if (result.status !== 200) {
    // The server's own plain sentence (for example the empty-scope refusal);
    // the draft stays exactly as it was so nothing has to be rebuilt.
    return {
      ok: false,
      message: capitalizeServerMessage(result.body.message ?? "Check your connection."),
    };
  }
  applyDiscountMutation(result.body.discount);
  discountEditorDrafts.delete(discountId);
  // The PATCH carries no status or summary; re-read the list so the chip and
  // the words are the server's, then show "Saved" in the freshly drawn editor.
  // LOCSET1 (B9): the editor lives in the edit sheet, which a full reload would close, so the list is re-read in place
  // and only the schedule part of the open sheet is redrawn; typed fields and ticked scope boxes stay as they are.
  const editingHere = discountEditId === discountId && !discountAddScrim.hidden;
  if (editingHere) {
    const reread = sessionOrUndefined();
    if (reread !== undefined) await fetchAndApplyDiscounts(reread, gen);
    const fresh = findDiscount(discountId);
    if (fresh !== undefined && discountEditId === discountId) {
      discountEditScheduleEl.querySelector(".admin-discount-special-wrap")?.remove();
      discountEditScheduleEl.append(renderDiscountSpecialSection(fresh));
      const scopeShown = !discountEditScopeEl.hidden;
      const scopeWanted = fresh.schedule === null || fresh.schedule === undefined;
      if (scopeShown !== scopeWanted) {
        discountEditScopeEl.replaceChildren();
        discountEditScopeEl.hidden = !scopeWanted;
        if (scopeWanted) discountEditScopeEl.append(renderDiscountScopeEditor(fresh));
      }
    }
  } else {
    await loadDiscounts();
  }
  const row = editingHere
    ? discountEditScheduleEl
    : settingsDiscountsEl.querySelector<HTMLElement>(
        `[data-testid="admin-discount-row"][data-discount-id="${discountId}"]`,
      );
  const status = row?.querySelector<HTMLElement>('[data-testid="admin-discount-special-status"]');
  if (status !== null && status !== undefined) {
    status.textContent = "Saved.";
    status.className = "admin-special-status admin-special-ok";
  }
  row?.querySelector<HTMLElement>('[data-testid="admin-discount-special-save"]')?.focus();
  return { ok: true };
}

// ---------------------------------------------------------------------
// DSC5: the attribute scope editor.
//
// FOUR SEPARATELY HEADED GROUPS, never one merged list of "item types",
// and that is the whole reason this is a function rather than another
// entry in the flags loop above.
//
// item_kind is WHAT YOU STOCK. line_kind is HOW IT IS SOLD. One bottle of
// bourbon is item_kind 'liquor' always, and it sells as line_kind
// 'cocktail' in an Old Fashioned and line_kind 'pour' neat, so "50% off
// cocktails" and "50% off liquor" hit different lines on the same ticket.
// The two vocabularies OVERLAP on 'draft beer' and 'packaged' and differ
// everywhere else, so a merged picker would look almost right and be
// wrong in a way nobody would spot on the screen. The headings, the
// one-line explanations under them, and the separate boxes are what stop
// an Admin from ever choosing between them without being told which
// question they are answering.
//
// This is an ADMIN surface, so a keyboard and pointer are assumed
// (product-owner ruling 2026-08-27). Every value is still a checkbox
// rather than a typed word, because the data-quality rule is separate
// from touch and these come from fixed vocabularies.
//
// Ticking nothing means the discount applies to EVERYTHING, which is what
// it meant before this editor existed, and the empty state says so
// instead of leaving it to be inferred.
interface DiscountScopeGroup {
  dimension: DiscountScopeClauseDto["dimension"];
  heading: string;
  testid: string;
  values: ReadonlyArray<[string, string]>;
}

// AU7, owner 2026-08-29, with both screens side by side: "In the site
// settings, we name the 4 tiers of shelves, but when making discount
// codes we dont use those names. You just call them shelf 1-4."
//
// The shelf group is therefore BUILT, not declared: its labels are the
// tenant's own configured tier names (Guest, Member, Purchase, Top
// Shelf), read through resolveShelfTiers, which is the same helper the
// Bottles filter chips, the per-bottle chip and the bottle form's tier
// picker already use, and which already falls back to the plain
// tier_1..tier_4 key when settings have not loaded. Scoping a discount
// to the Member shelf should not require knowing that Member is tier_2.
//
// The stored VALUE is still the tier key, unchanged: only the label an
// Admin reads changes, so no existing discount scope means anything
// different than it did.
//
// ITEM1 Slice 5 (docs/build-briefs/ITEM1-item-kinds-are-a-configurable-
// vocabulary.md, Section 4's "Slice 5" paragraph; migration 0114). The
// item_kind and line_kind groups used to be this same shape, a hardcoded
// five/six-value list -- accurate for the five kinds this product shipped
// with, but wrong the moment a tenant adds a sixth from Settings (ITEM1
// Slice 2, no migration), since a kind added there had no matching chip
// here and no way to become discount-scopable without a code change,
// exactly the gap this slice's own migration closes at the database.
// Both groups are now BUILT, the identical "labels come from the
// tenant's own vocabulary, not a literal" treatment the Shelf group above
// and the Category group below already have, via
// discountScopeItemKindValues/discountScopeLineKindValues just below.

/** ITEM1 Slice 5: line_kind's own label overrides, for the values whose
 * SOLD-AS phrase differs from the item_kind vocabulary's own label
 * (mirrors src/domain/discountScopes.ts's SCOPE_VALUE_WORDS.line_kind,
 * which gives the identical three words for the identical reason: "wine"
 * the bottle you stock and "wine by the glass" the way it is sold are
 * different sentences). Every OTHER value (a tenant-added kind included)
 * falls back to that kind's own item_kind vocabulary label unchanged,
 * since "how it is sold" and "what you stock" read the same for a kind
 * this product does not yet have special wording for. */
const LINE_KIND_LABEL_OVERRIDE: Readonly<Record<string, string>> = {
  cocktail: "Cocktail",
  pour: "Straight pour",
  wine: "Wine by the glass",
  cider: "Cider by the glass",
};

/** ITEM1 Slice 5: the item_kind discount-scope group's own values, driven
 * from the tenant's own active item_kind vocabulary (adminVocabularies),
 * mirroring discountScopeCategoryValues just below move for move: ITEM_
 * KINDS is the fallback for the short window before loadVocabularies()
 * has returned (the same fallback tenantItemKinds uses for the Add/Edit
 * Bottle kind chip), and any value already ticked on this discount that
 * the active list does not currently offer -- a kind retired after it
 * was scoped, or a read before vocabularies have loaded -- is merged in,
 * for the identical reason discountScopeCategoryValues' own comment
 * states: handleDiscountScopeChange reads the WHOLE scope off whatever
 * checkboxes are actually rendered. Deliberately NOT tenantItemKinds
 * itself: that function also merges in `editingBottle`'s own current
 * kind, a bottle-form-specific concern this discount screen has no
 * business reading. */
function discountScopeItemKindValues(discount: DiscountDto): Array<[string, string]> {
  const offered: { value: string; label: string }[] = activeVocabularyOptions(
    (adminVocabularies["item_kind"] ?? []).filter((entry) => isCurrentItemKind(entry.value)),
    ITEM_KINDS.map((kind) => ({ value: kind, label: itemKindLabel(kind) })),
  );
  for (const clause of discount.scope) {
    if (clause.dimension !== "item_kind" || !isCurrentItemKind(clause.value)) {
      continue;
    }
    if (!offered.some((entry) => entry.value === clause.value)) {
      offered.push({ value: clause.value, label: resolveItemKindLabel(clause.value) });
    }
  }
  return sortOptions(offered).map((entry) => [entry.value, entry.label] as [string, string]);
}

/** ITEM1 Slice 5: the line_kind group's DYNAMIC half is the SAME item_kind
 * vocabulary discountScopeItemKindValues just above reads, translated
 * through the identical 'liquor'->'pour' mapping src/domain/
 * discountScopes.ts's own itemKindRequiredForLineKind states from the
 * other direction: 'liquor' is a legal item_kind and NEVER a legal
 * line_kind (this module's own header, "the trap"; a liquor product
 * always sells AS 'pour'), so it is translated rather than offered
 * verbatim. 'cocktail' is the FIXED half: it has no item_kind counterpart
 * at all (a cocktail is never itself a stocked item) and is offered
 * unconditionally, first, matching the order every prior version of this
 * list already used. */
function discountScopeLineKindValues(discount: DiscountDto): Array<[string, string]> {
  const itemKindSource: { value: string; label: string }[] = activeVocabularyOptions(
    (adminVocabularies["item_kind"] ?? []).filter((entry) => isCurrentItemKind(entry.value)),
    ITEM_KINDS.map((kind) => ({ value: kind, label: itemKindLabel(kind) })),
  );
  const offered: { value: string; label: string }[] = [
    { value: "cocktail", label: LINE_KIND_LABEL_OVERRIDE["cocktail"] ?? "Cocktail" },
  ];
  for (const entry of itemKindSource) {
    const lineKindValue = entry.value === "liquor" ? "pour" : entry.value;
    if (offered.some((candidate) => candidate.value === lineKindValue)) {
      continue;
    }
    offered.push({
      value: lineKindValue,
      label: LINE_KIND_LABEL_OVERRIDE[lineKindValue] ?? entry.label,
    });
  }
  for (const clause of discount.scope) {
    if (clause.dimension !== "line_kind" || !isCurrentItemKind(clause.value)) {
      continue;
    }
    if (!offered.some((entry) => entry.value === clause.value)) {
      offered.push({
        value: clause.value,
        label: LINE_KIND_LABEL_OVERRIDE[clause.value] ?? resolveItemKindLabel(clause.value),
      });
    }
  }
  return sortOptions(offered).map((entry) => [entry.value, entry.label] as [string, string]);
}

// DSC11 (DEC-041, 2026-09-02): the Category chips follow the tenant's own
// Liquor-scoped item categories instead of a hardcoded seven, the CT1/SET7
// pattern (docs/build-briefs/CT1-container-type-vocabulary.md) already
// established for container type and this same discount screen's Shelf
// group above. A manager-added category (mezcal) is offered the moment it
// exists; ANY category already ticked on this discount that the active
// list does not currently offer -- a category retired after it was
// scoped, or a read before loadVocabularies() has returned -- is merged
// in exactly like renderDrinkLiquorSlotChips's own offered.push pattern
// for the Drinks form's liquor-slot chips, for the identical reason:
// handleDiscountScopeChange below reads the WHOLE scope off whatever
// checkboxes are actually rendered, so a category missing from this list
// would be silently dropped the next time an Admin ticks anything else on
// this row.
function discountScopeCategoryValues(discount: DiscountDto): Array<[string, string]> {
  const offered = tenantLiquorCategories();
  for (const clause of discount.scope) {
    if (clause.dimension !== "category") {
      continue;
    }
    if (!offered.some((entry) => entry.value === clause.value)) {
      offered.push({ value: clause.value, label: categoryLabel(clause.value) });
    }
  }
  return sortOptions(offered).map((entry) => [entry.value, entry.label] as [string, string]);
}

function discountScopeGroups(discount: DiscountDto): ReadonlyArray<DiscountScopeGroup> {
  return [
    {
      dimension: "shelf_tier",
      heading: "Shelf",
      testid: "admin-discount-scope-shelf-tier",
      values: resolveShelfTiers().map((tier) => [tier.tier, tier.name] as [string, string]),
    },
    {
      dimension: "category",
      heading: "Category",
      testid: "admin-discount-scope-category",
      values: discountScopeCategoryValues(discount),
    },
    {
      dimension: "item_kind",
      heading: "What you stock",
      testid: "admin-discount-scope-item-kind",
      values: discountScopeItemKindValues(discount),
    },
    {
      dimension: "line_kind",
      heading: "How it is sold",
      testid: "admin-discount-scope-line-kind",
      values: discountScopeLineKindValues(discount),
    },
  ];
}

function scopeHas(
  scope: readonly DiscountScopeClauseDto[],
  dimension: string,
  value: string,
): boolean {
  return scope.some((clause) => clause.dimension === dimension && clause.value === value);
}

function renderDiscountScopeEditor(discount: DiscountDto): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "admin-discount-scope";
  wrap.dataset["testid"] = "admin-discount-scope";
  // LOCSET1 (B9): the boxes only stage a change; Save on the edit sheet sends the whole scope. A scope that names a
  // removed kind cannot be changed until the owner maps it, exactly as the inline editor refused.
  const lockedByRemovedKind =
    discount.scope.some(
      (clause) => (clause.dimension === "item_kind" || clause.dimension === "line_kind") && !isCurrentItemKind(clause.value),
    );
  if (lockedByRemovedKind) wrap.dataset["locked"] = "true";

  const summary = document.createElement("p");
  summary.className = "admin-discount-scope-summary";
  summary.dataset["testid"] = "admin-discount-scope-summary";
  summary.textContent =
    discount.scope.length === 0
      ? "Applies to everything. Tick below to narrow it."
      : "Applies only where every ticked group matches.";
  if (lockedByRemovedKind) {
    summary.textContent = "This discount covers a removed kind. Owner mapping is required before its scope can change.";
  }
  wrap.append(summary);

  for (const group of discountScopeGroups(discount)) {
    const fieldset = document.createElement("fieldset");
    fieldset.className = "admin-discount-scope-group";
    fieldset.dataset["testid"] = group.testid;
    fieldset.dataset["dimension"] = group.dimension;

    const legend = document.createElement("legend");
    legend.textContent = group.heading;
    fieldset.append(legend);

    for (const [value, label] of group.values) {
      const check = document.createElement("label");
      check.className = "admin-discount-check";
      const input = document.createElement("input");
      input.type = "checkbox";
      input.dataset["testid"] = `${group.testid}-${value}`;
      input.dataset["dimension"] = group.dimension;
      input.dataset["value"] = value;
      input.checked = scopeHas(discount.scope, group.dimension, value);
      // The aria-label names the GROUP as well as the value, because
      // "Beer" alone appears twice on this row and a screen reader
      // would otherwise read two identical controls that mean different
      // things.
      input.setAttribute("aria-label", `${group.heading}: ${label} for ${discount.name}`);
      if (lockedByRemovedKind) input.disabled = true;
      check.append(input, document.createTextNode(` ${label}`));
      fieldset.append(check);
    }
    wrap.append(fieldset);
  }
  return wrap;
}

function findDiscount(discountId: string): DiscountDto | undefined {
  return settingsDiscounts.find((candidate) => candidate.id === discountId);
}

function discountWriteIsCurrent(gen: number, s: Session): boolean {
  const current = sessionOrUndefined();
  return (
    loadIsCurrent(gen) &&
    current !== undefined &&
    current.tenantId === s.tenantId &&
    current.token === s.token
  );
}

function applyDiscountMutation(discount: DiscountDto): DiscountDto {
  // Advance before touching the visible list. Any collection GET that
  // started earlier must then yield to this committed write.
  discountListMutationRevision += 1;
  const current = findDiscount(discount.id);
  if (current !== undefined) {
    Object.assign(current, discount);
    return current;
  }
  settingsDiscounts.push(discount);
  return discount;
}

// DSC24 (DEC-116): the one redraw path shared by every in-row discount
// write (rename, markdown change, flag change, the activation switch),
// called from each write's success branch only, so an edit is re-evaluated
// against the active filter and sort the same way handleDiscountAdd's own
// renderDiscountList() call already re-evaluates a create. Redrawing tears
// down and rebuilds every row's DOM node (renderDiscountList's own
// innerHTML reset), so this also decides where focus lands afterward:
// back into the same field of the surviving row when that field is where
// the edit actually happened, otherwise the Discounts rail tab, never left
// to fall through to <body>.
function redrawDiscountListAfterEdit(discountId: string, editedFieldSelector: string): void {
  const active = document.activeElement;
  const wasEditingThisField =
    active instanceof HTMLElement &&
    active.matches(editedFieldSelector) &&
    active.closest(`[data-testid="admin-discount-row"][data-discount-id="${discountId}"]`) !== null;
  renderDiscountList();
  if (wasEditingThisField) {
    const survivingRow = settingsDiscountsEl.querySelector<HTMLElement>(
      `[data-testid="admin-discount-row"][data-discount-id="${discountId}"]`,
    );
    const target = survivingRow?.querySelector<HTMLElement>(editedFieldSelector);
    if (target) {
      target.focus();
      return;
    }
  }
  document.getElementById("admin-group-tab-discounts")?.focus();
}

// DSC37-F1 (docs/build-briefs/DSC37-F1-red-discount-save-error.md): a refused Settings discount save shows the
// server's sentence in bright red beside the control the save started from (owner directive TEN21-F2). The
// toast and the status line under the list stay. The marker and message are removed on the next attempt for the row.
let discountFieldErrorSeq = 0;

function clearDiscountFieldErrors(row: Element | null): void {
  if (row === null) return;
  for (const node of Array.from(
    row.querySelectorAll('[data-testid="admin-discount-field-error"]'),
  )) {
    node.remove();
  }
  for (const node of Array.from(row.querySelectorAll('[aria-invalid="true"]'))) {
    node.removeAttribute("aria-invalid");
    const before = node.getAttribute("data-describedby-before");
    if (before === null) continue;
    if (before === "") node.removeAttribute("aria-describedby");
    else node.setAttribute("aria-describedby", before);
    node.removeAttribute("data-describedby-before");
  }
}

/** Shows the refusal under `anchor` (the control's own wrapper) and marks `control` invalid. */
function showDiscountFieldError(
  attempt: DiscountSaveAttempt,
  serverMessage: string | undefined,
  control: HTMLElement,
  anchor: HTMLElement,
): void {
  const row = control.closest('[data-testid="admin-discount-row"]');
  clearDiscountFieldErrors(row);
  discountFieldErrorSeq += 1;
  const error = document.createElement("p");
  error.id = `admin-discount-field-error-${discountFieldErrorSeq}`;
  error.className = "admin-discount-field-error";
  error.dataset["testid"] = "admin-discount-field-error";
  error.setAttribute("role", "alert");
  error.textContent = discountSaveRefusal(attempt, serverMessage).text;
  anchor.after(error);
  const before = control.getAttribute("aria-describedby") ?? "";
  control.setAttribute("data-describedby-before", before);
  control.setAttribute("aria-describedby", `${before} ${error.id}`.trim());
  control.setAttribute("aria-invalid", "true");
}

// UX92: the Add Discount popout. Always opens on a clean form, so a cancelled draft never leaks
// into the next open, and closing returns focus to the button that opened it.
function resetDiscountAddForm(): void {
  settingsDiscountNameInput.value = "";
  settingsDiscountTypeInput.value = "percentage";
  settingsDiscountValueInput.value = "";
  settingsDiscountTicketsInput.checked = true;
  settingsDiscountItemsInput.checked = true;
  settingsDiscountApprovalInput.checked = false;
  settingsDiscountReasonInput.checked = false;
  discountAddErrorEl.textContent = "";
  clearDiscountAddInvalid();
  settingsDiscountAddButton.disabled = false;
  // LOCSET1 (B9): back to the Add Discount shape.
  discountEditId = undefined;
  discountAddTitleEl.textContent = "Add Discount";
  settingsDiscountAddButton.textContent = "Add Discount";
  discountAddCloseButton.setAttribute("aria-label", "Close without adding");
  for (const part of [discountEditRulesEl, discountEditScopeEl, discountEditScheduleEl]) part.hidden = true;
  discountEditScopeEl.replaceChildren();
  discountEditScheduleEl.querySelector(".admin-discount-special-wrap")?.remove();
  discountEditorDrafts.clear();
  discountEditorOpen.clear();
  discountEditAutoApplyInput.checked = false;
  discountEditClosesTicketInput.checked = false;
  discountEditClosesTicketHint.hidden = true;
}

/** LOCSET1 (B9): the discount the Add Discount sheet is editing, or undefined while it adds. */
let discountEditId: string | undefined;
let discountEditReturnId: string | undefined;

/** The discount's scope as the ticked boxes of a staged scope editor now stand, or undefined when the editor is absent or locked. */
function readStagedDiscountScope(wrap: HTMLElement): DiscountScopeClauseDto[] | undefined {
  if (wrap.dataset["locked"] === "true") return undefined;
  const scope: DiscountScopeClauseDto[] = [];
  for (const input of Array.from(wrap.querySelectorAll<HTMLInputElement>("input[type=checkbox]"))) {
    if (!input.checked) continue;
    const dimension = input.dataset["dimension"];
    const value = input.dataset["value"];
    if (dimension === undefined || value === undefined) continue;
    scope.push({ dimension: dimension as DiscountScopeClauseDto["dimension"], value });
  }
  return scope;
}

/** Draws the schedule editor (its own Save schedule) and, for an unscheduled discount, the staged scope editor into the sheet. */
function drawDiscountEditSheetBody(discount: DiscountDto): void {
  discountEditScopeEl.replaceChildren();
  discountEditScopeEl.hidden = true;
  // DSC35-S5: a scheduled special names what it covers in the editor below (any one match), so the four-group AND editor
  // is hidden while a schedule exists; its stored clauses are left exactly as they are.
  if (discount.schedule === null || discount.schedule === undefined) {
    discountEditScopeEl.append(renderDiscountScopeEditor(discount));
    discountEditScopeEl.hidden = false;
  }
  discountEditScheduleEl.querySelector(".admin-discount-special-wrap")?.remove();
  discountEditScheduleEl.append(renderDiscountSpecialSection(discount));
  discountEditScheduleEl.hidden = false;
}

/** LOCSET1 (B9): opens the Add Discount sheet on an existing discount. The fields are the ones the inline row saved; Save sends
 * the changed ones in one request, and Cancel leaves the discount exactly as it was. */
function openDiscountEditSheet(discountId: string): void {
  const discount = findDiscount(discountId);
  if (discount === undefined || localDiscountsAllowed !== true || !discountAddScrim.hidden) return;
  resetDiscountAddForm();
  discountEditId = discountId;
  discountEditReturnId = discountId;
  discountAddTitleEl.textContent = "Edit discount";
  settingsDiscountAddButton.textContent = "Save changes";
  discountAddCloseButton.setAttribute("aria-label", "Close without saving");
  settingsDiscountNameInput.value = discount.name;
  settingsDiscountTypeInput.value = discount.markdown_type;
  settingsDiscountValueInput.value = discountValueText(discount);
  settingsDiscountTicketsInput.checked = discount.applies_to_tickets === true;
  settingsDiscountItemsInput.checked = discount.applies_to_items === true;
  settingsDiscountApprovalInput.checked = discount.requires_approval === true;
  settingsDiscountReasonInput.checked = discount.requires_reason === true;
  discountEditAutoApplyInput.checked = discount.auto_apply_on_membership === true;
  discountEditClosesTicketInput.checked = discount.closes_ticket === true;
  // The warning belongs to the ticked box: it shows only while Closes the ticket is ticked.
  discountEditClosesTicketHint.hidden = !discountEditClosesTicketInput.checked;
  discountEditMemberDailyCompEl.textContent = `Member daily comp: ${discount.member_daily_comp === true ? "On" : "Off"}`;
  discountEditRulesEl.hidden = false;
  drawDiscountEditSheetBody(discount);
  discountAddScrim.hidden = false;
  lockBodyScroll();
  settingsDiscountNameInput.focus();
}

/** LOCSET1 (B9): Save on the edit sheet. One request with only what changed; a refusal stays in the sheet with the typed values. */
async function handleDiscountEditSave(): Promise<void> {
  const discountId = discountEditId;
  const discount = discountId === undefined ? undefined : findDiscount(discountId);
  if (discountId === undefined || discount === undefined) {
    closeDiscountAddSheet();
    return;
  }
  discountAddErrorEl.textContent = "";
  clearDiscountAddInvalid();
  const name = settingsDiscountNameInput.value.trim();
  if (name.length === 0) {
    discountAddErrorEl.textContent = "A discount needs a name.";
    markDiscountAddInvalid(settingsDiscountNameInput);
    return;
  }
  let markdown;
  try {
    markdown = readDiscountMarkdown(settingsDiscountTypeInput.value, settingsDiscountValueInput.value);
  } catch (error) {
    discountAddErrorEl.textContent = (error as Error).message;
    markDiscountAddInvalid(settingsDiscountValueInput);
    return;
  }
  if (!settingsDiscountTicketsInput.checked && !settingsDiscountItemsInput.checked) {
    discountAddErrorEl.textContent =
      "Tick Tickets, Items, or both. A discount that applies to neither has nowhere to be used.";
    markDiscountAddInvalid(settingsDiscountTicketsInput, settingsDiscountItemsInput);
    return;
  }
  const patch: Parameters<typeof updateDiscount>[3] = {};
  // DSC37-F1: a refusal is marked on the control the save started from, which here is the first control that was changed.
  let atFault: HTMLElement | undefined;
  const renamed = name !== discount.name;
  if (renamed) {
    patch.name = name;
    atFault ??= settingsDiscountNameInput;
  }
  const markdownChanged =
    markdown.markdownType !== discount.markdown_type ||
    (markdown.markdownBasisPoints ?? null) !== discount.markdown_basis_points ||
    (markdown.markdownAmountCents ?? null) !== discount.markdown_amount_cents;
  // The markdown triple always moves TOGETHER, which is what stops a stale percentage sitting behind a dollar discount.
  if (markdownChanged) {
    Object.assign(patch, markdown);
    atFault ??= markdown.markdownType !== discount.markdown_type ? settingsDiscountTypeInput : settingsDiscountValueInput;
  }
  const flags: Array<[keyof DiscountDto, "appliesToTickets" | "appliesToItems" | "requiresApproval" | "requiresReason" | "autoApplyOnMembership" | "closesTicket", HTMLInputElement]> = [
    ["applies_to_tickets", "appliesToTickets", settingsDiscountTicketsInput],
    ["applies_to_items", "appliesToItems", settingsDiscountItemsInput],
    ["requires_approval", "requiresApproval", settingsDiscountApprovalInput],
    ["requires_reason", "requiresReason", settingsDiscountReasonInput],
    ["auto_apply_on_membership", "autoApplyOnMembership", discountEditAutoApplyInput],
    ["closes_ticket", "closesTicket", discountEditClosesTicketInput],
  ];
  let flagsChanged = false;
  for (const [field, param, input] of flags) {
    if (input.checked !== (discount[field] === true)) {
      patch[param] = input.checked;
      flagsChanged = true;
      atFault ??= input;
    }
  }
  const stagedScope = readStagedDiscountScope(discountEditScopeEl);
  // The server replaces the scope wholesale, so the WHOLE scope is sent, read off the boxes as they stand.
  const scopeChanged =
    stagedScope !== undefined &&
    (stagedScope.length !== discount.scope.length ||
      stagedScope.some((clause) => !scopeHas(discount.scope, clause.dimension, clause.value)));
  if (scopeChanged && stagedScope !== undefined) {
    patch.scope = stagedScope;
    atFault ??= Array.from(discountEditScopeEl.querySelectorAll<HTMLInputElement>("input[type=checkbox]")).find(
      (input) => input.checked !== scopeHas(discount.scope, input.dataset["dimension"] ?? "", input.dataset["value"] ?? ""),
    );
  }
  if (Object.keys(patch).length === 0) {
    closeDiscountAddSheet();
    return;
  }
  settingsDiscountStatusEl.textContent = "";
  const gen = loadGeneration;
  const s = session();
  settingsDiscountAddButton.disabled = true;
  const result = await updateDiscount(s.apiBaseUrl, s.tenantId, discountId, patch);
  if (!discountWriteIsCurrent(gen, s)) return;
  settingsDiscountAddButton.disabled = false;
  if (result.status !== 200) {
    // The reason shows in the sheet, in a toast (fixed on screen, DSC12) and in the status line, as every refused discount edit did.
    discountAddErrorEl.textContent = discountSaveRefusal("add", result.body.message).text;
    const message = `Could not change this discount. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`;
    settingsDiscountStatusEl.textContent = message;
    adminToast(message, "danger");
    if (atFault !== undefined) {
      atFault.setAttribute("aria-invalid", "true");
      atFault.setAttribute("aria-describedby", "admin-discount-add-error");
    }
    return;
  }
  const saved = applyDiscountMutation(result.body.discount);
  closeDiscountAddSheet();
  renderDiscountList();
  // The list was rebuilt, so focus returns to the new Edit button of the row that was edited, never to the page.
  settingsDiscountsEl
    .querySelector<HTMLElement>(`[data-testid="admin-discount-row"][data-discount-id="${discountId}"] [data-testid="admin-discount-edit"]`)
    ?.focus();
  // Renaming does not rewrite history: past tickets keep the reference AND the name the discount was applied under.
  if (renamed) adminToast(`Renamed to ${saved.name}. Past tickets keep the old wording.`, "success");
  if (markdownChanged || flagsChanged || scopeChanged) adminToast(`${saved.name} updated.`, "success");
}

function clearDiscountAddInvalid(): void {
  for (const control of [
    settingsDiscountNameInput,
    settingsDiscountTypeInput,
    settingsDiscountValueInput,
    settingsDiscountTicketsInput,
    settingsDiscountItemsInput,
    settingsDiscountApprovalInput,
    settingsDiscountReasonInput,
    discountEditAutoApplyInput,
    discountEditClosesTicketInput,
  ]) {
    control.removeAttribute("aria-invalid");
    if (control.getAttribute("aria-describedby") === "admin-discount-add-error") control.removeAttribute("aria-describedby");
  }
  for (const box of Array.from(discountEditScopeEl.querySelectorAll("input[aria-invalid]"))) {
    box.removeAttribute("aria-invalid");
    box.removeAttribute("aria-describedby");
  }
}

function openDiscountAddSheet(): void {
  if (localDiscountsAllowed !== true || !discountAddScrim.hidden) return;
  resetDiscountAddForm();
  discountAddScrim.hidden = false;
  lockBodyScroll();
  settingsDiscountNameInput.focus();
}

function closeDiscountAddSheet(): void {
  if (discountAddScrim.hidden) return;
  discountAddScrim.hidden = true;
  unlockBodyScroll();
  resetDiscountAddForm();
  // Sign-out hides the whole admin screen; only restore focus while the button is still shown.
  const returnTo = discountEditReturnId === undefined ? null : settingsDiscountsEl.querySelector<HTMLElement>(
    `[data-testid="admin-discount-row"][data-discount-id="${discountEditReturnId}"] [data-testid="admin-discount-edit"]`,
  );
  discountEditReturnId = undefined;
  if (returnTo !== null) returnTo.focus();
  else if (!discountAddOpenButton.hidden) discountAddOpenButton.focus();
}

function trapDiscountAddFocus(event: KeyboardEvent): void {
  const sheet = discountAddScrim.querySelector<HTMLElement>("#admin-discount-add-sheet");
  if (sheet === null) return;
  const focusable = Array.from(
    sheet.querySelectorAll<HTMLElement>("input, select, button, textarea, [tabindex]"),
  ).filter((node) => !node.hasAttribute("disabled") && node.tabIndex >= 0);
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (first === undefined || last === undefined) return;
  const active = document.activeElement;
  if (!sheet.contains(active)) {
    event.preventDefault();
    first.focus();
  } else if (event.shiftKey && active === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
}

/** Presentation only: shown to a session holding the permission the create route enforces. */
function syncDiscountAddOpenButton(): void {
  const active = currentSession;
  discountAddOpenButton.hidden =
    active === undefined || localDiscountsAllowed !== true || !hasPermission(active, "admin.location_settings.manage");
}

function markDiscountAddInvalid(...controls: HTMLElement[]): void {
  for (const control of controls) control.setAttribute("aria-invalid", "true");
  controls[0]?.focus();
}

async function handleDiscountAdd(): Promise<void> {
  discountAddErrorEl.textContent = "";
  clearDiscountAddInvalid();
  const name = settingsDiscountNameInput.value.trim();
  if (name.length === 0) {
    discountAddErrorEl.textContent = "A discount needs a name.";
    markDiscountAddInvalid(settingsDiscountNameInput);
    return;
  }
  let markdown;
  try {
    markdown = readDiscountMarkdown(
      settingsDiscountTypeInput.value,
      settingsDiscountValueInput.value,
    );
  } catch (error) {
    discountAddErrorEl.textContent = (error as Error).message;
    markDiscountAddInvalid(settingsDiscountValueInput);
    return;
  }
  const appliesToTickets = settingsDiscountTicketsInput.checked;
  const appliesToItems = settingsDiscountItemsInput.checked;
  if (!appliesToTickets && !appliesToItems) {
    discountAddErrorEl.textContent =
      "Tick Tickets, Items, or both. A discount that applies to neither has nowhere to be used.";
    markDiscountAddInvalid(settingsDiscountTicketsInput, settingsDiscountItemsInput);
    return;
  }
  settingsDiscountStatusEl.textContent = "";
  const gen = loadGeneration;
  const s = session();
  settingsDiscountAddButton.disabled = true;
  const result = await createDiscount(s.apiBaseUrl, s.tenantId, {
    name,
    ...markdown,
    appliesToTickets,
    appliesToItems,
    requiresApproval: settingsDiscountApprovalInput.checked,
    requiresReason: settingsDiscountReasonInput.checked,
  });
  if (!discountWriteIsCurrent(gen, s)) return;
  settingsDiscountAddButton.disabled = false;
  if (result.status !== 201) {
    discountAddErrorEl.textContent = `Could not add that discount. ${discountSaveRefusal("add", result.body.message).text}`;
    return;
  }
  applyDiscountMutation(result.body.discount);
  closeDiscountAddSheet();
  renderDiscountList();
  // DSC10 (DEC-041, 2026-09-02): a new discount is created INACTIVE, so
  // the toast and the row itself both have to say so plainly -- an Admin
  // who just named a discount and sees nothing else would reasonably
  // expect it to be live, the same way every other row on this screen
  // reads by default. The row's own "Off" toggle already carries the
  // state; this makes sure the Admin actually looks at that row instead
  // of trusting the toast alone, and the toast names the two steps left
  // (scope, then switch on) rather than a bare confirmation.
  const newRow = settingsDiscountsEl.querySelector<HTMLElement>(
    `[data-testid="admin-discount-row"][data-discount-id="${result.body.discount.id}"]`,
  );
  newRow?.scrollIntoView({ block: "center" });
  adminToast(
    `${result.body.discount.name} added, OFF. Set its scope below, then switch it on when it is ready.`,
    "success",
  );
}

// ---------------------------------------------------------------------
// Settings > Discount reasons. Byte-for-byte the Void reasons block's own
// shape, against the separate discount_reasons vocabulary. Deliberately
// not shared code: the two lists are edited on the same screen, look the
// same, and mean different things, and folding them into one renderer
// would make it one edit away from writing a discount reason into the
// void picker.
// ---------------------------------------------------------------------

/** Invalidates reads across this write without changing the shared switch contract. */
function beginDiscountReasonMutation(): () => void {
  const gen = loadGeneration;
  settingsDiscountReasonStatusEl.textContent = "";
  discountReasonMutationRevision += 1;
  discountReasonPendingWrites += 1;
  return () => {
    if (!loadIsCurrent(gen)) return;
    discountReasonMutationRevision += 1;
    discountReasonPendingWrites -= 1;
    // A successful local mutation already updates a complete list. Only an
    // interrupted initial read needs recovery, coalesced after the last write.
    if (discountReasonPendingWrites === 0 && !discountReasonListLoaded)
      void loadDiscountReasons(true);
  };
}

async function loadDiscountReasons(preserveMutationStatus = false): Promise<void> {
  if (discountReasonPendingWrites > 0) return;
  const gen = loadGeneration;
  const sequence = ++discountReasonReadSequence;
  const revision = discountReasonMutationRevision;
  const s = session();
  const result = await listDiscountReasons(s.apiBaseUrl, s.tenantId, { activeOnly: false });
  // SET16-F4: this session's teardown ran while the fetch above was in
  // flight; discard rather than write over what invalidate() already
  // cleared.
  if (
    !loadIsCurrent(gen) ||
    sequence !== discountReasonReadSequence ||
    revision !== discountReasonMutationRevision ||
    discountReasonPendingWrites > 0
  )
    return;
  if (result.status !== 200) {
    if (!preserveMutationStatus || settingsDiscountReasonStatusEl.textContent === "") {
      settingsDiscountReasonStatusEl.textContent = `Could not load discount reasons. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`;
    }
    return;
  }
  if (!preserveMutationStatus) settingsDiscountReasonStatusEl.textContent = "";
  // A recovering initial read may finish while a newly added row is being
  // edited. Carry its unsaved text and focus across the complete-list render.
  const drafts = new Map<
    string,
    { value: string; focused: boolean; start: number | null; end: number | null }
  >();
  for (const row of settingsDiscountReasonsEl.querySelectorAll<HTMLElement>(
    "[data-discount-reason-id]",
  )) {
    const id = row.dataset["discountReasonId"]!;
    const input = row.querySelector<HTMLInputElement>("input[type=text]");
    const current = settingsDiscountReasons.find((reason) => reason.id === id);
    if (input && (input.value !== current?.label || document.activeElement === input)) {
      drafts.set(id, {
        value: input.value,
        focused: document.activeElement === input,
        start: input.selectionStart,
        end: input.selectionEnd,
      });
    }
  }
  settingsDiscountReasons = result.body.discountReasons;
  discountReasonListLoaded = true;
  renderDiscountReasonList();
  for (const row of settingsDiscountReasonsEl.querySelectorAll<HTMLElement>(
    "[data-discount-reason-id]",
  )) {
    const draft = drafts.get(row.dataset["discountReasonId"]!);
    const input = row.querySelector<HTMLInputElement>("input[type=text]");
    if (!draft || !input) continue;
    input.value = draft.value;
    if (draft.focused) {
      input.focus({ preventScroll: true });
      input.setSelectionRange(draft.start, draft.end);
    }
  }
}

function renderDiscountReasonList(): void {
  settingsDiscountReasonsEl.innerHTML = "";
  if (settingsDiscountReasons.length === 0) {
    settingsDiscountReasonsEl.innerHTML = '<p class="tabs-empty">No discount reasons yet.</p>';
    return;
  }
  for (const reason of sortByName(settingsDiscountReasons, (reason) => reason.label)) {
    const row = document.createElement("li");
    row.className = "admin-void-reason-row";
    row.dataset["testid"] = "admin-discount-reason-row";
    row.dataset["discountReasonId"] = reason.id;

    const fields = document.createElement("div");
    fields.className = "admin-void-reason-fields";
    const nameInput = document.createElement("input");
    describeSetting(nameInput, "admin-settings-discount-reasons-help");
    nameInput.type = "text";
    nameInput.className = "field-input admin-void-reason-name";
    nameInput.dataset["testid"] = "admin-discount-reason-name";
    nameInput.value = reason.label;
    nameInput.setAttribute("value", reason.label);
    nameInput.maxLength = 60;
    nameInput.setAttribute("aria-label", `Name for ${reason.label}`);
    nameInput.addEventListener("blur", () => void handleDiscountReasonRename(reason.id, nameInput));
    nameInput.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        nameInput.blur();
      }
    });
    fields.append(nameInput);
    if (reason.is_shipped_default) {
      const shipped = document.createElement("span");
      shipped.className = "admin-void-reason-shipped";
      shipped.dataset["testid"] = "admin-discount-reason-shipped";
      shipped.textContent = "Default";
      fields.append(shipped);
    }
    row.append(fields);

    // DEC-052: the same switch and confirm-before-deactivating flow as
    // every other reversible activate/deactivate control in Settings.
    // Confirm copy reuses this section's own existing help-text wording.
    row.append(
      renderActivationSwitch({
        active: reason.active,
        entityLabel: reason.label,
        toggleTestId: "admin-discount-reason-toggle",
        resolveCurrent: () => {
          if (!canManageLocationSettingsLists()) return undefined;
          const current = settingsDiscountReasons.find((candidate) => candidate.id === reason.id);
          const currentRow = Array.from(settingsDiscountReasonsEl.children).find(
            (candidate) => (candidate as HTMLElement).dataset["discountReasonId"] === reason.id,
          );
          const currentInput = currentRow?.querySelector<HTMLInputElement>('[data-testid="admin-discount-reason-toggle"]');
          const currentStatus = currentRow?.querySelector<HTMLElement>('[data-testid="admin-discount-reason-status-text"]');
          if (current === undefined || currentInput === null || currentInput === undefined ||
            currentStatus === null || currentStatus === undefined) return undefined;
          return { input: currentInput, status: currentStatus, active: current.active };
        },

        describedById: "admin-settings-discount-reasons-help",
        statusTestId: "admin-discount-reason-status-text",
        activeText: "On",
        inactiveText: "Off",
        confirmDeactivate: (name) => ({
          title: `Turn off ${name}?`,
          message: `Turning ${name} off takes it off the bartender's picker and leaves past discounts readable.`,
          confirmLabel: "Turn off",
        }),
        write: async (nextActive) => {
          const s = session();
          const gen = loadGeneration;
          const finish = beginDiscountReasonMutation();
          try {
            const result = await updateDiscountReason(s.apiBaseUrl, s.tenantId, reason.id, {
              active: nextActive,
            });
            if (!loadIsCurrent(gen)) return { ok: false };
            if (result.status !== 200) {
              return { ok: false, message: result.body.message ?? "Check your connection." };
            }
            return { ok: true, active: result.body.discountReason.active };
          } finally {
            finish();
          }
        },
        onError: (message) => {
          settingsDiscountReasonStatusEl.textContent = `Could not change this reason. ${capitalizeServerMessage(message)}`;
        },
        onChange: (active) => {
          const current = settingsDiscountReasons.find((candidate) => candidate.id === reason.id);
          if (current !== undefined) current.active = active;
          settingsDiscountReasonStatusEl.textContent = "";
          adminToast(
            active
              ? `${reason.label} is back on the bartender's list.`
              : `${reason.label} is off the bartender's list. Past discounts still show it.`,
            "success",
          );
        },
      }),
    );

    settingsDiscountReasonsEl.append(row);
  }
}

async function handleDiscountReasonRename(
  discountReasonId: string,
  input: HTMLInputElement,
): Promise<void> {
  const reason = settingsDiscountReasons.find((candidate) => candidate.id === discountReasonId);
  if (reason === undefined) {
    return;
  }
  const label = input.value.trim();
  if (label === reason.label) {
    input.value = reason.label;
    return;
  }
  if (label.length === 0) {
    input.value = reason.label;
    settingsDiscountReasonStatusEl.textContent = "A discount reason needs a name.";
    return;
  }
  settingsDiscountReasonStatusEl.textContent = "";
  const s = session();
  const gen = loadGeneration;
  const finish = beginDiscountReasonMutation();
  try {
    const result = await updateDiscountReason(s.apiBaseUrl, s.tenantId, discountReasonId, {
      label,
    });
    if (!loadIsCurrent(gen)) return;
    if (result.status !== 200) {
      input.value = reason.label;
      settingsDiscountReasonStatusEl.textContent = `Could not rename this reason. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`;
      return;
    }
    reason.label = result.body.discountReason.label;
    input.value = reason.label;
    input.setAttribute("value", reason.label);
    adminToast(`Renamed to ${reason.label}. Past discounts keep the old wording.`, "success");
  } finally {
    finish();
  }
}

async function handleDiscountReasonAdd(): Promise<void> {
  const label = settingsDiscountReasonNameInput.value.trim();
  if (label.length === 0) {
    settingsDiscountReasonStatusEl.textContent = "A discount reason needs a name.";
    return;
  }
  settingsDiscountReasonStatusEl.textContent = "";
  const s = session();
  const gen = loadGeneration;
  const finish = beginDiscountReasonMutation();
  try {
    const result = await createDiscountReason(s.apiBaseUrl, s.tenantId, { label });
    if (!loadIsCurrent(gen)) return;
    if (result.status !== 201) {
      settingsDiscountReasonStatusEl.textContent = `Could not add that reason. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`;
      return;
    }
    settingsDiscountReasons.push(result.body.discountReason);
    settingsDiscountReasonNameInput.value = "";
    renderDiscountReasonList();
    adminToast(`${result.body.discountReason.label} added.`, "success");
  } finally {
    finish();
  }
}

// ---------------------------------------------------------------------
// M5 (DEC-282): Settings > Price change reasons, and the one threshold the
// closeout flags by. The list is the Discount reasons block's own shape
// against price_change_reasons; the same act-immediately, no-delete pattern.
// The threshold is a whole percent here and basis points on the wire, read
// back from the same GET the floor sheet uses so the two always agree.
// ---------------------------------------------------------------------

async function loadPriceChangeReasons(): Promise<void> {
  const gen = loadGeneration;
  const s = session();
  const result = await listPriceChangeReasons(s.apiBaseUrl, s.tenantId, { activeOnly: false });
  if (!loadIsCurrent(gen)) return;
  if (result.status !== 200) {
    settingsPriceChangeReasonStatusEl.textContent = `Could not load price change reasons. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`;
    return;
  }
  settingsPriceChangeReasonStatusEl.textContent = "";
  settingsPriceChangeReasons = result.body.priceChangeReasons;
  settingsPriceChangeFlagInput.value = String(result.body.flagOffBasisPoints / 100);
  renderPriceChangeReasonList();
}

function renderPriceChangeReasonList(): void {
  settingsPriceChangeReasonsEl.innerHTML = "";
  if (settingsPriceChangeReasons.length === 0) {
    settingsPriceChangeReasonsEl.innerHTML =
      '<p class="tabs-empty">No price change reasons yet.</p>';
    return;
  }
  for (const reason of sortByName(settingsPriceChangeReasons, (reason) => reason.label)) {
    const row = document.createElement("li");
    row.className = "admin-void-reason-row";
    row.dataset["testid"] = "admin-price-change-reason-row";
    row.dataset["priceChangeReasonId"] = reason.id;

    const fields = document.createElement("div");
    fields.className = "admin-void-reason-fields";
    const nameInput = document.createElement("input");
    describeSetting(nameInput, "admin-settings-price-change-reasons-help");
    nameInput.type = "text";
    nameInput.className = "field-input admin-void-reason-name";
    nameInput.dataset["testid"] = "admin-price-change-reason-name";
    nameInput.value = reason.label;
    nameInput.setAttribute("value", reason.label);
    nameInput.maxLength = 60;
    nameInput.setAttribute("aria-label", `Name for ${reason.label}`);
    nameInput.addEventListener(
      "blur",
      () => void handlePriceChangeReasonRename(reason.id, nameInput),
    );
    nameInput.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        nameInput.blur();
      }
    });
    fields.append(nameInput);
    if (reason.is_shipped_default) {
      const shipped = document.createElement("span");
      shipped.className = "admin-void-reason-shipped";
      shipped.dataset["testid"] = "admin-price-change-reason-shipped";
      shipped.textContent = "Default";
      fields.append(shipped);
    }
    row.append(fields);
    row.append(
      renderActivationSwitch({
        active: reason.active,
        entityLabel: reason.label,
        toggleTestId: "admin-price-change-reason-toggle",
        describedById: "admin-settings-price-change-reasons-help",
        statusTestId: "admin-price-change-reason-status-text",
        activeText: "On",
        inactiveText: "Off",
        confirmDeactivate: (name) => ({
          title: `Turn off ${name}?`,
          message: `Turning ${name} off takes it off the bartender's picker and leaves past price changes readable.`,
          confirmLabel: "Turn off",
        }),
        write: async (nextActive) => {
          const s = session();
          const result = await updatePriceChangeReason(s.apiBaseUrl, s.tenantId, reason.id, {
            active: nextActive,
          });
          if (result.status !== 200) {
            return { ok: false, message: result.body.message ?? "Check your connection." };
          }
          reason.active = result.body.priceChangeReason.active;
          return { ok: true, active: result.body.priceChangeReason.active };
        },
        onError: (message) => {
          settingsPriceChangeReasonStatusEl.textContent = `Could not change this reason. ${capitalizeServerMessage(message)}`;
        },
        onChange: (active) => {
          settingsPriceChangeReasonStatusEl.textContent = "";
          adminToast(
            active
              ? `${reason.label} is back on the bartender's list.`
              : `${reason.label} is off the bartender's list. Past price changes still show it.`,
            "success",
          );
        },
      }),
    );
    settingsPriceChangeReasonsEl.append(row);
  }
}

async function handlePriceChangeReasonRename(
  priceChangeReasonId: string,
  input: HTMLInputElement,
): Promise<void> {
  const reason = settingsPriceChangeReasons.find(
    (candidate) => candidate.id === priceChangeReasonId,
  );
  if (reason === undefined) {
    return;
  }
  const label = input.value.trim();
  if (label === reason.label) {
    input.value = reason.label;
    return;
  }
  if (label.length === 0) {
    input.value = reason.label;
    settingsPriceChangeReasonStatusEl.textContent = "A price change reason needs a name.";
    return;
  }
  settingsPriceChangeReasonStatusEl.textContent = "";
  const s = session();
  const result = await updatePriceChangeReason(s.apiBaseUrl, s.tenantId, priceChangeReasonId, {
    label,
  });
  if (result.status !== 200) {
    input.value = reason.label;
    settingsPriceChangeReasonStatusEl.textContent = `Could not rename this reason. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`;
    return;
  }
  reason.label = result.body.priceChangeReason.label;
  input.value = reason.label;
  input.setAttribute("value", reason.label);
  adminToast(`Renamed to ${reason.label}. Past price changes keep the old wording.`, "success");
}

async function handlePriceChangeReasonAdd(): Promise<void> {
  const label = settingsPriceChangeReasonNameInput.value.trim();
  if (label.length === 0) {
    settingsPriceChangeReasonStatusEl.textContent = "A price change reason needs a name.";
    return;
  }
  settingsPriceChangeReasonStatusEl.textContent = "";
  const s = session();
  const result = await createPriceChangeReason(s.apiBaseUrl, s.tenantId, { label });
  if (result.status !== 201) {
    settingsPriceChangeReasonStatusEl.textContent = `Could not add that reason. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`;
    return;
  }
  settingsPriceChangeReasons.push(result.body.priceChangeReason);
  settingsPriceChangeReasonNameInput.value = "";
  renderPriceChangeReasonList();
  adminToast(`${result.body.priceChangeReason.label} added.`, "success");
}

async function handleDiscountApprovalSave(): Promise<void> {
  const percent = Number(settingsDiscountApprovalPercentSelect.value);
  const dollars = Number(settingsDiscountApprovalAmountSelect.value);
  if (!Number.isInteger(percent) || percent <= 0 || !Number.isInteger(dollars) || dollars <= 0) {
    settingsDiscountApprovalStatusEl.textContent = "Pick a value from each list.";
    return;
  }
  settingsDiscountApprovalStatusEl.textContent = "";
  const s = session();
  const result = await updateSettings(s.apiBaseUrl, s.tenantId, {
    discountApprovalPercentBasisPoints: percent,
    discountApprovalAmountCents: dollars,
  });
  if (result.status !== 200) {
    settingsDiscountApprovalStatusEl.textContent = `Could not save this. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`;
    return;
  }
  adminToast(
    `Discounts over ${discountApprovalLabel("percent", percent)} or ${discountApprovalLabel("dollars", dollars)} now ask for a manager.`,
    "success",
  );
}

async function handlePriceChangeFlagSave(): Promise<void> {
  const percent = Number(settingsPriceChangeFlagInput.value);
  if (!Number.isInteger(percent) || percent < 1 || percent > 100) {
    settingsPriceChangeFlagStatusEl.textContent =
      "Type a whole percent from 1 to 100, for example 50.";
    return;
  }
  settingsPriceChangeFlagStatusEl.textContent = "";
  const s = session();
  const result = await updateSettings(s.apiBaseUrl, s.tenantId, {
    priceChangeFlagOffBasisPoints: percent * 100,
  });
  if (result.status !== 200) {
    settingsPriceChangeFlagStatusEl.textContent = `Could not save this. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`;
    return;
  }
  adminToast(
    `Price changes at ${percent}% off or more are now flagged on the closeout.`,
    "success",
  );
}

// ---------------------------------------------------------------------
// AU14-F4, AU14-F14, AU14-F15 / DEC-276, DEC-316: Settings > the action reasons
// lists. The price change reasons block's own shape against the shared
// action_reasons list, one editor per kind: act immediately, rename never
// rewrites history (every record keeps its label as it read then), turning one
// off takes it off the picker and never deletes it. Admin screen, so a text
// field and a keyboard are fine.
// ---------------------------------------------------------------------

async function loadActionReasons(editor: ActionReasonEditor): Promise<void> {
  applyReasonAddGating();
  const gen = loadGeneration;
  const s = session();
  const result = await listActionReasons(s.apiBaseUrl, s.tenantId, {
    kind: editor.kind,
    activeOnly: false,
  });
  if (!loadIsCurrent(gen)) return;
  if (result.status !== 200) {
    editor.statusEl.textContent = `${editor.words.loadFailed} ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`;
    return;
  }
  editor.statusEl.textContent = "";
  settingsActionReasons.set(editor.kind, result.body.actionReasons);
  renderActionReasonList(editor);
}

function renderActionReasonList(editor: ActionReasonEditor): void {
  const canEdit = canManageLocationSettingsLists();
  const reasons = settingsActionReasons.get(editor.kind) ?? [];
  editor.listEl.innerHTML = "";
  if (reasons.length === 0) {
    editor.listEl.innerHTML = `<p class="tabs-empty">${editor.words.empty}</p>`;
    return;
  }
  for (const reason of sortByName(reasons, (reason) => reason.label)) {
    const row = document.createElement("li");
    row.className = "admin-void-reason-row";
    row.dataset["testid"] = `admin-${editor.slug}-reason-row`;
    row.dataset["actionReasonId"] = reason.id;

    const fields = document.createElement("div");
    fields.className = "admin-void-reason-fields";
    const nameInput = document.createElement("input");
    describeSetting(nameInput, `admin-settings-${editor.slug}-reasons-help`);
    nameInput.type = "text";
    nameInput.className = "field-input admin-void-reason-name";
    nameInput.dataset["testid"] = `admin-${editor.slug}-reason-name`;
    nameInput.value = reason.label;
    nameInput.setAttribute("value", reason.label);
    nameInput.maxLength = 60;
    nameInput.setAttribute("enterkeyhint", "done");
    nameInput.setAttribute("aria-label", `Name for ${reason.label}`);
    nameInput.disabled = !canEdit;
    nameInput.addEventListener(
      "blur",
      () => void handleActionReasonRename(editor, reason.id, nameInput),
    );
    nameInput.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        nameInput.blur();
      }
    });
    fields.append(nameInput);
    if (reason.is_shipped_default) {
      const shipped = document.createElement("span");
      shipped.className = "admin-void-reason-shipped";
      shipped.dataset["testid"] = `admin-${editor.slug}-reason-shipped`;
      shipped.textContent = "Default";
      fields.append(shipped);
    }
    row.append(fields);
    row.append(
      renderActivationSwitch({
        active: reason.active,
        entityLabel: reason.label,
        toggleTestId: `admin-${editor.slug}-reason-toggle`,
        disabled: !canEdit,
        describedById: `admin-settings-${editor.slug}-reasons-help`,
        statusTestId: `admin-${editor.slug}-reason-status-text`,
        activeText: "On",
        inactiveText: "Off",
        confirmDeactivate: (name) => ({
          title: `Turn off ${name}?`,
          message: editor.words.turnOffMessage(name),
          confirmLabel: "Turn off",
        }),
        write: async (nextActive) => {
          const s = session();
          const result = await updateActionReason(s.apiBaseUrl, s.tenantId, reason.id, {
            active: nextActive,
          });
          if (result.status !== 200) {
            return { ok: false, message: result.body.message ?? "Check your connection." };
          }
          reason.active = result.body.actionReason.active;
          return { ok: true, active: result.body.actionReason.active };
        },
        onError: (message) => {
          editor.statusEl.textContent = `Could not change this reason. ${capitalizeServerMessage(message)}`;
        },
        onChange: (active) => {
          editor.statusEl.textContent = "";
          adminToast(
            active ? editor.words.backOnList(reason.label) : editor.words.offList(reason.label),
            "success",
          );
        },
      }),
    );
    editor.listEl.append(row);
  }
}

async function handleActionReasonRename(
  editor: ActionReasonEditor,
  actionReasonId: string,
  input: HTMLInputElement,
): Promise<void> {
  const reasons = settingsActionReasons.get(editor.kind) ?? [];
  const reason = reasons.find((candidate) => candidate.id === actionReasonId);
  if (reason === undefined) {
    return;
  }
  const label = input.value.trim();
  if (label === reason.label) {
    input.value = reason.label;
    return;
  }
  if (label.length === 0) {
    input.value = reason.label;
    editor.statusEl.textContent = editor.words.needsName;
    return;
  }
  editor.statusEl.textContent = "";
  const s = session();
  const result = await updateActionReason(s.apiBaseUrl, s.tenantId, actionReasonId, { label });
  if (result.status !== 200) {
    input.value = reason.label;
    editor.statusEl.textContent = `Could not rename this reason. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`;
    return;
  }
  reason.label = result.body.actionReason.label;
  input.value = reason.label;
  input.setAttribute("value", reason.label);
  adminToast(editor.words.renamed(reason.label), "success");
}

async function handleActionReasonAdd(editor: ActionReasonEditor): Promise<void> {
  const label = editor.nameInput.value.trim();
  if (label.length === 0) {
    editor.statusEl.textContent = editor.words.needsName;
    return;
  }
  editor.statusEl.textContent = "";
  const s = session();
  const result = await createActionReason(s.apiBaseUrl, s.tenantId, { kind: editor.kind, label });
  if (result.status !== 201) {
    editor.statusEl.textContent = `Could not add that reason. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`;
    return;
  }
  const reasons = settingsActionReasons.get(editor.kind) ?? [];
  reasons.push(result.body.actionReason);
  settingsActionReasons.set(editor.kind, reasons);
  editor.nameInput.value = "";
  renderActionReasonList(editor);
  adminToast(`${result.body.actionReason.label} added.`, "success");
}

// ---------------------------------------------------------------------
// REF1 (DEC-011, 2026-08-29): Settings > Refund reasons, a THIRD
// configurable reason list. Byte-for-byte the Discount reasons block's
// own shape directly above, against the separate refund_reasons
// vocabulary. Deliberately not shared code, same reasoning as that
// block's own header: the lists look the same and mean different things,
// and sharing a renderer is one edit away from writing a refund reason
// onto the discount or void picker.
// ---------------------------------------------------------------------

/** Invalidates reads across this write without changing the shared switch contract. */
function beginRefundReasonMutation(): () => void {
  const gen = loadGeneration;
  settingsRefundReasonStatusEl.textContent = "";
  refundReasonMutationRevision += 1;
  refundReasonPendingWrites += 1;
  return () => {
    if (!loadIsCurrent(gen)) return;
    refundReasonMutationRevision += 1;
    refundReasonPendingWrites -= 1;
    // A successful local mutation already updates a complete list. Only an
    // interrupted initial read needs recovery, coalesced after the last write.
    if (refundReasonPendingWrites === 0 && !refundReasonListLoaded) void loadRefundReasons(true);
  };
}

async function loadRefundReasons(preserveMutationStatus = false): Promise<void> {
  applyReasonAddGating();
  if (refundReasonPendingWrites > 0) return;
  const gen = loadGeneration;
  const sequence = ++refundReasonReadSequence;
  const revision = refundReasonMutationRevision;
  const s = session();
  const result = await listRefundReasons(s.apiBaseUrl, s.tenantId, { activeOnly: false });
  // SET16-F4: this session's teardown ran while the fetch above was in
  // flight; discard rather than write over what invalidate() already
  // cleared.
  if (
    !loadIsCurrent(gen) ||
    sequence !== refundReasonReadSequence ||
    revision !== refundReasonMutationRevision ||
    refundReasonPendingWrites > 0
  )
    return;
  if (result.status !== 200) {
    if (!preserveMutationStatus || settingsRefundReasonStatusEl.textContent === "") {
      settingsRefundReasonStatusEl.textContent = `Could not load refund reasons. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`;
    }
    return;
  }
  if (!preserveMutationStatus) settingsRefundReasonStatusEl.textContent = "";
  // A recovering initial read may finish while a newly added row is being
  // edited. Carry its unsaved text and focus across the complete-list render.
  const drafts = new Map<
    string,
    { value: string; focused: boolean; start: number | null; end: number | null }
  >();
  for (const row of settingsRefundReasonsEl.querySelectorAll<HTMLElement>(
    "[data-refund-reason-id]",
  )) {
    const id = row.dataset["refundReasonId"]!;
    const input = row.querySelector<HTMLInputElement>("input[type=text]");
    const current = settingsRefundReasons.find((reason) => reason.id === id);
    if (input && (input.value !== current?.label || document.activeElement === input)) {
      drafts.set(id, {
        value: input.value,
        focused: document.activeElement === input,
        start: input.selectionStart,
        end: input.selectionEnd,
      });
    }
  }
  settingsRefundReasons = result.body.refundReasons;
  refundReasonListLoaded = true;
  renderRefundReasonList();
  for (const row of settingsRefundReasonsEl.querySelectorAll<HTMLElement>(
    "[data-refund-reason-id]",
  )) {
    const draft = drafts.get(row.dataset["refundReasonId"]!);
    const input = row.querySelector<HTMLInputElement>("input[type=text]");
    if (!draft || !input) continue;
    input.value = draft.value;
    if (draft.focused) {
      input.focus({ preventScroll: true });
      input.setSelectionRange(draft.start, draft.end);
    }
  }
}

function renderRefundReasonList(): void {
  settingsRefundReasonsEl.innerHTML = "";
  applyReasonAddGating();
  const canEdit = canManageLocationSettingsLists();
  if (settingsRefundReasons.length === 0) {
    settingsRefundReasonsEl.innerHTML = '<p class="tabs-empty">No refund reasons yet.</p>';
    return;
  }
  for (const reason of sortByName(settingsRefundReasons, (reason) => reason.label)) {
    const row = document.createElement("li");
    row.className = "admin-void-reason-row";
    row.dataset["testid"] = "admin-refund-reason-row";
    row.dataset["refundReasonId"] = reason.id;

    const fields = document.createElement("div");
    fields.className = "admin-void-reason-fields";
    const nameInput = document.createElement("input");
    describeSetting(nameInput, "admin-settings-refund-reasons-help");
    nameInput.type = "text";
    nameInput.className = "field-input admin-void-reason-name";
    nameInput.dataset["testid"] = "admin-refund-reason-name";
    nameInput.value = reason.label;
    nameInput.setAttribute("value", reason.label);
    nameInput.maxLength = 60;
    nameInput.setAttribute("aria-label", `Name for ${reason.label}`);
    nameInput.disabled = !canEdit;
    nameInput.addEventListener("blur", () => void handleRefundReasonRename(reason.id, nameInput));
    nameInput.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        nameInput.blur();
      }
    });
    fields.append(nameInput);
    if (reason.is_shipped_default) {
      const shipped = document.createElement("span");
      shipped.className = "admin-void-reason-shipped";
      shipped.dataset["testid"] = "admin-refund-reason-shipped";
      shipped.textContent = "Default";
      fields.append(shipped);
    }
    row.append(fields);

    // DEC-052: the same switch and confirm-before-deactivating flow as
    // every other reversible activate/deactivate control in Settings.
    // Confirm copy reuses this section's own existing help-text wording.
    row.append(
      renderActivationSwitch({
        active: reason.active,
        entityLabel: reason.label,
        toggleTestId: "admin-refund-reason-toggle",
        disabled: !canEdit,
        describedById: "admin-settings-refund-reasons-help",
        statusTestId: "admin-refund-reason-status-text",
        activeText: "On",
        inactiveText: "Off",
        confirmDeactivate: (name) => ({
          title: `Turn off ${name}?`,
          message: `Turning ${name} off takes it off the bartender's picker and leaves past refunds readable.`,
          confirmLabel: "Turn off",
        }),
        write: async (nextActive) => {
          const s = session();
          const gen = loadGeneration;
          const finish = beginRefundReasonMutation();
          try {
            const result = await updateRefundReason(s.apiBaseUrl, s.tenantId, reason.id, {
              active: nextActive,
            });
            if (!loadIsCurrent(gen)) return { ok: false };
            if (result.status !== 200) {
              return { ok: false, message: result.body.message ?? "Check your connection." };
            }
            reason.active = result.body.refundReason.active;
            return { ok: true, active: result.body.refundReason.active };
          } finally {
            finish();
          }
        },
        onError: (message) => {
          settingsRefundReasonStatusEl.textContent = `Could not change this reason. ${capitalizeServerMessage(message)}`;
        },
        onChange: (active) => {
          settingsRefundReasonStatusEl.textContent = "";
          adminToast(
            active
              ? `${reason.label} is back on the bartender's list.`
              : `${reason.label} is off the bartender's list. Past refunds still show it.`,
            "success",
          );
        },
      }),
    );

    settingsRefundReasonsEl.append(row);
  }
}

async function handleRefundReasonRename(
  refundReasonId: string,
  input: HTMLInputElement,
): Promise<void> {
  const reason = settingsRefundReasons.find((candidate) => candidate.id === refundReasonId);
  if (reason === undefined) {
    return;
  }
  const label = input.value.trim();
  if (label === reason.label) {
    input.value = reason.label;
    return;
  }
  if (label.length === 0) {
    input.value = reason.label;
    settingsRefundReasonStatusEl.textContent = "A refund reason needs a name.";
    return;
  }
  settingsRefundReasonStatusEl.textContent = "";
  const s = session();
  const gen = loadGeneration;
  const finish = beginRefundReasonMutation();
  try {
    const result = await updateRefundReason(s.apiBaseUrl, s.tenantId, refundReasonId, { label });
    if (!loadIsCurrent(gen)) return;
    if (result.status !== 200) {
      input.value = reason.label;
      settingsRefundReasonStatusEl.textContent = `Could not rename this reason. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`;
      return;
    }
    reason.label = result.body.refundReason.label;
    input.value = reason.label;
    input.setAttribute("value", reason.label);
    adminToast(`Renamed to ${reason.label}. Past refunds keep the old wording.`, "success");
  } finally {
    finish();
  }
}

async function handleRefundReasonAdd(): Promise<void> {
  const label = settingsRefundReasonInput.value.trim();
  if (label.length === 0) {
    settingsRefundReasonStatusEl.textContent = "A refund reason needs a name.";
    return;
  }
  settingsRefundReasonStatusEl.textContent = "";
  const s = session();
  const gen = loadGeneration;
  const finish = beginRefundReasonMutation();
  try {
    const result = await createRefundReason(s.apiBaseUrl, s.tenantId, { label });
    if (!loadIsCurrent(gen)) return;
    if (result.status !== 201) {
      settingsRefundReasonStatusEl.textContent = `Could not add that reason. ${capitalizeServerMessage(result.body.message ?? "Check your connection.")}`;
      return;
    }
    settingsRefundReasons.push(result.body.refundReason);
    settingsRefundReasonInput.value = "";
    renderRefundReasonList();
    adminToast(`${result.body.refundReason.label} added.`, "success");
  } finally {
    finish();
  }
}
